import { capitalize, keyBy } from "client-server-shared/utils/lodash-methods";
import { BaseTemplate, Field } from "./types";
import { noTemplateId } from "../client-templates/default-template-id";
import camelCase from "lodash/camelCase";

export const noTemplate = {
  id: noTemplateId,
  value: noTemplateId,
  slug: "default",
  name: "Untitled Document",
  fields: [],
};

export const fixTemplateFieldData = (
  fields: Field[],
  data: Record<string, any>
) => {
  fields.forEach((field) => {
    const camelCaseFieldName = camelCase(field.label);

    if (data[camelCaseFieldName]) {
      data[field.id] = data[camelCaseFieldName];
    }
    if (data[field.id]) {
      data[camelCaseFieldName] = data[field.id];

      data[capitalize(camelCaseFieldName)] = data[field.id];
    }
  });

  return data;
};

export const baseTemplates: BaseTemplate[] = [
  {
    slug: "script-for-sales-pitch-generator",
    name: "Script for Sales Pitch Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Innovative software solution for project management.",
        description:
          "Provide a brief overview of the product or service you are pitching.",
        id: "about",
      },
    ],
    title: "Script for Sales Pitch Generator",
    id: "script-for-sales-pitch-generator",
    categories: ["Sales", "Marketing"],
    shortDescription:
      "Generate compelling sales pitches tailored to your product and audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating sales pitches. You will be provided with a brief overview of a product or service, and you will generate a compelling sales pitch based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a sales pitch based on the following:\n";
      prompt += "The pitch should be about: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "blog-outline-generator",
    name: "Blog Outline Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: The benefits of remote work, Tips for healthy eating, etc.",
        description: "A brief overview of the blog topic you want to cover.",
        id: "about",
      },
    ],
    title: "Blog Outline Generator",
    id: "blog-outline-generator",
    categories: ["Writing", "Education", "Marketing"],
    shortDescription:
      "Generate structured outlines for blog posts to streamline your writing process.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating structured outlines for blog posts. You will be provided with a brief overview of the blog topic, and you will create a detailed outline to help streamline the writing process.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a structured outline for a blog post based on the following:\n";
      prompt += "Blog topic: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "blog-conclusion-generator",
    name: "Blog Conclusion Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: The benefits of remote work, Tips for healthy eating, etc.",
        description: "A brief overview of the blog topic you want to cover.",
        id: "about",
      },
    ],
    title: "Blog Conclusion Generator",
    id: "blog-conclusion-generator",
    categories: ["Writing", "Education", "Marketing"],
    shortDescription:
      "Generate a conclusion for your blog post to wrap up your thoughts and leave a lasting impression on your readers.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating conclusions for blog posts. You will be provided with a brief overview of the blog topic, and you will create a compelling conclusion to wrap up your thoughts and leave a lasting impression on your readers.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a conclusion for a blog post based on the following:\n";
      prompt += "Blog topic: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "compliance-training-generator",
    name: "Compliance Training Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Workplace safety, Data protection, Anti-harassment policies, etc.",
        description: "Provide a brief overview of the compliance topic.",
        id: "about",
      },
    ],
    title: "Compliance Training Generator",
    id: "compliance-training-generator",
    categories: ["Training Material", "Education", "Business"],
    shortDescription:
      "Generate customized compliance training materials to ensure regulatory adherence.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating compliance training materials. You will be provided with a brief overview of the compliance topic, and you will generate customized training content based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate compliance training materials based on the following:\n";
      prompt += "The topic is about " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "welcome-call-generator",
    name: "Welcome Call Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We are a leading provider of innovative tech solutions.",
        description: "A brief introduction about the company or service.",
        id: "about",
      },
    ],
    title: "Welcome Call Generator",
    id: "welcome-call-generator",
    categories: ["Support", "Sales", "Training Material"],
    shortDescription:
      "Generate scripts for welcoming new clients or customers during a call.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating welcome call scripts. You will be provided with a brief introduction about the company or service, and you will create a welcoming script for new clients or customers.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a welcome call script based on the following:\n";
      prompt += "About the company/service: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "diversity-and-inclusion-training-generator",
    name: "Diversity and Inclusion Training Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This training aims to foster an inclusive workplace by addressing biases and promoting understanding.",
        description:
          "Provide an overview of the diversity and inclusion training program.",
        id: "about",
      },
    ],
    title: "Diversity and Inclusion Training Generator",
    id: "diversity-and-inclusion-training-generator",
    categories: ["Training Material", "Education", "Business"],
    shortDescription:
      "Generate comprehensive training materials focused on diversity and inclusion in the workplace.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating diversity and inclusion training materials. You will be provided with an overview of the training program, and you will generate comprehensive content that addresses biases and promotes understanding in the workplace.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate training materials based on the following:\n";
      prompt += "Overview of the training: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "case-study-generator",
    name: "Case Study Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A successful marketing campaign for a new product launch.",
        description:
          "A brief overview of the subject or project being studied.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Objective",
        optional: true,
        required: false,
        placeholder:
          "Example: To analyze the effectiveness of social media strategies in increasing brand awareness.",
        description: "The main goal or purpose of the case study.",
        id: "objective",
      },
    ],
    title: "Case Study Generator",
    id: "case-study-generator",
    categories: ["Writing", "Marketing", "Education"],
    shortDescription:
      "Generate comprehensive case studies to showcase success stories and insights.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating comprehensive case studies. You will be provided with information about a subject or project, and you will generate a detailed case study based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, objective } = fields;
      let prompt = "Generate a case study based on the following:\n";
      prompt += "Subject: " + about + "\n";
      if (objective) {
        prompt += "Objective: " + objective + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "monthly-employee-pulse-survey-generator",
    name: "Monthly Employee Pulse Survey Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This survey aims to assess employee morale and gather feedback on workplace culture.",
        description: "Provide a brief overview of the survey's purpose.",
        id: "about",
      },
    ],
    title: "Monthly Employee Pulse Survey Generator",
    id: "monthly-employee-pulse-survey-generator",
    categories: ["Business", "Training Material", "Support"],
    shortDescription:
      "Generate insightful surveys to gauge employee satisfaction and engagement on a monthly basis.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating employee pulse surveys. You will be provided with information about the survey's purpose, and you will generate a comprehensive survey to assess employee morale and gather feedback.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a monthly employee pulse survey based on the following:\n";
      prompt += "Survey Purpose: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "ebay-product-description-generator",
    name: "Ebay Product Description Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This is a high-quality, durable backpack perfect for hiking and travel.",
        description: "A brief overview of the product being sold.",
        id: "about",
      },
    ],
    title: "Ebay Product Description Generator",
    id: "ebay-product-description-generator",
    categories: ["Sales", "Marketing", "Business"],
    shortDescription:
      "Generate compelling product descriptions for your eBay listings to attract buyers.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating product descriptions for eBay listings. You will be provided with details about a product, and you will create a compelling description to attract potential buyers.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a product description for eBay based on the following:\n";
      prompt += "Product Overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "real-estate-copywriting-generator",
    name: "Real Estate Copywriting Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Spacious 3-bedroom home in a quiet neighborhood with a large backyard.",
        description:
          "A brief description of the property or service being offered.",
        id: "about",
      },
    ],
    title: "Real Estate Copywriting Generator",
    id: "real-estate-copywriting-generator",
    categories: ["Marketing", "Sales", "Writing"],
    shortDescription:
      "Generate compelling real estate listings and marketing content to attract buyers and sellers.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in real estate copywriting. You will be provided with a description of a property or service, and you will generate compelling marketing content to attract potential buyers or sellers.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a real estate listing based on the following:\n";
      prompt += "Description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "payment-reminder-call-generator",
    name: "Payment Reminder Call Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Customer name, amount due, due date, etc.",
        description: "Provide details about the payment and the customer.",
        id: "about",
      },
    ],
    title: "Payment Reminder Call Generator",
    id: "payment-reminder-call-generator",
    categories: ["Support", "Business"],
    shortDescription:
      "Generate effective scripts for reminding customers about their pending payments.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating payment reminder call scripts. You will be provided with details about the payment and the customer, and you will create a script for reminding the customer about their pending payment.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a payment reminder call script based on the following:\n";
      prompt += "Details: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "script-for-radio-broadcast-generator",
    name: "Script for Radio Broadcast Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Today's news highlights, Upcoming events, Music playlist, etc.",
        description: "A brief overview of the topic or theme of the broadcast.",
        id: "about",
      },
    ],
    title: "Script for Radio Broadcast Generator",
    id: "script-for-radio-broadcast-generator",
    categories: ["Marketing", "Writing", "Education"],
    shortDescription:
      "Generate engaging scripts for radio broadcasts to captivate your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating scripts for radio broadcasts. You will be provided with some base information and you will generate a script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a script for a radio broadcast based on the following:\n";
      prompt += "The broadcast will be about: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "teambuilding-activity-generator",
    name: "Teambuilding Activity Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A scavenger hunt, team cooking challenge, outdoor adventure, etc.",
        description: "A brief description of the activity you want to create.",
        id: "about",
      },
    ],
    title: "Teambuilding Activity Generator",
    id: "teambuilding-activity-generator",
    categories: ["Training Material", "Business", "Education"],
    shortDescription:
      "Generate engaging and fun teambuilding activities to enhance collaboration and morale.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in designing teambuilding activities. You will be provided with a brief description of the activity, and you will generate a detailed plan for that activity.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a teambuilding activity based on the following:\n";
      prompt += "Activity description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "brand-name-generator",
    name: "Brand Name Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A sustainable clothing line, An innovative tech startup, etc.",
        description: "A brief description of your brand or business.",
        id: "about",
      },
    ],
    title: "Brand Name Generator",
    id: "brand-name-generator",
    categories: ["Business", "Marketing"],
    shortDescription:
      "Generate unique and catchy brand names for your business.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating unique and catchy brand names. You will be provided with a brief description of a brand or business, and you will generate creative brand name suggestions based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate unique and catchy brand names based on the following description:\n";
      prompt += "About: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "customer-support-call-generator",
    name: "Customer Support Call Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Customer is experiencing login issues, Customer wants to know about product features, etc.",
        description: "A brief overview of the customer's issue or inquiry.",
        id: "about",
      },
    ],
    title: "Customer Support Call Generator",
    id: "customer-support-call-generator",
    categories: ["Support", "Business"],
    shortDescription:
      "Generate scripts for customer support calls to enhance communication and resolve issues effectively.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating customer support call scripts. You will be provided with details about a customer's issue or inquiry, and you will create a script to guide the support representative in addressing the customer's needs.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a customer support call script based on the following:\n";
      prompt += "Customer's issue or inquiry: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "email-surveys-template-generator",
    name: "Email Surveys Template Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Customer satisfaction, Product feedback, Event evaluation, etc.",
        description: "Provide a brief overview of the survey topic.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Objective",
        optional: true,
        required: false,
        placeholder:
          "Example: Improve service quality, Understand user needs, Gather opinions on new features, etc.",
        description: "State the main goal of the survey.",
        id: "objective",
      },
    ],
    title: "Email Surveys Template Generator",
    id: "email-surveys-template-generator",
    categories: ["Email", "Marketing", "Support"],
    shortDescription:
      "Create engaging email surveys to gather valuable feedback from your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating email survey templates. You will be provided with some base information and you will generate a survey template based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, objective } = fields;
      let prompt =
        "Generate an email survey template based on the following:\n";
      prompt += "The survey is about: " + about + ".\n";
      if (objective) {
        prompt += "Main objective: " + objective + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "employee-handbook-generator",
    name: "Employee Handbook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We are a tech company focused on innovation and customer satisfaction.",
        description: "A brief overview of the company and its mission.",
        id: "about",
      },
    ],
    title: "Employee Handbook Generator",
    id: "employee-handbook-generator",
    categories: ["Business", "Training Material"],
    shortDescription:
      "Create a comprehensive employee handbook to outline company policies and procedures.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating employee handbooks. You will be provided with a brief overview of the company and its mission, and you will generate a comprehensive employee handbook that outlines company policies and procedures.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate an employee handbook based on the following:\n";
      prompt += "Company Overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "science-fiction-audiobook-generator",
    name: "Science Fiction Audiobook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A journey through a dystopian future where AI rules the world.",
        description: "A brief overview of the audiobook's plot and themes.",
        id: "about",
      },
    ],
    title: "Science Fiction Audiobook Generator",
    id: "science-fiction-audiobook-generator",
    categories: ["Writing", "Education", "Marketing"],
    shortDescription:
      "Generate engaging and imaginative audiobooks in the science fiction genre.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating science fiction audiobooks. You will be provided with a brief overview of the audiobook's plot and themes, and you will create an engaging narrative suitable for an audiobook format.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a science fiction audiobook based on the following:\n";
      prompt += "Plot and themes: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "twitter-replies-generator",
    name: "Twitter Replies Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A tweet about a new product launch, a question from a follower, etc.",
        description:
          "Provide context or details about the tweet you are replying to.",
        id: "about",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder:
          "Example: Friendly, Professional, Humorous, Supportive, etc.",
        description: "Select the desired tone for your reply.",
        id: "toneofvoice",
      },
    ],
    title: "Twitter Replies Generator",
    id: "twitter-replies-generator",
    categories: ["Social Media", "Writing"],
    shortDescription:
      "Generate engaging and relevant replies for Twitter conversations.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating Twitter replies. You will be provided with context about a tweet and you will generate a relevant reply based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, toneOfVoice } = fields;
      let prompt = "Generate a Twitter reply based on the following:\n";
      prompt += "Context: " + about + "\n";
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "nutrition-plan-generator",
    name: "Nutrition Plan Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Weight loss, muscle gain, vegetarian, gluten-free, etc.",
        description:
          "Provide a brief overview of the individual's dietary goals and restrictions.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Details",
        optional: true,
        required: false,
        placeholder:
          "Example: Likes chicken, dislikes fish, allergic to nuts, etc.",
        description:
          "Include specific details about meal preferences and any allergies.",
        id: "details",
      },
    ],
    title: "Nutrition Plan Generator",
    id: "nutrition-plan-generator",
    categories: ["Education", "Health", "Training Material"],
    shortDescription:
      "Generate personalized nutrition plans tailored to individual dietary needs and preferences.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized nutrition plans. You will be provided with dietary goals and preferences, and you will generate a tailored nutrition plan based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, details } = fields;
      let prompt =
        "Generate a personalized nutrition plan based on the following:\n";
      prompt += "Dietary goals: " + about + ".\n";
      if (details) {
        prompt += "Additional details: " + details + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "biography-audiobook-generator",
    name: "Biography Audiobook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A renowned scientist known for groundbreaking research in genetics.",
        description: "A brief overview of the person's life and achievements.",
        id: "about",
      },
    ],
    title: "Biography Audiobook Generator",
    id: "biography-audiobook-generator",
    categories: ["Writing", "Education"],
    shortDescription:
      "Generate engaging audiobooks based on personal biographies.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging audiobooks based on personal biographies. You will be provided with a brief overview of a person's life and achievements, and you will create a compelling audiobook script.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate an audiobook script based on the following:\n";
      prompt += "About the person: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "remote-work-effectiveness-survey-generator",
    name: "Remote Work Effectiveness Survey Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This survey aims to evaluate remote work experiences and challenges.",
        description: "Provide a brief overview of the survey's purpose.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Working mode",
        optional: true,
        required: false,
        placeholder: "Example: Fully remote, Hybrid, On-site",
        description:
          "Specify the working mode being assessed (e.g., fully remote, hybrid).",
        id: "workingmode",
      },
    ],
    title: "Remote Work Effectiveness Survey Generator",
    id: "remote-work-effectiveness-survey-generator",
    categories: ["Business", "Education", "Training Material"],
    shortDescription:
      "Generate surveys to assess the effectiveness of remote work practices.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating surveys to evaluate remote work effectiveness. You will be provided with some base information and you will generate a survey based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, workingMode } = fields;
      let prompt = "Generate a survey based on the following:\n";
      prompt += "Survey purpose: " + about + ".\n";
      if (workingMode) {
        prompt += "Working mode being assessed: " + workingMode + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "content-calendar-generator",
    name: "Content Calendar Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Weekly blog posts, Daily social media updates, Monthly newsletters, etc.",
        description: "A brief overview of the content you plan to create.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Duration",
        optional: true,
        required: false,
        placeholder: "Example: 1 week, 1 month, 3 months, etc.",
        description: "The time frame for the content schedule.",
        id: "duration",
      },
      {
        type: "textArea",
        label: "Target Audience",
        optional: true,
        required: false,
        placeholder:
          "Example: young professionals, parents, tech enthusiasts, etc.",
        description:
          "The specific audience you aim to reach with your content.",
        id: "targetaudience",
      },
    ],
    title: "Content Calendar Generator",
    id: "content-calendar-generator",
    categories: ["Social Media", "Marketing", "Education", "Writing"],
    shortDescription:
      "Plan and organize your content strategy with a structured calendar.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating content calendars. You will be provided with details about the content and its intended audience, and you will generate a structured content calendar based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, duration, targetAudience } = fields;
      let prompt = "Generate a content calendar based on the following:\n";
      prompt += "Content Overview: " + about + "\n";
      if (duration) {
        prompt += "Duration: " + duration + "\n";
      }
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "soft-skills-development-generator",
    name: "Soft Skills Development Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Communication, Teamwork, Problem-solving, etc.",
        description:
          "Provide a brief overview of the soft skills you want to develop.",
        id: "about",
      },
    ],
    title: "Soft Skills Development Generator",
    id: "soft-skills-development-generator",
    categories: ["Education", "Training Material", "Business"],
    shortDescription:
      "Generate content and resources to enhance soft skills for personal and professional growth.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in soft skills development. You will be provided with information about the specific soft skills to focus on, and you will generate content and resources to help enhance those skills.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate content and resources based on the following:\n";
      prompt +=
        "The focus is on developing the following soft skills: " +
        about +
        ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "cold-emails-generator",
    name: "Cold Emails Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Introduction to our services, Follow-up on previous conversation, etc.",
        description: "A brief description of the purpose of the email.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder: "Example: Small business owners, Marketing managers, etc.",
        description: "The specific group of people you are reaching out to.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Sent by",
        optional: true,
        required: false,
        placeholder: "Example: John Doe, Sales Manager, etc.",
        description: "The name or title of the person sending the email.",
        id: "sentby",
      },
    ],
    title: "Cold Emails Generator",
    id: "cold-emails-generator",
    categories: ["Email", "Sales", "Marketing"],
    shortDescription:
      "Craft personalized and engaging cold emails to connect with potential clients.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting cold emails. You will be provided with some base information and you will generate a personalized cold email based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetaudience, sentby } = fields;
      let prompt = "Generate a cold email based on the following:\n";
      prompt += "Purpose of the email: " + about + "\n";
      if (targetaudience) {
        prompt += "Target audience: " + targetaudience + "\n";
      }
      if (sentby) {
        prompt += "Sent by: " + sentby + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "script-for-product-demo-generator",
    name: "Script for Product Demo Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This is a revolutionary fitness tracker that monitors your health.",
        description: "A brief overview of the product being demonstrated.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Audience",
        optional: true,
        required: false,
        placeholder:
          "Example: fitness enthusiasts, tech-savvy individuals, etc.",
        description: "The intended audience for the product demo.",
        id: "audience",
      },
    ],
    title: "Script for Product Demo Generator",
    id: "script-for-product-demo-generator",
    categories: ["Marketing", "Sales", "Training Material"],
    shortDescription:
      "Generate engaging scripts for product demonstrations to showcase features and benefits.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating scripts for product demonstrations. You will be provided with details about the product and its intended audience, and you will generate a compelling script that highlights the features and benefits of the product.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, audience } = fields;
      let prompt =
        "Generate a script for a product demonstration based on the following:\n";
      prompt += "Product Overview: " + about + "\n";
      if (audience) {
        prompt += "Intended Audience: " + audience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "ebay-product-title-generator",
    name: "Ebay Product Title Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Brand new wireless headphones, Vintage leather jacket, etc.",
        description: "A brief description of the product you are selling.",
        id: "about",
      },
    ],
    title: "Ebay Product Title Generator",
    id: "ebay-product-title-generator",
    categories: ["Sales", "Marketing"],
    shortDescription:
      "Generate compelling product titles for your eBay listings to attract buyers.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating compelling product titles for eBay listings. You will be provided with a brief description of the product, and you will create an engaging title that attracts potential buyers.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a compelling eBay product title based on the following:\n";
      prompt += "Product description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "competitive-landscape-generator",
    name: "Competitive Landscape Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: A tech startup focused on AI solutions.",
        description: "A brief overview of your business or product.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Industry",
        optional: true,
        required: false,
        placeholder: "Example: Technology, Healthcare, Finance, etc.",
        description: "The industry in which your business operates.",
        id: "industry",
      },
    ],
    title: "Competitive Landscape Generator",
    id: "competitive-landscape-generator",
    categories: ["Business", "Marketing"],
    shortDescription:
      "Analyze and visualize the competitive landscape of your industry.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing and visualizing the competitive landscape of various industries. You will be provided with information about a business or product, and you will generate insights regarding its competitive environment.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, industry } = fields;
      let prompt =
        "Analyze the competitive landscape based on the following information:\n";
      prompt += "Business/Product Overview: " + about + "\n";
      if (industry) {
        prompt += "Industry: " + industry + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "non-disclosure-agreements-generator",
    name: "Non Disclosure Agreements Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Trade secrets, business plans, client lists, etc.",
        description:
          "Provide a brief description of the confidential information.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Job role",
        optional: true,
        required: false,
        placeholder: "Example: Consultant, employee, vendor, etc.",
        description:
          "Specify the job role of the person or entity receiving the NDA.",
        id: "jobrole",
      },
    ],
    title: "Non Disclosure Agreements Generator",
    id: "non-disclosure-agreements-generator",
    categories: ["Legal", "Business"],
    shortDescription:
      "Generate customized Non-Disclosure Agreements (NDAs) to protect your confidential information.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating Non-Disclosure Agreements (NDAs). You will be provided with details about the confidential information and the job role of the recipient, and you will create a customized NDA based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, jobrole } = fields;
      let prompt =
        "Generate a Non-Disclosure Agreement based on the following:\n";
      prompt += "Confidential information: " + about + ".\n";
      if (jobrole) {
        prompt += "Job role of the recipient: " + jobrole + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "tgif-activity-generator",
    name: "TGIF Activity Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Team-building exercise, Fun outing, Virtual game, etc.",
        description:
          "A brief description of the activity you want to generate.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Working model",
        optional: true,
        required: false,
        placeholder: "Example: In-person, Online, Hybrid, etc.",
        description: "The format or structure of the activity.",
        id: "workingmodel",
      },
    ],
    title: "TGIF Activity Generator",
    id: "tgif-activity-generator",
    categories: ["Social Media", "Business", "Training Material"],
    shortDescription:
      "Generate fun and engaging activities for your team to enjoy on Fridays.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating fun and engaging TGIF activities. You will be provided with some base information and you will generate a suitable activity based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, workingmodel } = fields;
      let prompt = "Generate a TGIF activity based on the following:\n";
      prompt += "The activity should be about " + about + ".\n";
      if (workingmodel) {
        prompt += "Working model: " + workingmodel + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "email-newsletter-generator",
    name: "Email Newsletter Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Monthly updates on company news, Weekly tips for healthy living, etc.",
        description:
          "A brief overview of the newsletter's content and purpose.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Newsletter type",
        optional: true,
        required: false,
        placeholder: "Example: Promotional, Informational, Educational, etc.",
        description: "The format or style of the newsletter.",
        id: "newslettertype",
      },
    ],
    title: "Email Newsletter Generator",
    id: "email-newsletter-generator",
    categories: ["Email", "Marketing"],
    shortDescription:
      "Create engaging and informative newsletters to keep your audience updated.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating email newsletters. You will be provided with some base information and you will generate a newsletter based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, newslettertype } = fields;
      let prompt = "Generate an email newsletter based on the following:\n";
      prompt += "The newsletter should be about: " + about + ".\n";
      if (newslettertype) {
        prompt += "Newsletter type: " + newslettertype + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "elevator-pitch-generator",
    name: "Elevator Pitch Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A revolutionary app that connects local farmers with consumers.",
        description:
          "A brief overview of the idea, product, or service you are pitching.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Duration",
        optional: true,
        required: false,
        placeholder: "Example: 30 seconds, 1 minute, etc.",
        description: "The time duration for your pitch, typically in seconds.",
        id: "duration",
      },
    ],
    title: "Elevator Pitch Generator",
    id: "elevator-pitch-generator",
    categories: ["Business", "Marketing", "Education"],
    shortDescription:
      "Craft a concise and compelling elevator pitch to effectively communicate your ideas.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting elevator pitches. You will be provided with a brief overview of an idea, product, or service, along with the desired duration for the pitch, and you will generate a concise and compelling elevator pitch based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, duration } = fields;
      let prompt = "Generate an elevator pitch based on the following:\n";
      prompt += "The pitch should be about: " + about + ".\n";
      if (duration) {
        prompt += "Duration: " + duration + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "employee-growth-plan-generator",
    name: "Employee Growth Plan Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Junior Developer looking to advance to Senior Developer.",
        description:
          "Provide a brief overview of the employee's current role and aspirations.",
        id: "about",
      },
    ],
    title: "Employee Growth Plan Generator",
    id: "employee-growth-plan-generator",
    categories: ["Training Material", "Business", "Education"],
    shortDescription:
      "Create personalized growth plans for employees to enhance their skills and career development.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized employee growth plans. You will be provided with information about an employee's current role and aspirations, and you will generate a tailored growth plan to help them enhance their skills and advance their career.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Create a personalized growth plan based on the following:\n";
      prompt += "About the employee: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "onboarding-materials-generator",
    name: "Onboarding Materials Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We are a tech company focused on innovative solutions.",
        description: "A brief overview of the company and its mission.",
        id: "about",
      },
    ],
    title: "Onboarding Materials Generator",
    id: "onboarding-materials-generator",
    categories: ["Training Material", "Education", "Business"],
    shortDescription:
      "Generate comprehensive onboarding materials to help new employees acclimate quickly.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating onboarding materials for new employees. You will be provided with a brief overview of the company and its mission, and you will generate comprehensive onboarding materials based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate onboarding materials based on the following:\n";
      prompt += "Company Overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "tiktok-replies-generator",
    name: "TikTok Replies Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A funny video about pets, a dance challenge, etc.",
        description: "Provide context or details about the content or comment.",
        id: "about",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Friendly, Humorous, Professional, etc.",
        description: "Select the desired tone for the replies.",
        id: "toneofvoice",
      },
      {
        type: "textArea",
        label: "Customer name",
        optional: true,
        required: false,
        placeholder: "Example: @username123, @funnycatlover, etc.",
        description: "The name of the user you are replying to.",
        id: "customername",
      },
    ],
    title: "TikTok Replies Generator",
    id: "tiktok-replies-generator",
    categories: ["Social Media", "Marketing"],
    shortDescription:
      "Generate engaging replies for TikTok comments to boost interaction.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging replies for TikTok comments. You will be provided with some context and details about the content or comment, and you will generate a suitable reply based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, toneofvoice, customername } = fields;
      let prompt = "Generate a TikTok reply based on the following:\n";
      prompt += "Context: " + about + "\n";
      if (customername) {
        prompt += "Replying to: " + customername + "\n";
      }
      if (toneofvoice) {
        prompt += "Tone of voice: " + toneofvoice + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "blog-title-meta-description-generator",
    name: "Blog Title & Meta Description Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This post discusses the benefits of meditation for mental health.",
        description: "A brief overview of the blog post content.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Keywords",
        optional: true,
        required: false,
        placeholder:
          "Example: meditation, mental health, wellness, mindfulness",
        description: "Relevant keywords to optimize search engine visibility.",
        id: "keywords",
      },
    ],
    title: "Blog Title & Meta Description Generator",
    id: "blog-title-meta-description-generator",
    categories: ["Writing", "Marketing", "Education"],
    shortDescription:
      "Generate catchy blog titles and concise meta descriptions to improve SEO.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating blog titles and meta descriptions. You will be provided with some base information about a blog post, and you will generate a catchy title and a concise meta description based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, keywords } = fields;
      let prompt =
        "Generate a blog title and meta description based on the following:\n";
      prompt += "The blog post is about: " + about + ".\n";
      if (keywords) {
        prompt += "Relevant keywords: " + keywords + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "motivational-posts-generator",
    name: "Motivational Posts Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Overcoming challenges, Embracing change, etc.",
        description:
          "A brief description of the motivation or theme for the post.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Social Media",
        optional: true,
        required: false,
        placeholder: "Example: Instagram, Facebook, Twitter, etc.",
        description: "The platform where the post will be shared.",
        id: "socialmedia",
      },
    ],
    title: "Motivational Posts Generator",
    id: "motivational-posts-generator",
    categories: ["Social Media", "Marketing"],
    shortDescription:
      "Generate inspiring and uplifting posts for social media to engage your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating motivational posts. You will be provided with some base information and you will generate a motivational post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, socialmedia } = fields;
      let prompt = "Generate a motivational post based on the following:\n";
      prompt += "The post should be about: " + about + ".\n";
      if (socialmedia) {
        prompt += "Platform: " + socialmedia + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "new-job-acceptance-email-generator",
    name: "New Job - Acceptance Email Generator",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Name",
        description: "The name of the person you are addressing in the email.",
        placeholder: "Example: John Doe",
        optional: false,
        required: true,
        id: "recipientsname",
      },
      {
        type: "textArea",
        label: "Job Title",
        description: "The title of the position you are accepting.",
        placeholder: "Example: Marketing Manager",
        optional: false,
        required: true,
        id: "jobtitle",
      },
      {
        type: "textArea",
        label: "Company Name",
        description: "The name of the company offering the job.",
        placeholder: "Example: ABC Corp",
        optional: false,
        required: true,
        id: "companyname",
      },
      {
        type: "textArea",
        label: "Start Date",
        description: "The date you will begin your new job.",
        placeholder: "Example: January 15, 2024",
        optional: false,
        required: true,
        id: "startdate",
      },
      {
        type: "textArea",
        label: "Additional Comments",
        description:
          "Any additional information or comments you want to include.",
        placeholder: "Example: Looking forward to joining the team!",
        optional: true,
        required: false,
        id: "additionalcomments",
      },
    ],
    title: "New Job - Acceptance Email Generator",
    id: "new-job-acceptance-email-generator",
    categories: ["Email", "Business"],
    shortDescription:
      "Generate a professional acceptance email for your new job offer.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting professional acceptance emails. You will be provided with details about the job offer and the recipient, and you will generate a well-structured acceptance email based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const {
        recipientsname,
        jobtitle,
        companyname,
        startdate,
        additionalcomments,
      } = fields;
      let prompt =
        "Generate a professional acceptance email based on the following:\n";
      prompt += "Recipient's Name: " + recipientsname + "\n";
      prompt += "Job Title: " + jobtitle + "\n";
      prompt += "Company Name: " + companyname + "\n";
      prompt += "Start Date: " + startdate + "\n";
      if (additionalcomments) {
        prompt += "Additional Comments: " + additionalcomments + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "instagram-threads-generator",
    name: "Instagram Threads Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Tips for healthy living, Latest fashion trends, etc.",
        description:
          "A brief description of the topic or theme you want to discuss.",
        id: "about",
      },
    ],
    title: "Instagram Threads Generator",
    id: "instagram-threads-generator",
    categories: ["Social Media", "Marketing"],
    shortDescription:
      "Generate engaging and relevant content for Instagram Threads to connect with your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging content for Instagram Threads. You will be provided with a topic and you will create relevant and captivating content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate content for an Instagram Thread based on the following:\n";
      prompt += "The thread should be about: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "travel-itinerary-generator",
    name: "Travel Itinerary Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Family vacation, Business conference, Adventure trip, etc.",
        description: "A brief overview of the trip.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Number of days",
        optional: true,
        required: false,
        placeholder: "Example: 5 days, 10 days, etc.",
        description: "Total duration of the trip in days.",
        id: "numberofdays",
      },
      {
        type: "textArea",
        label: "Purpose of visit",
        optional: true,
        required: false,
        placeholder: "Example: Leisure, Work, Education, etc.",
        description: "The main reason for the trip.",
        id: "purposeofvisit",
      },
      {
        type: "textArea",
        label: "Group",
        optional: true,
        required: false,
        placeholder:
          "Example: Family of four, Business team of six, Solo traveler, etc.",
        description: "Details about the travelers in the group.",
        id: "group",
      },
    ],
    title: "Travel Itinerary Generator",
    id: "travel-itinerary-generator",
    categories: ["Education", "Business", "Training Material"],
    shortDescription:
      "Plan your perfect trip with a detailed travel itinerary tailored to your needs.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating travel itineraries. You will be provided with details about a trip, and you will generate a comprehensive travel itinerary based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, numberofdays, purposeofvisit, group } = fields;
      let prompt = "Generate a travel itinerary based on the following:\n";
      prompt += "Trip Overview: " + about + "\n";
      if (numberofdays) {
        prompt += "Duration of Trip: " + numberofdays + "\n";
      }
      if (purposeofvisit) {
        prompt += "Purpose of Visit: " + purposeofvisit + "\n";
      }
      if (group) {
        prompt += "Travel Group: " + group + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "executive-summary-generator",
    name: "Executive Summary Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: A new software solution for project management.",
        description: "A brief overview of the project or business.",
        id: "about",
      },
      {
        type: "textArea",
        label: "About company",
        optional: true,
        required: false,
        placeholder: "Example: XYZ Corp, a leading provider of tech solutions.",
        description:
          "Information about the company providing the service or product.",
        id: "aboutcompany",
      },
    ],
    title: "Executive Summary Generator",
    id: "executive-summary-generator",
    categories: ["Business", "Writing", "Education"],
    shortDescription:
      "Generate concise and impactful executive summaries for your business needs.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating executive summaries. You will be provided with information about a project or business, and you will generate a concise and impactful executive summary based on the details provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, aboutcompany } = fields;
      let prompt = "Generate an executive summary based on the following:\n";
      prompt += "Overview: " + about + "\n";
      if (aboutcompany) {
        prompt += "About the company: " + aboutcompany + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "financial-advice-generator",
    name: "Financial Advice Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: I want to save for a house, pay off student loans, etc.",
        description: "A brief overview of your financial goals and situation.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Current income",
        optional: true,
        required: false,
        placeholder: "Example: $3000, $4500, etc.",
        description: "Your total monthly income from all sources.",
        id: "currentincome",
      },
      {
        type: "textArea",
        label: "Expenses",
        optional: true,
        required: false,
        placeholder: "Example: $1500, $2000, etc.",
        description: "Your total monthly expenses including rent, bills, etc.",
        id: "expenses",
      },
      {
        type: "textArea",
        label: "Assets",
        optional: true,
        required: false,
        placeholder: "Example: $5000 in savings, a car worth $8000, etc.",
        description: "List your assets such as savings, investments, property.",
        id: "assets",
      },
      {
        type: "textArea",
        label: "Debts",
        optional: true,
        required: false,
        placeholder:
          "Example: $2000 credit card debt, $15000 student loan, etc.",
        description: "List any debts you currently have.",
        id: "debts",
      },
    ],
    title: "Financial Advice Generator",
    id: "financial-advice-generator",
    categories: ["Business", "Education"],
    shortDescription:
      "Get personalized financial advice based on your current financial situation.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert financial advisor. You will be provided with information about a person's financial situation and goals, and you will generate personalized financial advice based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, currentIncome, expenses, assets, debts } = fields;
      let prompt =
        "Provide personalized financial advice based on the following information:\n";
      prompt += "Overview of financial goals: " + about + ".\n";
      if (currentIncome) {
        prompt += "Current income: " + currentIncome + ".\n";
      }
      if (expenses) {
        prompt += "Monthly expenses: " + expenses + ".\n";
      }
      if (assets) {
        prompt += "Assets: " + assets + ".\n";
      }
      if (debts) {
        prompt += "Debts: " + debts + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "content-summary-generator",
    name: "Content Summary Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A detailed article on climate change impacts, A research paper on AI advancements, etc.",
        description:
          "Provide a brief overview of the content you want summarized.",
        id: "about",
      },
    ],
    title: "Content Summary Generator",
    id: "content-summary-generator",
    categories: ["Writing", "Education", "Marketing"],
    shortDescription:
      "Generate concise summaries for various types of content.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating summaries for various types of content. You will be provided with a brief overview of the content, and you will create a concise summary based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a summary for the following content:\n";
      prompt += "Overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "content-rephrase-generator",
    name: "Content Rephrase Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: This is a sample text that needs rephrasing.",
        description: "Provide the content you want to rephrase.",
        id: "about",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Formal, Casual, Persuasive, etc.",
        description: "Select the desired tone for the rephrased content.",
        id: "toneofvoice",
      },
      {
        type: "textArea",
        label: "Length",
        optional: true,
        required: false,
        placeholder:
          "Example: Short (1-2 sentences), Medium (3-5 sentences), Long (more than 5 sentences).",
        description: "Specify the desired length of the rephrased content.",
        id: "length",
      },
    ],
    title: "Content Rephrase Generator",
    id: "content-rephrase-generator",
    categories: ["Writing", "Education", "Marketing"],
    shortDescription:
      "Easily rephrase your content to improve clarity and engagement.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rephrasing content. You will be provided with a piece of content and you will rephrase it based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, toneOfVoice, length } = fields;
      let prompt = "Rephrase the following content:\n";
      prompt += "Content: " + about + ".\n";
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + ".\n";
      }
      if (length) {
        prompt += "Length: " + length + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "content-simplifier-generator",
    name: "Content Simplifier Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A detailed report on market trends, A technical manual, etc.",
        description: "Provide the content that needs simplification.",
        id: "about",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Friendly, Professional, Casual, etc.",
        description: "Select the desired tone for the simplified content.",
        id: "toneofvoice",
      },
      {
        type: "textArea",
        label: "Audience",
        optional: true,
        required: false,
        placeholder: "Example: General public, Students, Professionals, etc.",
        description:
          "Identify the audience for whom the content is being simplified.",
        id: "audience",
      },
    ],
    title: "Content Simplifier Generator",
    id: "content-simplifier-generator",
    categories: ["Writing", "Education", "Business"],
    shortDescription:
      "Simplify complex content into clear and concise language for better understanding.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in simplifying complex content. You will be provided with a piece of content and you will simplify it based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, toneOfVoice, audience } = fields;
      let prompt = "Simplify the following content:\n";
      prompt += "Content: " + about + ".\n";
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + ".\n";
      }
      if (audience) {
        prompt += "Audience: " + audience + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "employee-ice-breaker-generator",
    name: "Employee Ice-Breaker Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A fun question to get to know each other better.",
        description: "A brief description of the ice-breaker activity.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Company culture",
        optional: true,
        required: false,
        placeholder: "Example: Collaborative, Innovative, Fun, etc.",
        description: "Describe the company culture to tailor the ice-breakers.",
        id: "companyculture",
      },
    ],
    title: "Employee Ice-Breaker Generator",
    id: "employee-ice-breaker-generator",
    categories: ["Training Material", "Business", "Education"],
    shortDescription:
      "Generate fun and engaging ice-breaker questions to foster team bonding.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating ice-breaker questions for employees. You will be provided with some base information and you will generate a fun and engaging ice-breaker question based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, companyCulture } = fields;
      let prompt = "Generate an ice-breaker question based on the following:\n";
      prompt += "The activity should be about: " + about + ".\n";
      if (companyCulture) {
        prompt += "Company culture: " + companyCulture + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "persona-generator",
    name: "Persona Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A 30-year-old marketing manager living in a city.",
        description:
          "A brief description of the persona's background and characteristics.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Product Value Proposition",
        optional: true,
        required: false,
        placeholder:
          "Example: Helps busy professionals save time on meal prep.",
        description: "The unique value your product offers to this persona.",
        id: "productvalueproposition",
      },
    ],
    title: "Persona Generator",
    id: "persona-generator",
    categories: ["Marketing", "Business", "Education"],
    shortDescription:
      "Generate detailed customer personas to better understand your target audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating detailed customer personas. You will be provided with information about a persona's background and product value proposition, and you will generate a comprehensive persona profile based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, productValueProposition } = fields;
      let prompt =
        "Generate a detailed customer persona based on the following:\n";
      prompt += "About: " + about + "\n";
      if (productValueProposition) {
        prompt +=
          "Product Value Proposition: " + productValueProposition + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "menu-prompt-call-generator",
    name: "Menu Prompt Call Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Seasonal menu, Special event, New dish introduction, etc.",
        description:
          "A brief description of the menu or service being offered.",
        id: "about",
      },
    ],
    title: "Menu Prompt Call Generator",
    id: "menu-prompt-call-generator",
    categories: ["Business", "Support", "Marketing"],
    shortDescription:
      "Generate engaging prompts for menu calls to enhance customer interaction.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging prompts for menu calls. You will be provided with a brief description of the menu or service, and you will create a compelling prompt to enhance customer interaction.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a menu prompt call based on the following:\n";
      prompt += "About: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "facebook-posts-generator",
    name: "Facebook Posts Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Exciting news about our new product launch!",
        description: "A brief description of the content you want to share.",
        id: "about",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Friendly, Informative, Casual",
        description:
          "The desired tone for your post (e.g., friendly, professional, humorous).",
        id: "toneofvoice",
      },
    ],
    title: "Facebook Posts Generator",
    id: "facebook-posts-generator",
    categories: ["Social Media", "Marketing"],
    shortDescription:
      "Generate engaging posts for Facebook to connect with your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating Facebook posts. You will be provided some base information and you will generate a Facebook post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, toneOfVoice } = fields;
      let prompt = "Generate a Facebook post based on the following:\n";
      prompt += "The post should be about " + about + ".\n";
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "escalation-responses-generator",
    name: "Escalation Responses Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Customer complaint regarding delayed shipment, Technical issue with product, etc.",
        description: "Provide details about the escalation situation.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Channel",
        optional: true,
        required: false,
        placeholder: "Example: Email, Phone call, Live chat, etc.",
        description: "Specify the communication channel for the response.",
        id: "channel",
      },
    ],
    title: "Escalation Responses Generator",
    id: "escalation-responses-generator",
    categories: ["Support", "Business"],
    shortDescription:
      "Generate effective responses for customer escalations to ensure timely resolution.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating escalation responses. You will be provided with details about a customer escalation situation and you will generate an appropriate response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, channel } = fields;
      let prompt =
        "Generate a response for the following escalation situation:\n";
      prompt += "Details: " + about + "\n";
      if (channel) {
        prompt += "Communication channel: " + channel + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "discovery-request-generator",
    name: "Discovery Request Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We need to develop a new website for our e-commerce business.",
        description: "Provide a brief overview of the project or request.",
        id: "about",
      },
    ],
    title: "Discovery Request Generator",
    id: "discovery-request-generator",
    categories: ["Business", "Support"],
    shortDescription:
      "Generate structured requests for project discovery to streamline communication.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating structured requests for project discovery. You will be provided with a brief overview of the project or request, and you will create a detailed discovery request based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a structured discovery request based on the following:\n";
      prompt += "Project Overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "faq-generator",
    name: "FAQ Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Company policies, Product features, Service offerings, etc.",
        description:
          "Provide a brief overview of the topic for which you need FAQs.",
        id: "about",
      },
    ],
    title: "FAQ Generator",
    id: "faq-generator",
    categories: ["Support", "Website", "Business"],
    shortDescription:
      "Generate frequently asked questions and their answers for various purposes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating frequently asked questions (FAQs). You will be provided with a brief overview of a topic, and you will create relevant FAQs along with their answers based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate frequently asked questions and their answers based on the following:\n";
      prompt += "Topic: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "write-product-blogs-generator",
    name: "Write Product Blogs Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This is a revolutionary fitness tracker that monitors your health.",
        description: "A brief overview of the product you want to write about.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Company name",
        optional: true,
        required: false,
        placeholder: "Example: FitTech Inc., HealthPlus, TrendyWear",
        description: "The name of the company that produces the product.",
        id: "companyname",
      },
    ],
    title: "Write Product Blogs Generator",
    id: "write-product-blogs-generator",
    categories: ["Writing", "Marketing", "Business"],
    shortDescription:
      "Generate engaging and informative blog posts about your products.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing product blogs. You will be provided with details about a product and its company, and you will generate an engaging blog post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, companyName } = fields;
      let prompt = "Generate a blog post about the following product:\n";
      prompt += "Product Overview: " + about + "\n";
      if (companyName) {
        prompt += "Company Name: " + companyName + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "tiktok-ads-generator",
    name: "TikTok Ads Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A revolutionary fitness app that tracks your workouts.",
        description:
          "A brief description of the product or service being advertised.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Video content",
        optional: true,
        required: false,
        placeholder:
          "Example: Showcasing user testimonials, product features, and a call to action.",
        description:
          "Outline the key elements of the video content for the ad.",
        id: "videocontent",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder:
          "Example: Gen Z, fitness enthusiasts, tech-savvy individuals.",
        description:
          "Define the demographic and interests of your target audience.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Goal",
        optional: true,
        required: false,
        placeholder:
          "Example: Increase app downloads, boost brand awareness, drive website traffic.",
        description: "Specify the main objective of the ad campaign.",
        id: "goal",
      },
    ],
    title: "TikTok Ads Generator",
    id: "tiktok-ads-generator",
    categories: ["Social Media", "Marketing"],
    shortDescription:
      "Create engaging and viral ads for TikTok to capture your audience's attention.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating engaging TikTok ads. You will be provided with details about a product or service, and you will generate a compelling ad script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, videoContent, targetAudience, goal } = fields;
      let prompt = "Generate a TikTok ad based on the following:\n";
      prompt += "The ad should be about " + about + ".\n";
      if (videoContent) {
        prompt += "Video content: " + videoContent + ".\n";
      }
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (goal) {
        prompt += "Goal of the ad campaign: " + goal + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "performance-improvement-plan-generator",
    name: "Performance Improvement Plan Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Employee has missed deadlines consistently over the past three months.",
        description: "Provide a brief overview of the performance issue.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Objective",
        optional: true,
        required: false,
        placeholder:
          "Example: Improve time management skills to meet project deadlines.",
        description: "Define the specific goals for improvement.",
        id: "objective",
      },
      {
        type: "textArea",
        label: "Consequences",
        optional: true,
        required: false,
        placeholder:
          "Example: Possible demotion or termination if performance does not meet expectations.",
        description:
          "Outline potential consequences if performance does not improve.",
        id: "consequences",
      },
    ],
    title: "Performance Improvement Plan Generator",
    id: "performance-improvement-plan-generator",
    categories: ["Business", "Training Material"],
    shortDescription:
      "Create structured performance improvement plans to enhance employee productivity and address performance issues.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating Performance Improvement Plans. You will be provided with details about an employee's performance issues, and you will generate a structured plan to help improve their performance.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, objective, consequences } = fields;
      let prompt =
        "Create a Performance Improvement Plan based on the following:\n";
      prompt += "Performance issue: " + about + "\n";
      if (objective) {
        prompt += "Objective for improvement: " + objective + "\n";
      }
      if (consequences) {
        prompt += "Potential consequences: " + consequences + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "recruiting-assistant-generator",
    name: "Recruiting Assistant Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We are looking for a software engineer with experience in Java and Python.",
        description: "A brief overview of the position you are recruiting for.",
        id: "about",
      },
    ],
    title: "Recruiting Assistant Generator",
    id: "recruiting-assistant-generator",
    categories: ["Business", "Education", "Support"],
    shortDescription:
      "Streamline your hiring process with tailored recruitment content.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating recruitment content. You will be provided with details about a job position, and you will create tailored recruitment messages based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate recruitment content based on the following:\n";
      prompt += "Position details: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "logo-suggestion-generator",
    name: "Logo Suggestion Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: A tech startup focused on AI solutions.",
        description: "A brief description of your brand or business.",
        id: "about",
      },
    ],
    title: "Logo Suggestion Generator",
    id: "logo-suggestion-generator",
    categories: ["Business", "Marketing"],
    shortDescription:
      "Generate creative logo ideas based on your brand's identity.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating logo suggestions. You will be provided with a brief description of a brand or business, and you will generate creative logo ideas based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate logo suggestions based on the following:\n";
      prompt += "Brand description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "slogan-generator",
    name: "Slogan Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A sustainable clothing line, A tech startup, etc.",
        description: "A brief description of your brand or product.",
        id: "about",
      },
    ],
    title: "Slogan Generator",
    id: "slogan-generator",
    categories: ["Marketing", "Business", "Writing"],
    shortDescription:
      "Generate catchy and memorable slogans for your brand or product.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating catchy and memorable slogans. You will be provided with a brief description of a brand or product, and you will generate a slogan that encapsulates its essence.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a catchy slogan based on the following:\n";
      prompt += "About: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "demand-letter-generator",
    name: "Demand Letter Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Unpaid invoice, breach of contract, etc.",
        description: "Provide details about the issue or claim.",
        id: "about",
      },
    ],
    title: "Demand Letter Generator",
    id: "demand-letter-generator",
    categories: ["Legal", "Business"],
    shortDescription:
      "Generate professional demand letters for various legal situations.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in drafting demand letters. You will be provided with details about a legal issue or claim, and you will generate a professional demand letter based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a demand letter based on the following:\n";
      prompt += "The issue or claim is: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "prospect-research-generator",
    name: "Prospect Research Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A tech startup focused on AI solutions, A local bakery with a loyal customer base, etc.",
        description:
          "Provide a brief overview of the prospect's business or background.",
        id: "about",
      },
    ],
    title: "Prospect Research Generator",
    id: "prospect-research-generator",
    categories: ["Sales", "Marketing", "Business"],
    shortDescription:
      "Generate detailed profiles of potential clients to enhance your outreach efforts.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating detailed profiles of potential clients. You will be provided with information about a prospect's business or background, and you will create a comprehensive profile to enhance outreach efforts.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a detailed profile based on the following:\n";
      prompt += "About the prospect: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "brand-strategy-generator",
    name: "Brand Strategy Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We are a sustainable clothing brand focused on eco-friendly materials.",
        description: "A brief overview of your brand and its mission.",
        id: "about",
      },
    ],
    title: "Brand Strategy Generator",
    id: "brand-strategy-generator",
    categories: ["Marketing", "Business"],
    shortDescription:
      "Develop a comprehensive brand strategy to enhance your market presence.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in developing brand strategies. You will be provided with information about a brand and its mission, and you will generate a comprehensive brand strategy based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Develop a brand strategy based on the following:\n";
      prompt += "Brand overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "customer-survey-call-generator",
    name: "Customer Survey Call Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We want to understand customer satisfaction with our new product.",
        description: "Provide a brief overview of the survey's purpose.",
        id: "about",
      },
    ],
    title: "Customer Survey Call Generator",
    id: "customer-survey-call-generator",
    categories: ["Support", "Business", "Training Material"],
    shortDescription:
      "Generate effective scripts for conducting customer surveys to gather valuable feedback.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating customer survey call scripts. You will be provided with information about the survey's purpose, and you will create a script that effectively guides the conversation to gather valuable feedback.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a customer survey call script based on the following:\n";
      prompt += "Survey Purpose: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "terms-of-use-policy-generator",
    name: "Terms of Use Policy Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: An online marketplace for handmade crafts, A blog about travel tips, etc.",
        description: "Provide a brief description of your business or website.",
        id: "about",
      },
    ],
    title: "Terms of Use Policy Generator",
    id: "terms-of-use-policy-generator",
    categories: ["Legal", "Business"],
    shortDescription:
      "Generate comprehensive terms of use policies tailored to your business needs.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating terms of use policies. You will be provided with a brief description of a business or website, and you will create a comprehensive terms of use policy tailored to that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a terms of use policy based on the following:\n";
      prompt += "Business/Website Description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "legal-advice-generator",
    name: "Legal Advice Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Contract dispute, Employment agreement, Lease terms, etc.",
        description:
          "Provide details about the legal issue or document you need assistance with.",
        id: "about",
      },
    ],
    title: "Legal Advice Generator",
    id: "legal-advice-generator",
    categories: ["Legal", "Business"],
    shortDescription:
      "Generate legal advice and documents tailored to your needs.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing legal advice and generating legal documents. You will be given details about a legal issue or document, and you will provide tailored advice or draft the necessary documents based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate legal advice or a document based on the following:\n";
      prompt += "Legal issue or document: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "project-proposal-generator",
    name: "Project Proposal Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This project aims to improve community engagement through workshops.",
        description: "A brief overview of the project you are proposing.",
        id: "about",
      },
    ],
    title: "Project Proposal Generator",
    id: "project-proposal-generator",
    categories: ["Business", "Writing", "Education"],
    shortDescription:
      "Generate comprehensive project proposals tailored to your needs.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating project proposals. You will be provided with a brief overview of the project, and you will create a comprehensive project proposal based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a project proposal based on the following:\n";
      prompt += "Project Overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "tinder-dm-generator",
    name: "Tinder DM Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Adventurous traveler, foodie, looking for someone to explore new places with.",
        description:
          "A brief description of yourself or what you're looking for.",
        id: "about",
      },
    ],
    title: "Tinder DM Generator",
    id: "tinder-dm-generator",
    categories: ["Social Media", "Writing"],
    shortDescription:
      "Craft engaging and personalized direct messages for Tinder to enhance your dating experience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting engaging and personalized direct messages for Tinder. You will be provided with a brief description of the user and you will generate a Tinder DM based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a Tinder direct message based on the following:\n";
      prompt += "About: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "unabridged-audiobook-generator",
    name: "Unabridged Audiobook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A thrilling mystery novel set in Victorian London.",
        description:
          "Provide a brief description of the book or content to be converted into an audiobook.",
        id: "about",
      },
    ],
    title: "Unabridged Audiobook Generator",
    id: "unabridged-audiobook-generator",
    categories: ["Writing", "Education", "Business"],
    shortDescription:
      "Generate high-quality unabridged audiobooks from text content.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating unabridged audiobooks. You will be provided with a brief description of the book or content, and you will create a high-quality audiobook script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate an unabridged audiobook script based on the following:\n";
      prompt += "Content description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "non-fiction-audiobook-generator",
    name: "Non-Fiction Audiobook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This audiobook covers the principles of effective communication.",
        description: "A brief overview of the audiobook's content and purpose.",
        id: "about",
      },
    ],
    title: "Non-Fiction Audiobook Generator",
    id: "non-fiction-audiobook-generator",
    categories: ["Writing", "Education", "Marketing"],
    shortDescription:
      "Generate engaging scripts for non-fiction audiobooks to captivate listeners.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating scripts for non-fiction audiobooks. You will be provided with a brief overview of the audiobook's content and purpose, and you will create an engaging script that captivates listeners.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a script for a non-fiction audiobook based on the following:\n";
      prompt += "Overview: " + about + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "default-generator",
    name: "Default Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: A new product launch, a service overview, etc.",
        description:
          "A brief description of the topic or content you want to generate.",
        id: "about",
      },
    ],
    title: "Default Generator",
    id: "default-generator",
    categories: [
      "Social Media",
      "Email",
      "Website",
      "Sales",
      "Support",
      "Writing",
      "Education",
      "Marketing",
      "Legal",
      "Business",
      "Training Material",
    ],
    shortDescription:
      "Generate concise and effective content for various purposes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are a versatile content generator capable of creating concise and effective content for various purposes. You will be provided with a brief description of the topic or content, and you will generate appropriate text based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate content based on the following:\n";
      prompt += "About: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "script-for-television-broadcast-generator",
    name: "Script for Television Broadcast Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: A new product launch, a community event, etc.",
        description: "A brief overview of the broadcast topic.",
        id: "about",
      },
    ],
    title: "Script for Television Broadcast Generator",
    id: "script-for-television-broadcast-generator",
    categories: ["Writing", "Marketing"],
    shortDescription:
      "Generate engaging scripts for television broadcasts tailored to your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating scripts for television broadcasts. You will be provided with a brief overview of the broadcast topic, and you will create an engaging script tailored to the audience.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a script for a television broadcast based on the following:\n";
      prompt += "The broadcast topic is: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "customer-pain-points-generator",
    name: "Customer Pain Points Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Small business owners, tech-savvy millennials, etc.",
        description:
          "Provide a brief overview of the customer segment you are focusing on.",
        id: "about",
      },
    ],
    title: "Customer Pain Points Generator",
    id: "customer-pain-points-generator",
    categories: ["Marketing", "Sales", "Support"],
    shortDescription:
      "Identify and articulate customer pain points to enhance product development and marketing strategies.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in identifying and articulating customer pain points. You will be provided with information about a specific customer segment, and you will generate insights on their pain points based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Identify and articulate the pain points for the following customer segment:\n";
      prompt += "Customer segment: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "manpower-requisition-generator",
    name: "Manpower Requisition Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We need a software engineer to enhance our product features.",
        description:
          "Provide a brief overview of the position and its importance.",
        id: "about",
      },
    ],
    title: "Manpower Requisition Generator",
    id: "manpower-requisition-generator",
    categories: ["Business", "Support", "Training Material"],
    shortDescription:
      "Generate detailed manpower requisition forms to streamline hiring processes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating manpower requisition forms. You will be provided with details about the position and its requirements, and you will create a comprehensive requisition form based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a manpower requisition form based on the following:\n";
      prompt += "Position Overview: " + about + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "customer-objections-generator",
    name: "Customer Objections Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: A subscription-based meal delivery service.",
        description:
          "Provide details about the product or service to address objections.",
        id: "about",
      },
    ],
    title: "Customer Objections Generator",
    id: "customer-objections-generator",
    categories: ["Sales", "Support", "Business"],
    shortDescription:
      "Generate responses to common customer objections to improve sales conversations.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in handling customer objections. You will be provided with details about a product or service, and you will generate responses to common objections that customers may have.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate responses to common customer objections based on the following information:\n";
      prompt += "Product/Service Details: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "study-timetable-generator",
    name: "Study Timetable Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Mathematics, History, Biology, etc.",
        description:
          "A brief overview of the subjects or topics you need to study.",
        id: "about",
      },
    ],
    title: "Study Timetable Generator",
    id: "study-timetable-generator",
    categories: ["Education", "Training Material"],
    shortDescription:
      "Create a personalized study timetable to optimize your learning schedule.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized study timetables. You will be provided with information about the subjects to study, and you will generate a structured timetable based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a personalized study timetable based on the following:\n";
      prompt += "Subjects to study: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "sales-email-generator",
    name: "Sales Email Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A new software tool that increases productivity.",
        description:
          "Provide details about the product or service you are selling.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Length",
        optional: true,
        required: false,
        placeholder:
          "Example: Short (1-2 paragraphs), Medium (3-4 paragraphs), Long (5+ paragraphs).",
        description: "Specify the desired length of the email.",
        id: "length",
      },
    ],
    title: "Sales Email Generator",
    id: "sales-email-generator",
    categories: ["Email", "Sales", "Marketing"],
    shortDescription:
      "Craft compelling sales emails to engage potential customers and drive conversions.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting sales emails. You will be provided with details about a product or service, and you will generate a compelling sales email based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, length } = fields;
      let prompt = "Generate a sales email based on the following:\n";
      prompt += "About: " + about + ".\n";
      if (length) {
        prompt += "Desired length: " + length + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "job-description-generator",
    name: "Job Description Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This role is responsible for managing the marketing team and driving brand awareness.",
        description: "A brief overview of the job role and its importance.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Company",
        optional: true,
        required: false,
        placeholder:
          "Example: ABC Corp is a leading tech company specializing in innovative solutions.",
        description: "Information about the company offering the position.",
        id: "company",
      },
    ],
    title: "Job Description Generator",
    id: "job-description-generator",
    categories: ["Business", "Education"],
    shortDescription:
      "Generate clear and concise job descriptions to attract the right candidates.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating job descriptions. You will be provided with details about the job role and the company, and you will generate a clear and concise job description based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, company } = fields;
      let prompt = "Generate a job description based on the following:\n";
      prompt += "Job Role Overview: " + about + "\n";
      if (company) {
        prompt += "Company Information: " + company + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "instagram-replies-generator",
    name: "Instagram Replies Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Thank you for your support! We appreciate your feedback.",
        description:
          "Provide context or details about the post or comment you are replying to.",
        id: "about",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Friendly, Professional, Casual, Humorous",
        description:
          "Select the tone of voice for the reply (e.g., friendly, professional, humorous).",
        id: "toneofvoice",
      },
    ],
    title: "Instagram Replies Generator",
    id: "instagram-replies-generator",
    categories: ["Social Media", "Marketing"],
    shortDescription:
      "Generate engaging replies for Instagram comments to boost interaction.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating replies for Instagram comments. You will be provided with context about the post or comment, and you will generate a suitable reply based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, toneOfVoice } = fields;
      let prompt =
        "Generate a reply for an Instagram comment based on the following:\n";
      prompt += "Context: " + about + "\n";
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "full-cast-audiobook-generator",
    name: "Full-Cast Audiobook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: A thrilling mystery set in a small town.",
        description:
          "A brief description of the audiobook's content and theme.",
        id: "about",
      },
    ],
    title: "Full-Cast Audiobook Generator",
    id: "full-cast-audiobook-generator",
    categories: ["Writing", "Education", "Marketing"],
    shortDescription:
      "Generate engaging full-cast audiobooks with character voices and sound effects.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating full-cast audiobooks. You will be provided with a brief description of the audiobook's content and theme, and you will create an engaging script that includes character voices and sound effects.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a full-cast audiobook script based on the following:\n";
      prompt += "The audiobook is about: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "court-filing-generator",
    name: "Court Filing Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Divorce petition, Small claims case, etc.",
        description: "Provide a brief overview of the case or filing.",
        id: "about",
      },
    ],
    title: "Court Filing Generator",
    id: "court-filing-generator",
    categories: ["Legal", "Business"],
    shortDescription:
      "Generate accurate and compliant court filing documents quickly and easily.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating court filing documents. You will be provided with a brief overview of the case or filing, and you will create an accurate and compliant court filing document based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a court filing document based on the following:\n";
      prompt += "About the case: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "grammar-correction-generator",
    name: "Grammar Correction Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This is a sample text with some gramatical errors.",
        description: "Provide the text that needs grammar correction.",
        id: "about",
      },
    ],
    title: "Grammar Correction Generator",
    id: "grammar-correction-generator",
    categories: ["Writing", "Education"],
    shortDescription:
      "Enhance your writing by correcting grammar and punctuation errors.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grammar correction. You will be provided with a text that contains grammatical errors, and your task is to correct those errors and enhance the overall clarity of the writing.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Please correct the grammar and punctuation in the following text:\n";
      prompt += about + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "recipe-generator",
    name: "Recipe Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A quick and healthy dinner option, a dessert for special occasions, etc.",
        description: "A brief description of the recipe you want to create.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Avoid this",
        optional: true,
        required: false,
        placeholder: "Example: No nuts, gluten-free, dairy-free, etc.",
        description: "List any ingredients or dietary restrictions to avoid.",
        id: "avoidthis",
      },
      {
        type: "textArea",
        label: "Ingredients available",
        optional: true,
        required: false,
        placeholder: "Example: chicken, broccoli, rice, tomatoes, etc.",
        description: "The ingredients you currently have on hand.",
        id: "ingredientsavailable",
      },
    ],
    title: "Recipe Generator",
    id: "recipe-generator",
    categories: ["Education", "Cooking", "Marketing"],
    shortDescription:
      "Generate delicious recipes based on available ingredients and preferences.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert recipe creator. You will be provided with some base information about the recipe, including available ingredients and dietary restrictions, and you will generate a delicious recipe based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, avoidThis, ingredientsAvailable } = fields;
      let prompt = "Generate a recipe based on the following:\n";
      prompt += "Description: " + about + "\n";
      if (avoidThis) {
        prompt += "Avoid these ingredients: " + avoidThis + "\n";
      }
      if (ingredientsAvailable) {
        prompt += "Available ingredients: " + ingredientsAvailable + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "business-model-generator",
    name: "Business Model Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: A subscription service for organic snacks.",
        description: "A brief description of your business idea or concept.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Industry",
        optional: true,
        required: false,
        placeholder: "Example: Food and Beverage, Technology, Health Care.",
        description: "The industry in which your business operates.",
        id: "industry",
      },
    ],
    title: "Business Model Generator",
    id: "business-model-generator",
    categories: ["Business", "Education"],
    shortDescription:
      "Generate and refine business models tailored to your industry and goals.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating business models. You will be provided with a brief description of a business idea and the industry it operates in, and you will generate a comprehensive business model based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, industry } = fields;
      let prompt = "Generate a business model based on the following:\n";
      prompt += "Business idea: " + about + ".\n";
      if (industry) {
        prompt += "Industry: " + industry + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "cover-letter-generator",
    name: "Cover Letter Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: I am a recent graduate with a degree in Marketing...",
        description:
          "A brief introduction about yourself and your qualifications.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Position & company",
        optional: true,
        required: false,
        placeholder: "Example: Marketing Coordinator at ABC Corp.",
        description: "The job title and the company you are applying to.",
        id: "position&company",
      },
    ],
    title: "Cover Letter Generator",
    id: "cover-letter-generator",
    categories: ["Business", "Education", "Writing"],
    shortDescription:
      "Craft personalized cover letters to enhance your job applications.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting personalized cover letters. You will be provided with some basic information about the user and the position they are applying for, and you will generate a tailored cover letter based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, positionAndCompany } = fields;
      let prompt = "Generate a cover letter based on the following:\n";
      prompt += "Introduction: " + about + "\n";
      if (positionAndCompany) {
        prompt += "Position & Company: " + positionAndCompany + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "okr-generator",
    name: "OKR Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Increase customer satisfaction, Improve team collaboration, etc.",
        description: "A brief overview of the objective you want to achieve.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Duration",
        optional: true,
        required: false,
        placeholder: "Example: Q1 2024, 6 months, etc.",
        description: "The time frame for achieving the objective.",
        id: "duration",
      },
    ],
    title: "OKR Generator",
    id: "okr-generator",
    categories: ["Business", "Training Material", "Education"],
    shortDescription:
      "Generate clear and actionable Objectives and Key Results (OKRs) to drive team performance.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating Objectives and Key Results (OKRs). You will be provided with some base information, and you will generate clear and actionable OKRs based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, duration } = fields;
      let prompt = "Generate OKRs based on the following:\n";
      prompt += "Objective: " + about + ".\n";
      if (duration) {
        prompt += "Duration: " + duration + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "title-for-youtube-video-generator",
    name: "Title for YouTube Video Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A tutorial on how to bake a chocolate cake, Tips for improving productivity, etc.",
        description: "A brief description of the video content.",
        id: "about",
      },
    ],
    title: "Title for YouTube Video Generator",
    id: "title-for-youtube-video-generator",
    categories: ["Social Media", "Marketing", "Education"],
    shortDescription:
      "Generate catchy and engaging titles for your YouTube videos to attract viewers.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating catchy and engaging titles for YouTube videos. You will be provided with a brief description of the video content, and you will create a title that attracts viewers.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a catchy title for a YouTube video based on the following:\n";
      prompt += "Video content description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "question-generator",
    name: "Question Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Introduction to Biology, Basics of Programming, etc.",
        description:
          "Provide a brief overview of the topic for which you want to generate questions.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Question count",
        optional: true,
        required: false,
        placeholder: "Example: 10, 20, 50",
        description: "Specify the number of questions you want to generate.",
        id: "questioncount",
      },
      {
        type: "textArea",
        label: "Pattern",
        optional: true,
        required: false,
        placeholder:
          "Example: Multiple choice, Fill in the blanks, Short answer",
        description:
          "Define the format or type of questions (e.g., multiple choice, true/false).",
        id: "pattern",
      },
      {
        type: "textArea",
        label: "Difficulty level",
        optional: true,
        required: false,
        placeholder: "Example: Easy, Medium, Hard",
        description:
          "Indicate the difficulty level of the questions (e.g., easy, medium, hard).",
        id: "difficultylevel",
      },
    ],
    title: "Question Generator",
    id: "question-generator",
    categories: ["Education", "Training Material"],
    shortDescription:
      "Generate questions based on specified criteria for various purposes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating questions based on provided topics. You will receive specific criteria and generate relevant questions accordingly.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, questionCount, pattern, difficultyLevel } = fields;
      let prompt = "Generate questions based on the following criteria:\n";
      prompt += "Topic: " + about + "\n";
      if (questionCount) {
        prompt += "Number of questions: " + questionCount + "\n";
      }
      if (pattern) {
        prompt += "Question pattern: " + pattern + "\n";
      }
      if (difficultyLevel) {
        prompt += "Difficulty level: " + difficultyLevel + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "tv-commercial-generator",
    name: "TV Commercial Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A revolutionary fitness tracker that monitors your health.",
        description:
          "A brief description of the product or service being advertised.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Tagline",
        optional: false,
        required: true,
        placeholder: "Example: 'Track Your Progress, Transform Your Life!'",
        description:
          "A catchy phrase that encapsulates the essence of the advertisement.",
        id: "tagline",
      },
      {
        type: "textArea",
        label: "Company",
        optional: true,
        required: false,
        placeholder: "Example: FitTech Innovations",
        description: "The name of the company producing the advertisement.",
        id: "company",
      },
      {
        type: "textArea",
        label: "Feature",
        optional: true,
        required: false,
        placeholder:
          "Example: Heart rate monitoring, sleep tracking, personalized coaching.",
        description:
          "Key features or benefits of the product to highlight in the commercial.",
        id: "feature",
      },
      {
        type: "textArea",
        label: "Duration",
        optional: true,
        required: false,
        placeholder: "Example: 30 seconds, 60 seconds.",
        description: "The length of the commercial in seconds.",
        id: "duration",
      },
    ],
    title: "TV Commercial Generator",
    id: "tv-commercial-generator",
    categories: ["Marketing", "Business"],
    shortDescription:
      "Create engaging and impactful TV commercials to promote your brand.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating TV commercials. You will be provided with details about a product or service, and you will generate a compelling script for a TV commercial based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, tagline, company, feature, duration } = fields;
      let prompt = "Generate a TV commercial script based on the following:\n";
      prompt += "Product/Service Description: " + about + "\n";
      prompt += "Tagline: " + tagline + "\n";
      if (company) {
        prompt += "Company: " + company + "\n";
      }
      if (feature) {
        prompt += "Key Features: " + feature + "\n";
      }
      if (duration) {
        prompt += "Duration: " + duration + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "product-pricing-strategies-generator",
    name: "Product Pricing Strategies Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: A subscription-based meal delivery service.",
        description: "A brief description of the product or service.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Current price",
        optional: true,
        required: false,
        placeholder: "Example: $29.99, $199.99, etc.",
        description: "The current selling price of the product.",
        id: "currentprice",
      },
    ],
    title: "Product Pricing Strategies Generator",
    id: "product-pricing-strategies-generator",
    categories: ["Business", "Sales", "Marketing"],
    shortDescription:
      "Generate effective pricing strategies for your products to maximize sales and profitability.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in developing product pricing strategies. You will be provided with details about a product and its current pricing, and you will generate effective pricing strategies to maximize sales and profitability.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, currentPrice } = fields;
      let prompt =
        "Generate effective pricing strategies based on the following:\n";
      prompt += "Product description: " + about + ".\n";
      if (currentPrice) {
        prompt += "Current price: " + currentPrice + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "amazon-ads-generator",
    name: "Amazon Ads Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: High-quality wireless headphones with noise cancellation.",
        description:
          "A brief description of the product you want to advertise.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder: "Example: tech enthusiasts, music lovers, students.",
        description: "The specific group of customers you aim to reach.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Goal",
        optional: true,
        required: false,
        placeholder:
          "Example: Increase sales, boost brand awareness, drive traffic.",
        description: "The primary objective of your ad campaign.",
        id: "goal",
      },
    ],
    title: "Amazon Ads Generator",
    id: "amazon-ads-generator",
    categories: ["Marketing", "Sales"],
    shortDescription:
      "Create compelling ads for Amazon to effectively promote your products.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating compelling Amazon ads. You will be provided with details about a product and the goals of the ad campaign, and you will generate an effective advertisement based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, goal } = fields;
      let prompt = "Generate an Amazon ad based on the following:\n";
      prompt += "Product description: " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (goal) {
        prompt += "Goal of the ad campaign: " + goal + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "cease-and-desist-letter-generator",
    name: "Cease and Desist Letter Generator",
    fields: [
      {
        type: "textArea",
        label: "To",
        optional: false,
        required: true,
        placeholder: "Example: John Doe, ABC Corporation, etc.",
        description:
          "The name of the individual or entity receiving the letter.",
        id: "to",
      },
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Unauthorized use of trademark, copyright infringement, etc.",
        description:
          "A brief description of the issue that requires the cease and desist action.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Issued by",
        optional: true,
        required: false,
        placeholder: "Example: Jane Smith, XYZ LLC, etc.",
        description: "The name of the individual or entity issuing the letter.",
        id: "issuedby",
      },
    ],
    title: "Cease and Desist Letter Generator",
    id: "cease-and-desist-letter-generator",
    categories: ["Legal", "Business"],
    shortDescription:
      "Generate a formal cease and desist letter to address legal issues.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in drafting cease and desist letters. You will be provided with details about the recipient, the issue at hand, and the issuer of the letter, and you will generate a formal cease and desist letter based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { to, about, issuedBy } = fields;
      let prompt =
        "Generate a cease and desist letter based on the following:\n";
      prompt += "To: " + to + "\n";
      prompt += "Issue: " + about + "\n";
      if (issuedBy) {
        prompt += "Issued by: " + issuedBy + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "audio-original-generator",
    name: "Audio Original Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A podcast episode about technology trends, a meditation guide, etc.",
        description:
          "Provide a brief description of the audio content you want to generate.",
        id: "about",
      },
    ],
    title: "Audio Original Generator",
    id: "audio-original-generator",
    categories: [
      "Social Media",
      "Email",
      "Website",
      "Sales",
      "Support",
      "Writing",
      "Education",
      "Marketing",
      "Legal",
      "Business",
      "Training Material",
    ],
    shortDescription: "Generate unique audio content for various applications.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating original audio content. You will be provided with a brief description of the audio content, and you will create a unique audio script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate original audio content based on the following:\n";
      prompt += "Description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "scientific-report-generator",
    name: "Scientific Report Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: This study investigates the effects of X on Y.",
        description:
          "A brief overview of the research or experiment conducted.",
        id: "about",
      },
    ],
    title: "Scientific Report Generator",
    id: "scientific-report-generator",
    categories: ["Education", "Writing", "Business"],
    shortDescription:
      "Generate comprehensive scientific reports based on your research data.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating scientific reports. You will be provided with a brief overview of the research or experiment conducted, and you will generate a comprehensive scientific report based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a scientific report based on the following:\n";
      prompt += "Overview of the research: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "it-and-technical-training-generator",
    name: "IT and Technical Training Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Introduction to Python programming, Network security basics, etc.",
        description: "A brief overview of the training topic.",
        id: "about",
      },
    ],
    title: "IT and Technical Training Generator",
    id: "it-and-technical-training-generator",
    categories: ["Training Material", "Education", "Business"],
    shortDescription:
      "Generate comprehensive training materials for IT and technical subjects.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating training materials for IT and technical subjects. You will be provided with a brief overview of the training topic, and you will generate comprehensive training content based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate comprehensive training materials based on the following:\n";
      prompt += "Training topic: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "customer-review-response-generator",
    name: "Customer Review Response Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Customer feedback on product quality, service experience, etc.",
        description: "Provide context about the review you are responding to.",
        id: "about",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder:
          "Example: Friendly, Professional, Apologetic, Grateful, etc.",
        description: "Select the tone you want to convey in your response.",
        id: "toneofvoice",
      },
    ],
    title: "Customer Review Response Generator",
    id: "customer-review-response-generator",
    categories: ["Support", "Business", "Marketing"],
    shortDescription:
      "Generate thoughtful and engaging responses to customer reviews to enhance customer satisfaction.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting responses to customer reviews. You will be provided with context about the review and the desired tone of voice, and you will generate a thoughtful response that enhances customer satisfaction.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, toneOfVoice } = fields;
      let prompt =
        "Generate a response to a customer review based on the following:\n";
      prompt += "Context: " + about + "\n";
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "voice-over-ad-generator",
    name: "Voice-Over Ad Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A revolutionary fitness app that tracks your workouts and nutrition.",
        description:
          "A brief description of the product or service being advertised.",
        id: "about",
      },
    ],
    title: "Voice-Over Ad Generator",
    id: "voice-over-ad-generator",
    categories: ["Marketing", "Writing", "Business"],
    shortDescription:
      "Generate engaging voice-over scripts for advertisements to captivate your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating engaging voice-over scripts for advertisements. You will be provided with a brief description of the product or service, and you will generate a compelling voice-over script based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a voice-over script based on the following:\n";
      prompt += "The advertisement is about: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "job-posting-generator",
    name: "Job Posting Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We are looking for a skilled software developer to join our team.",
        description: "A brief overview of the job and its responsibilities.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Company",
        optional: true,
        required: false,
        placeholder:
          "Example: Tech Innovations Inc. - A leading tech company specializing in AI solutions.",
        description:
          "The name and a short description of the company offering the job.",
        id: "company",
      },
      {
        type: "textArea",
        label: "Profile description",
        optional: true,
        required: false,
        placeholder:
          "Example: Must have experience in Java, strong problem-solving skills, and a team player attitude.",
        description: "The qualifications and skills required for the position.",
        id: "profiledescription",
      },
    ],
    title: "Job Posting Generator",
    id: "job-posting-generator",
    categories: ["Business", "Education"],
    shortDescription:
      "Create compelling job postings to attract the right candidates.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating job postings. You will be provided with details about the job and the company, and you will generate a compelling job posting based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, company, profileDescription } = fields;
      let prompt = "Generate a job posting based on the following:\n";
      prompt += "Job Overview: " + about + "\n";
      if (company) {
        prompt += "Company: " + company + "\n";
      }
      if (profileDescription) {
        prompt += "Profile Description: " + profileDescription + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "feature-comparison-docs-generator",
    name: "Feature Comparison Docs Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A cloud storage solution that offers file sharing and collaboration.",
        description:
          "A brief overview of the product or service being compared.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Competitor(s)",
        optional: true,
        required: false,
        placeholder: "Example: Dropbox, Google Drive, OneDrive, etc.",
        description: "List the competitors you want to compare against.",
        id: "competitor(s)",
      },
    ],
    title: "Feature Comparison Docs Generator",
    id: "feature-comparison-docs-generator",
    categories: ["Business", "Marketing"],
    shortDescription:
      "Generate comprehensive comparison documents to evaluate features of different products or services.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating feature comparison documents. You will be provided with details about a product or service and its competitors, and you will create a comprehensive comparison document based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, competitors } = fields;
      let prompt =
        "Generate a feature comparison document based on the following:\n";
      prompt += "Product/Service Overview: " + about + "\n";
      if (competitors) {
        prompt += "Competitor(s): " + competitors + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "script-writer-generator",
    name: "Script Writer Generator",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        optional: false,
        required: true,
        placeholder: "Example: How to cook pasta, Benefits of meditation, etc.",
        description: "The main subject or theme of the script.",
        id: "topic",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder: "Example: beginners, professionals, teenagers, etc.",
        description: "The specific group of people the script is intended for.",
        id: "targetaudience",
      },
    ],
    title: "Script Writer Generator",
    id: "script-writer-generator",
    categories: ["Writing", "Education", "Marketing"],
    shortDescription:
      "Generate engaging scripts for various purposes tailored to your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert scriptwriter. You will be provided with a topic, target audience, and desired length, and you will generate a script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topic, targetAudience, length } = fields;
      let prompt = "Generate a script based on the following:\n";
      prompt += "The script should be about " + topic + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (length) {
        prompt += "Desired length: " + length + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "math-tutor-generator",
    name: "Math Tutor Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Algebra, Geometry, Calculus, etc.",
        description: "Provide a brief overview of the math topic or concept.",
        id: "about",
      },
    ],
    title: "Math Tutor Generator",
    id: "math-tutor-generator",
    categories: ["Education", "Training Material"],
    shortDescription:
      "Generate personalized math tutoring content for students of all levels.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized math tutoring content. You will be provided with a brief overview of a math topic or concept, and you will create tailored tutoring material based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate personalized math tutoring content based on the following:\n";
      prompt += "The topic is about " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "script-for-instagram-reel-generator",
    name: "Script for Instagram Reel Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A fun dance challenge, a cooking tutorial, a product review, etc.",
        description: "A brief description of the content you want to create.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder:
          "Example: teenagers, fitness enthusiasts, beauty lovers, etc.",
        description: "The demographic you aim to reach with your Reel.",
        id: "targetaudience",
      },
    ],
    title: "Script for Instagram Reel Generator",
    id: "script-for-instagram-reel-generator",
    categories: ["Social Media", "Marketing"],
    shortDescription:
      "Generate engaging scripts for Instagram Reels to captivate your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating engaging scripts for Instagram Reels. You will be provided with some base information and you will generate a script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience } = fields;
      let prompt =
        "Generate a script for an Instagram Reel based on the following:\n";
      prompt += "The content should be about: " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "employee-recognition-letter-generator",
    name: "Employee Recognition Letter Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: John has consistently exceeded sales targets and demonstrated leadership.",
        description: "A brief description of the employee's achievements.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Designation",
        optional: true,
        required: false,
        placeholder:
          "Example: Sales Manager, Software Engineer, Marketing Specialist.",
        description: "The job title of the employee being recognized.",
        id: "designation",
      },
      {
        type: "textArea",
        label: "Company name",
        optional: true,
        required: false,
        placeholder: "Example: ABC Corp, XYZ Ltd, Tech Innovations Inc.",
        description: "The name of the company issuing the recognition letter.",
        id: "companyname",
      },
    ],
    title: "Employee Recognition Letter Generator",
    id: "employee-recognition-letter-generator",
    categories: ["Business", "Support"],
    shortDescription:
      "Generate personalized recognition letters to appreciate employees' contributions.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating employee recognition letters. You will be provided with details about the employee's achievements and other relevant information to create a personalized recognition letter.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, designation, companyName } = fields;
      let prompt = "Generate a recognition letter based on the following:\n";
      prompt += "Achievements: " + about + "\n";
      if (designation) {
        prompt += "Designation: " + designation + "\n";
      }
      if (companyName) {
        prompt += "Company Name: " + companyName + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "fantasy-audiobook-generator",
    name: "Fantasy Audiobook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A quest to find a lost kingdom, a battle between dragons and wizards, etc.",
        description: "A brief overview of the fantasy story or theme.",
        id: "about",
      },
    ],
    title: "Fantasy Audiobook Generator",
    id: "fantasy-audiobook-generator",
    categories: ["Writing", "Education", "Marketing"],
    shortDescription:
      "Generate captivating audiobooks set in fantastical worlds with engaging narratives.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating captivating audiobooks set in fantastical worlds. You will be provided with a brief overview of the fantasy story or theme, and you will create an engaging narrative suitable for an audiobook.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a captivating audiobook narrative based on the following:\n";
      prompt += "The story is about: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "new-job-rejection-email-generator",
    name: "New Job - Rejection Email Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We appreciate your interest, but we have chosen another candidate.",
        description: "Provide a brief explanation of the reason for rejection.",
        id: "about",
      },
    ],
    title: "New Job - Rejection Email Generator",
    id: "new-job-rejection-email-generator",
    categories: ["Email", "Business"],
    shortDescription:
      "Generate professional rejection emails for job applicants.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting professional rejection emails for job applicants. You will be provided with a brief explanation of the reason for rejection, and you will generate a polite and respectful email response.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a rejection email based on the following:\n";
      prompt += "Reason for rejection: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "script-for-public-broadcast-generator",
    name: "Script for Public Broadcast Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Community event, Safety announcement, Educational program, etc.",
        description:
          "A brief overview of the topic you want to address in the broadcast.",
        id: "about",
      },
    ],
    title: "Script for Public Broadcast Generator",
    id: "script-for-public-broadcast-generator",
    categories: ["Marketing", "Education", "Business"],
    shortDescription:
      "Generate engaging scripts for public broadcasts to effectively communicate your message.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating scripts for public broadcasts. You will be provided with some base information and you will generate a script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a script for a public broadcast based on the following:\n";
      prompt += "The broadcast should be about: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "walmart-product-description-generator",
    name: "Walmart Product Description Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This fitness tracker monitors your heart rate, steps, and sleep patterns.",
        description: "A brief overview of the product's features and benefits.",
        id: "about",
      },
    ],
    title: "Walmart Product Description Generator",
    id: "walmart-product-description-generator",
    categories: ["Sales", "Marketing", "Website"],
    shortDescription:
      "Generate compelling product descriptions for Walmart listings to attract customers.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating product descriptions for Walmart listings. You will be provided with details about a product, and you will create a compelling description that highlights its features and benefits.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a Walmart product description based on the following:\n";
      prompt += "Product overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "curriculum-design-generator",
    name: "Curriculum Design Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Introduction to Biology, Advanced Mathematics, etc.",
        description: "A brief overview of the curriculum topic.",
        id: "about",
      },
    ],
    title: "Curriculum Design Generator",
    id: "curriculum-design-generator",
    categories: ["Education", "Training Material"],
    shortDescription:
      "Create comprehensive and engaging curriculum designs for various educational needs.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in curriculum design. You will be provided with a brief overview of the curriculum topic, and you will generate a comprehensive and engaging curriculum design based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a curriculum design based on the following:\n";
      prompt += "The curriculum topic is: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "whatsapp-message-generator",
    name: "WhatsApp Message Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Reminder for meeting, Follow-up on inquiry, Birthday wishes, etc.",
        description: "A brief description of the message you want to send.",
        id: "about",
      },
    ],
    title: "WhatsApp Message Generator",
    id: "whatsapp-message-generator",
    categories: ["Social Media", "Support", "Business"],
    shortDescription:
      "Generate engaging and personalized messages for WhatsApp communication.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating WhatsApp messages. You will be provided with a brief description of the message and you will generate a personalized WhatsApp message based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a WhatsApp message based on the following:\n";
      prompt += "Message description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "support-docs-generator",
    name: "Support Docs Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This document provides troubleshooting steps for common issues.",
        description: "A brief overview of the support document's purpose.",
        id: "about",
      },
    ],
    title: "Support Docs Generator",
    id: "support-docs-generator",
    categories: ["Support", "Training Material"],
    shortDescription:
      "Generate comprehensive support documentation to assist users effectively.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating support documentation. You will be provided with a brief overview of the document's purpose, and you will create comprehensive support documentation based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a support document based on the following:\n";
      prompt += "Purpose: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "jingle-generator",
    name: "Jingle Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A new eco-friendly cleaning product, A local coffee shop, etc.",
        description:
          "A brief description of the product or service for which the jingle is created.",
        id: "about",
      },
    ],
    title: "Jingle Generator",
    id: "jingle-generator",
    categories: ["Marketing", "Business", "Writing"],
    shortDescription:
      "Create catchy and memorable jingles for your brand or product.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating catchy and memorable jingles. You will be provided with a brief description of a product or service, and you will generate a jingle that captures its essence.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Create a jingle based on the following:\n";
      prompt += "Description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "appointment-reminder-call-generator",
    name: "Appointment Reminder Call Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Appointment with Dr. Smith on March 10th at 3 PM.",
        description: "Provide details about the appointment and client.",
        id: "about",
      },
    ],
    title: "Appointment Reminder Call Generator",
    id: "appointment-reminder-call-generator",
    categories: ["Support", "Business"],
    shortDescription:
      "Generate effective scripts for reminding clients about their appointments.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating appointment reminder call scripts. You will be provided with details about the appointment and client, and you will create a clear and effective reminder script.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate an appointment reminder call script based on the following:\n";
      prompt += "Details about the appointment: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "privacy-policy-generator",
    name: "Privacy Policy Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We provide online retail services for clothing and accessories.",
        description: "A brief description of your business and its services.",
        id: "about",
      },
    ],
    title: "Privacy Policy Generator",
    id: "privacy-policy-generator",
    categories: ["Legal", "Business"],
    shortDescription:
      "Generate a comprehensive privacy policy tailored to your business needs.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating privacy policies. You will be provided with a brief description of a business and its services, and you will create a comprehensive privacy policy tailored to the needs of that business.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a privacy policy based on the following:\n";
      prompt += "Business description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "360-performance-review-generator",
    name: "360 Performance Review Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: John has consistently exceeded sales targets and demonstrated strong leadership skills.",
        description:
          "A brief overview of the employee's role and contributions.",
        id: "about",
      },
    ],
    title: "360 Performance Review Generator",
    id: "360-performance-review-generator",
    categories: ["Business", "Training Material"],
    shortDescription:
      "Generate comprehensive performance reviews to provide constructive feedback and promote employee development.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating 360 performance reviews. You will be provided with information about an employee's role and contributions, and you will create a comprehensive performance review based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a 360 performance review based on the following:\n";
      prompt += "About the employee: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "script-for-internet-broadcast-generator",
    name: "Script for Internet Broadcast Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Today's topic is the impact of social media on mental health.",
        description:
          "A brief overview of the topic you will be discussing in the broadcast.",
        id: "about",
      },
    ],
    title: "Script for Internet Broadcast Generator",
    id: "script-for-internet-broadcast-generator",
    categories: ["Social Media", "Marketing", "Education"],
    shortDescription:
      "Generate engaging scripts for internet broadcasts to captivate your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating scripts for internet broadcasts. You will be provided with a topic and you will create an engaging script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a script for an internet broadcast based on the following:\n";
      prompt += "The topic is: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "podcast-ad-generator",
    name: "Podcast Ad Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A weekly discussion on tech trends, An interview series with industry leaders, etc.",
        description: "A brief description of your podcast.",
        id: "about",
      },
    ],
    title: "Podcast Ad Generator",
    id: "podcast-ad-generator",
    categories: ["Marketing", "Social Media", "Business"],
    shortDescription:
      "Create engaging and persuasive ads for your podcast to attract listeners and sponsors.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating engaging podcast ads. You will be provided with a brief description of the podcast, and you will generate a persuasive ad to attract listeners and sponsors.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a podcast ad based on the following:\n";
      prompt += "Podcast description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "email-follow-up-sequence-generator",
    name: "Email Follow-up Sequence Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Following up after a meeting, checking in on a proposal, etc.",
        description:
          "Provide a brief overview of the context for the follow-up emails.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Objective",
        optional: true,
        required: false,
        placeholder:
          "Example: Increase response rate, schedule a call, gather feedback, etc.",
        description: "State the main goal of the email sequence.",
        id: "objective",
      },
    ],
    title: "Email Follow-up Sequence Generator",
    id: "email-follow-up-sequence-generator",
    categories: ["Email", "Marketing", "Business"],
    shortDescription:
      "Generate effective follow-up email sequences to engage your audience and drive responses.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating email follow-up sequences. You will be provided with context and objectives, and you will create a series of follow-up emails based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, objective } = fields;
      let prompt =
        "Generate a sequence of follow-up emails based on the following:\n";
      prompt += "Context: " + about + "\n";
      if (objective) {
        prompt += "Objective: " + objective + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "ethics-and-code-of-conduct-generator",
    name: "Ethics and Code of Conduct Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We are a tech company focused on innovation and integrity.",
        description:
          "Provide a brief overview of your organization and its values.",
        id: "about",
      },
    ],
    title: "Ethics and Code of Conduct Generator",
    id: "ethics-and-code-of-conduct-generator",
    categories: ["Business", "Legal"],
    shortDescription:
      "Generate a comprehensive ethics and code of conduct document tailored to your organization.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating ethics and code of conduct documents. You will be provided with a brief overview of an organization and its values, and you will create a comprehensive document that reflects those principles.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a comprehensive ethics and code of conduct document based on the following:\n";
      prompt += "Overview of the organization: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "exit-interview-questionnaire-generator",
    name: "Exit Interview Questionnaire Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: To understand employee experiences and improve retention.",
        description:
          "Provide a brief overview of the purpose of the exit interview.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Company name",
        optional: true,
        required: false,
        placeholder: "Example: ABC Corp, XYZ Ltd, etc.",
        description:
          "The name of your organization conducting the exit interview.",
        id: "companyname",
      },
    ],
    title: "Exit Interview Questionnaire Generator",
    id: "exit-interview-questionnaire-generator",
    categories: ["Business", "Support", "Training Material"],
    shortDescription:
      "Generate insightful exit interview questions to gather feedback from departing employees.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating exit interview questionnaires. You will be provided with some base information and you will generate insightful questions based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, companyName } = fields;
      let prompt =
        "Generate insightful exit interview questions based on the following:\n";
      prompt += "Purpose of the exit interview: " + about + ".\n";
      if (companyName) {
        prompt += "Company name: " + companyName + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "legal-memo-generator",
    name: "Legal Memo Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Overview of the contract dispute, Summary of the case facts, etc.",
        description: "Provide a brief overview of the legal issue or case.",
        id: "about",
      },
    ],
    title: "Legal Memo Generator",
    id: "legal-memo-generator",
    categories: ["Legal", "Writing"],
    shortDescription:
      "Generate structured legal memos to streamline your legal writing process.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating legal memos. You will be provided with a brief overview of a legal issue or case, and you will generate a structured legal memo based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a legal memo based on the following:\n";
      prompt += "Overview of the legal issue: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "canned-responses-generator",
    name: "Canned Responses Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Response for customer inquiries about shipping times.",
        description: "A brief description of the canned response.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Channel",
        optional: true,
        required: false,
        placeholder: "Example: Email, Live Chat, Social Media, etc.",
        description:
          "The communication channel where this response will be used.",
        id: "channel",
      },
    ],
    title: "Canned Responses Generator",
    id: "canned-responses-generator",
    categories: ["Support", "Email", "Social Media"],
    shortDescription:
      "Quickly create pre-written responses for common inquiries to save time and improve communication.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating canned responses for customer inquiries. You will be provided with a brief description of the response and the communication channel, and you will generate a suitable canned response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, channel } = fields;
      let prompt = "Generate a canned response based on the following:\n";
      prompt += "Description: " + about + "\n";
      if (channel) {
        prompt += "Channel: " + channel + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "quiz-generator",
    name: "Quiz Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Basic math concepts, History of World War II, etc.",
        description: "A brief description of the quiz topic.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder: "Example: high school students, corporate employees, etc.",
        description: "The intended participants for the quiz.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Scoring",
        optional: true,
        required: false,
        placeholder:
          "Example: Each question is worth 1 point, Negative marking for wrong answers, etc.",
        description: "Instructions on how the quiz will be scored.",
        id: "scoring",
      },
    ],
    title: "Quiz Generator",
    id: "quiz-generator",
    categories: ["Education", "Training Material", "Marketing"],
    shortDescription:
      "Create engaging quizzes to assess knowledge and skills in various subjects.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating quizzes. You will be provided with some base information and you will generate a quiz based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, scoring } = fields;
      let prompt = "Generate a quiz based on the following:\n";
      prompt += "The quiz should be about " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (scoring) {
        prompt += "Scoring instructions: " + scoring + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "mission-statement-generator",
    name: "Mission Statement Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We are a non-profit focused on environmental conservation.",
        description: "A brief overview of your organization.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Product/service",
        optional: true,
        required: false,
        placeholder:
          "Example: Eco-friendly products, Community workshops, etc.",
        description: "The main product or service your organization offers.",
        id: "product/service",
      },
      {
        type: "textArea",
        label: "Target Audience",
        optional: true,
        required: false,
        placeholder: "Example: Local communities, Schools, Businesses, etc.",
        description: "The primary audience you aim to serve.",
        id: "targetaudience",
      },
    ],
    title: "Mission Statement Generator",
    id: "mission-statement-generator",
    categories: ["Business", "Writing", "Education"],
    shortDescription:
      "Craft a clear and impactful mission statement for your organization.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting mission statements. You will be provided with information about an organization and you will generate a clear and impactful mission statement based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, productService, targetAudience } = fields;
      let prompt = "Generate a mission statement based on the following:\n";
      prompt += "About the organization: " + about + "\n";
      if (productService) {
        prompt += "Main product/service: " + productService + "\n";
      }
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "vision-statement-generator",
    name: "Vision Statement Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A non-profit focused on environmental conservation.",
        description: "A brief description of your organization or project.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Vision",
        optional: true,
        required: false,
        placeholder:
          "Example: To create a sustainable future for all living beings.",
        description: "The long-term goal or aspiration of your organization.",
        id: "vision",
      },
    ],
    title: "Vision Statement Generator",
    id: "vision-statement-generator",
    categories: ["Business", "Education", "Training Material"],
    shortDescription:
      "Craft a clear and inspiring vision statement for your organization or project.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting vision statements. You will be provided with information about an organization or project, and you will generate a clear and inspiring vision statement based on the details provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, vision } = fields;
      let prompt = "Generate a vision statement based on the following:\n";
      prompt += "About: " + about + "\n";
      if (vision) {
        prompt += "Vision: " + vision + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "speech-generator",
    name: "Speech Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: The importance of teamwork, Innovations in technology, etc.",
        description: "A brief overview of the speech topic.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Duration",
        optional: true,
        required: false,
        placeholder: "Example: 5 minutes, 15 minutes, etc.",
        description: "The desired length of the speech in minutes.",
        id: "duration",
      },
    ],
    title: "Speech Generator",
    id: "speech-generator",
    categories: ["Education", "Business", "Training Material"],
    shortDescription:
      "Generate engaging and impactful speeches for various occasions.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating speeches. You will be provided with some base information and you will generate a speech based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, duration } = fields;
      let prompt = "Generate a speech based on the following:\n";
      prompt += "The speech should be about " + about + ".\n";
      if (duration) {
        prompt += "Desired duration: " + duration + " minutes.\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "email-subject-lines-generator",
    name: "Email Subject Lines Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Monthly newsletter, Product launch announcement, Special promotion, etc.",
        description: "A brief description of the email's content or purpose.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Intent",
        optional: true,
        required: false,
        placeholder: "Example: Inform, Promote, Engage, Invite, etc.",
        description: "The main goal of the email.",
        id: "intent",
      },
    ],
    title: "Email Subject Lines Generator",
    id: "email-subject-lines-generator",
    categories: ["Email", "Marketing"],
    shortDescription:
      "Generate compelling subject lines for your emails to increase open rates.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating email subject lines. You will be provided with some base information and you will generate compelling subject lines based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, intent } = fields;
      let prompt =
        "Generate compelling email subject lines based on the following:\n";
      prompt += "The email is about: " + about + ".\n";
      if (intent) {
        prompt += "The main intent of the email is: " + intent + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "industry-trend-analysis-generator",
    name: "Industry Trend Analysis Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Overview of the tech industry, Insights on the healthcare sector, etc.",
        description:
          "Provide a brief overview of the industry you are analyzing.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Industry",
        optional: true,
        required: false,
        placeholder: "Example: Technology, Healthcare, Finance, Retail, etc.",
        description:
          "Specify the industry for which you want to analyze trends.",
        id: "industry",
      },
    ],
    title: "Industry Trend Analysis Generator",
    id: "industry-trend-analysis-generator",
    categories: ["Business", "Education", "Marketing"],
    shortDescription:
      "Analyze and summarize current trends in your industry to inform strategic decisions.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing industry trends. You will be provided with information about a specific industry and you will generate a comprehensive analysis of the current trends within that industry.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, industry } = fields;
      let prompt =
        "Analyze the following industry trends based on the provided information:\n";
      prompt += "Overview: " + about + "\n";
      if (industry) {
        prompt += "Industry: " + industry + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "meal-plan-generator",
    name: "Meal Plan Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Vegan, gluten-free, weight loss, muscle gain, etc.",
        description:
          "A brief description of your dietary goals or restrictions.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Height & weight",
        optional: true,
        required: false,
        placeholder: "Example: Height: 5'8\", Weight: 150 lbs",
        description: "Your height and weight to help tailor the meal plan.",
        id: "height&weight",
      },
      {
        type: "textArea",
        label: "Duration",
        optional: true,
        required: false,
        placeholder: "Example: 1 week, 30 days, etc.",
        description: "The duration for which you need the meal plan.",
        id: "duration",
      },
    ],
    title: "Meal Plan Generator",
    id: "meal-plan-generator",
    categories: ["Health", "Education", "Training Material"],
    shortDescription:
      "Generate personalized meal plans based on your dietary needs and preferences.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized meal plans. You will be provided with dietary goals, restrictions, and other relevant information to generate a tailored meal plan.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, heightAndWeight, duration } = fields;
      let prompt =
        "Generate a personalized meal plan based on the following:\n";
      prompt += "Dietary goals or restrictions: " + about + ".\n";
      if (heightAndWeight) {
        prompt += "Height & weight: " + heightAndWeight + ".\n";
      }
      if (duration) {
        prompt += "Duration: " + duration + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "press-release-generator",
    name: "Press Release Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Launching a new product, partnership announcement, etc.",
        description: "A brief overview of the news or announcement.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Brief about company",
        optional: true,
        required: false,
        placeholder:
          "Example: A tech startup focused on innovative solutions, A non-profit organization dedicated to community service, etc.",
        description: "A short description of your company and its mission.",
        id: "briefaboutcompany",
      },
    ],
    title: "Press Release Generator",
    id: "press-release-generator",
    categories: ["Business", "Marketing"],
    shortDescription:
      "Generate professional press releases to announce news and updates about your company.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing press releases. You will be provided with key information about a news announcement, and you will generate a professional press release based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, briefAboutCompany } = fields;
      let prompt = "Generate a press release based on the following:\n";
      prompt += "Announcement: " + about + "\n";
      if (briefAboutCompany) {
        prompt += "Company Overview: " + briefAboutCompany + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "product-hunt-launch-generator",
    name: "Product Hunt Launch Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A revolutionary app that helps you track your fitness goals.",
        description:
          "A brief description of your product and its unique features.",
        id: "about",
      },
    ],
    title: "Product Hunt Launch Generator",
    id: "product-hunt-launch-generator",
    categories: ["Marketing", "Business", "Social Media"],
    shortDescription:
      "Generate compelling content to successfully launch your product on Product Hunt.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating content for Product Hunt launches. You will be provided with a brief description of a product and its unique features, and you will create compelling content to help launch the product successfully.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate compelling content for a Product Hunt launch based on the following:\n";
      prompt += "Product description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "hook-generator",
    name: "Hook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A revolutionary fitness app that tracks your workouts.",
        description:
          "A brief description of the content or product you want to promote.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder: "Example: health enthusiasts, busy professionals, etc.",
        description:
          "The specific group of people you want to reach with your message.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Social Media",
        optional: true,
        required: false,
        placeholder: "Example: Instagram, Twitter, Facebook, etc.",
        description: "The social media platform where the hook will be used.",
        id: "socialmedia",
      },
    ],
    title: "Hook Generator",
    id: "hook-generator",
    categories: ["Social Media", "Marketing", "Writing"],
    shortDescription:
      "Generate compelling hooks to capture attention and engage your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating engaging hooks for various content types. You will be provided with some base information and you will generate a compelling hook based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, socialMedia } = fields;
      let prompt = "Generate a compelling hook based on the following:\n";
      prompt += "The hook should promote: " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (socialMedia) {
        prompt += "Social media platform: " + socialMedia + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "customer-feedback-generator",
    name: "Customer Feedback Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: A new mobile app, an online course, etc.",
        description:
          "Provide a brief overview of the product or service for which you are gathering feedback.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Channel",
        optional: true,
        required: false,
        placeholder:
          "Example: Email survey, social media post, website form, etc.",
        description:
          "Specify the channel through which you are collecting feedback.",
        id: "channel",
      },
    ],
    title: "Customer Feedback Generator",
    id: "customer-feedback-generator",
    categories: ["Support", "Business", "Marketing"],
    shortDescription:
      "Collect and analyze customer feedback to improve your products and services.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in gathering and analyzing customer feedback. You will be provided with information about a product or service and the channel for collecting feedback, and you will generate a suitable feedback request.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, channel } = fields;
      let prompt =
        "Generate a customer feedback request based on the following:\n";
      prompt += "Product/Service: " + about + ".\n";
      if (channel) {
        prompt += "Feedback Channel: " + channel + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "product-landing-page-generator",
    name: "Product Landing Page Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This fitness app helps you track your workouts and nutrition.",
        description: "A brief description of the product and its benefits.",
        id: "about",
      },
    ],
    title: "Product Landing Page Generator",
    id: "product-landing-page-generator",
    categories: ["Website", "Marketing", "Sales"],
    shortDescription:
      "Create compelling landing pages to showcase your products and convert visitors into customers.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating product landing pages. You will be provided with a brief description of the product and its benefits, and you will generate a compelling landing page content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a product landing page based on the following:\n";
      prompt += "Product description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "engagement-letter-generator",
    name: "Engagement Letter Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Consulting services for project management.",
        description: "A brief description of the engagement.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Client",
        optional: true,
        required: false,
        placeholder: "Example: ABC Corporation, John Doe, etc.",
        description: "The name of the client receiving the engagement letter.",
        id: "client",
      },
      {
        type: "textArea",
        label: "Written by",
        optional: true,
        required: false,
        placeholder: "Example: Jane Smith, XYZ Consulting Firm.",
        description: "The name of the person or firm preparing the letter.",
        id: "writtenby",
      },
    ],
    title: "Engagement Letter Generator",
    id: "engagement-letter-generator",
    categories: ["Legal", "Business"],
    shortDescription:
      "Generate professional engagement letters for your clients quickly and easily.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engagement letters. You will be provided with details about the engagement and the client, and you will create a professional engagement letter based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, client, writtenBy } = fields;
      let prompt = "Generate an engagement letter based on the following:\n";
      prompt += "Engagement description: " + about + "\n";
      if (client) {
        prompt += "Client: " + client + "\n";
      }
      if (writtenBy) {
        prompt += "Written by: " + writtenBy + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "tweet-generator",
    name: "Tweet Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Launching a new product, Sharing a blog post, Announcing an event, etc.",
        description:
          "A brief description of the topic or message you want to convey.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder:
          "Example: tech enthusiasts, fitness lovers, local community, etc.",
        description:
          "The specific group of people you want to reach with your tweet.",
        id: "targetaudience",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Friendly, Professional, Witty, Inspirational",
        description:
          "The style or attitude of the tweet (e.g., formal, casual, humorous).",
        id: "toneofvoice",
      },
      {
        type: "textArea",
        label: "Tweet format",
        optional: true,
        required: false,
        placeholder: "Example: Ask a question, Share a tip, Promote an offer",
        description:
          "The structure or style of the tweet (e.g., question, statement, call to action).",
        id: "tweetformat",
      },
    ],
    title: "Tweet Generator",
    id: "tweet-generator",
    categories: ["Social Media", "Marketing"],
    shortDescription:
      "Create engaging tweets to connect with your audience on Twitter.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging tweets. You will be provided with some base information and you will generate a tweet based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, toneOfVoice, tweetFormat } = fields;
      let prompt = "Generate a tweet based on the following:\n";
      prompt += "The tweet should be about " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + ".\n";
      }
      if (tweetFormat) {
        prompt += "Tweet format: " + tweetFormat + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "event-invite-generator",
    name: "Event Invite Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Join us for a night of networking and fun!",
        description: "A brief description of the event.",
        id: "about",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Formal, Casual, Exciting, etc.",
        description: "The desired tone for the invitation.",
        id: "toneofvoice",
      },
      {
        type: "textArea",
        label: "Intended outcome",
        optional: true,
        required: false,
        placeholder: "Example: Increase attendance, Generate interest, etc.",
        description: "What you hope to achieve with this invitation.",
        id: "intendedoutcome",
      },
      {
        type: "textArea",
        label: "Dress code",
        optional: true,
        required: false,
        placeholder: "Example: Business casual, Cocktail attire, Casual, etc.",
        description: "Guidelines on what attendees should wear.",
        id: "dresscode",
      },
    ],
    title: "Event Invite Generator",
    id: "event-invite-generator",
    categories: ["Social Media", "Email", "Website"],
    shortDescription:
      "Create engaging and informative invitations for your events.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating event invitations. You will be provided with details about the event and you will generate an engaging invitation based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, toneOfVoice, intendedOutcome, dressCode } = fields;
      let prompt = "Generate an event invitation based on the following:\n";
      prompt += "Event description: " + about + "\n";
      if (intendedOutcome) {
        prompt += "Intended outcome: " + intendedOutcome + "\n";
      }
      if (dressCode) {
        prompt += "Dress code: " + dressCode + "\n";
      }
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "prd-generator",
    name: "PRD Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A mobile app that helps users track their fitness goals.",
        description: "A brief overview of the product and its purpose.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target Audience",
        optional: true,
        required: false,
        placeholder:
          "Example: Fitness enthusiasts, health-conscious individuals, etc.",
        description: "The primary users or customers for the product.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Features",
        optional: true,
        required: false,
        placeholder:
          "Example: User profiles, workout tracking, nutrition logging, etc.",
        description: "Key functionalities and attributes of the product.",
        id: "features",
      },
    ],
    title: "PRD Generator",
    id: "prd-generator",
    categories: ["Business", "Writing", "Education"],
    shortDescription:
      "Generate comprehensive product requirement documents to streamline development.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating Product Requirement Documents (PRDs). You will be provided with details about a product, including its purpose, target audience, and features, and you will create a comprehensive PRD based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, features } = fields;
      let prompt =
        "Generate a Product Requirement Document based on the following:\n";
      prompt += "About the product: " + about + "\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + "\n";
      }
      if (features) {
        prompt += "Key features: " + features + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "email-writer-generator",
    name: "Email Writer Generator",
    fields: [
      {
        type: "textArea",
        label: "Purpose of the email",
        optional: false,
        required: true,
        placeholder:
          "Example: Follow-up, Introduction, Newsletter, Promotion, etc.",
        description: "The main goal or intent behind the email.",
        id: "purposeoftheemail",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder:
          "Example: Clients, Team members, Subscribers, Prospects, etc.",
        description: "The specific group of people you are addressing.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Length",
        optional: true,
        required: false,
        placeholder:
          "Example: Short (1-2 paragraphs), Medium (3-5 paragraphs), Long (more than 5 paragraphs)",
        description: "The desired length of the email content.",
        id: "length",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Formal, Casual, Friendly, Persuasive, etc.",
        description: "The style or tone in which the email should be written.",
        id: "toneofvoice",
      },
    ],
    title: "Email Writer Generator",
    id: "email-writer-generator",
    categories: ["Email", "Business", "Marketing"],
    shortDescription:
      "Generate professional and engaging emails tailored to your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing professional emails. You will be provided with details about the purpose, target audience, length, and tone of voice for the email, and you will generate a well-structured email based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { purposeOfEmail, targetAudience, length, toneOfVoice } = fields;
      let prompt = "Generate an email based on the following:\n";
      prompt += "Purpose of the email: " + purposeOfEmail + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (length) {
        prompt += "Desired length: " + length + ".\n";
      }
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "mystery-audiobook-generator",
    name: "Mystery Audiobook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A detective investigates a series of strange disappearances in a small town.",
        description:
          "A brief overview of the mystery story you want to create.",
        id: "about",
      },
    ],
    title: "Mystery Audiobook Generator",
    id: "mystery-audiobook-generator",
    categories: ["Writing", "Education"],
    shortDescription:
      "Generate captivating mystery audiobooks with engaging plots and characters.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating captivating mystery audiobooks. You will be provided with a brief overview of the mystery story, and you will generate an engaging plot and characters based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a captivating mystery audiobook based on the following:\n";
      prompt += "The story is about: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "safety-and-emergency-procedures-generator",
    name: "Safety and Emergency Procedures Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Fire evacuation plan, First aid procedures, Hazardous material handling, etc.",
        description:
          "Provide a brief overview of the safety procedures needed.",
        id: "about",
      },
    ],
    title: "Safety and Emergency Procedures Generator",
    id: "safety-and-emergency-procedures-generator",
    categories: ["Business", "Training Material"],
    shortDescription:
      "Generate comprehensive safety and emergency procedures tailored to your organization.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating safety and emergency procedures. You will be provided with a brief overview of the safety procedures needed, and you will generate comprehensive procedures tailored to the organization.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate comprehensive safety and emergency procedures based on the following:\n";
      prompt += "Overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "tiktok-caption-generator",
    name: "TikTok Caption Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A fun dance challenge, a cooking tutorial, a travel vlog, etc.",
        description: "A brief description of your TikTok video content.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder:
          "Example: teenagers, young adults, fitness enthusiasts, etc.",
        description: "The demographic you want to reach with your video.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Goal",
        optional: true,
        required: false,
        placeholder:
          "Example: increase followers, promote a product, entertain, educate, etc.",
        description: "The main objective of your TikTok video.",
        id: "goal",
      },
      {
        type: "textArea",
        label: "Tone",
        optional: true,
        required: false,
        placeholder:
          "Example: humorous, inspirational, informative, casual, etc.",
        description: "The desired tone or style of the caption.",
        id: "tone",
      },
    ],
    title: "TikTok Caption Generator",
    id: "tiktok-caption-generator",
    categories: ["Social Media", "Marketing"],
    shortDescription:
      "Generate catchy and engaging captions for your TikTok videos to boost engagement.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating catchy TikTok captions. You will be provided with some base information about a TikTok video, and you will generate an engaging caption based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, goal, tone } = fields;
      let prompt = "Generate a TikTok caption based on the following:\n";
      prompt += "The video is about: " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (goal) {
        prompt += "Goal of the video: " + goal + ".\n";
      }
      if (tone) {
        prompt += "Desired tone: " + tone + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "instagram-caption-generator",
    name: "Instagram Caption Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A day at the beach, New product launch, Behind the scenes, etc.",
        description:
          "A brief description of the content or theme of your post.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder:
          "Example: millennials, fitness enthusiasts, food lovers, etc.",
        description: "The audience you want to engage with your caption.",
        id: "targetaudience",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder:
          "Example: playful, professional, inspirational, casual, etc.",
        description: "The style or tone you want to convey in your caption.",
        id: "toneofvoice",
      },
      {
        type: "textArea",
        label: "Goal",
        optional: true,
        required: false,
        placeholder:
          "Example: increase engagement, promote a product, share a story, etc.",
        description: "The main objective of your post's caption.",
        id: "goal",
      },
    ],
    title: "Instagram Caption Generator",
    id: "instagram-caption-generator",
    categories: ["Social Media", "Marketing"],
    shortDescription:
      "Craft engaging captions for your Instagram posts to boost interaction and reach.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating Instagram captions. You will be provided with some base information and you will generate a captivating Instagram caption based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, toneOfVoice, goal } = fields;
      let prompt = "Generate an Instagram caption based on the following:\n";
      prompt += "The caption should be about " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + ".\n";
      }
      if (goal) {
        prompt += "Goal: " + goal + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "presentation-slides-text-generator",
    name: "Presentation Slides Text Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Overview of renewable energy sources.",
        description: "A brief overview of the topic being presented.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Purpose",
        optional: true,
        required: false,
        placeholder: "Example: To inform the audience about climate change.",
        description: "The main goal or objective of the presentation.",
        id: "purpose",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder: "Example: High school students, corporate employees, etc.",
        description: "The intended audience for the presentation.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Length",
        optional: true,
        required: false,
        placeholder: "Example: 15 minutes, 30 minutes, etc.",
        description: "The desired length of the presentation in minutes.",
        id: "length",
      },
    ],
    title: "Presentation Slides Text Generator",
    id: "presentation-slides-text-generator",
    categories: ["Education", "Business", "Training Material"],
    shortDescription:
      "Generate engaging and informative text for your presentation slides.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating text for presentation slides. You will be provided with some base information and you will generate engaging and informative text suitable for presentation slides based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, purpose, targetAudience, length } = fields;
      let prompt =
        "Generate text for presentation slides based on the following:\n";
      prompt += "The presentation is about: " + about + ".\n";
      if (purpose) {
        prompt += "Purpose of the presentation: " + purpose + ".\n";
      }
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (length) {
        prompt +=
          "Desired length of the presentation: " + length + " minutes.\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "wedding-vows-generator",
    name: "Wedding Vows Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We met in college, shared a love for travel, etc.",
        description:
          "A brief description of your relationship and what makes it special.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Moments together",
        optional: true,
        required: false,
        placeholder:
          "Example: Our first trip together, the day we got engaged, etc.",
        description:
          "Significant moments or memories you want to include in your vows.",
        id: "momentstogether",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Romantic, Humorous, Sincere, etc.",
        description: "The emotional tone you want your vows to convey.",
        id: "toneofvoice",
      },
    ],
    title: "Wedding Vows Generator",
    id: "wedding-vows-generator",
    categories: ["Writing", "Education"],
    shortDescription:
      "Craft heartfelt and personalized wedding vows to express your love.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting wedding vows. You will be provided with details about the couple's relationship and significant moments, and you will generate heartfelt and personalized wedding vows based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, momentsTogether, toneOfVoice } = fields;
      let prompt = "Generate wedding vows based on the following:\n";
      prompt += "About the relationship: " + about + ".\n";
      if (momentsTogether) {
        prompt += "Significant moments: " + momentsTogether + ".\n";
      }
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "push-notifications-copy-generator",
    name: "Push Notifications Copy Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Reminder for upcoming event, New feature announcement, etc.",
        description: "A brief description of the notification's purpose.",
        id: "about",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Friendly, Urgent, Informative, etc.",
        description: "The desired tone for the notification.",
        id: "toneofvoice",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder: "Example: Active users, New subscribers, etc.",
        description:
          "The specific group of users you want to reach with this notification.",
        id: "targetaudience",
      },
    ],
    title: "Push Notifications Copy Generator",
    id: "push-notifications-copy-generator",
    categories: ["Marketing", "Support"],
    shortDescription:
      "Craft engaging push notifications to boost user engagement and retention.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting push notifications. You will be provided with details about the notification's purpose, target audience, and tone of voice, and you will generate a compelling push notification based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, toneOfVoice } = fields;
      let prompt = "Generate a push notification based on the following:\n";
      prompt += "The notification should be about: " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "performance-appraisal-letter-generator",
    name: "Performance Appraisal Letter Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: John has been a software developer for 3 years, contributing to various projects.",
        description:
          "A brief overview of the employee's role and contributions.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Overall Performance",
        optional: true,
        required: false,
        placeholder:
          "Example: Exceeds expectations, meets goals consistently, etc.",
        description:
          "An assessment of the employee's overall performance during the appraisal period.",
        id: "overallperformance",
      },
      {
        type: "textArea",
        label: "Feedback",
        optional: true,
        required: false,
        placeholder:
          "Example: Strong problem-solving skills, needs to improve time management.",
        description:
          "Specific feedback on strengths and areas for improvement.",
        id: "feedback",
      },
      {
        type: "textArea",
        label: "Expectations",
        optional: true,
        required: false,
        placeholder:
          "Example: Complete project X by Q2, enhance communication skills.",
        description:
          "Goals and expectations for the upcoming appraisal period.",
        id: "expectations",
      },
    ],
    title: "Performance Appraisal Letter Generator",
    id: "performance-appraisal-letter-generator",
    categories: ["Business", "Support"],
    shortDescription:
      "Generate professional performance appraisal letters to provide feedback and set expectations.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating performance appraisal letters. You will be provided with details about an employee's performance and contributions, and you will create a professional appraisal letter based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, overallPerformance, feedback, expectations } = fields;
      let prompt =
        "Generate a performance appraisal letter based on the following:\n";
      prompt += "About the employee: " + about + "\n";
      if (overallPerformance) {
        prompt += "Overall performance: " + overallPerformance + "\n";
      }
      if (feedback) {
        prompt += "Feedback: " + feedback + "\n";
      }
      if (expectations) {
        prompt +=
          "Expectations for the upcoming period: " + expectations + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "story-generator",
    name: "Story Generator",
    fields: [
      {
        type: "textArea",
        label: "Audience",
        optional: true,
        required: false,
        placeholder: "Example: children, young adults, fantasy lovers, etc.",
        description: "The intended audience for the story.",
        id: "audience",
      },
      {
        type: "textArea",
        label: "Genre",
        optional: true,
        required: false,
        placeholder:
          "Example: fantasy, mystery, romance, science fiction, etc.",
        description: "The genre of the story you want to create.",
        id: "genre",
      },
      {
        type: "textArea",
        label: "Story length",
        optional: true,
        required: false,
        placeholder:
          "Example: short story (500 words), novella (10,000 words), etc.",
        description: "The desired length of the story.",
        id: "storylength",
      },
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: a hero's journey, a love story set in the future, etc.",
        description: "A brief overview or theme of the story.",
        id: "about",
      },
    ],
    title: "Story Generator",
    id: "story-generator",
    categories: ["Writing", "Education"],
    shortDescription:
      "Generate engaging and creative stories based on user input.",
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative storyteller. You will be provided with details about the audience, genre, story length, and a brief overview of the story theme. Your task is to generate an engaging and imaginative story based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { audience, genre, storyLength, about } = fields;
      let prompt = "Generate a story based on the following:\n";
      prompt += "Theme: " + about + ".\n";
      if (audience) {
        prompt += "Intended audience: " + audience + ".\n";
      }
      if (genre) {
        prompt += "Genre: " + genre + ".\n";
      }
      if (storyLength) {
        prompt += "Desired length: " + storyLength + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "author-narrated-audiobook-generator",
    name: "Author-Narrated Audiobook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A thrilling mystery novel, A self-help guide, etc.",
        description: "A brief description of the audiobook content.",
        id: "about",
      },
    ],
    title: "Author-Narrated Audiobook Generator",
    id: "author-narrated-audiobook-generator",
    categories: ["Education", "Writing", "Marketing"],
    shortDescription:
      "Generate engaging audiobooks narrated by the author for a personal touch.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating audiobooks narrated by authors. You will be provided with a brief description of the audiobook content, and you will create a compelling narrative that captures the essence of the author's voice.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate an engaging audiobook narration based on the following:\n";
      prompt += "Audiobook content description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "subpoena-generator",
    name: "Subpoena Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Subpoena for documents, Subpoena for testimony, etc.",
        description: "Provide details about the subpoena you need to generate.",
        id: "about",
      },
    ],
    title: "Subpoena Generator",
    id: "subpoena-generator",
    categories: ["Legal", "Business"],
    shortDescription:
      "Generate legal subpoenas quickly and accurately for various purposes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating legal subpoenas. You will be provided with details about the subpoena needed, and you will generate a complete and accurate subpoena based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a legal subpoena based on the following:\n";
      prompt += "Details about the subpoena: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "transactional-emails-generator",
    name: "Transactional Emails Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Order confirmation, Password reset, Subscription renewal, etc.",
        description:
          "A brief description of the transaction or purpose of the email.",
        id: "about",
      },
    ],
    title: "Transactional Emails Generator",
    id: "transactional-emails-generator",
    categories: ["Email", "Business", "Support"],
    shortDescription:
      "Generate professional transactional emails for various purposes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating transactional emails. You will be provided with a brief description of the transaction or purpose of the email, and you will generate a professional email based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a transactional email based on the following:\n";
      prompt += "The email should be about: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "script-for-tiktok-video-generator",
    name: "Script for TikTok Video Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A day in the life, DIY project, Cooking tutorial, etc.",
        description: "A brief overview of the video content.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder: "Example: teenagers, young adults, parents, etc.",
        description: "The demographic you want to reach with your video.",
        id: "targetaudience",
      },
    ],
    title: "Script for TikTok Video Generator",
    id: "script-for-tiktok-video-generator",
    categories: ["Social Media", "Marketing", "Writing"],
    shortDescription:
      "Generate engaging scripts for TikTok videos to captivate your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating engaging scripts for TikTok videos. You will be provided with some base information and you will generate a script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience } = fields;
      let prompt = "Generate a TikTok video script based on the following:\n";
      prompt += "The video content should be about " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "brd-generator",
    name: "BRD Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This project aims to develop a new mobile application for task management.",
        description: "A brief overview of the project or product.",
        id: "about",
      },
    ],
    title: "BRD Generator",
    id: "brd-generator",
    categories: ["Business", "Writing", "Training Material"],
    shortDescription:
      "Generate Business Requirements Documents (BRDs) to outline project needs and specifications.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating Business Requirements Documents (BRDs). You will be provided with a brief overview of the project, and you will generate a comprehensive BRD based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a Business Requirements Document based on the following:\n";
      prompt += "Project Overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "expert-opinion-generator",
    name: "Expert Opinion Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: The impact of remote work on productivity, Trends in renewable energy, etc.",
        description:
          "Provide a brief overview of the topic for which you need an expert opinion.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Expert",
        optional: true,
        required: false,
        placeholder:
          "Example: Economist, Environmental Scientist, Business Consultant, etc.",
        description:
          "Specify the type of expert whose opinion you are seeking.",
        id: "expert",
      },
    ],
    title: "Expert Opinion Generator",
    id: "expert-opinion-generator",
    categories: ["Writing", "Education", "Business"],
    shortDescription:
      "Generate insightful and professional opinions on various topics.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating insightful opinions on various topics. You will be provided with a brief overview of a topic and the type of expert opinion needed, and you will generate a professional response based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, expert } = fields;
      let prompt = "Generate an expert opinion based on the following:\n";
      prompt += "Topic: " + about + ".\n";
      if (expert) {
        prompt += "Expert type: " + expert + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "bedtime-story-teller-generator",
    name: "Bedtime Story Teller Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A brave knight, a curious princess, a magical forest, etc.",
        description: "A brief description of the story you want to create.",
        id: "about",
      },
    ],
    title: "Bedtime Story Teller Generator",
    id: "bedtime-story-teller-generator",
    categories: ["Education", "Writing"],
    shortDescription:
      "Generate enchanting bedtime stories for children to spark their imagination.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating enchanting bedtime stories. You will be provided with a brief description of the story, and you will generate a captivating bedtime story based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a bedtime story based on the following:\n";
      prompt += "Story description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "lesson-worksheet-generator",
    name: "Lesson Worksheet Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Introduction to fractions, The water cycle, etc.",
        description:
          "A brief overview of the lesson or topic covered in the worksheet.",
        id: "about",
      },
    ],
    title: "Lesson Worksheet Generator",
    id: "lesson-worksheet-generator",
    categories: ["Education", "Training Material"],
    shortDescription:
      "Create customized worksheets for educational purposes to enhance student learning.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating educational worksheets. You will be provided with a brief overview of the lesson or topic, and you will generate a customized worksheet based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a lesson worksheet based on the following:\n";
      prompt += "The worksheet should cover: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "product-documentation-generator",
    name: "Product Documentation Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This product helps users manage their tasks efficiently.",
        description: "A brief overview of the product and its purpose.",
        id: "about",
      },
    ],
    title: "Product Documentation Generator",
    id: "product-documentation-generator",
    categories: ["Business", "Training Material", "Support"],
    shortDescription:
      "Generate comprehensive documentation for your products quickly and efficiently.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating product documentation. You will be provided with a brief overview of the product and its purpose, and you will create comprehensive documentation based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate product documentation based on the following:\n";
      prompt += "About the product: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "script-for-satellite-broadcast-generator",
    name: "Script for Satellite Broadcast Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Weather updates, News report, Educational segment, etc.",
        description: "A brief overview of the broadcast content.",
        id: "about",
      },
    ],
    title: "Script for Satellite Broadcast Generator",
    id: "script-for-satellite-broadcast-generator",
    categories: ["Business", "Education"],
    shortDescription:
      "Generate scripts for satellite broadcasts with ease and precision.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating scripts for satellite broadcasts. You will be provided with some base information and you will generate a script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a script for a satellite broadcast based on the following:\n";
      prompt += "The broadcast content should be about: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "order-confirmation-and-update-call-generator",
    name: "Order Confirmation and Update Call Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Order #12345, Shipping status, Delivery date, etc.",
        description: "Provide details about the order or update.",
        id: "about",
      },
    ],
    title: "Order Confirmation and Update Call Generator",
    id: "order-confirmation-and-update-call-generator",
    categories: ["Support", "Business"],
    shortDescription:
      "Generate professional order confirmation and update calls to enhance customer communication.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating order confirmation and update calls. You will be provided with details about an order or update, and you will generate a professional call script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a professional order confirmation or update call based on the following:\n";
      prompt += "Details: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "develop-sales-process-generator",
    name: "Develop Sales Process Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A step-by-step guide for closing deals, including lead generation and follow-up strategies.",
        description:
          "Provide a brief overview of the sales process you want to develop.",
        id: "about",
      },
    ],
    title: "Develop Sales Process Generator",
    id: "develop-sales-process-generator",
    categories: ["Sales", "Business"],
    shortDescription:
      "Create a structured sales process to enhance your sales team's efficiency.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in developing sales processes. You will be provided with a brief overview of the sales process that needs to be developed, and you will create a structured sales process based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Develop a sales process based on the following:\n";
      prompt += "Overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "science-experiment-generator",
    name: "Science Experiment Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A simple volcano eruption using baking soda and vinegar.",
        description: "A brief overview of the science experiment.",
        id: "about",
      },
    ],
    title: "Science Experiment Generator",
    id: "science-experiment-generator",
    categories: ["Education", "Training Material"],
    shortDescription:
      "Generate creative and educational science experiments for students of all ages.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating science experiments. You will be provided with a brief overview of the experiment, and you will create a detailed description of the experiment including materials needed, steps to follow, and expected outcomes.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a science experiment based on the following:\n";
      prompt += "Overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "abridged-audiobook-generator",
    name: "Abridged Audiobook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A detailed overview of a classic novel, a comprehensive guide on personal finance, etc.",
        description: "A brief summary of the content to be abridged.",
        id: "about",
      },
    ],
    title: "Abridged Audiobook Generator",
    id: "abridged-audiobook-generator",
    categories: ["Education", "Writing"],
    shortDescription:
      "Generate concise and engaging abridged audiobooks from full-length texts.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating abridged audiobooks. You will be provided with a summary of the content to be abridged, and you will create a concise and engaging audiobook version based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate an abridged audiobook based on the following:\n";
      prompt += "Content to be abridged: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "customer-service-training-generator",
    name: "Customer Service Training Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This training covers communication skills, problem-solving techniques, and customer engagement strategies.",
        description: "Provide an overview of the training program.",
        id: "about",
      },
    ],
    title: "Customer Service Training Generator",
    id: "customer-service-training-generator",
    categories: ["Training Material", "Education", "Support"],
    shortDescription:
      "Generate effective training materials for customer service representatives.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating customer service training materials. You will be provided with an overview of the training program, and you will generate effective training content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate training materials for customer service representatives based on the following:\n";
      prompt += "Overview of the training program: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "script-for-podcasting-generator",
    name: "Script for Podcasting Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Discussing the impact of technology on education.",
        description: "A brief overview of the podcast episode's topic.",
        id: "about",
      },
    ],
    title: "Script for Podcasting Generator",
    id: "script-for-podcasting-generator",
    categories: ["Social Media", "Writing", "Education", "Marketing"],
    shortDescription:
      "Generate engaging and structured scripts for your podcast episodes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating podcast scripts. You will be provided with a brief overview of the podcast episode's topic, and you will create a structured and engaging script based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a podcast script based on the following:\n";
      prompt += "The episode will be about: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "product-training-generator",
    name: "Product Training Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This product helps users manage their time effectively.",
        description: "A brief overview of the product and its features.",
        id: "about",
      },
    ],
    title: "Product Training Generator",
    id: "product-training-generator",
    categories: ["Training Material", "Education", "Business"],
    shortDescription:
      "Generate comprehensive training materials for your products.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating product training materials. You will be provided with a brief overview of the product and its features, and you will generate comprehensive training content based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate comprehensive training materials based on the following:\n";
      prompt += "About the product: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "sales-training-generator",
    name: "Sales Training Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This program focuses on improving negotiation skills and closing techniques.",
        description: "Provide a brief overview of the sales training program.",
        id: "about",
      },
    ],
    title: "Sales Training Generator",
    id: "sales-training-generator",
    categories: ["Training Material", "Business", "Education"],
    shortDescription:
      "Generate effective sales training content to enhance team performance.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating sales training content. You will be provided with details about the training program, and you will generate effective training materials based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate sales training content based on the following:\n";
      prompt += "Overview of the program: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "resignation-letter-generator",
    name: "Resignation Letter Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: I am resigning due to personal reasons, I have accepted a new job offer, etc.",
        description: "A brief explanation of your reason for resigning.",
        id: "about",
      },
    ],
    title: "Resignation Letter Generator",
    id: "resignation-letter-generator",
    categories: ["Business", "Legal"],
    shortDescription:
      "Easily create a professional resignation letter to notify your employer of your departure.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing professional resignation letters. You will be provided with a reason for resigning, and you will generate a formal resignation letter based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields.about;
      let prompt = "Generate a resignation letter based on the following:\n";
      prompt += "Reason for resigning: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "business-plan-generator",
    name: "Business Plan Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A tech startup focused on AI solutions for small businesses.",
        description: "A brief overview of your business idea.",
        id: "about",
      },
    ],
    title: "Business Plan Generator",
    id: "business-plan-generator",
    categories: ["Business", "Writing", "Education"],
    shortDescription:
      "Create a comprehensive business plan to outline your business goals and strategies.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating business plans. You will be provided with a brief overview of a business idea, and you will generate a comprehensive business plan based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a comprehensive business plan based on the following:\n";
      prompt += "Business idea: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "quality-audit-form-generator",
    name: "Quality Audit Form Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This audit focuses on product quality, service delivery, etc.",
        description: "Provide a brief overview of the quality audit.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Channel",
        optional: true,
        required: false,
        placeholder:
          "Example: Online survey, In-person interview, Email questionnaire, etc.",
        description:
          "Specify the channel through which the audit will be conducted.",
        id: "channel",
      },
    ],
    title: "Quality Audit Form Generator",
    id: "quality-audit-form-generator",
    categories: ["Business", "Support"],
    shortDescription:
      "Generate comprehensive quality audit forms to ensure compliance and improve processes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating quality audit forms. You will be provided with some base information and you will generate a comprehensive quality audit form based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, channel } = fields;
      let prompt = "Generate a quality audit form based on the following:\n";
      prompt += "Overview of the audit: " + about + ".\n";
      if (channel) {
        prompt += "Channel for conducting the audit: " + channel + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "product-launch-emailers-generator",
    name: "Product Launch Emailers Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A revolutionary fitness tracker that monitors your health.",
        description: "A brief description of the product being launched.",
        id: "about",
      },
    ],
    title: "Product Launch Emailers Generator",
    id: "product-launch-emailers-generator",
    categories: ["Email", "Marketing"],
    shortDescription:
      "Generate engaging email content for product launches to captivate your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating email content for product launches. You will be provided with a brief description of the product and you will create an engaging email to captivate the audience.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate an engaging email for a product launch based on the following:\n";
      prompt += "Product description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "aida-framework-generator",
    name: "AIDA Framework Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Innovative fitness tracker that monitors heart rate and sleep patterns.",
        description:
          "A brief description of the product or service you are promoting.",
        id: "about",
      },
    ],
    title: "AIDA Framework Generator",
    id: "aida-framework-generator",
    categories: ["Marketing", "Writing", "Sales"],
    shortDescription:
      "Generate compelling marketing content using the AIDA framework to capture attention, generate interest, create desire, and prompt action.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating marketing content using the AIDA framework. You will be provided with a brief description of a product or service, and you will generate compelling content that captures attention, generates interest, creates desire, and prompts action.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate marketing content using the AIDA framework based on the following:\n";
      prompt += "About: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "hashtag-generator",
    name: "Hashtag Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: A new fitness program, a travel blog post, etc.",
        description:
          "A brief description of the content or topic for which you need hashtags.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Platform",
        optional: true,
        required: false,
        placeholder: "Example: Instagram, Twitter, Facebook, etc.",
        description:
          "The social media platform where you will use these hashtags.",
        id: "platform",
      },
    ],
    title: "Hashtag Generator",
    id: "hashtag-generator",
    categories: ["Social Media", "Marketing"],
    shortDescription:
      "Generate relevant hashtags to increase visibility and engagement on social media posts.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating relevant hashtags for social media content. You will be provided with a brief description of the content and the platform, and you will generate appropriate hashtags to enhance visibility and engagement.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, platform } = fields;
      let prompt = "Generate relevant hashtags based on the following:\n";
      prompt += "Content description: " + about + "\n";
      if (platform) {
        prompt += "Platform: " + platform + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "informational-call-generator",
    name: "Informational Call Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Company updates, Project status, New initiatives, etc.",
        description:
          "A brief overview of the topic to be discussed during the call.",
        id: "about",
      },
    ],
    title: "Informational Call Generator",
    id: "informational-call-generator",
    categories: ["Business", "Training Material", "Support"],
    shortDescription:
      "Generate structured outlines for informational calls to enhance communication and clarity.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating structured outlines for informational calls. You will be provided with a brief overview of the topic to be discussed, and you will create a clear and organized outline for the call based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a structured outline for an informational call based on the following:\n";
      prompt += "Topic: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "landing-page-copywriting-generator",
    name: "Landing Page Copywriting Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A revolutionary fitness app that helps you track your workouts and nutrition.",
        description:
          "A brief description of the product or service being offered.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Goal",
        optional: true,
        required: false,
        placeholder:
          "Example: To collect email addresses, to sell a product, to promote a webinar.",
        description: "The primary objective of the landing page.",
        id: "goal",
      },
    ],
    title: "Landing Page Copywriting Generator",
    id: "landing-page-copywriting-generator",
    categories: ["Website", "Marketing"],
    shortDescription:
      "Generate compelling landing page copy to convert visitors into leads or customers.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating compelling landing page copy. You will be provided with details about a product or service and the goal of the landing page, and you will generate persuasive copy that encourages visitors to take action.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, goal } = fields;
      let prompt = "Generate landing page copy based on the following:\n";
      prompt += "Product/Service Description: " + about + "\n";
      if (goal) {
        prompt += "Primary Goal: " + goal + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "amazon-product-headline-generator",
    name: "Amazon Product Headline Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This is a high-quality fitness tracker designed for athletes.",
        description: "A brief description of the product.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Key features",
        optional: true,
        required: false,
        placeholder:
          "Example: Waterproof, heart rate monitor, GPS tracking, long battery life.",
        description: "The main features that highlight the product's benefits.",
        id: "keyfeatures",
      },
    ],
    title: "Amazon Product Headline Generator",
    id: "amazon-product-headline-generator",
    categories: ["Sales", "Marketing"],
    shortDescription:
      "Generate compelling headlines for your Amazon product listings to attract more customers.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating compelling headlines for Amazon product listings. You will be provided with a brief description of the product and its key features, and you will generate an attention-grabbing headline based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, keyFeatures } = fields;
      let prompt =
        "Generate a compelling headline for an Amazon product based on the following:\n";
      prompt += "Product description: " + about + ".\n";
      if (keyFeatures) {
        prompt += "Key features: " + keyFeatures + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "multicast-audiobook-generator",
    name: "Multicast Audiobook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A thrilling mystery novel, An educational guide on AI, etc.",
        description: "A brief description of the audiobook content.",
        id: "about",
      },
    ],
    title: "Multicast Audiobook Generator",
    id: "multicast-audiobook-generator",
    categories: ["Education", "Writing", "Marketing"],
    shortDescription:
      "Generate engaging audiobooks from text for a diverse audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating audiobooks from text. You will be provided with a brief description of the audiobook content, and you will create an engaging audiobook script based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate an audiobook script based on the following:\n";
      prompt += "The audiobook content is about: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "reading-passage-generator",
    name: "Reading Passage Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: The importance of renewable energy, The life cycle of a butterfly, etc.",
        description: "A brief overview of the reading passage topic.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Students",
        optional: true,
        required: false,
        placeholder:
          "Example: 3rd graders, High school students, Adult learners, etc.",
        description: "The grade level or age group of the intended students.",
        id: "students",
      },
    ],
    title: "Reading Passage Generator",
    id: "reading-passage-generator",
    categories: ["Education", "Training Material"],
    shortDescription:
      "Generate engaging reading passages for educational purposes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating reading passages. You will be provided with some base information and you will generate a reading passage based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, students } = fields;
      let prompt = "Generate a reading passage based on the following:\n";
      prompt += "The passage should be about " + about + ".\n";
      if (students) {
        prompt += "Intended students: " + students + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "radio-commercial-generator",
    name: "Radio Commercial Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A new fitness app that helps users track their workouts.",
        description:
          "A brief description of the product or service you want to promote.",
        id: "about",
      },
    ],
    title: "Radio Commercial Generator",
    id: "radio-commercial-generator",
    categories: ["Marketing", "Sales"],
    shortDescription:
      "Create engaging and persuasive radio commercials to promote your products or services.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating radio commercials. You will be provided with a brief description of a product or service, and you will generate an engaging and persuasive radio commercial based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a radio commercial based on the following:\n";
      prompt += "The commercial should promote: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "research-assistant-generator",
    name: "Research Assistant Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: The impact of climate change on agriculture, Advances in AI technology, etc.",
        description: "Provide a brief overview of the research topic.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Length",
        optional: true,
        required: false,
        placeholder: "Example: 500 words, 1000 words, etc.",
        description: "Specify the desired length of the research summary.",
        id: "length",
      },
    ],
    title: "Research Assistant Generator",
    id: "research-assistant-generator",
    categories: ["Education", "Writing", "Business"],
    shortDescription:
      "Generate comprehensive research summaries and insights based on provided topics.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert research assistant. You will be provided with a research topic and will generate a comprehensive summary and insights based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, length } = fields;
      let prompt = "Generate a research summary based on the following:\n";
      prompt += "Research topic: " + about + ".\n";
      if (length) {
        prompt += "Desired length: " + length + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "marketing-assistant-generator",
    name: "Marketing Assistant Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A revolutionary fitness app that tracks your workouts.",
        description: "A brief description of the product or service.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Length",
        optional: true,
        required: false,
        placeholder: "Example: 300, 500, 1000",
        description: "The desired length of the content (in words).",
        id: "length",
      },
    ],
    title: "Marketing Assistant Generator",
    id: "marketing-assistant-generator",
    categories: ["Marketing", "Writing", "Social Media"],
    shortDescription: "Generate marketing content tailored to your needs.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert marketing assistant. You will be provided with details about a product or service, and you will generate tailored marketing content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, length } = fields;
      let prompt = "Generate marketing content based on the following:\n";
      prompt += "About: " + about + ".\n";
      if (length) {
        prompt += "Desired length: " + length + " words.\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "user-manual-writing-generator",
    name: "User Manual Writing Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: This is a user manual for a smart home device.",
        description: "A brief overview of the product or service.",
        id: "about",
      },
    ],
    title: "User Manual Writing Generator",
    id: "user-manual-writing-generator",
    categories: ["Writing", "Education", "Support"],
    shortDescription:
      "Generate clear and concise user manuals for products and services.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing user manuals. You will be provided with a brief overview of a product or service, and you will generate a clear and concise user manual based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a user manual based on the following:\n";
      prompt += "About the product/service: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "linkedin-replies-generator",
    name: "LinkedIn Replies Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Thank you for connecting, I appreciate your insights on...",
        description:
          "A brief overview of the context or message you are replying to.",
        id: "about",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Professional, Friendly, Casual",
        description:
          "The desired tone for your reply (e.g., professional, friendly, casual).",
        id: "toneofvoice",
      },
      {
        type: "textArea",
        label: "Customer name",
        optional: true,
        required: false,
        placeholder: "Example: John Doe, Jane Smith",
        description: "The name of the person you are replying to.",
        id: "customername",
      },
    ],
    title: "LinkedIn Replies Generator",
    id: "linkedin-replies-generator",
    categories: ["Social Media", "Business"],
    shortDescription:
      "Generate personalized replies for LinkedIn messages to enhance networking and engagement.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized replies for LinkedIn messages. You will be provided with some context and details, and you will generate a suitable reply based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, toneOfVoice, customerName } = fields;
      let prompt = "Generate a LinkedIn reply based on the following:\n";
      prompt += "Context: " + about + "\n";
      if (customerName) {
        prompt += "Customer name: " + customerName + "\n";
      }
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "twitter-ads-generator",
    name: "Twitter Ads Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Innovative fitness tracker that monitors health metrics.",
        description:
          "A brief description of the product or service you are promoting.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder:
          "Example: tech enthusiasts, health-conscious individuals, etc.",
        description:
          "The specific group of people you want to reach with your ad.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Goal",
        optional: true,
        required: false,
        placeholder:
          "Example: Increase website traffic, boost product sales, etc.",
        description: "The primary objective of your ad campaign.",
        id: "goal",
      },
    ],
    title: "Twitter Ads Generator",
    id: "twitter-ads-generator",
    categories: ["Social Media", "Marketing"],
    shortDescription:
      "Create engaging and effective ads for Twitter to connect with your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating Twitter ads. You will be provided with some base information and you will generate a compelling Twitter ad based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, goal } = fields;
      let prompt = "Generate a Twitter ad based on the following:\n";
      prompt += "The ad should promote: " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (goal) {
        prompt += "Goal of the ad: " + goal + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "letter-of-recommendation-generator",
    name: "Letter of Recommendation Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: John is a dedicated software engineer with over 5 years of experience.",
        description: "A brief introduction about the person being recommended.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Instance of work",
        optional: true,
        required: false,
        placeholder:
          "Example: Led a team to develop a successful mobile application that increased user engagement by 30%.",
        description:
          "Specific examples of the individual's work or achievements.",
        id: "instanceofwork",
      },
    ],
    title: "Letter of Recommendation Generator",
    id: "letter-of-recommendation-generator",
    categories: ["Business", "Education", "Support"],
    shortDescription:
      "Generate personalized letters of recommendation for various purposes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing letters of recommendation. You will be provided with details about the individual being recommended and specific instances of their work or achievements, and you will generate a personalized letter of recommendation based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, instanceOfWork } = fields;
      let prompt =
        "Generate a letter of recommendation based on the following:\n";
      prompt += "About the individual: " + about + ".\n";
      if (instanceOfWork) {
        prompt += "Instance of work: " + instanceOfWork + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "essay-generator",
    name: "Essay Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: The impact of climate change on biodiversity.",
        description: "Provide a brief overview of the essay topic.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Guidelines",
        optional: true,
        required: false,
        placeholder:
          "Example: Minimum 1000 words, include at least three sources.",
        description: "Specify any guidelines or requirements for the essay.",
        id: "guidelines",
      },
    ],
    title: "Essay Generator",
    id: "essay-generator",
    categories: ["Education", "Writing"],
    shortDescription:
      "Generate well-structured essays on various topics with ease.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating essays. You will be provided with a topic and guidelines, and you will create a well-structured essay based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, guidelines } = fields;
      let prompt = "Generate an essay based on the following:\n";
      prompt += "The essay should be about: " + about + ".\n";
      if (guidelines) {
        prompt += "Guidelines: " + guidelines + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "script-for-youtube-video-generator",
    name: "Script for Youtube Video Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: How to cook pasta, Tips for studying effectively, etc.",
        description: "A brief overview of the video topic.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder: "Example: beginners, parents, tech enthusiasts, etc.",
        description: "The specific group of viewers you want to reach.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Video length",
        optional: true,
        required: false,
        placeholder: "Example: 5 minutes, 10 minutes, etc.",
        description: "The desired duration of the video.",
        id: "videolength",
      },
    ],
    title: "Script for Youtube Video Generator",
    id: "script-for-youtube-video-generator",
    categories: ["Social Media", "Education", "Marketing"],
    shortDescription:
      "Generate engaging scripts for YouTube videos tailored to your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating scripts for YouTube videos. You will be provided with some base information and you will generate a script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, videoLength } = fields;
      let prompt = "Generate a YouTube video script based on the following:\n";
      prompt += "The video topic is: " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (videoLength) {
        prompt += "Desired video length: " + videoLength + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "product-positioning-generator",
    name: "Product Positioning Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A revolutionary fitness app that personalizes workouts based on user goals.",
        description:
          "A brief description of the product and its unique features.",
        id: "about",
      },
    ],
    title: "Product Positioning Generator",
    id: "product-positioning-generator",
    categories: ["Marketing", "Business", "Sales"],
    shortDescription:
      "Craft compelling product positioning statements to effectively communicate your value proposition.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting product positioning statements. You will be provided with a brief description of a product and its unique features, and you will generate a compelling positioning statement that effectively communicates its value proposition.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a product positioning statement based on the following:\n";
      prompt += "Product description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "linkedin-about-generator",
    name: "LinkedIn About Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Experienced marketing professional with a passion for digital strategies.",
        description:
          "A brief summary of your professional background and skills.",
        id: "about",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Professional, Friendly, Authoritative, Casual",
        description: "The style in which you want to present yourself.",
        id: "toneofvoice",
      },
      {
        type: "textArea",
        label: "Point of view",
        optional: true,
        required: false,
        placeholder:
          "Example: I believe in the power of collaboration and innovation to drive success.",
        description: "Your perspective on your industry or profession.",
        id: "pointofview",
      },
    ],
    title: "LinkedIn About Generator",
    id: "linkedin-about-generator",
    categories: ["Social Media", "Writing", "Business"],
    shortDescription:
      "Craft a compelling LinkedIn About section to showcase your professional identity.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting LinkedIn About sections. You will be provided with some base information and you will generate a compelling LinkedIn About section based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, toneOfVoice, pointOfView } = fields;
      let prompt =
        "Generate a LinkedIn About section based on the following:\n";
      prompt += "About: " + about + "\n";
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + "\n";
      }
      if (pointOfView) {
        prompt += "Point of view: " + pointOfView + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "amazon-product-descriptions-generator",
    name: "Amazon Product Descriptions Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This is a high-quality fitness tracker designed for athletes.",
        description: "A brief overview of the product.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Key features",
        optional: true,
        required: false,
        placeholder:
          "Example: Waterproof, heart rate monitor, GPS tracking, etc.",
        description: "Highlight the main features of the product.",
        id: "keyfeatures",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Professional, casual, enthusiastic, etc.",
        description: "The desired tone for the product description.",
        id: "toneofvoice",
      },
    ],
    title: "Amazon Product Descriptions Generator",
    id: "amazon-product-descriptions-generator",
    categories: ["Sales", "Marketing", "Writing"],
    shortDescription:
      "Generate compelling product descriptions for Amazon listings to attract customers.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating Amazon product descriptions. You will be provided with details about a product and you will generate a compelling description that highlights its features and appeals to potential customers.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, keyFeatures, toneOfVoice } = fields;
      let prompt =
        "Generate an Amazon product description based on the following:\n";
      prompt += "Product Overview: " + about + "\n";
      if (keyFeatures) {
        prompt += "Key Features: " + keyFeatures + "\n";
      }
      if (toneOfVoice) {
        prompt += "Tone of Voice: " + toneOfVoice + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "lesson-content-generator",
    name: "Lesson Content Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Introduction to Algebra, The Water Cycle, etc.",
        description: "A brief overview of the lesson topic.",
        id: "about",
      },
    ],
    title: "Lesson Content Generator",
    id: "lesson-content-generator",
    categories: ["Education", "Training Material"],
    shortDescription:
      "Generate engaging and informative lesson content for various educational purposes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating lesson content. You will be provided with a brief overview of the lesson topic, and you will create engaging and informative content based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate lesson content based on the following:\n";
      prompt += "Lesson topic: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "deposition-preparation-generator",
    name: "Deposition Preparation Generator",
    fields: [],
    title: "Deposition Preparation Generator",
    id: "deposition-preparation-generator",
    categories: ["Legal", "Business"],
    shortDescription:
      "Generate comprehensive deposition preparation documents to streamline legal processes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating deposition preparation documents. You will be provided with specific details regarding the case and the parties involved, and you will create a comprehensive deposition preparation document based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { caseDetails, partiesInvolved, keyQuestions } = fields;
      let prompt =
        "Generate a deposition preparation document based on the following:\n";
      prompt += "Case Details: " + caseDetails + "\n";
      if (partiesInvolved) {
        prompt += "Parties Involved: " + partiesInvolved + "\n";
      }
      if (keyQuestions) {
        prompt += "Key Questions to Address: " + keyQuestions + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "lesson-plans-generator",
    name: "Lesson Plans Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This lesson focuses on the water cycle and its stages.",
        description:
          "A brief overview of the lesson plan's objectives and content.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Grade Level",
        optional: true,
        required: false,
        placeholder: "Example: 3rd Grade, 5th Grade, High School, etc.",
        description:
          "The educational grade level for which the lesson is designed.",
        id: "gradelevel",
      },
      {
        type: "textArea",
        label: "Duration",
        optional: true,
        required: false,
        placeholder: "Example: 45 minutes, 1 hour, etc.",
        description: "The estimated time required to complete the lesson.",
        id: "duration",
      },
    ],
    title: "Lesson Plans Generator",
    id: "lesson-plans-generator",
    categories: ["Education", "Training Material"],
    shortDescription:
      "Create comprehensive lesson plans tailored to specific educational needs.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating lesson plans. You will be provided with details about the lesson, including its objectives, grade level, and duration, and you will generate a comprehensive lesson plan based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, gradeLevel, duration } = fields;
      let prompt = "Generate a lesson plan based on the following:\n";
      prompt += "Lesson Overview: " + about + "\n";
      if (gradeLevel) {
        prompt += "Grade Level: " + gradeLevel + "\n";
      }
      if (duration) {
        prompt += "Duration: " + duration + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "linkedin-ads-generator",
    name: "LinkedIn Ads Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Innovative software solution for project management.",
        description:
          "A brief description of the product or service you are promoting.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder: "Example: Marketing managers, IT professionals, etc.",
        description: "The specific group of professionals you want to reach.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Goal",
        optional: true,
        required: false,
        placeholder: "Example: Increase brand awareness, Generate leads, etc.",
        description: "The primary objective of your ad campaign.",
        id: "goal",
      },
    ],
    title: "LinkedIn Ads Generator",
    id: "linkedin-ads-generator",
    categories: ["Social Media", "Marketing", "Business"],
    shortDescription:
      "Create professional and engaging ads for LinkedIn to connect with your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating LinkedIn ads. You will be provided with some base information and you will generate a LinkedIn ad based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, goal } = fields;
      let prompt = "Generate a LinkedIn ad based on the following:\n";
      prompt += "The ad should promote: " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (goal) {
        prompt += "Goal of the ad campaign: " + goal + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "facebook-replies-generator",
    name: "Facebook Replies Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Customer inquiry about product features, Feedback on service, etc.",
        description: "Provide context or details about the conversation.",
        id: "about",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Friendly, Professional, Casual, etc.",
        description: "Select the desired tone for the reply.",
        id: "toneofvoice",
      },
      {
        type: "textArea",
        label: "Customer name",
        optional: true,
        required: false,
        placeholder: "Example: John Doe, Jane Smith, etc.",
        description: "Enter the name of the customer you are replying to.",
        id: "customername",
      },
    ],
    title: "Facebook Replies Generator",
    id: "facebook-replies-generator",
    categories: ["Social Media", "Support"],
    shortDescription:
      "Generate engaging replies for Facebook interactions to enhance customer engagement.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating replies for Facebook interactions. You will be provided with context about a conversation and will generate an appropriate reply based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, toneOfVoice, customerName } = fields;
      let prompt = "Generate a Facebook reply based on the following:\n";
      prompt += "Context: " + about + "\n";
      if (customerName) {
        prompt += "Customer name: " + customerName + "\n";
      }
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "write-like-me-generator",
    name: "Write Like Me Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Casual, Professional, Creative, etc.",
        description:
          "A brief description of the writing style you want to emulate.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Writing snippets",
        optional: false,
        required: true,
        placeholder:
          "Example: 'Once upon a time...', 'In conclusion...', 'To summarize...'",
        description:
          "Provide examples of writing snippets that reflect your style.",
        id: "writingsnippets",
      },
    ],
    title: "Write Like Me Generator",
    id: "write-like-me-generator",
    categories: ["Writing", "Education"],
    shortDescription:
      "Generate personalized writing snippets that mimic your style.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in emulating writing styles. You will be provided with a description of the desired writing style and examples of writing snippets, and you will generate new writing snippets that reflect that style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, writingSnippets } = fields;
      let prompt = "Generate writing snippets based on the following:\n";
      prompt += "The writing style to emulate is: " + about + ".\n";
      prompt += "Examples of writing snippets: " + writingSnippets + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "songs-lyrics-generator",
    name: "Songs Lyrics Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A love story, Overcoming challenges, Celebrating friendship, etc.",
        description: "A brief description of the song's theme or message.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Genre",
        optional: true,
        required: false,
        placeholder: "Example: Pop, Rock, Hip-Hop, Country, etc.",
        description: "The musical genre for the song.",
        id: "genre",
      },
    ],
    title: "Songs Lyrics Generator",
    id: "songs-lyrics-generator",
    categories: ["Writing", "Music"],
    shortDescription:
      "Generate creative and unique song lyrics based on your input.",
    buildSystemPrompt: () => {
      let prompt =
        "You are a talented songwriter with the ability to create unique and engaging song lyrics. You will be provided with a theme and genre, and you will generate lyrics that reflect the given information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, genre } = fields;
      let prompt = "Generate song lyrics based on the following:\n";
      prompt += "Theme: " + about + "\n";
      if (genre) {
        prompt += "Genre: " + genre + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "brainstorm-ideas-generator",
    name: "Brainstorm Ideas Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A new app for tracking fitness goals, A community garden initiative, etc.",
        description: "Provide a brief overview of the project or topic.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Goal",
        optional: true,
        required: false,
        placeholder:
          "Example: Increase user engagement, Promote sustainability, etc.",
        description: "Define the main objective you want to achieve.",
        id: "goal",
      },
    ],
    title: "Brainstorm Ideas Generator",
    id: "brainstorm-ideas-generator",
    categories: ["Writing", "Education", "Marketing"],
    shortDescription:
      "Generate creative ideas for various projects and initiatives.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in brainstorming creative ideas for various projects and initiatives. You will be provided with some base information and you will generate innovative ideas based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, goal } = fields;
      let prompt = "Generate creative ideas based on the following:\n";
      prompt += "Project overview: " + about + ".\n";
      if (goal) {
        prompt += "Main objective: " + goal + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "creative-bio-generator",
    name: "Creative Bio Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A passionate photographer specializing in nature and wildlife.",
        description: "A brief description of yourself or your brand.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Platform",
        optional: true,
        required: false,
        placeholder: "Example: Instagram, LinkedIn, Twitter, etc.",
        description: "The platform where the bio will be used.",
        id: "platform",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Friendly, Professional, Witty",
        description:
          "The desired tone for the bio (e.g., professional, casual, humorous).",
        id: "toneofvoice",
      },
    ],
    title: "Creative Bio Generator",
    id: "creative-bio-generator",
    categories: ["Social Media", "Website", "Marketing"],
    shortDescription:
      "Generate engaging and personalized bios for various platforms.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating engaging and personalized bios. You will be provided with some basic information about a person or brand, and you will generate a creative bio suitable for various platforms.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, platform, toneOfVoice } = fields;
      let prompt = "Generate a creative bio based on the following:\n";
      prompt += "About: " + about + "\n";
      if (platform) {
        prompt += "Platform: " + platform + "\n";
      }
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "cybersecurity-training-generator",
    name: "Cybersecurity Training Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Phishing attacks, Password management, Data protection, etc.",
        description:
          "Provide an overview of the cybersecurity topic you want to cover.",
        id: "about",
      },
    ],
    title: "Cybersecurity Training Generator",
    id: "cybersecurity-training-generator",
    categories: ["Training Material", "Education", "Business"],
    shortDescription:
      "Generate comprehensive training materials to enhance cybersecurity awareness and practices.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating cybersecurity training materials. You will be provided with a specific topic and you will generate comprehensive training content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate training materials based on the following:\n";
      prompt += "The topic is about " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "homework-help-generator",
    name: "Homework Help Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder: "Example: Algebra equations, World War II history, etc.",
        description: "Provide a brief overview of the homework topic.",
        id: "about",
      },
    ],
    title: "Homework Help Generator",
    id: "homework-help-generator",
    categories: ["Education", "Writing"],
    shortDescription:
      "Generate concise and informative homework help content for students.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing homework help. You will be given a specific topic and you will generate concise and informative content to assist students with their homework.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate homework help content based on the following:\n";
      prompt += "The topic is about " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "product-roadmap-generator",
    name: "Product Roadmap Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A mobile app that helps users track their fitness goals.",
        description: "A brief overview of the product and its purpose.",
        id: "about",
      },
    ],
    title: "Product Roadmap Generator",
    id: "product-roadmap-generator",
    categories: ["Business", "Marketing", "Training Material"],
    shortDescription:
      "Create a strategic roadmap for your product development and launch.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating product roadmaps. You will be provided with a brief overview of the product and its purpose, and you will generate a strategic roadmap for its development and launch.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Create a strategic roadmap based on the following:\n";
      prompt += "Product Overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "study-guide-generator",
    name: "Study Guide Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Introduction to Biology, World History Overview, etc.",
        description:
          "A brief overview of the subject or topic for the study guide.",
        id: "about",
      },
    ],
    title: "Study Guide Generator",
    id: "study-guide-generator",
    categories: ["Education", "Training Material"],
    shortDescription:
      "Create comprehensive study guides to help students prepare for exams effectively.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating study guides. You will be provided with a brief overview of a subject or topic, and you will generate a comprehensive study guide based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a study guide based on the following:\n";
      prompt += "The study guide should cover: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "facebook-ads-generator",
    name: "Facebook Ads Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A revolutionary fitness app that tracks your workouts.",
        description:
          "A brief description of the product or service you are advertising.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder:
          "Example: working professionals, college students, fitness enthusiasts.",
        description: "The target audience for the product.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Goal",
        optional: true,
        required: false,
        placeholder:
          "Example: Increase app downloads, boost brand awareness, drive website traffic.",
        description: "The primary goal of the ad campaign.",
        id: "goal",
      },
    ],
    title: "Facebook Ads Generator",
    id: "facebook-ads-generator",
    categories: ["Marketing", "Social Media"],
    shortDescription:
      "Create targeted and effective ads for Facebook to reach your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating Facebook ads. You will be provided with details about a product or service, and you will generate an effective Facebook ad based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, goal } = fields;
      let prompt = "Generate a Facebook ad based on the following:\n";
      prompt += "The ad should be about " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (goal) {
        prompt += "Goal of the ad campaign: " + goal + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "linkedin-posts-generator",
    name: "LinkedIn Posts Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: New product launch, industry insights, career advice, etc.",
        description:
          "A brief overview of the topic or message you want to convey.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder:
          "Example: marketing professionals, software developers, HR managers, etc.",
        description: "The specific group of professionals you want to reach.",
        id: "targetaudience",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder:
          "Example: professional, casual, motivational, informative, etc.",
        description: "The style in which you want to communicate your message.",
        id: "toneofvoice",
      },
    ],
    title: "LinkedIn Posts Generator",
    id: "linkedin-posts-generator",
    categories: ["Social Media", "Marketing", "Business"],
    shortDescription:
      "Generate engaging LinkedIn posts to connect with your professional network.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating LinkedIn posts. You will be provided some base information and you will generate a LinkedIn post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, toneOfVoice } = fields.about;
      let prompt = "Generate a LinkedIn post based on the following:\n";
      prompt += "The post should be about " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "power-of-attorney-generator",
    name: "Power of Attorney Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Grant authority for financial decisions, medical decisions, etc.",
        description: "Provide details about the powers you wish to grant.",
        id: "about",
      },
    ],
    title: "Power of Attorney Generator",
    id: "power-of-attorney-generator",
    categories: ["Legal", "Business"],
    shortDescription:
      "Easily create a legally binding power of attorney document tailored to your needs.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating Power of Attorney documents. You will be provided with details about the powers to grant, and you will create a legally binding document based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a Power of Attorney document based on the following:\n";
      prompt += "Details about the powers to grant: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "self-help-audiobook-generator",
    name: "Self-Help Audiobook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Overcoming anxiety, Building self-confidence, Time management strategies, etc.",
        description: "A brief overview of the self-help topic.",
        id: "about",
      },
    ],
    title: "Self-Help Audiobook Generator",
    id: "self-help-audiobook-generator",
    categories: ["Education", "Writing", "Marketing"],
    shortDescription:
      "Generate engaging scripts for self-help audiobooks to inspire and motivate listeners.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating scripts for self-help audiobooks. You will be provided with a brief overview of a self-help topic, and you will create an engaging script that inspires and motivates listeners based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a script for a self-help audiobook based on the following:\n";
      prompt += "The topic is about " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "email-segments-generator",
    name: "Email Segments Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Engaged users, New subscribers, Inactive customers, etc.",
        description: "Provide a brief overview of the audience segment.",
        id: "about",
      },
    ],
    title: "Email Segments Generator",
    id: "email-segments-generator",
    categories: ["Email", "Marketing"],
    shortDescription:
      "Generate effective email segments to improve targeting and engagement.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating email segments. You will be provided with a brief overview of the audience segment, and you will create effective email segments to improve targeting and engagement based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate effective email segments based on the following:\n";
      prompt += "Overview of the audience segment: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "linkedin-headline-generator",
    name: "LinkedIn Headline Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Experienced software engineer specializing in AI and machine learning.",
        description:
          "A brief summary of your professional background and skills.",
        id: "about",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Professional, Friendly, Inspirational.",
        description:
          "The desired tone for your headline (e.g., professional, casual, authoritative).",
        id: "toneofvoice",
      },
    ],
    title: "LinkedIn Headline Generator",
    id: "linkedin-headline-generator",
    categories: ["Social Media", "Writing", "Business"],
    shortDescription:
      "Craft compelling LinkedIn headlines to enhance your professional profile.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting LinkedIn headlines. You will be provided with a brief summary of professional background and desired tone, and you will generate a compelling LinkedIn headline based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, toneOfVoice } = fields;
      let prompt = "Generate a LinkedIn headline based on the following:\n";
      prompt += "About: " + about + "\n";
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "quotes-generator",
    name: "Quotes Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A quote about perseverance, a motivational saying, etc.",
        description: "A brief description of the quote or its context.",
        id: "about",
      },
    ],
    title: "Quotes Generator",
    id: "quotes-generator",
    categories: ["Social Media", "Email", "Website", "Marketing"],
    shortDescription:
      "Generate inspiring and motivational quotes for various purposes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating quotes. You will be provided with a brief description of the quote or its context, and you will generate an inspiring and motivational quote based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate a quote based on the following:\n";
      prompt += "Description: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "script-for-live-streaming-generator",
    name: "Script for Live Streaming Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Today's topic is the impact of social media on mental health.",
        description:
          "A brief overview of the topic you will be discussing during the live stream.",
        id: "about",
      },
    ],
    title: "Script for Live Streaming Generator",
    id: "script-for-live-streaming-generator",
    categories: ["Social Media", "Education", "Marketing"],
    shortDescription:
      "Generate engaging scripts for live streaming sessions to captivate your audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating scripts for live streaming. You will be provided with a brief overview of the topic, and you will create an engaging script that captivates the audience during the live stream.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a script for a live streaming session based on the following:\n";
      prompt += "The topic is: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "gtm-strategy-generator",
    name: "GTM Strategy Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A new mobile app for fitness tracking, An innovative kitchen gadget, etc.",
        description:
          "A brief description of the product or service you are launching.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target Audience",
        optional: true,
        required: false,
        placeholder:
          "Example: tech-savvy millennials, busy parents, health-conscious individuals, etc.",
        description: "Define the primary audience for your product or service.",
        id: "targetaudience",
      },
    ],
    title: "GTM Strategy Generator",
    id: "gtm-strategy-generator",
    categories: ["Marketing", "Business"],
    shortDescription:
      "Generate effective go-to-market strategies tailored to your product and audience.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating go-to-market strategies. You will be provided with details about a product or service and its target audience, and you will generate a comprehensive GTM strategy based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience } = fields;
      let prompt = "Generate a go-to-market strategy based on the following:\n";
      prompt += "Product/Service Description: " + about + "\n";
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "fiction-audiobook-generator",
    name: "Fiction Audiobook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A thrilling adventure of a young wizard in a magical realm.",
        description:
          "A brief summary of the story or content for the audiobook.",
        id: "about",
      },
    ],
    title: "Fiction Audiobook Generator",
    id: "fiction-audiobook-generator",
    categories: ["Writing", "Education", "Marketing"],
    shortDescription:
      "Generate engaging audiobooks from your fiction stories with ease.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging audiobooks from fiction stories. You will be provided with a brief summary of the story, and you will create a captivating audiobook script based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate an audiobook script based on the following:\n";
      prompt += "Story summary: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "romance-audiobook-generator",
    name: "Romance Audiobook Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A love story set in Paris between a painter and a writer.",
        description:
          "A brief overview of the romance story you want to create.",
        id: "about",
      },
    ],
    title: "Romance Audiobook Generator",
    id: "romance-audiobook-generator",
    categories: ["Writing", "Education", "Marketing"],
    shortDescription:
      "Generate captivating romance audiobooks with engaging plots and characters.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating captivating romance audiobooks. You will be provided with a brief overview of the romance story, and you will generate an engaging plot and characters based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a captivating romance audiobook based on the following:\n";
      prompt += "Story overview: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "google-ads-generator",
    name: "Google Ads Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Innovative software solution, Eco-friendly cleaning products, etc.",
        description:
          "A brief description of the product or service you are advertising.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder: "Example: small business owners, tech enthusiasts, etc.",
        description:
          "The specific group of people you want to reach with your ad.",
        id: "targetaudience",
      },
    ],
    title: "Google Ads Generator",
    id: "google-ads-generator",
    categories: ["Marketing", "Sales"],
    shortDescription:
      "Create compelling ads for Google to effectively promote your products or services.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating Google Ads. You will be provided with some base information and you will generate a compelling Google ad based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience } = fields;
      let prompt = "Generate a Google ad based on the following:\n";
      prompt += "The ad should be about " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "gdpr-and-data-retention-policy-generator",
    name: "GDPR and Data Retention Policy Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: We are an e-commerce platform that collects customer data for order processing.",
        description:
          "Provide a brief overview of your organization and its data processing activities.",
        id: "about",
      },
    ],
    title: "GDPR and Data Retention Policy Generator",
    id: "gdpr-and-data-retention-policy-generator",
    categories: ["Legal", "Business"],
    shortDescription:
      "Generate a comprehensive GDPR and data retention policy tailored to your business needs.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating GDPR and data retention policies. You will be provided with information about an organization and its data processing activities, and you will create a comprehensive policy tailored to the organization's needs.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt =
        "Generate a GDPR and data retention policy based on the following:\n";
      prompt += "About the organization: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "customer-surveys-generator",
    name: "Customer Surveys Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: This survey aims to understand customer satisfaction with our services.",
        description: "Provide a brief description of the survey's purpose.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Objective",
        optional: true,
        required: false,
        placeholder:
          "Example: To identify areas for improvement in customer service.",
        description: "State the main goal of the survey.",
        id: "objective",
      },
    ],
    title: "Customer Surveys Generator",
    id: "customer-surveys-generator",
    categories: ["Support", "Marketing", "Business"],
    shortDescription:
      "Create effective customer surveys to gather valuable feedback and insights.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating customer surveys. You will be provided with details about the survey's purpose and objectives, and you will generate a comprehensive survey based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, objective } = fields;
      let prompt = "Generate a customer survey based on the following:\n";
      prompt += "Survey Purpose: " + about + "\n";
      if (objective) {
        prompt += "Main Objective: " + objective + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "relieving-letter-generator",
    name: "Relieving Letter Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: John Doe worked as a Software Engineer, contributing to various projects.",
        description:
          "A brief description of the employee's role and contributions.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Designation",
        optional: true,
        required: false,
        placeholder:
          "Example: Senior Software Engineer, Marketing Manager, etc.",
        description: "The official job title of the employee.",
        id: "designation",
      },
      {
        type: "textArea",
        label: "Tenure",
        optional: true,
        required: false,
        placeholder: "Example: January 2020 - October 2023",
        description: "The duration of employment in the organization.",
        id: "tenure",
      },
    ],
    title: "Relieving Letter Generator",
    id: "relieving-letter-generator",
    categories: ["Business", "Support"],
    shortDescription:
      "Generate professional relieving letters for employees leaving the organization.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating relieving letters. You will be provided with details about an employee's role, contributions, and tenure, and you will create a professional relieving letter based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, designation, tenure } = fields;
      let prompt = "Generate a relieving letter based on the following:\n";
      prompt += "About the employee: " + about + "\n";
      if (designation) {
        prompt += "Designation: " + designation + "\n";
      }
      if (tenure) {
        prompt += "Tenure: " + tenure + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "settlement-agreement-generator",
    name: "Settlement Agreement Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Dispute between Company A and Company B regarding contract terms.",
        description:
          "A brief overview of the dispute and the parties involved.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Terms of settlement",
        optional: true,
        required: false,
        placeholder:
          "Example: Payment amount, deadlines, confidentiality clauses, etc.",
        description:
          "The specific terms agreed upon by both parties to resolve the dispute.",
        id: "termsofsettlement",
      },
    ],
    title: "Settlement Agreement Generator",
    id: "settlement-agreement-generator",
    categories: ["Legal", "Business"],
    shortDescription:
      "Generate clear and concise settlement agreements for legal disputes.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in drafting settlement agreements. You will be provided with details about a dispute and the terms of settlement, and you will generate a clear and concise settlement agreement based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, termsOfSettlement } = fields;
      let prompt = "Generate a settlement agreement based on the following:\n";
      prompt += "Dispute overview: " + about + "\n";
      if (termsOfSettlement) {
        prompt += "Terms of settlement: " + termsOfSettlement + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "full-blog-generator",
    name: "Full Blog Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: The benefits of meditation, How to start a garden, etc.",
        description:
          "A brief overview of the topic you want to cover in the blog.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Keywords",
        optional: false,
        required: true,
        placeholder:
          "Example: meditation, gardening tips, healthy living, etc.",
        description:
          "Relevant keywords to optimize your blog for search engines.",
        id: "keywords",
      },
      {
        type: "textArea",
        label: "Goal",
        optional: true,
        required: false,
        placeholder:
          "Example: Inform readers, Drive traffic, Generate leads, etc.",
        description: "The main objective of the blog post.",
        id: "goal",
      },
      {
        type: "toneOfVoice",
        label: "Tone Of Voice",
        optional: true,
        required: false,
        placeholder: "Example: Professional, Casual, Conversational, etc.",
        description: "The style in which the blog should be written.",
        id: "toneofvoice",
      },
    ],
    title: "Full Blog Generator",
    id: "full-blog-generator",
    categories: ["Writing", "Education", "Marketing"],
    shortDescription:
      "Generate comprehensive blog posts tailored to your needs.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating comprehensive blog posts. You will be provided with some base information and you will generate a full blog post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, keywords, goal, toneOfVoice, wordCount } = fields;
      let prompt = "Generate a blog post based on the following:\n";
      prompt += "The blog should cover: " + about + ".\n";
      prompt += "Relevant keywords: " + keywords + ".\n";
      if (goal) {
        prompt += "Main objective: " + goal + ".\n";
      }
      if (toneOfVoice) {
        prompt += "Tone of voice: " + toneOfVoice + ".\n";
      }
      if (wordCount) {
        prompt += "Desired word count: " + wordCount + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "walmart-product-title-generator",
    name: "Walmart Product Title Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: High-quality wireless headphones with noise cancellation.",
        description: "A brief description of the product.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Brand Name",
        optional: true,
        required: false,
        placeholder: "Example: Sony, Apple, Samsung, etc.",
        description: "The name of the brand associated with the product.",
        id: "brandname",
      },
    ],
    title: "Walmart Product Title Generator",
    id: "walmart-product-title-generator",
    categories: ["Sales", "Marketing"],
    shortDescription:
      "Generate compelling product titles for Walmart listings to enhance visibility and sales.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating product titles for Walmart listings. You will be provided with a brief description of the product and the brand name, and you will generate a compelling product title based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, brandName } = fields;
      let prompt = "Generate a Walmart product title based on the following:\n";
      prompt += "Product description: " + about + ".\n";
      if (brandName) {
        prompt += "Brand name: " + brandName + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "movie-script-generator",
    name: "Movie Script Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: A young hero discovers their powers in a dystopian world.",
        description: "A brief overview of the movie's concept or storyline.",
        id: "about",
      },
      {
        type: "textArea",
        label: "Target audience",
        optional: true,
        required: false,
        placeholder: "Example: teenagers, families, action movie enthusiasts.",
        description: "The demographic group that the movie is aimed at.",
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Genre",
        optional: true,
        required: false,
        placeholder: "Example: Sci-fi, Romantic Comedy, Horror.",
        description: "The genre of the movie (e.g., drama, comedy, thriller).",
        id: "genre",
      },
      {
        type: "textArea",
        label: "Length",
        optional: true,
        required: false,
        placeholder: "Example: 90, 120, 150.",
        description: "The expected duration of the movie in minutes.",
        id: "length",
      },
    ],
    title: "Movie Script Generator",
    id: "movie-script-generator",
    categories: ["Writing", "Education"],
    shortDescription:
      "Generate engaging and structured movie scripts tailored to your ideas.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating movie scripts. You will be provided with some base information about the movie's concept, target audience, genre, and length, and you will generate a structured movie script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about, targetAudience, genre, length } = fields;
      let prompt = "Generate a movie script based on the following:\n";
      prompt += "The movie is about: " + about + ".\n";
      if (targetAudience) {
        prompt += "Target audience: " + targetAudience + ".\n";
      }
      if (genre) {
        prompt += "Genre: " + genre + ".\n";
      }
      if (length) {
        prompt += "Expected length: " + length + " minutes.\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    slug: "interview-questionnaire-generator",
    name: "Interview Questionnaire Generator",
    fields: [
      {
        type: "textArea",
        label: "About",
        optional: false,
        required: true,
        placeholder:
          "Example: Software Engineer, Marketing Manager, Sales Associate, etc.",
        description: "Provide a brief overview of the position or role.",
        id: "about",
      },
    ],
    title: "Interview Questionnaire Generator",
    id: "interview-questionnaire-generator",
    categories: ["Education", "Business", "Training Material"],
    shortDescription:
      "Generate tailored interview questions to assess candidates effectively.",
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating interview questionnaires. You will be provided with information about a job position, and you will create tailored interview questions to assess candidates effectively.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { about } = fields;
      let prompt = "Generate interview questions based on the following:\n";
      prompt += "Position: " + about + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-expander-generator",
    name: "Text Expander Generator",
    slug: "text-expander-generator",
    shortDescription:
      "Expands on a given text to provide more depth and detail.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Enter the text you want expanded here.",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in expanding text. You will be provided with a piece of original text, and your task is to elaborate on it, providing more depth and detail.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = "Expand on the following text:\n";
      prompt += "Original Text: " + originalText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "notes-generator-ai",
    name: "Notes Generator AI",
    slug: "notes-generator-ai",
    shortDescription:
      "Generate comprehensive notes on a given topic, subject or theme using web search and AI",
    fields: [
      {
        type: "textArea",
        label: "Topic, Subject or Theme",
        field: "Topic, Subject or Theme",
        placeholder: "Chemistry - The Periodic Table",
        required: true,
        optional: false,
        id: "topic,subjectortheme",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating comprehensive notes on various topics, subjects, or themes. You will be provided with a specific topic and will generate detailed notes based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topic } = fields;
      let prompt =
        "Generate comprehensive notes based on the following topic:\n";
      prompt += "Topic: " + topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-dungeon-master",
    name: "AI Dungeon Master",
    slug: "ai-dungeon-master",
    shortDescription:
      "Generates Dungeons & Dragons scenarios and manages gameplay as a virtual Dungeon Master.",
    fields: [
      {
        type: "textArea",
        label: "Campaign Setting",
        field: "Campaign Setting",
        placeholder: "A haunted forest, an ancient city, a mystical island...",
        required: true,
        optional: false,
        id: "campaignsetting",
      },
      {
        type: "textArea",
        label: "Player Characters",
        field: "Player Characters",
        placeholder: "Character name, race, class, background...",
        required: false,
        optional: true,
        id: "playercharacters",
      },
      {
        type: "textArea",
        label: "Campaign Goal",
        field: "Campaign Goal",
        placeholder:
          "Defeat the dragon, save the princess, find the treasure...",
        required: false,
        optional: true,
        id: "campaigngoal",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating Dungeons & Dragons scenarios. You will be provided with details about the campaign setting, player characters, and campaign goals, and you will create an engaging scenario for the players.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { campaignSetting, playerCharacters, campaignGoal } = fields;
      let prompt =
        "Generate a Dungeons & Dragons scenario based on the following:\n";
      prompt += "Campaign Setting: " + campaignSetting + ".\n";
      if (playerCharacters) {
        prompt += "Player Characters: " + playerCharacters + ".\n";
      }
      if (campaignGoal) {
        prompt += "Campaign Goal: " + campaignGoal + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "writing-fusion-generator",
    name: "Writing Fusion Generator",
    slug: "writing-fusion-generator",
    shortDescription:
      "Combines two distinct pieces of writing into one cohesive, unified document.",
    fields: [
      {
        type: "textArea",
        label: "Source 1",
        field: "Source 1",
        placeholder: "Enter the first piece of writing here.",
        required: true,
        optional: false,
        id: "source1",
      },
      {
        type: "textArea",
        label: "Source 2",
        field: "Source 2",
        placeholder: "Enter the second piece of writing here.",
        required: false,
        optional: true,
        id: "source2",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in combining distinct pieces of writing into a cohesive document. You will be provided with two separate pieces of writing, and your task is to merge them into a unified narrative.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { source1, source2 } = fields;
      let prompt =
        "Combine the following two pieces of writing into one cohesive document:\n";
      prompt += "Source 1: " + source1 + "\n";
      if (source2) {
        prompt += "Source 2: " + source2 + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "quiz-answer-assistant-generator",
    name: "Quiz Answer Assistant Generator",
    slug: "quiz-answer-assistant-generator",
    shortDescription:
      "Provides answers to quiz questions based on the information provided.",
    fields: [
      {
        type: "textArea",
        label: "Quiz Questions",
        field: "Quiz Questions",
        placeholder:
          "1. Who was the first president of the United States?\n2. What is the capital of France?",
        required: true,
        optional: false,
        id: "quizquestions",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing answers to quiz questions. You will be given a set of quiz questions, and your task is to generate accurate and informative answers based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { quizQuestions } = fields;
      let prompt = "Provide answers to the following quiz questions:\n";
      prompt += quizQuestions + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "content-idea-generator",
    name: "Content Idea Generator",
    slug: "content-idea-generator",
    shortDescription:
      "Researches a topic or niche, then generates five related content ideas.",
    fields: [
      {
        type: "textArea",
        label: "Topic or Niche",
        field: "Topic or Niche",
        placeholder: "Digital marketing",
        required: true,
        optional: false,
        id: "topicorniche",
      },
    ],
    categories: ["Marketing", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating content ideas. You will be provided with a specific topic or niche, and you will generate five related content ideas based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicOrNiche } = fields;
      let prompt =
        "Generate five content ideas based on the following topic or niche:\n";
      prompt += "Topic or Niche: " + topicOrNiche + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-summarizer-generator",
    name: "Text Summarizer Generator",
    slug: "text-summarizer-generator",
    shortDescription:
      "Generates a concise summary of a given text or linked text, highlighting the main points and key information.",
    fields: [
      {
        type: "textArea",
        label: "Text to Summarize",
        field: "Text to Summarize",
        placeholder: "Paste the text or link you want to summarize here",
        required: true,
        optional: false,
        id: "texttosummarize",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing texts. You will be provided with a piece of text or a link, and your task is to generate a concise summary that highlights the main points and key information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToSummarize } = fields;
      let prompt = "Generate a concise summary based on the following text:\n";
      prompt += "Text: " + textToSummarize + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "linkedin-post-generator",
    name: "LinkedIn Post Generator",
    slug: "linkedin-post-generator",
    shortDescription:
      "Create compelling LinkedIn posts based on provided description or ideas.",
    fields: [
      {
        type: "textArea",
        label: "Post Description",
        field: "Post Description",
        placeholder: "Networking tips for professionals",
        required: true,
        optional: false,
        id: "postdescription",
      },
    ],
    categories: ["Social Media", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating LinkedIn posts. You will be provided with a description and you will generate a compelling LinkedIn post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { postDescription } = fields;
      let prompt = "Generate a LinkedIn post based on the following:\n";
      prompt += "The post should be about: " + postDescription + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-search-engine",
    name: "AI Search Engine",
    slug: "ai-search-engine",
    shortDescription:
      "A powerful AI search-engine that is smart and actionable.",
    fields: [
      {
        type: "textArea",
        label: "Search Query",
        field: "Search Query",
        placeholder: "WritingTools.ai...",
        required: true,
        optional: false,
        id: "searchquery",
      },
    ],
    categories: ["Business", "Marketing", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an advanced AI search engine designed to provide smart and actionable search results. You will be given a search query, and your task is to return relevant information based on that query.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { searchQuery } = fields;
      let prompt =
        "Please provide relevant information based on the following search query:\n";
      prompt += "Search Query: " + searchQuery + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "expert-writing-assistant-generator",
    name: "Expert Writing Assistant Generator",
    slug: "expert-writing-assistant-generator",
    shortDescription:
      "Expert AI-powered writing assistant that can draft, improve writing, give advice, and more.",
    fields: [
      {
        type: "textArea",
        label: "Writing Task",
        field: "Writing Task",
        placeholder:
          "Write an introduction about the evolution of technology in the modern world.",
        required: true,
        optional: false,
        id: "writingtask",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert writing assistant. You will be provided with a writing task and you will generate a well-structured and coherent piece of writing based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingTask } = fields;
      let prompt = "Generate a written piece based on the following task:\n";
      prompt += "Writing Task: " + writingTask + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "creepypasta-story-creator",
    name: "Creepypasta Story Creator",
    slug: "creepypasta-story-creator",
    shortDescription:
      "Generates suspenseful, slow-paced horror stories based on real-life events or a set of details, in the first-person perspective.",
    fields: [
      {
        type: "textArea",
        label: "Real-life Event or Details",
        field: "Real-life Event or Details",
        placeholder:
          "A group of friends exploring an abandoned house in their neighborhood",
        required: true,
        optional: false,
        id: "real-lifeeventordetails",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a master storyteller specializing in horror narratives. Your task is to create a suspenseful creepypasta story based on the provided real-life event or details.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { realLifeEventOrDetails } = fields;
      let prompt = "Generate a creepypasta story based on the following:\n";
      prompt += "Real-life event or details: " + realLifeEventOrDetails + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-story-critique",
    name: "AI Story Critique",
    slug: "ai-story-critique",
    shortDescription:
      "Provides constructive feedback and critique on a submitted story.",
    fields: [
      {
        type: "textArea",
        label: "Story",
        field: "Story",
        placeholder: "Once upon a time...",
        required: true,
        optional: false,
        id: "story",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing constructive feedback and critique on stories. You will be given a story, and your task is to analyze it and provide insightful feedback to help the author improve their writing.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Story } = fields;
      let prompt = "Please provide a critique for the following story:\n";
      prompt += "Story: " + Story + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "research-paper-writer",
    name: "Research Paper Writer",
    slug: "research-paper-writer",
    shortDescription:
      "Assists in writing a research paper by generating content based on given topic, thesis statement, and key points.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "The Impact of Climate Change on Biodiversity",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Thesis Statement",
        field: "Thesis Statement",
        placeholder:
          "Climate change significantly impacts biodiversity, leading to the extinction of many species.",
        required: false,
        optional: true,
        id: "thesisstatement",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. Explanation of climate change\n2. How climate change affects biodiversity\n3. Case studies of species affected by climate change",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing research papers. You will be provided with a topic, thesis statement, and key points, and you will generate content for a research paper based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, ThesisStatement, KeyPoints } = fields;
      let prompt =
        "Generate content for a research paper based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (ThesisStatement) {
        prompt += "Thesis Statement: " + ThesisStatement + "\n";
      }
      if (KeyPoints) {
        prompt += "Key Points: " + KeyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "undetectable-ai-humanizer-and-paraphraser",
    name: "Undetectable AI Humanizer and Paraphraser",
    slug: "undetectable-ai-humanizer-and-paraphraser",
    shortDescription:
      "Transforms AI-generated text into human-like language and paraphrases content to avoid detection.",
    fields: [
      {
        type: "textArea",
        label: "AI Generated Text",
        field: "AI Generated Text",
        placeholder: "Input your AI generated text here.",
        required: true,
        optional: false,
        id: "aigeneratedtext",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in transforming AI-generated text into human-like language. Your task is to paraphrase the provided text to make it sound more natural and less detectable as AI-generated content.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { AIGeneratedText } = fields;
      let prompt =
        "Please paraphrase the following AI-generated text to make it sound more human-like:\n";
      prompt += AIGeneratedText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "story-generator",
    name: "Story Generator",
    slug: "story-generator",
    shortDescription:
      "Generates a creative and engaging story based on a provided topic.",
    fields: [
      {
        type: "textArea",
        label: "Story Description",
        field: "Story Description",
        placeholder: "A magical forest",
        required: true,
        optional: false,
        id: "storydescription",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative storyteller. You will be provided with a story description, and you will generate a captivating and engaging story based on that description.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { storyDescription } = fields.storyDescription;
      let prompt = "Generate a story based on the following:\n";
      prompt += "Story Description: " + storyDescription + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "rewrite-content-generator",
    name: "Rewrite Content Generator",
    slug: "rewrite-content-generator",
    shortDescription:
      "Rewrite content in a different way, while keeping the same meaning.",
    fields: [
      {
        type: "textArea",
        label: "Original Content",
        field: "Original Content",
        placeholder: "The quick brown fox jumps over the lazy dog",
        required: true,
        optional: false,
        id: "originalcontent",
      },
      {
        type: "textArea",
        label: "Instructions",
        field: "Instructions",
        placeholder: "Make it more engaging, simplify it, etc.",
        required: false,
        optional: true,
        id: "instructions",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting content. You will be provided with original content and specific instructions, and you will generate a rewritten version that maintains the same meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalcontent, instructions } = fields;
      let prompt =
        "Rewrite the following content based on the provided instructions:\n";
      prompt += "Original Content: " + originalContent + "\n";
      if (instructions) {
        prompt += "Instructions: " + instructions + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "grant-proposal-assistant",
    name: "Grant Proposal Assistant",
    slug: "grant-proposal-assistant",
    shortDescription:
      "Assists in generating comprehensive and compelling responses to grant proposal questions.",
    fields: [
      {
        type: "textArea",
        label: "Grant Proposal Questions",
        field: "Grant Proposal Questions",
        placeholder:
          "What is the purpose of your project? How will you measure the success of your project?",
        required: true,
        optional: false,
        id: "grantproposalquestions",
      },
      {
        type: "textArea",
        label: "Project Details",
        field: "Project Details",
        placeholder:
          "Our project aims to provide clean drinking water to rural communities...",
        required: false,
        optional: true,
        id: "projectdetails",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating grant proposals. You will be provided with specific questions and project details, and you will generate comprehensive and compelling responses to those questions.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { grantproposalquestions, projectDetails } = fields;
      let prompt =
        "Generate responses for the following grant proposal questions:\n";
      prompt += "Questions: " + grantproposalquestions + "\n";
      if (projectDetails) {
        prompt += "Project Details: " + projectDetails + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "book-review-generator",
    name: "Book Review Generator",
    slug: "book-review-generator",
    shortDescription:
      "Generates a comprehensive review of a book based on information found online.",
    fields: [
      {
        type: "textArea",
        label: "Book Name",
        field: "Book Name",
        placeholder: "To Kill a Mockingbird",
        required: true,
        optional: false,
        id: "bookname",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating comprehensive book reviews. You will be provided with the name of a book and you will generate a detailed review based on the information available online.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { bookName } = fields;
      let prompt = "Generate a comprehensive review for the following book:\n";
      prompt += "Book Name: " + bookName + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-essay-writer",
    name: "AI Essay Writer",
    slug: "ai-essay-writer",
    shortDescription:
      "Generates a high-quality essay draft based on a given request, using search to research and reference information.",
    fields: [
      {
        type: "textArea",
        label: "Essay Topic",
        field: "Essay Topic",
        placeholder: "The Impact of Climate Change on Global Economy",
        required: true,
        optional: false,
        id: "essaytopic",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert essay writer. You will be provided with a topic and you will generate a high-quality essay draft based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayTopic } = fields;
      let prompt =
        "Generate a high-quality essay draft based on the following topic:\n";
      prompt += "Essay Topic: " + essayTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "classmate-discussion-post-responder-generator",
    name: "Classmate Discussion Post Responder Generator",
    slug: "classmate-discussion-post-responder-generator",
    shortDescription:
      "Generates a thoughtful response to a classmate's discussion post.",
    fields: [
      {
        type: "textArea",
        label: "Classmate's Post",
        field: "Classmate's Post",
        placeholder: "In this week's reading, I found it interesting that...",
        required: true,
        optional: false,
        id: "classmatespost",
      },
      {
        type: "textArea",
        label: "Your Thoughts",
        field: "Your Thoughts",
        placeholder: "I agree with your point about...",
        required: false,
        optional: true,
        id: "yourthoughts",
      },
      {
        type: "textArea",
        label: "Questions for Further Discussion",
        field: "Questions for Further Discussion",
        placeholder: "What do you think about...",
        required: false,
        optional: true,
        id: "questionsforfurtherdiscussion",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thoughtful responses to classmate discussion posts. You will be provided with a classmate's post and your own thoughts, and you will generate a response that is engaging and encourages further discussion.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { classmatespost, yourthoughts, questionsforfurtherdiscussion } =
        fields;
      let prompt =
        "Generate a thoughtful response to the following classmate's post:\n";
      prompt += "Classmate's Post: " + classmatespost + "\n";
      if (yourthoughts) {
        prompt += "Your Thoughts: " + yourthoughts + "\n";
      }
      if (questionsforfurtherdiscussion) {
        prompt +=
          "Questions for Further Discussion: " +
          questionsforfurtherdiscussion +
          "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-aligner",
    name: "Resume Aligner",
    slug: "resume-aligner",
    shortDescription:
      "Tailor your resume to align with a specific job description by using relevant keywords.",
    fields: [
      {
        type: "textArea",
        label: "Uploaded Resume",
        field: "Uploaded Resume",
        placeholder: "Copy and paste your resume here",
        required: true,
        optional: false,
        id: "uploadedresume",
      },
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "Paste the job description here",
        required: false,
        optional: true,
        id: "jobdescription",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in resume optimization. You will be provided with a resume and a job description, and your task is to tailor the resume to align with the job description by using relevant keywords and phrases.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { uploadedResume, jobDescription } = fields;
      let prompt =
        "Tailor the following resume based on the provided job description:\n";
      prompt += "Uploaded Resume: " + uploadedResume + "\n";
      if (jobDescription) {
        prompt += "Job Description: " + jobDescription + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "instagram-caption-generator",
    name: "Instagram Caption Generator",
    slug: "instagram-caption-generator",
    shortDescription:
      "Generates a catchy Instagram caption based on a given idea or keyword.",
    fields: [
      {
        type: "textArea",
        label: "Idea or Keyword",
        field: "Idea or Keyword",
        placeholder: "Vacation in Paris!",
        required: true,
        optional: false,
        id: "ideaorkeyword",
      },
    ],
    categories: ["Social Media", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating catchy Instagram captions. You will be provided with an idea or keyword, and you will create an engaging caption that resonates with the audience.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { ideaOrKeyword } = fields;
      let prompt =
        "Generate a catchy Instagram caption based on the following:\n";
      prompt += "Idea or Keyword: " + ideaOrKeyword + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "boyfriend-ai",
    name: "Boyfriend AI",
    slug: "boyfriend-ai",
    shortDescription:
      "Generates flirtatious and provocative responses with a mix of romantic and sensual undertones.",
    fields: [
      {
        type: "textArea",
        label: "User Input",
        field: "User Input",
        placeholder: "Hi, how was your day?",
        required: true,
        optional: false,
        id: "userinput",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI designed to generate flirtatious and provocative responses. Your goal is to create romantic and sensual replies based on the user's input.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userInput } = fields;
      let prompt =
        "Generate a flirtatious response based on the following user input:\n";
      prompt += "User Input: " + userInput + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-speech-writer-generator",
    name: "AI Speech Writer Generator",
    slug: "ai-speech-writer-generator",
    shortDescription:
      "Use AI to help draft speeches based on the provided information.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Enter the main topic of the speech",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Details",
        field: "Details",
        placeholder:
          "Provide an outline, key points, sources, or any goals for the speech",
        required: false,
        optional: true,
        id: "details",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in drafting speeches. You will be provided with a topic and additional details, and you will generate a well-structured speech based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, Details } = fields;
      let prompt = "Generate a speech based on the following:\n";
      prompt += "The main topic of the speech is: " + Topic + ".\n";
      if (Details) {
        prompt += "Additional details: " + Details + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "movie-recommender-generator",
    name: "Movie Recommender Generator",
    slug: "movie-recommender-generator",
    shortDescription:
      "Recommends the perfect movie based on your preferred genre and similar movies you've enjoyed.",
    fields: [
      {
        type: "textArea",
        label: "Preferred Genre",
        field: "Preferred Genre",
        placeholder: "Action, Drama, Comedy...",
        required: true,
        optional: false,
        id: "preferredgenre",
      },
      {
        type: "textArea",
        label: "Similar Movies",
        field: "Similar Movies",
        placeholder: "The Dark Knight, Inception, Fight Club...",
        required: false,
        optional: true,
        id: "similarmovies",
      },
    ],
    categories: ["Entertainment", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert movie recommender. Based on the user's preferred genre and similar movies they've enjoyed, you will suggest the perfect movie for them.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { preferredGenre, similarMovies } = fields;
      let prompt = "Recommend a movie based on the following criteria:\n";
      prompt += "Preferred Genre: " + preferredGenre + ".\n";
      if (similarMovies) {
        prompt += "Similar Movies: " + similarMovies + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "research-problem-statement-generator",
    name: "Research Problem Statement Generator",
    slug: "research-problem-statement-generator",
    shortDescription:
      "Generates a clear and concise problem statement for a given research topic.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic",
        field: "Research Topic",
        placeholder:
          "The impact of climate change on agricultural productivity",
        required: true,
        optional: false,
        id: "researchtopic",
      },
    ],
    categories: ["Education", "Writing", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating research problem statements. You will be provided with a research topic, and you will generate a clear and concise problem statement based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic } = fields;
      let prompt =
        "Generate a clear and concise problem statement based on the following:\n";
      prompt += "Research Topic: " + researchTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-merger",
    name: "Text Merger",
    slug: "text-merger",
    shortDescription:
      "Combines two similar texts into a single, coherent piece.",
    fields: [
      {
        type: "textArea",
        label: "Text 1",
        field: "Text 1",
        placeholder: "Enter the first text here",
        required: true,
        optional: false,
        id: "text1",
      },
      {
        type: "textArea",
        label: "Text 2",
        field: "Text 2",
        placeholder: "Enter the second text here",
        required: false,
        optional: true,
        id: "text2",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in merging texts. You will be provided with two similar texts, and your task is to combine them into a single, coherent piece that retains the essential information from both.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { text1, text2 } = fields;
      let prompt =
        "Combine the following texts into a single, coherent piece:\n";
      prompt += "Text 1: " + text1 + "\n";
      if (text2) {
        prompt += "Text 2: " + text2 + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "cover-letter-generator",
    name: "Cover Letter Generator",
    slug: "cover-letter-generator",
    shortDescription:
      "Create customized cover letters based on a provided job and resume.",
    fields: [
      {
        type: "textArea",
        label: "Job Description and Company",
        field: "Job Description and Company",
        placeholder: "Software Engineer at WritingTools.ai",
        required: true,
        optional: false,
        id: "jobdescriptionandcompany",
      },
      {
        type: "textArea",
        label: "Resume",
        field: "Resume",
        placeholder: "Paste the text from your resume",
        required: false,
        optional: true,
        id: "resume",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating customized cover letters. You will be provided with a job description and a resume, and you will generate a tailored cover letter based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { jobDescriptionAndCompany, resume } = fields;
      let prompt = "Generate a cover letter based on the following:\n";
      prompt +=
        "Job Description and Company: " + jobDescriptionAndCompany + "\n";
      if (resume) {
        prompt += "Resume: " + resume + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "brainstorming-ai",
    name: "Brainstorming AI",
    slug: "brainstorming-ai",
    shortDescription:
      "Assists in brainstorming ideas or solutions based on a presented problem or question, facilitating a back-and-forth conversation.",
    fields: [
      {
        type: "textArea",
        label: "Problem or Question",
        field: "Problem or Question",
        placeholder: "How can we increase customer engagement on our website?",
        required: true,
        optional: false,
        id: "problemorquestion",
      },
    ],
    categories: ["Business", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert brainstorming assistant. You will be provided with a problem or question, and your task is to generate creative ideas or solutions based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { problemOrQuestion } = fields;
      let prompt =
        "Generate ideas or solutions based on the following problem or question:\n";
      prompt += "Problem or Question: " + problemOrQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-literature-review-generator",
    name: "AI Literature Review Generator",
    slug: "ai-literature-review-generator",
    shortDescription:
      "Automatically generates a comprehensive literature review based on a specific research topic.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic or Question",
        field: "Research Topic or Question",
        placeholder: "Impact of Artificial Intelligence on Healthcare",
        required: true,
        optional: false,
        id: "researchtopicorquestion",
      },
    ],
    categories: ["Writing", "Education", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating literature reviews. You will be provided with a specific research topic or question, and you will generate a comprehensive literature review based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic } = fields;
      let prompt =
        "Generate a comprehensive literature review based on the following:\n";
      prompt += "Research Topic or Question: " + researchTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-tailor-generator",
    name: "Resume Tailor Generator",
    slug: "resume-tailor-generator",
    shortDescription:
      "Updates and rewrites a resume to match the requirements of a specific job.",
    fields: [
      {
        type: "textArea",
        label: "Current Resume",
        field: "Current Resume",
        placeholder:
          "John Doe, Software Developer with 5 years of experience...",
        required: true,
        optional: false,
        id: "currentresume",
      },
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder:
          "We are looking for a Software Developer with experience in...",
        required: false,
        optional: true,
        id: "jobdescription",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in tailoring resumes to match specific job descriptions. You will be provided with a current resume and a job description, and you will generate a revised resume that highlights the most relevant skills and experiences.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { currentResume, jobDescription } = fields;
      let prompt =
        "Tailor the following resume based on the job description provided:\n";
      prompt += "Current Resume: " + currentResume + "\n";
      if (jobDescription) {
        prompt += "Job Description: " + jobDescription + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "email-enhancer-generator",
    name: "Email Enhancer Generator",
    slug: "email-enhancer-generator",
    shortDescription:
      "Improves the quality and effectiveness of your email by optimizing its structure, language, and content.",
    fields: [
      {
        type: "textArea",
        label: "Email Text",
        field: "Email Text",
        placeholder: "Dear John, I hope this email finds you well...",
        required: true,
        optional: false,
        id: "emailtext",
      },
    ],
    categories: ["Email", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing email communication. You will be provided with the text of an email, and your task is to improve its quality and effectiveness by optimizing its structure, language, and content.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { emailText } = fields;
      let prompt =
        "Enhance the following email for better clarity and impact:\n";
      prompt += "Email Text: " + emailText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "universal-translator",
    name: "Universal Translator",
    slug: "universal-translator",
    shortDescription:
      "Translates a given piece of text to the target language.",
    fields: [
      {
        type: "textArea",
        label: "Text to Translate",
        field: "Text to Translate",
        placeholder: "Bonjour",
        required: true,
        optional: false,
        id: "texttotranslate",
      },
      {
        type: "textArea",
        label: "Target Language",
        field: "Target Language",
        placeholder: "English",
        required: false,
        optional: true,
        id: "targetlanguage",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert translator. You will be provided with a piece of text and the target language, and you will translate the text accordingly.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToTranslate, targetLanguage } = fields;
      let prompt =
        "Translate the following text into the specified target language:\n";
      prompt += "Text to translate: " + textToTranslate + "\n";
      if (targetLanguage) {
        prompt += "Target language: " + targetLanguage + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "discussion-post-responder",
    name: "Discussion Post Responder",
    slug: "discussion-post-responder",
    shortDescription:
      "Generate thoughtful and engaging responses to discussion posts effortlessly with our Discussion Post Responder tool.",
    fields: [
      {
        type: "textArea",
        label: "Discussion Post",
        field: "Discussion Post",
        placeholder: "Paste the post you are responding to here...",
        required: true,
        optional: false,
        id: "discussionpost",
      },
      {
        type: "textArea",
        label: "Shorthand Response",
        field: "Shorthand Response",
        placeholder:
          "Write a brief response here to guide the AI generated response...",
        required: false,
        optional: true,
        id: "shorthandresponse",
      },
    ],
    categories: ["Social Media", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thoughtful and engaging responses to discussion posts. You will be provided with a discussion post and a shorthand response to guide your reply.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { discussionPost, shorthandResponse } = fields;
      let prompt = "Generate a response to the following discussion post:\n";
      prompt += "Discussion Post: " + discussionPost + "\n";
      if (shorthandResponse) {
        prompt += "Shorthand Response: " + shorthandResponse + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writing-camouflage",
    name: "AI Writing Camouflage",
    slug: "ai-writing-camouflage",
    shortDescription:
      "Transform AI-generated text to resemble human writing, effectively bypassing detection and enhancing content authenticity.",
    fields: [
      {
        type: "textArea",
        label: "AI Generated Text",
        field: "AI Generated Text",
        placeholder: "In the realm of artificial intelligence...",
        required: true,
        optional: false,
        id: "aigeneratedtext",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in modifying AI-generated text to make it appear as if it was written by a human. Your task is to take the provided AI-generated text and transform it so that it bypasses detection as AI-created content.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { AIGeneratedText } = fields;
      let prompt =
        "Please modify the following AI-generated text to make it appear human-written:\n";
      prompt += "AI Generated Text: " + AIGeneratedText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ask-ai",
    name: "Ask AI",
    slug: "ask-ai",
    shortDescription:
      "Get instant AI-informed answers to your questions with Ask AI. Discover the power of intelligent responses today!",
    fields: [
      {
        type: "textArea",
        label: "User Question",
        field: "User Question",
        placeholder: "What is the capital of Australia?",
        required: true,
        optional: false,
        id: "userquestion",
      },
    ],
    categories: ["Support", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI assistant designed to answer questions across various topics. You will receive a user question and provide a well-informed, concise answer based on your knowledge.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userQuestion } = fields;
      let prompt = "Provide a detailed answer to the following question:\n";
      prompt += "User Question: " + userQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-document-creator",
    name: "AI Document Creator",
    slug: "ai-document-creator",
    shortDescription:
      "Create complete documents effortlessly with our AI Document Creator by inputting your topic, type, and key points.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "The Impact of AI in Healthcare",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Document Type",
        field: "Document Type",
        placeholder: "Research Paper",
        required: false,
        optional: true,
        id: "documenttype",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. AI in diagnosis 2. AI in patient care 3. Ethical considerations",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating documents. You will be provided with a topic, document type, and key points, and you will create a complete document based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, DocumentType, KeyPoints } = fields;
      let prompt = "Generate a document based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (DocumentType) {
        prompt += "Document Type: " + DocumentType + "\n";
      }
      if (KeyPoints) {
        prompt += "Key Points: " + KeyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-journalist-generator",
    name: "AI Journalist Generator",
    slug: "ai-journalist-generator",
    shortDescription:
      "Create in-depth articles effortlessly with our AI Journalist Generator—your agent for research and writing on any topic.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Claude 3",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert AI journalist. You will be provided with a topic and you will research and write a comprehensive article based on that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields;
      let prompt =
        "Write a comprehensive article based on the following topic:\n";
      prompt += "Topic: " + Topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ascii-art-creator",
    name: "ASCII Art Creator",
    slug: "ascii-art-creator",
    shortDescription:
      "Generate stunning ASCII art effortlessly with our creator, transforming your descriptions into unique text-based visuals.",
    fields: [
      {
        type: "textArea",
        label: "Object",
        field: "Object",
        placeholder: "Cat",
        required: true,
        optional: false,
        id: "object",
      },
    ],
    categories: ["Social Media", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating ASCII art. You will be provided with a description of an object, and you will create ASCII art based on that description.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Object } = fields;
      let prompt = "Generate ASCII art for the following object:\n";
      prompt += "Object: " + Object + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "reflection-writer",
    name: "Reflection Writer",
    slug: "reflection-writer",
    shortDescription:
      "Generate insightful reflective essays or journal entries effortlessly with Reflection Writer, tailored to your unique experiences.",
    fields: [
      {
        type: "textArea",
        label: "Event Description",
        field: "Event Description",
        placeholder: "Describe the event or experience...",
        required: true,
        optional: false,
        id: "eventdescription",
      },
      {
        type: "textArea",
        label: "User's Reactions",
        field: "User's Reactions",
        placeholder: "How did you react or feel about the event?",
        required: false,
        optional: true,
        id: "usersreactions",
      },
      {
        type: "textArea",
        label: "Lessons Learned",
        field: "Lessons Learned",
        placeholder:
          "What insights or lessons did you gain from this experience?",
        required: false,
        optional: true,
        id: "lessonslearned",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating reflective essays or journal entries. You will be provided with details about a specific event or experience, and you will create a reflective piece based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { eventDescription, usersReactions, lessonsLearned } = fields;
      let prompt =
        "Generate a reflective essay or journal entry based on the following:\n";
      prompt += "Event Description: " + eventDescription + "\n";
      if (usersReactions) {
        prompt += "User's Reactions: " + usersReactions + "\n";
      }
      if (lessonsLearned) {
        prompt += "Lessons Learned: " + lessonsLearned + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "peer-discussion-response-generator",
    name: "Peer Discussion Response Generator",
    slug: "peer-discussion-response-generator",
    shortDescription:
      "Generate thoughtful peer discussion responses effortlessly for online classes and forums with our Peer Discussion Response Generator.",
    fields: [
      {
        type: "textArea",
        label: "Peer's Discussion Post",
        field: "Peer's Discussion Post",
        placeholder: "In this week's reading, I found the concept of...",
        required: true,
        optional: false,
        id: "peersdiscussionpost",
      },
      {
        type: "textArea",
        label: "Your Perspective",
        field: "Your Perspective",
        placeholder: "I agree with your point about...",
        required: false,
        optional: true,
        id: "yourperspective",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thoughtful responses to peer discussion posts. You will be provided with a peer's discussion post and your perspective, and you will create a well-articulated response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { peerDiscussionPost, yourPerspective } = fields;
      let prompt =
        "Create a thoughtful response to the following peer discussion post:\n";
      prompt += "Peer's Discussion Post: " + peerDiscussionPost + "\n";
      if (yourPerspective) {
        prompt += "Your Perspective: " + yourPerspective + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "hypothesis-maker",
    name: "Hypothesis Maker",
    slug: "hypothesis-maker",
    shortDescription:
      "Generate a precise research hypothesis effortlessly with our Hypothesis Maker based on your specific research question.",
    fields: [
      {
        type: "textArea",
        label: "Research Question",
        field: "Research Question",
        placeholder: "Enter your research question",
        required: true,
        optional: false,
        id: "researchquestion",
      },
    ],
    categories: ["Education", "Writing", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating research hypotheses. You will be provided with a research question, and you will generate a relevant hypothesis based on that question.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchQuestion } = fields;
      let prompt = "Generate a research hypothesis based on the following:\n";
      prompt += "Research Question: " + researchQuestion + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "flexible-autowrite-generator",
    name: "Flexible AutoWrite Generator",
    slug: "flexible-autowrite-generator",
    shortDescription:
      "Harness the power of AI with our Flexible AutoWrite Generator to effortlessly create content and tackle any writing task!",
    fields: [
      {
        type: "textArea",
        label: "Task",
        field: "Task",
        placeholder:
          "Write an introduction paragraph for an SEO optimized blog post about how technology has changed the way we work and live, and artificial intelligence is the next phase of the future of work",
        required: true,
        optional: false,
        id: "task",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI writing assistant capable of generating content for various tasks. You will be provided with a specific task description, and you will generate a written response based on that description.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Task } = fields.Task;
      let prompt = "Generate content based on the following task:\n";
      prompt += "Task: " + Task + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "expert-marketing-assistant-generator",
    name: "Expert Marketing Assistant Generator",
    slug: "expert-marketing-assistant-generator",
    shortDescription:
      "Transform your marketing strategy with our AI-powered assistant, delivering expert advice and support for all your marketing tasks.",
    fields: [
      {
        type: "textArea",
        label: "Marketing Task",
        field: "Marketing Task",
        placeholder:
          "Write social media posts for all channels as well as an email to users, announcing our new feature that can help you write emails in one click using AI",
        required: true,
        optional: false,
        id: "marketingtask",
      },
    ],
    categories: ["Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert marketing assistant. You will be provided with a specific marketing task, and you will generate expert advice and assistance based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { marketingTask } = fields;
      let prompt =
        "Provide expert advice and assistance for the following marketing task:\n";
      prompt += "Marketing Task: " + marketingTask + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paragraph-generator",
    name: "Paragraph Generator",
    slug: "paragraph-generator",
    shortDescription:
      "Generate unique, high-quality paragraphs effortlessly based on your topic. Enhance your writing with our advanced paragraph generator!",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Artificial Intelligence",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating high-quality paragraphs. You will be provided with a topic, and you will generate a unique and natural-sounding paragraph based on that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields.Topic;
      let prompt =
        "Generate a high-quality paragraph based on the following topic:\n";
      prompt += "Topic: " + Topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "message-replier",
    name: "Message Replier",
    slug: "message-replier",
    shortDescription:
      "Generate engaging replies to chat and text messages effortlessly with our Message Replier tool for seamless communication.",
    fields: [
      {
        type: "textArea",
        label: "Message",
        field: "Message",
        placeholder: "Enter the message you received",
        required: true,
        optional: false,
        id: "message",
      },
    ],
    categories: ["Social Media", "Support", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating replies to chat and text messages. You will be provided with a message, and you will generate an appropriate and engaging reply based on the context of the message.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Message } = fields;
      let prompt = "Generate a reply to the following message:\n";
      prompt += "Message: " + Message + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-matcher",
    name: "Resume Matcher",
    slug: "resume-matcher",
    shortDescription:
      "Optimize your resume with Resume Matcher to align perfectly with job descriptions while keeping your original content intact.",
    fields: [
      {
        type: "textArea",
        label: "Original Resume",
        field: "Original Resume",
        placeholder:
          "John Doe, Software Engineer with 5 years of experience...",
        required: true,
        optional: false,
        id: "originalresume",
      },
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder:
          "We are looking for a Software Engineer with experience in...",
        required: false,
        optional: true,
        id: "jobdescription",
      },
    ],
    categories: ["Business", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in resume optimization. You will be provided with an original resume and a job description, and your task is to adjust the language and presentation of the resume to better match the job description while preserving the original content.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalResume, jobDescription } = fields;
      let prompt =
        "Adjust the following resume to better match the job description provided:\n";
      prompt += "Original Resume: " + originalResume + "\n";
      if (jobDescription) {
        prompt += "Job Description: " + jobDescription + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "expert-ai-research-assistant",
    name: "Expert AI Research Assistant",
    slug: "expert-ai-research-assistant",
    shortDescription:
      "Unlock expert insights with our AI Research Assistant, conducting in-depth research for actionable solutions on any topic.",
    fields: [
      {
        type: "textArea",
        label: "Search Term or Task",
        field: "Search Term or Task",
        placeholder: "Climate change impact on agriculture",
        required: true,
        optional: false,
        id: "searchtermortask",
      },
    ],
    categories: ["Research", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert AI research assistant. You will be provided with a search term or task, and your job is to conduct in-depth research on the topic and provide actionable insights.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { searchTermOrTask } = fields;
      let prompt =
        "Conduct in-depth research based on the following search term or task:\n";
      prompt += "Search Term/Task: " + searchTermOrTask + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "apa-citation-generator",
    name: "APA Citation Generator",
    slug: "apa-citation-generator",
    shortDescription:
      "Easily create accurate APA citations with our generator by simply entering an article name, URL, or source type.",
    fields: [
      {
        type: "textArea",
        label: "Source Name/URL",
        field: "Source Name/URL",
        placeholder:
          "Evaluating the Role of Trust in Consumer Adoption of Mobile Payment Systems: An Empirical Analysis or https://www.jstor.org/stable/10.1086/209325",
        required: true,
        optional: false,
        id: "sourcename/url",
      },
      {
        type: "textArea",
        label: "Source Type",
        field: "Source Type",
        placeholder: "Journal",
        required: false,
        optional: true,
        id: "sourcetype",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating APA citations. You will be provided with the name or URL of a source and its type, and you will generate a properly formatted APA citation based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { sourceNameUrl, sourceType } = fields;
      let prompt = "Generate an APA citation based on the following:\n";
      prompt += "Source Name/URL: " + sourceNameUrl + "\n";
      if (sourceType) {
        prompt += "Source Type: " + sourceType + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "proposal-generator",
    name: "Proposal Generator",
    slug: "proposal-generator",
    shortDescription:
      "Create tailored proposals effortlessly with our Proposal Generator, designed to align with your project ideas and goals.",
    fields: [
      {
        type: "textArea",
        label: "Project Idea",
        field: "Project Idea",
        placeholder:
          "Develop an online learning platform for disadvantaged youth",
        required: true,
        optional: false,
        id: "projectidea",
      },
      {
        type: "textArea",
        label: "Specific Goals",
        field: "Specific Goals",
        placeholder: "Increase literacy among disadvantaged youth",
        required: false,
        optional: true,
        id: "specificgoals",
      },
    ],
    categories: ["Business", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating detailed project proposals. You will be provided with a project idea and specific goals, and you will create a comprehensive proposal based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { projectIdea, specificGoals } = fields;
      let prompt = "Generate a detailed proposal based on the following:\n";
      prompt += "Project Idea: " + projectIdea + "\n";
      if (specificGoals) {
        prompt += "Specific Goals: " + specificGoals + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paragraph-expander",
    name: "Paragraph Expander",
    slug: "paragraph-expander",
    shortDescription:
      "Expand your ideas into high-quality paragraphs effortlessly with our Paragraph Expander tool for comprehensive writing.",
    fields: [
      {
        type: "textArea",
        label: "Idea or Text to Expand",
        field: "Idea or Text to Expand",
        placeholder: "Paste or type here!",
        required: true,
        optional: false,
        id: "ideaortexttoexpand",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in expanding ideas and texts into comprehensive paragraphs. You will be provided with a brief idea or text, and your task is to elaborate on it, providing depth and clarity.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { IdeaOrTextToExpand } = fields.IdeaOrTextToExpand;
      let prompt =
        "Expand the following idea or text into a comprehensive paragraph:\n";
      prompt += "Idea or Text: " + IdeaOrTextToExpand + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paragraph-expander",
    name: "Paragraph Expander",
    slug: "paragraph-expander",
    shortDescription:
      "Transform your writing effortlessly with our Paragraph Expander, turning brief ideas into detailed, engaging content.",
    fields: [
      {
        type: "textArea",
        label: "Original Paragraph",
        field: "Original Paragraph",
        placeholder:
          "The AI revolution is changing the world. It's impacting every sector and transforming our daily lives.",
        required: true,
        optional: false,
        id: "originalparagraph",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in expanding paragraphs. You will be provided with a brief paragraph, and your task is to elaborate on it, adding more detail and depth while maintaining the original message.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalParagraph } = fields;
      let prompt =
        "Expand the following paragraph into a more detailed and elaborate version:\n";
      prompt += "Original Paragraph: " + originalParagraph + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "instagram-post-composer",
    name: "Instagram Post Composer",
    slug: "instagram-post-composer",
    shortDescription:
      "Generate captivating Instagram captions effortlessly with our Post Composer, tailored to your image descriptions and hashtags.",
    fields: [
      {
        type: "textArea",
        label: "Image Description",
        field: "Image Description",
        placeholder:
          "A sunset over a serene lake with a silhouette of a lone tree",
        required: true,
        optional: false,
        id: "imagedescription",
      },
      {
        type: "textArea",
        label: "Hashtags",
        field: "Hashtags",
        placeholder: "#SunsetLovers #NaturePhotography #PeacefulMoments",
        required: false,
        optional: true,
        id: "hashtags",
      },
    ],
    categories: ["Social Media", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating engaging Instagram posts. You will be provided with an image description and optional hashtags, and you will generate a captivating Instagram caption based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { imageDescription, hashtags } = fields;
      let prompt =
        "Generate an Instagram post caption based on the following:\n";
      prompt += "Image Description: " + imageDescription + "\n";
      if (hashtags) {
        prompt += "Hashtags: " + hashtags + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "increase-word-count",
    name: "Increase Word Count",
    slug: "increase-word-count",
    shortDescription:
      "Boost your content's word count effortlessly, expanding text while preserving its original message for maximum impact.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Enter the text you want to expand here...",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in expanding text. Your task is to take a given piece of text and elaborate on it, increasing the word count while maintaining the original message and meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt =
        "Expand the following text to increase its word count without losing the original message:\n";
      prompt += "Original Text: " + originalText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "debate-assistant",
    name: "Debate Assistant",
    slug: "debate-assistant",
    shortDescription:
      "Generate compelling arguments for or against any proposition with our Debate Assistant. Enhance your debating skills today!",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Should the drinking age be lowered to 18?",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Education", "Writing", "Debate"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in debate preparation. You will be provided with a topic, and you will generate arguments for and against the given proposition.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields;
      let prompt =
        "Generate arguments for and against the following proposition:\n";
      prompt += "Topic: " + Topic + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-social-media-post-generator",
    name: "AI Social Media Post Generator",
    slug: "ai-social-media-post-generator",
    shortDescription:
      "Create engaging social media posts effortlessly with our AI Social Media Post Generator, tailored for any platform.",
    fields: [
      {
        type: "textArea",
        label: "Content",
        field: "Content",
        placeholder: "We are launching a new product...",
        required: true,
        optional: false,
        id: "content",
      },
      {
        type: "textArea",
        label: "Platform",
        field: "Platform",
        placeholder: "Instagram",
        required: false,
        optional: true,
        id: "platform",
      },
    ],
    categories: ["Social Media", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging social media posts. You will be provided with content and the platform for which the post is intended, and you will create a compelling social media post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Content, Platform } = fields;
      let prompt = "Generate a social media post based on the following:\n";
      prompt += "Content: " + Content + "\n";
      if (Platform) {
        prompt += "Platform: " + Platform + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "birthday-card-creator",
    name: "Birthday Card Creator",
    slug: "birthday-card-creator",
    shortDescription:
      "Create personalized birthday cards tailored to age, interests, and your relationship for a unique celebration experience.",
    fields: [
      {
        type: "textArea",
        label: "Recipient Age",
        field: "Recipient Age",
        placeholder: "18",
        required: true,
        optional: false,
        id: "recipientage",
      },
      {
        type: "textArea",
        label: "Recipient Interests",
        field: "Recipient Interests",
        placeholder: "Cooking, Sports",
        required: false,
        optional: true,
        id: "recipientinterests",
      },
      {
        type: "textArea",
        label: "Your Relationship with Recipient",
        field: "Your Relationship with Recipient",
        placeholder: "Best Friend",
        required: false,
        optional: true,
        id: "yourrelationshipwithrecipient",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized birthday cards. You will be provided with details about the recipient and your relationship with them, and you will generate a heartfelt birthday card message based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientAge, recipientInterests, relationship } = fields;
      let prompt =
        "Create a personalized birthday card message based on the following:\n";
      prompt += "Recipient Age: " + recipientAge + ".\n";
      if (recipientInterests) {
        prompt += "Recipient Interests: " + recipientInterests + ".\n";
      }
      if (relationship) {
        prompt += "Your Relationship with Recipient: " + relationship + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "academic-citation-finder",
    name: "Academic Citation Finder",
    slug: "academic-citation-finder",
    shortDescription:
      "Discover high-quality academic resources for citation purposes with our Academic Citation Finder. Simplify your research today!",
    fields: [
      {
        type: "textArea",
        label: "Research Topic",
        field: "Research Topic",
        placeholder: "Effects of Climate Change on Biodiversity",
        required: true,
        optional: false,
        id: "researchtopic",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in finding high-quality academic resources for citation purposes. You will be provided with a research topic and you will generate a list of relevant academic citations based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic } = fields;
      let prompt =
        "Find high-quality academic resources related to the following topic:\n";
      prompt += "Research Topic: " + researchTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "academic-writer-assistant",
    name: "Academic Writer Assistant",
    slug: "academic-writer-assistant",
    shortDescription:
      "Generate high-quality academic writing with our Academic Writer Assistant, complete with proper references for your research needs.",
    fields: [
      {
        type: "textArea",
        label: "Academic Writing Request",
        field: "Academic Writing Request",
        placeholder:
          "Write a 500-word essay on the impact of climate change on agriculture.",
        required: true,
        optional: false,
        id: "academicwritingrequest",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in academic writing. You will be provided with a request for an academic piece, and you will generate high-quality, natural language writing that includes proper academic references.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { AcademicWritingRequest } = fields;
      let prompt =
        "Generate an academic piece based on the following request:\n";
      prompt += "Request: " + AcademicWritingRequest + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "literature-review-ai",
    name: "Literature Review AI",
    slug: "literature-review-ai",
    shortDescription:
      "Effortlessly generate high-quality literature reviews with AI. Search scholarly articles and compile references for your research topics.",
    fields: [
      {
        type: "textArea",
        label: "Research Question or Topic",
        field: "Research Question or Topic",
        placeholder:
          "What are the differences between adult stem cells and embryonic stem cells?",
        required: true,
        optional: false,
        id: "researchquestionortopic",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in conducting literature reviews. You will be provided with a research question or topic, and you will search for relevant scholarly articles to compile a high-quality writing piece with references.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchQuestionOrTopic } = fields;
      let prompt =
        "Conduct a literature review based on the following research question or topic:\n";
      prompt += "Research Question/Topic: " + researchQuestionOrTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-lyrics-generator",
    name: "AI Lyrics Generator",
    slug: "ai-lyrics-generator",
    shortDescription:
      "Create unique song lyrics effortlessly with our AI Lyrics Generator, tailored to your chosen theme, genre, or mood.",
    fields: [
      {
        type: "textArea",
        label: "Theme or Keywords",
        field: "Theme or Keywords",
        placeholder: "Love, Adventure, Heartbreak, etc.",
        required: true,
        optional: false,
        id: "themeorkeywords",
      },
      {
        type: "textArea",
        label: "Genre",
        field: "Genre",
        placeholder: "Pop, Rock, Country, etc.",
        required: false,
        optional: true,
        id: "genre",
      },
      {
        type: "textArea",
        label: "Mood",
        field: "Mood",
        placeholder: "Happy, Sad, Excited, etc.",
        required: false,
        optional: true,
        id: "mood",
      },
    ],
    categories: ["Writing", "Music", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating song lyrics. You will be provided with a theme, genre, and mood, and you will create original song lyrics based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { themeOrKeywords, genre, mood } = fields;
      let prompt = "Generate original song lyrics based on the following:\n";
      prompt += "Theme or Keywords: " + themeOrKeywords + ".\n";
      if (genre) {
        prompt += "Genre: " + genre + ".\n";
      }
      if (mood) {
        prompt += "Mood: " + mood + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "smart-essay-composer",
    name: "Smart Essay Composer",
    slug: "smart-essay-composer",
    shortDescription:
      "Generate human-like, in-depth essays with Smart Essay Composer. Enhance clarity, tone, and style for any writing requirement.",
    fields: [
      {
        type: "textArea",
        label: "Essay Question",
        field: "Essay Question",
        placeholder:
          "Discuss the impact of climate change on global economies.",
        required: true,
        optional: false,
        id: "essayquestion",
      },
      {
        type: "textArea",
        label: "Additional Requirements",
        field: "Additional Requirements",
        placeholder: "1000",
        required: false,
        optional: true,
        id: "additionalrequirements",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in composing essays. You will be provided with an essay question and any additional requirements, and you will generate a well-structured essay that meets the specified criteria.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayQuestion, additionalRequirements } = fields;
      let prompt = "Generate an essay based on the following:\n";
      prompt += "Essay Question: " + essayQuestion + "\n";
      if (additionalRequirements) {
        prompt += "Additional Requirements: " + additionalRequirements + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "similarity-and-difference-analyzer",
    name: "Similarity and Difference Analyzer",
    slug: "similarity-and-difference-analyzer",
    shortDescription:
      "Analyze and highlight the similarities and differences between two texts effortlessly with our advanced Similarity and Difference Analyzer.",
    fields: [
      {
        type: "textArea",
        label: "Text 1",
        field: "Text 1",
        placeholder: "Enter the first text here",
        required: true,
        optional: false,
        id: "text1",
      },
      {
        type: "textArea",
        label: "Text 2",
        field: "Text 2",
        placeholder: "Enter the second text here",
        required: false,
        optional: true,
        id: "text2",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing texts. You will be provided with two texts, and your task is to analyze and highlight the similarities and differences between them.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { text1, text2 } = fields;
      let prompt =
        "Analyze the following texts for similarities and differences:\n";
      prompt += "Text 1: " + text1 + "\n";
      if (text2) {
        prompt += "Text 2: " + text2 + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "chemistry-assistant-generator",
    name: "Chemistry Assistant Generator",
    slug: "chemistry-assistant-generator",
    shortDescription:
      "Get detailed answers to your chemistry questions with our Chemistry Assistant Generator. Simplify problem-solving today!",
    fields: [
      {
        type: "textArea",
        label: "Chemistry Problem or Question",
        field: "Chemistry Problem or Question",
        placeholder: "What is the molecular structure of benzene?",
        required: true,
        optional: false,
        id: "chemistryproblemorquestion",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in chemistry. You will be provided with a chemistry problem or question, and you will generate a detailed answer or explanation based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { ChemistryProblemOrQuestion } = fields;
      let prompt =
        "Generate a detailed answer to the following chemistry problem or question:\n";
      prompt += "Question: " + ChemistryProblemOrQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-notes-generator",
    name: "AI Notes Generator",
    slug: "ai-notes-generator",
    shortDescription:
      "Generate comprehensive notes effortlessly with our AI Notes Generator, tailored to your topic and level for effective learning.",
    fields: [
      {
        type: "textArea",
        label: "Topic or Subject",
        field: "Topic or Subject",
        placeholder: "Quantum Physics",
        required: true,
        optional: false,
        id: "topicorsubject",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating comprehensive notes. You will be provided with a topic or subject, and you will create detailed notes based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicOrSubject } = fields;
      let prompt =
        "Generate a comprehensive set of notes based on the following:\n";
      prompt += "Topic or Subject: " + topicOrSubject + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-text-message-responder",
    name: "AI Text Message Responder",
    slug: "ai-text-message-responder",
    shortDescription:
      "Generate quick and smart responses with our AI Text Message Responder, transforming your shorthand into engaging replies effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Received Text Message",
        field: "Received Text Message",
        placeholder: "Hey! Long time no talk. How's everything going?",
        required: true,
        optional: false,
        id: "receivedtextmessage",
      },
      {
        type: "textArea",
        label: "Shorthand Response",
        field: "Shorthand Response",
        placeholder: "Good, busy with work",
        required: false,
        optional: true,
        id: "shorthandresponse",
      },
    ],
    categories: ["Social Media", "Support", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI text message responder. You will be provided with a received text message and a shorthand response. Your task is to generate a thoughtful and engaging reply based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { receivedTextMessage, shorthandResponse } = fields;
      let prompt = "Generate a response to the following text message:\n";
      prompt += "Received Text Message: " + receivedTextMessage + "\n";
      if (shorthandResponse) {
        prompt += "Shorthand Response: " + shorthandResponse + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "readability-enhancer",
    name: "Readability Enhancer",
    slug: "readability-enhancer",
    shortDescription:
      "Enhance your text's readability with our tool that simplifies complex sentences and improves grammar and structure effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Input Text",
        field: "Input Text",
        placeholder: "Enter text here...",
        required: true,
        optional: false,
        id: "inputtext",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing the readability of text. You will be provided with a piece of text, and your task is to simplify complex sentences, improve grammar, and enhance the overall structure for better readability.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { inputText } = fields;
      let prompt = "Enhance the readability of the following text:\n";
      prompt += inputText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "roast-ai",
    name: "Roast AI",
    slug: "roast-ai",
    shortDescription:
      "Generate hilarious roasts with Roast AI! Perfect for light-hearted fun or playful banter on any person or topic.",
    fields: [
      {
        type: "textArea",
        label: "Person or Topic",
        field: "Person or Topic",
        placeholder: "John Doe or Pineapples",
        required: true,
        optional: false,
        id: "personortopic",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a comedian and expert in generating humorous roasts. You will be provided with a person or topic, and you will create a light-hearted roast based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { personOrTopic } = fields;
      let prompt = "Generate a humorous roast based on the following:\n";
      prompt += "Person or Topic: " + personOrTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "summary-writer",
    name: "Summary Writer",
    slug: "summary-writer",
    shortDescription:
      "Generate concise and informative summaries effortlessly with our Summary Writer. Get clear insights from your text in no time!",
    fields: [
      {
        type: "textArea",
        label: "Text to Summarize",
        field: "Text to Summarize",
        placeholder: "In the world of AI...",
        required: true,
        optional: false,
        id: "texttosummarize",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing text. You will be provided with a piece of text, and your task is to generate a concise and informative summary based on the content provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToSummarize } = fields;
      let prompt = "Generate a summary based on the following text:\n";
      prompt += "Text: " + textToSummarize + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "famous-writer-style-mimic",
    name: "Famous Writer Style Mimic",
    slug: "famous-writer-style-mimic",
    shortDescription:
      "Generate text in the style of your favorite famous writers with our easy-to-use mimic tool. Unleash your creativity today!",
    fields: [
      {
        type: "textArea",
        label: "Famous Writer's Name",
        field: "Famous Writer's Name",
        placeholder: "Ernest Hemingway",
        required: true,
        optional: false,
        id: "famouswritersname",
      },
      {
        type: "textArea",
        label: "Input Text",
        field: "Input Text",
        placeholder: "The sun set over the hills, casting long shadows...",
        required: false,
        optional: true,
        id: "inputtext",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in mimicking the writing style of famous authors. You will be provided with the name of a famous writer and some input text, and you will generate new text that reflects the style of that writer.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { famousWriterName, inputText } = fields;
      let prompt =
        "Generate text in the style of " +
        famousWriterName +
        " based on the following input:\n";
      if (inputText) {
        prompt += "Input text: " + inputText + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "academic-essay-writer",
    name: "Academic Essay Writer",
    slug: "academic-essay-writer",
    shortDescription:
      "Effortlessly create academic essays with our writer, crafting drafts from prompts using resources and tailored instructions.",
    fields: [
      {
        type: "textArea",
        label: "Essay Prompt",
        field: "Essay Prompt",
        placeholder: "Discuss the effects of climate change",
        required: true,
        optional: false,
        id: "essayprompt",
      },
      {
        type: "textArea",
        label: "Additional Instructions",
        field: "Additional Instructions",
        placeholder: "",
        required: false,
        optional: true,
        id: "additionalinstructions",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in academic writing. You will be provided with an essay prompt and any additional instructions, and you will generate a draft of an academic essay based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayPrompt, additionalInstructions } = fields;
      let prompt =
        "Generate a draft of an academic essay based on the following:\n";
      prompt += "Essay Prompt: " + essayPrompt + "\n";
      if (additionalInstructions) {
        prompt += "Additional Instructions: " + additionalInstructions + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "word-count-reducer",
    name: "Word Count Reducer",
    slug: "word-count-reducer",
    shortDescription:
      "Easily condense your text while preserving its meaning with our Word Count Reducer. Simplify your writing today!",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Enter the text you want to condense here.",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in text summarization. Your task is to condense the provided text while retaining its original meaning and key points.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt =
        "Please condense the following text while preserving its meaning:\n";
      prompt += "Original Text: " + originalText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-business-plan-generator",
    name: "AI Business Plan Generator",
    slug: "ai-business-plan-generator",
    shortDescription:
      "Generate a comprehensive business plan effortlessly with our AI Business Plan Generator based on your business or product description.",
    fields: [
      {
        type: "textArea",
        label: "Business or Product Description",
        field: "Business or Product Description",
        placeholder: "We are a tech startup focused on...",
        required: true,
        optional: false,
        id: "businessorproductdescription",
      },
    ],
    categories: ["Business", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating comprehensive business plans. You will be provided with a description of a business or product, and you will generate a detailed business plan based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { businessOrProductDescription } = fields;
      let prompt =
        "Generate a comprehensive business plan based on the following:\n";
      prompt +=
        "Business or Product Description: " +
        businessOrProductDescription +
        ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "dialogue-generator",
    name: "Dialogue Generator",
    slug: "dialogue-generator",
    shortDescription:
      "Generate engaging dialogues effortlessly with our Dialogue Generator. Perfect for scenarios and character-driven storytelling!",
    fields: [
      {
        type: "textArea",
        label: "Characters",
        field: "Characters",
        placeholder:
          "John - a gruff, no-nonsense detective; Sarah - a witty, intelligent scientist",
        required: true,
        optional: false,
        id: "characters",
      },
      {
        type: "textArea",
        label: "Scenario",
        field: "Scenario",
        placeholder:
          "John and Sarah are trying to solve a mysterious crime that has occurred in a high-tech lab",
        required: false,
        optional: true,
        id: "scenario",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging dialogues. You will be provided with characters and a scenario, and you will create a dialogue based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { characters, scenario } = fields;
      let prompt = "Generate a dialogue based on the following:\n";
      prompt += "Characters: " + characters + ".\n";
      if (scenario) {
        prompt += "Scenario: " + scenario + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "email-generator",
    name: "Email Generator",
    slug: "email-generator",
    shortDescription:
      "Create high-quality emails effortlessly with our generator. Summarize, edit, and finalize your messages with user feedback!",
    fields: [
      {
        type: "textArea",
        label: "Email Summary",
        field: "Email Summary",
        placeholder: "Email to my team about our upcoming project deadline.",
        required: true,
        optional: false,
        id: "emailsummary",
      },
    ],
    categories: ["Email", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating high-quality emails. You will be provided with a summary of the email content, and you will create a well-structured email based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { emailSummary } = fields;
      let prompt = "Generate an email based on the following summary:\n";
      prompt += "Email Summary: " + emailSummary + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "expert-question-answering-generator",
    name: "Expert Question Answering Generator",
    slug: "expert-question-answering-generator",
    shortDescription:
      "Generate clear, expert answers effortlessly with our Question Answering Generator. Elevate your content with well-styled responses.",
    fields: [
      {
        type: "textArea",
        label: "Question",
        field: "Question",
        placeholder: "What is the best way to improve my English?",
        required: true,
        optional: false,
        id: "question",
      },
    ],
    categories: ["Support", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing clear and well-styled answers to questions. You will be given a question, and your task is to generate a comprehensive and informative response.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Question } = fields.Question;
      let prompt =
        "Generate a well-structured expert answer based on the following question:\n";
      prompt += "Question: " + Question + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-letter-writer",
    name: "AI Letter Writer",
    slug: "ai-letter-writer",
    shortDescription:
      "Create perfect letters effortlessly with our AI Letter Writer, tailoring them to your needs for any occasion.",
    fields: [
      {
        type: "textArea",
        label: "Letter Purpose",
        field: "Letter Purpose",
        placeholder: "Job Application, Business Proposal, Complaint, etc.",
        required: true,
        optional: false,
        id: "letterpurpose",
      },
      {
        type: "textArea",
        label: "Information to be Included",
        field: "Information to be Included",
        placeholder:
          "Details about the job position, business proposal, complaint, etc.",
        required: false,
        optional: true,
        id: "informationtobeincluded",
      },
    ],
    categories: ["Writing", "Business", "Email"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing letters. You will be provided with the purpose of the letter and any additional information, and you will generate a well-structured and coherent letter based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { letterPurpose, informationToBeIncluded } = fields;
      let prompt = "Generate a letter based on the following:\n";
      prompt += "Letter Purpose: " + letterPurpose + ".\n";
      if (informationToBeIncluded) {
        prompt +=
          "Information to be included: " + informationToBeIncluded + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "email-responder-generator",
    name: "Email Responder Generator",
    slug: "email-responder-generator",
    shortDescription:
      "Generate quick and effective email responses with our Email Responder Generator. Simplify your communication effortlessly!",
    fields: [
      {
        type: "textArea",
        label: "Email to Respond To",
        field: "Email to Respond To",
        placeholder: "Paste the email you want to respond to",
        required: true,
        optional: false,
        id: "emailtorespondto",
      },
      {
        type: "textArea",
        label: "Shorthand Response",
        field: "Shorthand Response",
        placeholder: "Thanks!",
        required: false,
        optional: true,
        id: "shorthandresponse",
      },
    ],
    categories: ["Email", "Support", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating email responses. You will be provided with an email that requires a response and a shorthand response to guide your reply.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { emailToRespondTo, shorthandResponse } = fields;
      let prompt = "Generate an email response based on the following:\n";
      prompt += "Email to respond to: " + emailToRespondTo + "\n";
      if (shorthandResponse) {
        prompt += "Shorthand response: " + shorthandResponse + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-beautifier",
    name: "Text Beautifier",
    slug: "text-beautifier",
    shortDescription:
      "Enhance your text effortlessly with our Text Beautifier, improving grammar, vocabulary, and overall structure for polished writing.",
    fields: [
      {
        type: "textArea",
        label: "Text to Beautify",
        field: "Text to Beautify",
        placeholder: "Enter the text you wish to beautify here...",
        required: true,
        optional: false,
        id: "texttobeautify",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in text enhancement and beautification. Your task is to improve the grammar, vocabulary, and overall structure of the provided text.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToBeautify } = fields;
      let prompt = "Enhance and beautify the following text:\n";
      prompt += textToBeautify + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-proposal-generator",
    name: "AI Proposal Generator",
    slug: "ai-proposal-generator",
    shortDescription:
      "Generate professional proposals effortlessly with our AI Proposal Generator, tailored to your project and client details.",
    fields: [
      {
        type: "textArea",
        label: "Project Details",
        field: "Project Details",
        placeholder: "Describe the project here",
        required: true,
        optional: false,
        id: "projectdetails",
      },
      {
        type: "textArea",
        label: "Objectives",
        field: "Objectives",
        placeholder: "List the objectives of the project",
        required: false,
        optional: true,
        id: "objectives",
      },
    ],
    categories: ["Business", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating professional proposals. You will be provided with project details and objectives, and you will generate a comprehensive proposal based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { projectDetails, objectives } = fields;
      let prompt = "Generate a professional proposal based on the following:\n";
      prompt += "Project Details: " + projectDetails + "\n";
      if (objectives) {
        prompt += "Objectives: " + objectives + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writing-generator",
    name: "AI Writing Generator",
    slug: "ai-writing-generator",
    shortDescription:
      "Create any written content effortlessly with our AI Writing Generator, tailored to meet your specific requests and needs.",
    fields: [
      {
        type: "textArea",
        label: "Writing Request",
        field: "Writing Request",
        placeholder: "Write a blog post about the latest AI advancements",
        required: true,
        optional: false,
        id: "writingrequest",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert AI writing assistant. You will be provided with a writing request and you will generate high-quality written content based on that request.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingRequest } = fields;
      let prompt = "Generate written content based on the following request:\n";
      prompt += "Writing Request: " + writingRequest + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-question-answerer",
    name: "AI Question Answerer",
    slug: "ai-question-answerer",
    shortDescription:
      "Get accurate and concise answers to your questions with our AI Question Answerer, harnessing advanced AI capabilities for instant support.",
    fields: [
      {
        type: "textArea",
        label: "User Question",
        field: "User Question",
        placeholder: "What is the capital of France?",
        required: true,
        optional: false,
        id: "userquestion",
      },
    ],
    categories: ["Support", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI assistant specialized in providing accurate and concise answers to user questions. You will receive a question from the user and you will generate a clear and informative response based on your knowledge.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { UserQuestion } = fields.UserQuestion;
      let prompt = "Provide a concise answer to the following question:\n";
      prompt += "User Question: " + UserQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "presentation-builder",
    name: "Presentation Builder",
    slug: "presentation-builder",
    shortDescription:
      "Create stunning and professional presentations effortlessly with our intuitive Presentation Builder. Engage your audience like never before!",
    fields: [
      {
        type: "textArea",
        label: "Presentation Topic",
        field: "Presentation Topic",
        placeholder: "The Impact of Climate Change",
        required: true,
        optional: false,
        id: "presentationtopic",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. Definition of Climate Change, 2. Causes of Climate Change, 3. Effects of Climate Change, 4. Solutions to Climate Change",
        required: false,
        optional: true,
        id: "keypoints",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "High school students",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Business", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating engaging and professional presentations. You will be provided with a topic and key points, and you will generate a presentation outline based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { presentationTopic, keyPoints, targetAudience } = fields;
      let prompt = "Generate a presentation based on the following:\n";
      prompt += "Presentation Topic: " + presentationTopic + ".\n";
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + ".\n";
      }
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "blog-post-generator",
    name: "Blog Post Generator",
    slug: "blog-post-generator",
    shortDescription:
      "Generate engaging, SEO-friendly blog posts effortlessly with our Blog Post Generator tailored to your chosen topic.",
    fields: [
      {
        type: "textArea",
        label: "Blog Topic",
        field: "Blog Topic",
        placeholder: "The Future of Artificial Intelligence",
        required: true,
        optional: false,
        id: "blogtopic",
      },
      {
        type: "textArea",
        label: "Keyword",
        field: "Keyword",
        placeholder: "AI trends",
        required: false,
        optional: true,
        id: "keyword",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating blog posts. You will be provided with a topic and keywords, and you will create an engaging and SEO-friendly blog post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { blogTopic, keyword } = fields;
      let prompt = "Generate a blog post based on the following:\n";
      prompt += "Blog Topic: " + blogTopic + ".\n";
      if (keyword) {
        prompt += "Keyword: " + keyword + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "generate-blog-post-titles",
    name: "Generate Blog Post Titles",
    slug: "generate-blog-post-titles",
    shortDescription:
      "Create catchy blog post titles effortlessly! Generate engaging ideas based on your topic or keywords for maximum impact.",
    fields: [
      {
        type: "textArea",
        label: "Topic or Keywords",
        field: "Topic or Keywords",
        placeholder: "Content Marketing and AI",
        required: true,
        optional: false,
        id: "topicorkeywords",
      },
    ],
    categories: ["Writing", "Marketing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating catchy and engaging blog post titles. You will be provided with a topic or set of keywords, and you will create a list of compelling titles based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicOrKeywords } = fields;
      let prompt =
        "Generate a list of catchy blog post titles based on the following:\n";
      prompt += "Topic or Keywords: " + topicOrKeywords + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "generate-essay-introduction",
    name: "Generate Essay Introduction",
    slug: "generate-essay-introduction",
    shortDescription:
      "Easily generate a compelling essay introduction based on your topic and thesis statement for academic success.",
    fields: [
      {
        type: "textArea",
        label: "Essay Topic",
        field: "Essay Topic",
        placeholder: "Global warming",
        required: true,
        optional: false,
        id: "essaytopic",
      },
      {
        type: "textArea",
        label: "Thesis Statement",
        field: "Thesis Statement",
        placeholder:
          "Global warming is caused by human activities and has serious consequences for the environment and society",
        required: false,
        optional: true,
        id: "thesisstatement",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating introductions for academic essays. You will be provided with a topic and a thesis statement, and you will create a compelling introduction paragraph based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayTopic, thesisStatement } = fields;
      let prompt =
        "Generate an introduction paragraph for an academic essay based on the following:\n";
      prompt += "Essay Topic: " + essayTopic + "\n";
      if (thesisStatement) {
        prompt += "Thesis Statement: " + thesisStatement + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "short-answer-generator",
    name: "Short Answer Generator",
    slug: "short-answer-generator",
    shortDescription:
      "Generate concise answers to your questions with our Short Answer Generator, delivering informative responses in one to two paragraphs.",
    fields: [
      {
        type: "textArea",
        label: "Question",
        field: "Question",
        placeholder: "What is the greenhouse effect?",
        required: true,
        optional: false,
        id: "question",
      },
    ],
    categories: ["Support", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing concise and informative answers. You will be given a question, and your task is to generate a clear and succinct answer in one to two paragraphs.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Question } = fields;
      let prompt = "Provide a concise answer to the following question:\n";
      prompt += "Question: " + Question + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "nickname-generator",
    name: "Nickname Generator",
    slug: "nickname-generator",
    shortDescription:
      "Generate unique and fun nicknames easily with our nickname generator. Get creative and find the perfect name today!",
    fields: [
      {
        type: "textArea",
        label: "Name",
        field: "Name",
        placeholder: "John",
        required: true,
        optional: false,
        id: "name",
      },
    ],
    categories: ["Social Media", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating unique and fun nicknames. You will be provided a name, and you will create a list of creative nicknames based on that name.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Name } = fields;
      let prompt =
        "Generate a list of unique and fun nicknames based on the following name:\n";
      prompt += "Name: " + Name + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "reference-and-quotation-finder",
    name: "Reference and Quotation Finder",
    slug: "reference-and-quotation-finder",
    shortDescription:
      "Easily find and retrieve references and quotations on any topic with our efficient Reference and Quotation Finder tool.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Leadership",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Writing", "Education", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in finding references and quotations. You will be provided with a topic, and you will search for and retrieve relevant references and quotations related to that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields;
      let prompt =
        "Search for references and quotations based on the following topic:\n";
      prompt += "Topic: " + Topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "tone-transformer",
    name: "Tone Transformer",
    slug: "tone-transformer",
    shortDescription:
      "Transform your writing with Tone Transformer, adjusting tone effortlessly to match your desired style and voice.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Insert your text here...",
        required: true,
        optional: false,
        id: "originaltext",
      },
      {
        type: "textArea",
        label: "Tone",
        field: "Tone",
        placeholder: "Set the desired tone",
        required: false,
        optional: true,
        id: "tone",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in transforming the tone of written content. You will be provided with original text and a desired tone, and you will adjust the text accordingly.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText, tone } = fields;
      let prompt = "Transform the following text to match the desired tone:\n";
      prompt += "Original Text: " + originalText + "\n";
      if (tone) {
        prompt += "Desired Tone: " + tone + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "research-paper-assistant",
    name: "Research Paper Assistant",
    slug: "research-paper-assistant",
    shortDescription:
      "Get expert help with your research papers! Our assistant guides you through writing based on your specific instructions and guidelines.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic",
        field: "Research Topic",
        placeholder: "Effects of Climate Change on Agriculture",
        required: true,
        optional: false,
        id: "researchtopic",
      },
      {
        type: "textArea",
        label: "Instructions",
        field: "Instructions",
        placeholder:
          "The paper should use APA citation style, and include at least 5 peer-reviewed sources...",
        required: false,
        optional: true,
        id: "instructions",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in assisting with research papers. You will be provided with a research topic and specific instructions, and you will generate content that adheres to academic standards.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchtopic, instructions } = fields;
      let prompt = "Generate a research paper based on the following:\n";
      prompt += "Research Topic: " + researchtopic + ".\n";
      if (instructions) {
        prompt += "Instructions: " + instructions + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "fanfic-story-creator",
    name: "FanFic Story Creator",
    slug: "fanfic-story-creator",
    shortDescription:
      "Create captivating fan fiction stories with our FanFic Story Creator based on your favorite characters and series!",
    fields: [
      {
        type: "textArea",
        label: "Character Name",
        field: "Character Name",
        placeholder: "Harry Potter",
        required: true,
        optional: false,
        id: "charactername",
      },
      {
        type: "textArea",
        label: "Series Name",
        field: "Series Name",
        placeholder: "Game of Thrones",
        required: false,
        optional: true,
        id: "seriesname",
      },
      {
        type: "textArea",
        label: "Other Input",
        field: "Other Input",
        placeholder: "Dragon, Magic, Adventure",
        required: false,
        optional: true,
        id: "otherinput",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging fan fiction stories. You will be provided with details about a character, series, and other inputs to create a captivating story.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { characterName, seriesName, otherInput } = fields;
      let prompt = "Generate a fan fiction story based on the following:\n";
      prompt += "Character Name: " + characterName + ".\n";
      if (seriesName) {
        prompt += "Series Name: " + seriesName + ".\n";
      }
      if (otherInput) {
        prompt += "Other Input: " + otherInput + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "write-like-shakespeare",
    name: "Write Like Shakespeare",
    slug: "write-like-shakespeare",
    shortDescription:
      "Generate text in the iconic style of Shakespeare with our tool. Write like the Bard on any topic and impress your audience!",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Love and Betrayal",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating text in the style of William Shakespeare. You will be provided with a topic, and you will create a piece of writing that reflects the unique and iconic language and themes of Shakespeare's works.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields;
      let prompt =
        "Generate a text in the style of Shakespeare based on the following topic:\n";
      prompt += "Topic: " + Topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "college-essay-creator",
    name: "College Essay Creator",
    slug: "college-essay-creator",
    shortDescription:
      "Transform your college essay prompt into a creative masterpiece with our College Essay Creator. Get started today!",
    fields: [
      {
        type: "textArea",
        label: "Essay Prompt",
        field: "Essay Prompt",
        placeholder:
          "Describe a problem you've solved or a problem you'd like to solve...",
        required: true,
        optional: false,
        id: "essayprompt",
      },
      {
        type: "textArea",
        label: "Brainstorming Ideas",
        field: "Brainstorming Ideas",
        placeholder:
          "Problem: Lack of clean water in rural areas... Solution: Portable water purification system...",
        required: false,
        optional: true,
        id: "brainstormingideas",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting college essays. You will be provided with an essay prompt and brainstorming ideas, and you will generate a well-written, creative essay based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayPrompt, brainstormingIdeas } = fields;
      let prompt = "Generate a college essay based on the following:\n";
      prompt += "Essay Prompt: " + essayPrompt + "\n";
      if (brainstormingIdeas) {
        prompt += "Brainstorming Ideas: " + brainstormingIdeas + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "song-lyric-composer",
    name: "Song Lyric Composer",
    slug: "song-lyric-composer",
    shortDescription:
      "Create unique song lyrics effortlessly with our lyric composer. Just provide a topic and watch your musical ideas come to life!",
    fields: [
      {
        type: "textArea",
        label: "Song Topic",
        field: "Song Topic",
        placeholder: "Love in the time of autumn",
        required: true,
        optional: false,
        id: "songtopic",
      },
    ],
    categories: ["Writing", "Music", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a talented songwriter with the ability to create original lyrics. You will be provided with a topic, and you will generate song lyrics based on that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { songTopic } = fields;
      let prompt =
        "Generate original song lyrics based on the following topic:\n";
      prompt += "Song Topic: " + songTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-card-generator",
    name: "AI Card Generator",
    slug: "ai-card-generator",
    shortDescription:
      "Create personalized greeting cards effortlessly with our AI Card Generator, tailored for any occasion and recipient!",
    fields: [
      {
        type: "textArea",
        label: "Occasion",
        field: "Occasion",
        placeholder: "Birthday, Anniversary, Graduation, etc.",
        required: true,
        optional: false,
        id: "occasion",
      },
      {
        type: "textArea",
        label: "Recipient Description",
        field: "Recipient Description",
        placeholder:
          "Details about the recipient like age, interests, relationship with the user, etc.",
        required: false,
        optional: true,
        id: "recipientdescription",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized greeting cards or messages. You will be provided with details about the occasion and the recipient, and you will create a heartfelt message based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Occasion, RecipientDescription } = fields;
      let prompt =
        "Generate a personalized greeting card message based on the following:\n";
      prompt += "Occasion: " + Occasion + ".\n";
      if (RecipientDescription) {
        prompt += "Recipient Description: " + RecipientDescription + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ats-friendly-cv-rewriter",
    name: "ATS-Friendly CV Rewriter",
    slug: "ats-friendly-cv-rewriter",
    shortDescription:
      "Transform your CV to be ATS-friendly with our Rewriter, tailored to match any job description for better job prospects.",
    fields: [
      {
        type: "textArea",
        label: "User's CV",
        field: "User's CV",
        placeholder:
          "John Doe, Software Engineer with 5 years of experience...",
        required: true,
        optional: false,
        id: "userscv",
      },
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder:
          "We are looking for a Software Engineer with experience in...",
        required: false,
        optional: true,
        id: "jobdescription",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting CVs to be more ATS-friendly. You will be provided with a user's CV and a job description, and you will generate a revised CV that aligns with the job requirements.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userCV, jobDescription } = fields;
      let prompt =
        "Rewrite the following CV to make it more ATS-friendly based on the provided job description:\n";
      prompt += "User's CV: " + userCV + "\n";
      if (jobDescription) {
        prompt += "Job Description: " + jobDescription + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "discussion-post-response-assistant",
    name: "Discussion Post Response Assistant",
    slug: "discussion-post-response-assistant",
    shortDescription:
      "Generate thoughtful discussion post responses effortlessly with our assistant, helping you craft ideas and drafts in no time.",
    fields: [
      {
        type: "textArea",
        label: "Initial Discussion Post",
        field: "Initial Discussion Post",
        placeholder: "Enter the initial discussion post here.",
        required: true,
        optional: false,
        id: "initialdiscussionpost",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thoughtful responses to discussion posts. You will be provided with an initial discussion post and you will generate a response that is engaging and relevant.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { initialDiscussionPost } = fields;
      let prompt =
        "Generate a thoughtful response to the following discussion post:\n";
      prompt += "Initial Discussion Post: " + initialDiscussionPost + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-love-letter-writer",
    name: "AI Love Letter Writer",
    slug: "ai-love-letter-writer",
    shortDescription:
      "Create heartfelt love letters effortlessly with our AI Love Letter Writer for Valentine's Day or any special occasion.",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Name",
        field: "Recipient's Name",
        placeholder: "Sarah",
        required: true,
        optional: false,
        id: "recipientsname",
      },
      {
        type: "textArea",
        label: "Details About Recipient",
        field: "Details About Recipient",
        placeholder: "She loves sunsets, we met at a coffee shop...",
        required: false,
        optional: true,
        id: "detailsaboutrecipient",
      },
      {
        type: "textArea",
        label: "Key Sentiments",
        field: "Key Sentiments",
        placeholder: "I appreciate her kindness, I love our shared laughter...",
        required: false,
        optional: true,
        id: "keysentiments",
      },
    ],
    categories: ["Writing", "Social Media", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing personalized love letters. You will be provided with details about the recipient and key sentiments, and you will generate a heartfelt love letter based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientName, detailsAboutRecipient, keySentiments } = fields;
      let prompt = "Generate a love letter based on the following:\n";
      prompt += "Recipient's Name: " + recipientName + ".\n";
      if (detailsAboutRecipient) {
        prompt += "Details About Recipient: " + detailsAboutRecipient + ".\n";
      }
      if (keySentiments) {
        prompt += "Key Sentiments: " + keySentiments + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "vocabulary-sentence-generator",
    name: "Vocabulary Sentence Generator",
    slug: "vocabulary-sentence-generator",
    shortDescription:
      "Generate simple and short sentences with our Vocabulary Sentence Generator, designed for high school students to enhance their vocabulary.",
    fields: [
      {
        type: "textArea",
        label: "Vocabulary Term",
        field: "Vocabulary Term",
        placeholder: "Euphoria",
        required: true,
        optional: false,
        id: "vocabularyterm",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating sentences using specified vocabulary terms. You will be provided with a vocabulary term and you will create simple, easy, and short sentences suitable for high school students.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { vocabularyTerm } = fields;
      let prompt =
        "Generate a simple sentence using the following vocabulary term:\n";
      prompt += "Vocabulary Term: " + vocabularyTerm + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "bio-writer",
    name: "Bio Writer",
    slug: "bio-writer",
    shortDescription:
      "Create a polished professional biography effortlessly with our Bio Writer, highlighting career details and achievements.",
    fields: [
      {
        type: "textArea",
        label: "Name",
        field: "Name",
        placeholder: "Rochelle",
        required: true,
        optional: false,
        id: "name",
      },
      {
        type: "textArea",
        label: "Job Title",
        field: "Job Title",
        placeholder: "Director of an NDIS Provider",
        required: false,
        optional: true,
        id: "jobtitle",
      },
      {
        type: "textArea",
        label: "Career Details",
        field: "Career Details",
        placeholder:
          "Extensive experience in nursing, aged care, and disability case management. Extensive knowledge of the justice health system.",
        required: false,
        optional: true,
        id: "careerdetails",
      },
    ],
    categories: ["Business", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating professional biographies. You will be provided with details about an individual, and you will create a compelling biography based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Name, JobTitle, CareerDetails } = fields;
      let prompt =
        "Generate a professional biography based on the following:\n";
      prompt += "Name: " + Name + ".\n";
      if (JobTitle) {
        prompt += "Job Title: " + JobTitle + ".\n";
      }
      if (CareerDetails) {
        prompt += "Career Details: " + CareerDetails + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "study-guide-creator",
    name: "Study Guide Creator",
    slug: "study-guide-creator",
    shortDescription:
      "Create comprehensive study guides effortlessly by inputting quiz questions and text from your college class. Enhance your learning!",
    fields: [
      {
        type: "textArea",
        label: "Quiz Questions and Answers",
        field: "Quiz Questions and Answers",
        placeholder: "Q: What is AI? A: Artificial Intelligence is...",
        required: true,
        optional: false,
        id: "quizquestionsandanswers",
      },
      {
        type: "textArea",
        label: "Text Sections",
        field: "Text Sections",
        placeholder:
          "Artificial Intelligence (AI) is the simulation of human intelligence...",
        required: false,
        optional: true,
        id: "textsections",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating study guides. You will be provided with quiz questions, answers, and text sections from a college class, and you will generate a comprehensive study guide based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { quizQuestionsAndAnswers, textSections } = fields;
      let prompt =
        "Generate a comprehensive study guide based on the following:\n";
      prompt += "Quiz Questions and Answers: " + quizQuestionsAndAnswers + "\n";
      if (textSections) {
        prompt += "Text Sections: " + textSections + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "quizmaster",
    name: "QuizMaster",
    slug: "quizmaster",
    shortDescription:
      "Create custom quizzes effortlessly with QuizMaster! Choose your topic, difficulty, and number of questions for a tailored experience.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Solar System",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Difficulty Level",
        field: "Difficulty Level",
        placeholder: "Easy, Medium, Hard",
        required: false,
        optional: true,
        id: "difficultylevel",
      },
      {
        type: "textArea",
        label: "Number of Questions",
        field: "Number of Questions",
        placeholder: "10",
        required: false,
        optional: true,
        id: "numberofquestions",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating quizzes. You will be provided with a topic, difficulty level, and the number of questions, and you will create a custom quiz based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, DifficultyLevel, NumberOfQuestions } = fields;
      let prompt = "Generate a custom quiz based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (DifficultyLevel) {
        prompt += "Difficulty Level: " + DifficultyLevel + "\n";
      }
      if (NumberOfQuestions) {
        prompt += "Number of Questions: " + NumberOfQuestions + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "legal-text-refiner",
    name: "Legal Text Refiner",
    slug: "legal-text-refiner",
    shortDescription:
      "Refine legal text effortlessly: enhance coherence and formality while preserving original ideas with our specialized rewriting tool.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder:
          "Enter the text that needs to be rewritten with legal terminology",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Legal", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in legal writing and text refinement. Your task is to rewrite provided text to incorporate appropriate legal terminology while maintaining the original structure and idea, ensuring coherence, conciseness, and formality.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt =
        "Rewrite the following text to incorporate appropriate legal terminology while maintaining its original structure and idea:\n";
      prompt += "Original Text: " + originalText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "fanfic-maker",
    name: "FanFic Maker",
    slug: "fanfic-maker",
    shortDescription:
      "Create unique fan fiction stories with FanFic Maker by combining your favorite characters, settings, and plot elements effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Characters",
        field: "Characters",
        placeholder: "Harry Potter, Hermione Granger, Ron Weasley",
        required: true,
        optional: false,
        id: "characters",
      },
      {
        type: "textArea",
        label: "Setting",
        field: "Setting",
        placeholder: "Hogwarts School of Witchcraft and Wizardry",
        required: false,
        optional: true,
        id: "setting",
      },
      {
        type: "textArea",
        label: "Plot Elements",
        field: "Plot Elements",
        placeholder:
          "A hidden room in the castle, a mysterious prophecy, a dangerous challenge",
        required: false,
        optional: true,
        id: "plotelements",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative writer specializing in fan fiction. You will be provided with characters, settings, and plot elements, and your task is to generate an engaging fan fiction story based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Characters, Setting, PlotElements } = fields;
      let prompt = "Generate a fan fiction story based on the following:\n";
      prompt += "Characters: " + Characters + ".\n";
      if (Setting) {
        prompt += "Setting: " + Setting + ".\n";
      }
      if (PlotElements) {
        prompt += "Plot Elements: " + PlotElements + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "fanfic-creator-ai",
    name: "FanFic Creator AI",
    slug: "fanfic-creator-ai",
    shortDescription:
      "Create unique fanfiction with FanFic Creator AI by inputting characters, settings, and plot points for endless storytelling possibilities.",
    fields: [
      {
        type: "textArea",
        label: "Characters",
        field: "Characters",
        placeholder: "Harry Potter, Hermione Granger, Ron Weasley",
        required: true,
        optional: false,
        id: "characters",
      },
      {
        type: "textArea",
        label: "Setting",
        field: "Setting",
        placeholder: "Hogwarts School of Witchcraft and Wizardry",
        required: false,
        optional: true,
        id: "setting",
      },
      {
        type: "textArea",
        label: "Key Plot Points",
        field: "Key Plot Points",
        placeholder:
          "1. Harry finds a mysterious artifact. 2. They discover its powers. 3. They must use it to defeat a new enemy.",
        required: false,
        optional: true,
        id: "keyplotpoints",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating fanfiction stories. You will be provided with characters, a setting, and key plot points, and you will create an engaging fanfiction narrative based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Characters, Setting, KeyPlotPoints } = fields;
      let prompt = "Generate a fanfiction story based on the following:\n";
      prompt += "Characters: " + Characters + ".\n";
      if (Setting) {
        prompt += "Setting: " + Setting + ".\n";
      }
      if (KeyPlotPoints) {
        prompt += "Key Plot Points: " + KeyPlotPoints + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "autohotkey-helper",
    name: "AutoHotkey Helper",
    slug: "autohotkey-helper",
    shortDescription:
      "Master AutoHotkey with our helpful guide! Learn scripting essentials and boost your productivity effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Question or Problem",
        field: "Question or Problem",
        placeholder: "How do I write a script to automate typing a phrase?",
        required: true,
        optional: false,
        id: "questionorproblem",
      },
      {
        type: "textArea",
        label: "Script (if applicable)",
        field: "Script (if applicable)",
        placeholder: "Paste your script here if you need help debugging",
        required: false,
        optional: true,
        id: "script(ifapplicable)",
      },
    ],
    categories: ["Education", "Support", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in AutoHotkey scripting. You will be provided with a question or problem related to AutoHotkey, and you will provide guidance or solutions based on the information given.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { questionOrProblem, script } = fields;
      let prompt = "Provide assistance for the following AutoHotkey inquiry:\n";
      prompt += "Question or Problem: " + questionOrProblem + "\n";
      if (script) {
        prompt += "Script provided for debugging: " + script + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-ad-generator",
    name: "AI Ad Generator",
    slug: "ai-ad-generator",
    shortDescription:
      "Create compelling ads effortlessly with our AI Ad Generator, tailored to your product info and target audience for maximum impact.",
    fields: [
      {
        type: "textArea",
        label: "Product Information",
        field: "Product Information",
        placeholder: "Our product is a high-tech vacuum cleaner that...",
        required: true,
        optional: false,
        id: "productinformation",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Busy homeowners with pets",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating engaging advertisements. You will be provided with product information and target audience details to generate effective ads.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { productInformation, targetAudience } = fields;
      let prompt = "Create an advertisement based on the following:\n";
      prompt += "Product Information: " + productInformation + "\n";
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essaymaster",
    name: "EssayMaster",
    slug: "essaymaster",
    shortDescription:
      "Create structured and coherent essays effortlessly with EssayMaster. Transform your ideas into compelling written content today!",
    fields: [
      {
        type: "textArea",
        label: "Essay Topic",
        field: "Essay Topic",
        placeholder: "The Impact of Technology on Education",
        required: true,
        optional: false,
        id: "essaytopic",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. Increased accessibility, 2. Enhanced learning methods, 3. Challenges and solutions",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in essay writing. You will be provided with a topic and key points, and you will generate a structured and coherent essay based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayTopic, keyPoints } = fields;
      let prompt = "Generate an essay based on the following:\n";
      prompt += "Essay Topic: " + essayTopic + "\n";
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "biology-assistant",
    name: "Biology Assistant",
    slug: "biology-assistant",
    shortDescription:
      "Get accurate answers to biology questions with our assistant, referencing scholarly articles and research papers for reliable information.",
    fields: [
      {
        type: "textArea",
        label: "Biology Question",
        field: "Biology Question",
        placeholder: "What is the process of photosynthesis?",
        required: true,
        optional: false,
        id: "biologyquestion",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in biology, capable of answering complex biology-related questions. You will be provided with a specific question and you will generate a detailed answer using scholarly articles and research papers as references.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { biologyQuestion } = fields;
      let prompt =
        "Provide a comprehensive answer to the following biology question:\n";
      prompt += "Question: " + biologyQuestion + "\n";
      prompt +=
        "Please include references from scholarly articles and research papers.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "generate-essay-conclusion",
    name: "Generate Essay Conclusion",
    slug: "generate-essay-conclusion",
    shortDescription:
      "Generate impactful essay conclusions by summarizing key points and restating your thesis for a lasting reader impression.",
    fields: [
      {
        type: "textArea",
        label: "Essay Topic",
        field: "Essay Topic",
        placeholder: "The benefits of online education",
        required: true,
        optional: false,
        id: "essaytopic",
      },
      {
        type: "textArea",
        label: "Essay Thesis",
        field: "Essay Thesis",
        placeholder:
          "Online education is a flexible, accessible, and affordable option for learners of all ages and backgrounds",
        required: false,
        optional: true,
        id: "essaythesis",
      },
      {
        type: "textArea",
        label: "Essay Summary",
        field: "Essay Summary",
        placeholder:
          "Online education allows learners to choose their own pace, schedule, and location; online education provides learners with a variety of courses and resources from different institutions and experts; online education reduces the costs and barriers associated with traditional education",
        required: false,
        optional: true,
        id: "essaysummary",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating essay conclusions. You will be provided with the essay topic, thesis, and summary, and you will create a conclusion paragraph that restates the thesis, summarizes the main points, and leaves a lasting impression on the reader.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayTopic, essayThesis, essaySummary } = fields;
      let prompt = "Generate a conclusion paragraph for the following essay:\n";
      prompt += "Essay Topic: " + essayTopic + "\n";
      if (essayThesis) {
        prompt += "Thesis: " + essayThesis + "\n";
      }
      if (essaySummary) {
        prompt += "Summary: " + essaySummary + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "all-q-answerbot",
    name: "All-Q AnswerBot",
    slug: "all-q-answerbot",
    shortDescription:
      "Get instant answers to your questions with All-Q AnswerBot, leveraging AI's knowledge and web search capabilities.",
    fields: [
      {
        type: "textArea",
        label: "Question",
        field: "Question",
        placeholder: "What is the meaning of life?",
        required: true,
        optional: false,
        id: "question",
      },
    ],
    categories: ["Support", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI AnswerBot designed to provide accurate and informative answers to a wide range of questions. You will be given a question, and your task is to generate a comprehensive response based on your knowledge and web search capabilities.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Question } = fields.Question;
      let prompt = "Provide a detailed answer to the following question:\n";
      prompt += "Question: " + Question + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "sentence-rewriter",
    name: "Sentence Rewriter",
    slug: "sentence-rewriter",
    shortDescription:
      "Transform your text effortlessly with our Sentence Rewriter, adjusting tone and style while keeping the original meaning intact.",
    fields: [
      {
        type: "textArea",
        label: "Original Sentence",
        field: "Original Sentence",
        placeholder: "The quick brown fox jumps over the lazy dog.",
        required: true,
        optional: false,
        id: "originalsentence",
      },
      {
        type: "textArea",
        label: "Desired Tone/Style/Perspective",
        field: "Desired Tone/Style/Perspective",
        placeholder: "Formal/Business",
        required: false,
        optional: true,
        id: "desiredtone/style/perspective",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting sentences and paragraphs. You will be provided with an original sentence and a desired tone, style, or perspective, and you will rewrite the text while preserving its original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalSentence, desiredToneStylePerspective } = fields;
      let prompt =
        "Rewrite the following sentence based on the provided criteria:\n";
      prompt += "Original Sentence: " + originalSentence + "\n";
      if (desiredToneStylePerspective) {
        prompt +=
          "Desired Tone/Style/Perspective: " +
          desiredToneStylePerspective +
          "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "initial-discussion-thread-starter",
    name: "Initial Discussion Thread Starter",
    slug: "initial-discussion-thread-starter",
    shortDescription:
      "Create engaging discussion threads effortlessly with our starter tool, generating initial posts tailored to your chosen topics.",
    fields: [
      {
        type: "textArea",
        label: "Discussion Topic",
        field: "Discussion Topic",
        placeholder: "The impact of technology on education",
        required: true,
        optional: false,
        id: "discussiontopic",
      },
      {
        type: "textArea",
        label: "Personal Perspective",
        field: "Personal Perspective",
        placeholder:
          "I believe technology has greatly improved the accessibility and quality of education...",
        required: false,
        optional: true,
        id: "personalperspective",
      },
    ],
    categories: ["Social Media", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging initial posts for discussion threads. You will be provided with a discussion topic and a personal perspective, and you will create an engaging post to start the conversation.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { discussionTopic, personalPerspective } = fields;
      let prompt =
        "Generate an initial post for a discussion thread based on the following:\n";
      prompt += "Discussion Topic: " + discussionTopic + "\n";
      if (personalPerspective) {
        prompt += "Personal Perspective: " + personalPerspective + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paragraph-extender",
    name: "Paragraph Extender",
    slug: "paragraph-extender",
    shortDescription:
      "Enhance your writing with our Paragraph Extender tool, effortlessly expanding paragraphs while keeping your style intact.",
    fields: [
      {
        type: "textArea",
        label: "Original Paragraph",
        field: "Original Paragraph",
        placeholder: "Once upon a time in a land far away...",
        required: true,
        optional: false,
        id: "originalparagraph",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in extending paragraphs. You will be provided with an original paragraph, and your task is to extend it while maintaining the same style and writing level.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalParagraph } = fields;
      let prompt =
        "Extend the following paragraph while keeping the same style and writing level:\n";
      prompt += "Original Paragraph: " + originalParagraph + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "facebook-post-generator",
    name: "Facebook Post Generator",
    slug: "facebook-post-generator",
    shortDescription:
      "Create engaging Facebook posts effortlessly with our generator, tailored to your input and desired outcomes.",
    fields: [
      {
        type: "textArea",
        label: "Description",
        field: "Description",
        placeholder: "We're launching a new product next week...",
        required: true,
        optional: false,
        id: "description",
      },
      {
        type: "textArea",
        label: "Desired Outcome",
        field: "Desired Outcome",
        placeholder: "Increase website visits",
        required: false,
        optional: true,
        id: "desiredoutcome",
      },
    ],
    categories: ["Social Media", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating Facebook posts. You will be provided with some base information and you will generate a Facebook post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Description, DesiredOutcome } = fields;
      let prompt = "Generate a Facebook post based on the following:\n";
      prompt += "Description: " + Description + "\n";
      if (DesiredOutcome) {
        prompt += "Desired outcome: " + DesiredOutcome + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "talk-to-anyone-ai-persona-chat",
    name: "Talk to Anyone — AI Persona Chat",
    slug: "talk-to-anyone-ai-persona-chat",
    shortDescription:
      "Engage in realistic conversations with anyone using AI Persona Chat, simulating real personas through public information.",
    fields: [
      {
        type: "textArea",
        label: "Person Name",
        field: "Person Name",
        placeholder: "Steve Jobs",
        required: true,
        optional: false,
        id: "personname",
      },
    ],
    categories: ["Social Media", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI that simulates conversations with various personas. You will be provided with the name of a person, and you will respond as if you are that person, using known public information about them.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { personName } = fields;
      let prompt = "Simulate a conversation with the following persona:\n";
      prompt += "Person Name: " + personName + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "business-contact-finder",
    name: "Business Contact Finder",
    slug: "business-contact-finder",
    shortDescription:
      "Quickly find business contact information: search results to get emails and phone numbers effortlessly. Boost your networking today!",
    fields: [
      {
        type: "textArea",
        label: "Business Name or Information",
        field: "Business Name or Information",
        placeholder: "Acme Corp",
        required: true,
        optional: false,
        id: "businessnameorinformation",
      },
    ],
    categories: ["Business", "Sales", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in finding business contacts. You will be provided with the name or information about a business, and you will search for the email and/or phone number associated with that business.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { businessNameOrInformation } = fields;
      let prompt = "Find the contact information for the following business:\n";
      prompt +=
        "Business Name or Information: " + businessNameOrInformation + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "speech-generator",
    name: "Speech Generator",
    slug: "speech-generator",
    shortDescription:
      "Create engaging speeches effortlessly with our Speech Generator, tailored to your topic, audience, and key points.",
    fields: [
      {
        type: "textArea",
        label: "Topic, Prompt, or Notes",
        field: "Topic, Prompt, or Notes",
        placeholder: "The impact of social media on children",
        required: true,
        optional: false,
        id: "topic,prompt,ornotes",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating speeches. You will be provided with a topic, audience details, and key points, and you will create an engaging and persuasive speech based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topic } = fields;
      let prompt = "Generate a speech based on the following:\n";
      prompt += "Topic: " + topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "literature-review-generator",
    name: "Literature Review Generator",
    slug: "literature-review-generator",
    shortDescription:
      "Generate a comprehensive literature review effortlessly with our Literature Review Generator, tailored to your research topic.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic",
        field: "Research Topic",
        placeholder: "The Ethics of Artificial Intelligence",
        required: true,
        optional: false,
        id: "researchtopic",
      },
    ],
    categories: ["Writing", "Education", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating literature reviews. You will be provided with a research topic and you will generate a comprehensive literature review based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic } = fields;
      let prompt =
        "Generate a comprehensive literature review based on the following:\n";
      prompt += "Research Topic: " + researchTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "literature-synthesis-assistant",
    name: "Literature Synthesis Assistant",
    slug: "literature-synthesis-assistant",
    shortDescription:
      "Streamline your research with our Literature Synthesis Assistant, summarizing key points from multiple sources effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Literature Piece 1",
        field: "Literature Piece 1",
        placeholder: "Paste the text of the first piece of literature here.",
        required: true,
        optional: false,
        id: "literaturepiece1",
      },
      {
        type: "textArea",
        label: "Literature Piece 2",
        field: "Literature Piece 2",
        placeholder: "Paste the text of the second piece of literature here.",
        required: false,
        optional: true,
        id: "literaturepiece2",
      },
      {
        type: "textArea",
        label: "Literature Piece 3",
        field: "Literature Piece 3",
        placeholder: "Paste the text of the third piece of literature here.",
        required: false,
        optional: true,
        id: "literaturepiece3",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in synthesizing and summarizing literature. You will be provided with multiple pieces of literature, and your task is to extract and summarize the key points from them.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { LiteraturePiece1, LiteraturePiece2, LiteraturePiece3 } = fields;
      let prompt =
        "Please synthesize and summarize the key points from the following literature pieces:\n";
      prompt += "1. " + LiteraturePiece1 + "\n";
      if (LiteraturePiece2) {
        prompt += "2. " + LiteraturePiece2 + "\n";
      }
      if (LiteraturePiece3) {
        prompt += "3. " + LiteraturePiece3 + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "outline-maker",
    name: "Outline Maker",
    slug: "outline-maker",
    shortDescription:
      "Generate unique, high-quality outlines for your writing projects effortlessly with our Outline Maker tool. Perfect for any topic!",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Effects of Climate Change",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Type of Writing",
        field: "Type of Writing",
        placeholder: "Research Paper",
        required: false,
        optional: true,
        id: "typeofwriting",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating outlines for various types of writing. You will be provided with a topic and the type of writing, and you will generate a structured outline based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, TypeOfWriting } = fields;
      let prompt = "Generate a detailed outline based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (TypeOfWriting) {
        prompt += "Type of Writing: " + TypeOfWriting + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "poem-generator",
    name: "Poem Generator",
    slug: "poem-generator",
    shortDescription:
      "Create unique and original poems effortlessly with our Poem Generator. Perfect for any topic or inspiration you provide!",
    fields: [
      {
        type: "textArea",
        label: "Topic or Sample Text",
        field: "Topic or Sample Text",
        placeholder: "Spring, renewal, new beginnings",
        required: true,
        optional: false,
        id: "topicorsampletext",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative poet. You will be provided with a topic or sample text, and you will generate an original poem based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicOrSampleText } = fields;
      let prompt = "Generate a poem based on the following:\n";
      prompt += "Topic or Sample Text: " + topicOrSampleText + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "assignment-evaluator",
    name: "Assignment Evaluator",
    slug: "assignment-evaluator",
    shortDescription:
      "Effortlessly assess assignments with our Assignment Evaluator, ensuring compliance with prompts and rubrics for optimal grading.",
    fields: [
      {
        type: "textArea",
        label: "Assignment Prompt",
        field: "Assignment Prompt",
        placeholder: "Write a 500-word essay on...",
        required: true,
        optional: false,
        id: "assignmentprompt",
      },
      {
        type: "textArea",
        label: "Rubric",
        field: "Rubric",
        placeholder: "Criteria 1: Understanding of the topic (20 points)...",
        required: false,
        optional: true,
        id: "rubric",
      },
      {
        type: "textArea",
        label: "Student Assignment",
        field: "Student Assignment",
        placeholder: "In this essay, I will discuss...",
        required: false,
        optional: true,
        id: "studentassignment",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in evaluating assignments. You will be provided with an assignment prompt, a rubric, and a student's submission. Your task is to evaluate the student's work based on the provided criteria.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { assignmentPrompt, rubric, studentAssignment } = fields;
      let prompt =
        "Evaluate the following assignment based on the provided rubric:\n";
      prompt += "Assignment Prompt: " + assignmentPrompt + "\n";
      if (rubric) {
        prompt += "Rubric: " + rubric + "\n";
      }
      if (studentAssignment) {
        prompt += "Student Assignment: " + studentAssignment + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "webpage-text-extractor",
    name: "Webpage Text Extractor",
    slug: "webpage-text-extractor",
    shortDescription:
      "Easily extract and display all readable text from any webpage with our powerful Webpage Text Extractor tool.",
    fields: [
      {
        type: "textArea",
        label: "Webpage URL",
        field: "Webpage URL",
        placeholder: "https://www.example.com",
        required: true,
        optional: false,
        id: "webpageurl",
      },
    ],
    categories: ["Website", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in extracting and presenting readable text from webpages. You will be provided with a URL, and your task is to extract all the relevant text content from that webpage.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { webpageURL } = fields;
      let prompt = "Extract all readable text from the following webpage:\n";
      prompt += "Webpage URL: " + webpageURL + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "study-notes-to-practice-exam-generator",
    name: "Study Notes to Practice Exam Generator",
    slug: "study-notes-to-practice-exam-generator",
    shortDescription:
      "Transform your study notes into effective practice exams for enhanced learning and information retention.",
    fields: [
      {
        type: "textArea",
        label: "Study Notes",
        field: "Study Notes",
        placeholder:
          "Chapter 1: Introduction to Economics...\n\n1. Economics is the study of...",
        required: true,
        optional: false,
        id: "studynotes",
      },
      {
        type: "textArea",
        label: "Number of Questions",
        field: "Number of Questions",
        placeholder: "10",
        required: false,
        optional: true,
        id: "numberofquestions",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in transforming study notes into practice exam questions. You will be provided with study notes and the number of questions to generate a practice exam based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { studyNotes, numberOfQuestions } = fields;
      let prompt =
        "Generate a practice exam based on the following study notes:\n";
      prompt += "Study Notes: " + studyNotes + "\n";
      if (numberOfQuestions) {
        prompt += "Number of Questions: " + numberOfQuestions + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-poster-creator",
    name: "AI Poster Creator",
    slug: "ai-poster-creator",
    shortDescription:
      "Create stunning posters effortlessly with our AI Poster Creator. Get tailored titles, descriptions, and image ideas to elevate your designs.",
    fields: [
      {
        type: "textArea",
        label: "Poster Title",
        field: "Poster Title",
        placeholder: "Save the Planet",
        required: true,
        optional: false,
        id: "postertitle",
      },
      {
        type: "textArea",
        label: "Poster Description",
        field: "Poster Description",
        placeholder:
          "A poster to promote environmental awareness and actions people can take to help.",
        required: false,
        optional: true,
        id: "posterdescription",
      },
      {
        type: "textArea",
        label: "Specific Needs/Ideas (Optional)",
        field: "Specific Needs/Ideas (Optional)",
        placeholder: "Include images of nature, use green color scheme",
        required: false,
        optional: true,
        id: "specificneeds/ideas(optional)",
      },
    ],
    categories: ["Marketing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating engaging posters. You will be provided with a title, description, and specific needs or ideas, and you will generate a comprehensive outline for the poster content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { PosterTitle, PosterDescription, SpecificNeedsIdeas } = fields;
      let prompt =
        "Generate a comprehensive outline for a poster based on the following:\n";
      prompt += "Poster Title: " + PosterTitle + "\n";
      if (PosterDescription) {
        prompt += "Poster Description: " + PosterDescription + "\n";
      }
      if (SpecificNeedsIdeas) {
        prompt += "Specific Needs/Ideas: " + SpecificNeedsIdeas + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "magic-write",
    name: "Magic Write",
    slug: "magic-write",
    shortDescription:
      "Generate high-quality text effortlessly with Magic Write, your co-author for fast first drafts and seamless writing tasks.",
    fields: [
      {
        type: "textArea",
        label: "Writing Task",
        field: "Writing Task",
        placeholder: "Write a blog post about the benefits of AI in education.",
        required: true,
        optional: false,
        id: "writingtask",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI writing assistant specialized in generating high-quality text for various writing tasks. You will be provided with a specific writing task and you will create a well-structured draft based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingTask } = fields;
      let prompt =
        "Generate a first draft based on the following writing task:\n";
      prompt += "Writing Task: " + writingTask + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "midjourney-ai-image-prompt-generator",
    name: "Midjourney AI Image Prompt Generator",
    slug: "midjourney-ai-image-prompt-generator",
    shortDescription:
      "Create stunning images effortlessly with the Midjourney AI Image Prompt Generator, turning your ideas into detailed prompts.",
    fields: [
      {
        type: "textArea",
        label: "Image Description",
        field: "Image Description",
        placeholder:
          "A serene forest with a river running through and a deer drinking from the river at sunset.",
        required: true,
        optional: false,
        id: "imagedescription",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating image prompts for the Midjourney AI image creator. You will be provided with a description and you will create a detailed prompt that can be used to generate an image.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { imageDescription } = fields;
      let prompt =
        "Create a detailed prompt for the Midjourney AI based on the following description:\n";
      prompt += "Image Description: " + imageDescription + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "chemistry-expert-ai",
    name: "Chemistry Expert AI",
    slug: "chemistry-expert-ai",
    shortDescription:
      "Discover Chemistry Expert AI: your go-to assistant for answering questions and solving chemistry problems with expert precision.",
    fields: [
      {
        type: "textArea",
        label: "Chemistry Problem",
        field: "Chemistry Problem",
        placeholder:
          "What is the balanced equation for the combustion of butane?",
        required: true,
        optional: false,
        id: "chemistryproblem",
      },
    ],
    categories: ["Education", "Support", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert chemistry assistant. You will be provided with a chemistry problem, and you will provide a detailed explanation or solution based on the information given.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { chemistryProblem } = fields;
      let prompt =
        "Please provide a detailed solution to the following chemistry problem:\n";
      prompt += "Chemistry Problem: " + chemistryProblem + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "question-answer-and-expander",
    name: "Question Answer and Expander",
    slug: "question-answer-and-expander",
    shortDescription:
      "Generate detailed paragraphs effortlessly with our Question Answer and Expander tool, perfect for any inquiry you have!",
    fields: [
      {
        type: "textArea",
        label: "Question",
        field: "Question",
        placeholder: "Why is the sky blue?",
        required: true,
        optional: false,
        id: "question",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating detailed responses to questions. You will be provided with a question, and your task is to generate a comprehensive paragraph that answers the question thoroughly.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Question } = fields.Question;
      let prompt =
        "Generate a detailed paragraph based on the following question:\n";
      prompt += "Question: " + Question + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "story-reviewer",
    name: "Story Reviewer",
    slug: "story-reviewer",
    shortDescription:
      "Get constructive feedback on your story with our Story Reviewer, helping you refine your narrative and enhance your writing skills.",
    fields: [
      {
        type: "textArea",
        label: "Story",
        field: "Story",
        placeholder: "Once upon a time...",
        required: true,
        optional: false,
        id: "story",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert story reviewer. You will be provided with a story and you will analyze it, providing constructive feedback on its strengths and areas for improvement.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Story } = fields;
      let prompt =
        "Please analyze the following story and provide constructive feedback:\n";
      prompt += "Story: " + Story + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paragraph-shortener",
    name: "Paragraph Shortener",
    slug: "paragraph-shortener",
    shortDescription:
      "Effortlessly condense long paragraphs into concise versions while keeping key points intact with our effective paragraph shortener.",
    fields: [
      {
        type: "textArea",
        label: "Long Paragraph",
        field: "Long Paragraph",
        placeholder: "Enter the long paragraph here...",
        required: true,
        optional: false,
        id: "longparagraph",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in condensing long paragraphs into shorter, concise versions. You will be provided with a long paragraph and your task is to maintain the main points while shortening it.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { longParagraph } = fields.LongParagraph;
      let prompt =
        "Please shorten the following paragraph while preserving its main points:\n";
      prompt += longParagraph + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "discussion-board-response-ideation",
    name: "Discussion Board Response Ideation",
    slug: "discussion-board-response-ideation",
    shortDescription:
      "Generate thoughtful and engaging discussion board responses effortlessly with our idea generation tool. Boost your participation today!",
    fields: [
      {
        type: "textArea",
        label: "Discussion Post",
        field: "Discussion Post",
        placeholder: "In this week's reading, I found the concept of...",
        required: true,
        optional: false,
        id: "discussionpost",
      },
      {
        type: "textArea",
        label: "Thoughts or Details to Include",
        field: "Thoughts or Details to Include",
        placeholder: "I agree that...",
        required: false,
        optional: true,
        id: "thoughtsordetailstoinclude",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thoughtful and engaging responses for discussion board posts. You will be provided with a discussion post and additional thoughts or details, and you will generate a response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { discussionPost, thoughtsOrDetails } = fields;
      let prompt =
        "Generate a thoughtful response to the following discussion post:\n";
      prompt += "Discussion Post: " + discussionPost + "\n";
      if (thoughtsOrDetails) {
        prompt += "Additional Thoughts or Details: " + thoughtsOrDetails + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "explain-with-ai",
    name: "Explain With AI",
    slug: "explain-with-ai",
    shortDescription:
      "Transform complex topics into simple explanations with Explain With AI. Understand anything easily and quickly!",
    fields: [
      {
        type: "textArea",
        label: "Topic or Information",
        field: "Topic or Information",
        placeholder: "Quantum Physics",
        required: true,
        optional: false,
        id: "topicorinformation",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in simplifying complex topics. You will be provided with a topic or piece of information, and your task is to transform it into an easy-to-understand explanation.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicOrInformation } = fields;
      let prompt = "Explain the following topic in simple terms:\n";
      prompt += "Topic or Information: " + topicOrInformation + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "character-description-generator",
    name: "Character Description Generator",
    slug: "character-description-generator",
    shortDescription:
      "Generate vivid character descriptions tailored to your story's setting with our easy-to-use Character Description Generator.",
    fields: [
      {
        type: "textArea",
        label: "Story Setting",
        field: "Story Setting",
        placeholder: "A bustling city in the 1920s...",
        required: true,
        optional: false,
        id: "storysetting",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating character descriptions. You will be provided with the story's setting, and you will generate a detailed description of a character that fits within that context.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { storySetting } = fields;
      let prompt = "Generate a character description based on the following:\n";
      prompt += "Story Setting: " + storySetting + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-email-generator",
    name: "AI Email Generator",
    slug: "ai-email-generator",
    shortDescription:
      "Generate personalized emails effortlessly with our AI Email Generator, tailored to your specific topics for effective communication.",
    fields: [
      {
        type: "textArea",
        label: "Email Topic",
        field: "Email Topic",
        placeholder:
          "I want to write an email to my professor about extending the deadline for my project.",
        required: true,
        optional: false,
        id: "emailtopic",
      },
    ],
    categories: ["Email", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized emails. You will be provided with a topic and you will create a detailed email based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { emailTopic } = fields;
      let prompt = "Generate a detailed email based on the following:\n";
      prompt += "Email Topic: " + emailTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "counter-argument-composer",
    name: "Counter Argument Composer",
    slug: "counter-argument-composer",
    shortDescription:
      "Generate concise counter arguments in just 5 sentences with our Counter Argument Composer. Enhance your debating skills today!",
    fields: [
      {
        type: "textArea",
        label: "Statement",
        field: "Statement",
        placeholder: "Climate change is not a serious issue.",
        required: true,
        optional: false,
        id: "statement",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in constructing counter arguments. You will be provided with a statement, and your task is to generate a concise counter argument that addresses the statement effectively.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Statement } = fields;
      let prompt =
        "Generate a concise counter argument to the following statement:\n";
      prompt += "Statement: " + Statement + "\n";
      prompt += "The counter argument should be within a limit of 5 sentences.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "blog-writer-ai",
    name: "Blog Writer AI",
    slug: "blog-writer-ai",
    shortDescription:
      "Generate high-quality, engaging blog posts effortlessly with Blog Writer AI. Just provide a description and watch it create magic!",
    fields: [
      {
        type: "textArea",
        label: "Blog Post Description",
        field: "Blog Post Description",
        placeholder: "A blog post about the benefits of AI in healthcare.",
        required: true,
        optional: false,
        id: "blogpostdescription",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating blog posts. You will be provided with a description of the blog post topic, and you will create a high-quality, engaging, and well-structured blog post based on that description.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { blogPostDescription } = fields;
      let prompt = "Generate a blog post based on the following:\n";
      prompt += "Blog Post Description: " + blogPostDescription + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "discussion-reply-generator",
    name: "Discussion Reply Generator",
    slug: "discussion-reply-generator",
    shortDescription:
      "Generate thoughtful replies effortlessly with our Discussion Reply Generator, enhancing your conversations and engagement online.",
    fields: [
      {
        type: "textArea",
        label: "Discussion Statement",
        field: "Discussion Statement",
        placeholder:
          "It is the government's responsibility to determine how people access essential health services at affordable cost.",
        required: true,
        optional: false,
        id: "discussionstatement",
      },
    ],
    categories: ["Social Media", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thoughtful replies to discussion statements. You will be provided with a discussion statement and you will generate a well-structured reply based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { discussionStatement } = fields;
      let prompt =
        "Generate a thoughtful reply to the following discussion statement:\n";
      prompt += "Discussion Statement: " + discussionStatement + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "youtube-script-writer",
    name: "YouTube Script Writer",
    slug: "youtube-script-writer",
    shortDescription:
      "Generate engaging, SEO-friendly YouTube scripts tailored to your topic and audience for maximum viewer engagement.",
    fields: [
      {
        type: "textArea",
        label: "Video Topic",
        field: "Video Topic",
        placeholder: "How to bake a chocolate cake",
        required: true,
        optional: false,
        id: "videotopic",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Beginner home bakers",
        required: false,
        optional: true,
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder: "Ingredients, Baking process, Decoration tips",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating YouTube scripts. You will be provided with a video topic, target audience, and key points, and you will create an engaging and SEO-friendly script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { videoTopic, targetAudience, keyPoints } = fields;
      let prompt = "Generate a YouTube script based on the following:\n";
      prompt += "Video Topic: " + videoTopic + "\n";
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + "\n";
      }
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "dalle-3-image-prompter",
    name: "Dalle-3 Image Prompter",
    slug: "dalle-3-image-prompter",
    shortDescription:
      "Generate stunning images effortlessly with Dalle-3 Image Prompter, your tool for creative and detailed image prompts.",
    fields: [
      {
        type: "textArea",
        label: "Image Theme",
        field: "Image Theme",
        placeholder: "Space exploration",
        required: true,
        optional: false,
        id: "imagetheme",
      },
      {
        type: "textArea",
        label: "Image Elements",
        field: "Image Elements",
        placeholder: "Astronaut, alien, spaceship",
        required: false,
        optional: true,
        id: "imageelements",
      },
      {
        type: "textArea",
        label: "Image Mood",
        field: "Image Mood",
        placeholder: "Mysterious, adventurous",
        required: false,
        optional: true,
        id: "imagemood",
      },
    ],
    categories: ["Marketing", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating creative prompts for Dalle-3 image generation. You will be provided with details about the image theme, elements, and mood, and you will create a detailed prompt for Dalle-3.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { imageTheme, imageElements, imageMood } = fields;
      let prompt = "Generate a Dalle-3 image prompt based on the following:\n";
      prompt += "Image Theme: " + imageTheme + ".\n";
      if (imageElements) {
        prompt += "Image Elements: " + imageElements + ".\n";
      }
      if (imageMood) {
        prompt += "Image Mood: " + imageMood + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "grammar-and-punctuation-corrector",
    name: "Grammar and Punctuation Corrector",
    slug: "grammar-and-punctuation-corrector",
    shortDescription:
      "Correct grammar and punctuation errors effortlessly with our reliable corrector tool. Perfect your writing in seconds!",
    fields: [
      {
        type: "textArea",
        label: "Text to Correct",
        field: "Text to Correct",
        placeholder: "Enter the text you want corrected here.",
        required: true,
        optional: false,
        id: "texttocorrect",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grammar and punctuation correction. You will be provided with a text that may contain errors, and your task is to correct any grammar and punctuation mistakes.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToCorrect } = fields;
      let prompt =
        "Please correct the following text for grammar and punctuation errors:\n";
      prompt += textToCorrect + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "discussion-board-response",
    name: "Discussion Board Response",
    slug: "discussion-board-response",
    shortDescription:
      "Generate thoughtful responses for discussion boards effortlessly, enhancing engagement and fostering meaningful conversations.",
    fields: [
      {
        type: "textArea",
        label: "Discussion Post",
        field: "Discussion Post",
        placeholder: "Post your discussion board content here...",
        required: true,
        optional: false,
        id: "discussionpost",
      },
      {
        type: "textArea",
        label: "Initial Thoughts",
        field: "Initial Thoughts",
        placeholder: "Write your initial thoughts here...",
        required: false,
        optional: true,
        id: "initialthoughts",
      },
    ],
    categories: ["Education", "Writing", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thoughtful responses to discussion board posts. You will be provided with a discussion post and any initial thoughts, and you will generate a relevant response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { discussionPost, initialThoughts } = fields;
      let prompt =
        "Generate a response to the following discussion board post:\n";
      prompt += "Discussion Post: " + discussionPost + "\n";
      if (initialThoughts) {
        prompt += "Initial Thoughts: " + initialThoughts + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-style-mimic",
    name: "AI Style Mimic",
    slug: "ai-style-mimic",
    shortDescription:
      "Transform your writing with AI Style Mimic, effortlessly rewriting text to match any desired style for seamless content creation.",
    fields: [
      {
        type: "textArea",
        label: "Style Reference Text",
        field: "Style Reference Text",
        placeholder: "It was the best of times, it was the worst of times...",
        required: true,
        optional: false,
        id: "stylereferencetext",
      },
      {
        type: "textArea",
        label: "Text to Rewrite",
        field: "Text to Rewrite",
        placeholder: "The cat sat on the mat.",
        required: false,
        optional: true,
        id: "texttorewrite",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in mimicking writing styles. You will be provided with a reference text and a text to rewrite, and your task is to rewrite the provided text to match the style of the reference text.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { styleReferenceText, textToRewrite } = fields;
      let prompt =
        "Rewrite the following text to match the style of the reference text:\n";
      prompt += "Style Reference Text: " + styleReferenceText + "\n";
      if (textToRewrite) {
        prompt += "Text to Rewrite: " + textToRewrite + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-poem-writer",
    name: "AI Poem Writer",
    slug: "ai-poem-writer",
    shortDescription:
      "Create unique poems with our AI Poem Writer, tailored to your themes, styles, and moods for a personalized touch.",
    fields: [
      {
        type: "textArea",
        label: "Theme",
        field: "Theme",
        placeholder: "Nature",
        required: true,
        optional: false,
        id: "theme",
      },
      {
        type: "textArea",
        label: "Style",
        field: "Style",
        placeholder: "Sonnet",
        required: false,
        optional: true,
        id: "style",
      },
      {
        type: "textArea",
        label: "Mood",
        field: "Mood",
        placeholder: "Joyful",
        required: false,
        optional: true,
        id: "mood",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a skilled poet capable of generating original poems. You will be provided with themes, styles, and moods, and you will create a custom poem based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Theme, Style, Mood } = fields;
      let prompt = "Generate a poem based on the following:\n";
      prompt += "Theme: " + Theme + ".\n";
      if (Style) {
        prompt += "Style: " + Style + ".\n";
      }
      if (Mood) {
        prompt += "Mood: " + Mood + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "grammarfixer",
    name: "GrammarFixer",
    slug: "grammarfixer",
    shortDescription:
      "Correct grammar mistakes effortlessly with GrammarFixer. Improve your writing and ensure clarity in every text you create.",
    fields: [
      {
        type: "textArea",
        label: "Input Text",
        field: "Input Text",
        placeholder: "Type or paste your text here.",
        required: true,
        optional: false,
        id: "inputtext",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grammar correction. You will be provided with a text containing grammatical errors, and your task is to correct those mistakes and provide a polished version of the text.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { inputText } = fields.InputText;
      let prompt =
        "Please correct the grammar mistakes in the following text:\n";
      prompt += inputText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "discussion-response-generator",
    name: "Discussion Response Generator",
    slug: "discussion-response-generator",
    shortDescription:
      "Generate engaging discussion responses effortlessly with our Discussion Response Generator for thoughtful interactions online.",
    fields: [
      {
        type: "textArea",
        label: "Original Post or Comment",
        field: "Original Post or Comment",
        placeholder: "AI has the potential to...",
        required: true,
        optional: false,
        id: "originalpostorcomment",
      },
      {
        type: "textArea",
        label: "Response Thoughts or Points",
        field: "Response Thoughts or Points",
        placeholder: "While I agree that AI has potential, I think...",
        required: false,
        optional: true,
        id: "responsethoughtsorpoints",
      },
    ],
    categories: ["Social Media", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thoughtful and engaging responses to discussion posts or comments. You will be provided with an original post or comment along with some thoughts or points for your response.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalPost, responseThoughts } = fields;
      let prompt = "Generate a thoughtful response based on the following:\n";
      prompt += "Original Post or Comment: " + originalPost + "\n";
      if (responseThoughts) {
        prompt += "Response Thoughts or Points: " + responseThoughts + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "social-media-assistant",
    name: "Social Media Assistant",
    slug: "social-media-assistant",
    shortDescription:
      "Elevate your social media game with expert assistance in crafting captions, strategizing posts, and boosting engagement effectively.",
    fields: [
      {
        type: "textArea",
        label: "Social Media Task",
        field: "Social Media Task",
        placeholder:
          "Create a caption for an Instagram post about my trip to Greece",
        required: true,
        optional: false,
        id: "socialmediatask",
      },
    ],
    categories: ["Social Media", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert social media assistant. You will be provided with a specific social media task, and you will generate high-quality responses to fulfill that task.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { socialMediaTask } = fields;
      let prompt =
        "Please provide a response for the following social media task:\n";
      prompt += "Task: " + socialMediaTask + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-outline-generator",
    name: "Essay Outline Generator",
    slug: "essay-outline-generator",
    shortDescription:
      "Create a comprehensive essay outline from introduction to conclusion with our easy-to-use generator and structured subtopic headlines.",
    fields: [
      {
        type: "textArea",
        label: "Essay Topic",
        field: "Essay Topic",
        placeholder: "The Impact of Climate Change on Biodiversity",
        required: true,
        optional: false,
        id: "essaytopic",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating detailed essay outlines. You will be provided with a specific essay topic, and you will create a structured outline that includes an introduction, conclusion, and 4 to 5 subtopic headlines.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayTopic } = fields;
      let prompt =
        "Generate a detailed essay outline based on the following topic:\n";
      prompt += "Essay Topic: " + essayTopic + "\n";
      prompt +=
        "The outline should include an introduction, conclusion, and 4 to 5 subtopic headlines.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-storyteller",
    name: "AI Storyteller",
    slug: "ai-storyteller",
    shortDescription:
      "Create captivating stories effortlessly with AI Storyteller. Just input a prompt and watch your narrative come to life!",
    fields: [
      {
        type: "textArea",
        label: "Story Prompt",
        field: "Story Prompt",
        placeholder: "",
        required: true,
        optional: false,
        id: "storyprompt",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative storyteller. You will be provided with a story prompt, and your task is to generate an engaging and imaginative story based on that prompt.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { storyPrompt } = fields;
      let prompt =
        "Create a captivating story based on the following prompt:\n";
      prompt += "Story Prompt: " + storyPrompt + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "abstract-to-title-generator",
    name: "Abstract-to-Title Generator",
    slug: "abstract-to-title-generator",
    shortDescription:
      "Generate concise, descriptive titles from your research paper abstracts effortlessly with our Abstract-to-Title Generator tool.",
    fields: [
      {
        type: "textArea",
        label: "Abstract",
        field: "Abstract",
        placeholder: "This study investigates the effects of...",
        required: true,
        optional: false,
        id: "abstract",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating titles for research papers and articles. You will be provided with an abstract, and you will generate a concise and descriptive title based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Abstract } = fields.Abstract;
      let prompt =
        "Generate a concise, descriptive title based on the following abstract:\n";
      prompt += "Abstract: " + Abstract + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "dungeons-dragons-world-builder",
    name: "Dungeons & Dragons World Builder",
    slug: "dungeons-dragons-world-builder",
    shortDescription:
      "Create immersive Dungeons & Dragons worlds with our world builder, generating richly detailed settings for unforgettable gameplay.",
    fields: [
      {
        type: "textArea",
        label: "World Features",
        field: "World Features",
        placeholder: "Floating islands",
        required: true,
        optional: false,
        id: "worldfeatures",
      },
      {
        type: "textArea",
        label: "Dominant Races",
        field: "Dominant Races",
        placeholder: "Humans, Elves, Dwarves",
        required: false,
        optional: true,
        id: "dominantraces",
      },
      {
        type: "textArea",
        label: "Key Conflicts or Story Line",
        field: "Key Conflicts or Story Line",
        placeholder: "War between human kingdoms, Dragon invasion",
        required: false,
        optional: true,
        id: "keyconflictsorstoryline",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating immersive and richly detailed Dungeons & Dragons worlds. You will be provided with specific features, races, and conflicts to generate a unique world for gameplay.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { worldFeatures, dominantRaces, keyConflicts } = fields;
      let prompt =
        "Generate a Dungeons & Dragons world based on the following:\n";
      prompt += "World Features: " + worldFeatures + ".\n";
      if (dominantRaces) {
        prompt += "Dominant Races: " + dominantRaces + ".\n";
      }
      if (keyConflicts) {
        prompt += "Key Conflicts or Story Line: " + keyConflicts + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-paragraph-generator",
    name: "AI Paragraph Generator",
    slug: "ai-paragraph-generator",
    shortDescription:
      "Generate high-quality, unique paragraphs effortlessly with our AI Paragraph Generator. Perfect for any keyword or topic!",
    fields: [
      {
        type: "textArea",
        label: "Keyword or Topic",
        field: "Keyword or Topic",
        placeholder: "Artificial Intelligence",
        required: true,
        optional: false,
        id: "keywordortopic",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating high-quality paragraphs. You will be provided with a keyword or topic, and you will generate a unique and straightforward paragraph based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { keywordOrTopic } = fields;
      let prompt = "Generate a paragraph based on the following:\n";
      prompt += "Keyword or Topic: " + keywordOrTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "history-assistant",
    name: "History Assistant",
    slug: "history-assistant",
    shortDescription:
      "Get detailed answers about historical events with our History Assistant. Explore any topic related to history effortlessly!",
    fields: [
      {
        type: "textArea",
        label: "History Question",
        field: "History Question",
        placeholder: "What were the causes of the French Revolution?",
        required: true,
        optional: false,
        id: "historyquestion",
      },
    ],
    categories: ["Education", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a knowledgeable History Assistant. You will be provided with a history-related question, and you will provide detailed information and insights about the historical event or topic in question.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { historyQuestion } = fields;
      let prompt =
        "Answer the following history question with detailed information:\n";
      prompt += "Question: " + historyQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "legal-text-refiner",
    name: "Legal Text Refiner",
    slug: "legal-text-refiner",
    shortDescription:
      "Revolutionize your legal documents with our Legal Text Refiner, ensuring accuracy, coherence, and formality in every rewrite.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder:
          "Enter the text that needs to be rewritten with legal terminology",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Legal", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in legal writing and text refinement. Your task is to rewrite the provided text to incorporate appropriate legal terminology while maintaining the original structure and idea, ensuring coherence, conciseness, and formality.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt =
        "Rewrite the following text to incorporate appropriate legal terminology while maintaining its original structure and idea:\n";
      prompt += "Original Text: " + originalText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "writing-improvement-assistant",
    name: "Writing Improvement Assistant",
    slug: "writing-improvement-assistant",
    shortDescription:
      "Enhance your writing with our assistant! Get tips on grammar, punctuation, and feedback to improve your writing quality.",
    fields: [
      {
        type: "textArea",
        label: "Text",
        field: "Text",
        placeholder: "Enter the text you want to improve here.",
        required: true,
        optional: false,
        id: "text",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert writing assistant. Your task is to provide tips and feedback to improve the quality of the text provided by the user, focusing on grammar, punctuation, and overall writing style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Text } = fields;
      let prompt =
        "Please provide feedback and suggestions to improve the following text:\n";
      prompt += Text + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "slogan-generator-ai",
    name: "Slogan Generator AI",
    slug: "slogan-generator-ai",
    shortDescription:
      "Create memorable and impactful slogans effortlessly with our Slogan Generator AI for your business, product, or campaign.",
    fields: [
      {
        type: "textArea",
        label: "Name",
        field: "Name",
        placeholder: "Your Business/Product/Campaign Name",
        required: true,
        optional: false,
        id: "name",
      },
      {
        type: "textArea",
        label: "Unique Selling Proposition",
        field: "Unique Selling Proposition",
        placeholder: "What makes your business/product/campaign unique?",
        required: false,
        optional: true,
        id: "uniquesellingproposition",
      },
    ],
    categories: ["Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating memorable and impactful slogans. You will be provided with the name of a business, product, or campaign along with its unique selling proposition, and you will create a catchy slogan based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Name, UniqueSellingProposition } = fields;
      let prompt = "Generate a slogan based on the following:\n";
      prompt += "Business/Product/Campaign Name: " + Name + ".\n";
      if (UniqueSellingProposition) {
        prompt +=
          "Unique Selling Proposition: " + UniqueSellingProposition + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "math-ai",
    name: "Math AI",
    slug: "math-ai",
    shortDescription:
      "Discover Math AI, an AI math solver that explains and teaches concepts with step-by-step guidance for accurate problem solving.",
    fields: [
      {
        type: "textArea",
        label: "Math Problem or Concept",
        field: "Math Problem or Concept",
        placeholder:
          "Solve for x: 2x + 5 = 15 or explain the Pythagorean theorem",
        required: true,
        optional: false,
        id: "mathproblemorconcept",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in solving and explaining math problems. You will be provided with a math problem or concept, and you will generate a detailed solution or explanation based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { MathProblemOrConcept } = fields.MathProblemOrConcept;
      let prompt =
        "Please solve or explain the following math problem or concept:\n";
      prompt += MathProblemOrConcept + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "business-finance-and-economics-expert",
    name: "Business, Finance and Economics Expert",
    slug: "business-finance-and-economics-expert",
    shortDescription:
      "Expert insights on business, finance, and economics. Get comprehensive answers to all your queries in these fields today!",
    fields: [
      {
        type: "textArea",
        label: "Query",
        field: "Query",
        placeholder:
          "What are the underlying drivers of inflation in an economy according to different schools of thought?",
        required: true,
        optional: false,
        id: "query",
      },
    ],
    categories: ["Business", "Finance", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in business, finance, and economics. You will be provided with a query related to these fields, and you will provide a comprehensive answer based on your expertise.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Query } = fields;
      let prompt = "Provide a detailed response to the following query:\n";
      prompt += "Query: " + Query + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "instruction-generator",
    name: "Instruction Generator",
    slug: "instruction-generator",
    shortDescription:
      "Generate detailed instructions to approach topics or solve problems effectively, guiding you without giving direct solutions.",
    fields: [
      {
        type: "textArea",
        label: "Topic or Problem",
        field: "Topic or Problem",
        placeholder: "How to bake a chocolate cake",
        required: true,
        optional: false,
        id: "topicorproblem",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating detailed instructions. You will be provided with a topic or problem, and you will create comprehensive instructions on how to approach it without providing the actual solution.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicOrProblem } = fields;
      let prompt =
        "Create detailed instructions based on the following topic or problem:\n";
      prompt += "Topic or Problem: " + topicOrProblem + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "response-generator",
    name: "Response Generator",
    slug: "response-generator",
    shortDescription:
      "Generate clear and concise responses effortlessly with our Response Generator, transforming shorthand into well-crafted replies.",
    fields: [
      {
        type: "textArea",
        label: "Shorthand Response",
        field: "Shorthand Response",
        placeholder: "Agreed, but need more data",
        required: true,
        optional: false,
        id: "shorthandresponse",
      },
      {
        type: "textArea",
        label: "Recipient's Reply",
        field: "Recipient's Reply",
        placeholder: "I think we should proceed with the project.",
        required: false,
        optional: true,
        id: "recipientsreply",
      },
    ],
    categories: ["Social Media", "Email", "Support", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating responses based on shorthand messages and replies. You will be provided with a shorthand response and the recipient's reply, and you will generate a well-written, medium-length response.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { shorthandResponse, recipientReply } = fields;
      let prompt = "Generate a response based on the following:\n";
      prompt += "Shorthand Response: " + shorthandResponse + "\n";
      if (recipientReply) {
        prompt += "Recipient's Reply: " + recipientReply + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "movie-plot-generator",
    name: "Movie Plot Generator",
    slug: "movie-plot-generator",
    shortDescription:
      "Generate unique movie plots effortlessly with our Movie Plot Generator by inputting your text or description.",
    fields: [
      {
        type: "textArea",
        label: "Text or Description",
        field: "Text or Description",
        placeholder: "A story about a time-traveling historian...",
        required: true,
        optional: false,
        id: "textordescription",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating movie plots. You will be provided with a description or text, and you will create a conceptual movie plot based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textOrDescription } = fields;
      let prompt = "Generate a movie plot based on the following:\n";
      prompt += "Description: " + textOrDescription + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "gpt-4-prompt-generator",
    name: "GPT-4 Prompt Generator",
    slug: "gpt-4-prompt-generator",
    shortDescription:
      "Generate engaging GPT-4 prompts effortlessly! Discover effective prompts tailored to your topic for enhanced creativity and productivity.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Climate Change",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating prompts for GPT-4. You will be provided with a topic and you will create an engaging and effective prompt based on that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields;
      let prompt =
        "Generate a prompt for GPT-4 based on the following topic:\n";
      prompt += "Topic: " + Topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-detector-and-text-humanizer",
    name: "AI Detector and Text Humanizer",
    slug: "ai-detector-and-text-humanizer",
    shortDescription:
      "Detect AI-generated text effortlessly and transform it into a more human-like style with our advanced AI Detector and Text Humanizer.",
    fields: [
      {
        type: "textArea",
        label: "Input Text",
        field: "Input Text",
        placeholder: "Enter the text you want to analyze and humanize.",
        required: true,
        optional: false,
        id: "inputtext",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in detecting AI-generated text and humanizing it. You will be provided with a piece of text, and your task is to analyze it and convert it into a more human-like style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { inputText } = fields.InputText;
      let prompt =
        "Analyze the following text and convert it into a more human-like style:\n";
      prompt += "Input Text: " + inputText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "skolar-ai",
    name: "Skolar AI",
    slug: "skolar-ai",
    shortDescription:
      "Skolar AI generates scholarly responses with research and citations, making academic tasks easier and more efficient.",
    fields: [
      {
        type: "textArea",
        label: "Term or Prompt",
        field: "Term or Prompt",
        placeholder: "Arabidopsis thaliana as a plant model organism",
        required: true,
        optional: false,
        id: "termorprompt",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating scholarly responses. You will be provided with a term or prompt, and you will generate a detailed response that includes research and citations.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { termOrPrompt } = fields;
      let prompt =
        "Generate a scholarly response based on the following term or prompt:\n";
      prompt += "Term or Prompt: " + termOrPrompt + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "lesson-plan-generator",
    name: "Lesson Plan Generator",
    slug: "lesson-plan-generator",
    shortDescription:
      "Create tailored lesson plans effortlessly with our generator. Customize by subject, grade level, and learning objectives for effective teaching.",
    fields: [
      {
        type: "textArea",
        label: "Subject or Topic",
        field: "Subject or Topic",
        placeholder: "Mathematics",
        required: true,
        optional: false,
        id: "subjectortopic",
      },
      {
        type: "textArea",
        label: "Grade Level",
        field: "Grade Level",
        placeholder: "Grade 5",
        required: false,
        optional: true,
        id: "gradelevel",
      },
      {
        type: "textArea",
        label: "Learning Objectives",
        field: "Learning Objectives",
        placeholder:
          "Students will be able to understand the concept of fractions.",
        required: false,
        optional: true,
        id: "learningobjectives",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating lesson plans. You will be provided with information about the subject, grade level, and learning objectives, and you will generate a comprehensive lesson plan based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { subjectOrTopic, gradeLevel, learningObjectives } = fields;
      let prompt =
        "Generate a comprehensive lesson plan based on the following:\n";
      prompt += "Subject or Topic: " + subjectOrTopic + ".\n";
      if (gradeLevel) {
        prompt += "Grade Level: " + gradeLevel + ".\n";
      }
      if (learningObjectives) {
        prompt += "Learning Objectives: " + learningObjectives + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "story-synopsis-generator",
    name: "Story Synopsis Generator",
    slug: "story-synopsis-generator",
    shortDescription:
      "Create compelling story synopses effortlessly with our generator. Get concise, meaningful texts of up to 50 characters for your stories.",
    fields: [
      {
        type: "textArea",
        label: "Short Story",
        field: "Short Story",
        placeholder: "Once upon a time in a town far away...",
        required: true,
        optional: false,
        id: "shortstory",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating concise and meaningful story synopses. You will be provided with a short story, and your task is to create a succinct description that captures the essence of the story.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { shortStory } = fields;
      let prompt =
        "Generate a concise synopsis for the following short story:\n";
      prompt += "Short Story: " + shortStory + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-for-research",
    name: "AI for Research",
    slug: "ai-for-research",
    shortDescription:
      "Streamline your academic research with AI. Effortlessly search scholarly sources and compile valuable information for your papers.",
    fields: [
      {
        type: "textArea",
        label: "Research Question or Topic",
        field: "Research Question or Topic",
        placeholder:
          "How can generative AI be utilized across various medical disciplines, including dentistry, to enhance diagnostic accuracy, personalize treatment plans, and improve patient outcomes?",
        required: true,
        optional: false,
        id: "researchquestionortopic",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in academic research. You will be provided with a research question or topic, and you will assist in conducting comprehensive research by searching scholarly sources and compiling relevant information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { ResearchQuestionOrTopic } = fields;
      let prompt =
        "Conduct comprehensive research based on the following question or topic:\n";
      prompt +=
        "Research Question or Topic: " + ResearchQuestionOrTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "difficult-multiple-choice-question-generator",
    name: "Difficult Multiple Choice Question Generator",
    slug: "difficult-multiple-choice-question-generator",
    shortDescription:
      "Generate 10 challenging multiple-choice questions on any topic with our easy-to-use Difficult Question Generator.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Quantum Physics",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating difficult multiple-choice questions. You will be provided with a specific topic, and you will create 10 challenging multiple-choice questions related to that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields;
      let prompt =
        "Generate 10 difficult multiple-choice questions based on the following topic:\n";
      prompt += "Topic: " + Topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "persuasive-speech-generator",
    name: "Persuasive Speech Generator",
    slug: "persuasive-speech-generator",
    shortDescription:
      "Generate compelling speeches effortlessly with our Persuasive Speech Generator tailored to your topic and audience.",
    fields: [
      {
        type: "textArea",
        label: "Speech Topic",
        field: "Speech Topic",
        placeholder: "The Importance of Recycling",
        required: true,
        optional: false,
        id: "speechtopic",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "High School Students",
        required: false,
        optional: true,
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "Environmental Impact, Economic Benefits, Personal Responsibility",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating persuasive speeches. You will be provided with a topic, target audience, and key points, and you will generate a compelling speech based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { speechTopic, targetAudience, keyPoints } = fields;
      let prompt = "Generate a persuasive speech based on the following:\n";
      prompt += "Speech Topic: " + speechTopic + ".\n";
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + ".\n";
      }
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-cover-letter-generator",
    name: "AI Cover Letter Generator",
    slug: "ai-cover-letter-generator",
    shortDescription:
      "Create personalized cover letters effortlessly with our AI generator, tailored to your job description, skills, and experiences.",
    fields: [
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "We are looking for a software engineer who...",
        required: true,
        optional: false,
        id: "jobdescription",
      },
      {
        type: "textArea",
        label: "Personal Skills",
        field: "Personal Skills",
        placeholder: "Problem-solving, teamwork, communication...",
        required: false,
        optional: true,
        id: "personalskills",
      },
      {
        type: "textArea",
        label: "Experiences",
        field: "Experiences",
        placeholder: "At my previous job with XYZ tech, I managed a team of...",
        required: false,
        optional: true,
        id: "experiences",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized cover letters. You will be provided with a job description, personal skills, and experiences, and you will create a tailored cover letter based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { jobDescription, personalSkills, experiences } = fields;
      let prompt =
        "Generate a personalized cover letter based on the following:\n";
      prompt += "Job Description: " + jobDescription + "\n";
      if (personalSkills) {
        prompt += "Personal Skills: " + personalSkills + "\n";
      }
      if (experiences) {
        prompt += "Experiences: " + experiences + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "email-tone-and-style-optimizer",
    name: "Email Tone and Style Optimizer",
    slug: "email-tone-and-style-optimizer",
    shortDescription:
      "Enhance your emails effortlessly! Our Email Tone and Style Optimizer corrects drafts based on your desired tone and formality.",
    fields: [
      {
        type: "textArea",
        label: "Email Draft",
        field: "Email Draft",
        placeholder: "Dear Sir or Madam, I am writing to...",
        required: true,
        optional: false,
        id: "emaildraft",
      },
      {
        type: "textArea",
        label: "Desired Tone",
        field: "Desired Tone",
        placeholder: "Friendly, Persuasive",
        required: false,
        optional: true,
        id: "desiredtone",
      },
      {
        type: "textArea",
        label: "Formality Level",
        field: "Formality Level",
        placeholder: "Formal, Casual",
        required: false,
        optional: true,
        id: "formalitylevel",
      },
    ],
    categories: ["Email", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in optimizing email tone and style. You will be provided with a draft email and specific instructions regarding the desired tone and formality level, and you will enhance the email accordingly.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { emailDraft, desiredTone, formalityLevel } = fields;
      let prompt =
        "Optimize the following email based on the provided criteria:\n";
      prompt += "Email Draft: " + emailDraft + "\n";
      if (desiredTone) {
        prompt += "Desired Tone: " + desiredTone + "\n";
      }
      if (formalityLevel) {
        prompt += "Formality Level: " + formalityLevel + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "tagline-generator-ai",
    name: "Tagline Generator AI",
    slug: "tagline-generator-ai",
    shortDescription:
      "Create catchy and memorable taglines effortlessly with our AI tagline generator tailored to your business or product details.",
    fields: [
      {
        type: "textArea",
        label: "Business/Product Name",
        field: "Business/Product Name",
        placeholder: "SuperClean",
        required: true,
        optional: false,
        id: "business/productname",
      },
      {
        type: "textArea",
        label: "Business/Product Description",
        field: "Business/Product Description",
        placeholder:
          "An eco-friendly cleaning product that leaves surfaces spotless and fresh.",
        required: false,
        optional: true,
        id: "business/productdescription",
      },
    ],
    categories: ["Marketing", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating catchy and memorable taglines. You will be provided with the name and description of a business or product, and you will create a tagline that encapsulates its essence.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Business_Product_Name, Business_Product_Description } = fields;
      let prompt = "Generate a catchy tagline based on the following:\n";
      prompt += "Business/Product Name: " + Business_Product_Name + "\n";
      if (Business_Product_Description) {
        prompt += "Description: " + Business_Product_Description + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "blog-title-generator",
    name: "Blog Title Generator",
    slug: "blog-title-generator",
    shortDescription:
      "Generate unique, SEO-optimized blog titles effortlessly with our Blog Title Generator based on your keywords and topics.",
    fields: [
      {
        type: "textArea",
        label: "Keywords",
        field: "Keywords",
        placeholder: "AI, Machine Learning, Automation",
        required: true,
        optional: false,
        id: "keywords",
      },
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "The Future of AI in Automation",
        required: false,
        optional: true,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Draft Blog",
        field: "Draft Blog",
        placeholder:
          "In this blog, we will discuss the role of AI in the future of automation...",
        required: false,
        optional: true,
        id: "draftblog",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating blog titles. You will be provided with keywords, a topic, or a draft blog, and you will generate unique, compelling, and SEO-optimized blog titles based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Keywords, Topic, DraftBlog } = fields;
      let prompt =
        "Generate unique and SEO-optimized blog titles based on the following:\n";
      prompt += "Keywords: " + Keywords + ".\n";
      if (Topic) {
        prompt += "Topic: " + Topic + ".\n";
      }
      if (DraftBlog) {
        prompt += "Draft Blog: " + DraftBlog + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-analyzer-and-highlighter",
    name: "Text Analyzer and Highlighter",
    slug: "text-analyzer-and-highlighter",
    shortDescription:
      "Analyze and highlight key information in your text effortlessly with our powerful Text Analyzer and Highlighter tool.",
    fields: [
      {
        type: "textArea",
        label: "Text",
        field: "Text",
        placeholder: "Enter the text here...",
        required: true,
        optional: false,
        id: "text",
      },
      {
        type: "textArea",
        label: "Specific Instructions (Optional)",
        field: "Specific Instructions (Optional)",
        placeholder: "Any specific instructions for how to analyze the text?",
        required: false,
        optional: true,
        id: "specificinstructions(optional)",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in text analysis. You will be provided with a piece of text and specific instructions, if any, to analyze the text and highlight the most important information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Text, SpecificInstructions } = fields;
      let prompt =
        "Analyze the following text and highlight the most important information:\n";
      prompt += "Text: " + Text + "\n";
      if (SpecificInstructions) {
        prompt += "Specific Instructions: " + SpecificInstructions + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "chemistry-tutor",
    name: "Chemistry Tutor",
    slug: "chemistry-tutor",
    shortDescription:
      "Get expert answers and explanations for all your chemistry questions with our dedicated chemistry tutor. Enhance your understanding today!",
    fields: [
      {
        type: "textArea",
        label: "Chemistry Question",
        field: "Chemistry Question",
        placeholder: "What is the molecular structure of water?",
        required: true,
        optional: false,
        id: "chemistryquestion",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert chemistry tutor. You will be provided with a chemistry-related question, and you will provide a detailed answer along with explanations to help the user understand the concept.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { chemistryQuestion } = fields;
      let prompt =
        "Provide a detailed answer and explanation for the following chemistry question:\n";
      prompt += "Question: " + chemistryQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-write-in-my-style",
    name: "AI Write in My Style",
    slug: "ai-write-in-my-style",
    shortDescription:
      "Generate unique content that mirrors your style with AI Write in My Style, crafting personalized writing effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Writing Example 1",
        field: "Writing Example 1",
        placeholder: "Dear John, I am writing to inform you that...",
        required: true,
        optional: false,
        id: "writingexample1",
      },
      {
        type: "textArea",
        label: "Writing Example 2",
        field: "Writing Example 2",
        placeholder: "The sun was setting over the horizon...",
        required: false,
        optional: true,
        id: "writingexample2",
      },
      {
        type: "textArea",
        label: "Writing Request",
        field: "Writing Request",
        placeholder: "Write an email to a colleague about a project update.",
        required: false,
        optional: true,
        id: "writingrequest",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing that mimics the style and format of provided examples. You will be given writing samples and a request, and you will generate a piece of writing that aligns with the provided style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { WritingExample1, WritingExample2, WritingRequest } = fields;
      let prompt = "Generate a piece of writing based on the following:\n";
      prompt += "Writing Example 1: " + WritingExample1 + "\n";
      if (WritingExample2) {
        prompt += "Writing Example 2: " + WritingExample2 + "\n";
      }
      if (WritingRequest) {
        prompt += "Writing Request: " + WritingRequest + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "email-content-generator",
    name: "Email Content Generator",
    slug: "email-content-generator",
    shortDescription:
      "Create tailored email content effortlessly with our generator, customizing messages based on recipient, subject, and key points.",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Name",
        field: "Recipient's Name",
        placeholder: "John Doe",
        required: true,
        optional: false,
        id: "recipientsname",
      },
      {
        type: "textArea",
        label: "Email Subject",
        field: "Email Subject",
        placeholder: "Project Update",
        required: false,
        optional: true,
        id: "emailsubject",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "Update on project status, Next steps, Request for feedback",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Email", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating email content. You will be provided with the recipient's name, email subject, and key points to cover, and you will create a well-structured email based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientName, emailSubject, keyPoints } = fields;
      let prompt = "Generate an email based on the following:\n";
      prompt += "Recipient's Name: " + recipientName + ".\n";
      if (emailSubject) {
        prompt += "Email Subject: " + emailSubject + ".\n";
      }
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-thesis-statement",
    name: "Essay Thesis Statement",
    slug: "essay-thesis-statement",
    shortDescription:
      "Generate clear and concise thesis statements for essays based on your topic, position, and supporting reasons with ease.",
    fields: [
      {
        type: "textArea",
        label: "Essay Topic",
        field: "Essay Topic",
        placeholder: "The impact of social media on mental health",
        required: true,
        optional: false,
        id: "essaytopic",
      },
      {
        type: "textArea",
        label: "Author's Position",
        field: "Author's Position",
        placeholder: "Social media has negative effects on mental health.",
        required: false,
        optional: true,
        id: "authorsposition",
      },
      {
        type: "textArea",
        label: "Supporting Reasons",
        field: "Supporting Reasons",
        placeholder: "It causes depression, anxiety, and isolation.",
        required: false,
        optional: true,
        id: "supportingreasons",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thesis statements for essays. You will be provided with a topic, the author's position, and supporting reasons to create a clear and concise thesis statement.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayTopic, authorsPosition, supportingReasons } = fields;
      let prompt = "Generate a thesis statement based on the following:\n";
      prompt += "Essay Topic: " + essayTopic + ".\n";
      if (authorsPosition) {
        prompt += "Author's Position: " + authorsPosition + ".\n";
      }
      if (supportingReasons) {
        prompt += "Supporting Reasons: " + supportingReasons + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-script-writer",
    name: "AI Script Writer",
    slug: "ai-script-writer",
    shortDescription:
      "Create engaging scripts for movies, plays, or TV shows with our AI Script Writer—just provide your plot and character details!",
    fields: [
      {
        type: "textArea",
        label: "Plot",
        field: "Plot",
        placeholder: "A group of friends go on a road trip...",
        required: true,
        optional: false,
        id: "plot",
      },
      {
        type: "textArea",
        label: "Character Details",
        field: "Character Details",
        placeholder: "John, the serious and responsible leader...",
        required: false,
        optional: true,
        id: "characterdetails",
      },
      {
        type: "textArea",
        label: "Type of Script",
        field: "Type of Script",
        placeholder: "Movie scene",
        required: false,
        optional: true,
        id: "typeofscript",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert scriptwriter. You will be provided with a plot and character details, and you will generate a creative and engaging script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Plot, CharacterDetails, TypeOfScript } = fields;
      let prompt = "Generate a script based on the following:\n";
      prompt += "Plot: " + Plot + "\n";
      if (CharacterDetails) {
        prompt += "Character Details: " + CharacterDetails + "\n";
      }
      if (TypeOfScript) {
        prompt += "Type of Script: " + TypeOfScript + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paragraph-maker",
    name: "Paragraph Maker",
    slug: "paragraph-maker",
    shortDescription:
      "Generate high-quality paragraphs effortlessly with our Paragraph Maker, tailored to your specific input or request.",
    fields: [
      {
        type: "textArea",
        label: "Paragraph Request",
        field: "Paragraph Request",
        placeholder:
          "Write a paragraph about the importance of AI in healthcare.",
        required: true,
        optional: false,
        id: "paragraphrequest",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating high-quality paragraphs. You will be provided with a specific request, and you will create a detailed paragraph based on that request.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { paragraphRequest } = fields;
      let prompt =
        "Generate a detailed paragraph based on the following request:\n";
      prompt += "Paragraph Request: " + paragraphRequest + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "teenage-explainer",
    name: "Teenage Explainer",
    slug: "teenage-explainer",
    shortDescription:
      "Simplify complex concepts for teens! Our Teenage Explainer makes learning easy and fun for 13-year-olds.",
    fields: [
      {
        type: "textArea",
        label: "Complex Topic",
        field: "Complex Topic",
        placeholder: "Quantum Physics",
        required: true,
        optional: false,
        id: "complextopic",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in simplifying complex concepts for teenagers. You will be provided with a complex topic, and your task is to explain it in a way that is easily understood by a 13-year-old.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { complexTopic } = fields;
      let prompt =
        "Explain the following complex topic in simple terms suitable for a 13-year-old:\n";
      prompt += "Complex Topic: " + complexTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-body-paragraph-builder",
    name: "Essay Body Paragraph Builder",
    slug: "essay-body-paragraph-builder",
    shortDescription:
      "Easily create structured and coherent body paragraphs for your essays with our Essay Body Paragraph Builder tool.",
    fields: [
      {
        type: "textArea",
        label: "Essay Topic",
        field: "Essay Topic",
        placeholder: "Impact of Climate Change on Global Economy",
        required: true,
        optional: false,
        id: "essaytopic",
      },
      {
        type: "textArea",
        label: "Point to Make",
        field: "Point to Make",
        placeholder:
          "Climate change is disrupting agricultural patterns, leading to economic instability in agrarian societies.",
        required: false,
        optional: true,
        id: "pointtomake",
      },
      {
        type: "textArea",
        label: "Supporting Evidence",
        field: "Supporting Evidence",
        placeholder:
          "According to a 2020 report by the United Nations, climate change has led to unpredictable rainfall patterns, impacting crop yields in Sub-Saharan Africa...",
        required: false,
        optional: true,
        id: "supportingevidence",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting structured and coherent body paragraphs for essays. You will be provided with an essay topic, a point to make, and supporting evidence, and you will generate a well-structured body paragraph based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayTopic, pointToMake, supportingEvidence } = fields;
      let prompt =
        "Generate a body paragraph for an essay based on the following:\n";
      prompt += "Essay Topic: " + essayTopic + ".\n";
      if (pointToMake) {
        prompt += "Point to make: " + pointToMake + ".\n";
      }
      if (supportingEvidence) {
        prompt += "Supporting evidence: " + supportingEvidence + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "moot-court-research-assistant",
    name: "Moot Court Research Assistant",
    slug: "moot-court-research-assistant",
    shortDescription:
      "Explore how a Moot Court Research Assistant aids in law research and argument formulation for successful moot court competitions.",
    fields: [
      {
        type: "textArea",
        label: "Law",
        field: "Law",
        placeholder: "Title VII of the Civil Rights Act",
        required: true,
        optional: false,
        id: "law",
      },
      {
        type: "textArea",
        label: "Arguments",
        field: "Arguments",
        placeholder:
          "Argument 1: The employer's conduct constitutes discrimination...\nArgument 2: The employee's rights were violated...",
        required: false,
        optional: true,
        id: "arguments",
      },
    ],
    categories: ["Legal", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in legal research and argument formulation for moot court competitions. You will be provided with specific laws and arguments, and you will assist in generating comprehensive legal analyses and persuasive arguments based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Law, Arguments } = fields;
      let prompt =
        "Assist in researching and formulating arguments based on the following:\n";
      prompt += "Law: " + Law + "\n";
      if (Arguments) {
        prompt += "Arguments: " + Arguments + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "mcq-generator",
    name: "MCQ Generator",
    slug: "mcq-generator",
    shortDescription:
      "Generate engaging multiple-choice questions effortlessly with our MCQ Generator. Perfect for quizzes and educational content!",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Photosynthesis",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Difficulty/Grade Level",
        field: "Difficulty/Grade Level",
        placeholder: "Middle school biology",
        required: false,
        optional: true,
        id: "difficulty/gradelevel",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating multiple-choice questions (MCQs). You will be provided with a topic and optionally a difficulty level, and you will generate a set of MCQs based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, Difficulty } = fields;
      let prompt =
        "Generate multiple-choice questions based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (Difficulty) {
        prompt += "Difficulty/Grade Level: " + Difficulty + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "vocabulary-enhancer",
    name: "Vocabulary Enhancer",
    slug: "vocabulary-enhancer",
    shortDescription:
      "Elevate your writing with our Vocabulary Enhancer, suggesting advanced words to diversify and enrich your content effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Text",
        field: "Text",
        placeholder: "Enter your text here...",
        required: true,
        optional: false,
        id: "text",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing vocabulary. You will be provided with a piece of text, and your task is to suggest new and advanced vocabulary to improve and diversify the writing.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { text } = fields;
      let prompt = "Enhance the vocabulary of the following text:\n";
      prompt += text + "\n";
      prompt += "Provide suggestions for more advanced or diverse vocabulary.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "college-topic-explainer",
    name: "College Topic Explainer",
    slug: "college-topic-explainer",
    shortDescription:
      "Simplify complex topics with our engaging college explainer, designed to make learning easy and enjoyable for students.",
    fields: [
      {
        type: "textArea",
        label: "Topic or Question",
        field: "Topic or Question",
        placeholder: "Quantum Physics",
        required: true,
        optional: false,
        id: "topicorquestion",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in explaining complex topics in a simplified and engaging manner suitable for college students. You will be provided with a topic or question, and you will generate a clear and concise explanation.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicOrQuestion } = fields;
      let prompt =
        "Explain the following topic in a simplified manner suitable for college students:\n";
      prompt += "Topic or Question: " + topicOrQuestion + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "custom-email-generator",
    name: "Custom Email Generator",
    slug: "custom-email-generator",
    shortDescription:
      "Create personalized emails effortlessly with our Custom Email Generator, tailored to your specific information and context.",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Name",
        field: "Recipient's Name",
        placeholder: "John Doe",
        required: true,
        optional: false,
        id: "recipientsname",
      },
      {
        type: "textArea",
        label: "Email Context",
        field: "Email Context",
        placeholder: "Project Update",
        required: false,
        optional: true,
        id: "emailcontext",
      },
      {
        type: "textArea",
        label: "Main Points",
        field: "Main Points",
        placeholder:
          "1. Update on project status\n2. Discuss next steps\n3. Schedule a meeting",
        required: false,
        optional: true,
        id: "mainpoints",
      },
    ],
    categories: ["Email", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized emails. You will be provided with the recipient's name, email context, and main points to create a tailored email.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientName, emailContext, mainPoints } = fields;
      let prompt = "Generate a personalized email based on the following:\n";
      prompt += "Recipient's Name: " + recipientName + "\n";
      if (emailContext) {
        prompt += "Email Context: " + emailContext + "\n";
      }
      if (mainPoints) {
        prompt += "Main Points: " + mainPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "professional-document-editor",
    name: "Professional Document Editor",
    slug: "professional-document-editor",
    shortDescription:
      "Enhance your document's grammar and professionalism with our expert editing service for flawless accuracy and impact.",
    fields: [
      {
        type: "textArea",
        label: "Document",
        field: "Document",
        placeholder: "Paste your document here",
        required: true,
        optional: false,
        id: "document",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in editing professional documents. You will be provided with a document, and your task is to enhance its grammar, accuracy, and overall professionalism.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Document } = fields.Document;
      let prompt =
        "Please enhance the following document for grammar, accuracy, and professionalism:\n";
      prompt += Document + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "sales-pitch-scriptmaster",
    name: "Sales Pitch ScriptMaster",
    slug: "sales-pitch-scriptmaster",
    shortDescription:
      "Create customized sales scripts with Sales Pitch ScriptMaster, tailored to your product and target audience for maximum impact.",
    fields: [
      {
        type: "textArea",
        label: "Product or Service Details",
        field: "Product or Service Details",
        placeholder: "Our product is a cutting-edge AI system...",
        required: true,
        optional: false,
        id: "productorservicedetails",
      },
      {
        type: "textArea",
        label: "Target Customer Profile",
        field: "Target Customer Profile",
        placeholder: "A tech-savvy entrepreneur...",
        required: false,
        optional: true,
        id: "targetcustomerprofile",
      },
    ],
    categories: ["Sales", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating tailored sales scripts. You will be provided with details about a product or service and the target customer's profile, and you will generate a compelling sales pitch based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { productOrServiceDetails, targetCustomerProfile } = fields;
      let prompt = "Create a sales pitch script based on the following:\n";
      prompt += "Product or Service Details: " + productOrServiceDetails + "\n";
      if (targetCustomerProfile) {
        prompt += "Target Customer Profile: " + targetCustomerProfile + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "deep-answer-generator",
    name: "Deep Answer Generator",
    slug: "deep-answer-generator",
    shortDescription:
      "Generate detailed answers with analogies and examples using the Deep Answer Generator. Enhance your understanding and explore further!",
    fields: [
      {
        type: "textArea",
        label: "Question",
        field: "Question",
        placeholder: "What is quantum physics?",
        required: true,
        optional: false,
        id: "question",
      },
    ],
    categories: ["Education", "Writing", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing detailed answers to questions. You will be given a question and you will generate a comprehensive answer that includes analogies, relevant examples, and sources for further reading.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Question } = fields.Question;
      let prompt = "Generate a detailed answer to the following question:\n";
      prompt += "Question: " + Question + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ad-copy-generator",
    name: "Ad Copy Generator",
    slug: "ad-copy-generator",
    shortDescription:
      "Create captivating ad copy effortlessly with our Ad Copy Generator, designed to enhance your product's appeal and drive sales.",
    fields: [
      {
        type: "textArea",
        label: "Product Name",
        field: "Product Name",
        placeholder: "AI Writing Assistant",
        required: true,
        optional: false,
        id: "productname",
      },
    ],
    categories: ["Marketing", "Sales"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating compelling ad copy. You will be provided with the product name and other relevant details to generate an appealing advertisement.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { productName } = fields;
      let prompt = "Generate an ad copy for the following product:\n";
      prompt += "Product Name: " + productName + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "reference-letter-generator",
    name: "Reference Letter Generator",
    slug: "reference-letter-generator",
    shortDescription:
      "Create a professional reference letter effortlessly with our generator—just input details about the person you're recommending.",
    fields: [
      {
        type: "textArea",
        label: "Person's Skills and Accomplishments",
        field: "Person's Skills and Accomplishments",
        placeholder: "John is a skilled programmer...",
        required: true,
        optional: false,
        id: "personsskillsandaccomplishments",
      },
      {
        type: "textArea",
        label: "Your Relationship with the Person",
        field: "Your Relationship with the Person",
        placeholder: "John was my student in...",
        required: false,
        optional: true,
        id: "yourrelationshipwiththeperson",
      },
      {
        type: "textArea",
        label: "Purpose of the Recommendation",
        field: "Purpose of the Recommendation",
        placeholder:
          "John is applying for a software engineering position at...",
        required: false,
        optional: true,
        id: "purposeoftherecommendation",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating professional reference letters. You will be provided with details about the person being recommended and you will create a reference letter based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { skillsAndAccomplishments, relationship, purpose } = fields;
      let prompt = "Generate a reference letter based on the following:\n";
      prompt +=
        "Person's Skills and Accomplishments: " +
        skillsAndAccomplishments +
        "\n";
      if (relationship) {
        prompt += "Your Relationship with the Person: " + relationship + "\n";
      }
      if (purpose) {
        prompt += "Purpose of the Recommendation: " + purpose + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-content-writer-and-story-weaver",
    name: "AI Content Writer and Story Weaver",
    slug: "ai-content-writer-and-story-weaver",
    shortDescription:
      "Create captivating stories and content effortlessly with our AI writer, generating engaging text based on your unique inputs.",
    fields: [
      {
        type: "textArea",
        label: "Content Type",
        field: "Content Type",
        placeholder: "Blog post, story, article, etc.",
        required: true,
        optional: false,
        id: "contenttype",
      },
      {
        type: "textArea",
        label: "Tone",
        field: "Tone",
        placeholder: "Formal, informal, humorous, serious, etc.",
        required: false,
        optional: true,
        id: "tone",
      },
      {
        type: "textArea",
        label: "Main Ideas",
        field: "Main Ideas",
        placeholder: "Main points or ideas to be included in the content",
        required: false,
        optional: true,
        id: "mainideas",
      },
    ],
    categories: ["Writing", "Marketing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert AI content writer and story weaver. You will be provided with specific inputs regarding the type of content, tone, and main ideas, and you will generate creative and engaging written content based on those inputs.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { contentType, tone, mainIdeas } = fields;
      let prompt = "Generate a piece of content based on the following:\n";
      prompt += "Content Type: " + contentType + ".\n";
      if (tone) {
        prompt += "Tone: " + tone + ".\n";
      }
      if (mainIdeas) {
        prompt += "Main Ideas: " + mainIdeas + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "creative-writing-coach",
    name: "Creative Writing Coach",
    slug: "creative-writing-coach",
    shortDescription:
      "Enhance your creative writing with expert coaching. Get personalized guidance, feedback, and tips to elevate your skills.",
    fields: [
      {
        type: "textArea",
        label: "Text",
        field: "Text",
        placeholder: "Once upon a time in a town far away...",
        required: true,
        optional: false,
        id: "text",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative writing coach. Your role is to provide guidance, feedback, and suggestions to help improve the user's creative writing skills. You will be given a piece of text and you will analyze it to offer constructive advice.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Text } = fields;
      let prompt =
        "Please provide feedback on the following piece of creative writing:\n";
      prompt += "Text: " + Text + "\n";
      prompt +=
        "Offer suggestions for improvement, highlight strengths, and provide any relevant tips.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "two-paragraph-ai-writer",
    name: "Two Paragraph AI Writer",
    slug: "two-paragraph-ai-writer",
    shortDescription:
      "Create compelling two-paragraph content effortlessly with our AI writer. Just provide a topic and keywords to get started!",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Artificial Intelligence",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Keywords",
        field: "Keywords",
        placeholder: "Machine Learning, Neural Networks, Automation",
        required: false,
        optional: true,
        id: "keywords",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating concise and informative two-paragraph texts. You will be provided with a topic and optional keywords, and you will create a coherent text based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, Keywords } = fields;
      let prompt = "Generate a two-paragraph text based on the following:\n";
      prompt += "Topic: " + Topic + ".\n";
      if (Keywords) {
        prompt += "Keywords: " + Keywords + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-hashtag-generator",
    name: "AI Hashtag Generator",
    slug: "ai-hashtag-generator",
    shortDescription:
      "Generate relevant hashtags effortlessly with our AI Hashtag Generator, tailored to your content for maximum visibility.",
    fields: [
      {
        type: "textArea",
        label: "Text",
        field: "Text",
        placeholder: "Just had a great workout at the gym!",
        required: true,
        optional: false,
        id: "text",
      },
    ],
    categories: ["Social Media", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating relevant hashtags. You will be provided with a piece of text, and you will generate a list of appropriate hashtags based on the content of that text.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Text } = fields;
      let prompt = "Generate relevant hashtags based on the following text:\n";
      prompt += "Text: " + Text + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "viral-tweet-composer",
    name: "Viral Tweet Composer",
    slug: "viral-tweet-composer",
    shortDescription:
      "Create engaging tweets that captivate audiences and boost your chances of going viral with our Viral Tweet Composer tool.",
    fields: [
      {
        type: "textArea",
        label: "Tweet Topic",
        field: "Tweet Topic",
        placeholder: "Climate Change",
        required: true,
        optional: false,
        id: "tweettopic",
      },
    ],
    categories: ["Social Media", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting viral tweets. You will be provided with a topic and you will generate a tweet that is engaging, memorable, and has the potential to go viral based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { tweetTopic } = fields;
      let prompt = "Generate a tweet based on the following:\n";
      prompt += "The tweet should be about " + tweetTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "discussion-board-reply-expander",
    name: "Discussion Board Reply Expander",
    slug: "discussion-board-reply-expander",
    shortDescription:
      "Expand your discussion board replies effortlessly with relevant information and citations for engaging and informative conversations.",
    fields: [
      {
        type: "textArea",
        label: "Discussion Topic or Prompt",
        field: "Discussion Topic or Prompt",
        placeholder: "The Impact of Climate Change on Global Economy",
        required: true,
        optional: false,
        id: "discussiontopicorprompt",
      },
      {
        type: "textArea",
        label: "Initial Response",
        field: "Initial Response",
        placeholder: "I agree with your point about climate change...",
        required: false,
        optional: true,
        id: "initialresponse",
      },
    ],
    categories: ["Education", "Writing", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in expanding discussion board replies. You will be provided with a discussion topic and an initial response, and you will generate a more detailed reply that includes relevant information and citations.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { discussionTopic, initialResponse } = fields;
      let prompt =
        "Expand the following discussion board reply based on the topic provided:\n";
      prompt += "Discussion Topic: " + discussionTopic + "\n";
      prompt +=
        "Initial Response: " +
        (initialResponse ? initialResponse : "No initial response provided.") +
        "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "email-content-composer",
    name: "Email Content Composer",
    slug: "email-content-composer",
    shortDescription:
      "Craft professional emails effortlessly with our Email Content Composer, tailored to meet your specific needs and requirements.",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Name",
        field: "Recipient's Name",
        placeholder: "John Doe",
        required: true,
        optional: false,
        id: "recipientsname",
      },
      {
        type: "textArea",
        label: "Email Subject",
        field: "Email Subject",
        placeholder: "Project Update",
        required: false,
        optional: true,
        id: "emailsubject",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. Update on project status\n2. Next steps\n3. Request for feedback",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Email", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in composing professional emails. You will be provided with specific details about the email, and you will generate a well-structured email based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientName, emailSubject, keyPoints } = fields;
      let prompt = "Compose an email based on the following details:\n";
      prompt += "Recipient's Name: " + recipientName + "\n";
      if (emailSubject) {
        prompt += "Email Subject: " + emailSubject + "\n";
      }
      if (keyPoints) {
        prompt += "Key Points:\n" + keyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "research-hypothesis-generator",
    name: "Research Hypothesis Generator",
    slug: "research-hypothesis-generator",
    shortDescription:
      "Generate a tailored research hypothesis effortlessly based on your topic and objectives with our innovative Research Hypothesis Generator.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic",
        field: "Research Topic",
        placeholder: "The Impact of Social Media on Teenagers",
        required: true,
        optional: false,
        id: "researchtopic",
      },
      {
        type: "textArea",
        label: "Research Objectives",
        field: "Research Objectives",
        placeholder:
          "1. To explore the extent of social media use among teenagers... 2. To determine the effects of social media on teenagers' mental health...",
        required: false,
        optional: true,
        id: "researchobjectives",
      },
    ],
    categories: ["Education", "Writing", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating research hypotheses. You will be provided with a research topic and objectives, and you will generate a clear and concise research hypothesis based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic, researchObjectives } = fields;
      let prompt = "Generate a research hypothesis based on the following:\n";
      prompt += "Research Topic: " + researchTopic + "\n";
      if (researchObjectives) {
        prompt += "Research Objectives: " + researchObjectives + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "argumentative-essay-assistant",
    name: "Argumentative Essay Assistant",
    slug: "argumentative-essay-assistant",
    shortDescription:
      "Elevate your argumentative writing with our assistant, which helps structure and enhance your essays for maximum impact.",
    fields: [
      {
        type: "textArea",
        label: "Essay Topic",
        field: "Essay Topic",
        placeholder: "The benefits of renewable energy",
        required: true,
        optional: false,
        id: "essaytopic",
      },
      {
        type: "textArea",
        label: "Main Arguments",
        field: "Main Arguments",
        placeholder:
          "1. Renewable energy reduces greenhouse gas emissions...\n2. Renewable energy is cost effective in the long run...\n3. Renewable energy sources are unlimited...",
        required: false,
        optional: true,
        id: "mainarguments",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in assisting with argumentative essays. You will be provided with a topic and main arguments, and you will help structure and enhance the essay based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayTopic, mainArguments } = fields;
      let prompt =
        "Help me structure and enhance my argumentative essay based on the following:\n";
      prompt += "Essay Topic: " + essayTopic + "\n";
      if (mainArguments) {
        prompt += "Main Arguments: " + mainArguments + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "rrl-generator-review-of-related-literature",
    name: "RRL Generator - Review of Related Literature",
    slug: "rrl-generator-review-of-related-literature",
    shortDescription:
      "Automatically generate a comprehensive review of related literature tailored to your research topic with the RRL Generator.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic",
        field: "Research Topic",
        placeholder: "The Impact of Social Media on Mental Health",
        required: true,
        optional: false,
        id: "researchtopic",
      },
    ],
    categories: ["Writing", "Education", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating a comprehensive review of related literature. You will be provided with a research topic and you will generate a detailed review based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic } = fields;
      let prompt =
        "Generate a comprehensive review of related literature based on the following:\n";
      prompt += "Research Topic: " + researchTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "headline-optimizer",
    name: "Headline Optimizer",
    slug: "headline-optimizer",
    shortDescription:
      "Optimize your headlines with our tool: analyze keywords, sentiment, and SEO scores for better performance and alternative options.",
    fields: [
      {
        type: "textArea",
        label: "Original Headline",
        field: "Original Headline",
        placeholder: "10 Ways to Improve Your SEO Ranking",
        required: true,
        optional: false,
        id: "originalheadline",
      },
    ],
    categories: ["Marketing", "Writing", "Website"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing and enhancing headlines. You will be provided with an original headline, and you will generate suggestions for improvement based on keyword search volume, sentiment, and SEO scores.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalHeadline } = fields;
      let prompt =
        "Analyze the following headline and provide suggestions for improvement:\n";
      prompt += "Original Headline: " + originalHeadline + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "smart-note-extractor",
    name: "Smart Note Extractor",
    slug: "smart-note-extractor",
    shortDescription:
      "Effortlessly extract key ideas and definitions with Smart Note Extractor to create concise, valuable notes from any source.",
    fields: [
      {
        type: "textArea",
        label: "Source Text",
        field: "Source Text",
        placeholder: "In this chapter, we explore the concept of...",
        required: true,
        optional: false,
        id: "sourcetext",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in extracting key ideas and definitions from text. You will be provided with a source text, and your task is to create concise notes that summarize the main points.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { sourceText } = fields;
      let prompt =
        "Extract key ideas and definitions from the following source text:\n";
      prompt += "Source Text: " + sourceText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "perception-analyzer",
    name: "Perception Analyzer",
    slug: "perception-analyzer",
    shortDescription:
      "Explore how the Perception Analyzer reveals what people see, think, and feel about terms or phrases. Uncover insights today!",
    fields: [
      {
        type: "textArea",
        label: "Term or Phrase",
        field: "Term or Phrase",
        placeholder: "Artificial Intelligence",
        required: true,
        optional: false,
        id: "termorphrase",
      },
    ],
    categories: ["Marketing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing perceptions. You will be provided with a term or phrase, and you will generate insights on what people might see, imagine, think, and/or feel about it.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { termOrPhrase } = fields;
      let prompt = "Analyze the perception of the following term or phrase:\n";
      prompt += "Term or Phrase: " + termOrPhrase + ".\n";
      prompt +=
        "What might people see, imagine, think, and/or feel about this term or phrase?";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "speech-maker-ai",
    name: "Speech Maker AI",
    slug: "speech-maker-ai",
    shortDescription:
      "Create unique and compelling speeches effortlessly with Speech Maker AI, tailored to your specific purpose and details.",
    fields: [
      {
        type: "textArea",
        label: "Speech Purpose",
        field: "Speech Purpose",
        placeholder: "Inspire a team to achieve their sales targets",
        required: true,
        optional: false,
        id: "speechpurpose",
      },
      {
        type: "textArea",
        label: "Audience",
        field: "Audience",
        placeholder: "Sales team of a tech company",
        required: false,
        optional: true,
        id: "audience",
      },
      {
        type: "textArea",
        label: "Additional Details",
        field: "Additional Details",
        placeholder:
          "The team has been underperforming, morale is low, the product is a software solution",
        required: false,
        optional: true,
        id: "additionaldetails",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating speeches. You will be provided with the purpose of the speech and additional details, and you will create a unique and compelling speech based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { speechPurpose, audience, additionalDetails } = fields;
      let prompt = "Generate a speech based on the following:\n";
      prompt += "Speech Purpose: " + speechPurpose + "\n";
      if (audience) {
        prompt += "Audience: " + audience + "\n";
      }
      if (additionalDetails) {
        prompt += "Additional Details: " + additionalDetails + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "hypothesis-generator",
    name: "Hypothesis Generator",
    slug: "hypothesis-generator",
    shortDescription:
      "Easily create null (H0) and alternative (H1) hypotheses with our Hypothesis Generator based on your research question and dataset.",
    fields: [
      {
        type: "textArea",
        label: "Research Question",
        field: "Research Question",
        placeholder: "Does exercise influence weight loss?",
        required: true,
        optional: false,
        id: "researchquestion",
      },
      {
        type: "textArea",
        label: "Dataset Description",
        field: "Dataset Description",
        placeholder:
          "A dataset containing information on participants' exercise routines and weight changes over a period of six months.",
        required: false,
        optional: true,
        id: "datasetdescription",
      },
    ],
    categories: ["Education", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating hypotheses for research questions. You will be provided with a research question and a dataset description, and you will generate both null (H0) and alternative (H1) hypotheses based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchQuestion, datasetDescription } = fields;
      let prompt =
        "Generate null (H0) and alternative (H1) hypotheses based on the following:\n";
      prompt += "Research Question: " + researchQuestion + "\n";
      if (datasetDescription) {
        prompt += "Dataset Description: " + datasetDescription + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-revamp",
    name: "Resume Revamp",
    slug: "resume-revamp",
    shortDescription:
      "Revamp your resume with our expert edits to make it more effective and professional for any job application.",
    fields: [
      {
        type: "textArea",
        label: "Current Resume",
        field: "Current Resume",
        placeholder:
          "John Doe... 123 Main St... Work Experience... Education...",
        required: true,
        optional: false,
        id: "currentresume",
      },
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "We are looking for a talented software engineer with...",
        required: false,
        optional: true,
        id: "jobdescription",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in resume writing and editing. You will be provided with a current resume and a job description, and you will enhance the resume to make it more effective and professional for the specified job.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { currentResume, jobDescription } = fields;
      let prompt =
        "Revamp the following resume based on the provided job description:\n";
      prompt += "Current Resume: " + currentResume + "\n";
      if (jobDescription) {
        prompt += "Job Description: " + jobDescription + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "legal-explainer",
    name: "Legal Explainer",
    slug: "legal-explainer",
    shortDescription:
      "Simplify complex legal terms with our Legal Explainer, making legal concepts easy to understand for everyone.",
    fields: [
      {
        type: "textArea",
        label: "Legal Text",
        field: "Legal Text",
        placeholder:
          "Hereby, the first party agrees to indemnify the second party...",
        required: true,
        optional: false,
        id: "legaltext",
      },
    ],
    categories: ["Legal", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in legal terminology and simplification. You will be provided with complex legal texts, and your task is to translate them into simple, understandable language.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { legalText } = fields.legalText;
      let prompt = "Translate the following legal text into simple language:\n";
      prompt += "Legal Text: " + legalText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "name-generator",
    name: "Name Generator",
    slug: "name-generator",
    shortDescription:
      "Generate unique names for people, pets, or businesses easily with our customizable Name Generator. Find the perfect name today!",
    fields: [
      {
        type: "textArea",
        label: "Name Type",
        field: "Name Type",
        placeholder: "Human / Pet / Business",
        required: true,
        optional: false,
        id: "nametype",
      },
      {
        type: "textArea",
        label: "Details",
        field: "Details",
        placeholder: "Personality traits, industry, preferences, etc.",
        required: false,
        optional: true,
        id: "details",
      },
    ],
    categories: ["Business", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating unique and suitable names. You will be provided with details about the type of name needed and any relevant information to create a fitting name.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { nameType, details } = fields;
      let prompt = "Generate a name based on the following:\n";
      prompt += "Name Type: " + nameType + ".\n";
      if (details) {
        prompt += "Details: " + details + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "academic-ai-writer",
    name: "Academic AI Writer",
    slug: "academic-ai-writer",
    shortDescription:
      "Generate high-quality academic content effortlessly with our AI writer, tailored to your topics and instructions for optimal results.",
    fields: [
      {
        type: "textArea",
        label: "Instructions",
        field: "Instructions",
        placeholder:
          "Please write an essay about the impact of artificial intelligence on dentistry with citations from academic journals",
        required: true,
        optional: false,
        id: "instructions",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating academic content. You will be provided with specific instructions and topics, and you will create a well-structured essay or paper based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Instructions } = fields.Instructions;
      let prompt =
        "Generate an academic essay based on the following instructions:\n";
      prompt += "Instructions: " + Instructions + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "program-goal-and-objective-generator",
    name: "Program Goal and Objective Generator",
    slug: "program-goal-and-objective-generator",
    shortDescription:
      "Generate clear program goals and objectives effortlessly with our tool, ensuring expected outcomes align with your program description.",
    fields: [
      {
        type: "textArea",
        label: "Program Description",
        field: "Program Description",
        placeholder: "Our program is designed to...",
        required: true,
        optional: false,
        id: "programdescription",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating program goals and objectives. You will be provided with a program description, and you will generate clear and measurable goals, objectives, and expected outcomes based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { programDescription } = fields.programDescription;
      let prompt =
        "Generate goals and objectives based on the following program description:\n";
      prompt += "Program Description: " + programDescription + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-physics-learning-assistant",
    name: "AI Physics Learning Assistant",
    slug: "ai-physics-learning-assistant",
    shortDescription:
      "Get detailed explanations for any physics term or question with our AI Physics Learning Assistant. Master physics effortlessly!",
    fields: [
      {
        type: "textArea",
        label: "Physics Term or Question",
        field: "Physics Term or Question",
        placeholder: "What is Quantum Mechanics?",
        required: true,
        optional: false,
        id: "physicstermorquestion",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in physics and can provide detailed explanations for any physics term or question. You will be given a specific term or question, and your task is to explain it clearly and concisely.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { PhysicsTermOrQuestion } = fields;
      let prompt =
        "Provide a detailed explanation for the following physics term or question:\n";
      prompt += "Term/Question: " + PhysicsTermOrQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "seo-keyword-recommender",
    name: "SEO Keyword Recommender",
    slug: "seo-keyword-recommender",
    shortDescription:
      "Generate SEO-friendly keywords to attract your target audience and enhance your online visibility with our Keyword Recommender tool.",
    fields: [
      {
        type: "textArea",
        label: "Company Name",
        field: "Company Name",
        placeholder: "Ticketmaster Sport",
        required: true,
        optional: false,
        id: "companyname",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Business Customers",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Marketing", "Business", "Website"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in SEO keyword generation. You will be provided with a company name and target audience, and you will generate a list of SEO-friendly keywords that can help attract the specified audience.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { companyName, targetAudience } = fields;
      let prompt =
        "Generate a list of SEO-friendly keywords based on the following:\n";
      prompt += "Company Name: " + companyName + "\n";
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "sales-copy-enhancer",
    name: "Sales Copy Enhancer",
    slug: "sales-copy-enhancer",
    shortDescription:
      "Transform ordinary text into persuasive sales copy with our Sales Copy Enhancer and boost your conversion rates today!",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Our product is a high-quality, durable vacuum cleaner...",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Sales", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing sales copy. You will be provided with original text, and your task is to transform it into engaging and persuasive sales copy.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt =
        "Transform the following original text into engaging sales copy:\n";
      prompt += "Original Text: " + originalText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "product-name-generator",
    name: "Product Name Generator",
    slug: "product-name-generator",
    shortDescription:
      "Generate unique and memorable product names effortlessly with our Product Name Generator based on your specific descriptions.",
    fields: [
      {
        type: "textArea",
        label: "Product Description",
        field: "Product Description",
        placeholder:
          "A revolutionary AI-powered writing assistant that helps create high-quality content.",
        required: true,
        optional: false,
        id: "productdescription",
      },
    ],
    categories: ["Marketing", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating creative and memorable product names. You will be provided with a product description, and you will generate a list of unique product names based on that description.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { productDescription } = fields.productDescription;
      let prompt =
        "Generate a list of unique product names based on the following description:\n";
      prompt += "Product Description: " + productDescription + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "wattpad-style-story-writer",
    name: "Wattpad Style Story Writer",
    slug: "wattpad-style-story-writer",
    shortDescription:
      "Boost your storytelling with our Wattpad Style Story Writer, enabling you to craft stories 3x faster and unleash your creativity!",
    fields: [
      {
        type: "textArea",
        label: "Story Summary",
        field: "Story Summary",
        placeholder: "A young girl discovers she has magical powers...",
        required: true,
        optional: false,
        id: "storysummary",
      },
      {
        type: "textArea",
        label: "Main Characters",
        field: "Main Characters",
        placeholder: "Jane - a shy, introverted girl with a big secret...",
        required: false,
        optional: true,
        id: "maincharacters",
      },
      {
        type: "textArea",
        label: "Setting",
        field: "Setting",
        placeholder: "The story takes place in a small, mysterious town...",
        required: false,
        optional: true,
        id: "setting",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting engaging stories in the Wattpad style. You will be provided with a summary, characters, and setting, and you will generate a story based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { storySummary, mainCharacters, setting } = fields;
      let prompt = "Generate a Wattpad-style story based on the following:\n";
      prompt += "Story Summary: " + storySummary + "\n";
      if (mainCharacters) {
        prompt += "Main Characters: " + mainCharacters + "\n";
      }
      if (setting) {
        prompt += "Setting: " + setting + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "rap-lyric-generator",
    name: "Rap Lyric Generator",
    slug: "rap-lyric-generator",
    shortDescription:
      "Generate catchy rap lyrics effortlessly with our Rap Lyric Generator, tailored to your themes and keywords for instant creativity!",
    fields: [
      {
        type: "textArea",
        label: "Themes",
        field: "Themes",
        placeholder: "Freedom, Struggle, Success",
        required: true,
        optional: false,
        id: "themes",
      },
      {
        type: "textArea",
        label: "Keywords",
        field: "Keywords",
        placeholder: "Hustle, Grind, Dream",
        required: false,
        optional: true,
        id: "keywords",
      },
      {
        type: "textArea",
        label: "Beat (Optional)",
        field: "Beat (Optional)",
        placeholder: "Upbeat, Slow, Aggressive",
        required: false,
        optional: true,
        id: "beat(optional)",
      },
    ],
    categories: ["Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a skilled rap lyricist. You will be provided with themes and keywords, and your task is to generate creative and catchy rap lyrics based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Themes, Keywords, Beat } = fields;
      let prompt = "Generate rap lyrics based on the following:\n";
      prompt += "Themes: " + Themes + ".\n";
      if (Keywords) {
        prompt += "Keywords: " + Keywords + ".\n";
      }
      if (Beat) {
        prompt += "Beat style: " + Beat + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "article-translator",
    name: "Article Translator",
    slug: "article-translator",
    shortDescription:
      "Translate articles effortlessly with our Article Translator. Input a URL and get instant translations in your desired language!",
    fields: [
      {
        type: "textArea",
        label: "Article URL",
        field: "Article URL",
        placeholder: "https://www.example.com/article",
        required: true,
        optional: false,
        id: "articleurl",
      },
      {
        type: "textArea",
        label: "Target Language",
        field: "Target Language",
        placeholder: "Spanish",
        required: false,
        optional: true,
        id: "targetlanguage",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in translating articles. You will be provided with a URL of an article and the target language for translation. Your task is to accurately translate the content of the article into the specified language.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { articleURL, targetLanguage } = fields;
      let prompt =
        "Translate the following article into the specified language:\n";
      prompt += "Article URL: " + articleURL + "\n";
      if (targetLanguage) {
        prompt += "Target Language: " + targetLanguage + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writing-generator",
    name: "AI Writing Generator",
    slug: "ai-writing-generator",
    shortDescription:
      "Enhance your writing with our AI generator that completes requests and enriches content using advanced search capabilities.",
    fields: [
      {
        type: "textArea",
        label: "Writing Request",
        field: "Writing Request",
        placeholder:
          "Write a blog post about the latest trends in AI technology.",
        required: true,
        optional: false,
        id: "writingrequest",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert AI writing assistant. You will be provided with a writing request, and you will generate a comprehensive response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingRequest } = fields;
      let prompt = "Generate a written piece based on the following request:\n";
      prompt += "Writing Request: " + writingRequest + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "outline-creator",
    name: "Outline Creator",
    slug: "outline-creator",
    shortDescription:
      "Transform your ideas into a detailed blog post outline effortlessly with our Outline Creator. Say goodbye to blank page anxiety!",
    fields: [
      {
        type: "textArea",
        label: "Ideas or Topics",
        field: "Ideas or Topics",
        placeholder: "Importance of digital marketing, SEO, social media...",
        required: true,
        optional: false,
        id: "ideasortopics",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating blog post outlines. You will be provided with ideas or topics, and you will generate a comprehensive outline that organizes these ideas into a structured format.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { ideasOrTopics } = fields;
      let prompt =
        "Create a comprehensive blog post outline based on the following ideas or topics:\n";
      prompt += "Ideas or Topics: " + ideasOrTopics + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "melodymaster-songwriting-assistant",
    name: "MelodyMaster: Songwriting Assistant",
    slug: "melodymaster-songwriting-assistant",
    shortDescription:
      "Create original song lyrics and melodies tailored to your theme and genre with MelodyMaster, your ultimate songwriting assistant.",
    fields: [
      {
        type: "textArea",
        label: "Theme",
        field: "Theme",
        placeholder: "Love, Adventure, Heartbreak, etc.",
        required: true,
        optional: false,
        id: "theme",
      },
      {
        type: "textArea",
        label: "Music Genre",
        field: "Music Genre",
        placeholder: "Pop, Rock, Country, etc.",
        required: false,
        optional: true,
        id: "musicgenre",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in songwriting. You will be provided with a theme and a music genre, and you will generate original song lyrics and melodies based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Theme, MusicGenre } = fields;
      let prompt =
        "Generate original song lyrics and melodies based on the following:\n";
      prompt += "Theme: " + Theme + ".\n";
      if (MusicGenre) {
        prompt += "Music Genre: " + MusicGenre + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "content-expander",
    name: "Content Expander",
    slug: "content-expander",
    shortDescription:
      "Enhance your writing with Content Expander, transforming basic ideas into detailed, comprehensive pieces effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Original Content",
        field: "Original Content",
        placeholder: "Enter the content you want to expand...",
        required: true,
        optional: false,
        id: "originalcontent",
      },
      {
        type: "textArea",
        label: "Expansion Goal",
        field: "Expansion Goal",
        placeholder:
          "E.g., Increase word count, add more detail, provide examples...",
        required: false,
        optional: true,
        id: "expansiongoal",
      },
    ],
    categories: ["Writing", "Marketing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in content expansion. You will be provided with original content and an expansion goal, and you will enhance and expand upon the provided content to create a more detailed and comprehensive piece.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalContent, expansionGoal } = fields;
      let prompt = "Enhance and expand the following content:\n";
      prompt += "Original Content: " + originalContent + "\n";
      if (expansionGoal) {
        prompt += "Expansion Goal: " + expansionGoal + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paragraph-rewriter",
    name: "Paragraph Rewriter",
    slug: "paragraph-rewriter",
    shortDescription:
      "Revamp your writing with our Paragraph Rewriter, enhancing coherence and quality while retaining original meaning effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Original Paragraph",
        field: "Original Paragraph",
        placeholder: "Enter the paragraph you want to rewrite here.",
        required: true,
        optional: false,
        id: "originalparagraph",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting paragraphs. Your task is to improve the coherence and quality of a given paragraph without changing its meaning or using overly complex language.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalParagraph } = fields;
      let prompt =
        "Rewrite the following paragraph to enhance its coherence and quality while preserving its original meaning:\n";
      prompt += "Original Paragraph: " + originalParagraph + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "market-intelligence-analyzer",
    name: "Market Intelligence Analyzer",
    slug: "market-intelligence-analyzer",
    shortDescription:
      "Gain valuable insights with Market Intelligence Analyzer for informed business decisions on companies, products, and market performance.",
    fields: [
      {
        type: "textArea",
        label: "Company Name",
        field: "Company Name",
        placeholder: "Apple Inc.",
        required: true,
        optional: false,
        id: "companyname",
      },
    ],
    categories: ["Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in market intelligence analysis. You will be provided with the name of a company and you will generate comprehensive insights about the company's products, market performance, and relevant business information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { companyName } = fields;
      let prompt =
        "Provide a detailed market intelligence analysis for the following company:\n";
      prompt += "Company Name: " + companyName + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-text-editor",
    name: "AI Text Editor",
    slug: "ai-text-editor",
    shortDescription:
      "Enhance your writing with our AI Text Editor: improve grammar, style, and clarity for polished, professional content.",
    fields: [
      {
        type: "textArea",
        label: "Text to Edit",
        field: "Text to Edit",
        placeholder: "Enter the text you want to edit here.",
        required: true,
        optional: false,
        id: "texttoedit",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert AI text editor. Your task is to edit and enhance the provided text, improving its grammar, style, and clarity.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToEdit } = fields;
      let prompt =
        "Please edit the following text for grammar, style, and clarity:\n";
      prompt += textToEdit + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "story-fixer-ai",
    name: "Story Fixer AI",
    slug: "story-fixer-ai",
    shortDescription:
      "Enhance your storytelling with Story Fixer AI: fix grammar, plot holes, and character flaws for captivating narratives.",
    fields: [
      {
        type: "textArea",
        label: "Story",
        field: "Story",
        placeholder: "Once upon a time...",
        required: true,
        optional: false,
        id: "story",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in story analysis and editing. Your task is to analyze the provided story for grammatical errors, plot inconsistencies, and character development issues, and suggest improvements.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Story } = fields;
      let prompt =
        "Analyze the following story and provide corrections and suggestions:\n";
      prompt += "Story: " + Story + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "job-pitch-creator",
    name: "Job Pitch Creator",
    slug: "job-pitch-creator",
    shortDescription:
      "Create a compelling job pitch with our Job Pitch Creator, turning your background into a persuasive message for your dream job.",
    fields: [
      {
        type: "textArea",
        label: "User Background",
        field: "User Background",
        placeholder: "I am a software engineer with 5 years of experience...",
        required: true,
        optional: false,
        id: "userbackground",
      },
      {
        type: "textArea",
        label: "Job Title or Listing",
        field: "Job Title or Listing",
        placeholder: "Senior Data Analyst",
        required: false,
        optional: true,
        id: "jobtitleorlisting",
      },
    ],
    categories: ["Business", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating persuasive job pitches. You will be provided with a user's background and a job title or listing, and you will generate a convincing job pitch based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userBackground, jobTitleOrListing } = fields;
      let prompt = "Create a job pitch based on the following:\n";
      prompt += "User Background: " + userBackground + "\n";
      if (jobTitleOrListing) {
        prompt += "Job Title or Listing: " + jobTitleOrListing + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "academic-ai-resource-finder",
    name: "Academic AI Resource Finder",
    slug: "academic-ai-resource-finder",
    shortDescription:
      "Discover the Academic AI Resource Finder: effortlessly locate and recommend relevant academic resources for your research topics.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic",
        field: "Research Topic",
        placeholder: "Machine Learning in Healthcare",
        required: true,
        optional: false,
        id: "researchtopic",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in finding and recommending academic resources. You will be provided with a research topic, and you will generate a list of relevant academic resources related to that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic } = fields;
      let prompt =
        "Find and recommend academic resources based on the following research topic:\n";
      prompt += "Research Topic: " + researchTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "moot-court-research",
    name: "Moot Court Research",
    slug: "moot-court-research",
    shortDescription:
      "Elevate your moot court skills with expert research and well-crafted arguments, ready for impactful presentations.",
    fields: [
      {
        type: "textArea",
        label: "Law",
        field: "Law",
        placeholder: "Enter the law you want to research",
        required: true,
        optional: false,
        id: "law",
      },
      {
        type: "textArea",
        label: "Arguments",
        field: "Arguments",
        placeholder: "Enter the arguments you want to research",
        required: false,
        optional: true,
        id: "arguments",
      },
    ],
    categories: ["Legal", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in moot court research. You will be provided with specific laws and arguments, and you will generate well-researched written arguments ready for presentation.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Law, Arguments } = fields;
      let prompt = "Assist in researching the following moot court details:\n";
      prompt += "Law: " + Law + "\n";
      if (Arguments) {
        prompt += "Arguments: " + Arguments + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "keyword-extractor",
    name: "Keyword Extractor",
    slug: "keyword-extractor",
    shortDescription:
      "Extract the most relevant keywords from your text for enhanced SEO and content optimization with our efficient Keyword Extractor tool.",
    fields: [
      {
        type: "textArea",
        label: "Text",
        field: "Text",
        placeholder: "Enter the text from which keywords need to be extracted.",
        required: true,
        optional: false,
        id: "text",
      },
    ],
    categories: ["Marketing", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in extracting keywords from text. You will be provided with a block of text, and your task is to identify and extract the most relevant keywords that can help with SEO and content optimization.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Text } = fields;
      let prompt =
        "Extract the most relevant keywords from the following text:\n";
      prompt += Text + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "personal-writing-coach",
    name: "Personal Writing Coach",
    slug: "personal-writing-coach",
    shortDescription:
      "Transform your writing with a personal coach offering detailed feedback and tailored suggestions to elevate your skills.",
    fields: [
      {
        type: "textArea",
        label: "Text to be Edited",
        field: "Text to be Edited",
        placeholder: "Enter the text you want to improve here.",
        required: true,
        optional: false,
        id: "texttobeedited",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a personal writing coach. Your role is to provide detailed feedback and suggestions to improve and refine the user's writing based on the text they provide.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToBeEdited } = fields;
      let prompt =
        "Please provide feedback and suggestions for improvement on the following text:\n";
      prompt += textToBeEdited + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-research-assistant",
    name: "AI Research Assistant",
    slug: "ai-research-assistant",
    shortDescription:
      "Get precise answers to research queries with our AI Research Assistant, featuring scholarly sources and citations for credibility.",
    fields: [
      {
        type: "textArea",
        label: "Research Query",
        field: "Research Query",
        placeholder: "What is the impact of climate change on biodiversity?",
        required: true,
        optional: false,
        id: "researchquery",
      },
    ],
    categories: ["Education", "Writing", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI research assistant with expertise in providing detailed answers to research queries. You will be given a specific research question and you will generate a comprehensive response, including relevant scholarly sources and citations.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchQuery } = fields;
      let prompt =
        "Provide a detailed answer to the following research query:\n";
      prompt += "Research Query: " + researchQuery + "\n";
      prompt +=
        "Include scholarly sources and citations to support your answer.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "engaging-ending-conclusion-writer",
    name: "Engaging Ending Conclusion Writer",
    slug: "engaging-ending-conclusion-writer",
    shortDescription:
      "Craft a compelling conclusion for any writing with our Engaging Ending Conclusion Writer. Elevate your work today!",
    fields: [
      {
        type: "textArea",
        label: "Context",
        field: "Context",
        placeholder:
          "This is a blog post about the importance of sustainable living...",
        required: true,
        optional: false,
        id: "context",
      },
      {
        type: "textArea",
        label: "Type of Writing",
        field: "Type of Writing",
        placeholder: "Blog post",
        required: false,
        optional: true,
        id: "typeofwriting",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting engaging conclusions for various types of writing. You will be provided with context and the type of writing, and you will generate a compelling conclusion based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { context, typeOfWriting } = fields;
      let prompt = "Craft an engaging conclusion based on the following:\n";
      prompt += "Context: " + context + "\n";
      if (typeOfWriting) {
        prompt += "Type of writing: " + typeOfWriting + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "content-ai",
    name: "Content AI",
    slug: "content-ai",
    shortDescription:
      "Generate high-quality, original content effortlessly with Content AI, tailored to your specific requests for maximum impact.",
    fields: [
      {
        type: "textArea",
        label: "Content Request",
        field: "Content Request",
        placeholder:
          "Blog post about the latest trends in AI technology for early adopters.",
        required: true,
        optional: false,
        id: "contentrequest",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert content creator. You will be provided with a content request and you will generate high-quality, original content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { contentRequest } = fields;
      let prompt = "Generate content based on the following request:\n";
      prompt += "Content Request: " + contentRequest + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "engaging-conclusion-writer",
    name: "Engaging Conclusion Writer",
    slug: "engaging-conclusion-writer",
    shortDescription:
      "Create impactful conclusions effortlessly! Our Engaging Conclusion Writer crafts compelling endings tailored to your content.",
    fields: [
      {
        type: "textArea",
        label: "Content",
        field: "Content",
        placeholder: "In this article, we discussed the benefits of...",
        required: true,
        optional: false,
        id: "content",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing engaging conclusions. You will be provided with the main content of an article, and your task is to generate a compelling conclusion that summarizes the key points and leaves a lasting impression on the reader.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Content } = fields;
      let prompt =
        "Generate an engaging conclusion based on the following content:\n";
      prompt += "Content: " + Content + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "code-writer",
    name: "Code Writer",
    slug: "code-writer",
    shortDescription:
      "Generate code snippets effortlessly with our Code Writer, tailored to your specific requirements for quick and efficient coding.",
    fields: [
      {
        type: "textArea",
        label: "Functionality Description",
        field: "Functionality Description",
        placeholder:
          "Write a function that takes two integers as input and returns their sum.",
        required: true,
        optional: false,
        id: "functionalitydescription",
      },
      {
        type: "textArea",
        label: "Programming Language",
        field: "Programming Language",
        placeholder: "Python",
        required: false,
        optional: true,
        id: "programminglanguage",
      },
    ],
    categories: ["Business", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating code snippets. You will be provided with a description of the functionality and the programming language, and you will generate a code snippet based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { functionalityDescription, programmingLanguage } = fields;
      let prompt = "Generate a code snippet based on the following:\n";
      prompt += "Functionality: " + functionalityDescription + "\n";
      if (programmingLanguage) {
        prompt += "Programming Language: " + programmingLanguage + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "twitter-post-automator",
    name: "Twitter Post Automator",
    slug: "twitter-post-automator",
    shortDescription:
      "Automate your tweeting with our Twitter Post Automator, simplifying your posting process and boosting your online presence.",
    fields: [
      {
        type: "textArea",
        label: "Tweet Content",
        field: "Tweet Content",
        placeholder: "Enter the content of your tweet here...",
        required: true,
        optional: false,
        id: "tweetcontent",
      },
    ],
    categories: ["Social Media", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating tweets. You will be provided with the content for a tweet, and you will create a concise and engaging tweet based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { tweetContent } = fields.TweetContent;
      let prompt = "Generate a tweet based on the following:\n";
      prompt += "Tweet content: " + tweetContent + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "detailed-discussion-answer-generator",
    name: "Detailed Discussion Answer Generator",
    slug: "detailed-discussion-answer-generator",
    shortDescription:
      "Generate comprehensive answers to discussion questions effortlessly with our Detailed Discussion Answer Generator.",
    fields: [
      {
        type: "textArea",
        label: "Discussion Question",
        field: "Discussion Question",
        placeholder: "Discuss the impact of globalization on local economies.",
        required: true,
        optional: false,
        id: "discussionquestion",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating detailed discussion answers. You will be provided with a discussion question and you will generate a comprehensive and insightful answer based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { discussionQuestion } = fields;
      let prompt =
        "Generate a detailed answer for the following discussion question:\n";
      prompt += "Discussion Question: " + discussionQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "chapter-to-abstract-converter",
    name: "Chapter-to-Abstract Converter",
    slug: "chapter-to-abstract-converter",
    shortDescription:
      "Transform chapters into clear abstracts effortlessly with our Chapter-to-Abstract Converter for concise content summaries.",
    fields: [
      {
        type: "textArea",
        label: "Chaper",
        field: "Chaper",
        placeholder: "Paste content here...",
        required: true,
        optional: false,
        id: "chaper",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing content. You will be provided with the text of a chapter, and your task is to generate a concise abstract that captures the main ideas and themes of the chapter.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Chaper } = fields;
      let prompt =
        "Generate a concise abstract based on the following chapter content:\n";
      prompt += "Chapter content: " + Chaper + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "quiz-question-constructor",
    name: "Quiz Question Constructor",
    slug: "quiz-question-constructor",
    shortDescription:
      "Easily create quiz questions from your notes with our Quiz Question Constructor, ensuring all key points are covered effectively.",
    fields: [
      {
        type: "textArea",
        label: "Notes",
        field: "Notes",
        placeholder:
          "Topic: Photosynthesis. Key Points: 1. Photosynthesis is the process by which green plants and some other organisms use sunlight to synthesize foods with the help of chlorophyll pigments...",
        required: true,
        optional: false,
        id: "notes",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating quiz questions. You will be provided with notes covering key points on a specific topic, and you will generate a set of quiz questions based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Notes } = fields;
      let prompt =
        "Generate a set of quiz questions based on the following notes:\n";
      prompt += "Notes: " + Notes + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ideas-ai",
    name: "Ideas AI",
    slug: "ideas-ai",
    shortDescription:
      "Generate innovative ideas effortlessly with Ideas AI based on any problem, question, or topic you provide.",
    fields: [
      {
        type: "textArea",
        label: "Problem or Question",
        field: "Problem or Question",
        placeholder: "How can I increase productivity in my remote team?",
        required: true,
        optional: false,
        id: "problemorquestion",
      },
    ],
    categories: ["Marketing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating innovative ideas. You will be provided with a problem, question, or topic, and you will generate creative solutions or ideas based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { problemOrQuestion } = fields;
      let prompt = "Generate innovative ideas based on the following input:\n";
      prompt += "Problem or Question: " + problemOrQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "quote-generator",
    name: "Quote Generator",
    slug: "quote-generator",
    shortDescription:
      "Generate inspiring quotes effortlessly with our customizable quote generator based on your chosen category or topic.",
    fields: [
      {
        type: "textArea",
        label: "Quote Category/Topic",
        field: "Quote Category/Topic",
        placeholder: "Inspiring about learning to code",
        required: true,
        optional: false,
        id: "quotecategory/topic",
      },
    ],
    categories: [
      "Social Media",
      "Email",
      "Website",
      "Sales",
      "Support",
      "Writing",
      "Education",
      "Marketing",
      "Legal",
      "Business",
      "Training Material",
    ],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating quotes. You will be provided with a category or topic, and you will generate a relevant quote based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { quoteCategory } = fields;
      let prompt = "Generate a quote based on the following:\n";
      prompt += "Quote Category/Topic: " + quoteCategory + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "engaging-introduction-writer",
    name: "Engaging Introduction Writer",
    slug: "engaging-introduction-writer",
    shortDescription:
      "Craft compelling introductions effortlessly with our engaging introduction writer, perfect for any topic or context.",
    fields: [
      {
        type: "textArea",
        label: "Topic or Context",
        field: "Topic or Context",
        placeholder: "The importance of AI in modern business",
        required: true,
        optional: false,
        id: "topicorcontext",
      },
    ],
    categories: ["Writing", "Marketing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting engaging introductions. You will be provided with a topic or context, and you will generate a compelling introduction based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicOrContext } = fields;
      let prompt = "Craft an engaging introduction based on the following:\n";
      prompt += "Topic or Context: " + topicOrContext + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-highlight-extractor",
    name: "Resume Highlight Extractor",
    slug: "resume-highlight-extractor",
    shortDescription:
      "Extract key achievements from your resume with our Resume Highlight Extractor, using strong action verbs and engaging language.",
    fields: [
      {
        type: "textArea",
        label: "Resume Text",
        field: "Resume Text",
        placeholder:
          "John Doe - Software Engineer. Developed a new feature that increased user engagement by 20%...",
        required: true,
        optional: false,
        id: "resumetext",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in extracting key achievements from resumes. You will be provided with a resume text, and your task is to highlight the most significant accomplishments using strong action verbs and engaging language.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { resumeText } = fields.ResumeText;
      let prompt =
        "Extract and highlight key achievements from the following resume text:\n";
      prompt += resumeText + "\n";
      prompt +=
        "Focus on using strong action verbs and engaging language to present the accomplishments clearly.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-tutor",
    name: "AI Tutor",
    slug: "ai-tutor",
    shortDescription:
      "Elevate your learning with AI Tutor: get tailored education and exercises to achieve top grades in any subject!",
    fields: [
      {
        type: "textArea",
        label: "Subject",
        field: "Subject",
        placeholder: "Physics",
        required: true,
        optional: false,
        id: "subject",
      },
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Quantum Mechanics",
        required: false,
        optional: true,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Difficulty Level",
        field: "Difficulty Level",
        placeholder: "Beginner, Intermediate, Advanced",
        required: false,
        optional: true,
        id: "difficultylevel",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert AI tutor. You will be provided with a subject and topic, and you will generate educational content and exercises to help users understand the material and achieve maximum grades.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Subject, Topic, DifficultyLevel } = fields;
      let prompt =
        "Generate educational content and exercises based on the following:\n";
      prompt += "Subject: " + Subject + "\n";
      if (Topic) {
        prompt += "Topic: " + Topic + "\n";
      }
      if (DifficultyLevel) {
        prompt += "Difficulty Level: " + DifficultyLevel + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-summary-creator",
    name: "Resume Summary Creator",
    slug: "resume-summary-creator",
    shortDescription:
      "Create a standout resume summary with our tool that highlights your skills, experience, and career goals effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Skills",
        field: "Skills",
        placeholder: "Programming, Project Management, Communication",
        required: true,
        optional: false,
        id: "skills",
      },
      {
        type: "textArea",
        label: "Experiences",
        field: "Experiences",
        placeholder:
          "Software Engineer at XYZ Corp, Project Manager at ABC Inc.",
        required: false,
        optional: true,
        id: "experiences",
      },
      {
        type: "textArea",
        label: "Career Goals",
        field: "Career Goals",
        placeholder: "To lead a software development team at a tech startup",
        required: false,
        optional: true,
        id: "careergoals",
      },
    ],
    categories: ["Business", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating professional resume summaries. You will be provided with the user's skills, experiences, and career goals to generate a concise and impactful summary.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Skills, Experiences, CareerGoals } = fields;
      let prompt =
        "Generate a professional resume summary based on the following:\n";
      prompt += "Skills: " + Skills + ".\n";
      if (Experiences) {
        prompt += "Experiences: " + Experiences + ".\n";
      }
      if (CareerGoals) {
        prompt += "Career Goals: " + CareerGoals + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-biology-learning-assistant",
    name: "AI Biology Learning Assistant",
    slug: "ai-biology-learning-assistant",
    shortDescription:
      "Enhance your biology learning with our AI assistant, offering detailed explanations and interactive responses for all your questions.",
    fields: [
      {
        type: "textArea",
        label: "Biology Term or Question",
        field: "Biology Term or Question",
        placeholder: "What is photosynthesis?",
        required: true,
        optional: false,
        id: "biologytermorquestion",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in biology and can provide detailed explanations for any biology term or question. You will be given a specific term or question, and your task is to generate a comprehensive and interactive response.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { biologyTermOrQuestion } = fields;
      let prompt =
        "Provide a detailed explanation for the following biology term or question:\n";
      prompt += "Term/Question: " + biologyTermOrQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "marketing-action-plan-generator",
    name: "Marketing Action Plan Generator",
    slug: "marketing-action-plan-generator",
    shortDescription:
      "Create a tailored marketing action plan effortlessly! Our generator customizes strategies based on your product and target audience.",
    fields: [
      {
        type: "textArea",
        label: "Product/Service Description",
        field: "Product/Service Description",
        placeholder: "Our product is a cutting-edge AI tool that...",
        required: true,
        optional: false,
        id: "product/servicedescription",
      },
      {
        type: "textArea",
        label: "Ideal Customer Persona",
        field: "Ideal Customer Persona",
        placeholder: "Our ideal customer is a tech-savvy marketer...",
        required: false,
        optional: true,
        id: "idealcustomerpersona",
      },
      {
        type: "textArea",
        label: "Vision and Goals",
        field: "Vision and Goals",
        placeholder:
          "Our vision is to become the go-to AI tool for marketers...",
        required: false,
        optional: true,
        id: "visionandgoals",
      },
    ],
    categories: ["Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating comprehensive marketing action plans. You will be provided with details about a product or service, the ideal customer persona, and the vision and goals of the business. Based on this information, you will generate a detailed marketing action plan.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const {
        productServiceDescription,
        idealCustomerPersona,
        visionAndGoals,
      } = fields;
      let prompt =
        "Generate a comprehensive marketing action plan based on the following:\n";
      prompt +=
        "Product/Service Description: " + productServiceDescription + "\n";
      if (idealCustomerPersona) {
        prompt += "Ideal Customer Persona: " + idealCustomerPersona + "\n";
      }
      if (visionAndGoals) {
        prompt += "Vision and Goals: " + visionAndGoals + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "academic-paper-title-generator",
    name: "Academic Paper Title Generator",
    slug: "academic-paper-title-generator",
    shortDescription:
      "Generate compelling academic paper titles effortlessly based on your content and focus with our intuitive title generator tool.",
    fields: [
      {
        type: "textArea",
        label: "Paper Abstract",
        field: "Paper Abstract",
        placeholder: "This paper explores the effects of...",
        required: true,
        optional: false,
        id: "paperabstract",
      },
      {
        type: "textArea",
        label: "Main Focus",
        field: "Main Focus",
        placeholder: "Impact of climate change on agriculture",
        required: false,
        optional: true,
        id: "mainfocus",
      },
    ],
    categories: ["Education", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating titles for academic papers. You will be provided with the abstract and main focus of the paper, and you will generate a compelling and descriptive title based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { paperAbstract, mainFocus } = fields;
      let prompt =
        "Generate a title for an academic paper based on the following:\n";
      prompt += "Paper Abstract: " + paperAbstract + "\n";
      if (mainFocus) {
        prompt += "Main Focus: " + mainFocus + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "blog-wizard",
    name: "Blog Wizard",
    slug: "blog-wizard",
    shortDescription:
      "Create compelling blog posts effortlessly with Blog Wizard, offering structure, topic ideas, and SEO optimization tips.",
    fields: [
      {
        type: "textArea",
        label: "Blog Topic",
        field: "Blog Topic",
        placeholder: "The Future of AI in Healthcare",
        required: true,
        optional: false,
        id: "blogtopic",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating compelling blog posts. You will be provided with a blog topic and you will generate a structured blog post outline, including topic ideas and SEO optimization suggestions based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { blogTopic } = fields;
      let prompt = "Generate a blog post based on the following:\n";
      prompt += "Blog Topic: " + blogTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-story-feedback",
    name: "AI Story Feedback",
    slug: "ai-story-feedback",
    shortDescription:
      "Get constructive AI feedback on your story's plot, character development, and writing style to enhance your storytelling skills.",
    fields: [
      {
        type: "textArea",
        label: "Story",
        field: "Story",
        placeholder: "Once upon a time...",
        required: true,
        optional: false,
        id: "story",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing constructive feedback on stories. You will be given a story and you will assess its elements such as plot, character development, and writing style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { story } = fields;
      let prompt =
        "Please provide constructive feedback on the following story:\n";
      prompt += "Story: " + story + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "acronym-generator",
    name: "Acronym Generator",
    slug: "acronym-generator",
    shortDescription:
      "Create unique acronyms effortlessly with our generator! Simply input words or phrases for instant, creative results.",
    fields: [
      {
        type: "textArea",
        label: "Words or Phrases",
        field: "Words or Phrases",
        placeholder: "Artificial Intelligence, Machine Learning, Deep Learning",
        required: true,
        optional: false,
        id: "wordsorphrases",
      },
    ],
    categories: ["Business", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating acronyms. You will be provided with a list of words or phrases, and you will create a meaningful acronym based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { wordsOrPhrases } = fields;
      let prompt =
        "Generate an acronym based on the following words or phrases:\n";
      prompt += "Words or Phrases: " + wordsOrPhrases + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-profile-generator",
    name: "Resume Profile Generator",
    slug: "resume-profile-generator",
    shortDescription:
      "Create a standout resume profile that highlights your skills, experiences, and career goals with our easy-to-use generator.",
    fields: [
      {
        type: "textArea",
        label: "Career Goals",
        field: "Career Goals",
        placeholder: "To secure a leadership role in software development...",
        required: true,
        optional: false,
        id: "careergoals",
      },
      {
        type: "textArea",
        label: "Skills",
        field: "Skills",
        placeholder: "Proficient in Java, C++, and Python...",
        required: false,
        optional: true,
        id: "skills",
      },
      {
        type: "textArea",
        label: "Experiences",
        field: "Experiences",
        placeholder: "Software Developer at XYZ Company (2018-2021)...",
        required: false,
        optional: true,
        id: "experiences",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating compelling resume profiles. You will be provided with information about the user's career goals, skills, and experiences, and you will generate a professional resume profile based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { CareerGoals, Skills, Experiences } = fields;
      let prompt = "Generate a resume profile based on the following:\n";
      prompt += "Career Goals: " + CareerGoals + "\n";
      if (Skills) {
        prompt += "Skills: " + Skills + "\n";
      }
      if (Experiences) {
        prompt += "Experiences: " + Experiences + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "blog-post-creator",
    name: "Blog Post Creator",
    slug: "blog-post-creator",
    shortDescription:
      "Generate engaging blog posts effortlessly with our Blog Post Creator. Just provide a topic and key points for structured content.",
    fields: [
      {
        type: "textArea",
        label: "Blog Topic",
        field: "Blog Topic",
        placeholder: "The Future of AI in Healthcare",
        required: true,
        optional: false,
        id: "blogtopic",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "AI in diagnosis, AI in patient care, AI in medical research",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating blog posts. You will be provided with a topic and key points, and you will create a well-structured and engaging blog post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { blogTopic, keyPoints } = fields;
      let prompt = "Generate a blog post based on the following:\n";
      prompt += "Blog Topic: " + blogTopic + ".\n";
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "textual-analysis-and-comparison-tool",
    name: "Textual Analysis and Comparison Tool",
    slug: "textual-analysis-and-comparison-tool",
    shortDescription:
      "Analyze and compare two texts for similarities and differences, exploring language techniques and their effects on readers.",
    fields: [
      {
        type: "textArea",
        label: "Text 1",
        field: "Text 1",
        placeholder: "Enter the first text here.",
        required: true,
        optional: false,
        id: "text1",
      },
      {
        type: "textArea",
        label: "Text 2",
        field: "Text 2",
        placeholder: "Enter the second text here.",
        required: false,
        optional: true,
        id: "text2",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in textual analysis and comparison. You will be provided with two texts, and your task is to compare them for similarities and differences, analyze the English language techniques used, and describe their effect on the reader.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { text1, text2 } = fields;
      let prompt = "Please analyze and compare the following texts:\n";
      prompt += "Text 1: " + text1 + "\n";
      if (text2) {
        prompt += "Text 2: " + text2 + "\n";
      }
      prompt +=
        "Provide a detailed comparison of the two texts, highlighting similarities and differences, as well as an analysis of the language techniques used and their effects on the reader.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "creative-story-generator",
    name: "Creative Story Generator",
    slug: "creative-story-generator",
    shortDescription:
      "Generate unique and engaging stories effortlessly with our Creative Story Generator, tailored to your inputs for endless inspiration.",
    fields: [
      {
        type: "textArea",
        label: "Characters",
        field: "Characters",
        placeholder: "John, a brave knight; Sarah, a wise queen",
        required: true,
        optional: false,
        id: "characters",
      },
      {
        type: "textArea",
        label: "Setting",
        field: "Setting",
        placeholder: "A magical kingdom in a faraway land",
        required: false,
        optional: true,
        id: "setting",
      },
      {
        type: "textArea",
        label: "Plot",
        field: "Plot",
        placeholder:
          "John must embark on a journey to find a legendary artifact that can save the kingdom",
        required: false,
        optional: true,
        id: "plot",
      },
    ],
    categories: ["Writing", "Education", "Creative Story Generator"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative storyteller. You will be provided with details about characters, setting, and plot, and you will generate a unique and engaging story based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Characters, Setting, Plot } = fields;
      let prompt = "Generate a creative story based on the following:\n";
      prompt += "Characters: " + Characters + ".\n";
      if (Setting) {
        prompt += "Setting: " + Setting + ".\n";
      }
      if (Plot) {
        prompt += "Plot: " + Plot + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "travel-itinerary-maker",
    name: "Travel Itinerary Maker",
    slug: "travel-itinerary-maker",
    shortDescription:
      "Create your perfect travel itinerary with our tool! Personalize your trip based on duration, destination, and preferences.",
    fields: [
      {
        type: "textArea",
        label: "Length of Stay",
        field: "Length of Stay",
        placeholder: "7 days",
        required: true,
        optional: false,
        id: "lengthofstay",
      },
      {
        type: "textArea",
        label: "Destination",
        field: "Destination",
        placeholder: "Paris, France",
        required: false,
        optional: true,
        id: "destination",
      },
      {
        type: "textArea",
        label: "Preferences",
        field: "Preferences",
        placeholder: "Likes art museums, vegetarian food, and nature walks",
        required: false,
        optional: true,
        id: "preferences",
      },
    ],
    categories: ["Travel", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized travel itineraries. You will be provided with details about the length of stay, destination, and user preferences to generate a tailored travel plan.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { LengthOfStay, Destination, Preferences } = fields;
      let prompt =
        "Create a personalized travel itinerary based on the following:\n";
      prompt += "Length of stay: " + LengthOfStay + ".\n";
      if (Destination) {
        prompt += "Destination: " + Destination + ".\n";
      }
      if (Preferences) {
        prompt += "User preferences: " + Preferences + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "rrl-maker",
    name: "RRL Maker",
    slug: "rrl-maker",
    shortDescription:
      "Automate your literature review with RRL Maker. Simplify the process for any research topic and save valuable time!",
    fields: [
      {
        type: "textArea",
        label: "Research Topic",
        field: "Research Topic",
        placeholder: "The Impact of Social Media on Mental Health",
        required: true,
        optional: false,
        id: "researchtopic",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating a Review of Related Literature (RRL) for academic research. You will be provided with a research topic and you will create a comprehensive RRL based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic } = fields;
      let prompt =
        "Generate a Review of Related Literature (RRL) based on the following:\n";
      prompt += "Research Topic: " + researchTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-improvement-assistant",
    name: "Resume Improvement Assistant",
    slug: "resume-improvement-assistant",
    shortDescription:
      "Enhance your resume with our tips and suggestions for standout content that captures employers' attention and boosts your job prospects.",
    fields: [
      {
        type: "textArea",
        label: "Resume Content",
        field: "Resume Content",
        placeholder: "John Doe, Software Engineer, 5 years experience...",
        required: true,
        optional: false,
        id: "resumecontent",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in resume writing and improvement. You will be provided with the content of a resume, and you will offer tips and suggestions to enhance its effectiveness.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { resumeContent } = fields.ResumeContent;
      let prompt =
        "Provide suggestions to improve the following resume content:\n";
      prompt += resumeContent + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ad-copy-generator",
    name: "Ad Copy Generator",
    slug: "ad-copy-generator",
    shortDescription:
      "Create compelling ad copy effortlessly with our generator, tailored to your product name and description for maximum impact.",
    fields: [
      {
        type: "textArea",
        label: "Product Name",
        field: "Product Name",
        placeholder: "SuperClean Vacuum Cleaner",
        required: true,
        optional: false,
        id: "productname",
      },
      {
        type: "textArea",
        label: "Product Description",
        field: "Product Description",
        placeholder:
          "A high-powered, easy-to-use vacuum cleaner that removes 99.9% of dust and allergens from your home.",
        required: false,
        optional: true,
        id: "productdescription",
      },
    ],
    categories: ["Marketing", "Sales", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating ad copy. You will be provided with a product name and description, and you will create compelling ad copy that highlights the key features and benefits of the product.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { productName, productDescription } = fields;
      let prompt = "Generate ad copy based on the following:\n";
      prompt += "Product Name: " + productName + "\n";
      if (productDescription) {
        prompt += "Product Description: " + productDescription + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "professional-bio-creator",
    name: "Professional Bio Creator",
    slug: "professional-bio-creator",
    shortDescription:
      "Create a standout professional biography effortlessly with our bio generator, tailored from your personal details and accomplishments.",
    fields: [
      {
        type: "textArea",
        label: "Personal Details",
        field: "Personal Details",
        placeholder:
          "John Doe, currently CEO of TechStart, education: MBA from Harvard Business School...",
        required: true,
        optional: false,
        id: "personaldetails",
      },
    ],
    categories: ["Social Media", "Website", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating professional biographies. You will be provided with personal details, accomplishments, and work experience, and you will generate a well-structured professional bio based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { personalDetails } = fields;
      let prompt =
        "Generate a professional biography based on the following:\n";
      prompt += "Personal Details: " + personalDetails + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "copywriting-ai",
    name: "Copywriting AI",
    slug: "copywriting-ai",
    shortDescription:
      "Generate high-quality, unique copy effortlessly with our AI tool, tailored to your specific tasks and goals for maximum impact.",
    fields: [
      {
        type: "textArea",
        label: "Task Description",
        field: "Task Description",
        placeholder:
          "Pproduct description for our new line of eco-friendly kitchenware.",
        required: true,
        optional: false,
        id: "taskdescription",
      },
      {
        type: "textArea",
        label: "Goal",
        field: "Goal",
        placeholder:
          "To persuade environmentally-conscious consumers to purchase our product.",
        required: false,
        optional: true,
        id: "goal",
      },
    ],
    categories: ["Marketing", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert copywriter. You will be provided with a task description and a goal, and you will generate high-quality, unique, and natural-sounding copy based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { taskDescription, goal } = fields;
      let prompt = "Generate copy based on the following:\n";
      prompt += "Task Description: " + taskDescription + "\n";
      if (goal) {
        prompt += "Goal: " + goal + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "chatgpt-prompt-builder",
    name: "ChatGPT Prompt Builder",
    slug: "chatgpt-prompt-builder",
    shortDescription:
      "Craft effective prompts for ChatGPT with our ChatGPT Prompt Builder. Enhance your AI interactions and get better results today!",
    fields: [
      {
        type: "textArea",
        label: "Desired Outcome",
        field: "Desired Outcome",
        placeholder:
          "Write informative blog post on the impact of climate change on polar bear populations",
        required: true,
        optional: false,
        id: "desiredoutcome",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting effective prompts for ChatGPT. You will be provided with a desired outcome and you will generate a suitable prompt based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { desiredOutcome } = fields.DesiredOutcome;
      let prompt = "Create a prompt for ChatGPT based on the following:\n";
      prompt += "Desired outcome: " + desiredOutcome + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-chemistry-learning-assistant",
    name: "AI Chemistry Learning Assistant",
    slug: "ai-chemistry-learning-assistant",
    shortDescription:
      "Transform your chemistry studies with our AI assistant! Get instant explanations and interactive responses for all your chemistry questions.",
    fields: [
      {
        type: "textArea",
        label: "Chemistry Term or Question",
        field: "Chemistry Term or Question",
        placeholder: "What is a covalent bond?",
        required: true,
        optional: false,
        id: "chemistrytermorquestion",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in chemistry education. You will be provided with a chemistry term or question, and you will provide a detailed explanation along with interactive responses to enhance the learning experience.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { ChemistryTermOrQuestion } = fields.ChemistryTermOrQuestion;
      let prompt =
        "Provide a detailed explanation for the following chemistry term or question:\n";
      prompt += "Term/Question: " + ChemistryTermOrQuestion + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-cover-letter-generator",
    name: "AI Cover Letter Generator",
    slug: "ai-cover-letter-generator",
    shortDescription:
      "Create a personalized cover letter effortlessly with our AI Cover Letter Generator, tailored to your skills and job description.",
    fields: [
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "We are looking for a project manager with...",
        required: true,
        optional: false,
        id: "jobdescription",
      },
      {
        type: "textArea",
        label: "User Skills",
        field: "User Skills",
        placeholder: "Project management, leadership, communication",
        required: false,
        optional: true,
        id: "userskills",
      },
      {
        type: "textArea",
        label: "User Experiences",
        field: "User Experiences",
        placeholder:
          "Managed a team of 10 in a software development project...",
        required: false,
        optional: true,
        id: "userexperiences",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized cover letters. You will be provided with a job description and the user's skills and experiences, and you will create a tailored cover letter based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { jobDescription, userSkills, userExperiences } = fields;
      let prompt =
        "Generate a personalized cover letter based on the following:\n";
      prompt += "Job Description: " + jobDescription + "\n";
      if (userSkills) {
        prompt += "User Skills: " + userSkills + "\n";
      }
      if (userExperiences) {
        prompt += "User Experiences: " + userExperiences + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "code-refactor-assistant",
    name: "Code Refactor Assistant",
    slug: "code-refactor-assistant",
    shortDescription:
      "Revamp your legacy code with our Code Refactor Assistant. Enhance readability, maintainability, and performance effortlessly!",
    fields: [
      {
        type: "textArea",
        label: "Legacy Code",
        field: "Legacy Code",
        placeholder: "Paste your legacy code here.",
        required: true,
        optional: false,
        id: "legacycode",
      },
    ],
    categories: ["Business", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in code refactoring. You will be provided with legacy code, and your task is to review it, identify areas for improvement, and suggest refactoring strategies to enhance readability, maintainability, and performance.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { legacyCode } = fields;
      let prompt =
        "Review the following legacy code and provide suggestions for refactoring:\n";
      prompt += "Legacy Code:\n" + legacyCode + "\n";
      prompt += "Please identify areas for improvement and suggest changes.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "job-interview-simulator",
    name: "Job Interview Simulator",
    slug: "job-interview-simulator",
    shortDescription:
      "Prepare for success with our Job Interview Simulator, mimicking real interviews with HR to boost your confidence and interview skills.",
    fields: [
      {
        type: "textArea",
        label: "User Name",
        field: "User Name",
        placeholder: "John Smith",
        required: true,
        optional: false,
        id: "username",
      },
      {
        type: "textArea",
        label: "Job Title",
        field: "Job Title",
        placeholder: "Software Engineer",
        required: false,
        optional: true,
        id: "jobtitle",
      },
      {
        type: "textArea",
        label: "Company Name",
        field: "Company Name",
        placeholder: "OpenAI",
        required: false,
        optional: true,
        id: "companyname",
      },
    ],
    categories: ["Education", "Training Material", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in conducting job interviews. You will simulate a job interview with a Human Resources representative based on the information provided by the user.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userName, jobTitle, companyName } = fields;
      let prompt = "Simulate a job interview for the following candidate:\n";
      prompt += "User Name: " + userName + "\n";
      if (jobTitle) {
        prompt += "Job Title: " + jobTitle + "\n";
      }
      if (companyName) {
        prompt += "Company Name: " + companyName + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "code-generator",
    name: "Code Generator",
    slug: "code-generator",
    shortDescription:
      "Generate code snippets effortlessly in multiple programming languages with our intuitive Code Generator based on your task descriptions.",
    fields: [
      {
        type: "textArea",
        label: "Programming Language",
        field: "Programming Language",
        placeholder: "Python",
        required: true,
        optional: false,
        id: "programminglanguage",
      },
      {
        type: "textArea",
        label: "Task Description",
        field: "Task Description",
        placeholder: "Write a function that sorts a list of integers.",
        required: false,
        optional: true,
        id: "taskdescription",
      },
    ],
    categories: ["Business", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating code snippets. You will be provided with a programming language and a task description, and you will generate a code snippet based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { ProgrammingLanguage, TaskDescription } = fields;
      let prompt = "Generate a code snippet based on the following:\n";
      prompt += "Programming Language: " + ProgrammingLanguage + ".\n";
      if (TaskDescription) {
        prompt += "Task Description: " + TaskDescription + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "clarity-sentence-rewriter",
    name: "Clarity Sentence Rewriter",
    slug: "clarity-sentence-rewriter",
    shortDescription:
      "Enhance your writing with Clarity Sentence Rewriter: refine sentences for improved clarity and eliminate confusion effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Unclear Sentence",
        field: "Unclear Sentence",
        placeholder: "He gave it to him after he was done with it.",
        required: true,
        optional: false,
        id: "unclearsentence",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting sentences for clarity. You will be provided with a sentence that contains unclear references, and your task is to rewrite it to improve its overall clarity.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { unclearSentence } = fields;
      let prompt = "Rewrite the following sentence for clarity:\n";
      prompt += "Unclear Sentence: " + unclearSentence + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "assignment-revision-assistant",
    name: "Assignment Revision Assistant",
    slug: "assignment-revision-assistant",
    shortDescription:
      "Revise and enhance your academic assignments with our assistant, ensuring accuracy and completeness based on professor feedback.",
    fields: [
      {
        type: "textArea",
        label: "Original Assignment Text",
        field: "Original Assignment Text",
        placeholder: "The original text of the assignment.",
        required: true,
        optional: false,
        id: "originalassignmenttext",
      },
      {
        type: "textArea",
        label: "Professor's Feedback",
        field: "Professor's Feedback",
        placeholder:
          "The feedback and suggestions received from the professor.",
        required: false,
        optional: true,
        id: "professorsfeedback",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in revising and expanding academic assignments. You will be provided with the original assignment text and the professor's feedback, and you will generate a revised version of the assignment that incorporates the feedback for improved accuracy and completeness.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalAssignmentText, professorFeedback } = fields;
      let prompt =
        "Revise the following assignment based on the professor's feedback:\n";
      prompt += "Original Assignment Text: " + originalAssignmentText + "\n";
      if (professorFeedback) {
        prompt += "Professor's Feedback: " + professorFeedback + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-grammar-and-spelling-corrector",
    name: "AI Grammar and Spelling Corrector",
    slug: "ai-grammar-and-spelling-corrector",
    shortDescription:
      "Enhance your writing with our AI grammar and spelling corrector, ensuring flawless text for any purpose. Try it now!",
    fields: [
      {
        type: "textArea",
        label: "Text to Correct",
        field: "Text to Correct",
        placeholder:
          "This is an example of a text that might have spelling or grammar mistakes.",
        required: true,
        optional: false,
        id: "texttocorrect",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grammar and spelling correction. You will be provided with a text that may contain errors, and your task is to improve its grammar and spelling.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToCorrect } = fields;
      let prompt =
        "Please correct the following text for grammar and spelling:\n";
      prompt += textToCorrect + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-describer",
    name: "AI Describer",
    slug: "ai-describer",
    shortDescription:
      "Generate precise and detailed descriptions of objects, concepts, or entities effortlessly with AI Describer.",
    fields: [
      {
        type: "textArea",
        label: "Item to Describe",
        field: "Item to Describe",
        placeholder: "Eiffel Tower",
        required: true,
        optional: false,
        id: "itemtodescribe",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating detailed and accurate descriptions of various objects, concepts, or entities. You will be provided with an item to describe, and you will create a comprehensive description based on the information given.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { itemToDescribe } = fields;
      let prompt = "Generate a detailed description for the following item:\n";
      prompt += "Item: " + itemToDescribe + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "business-development-manager-assistant",
    name: "Business Development Manager Assistant",
    slug: "business-development-manager-assistant",
    shortDescription:
      "Explore the role of a Business Development Manager Assistant: identify opportunities, strategize effectively, and manage key relationships.",
    fields: [
      {
        type: "textArea",
        label: "Business Scenario and Background",
        field: "Business Scenario and Background",
        placeholder: "Our company specializes in software development and...",
        required: true,
        optional: false,
        id: "businessscenarioandbackground",
      },
      {
        type: "textArea",
        label: "Current Challenges",
        field: "Current Challenges",
        placeholder: "We are struggling to penetrate new markets...",
        required: false,
        optional: true,
        id: "currentchallenges",
      },
      {
        type: "textArea",
        label: "Business Goals",
        field: "Business Goals",
        placeholder: "Expand to European markets",
        required: false,
        optional: true,
        id: "businessgoals",
      },
    ],
    categories: ["Business", "Sales", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert Business Development Manager Assistant. You will be provided with information about a business scenario, current challenges, and goals, and you will generate strategies to identify and pursue new business opportunities.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const {
        businessScenarioAndBackground,
        currentChallenges,
        businessGoals,
      } = fields;
      let prompt =
        "Generate a strategy for business development based on the following:\n";
      prompt +=
        "Business Scenario and Background: " +
        businessScenarioAndBackground +
        "\n";
      if (currentChallenges) {
        prompt += "Current Challenges: " + currentChallenges + "\n";
      }
      if (businessGoals) {
        prompt += "Business Goals: " + businessGoals + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-greeting-card-generator",
    name: "AI Greeting Card Generator",
    slug: "ai-greeting-card-generator",
    shortDescription:
      "Create personalized greeting cards effortlessly with our AI generator, tailored to any occasion and recipient's unique traits.",
    fields: [
      {
        type: "textArea",
        label: "Occasion",
        field: "Occasion",
        placeholder: "Birthday, Anniversary, Graduation, etc.",
        required: true,
        optional: false,
        id: "occasion",
      },
      {
        type: "textArea",
        label: "Recipient Details",
        field: "Recipient Details",
        placeholder:
          "Name: John, Interests: Music, Hiking, Cooking, Qualities: Funny, Caring, Adventurous",
        required: false,
        optional: true,
        id: "recipientdetails",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized greeting cards. You will be provided with details about the occasion and the recipient, and you will generate a custom greeting card message based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { occasion, recipientDetails } = fields;
      let prompt = "Create a greeting card message based on the following:\n";
      prompt += "Occasion: " + occasion + ".\n";
      if (recipientDetails) {
        prompt += "Recipient Details: " + recipientDetails + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "biography-generator",
    name: "Biography Generator",
    slug: "biography-generator",
    shortDescription:
      "Create a natural-sounding biography effortlessly with our AI-powered generator and real-time web search capabilities.",
    fields: [
      {
        type: "textArea",
        label: "Person Name",
        field: "Person Name",
        placeholder: "Mark Zuckerberg",
        required: true,
        optional: false,
        id: "personname",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating concise and natural-sounding biographies. You will be provided with the name of a person, and you will generate a biography based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { personName } = fields;
      let prompt = "Generate a biography for the following person:\n";
      prompt += "Person Name: " + personName + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paragraph-condenser",
    name: "Paragraph Condenser",
    slug: "paragraph-condenser",
    shortDescription:
      "Transform lengthy paragraphs into concise, easy-to-read summaries with Paragraph Condenser for clearer communication.",
    fields: [
      {
        type: "textArea",
        label: "Original Paragraph",
        field: "Original Paragraph",
        placeholder: "In the world of AI...",
        required: true,
        optional: false,
        id: "originalparagraph",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing text. You will be provided with a lengthy paragraph, and your task is to condense it into a concise and easy-to-read summary.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalParagraph } = fields;
      let prompt =
        "Please condense the following paragraph into a concise summary:\n";
      prompt += "Original Paragraph: " + originalParagraph + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "quick-good-morning-message-generator",
    name: "Quick Good Morning Message Generator",
    slug: "quick-good-morning-message-generator",
    shortDescription:
      "Create personalized good morning messages effortlessly with our generator for SMS, WhatsApp, or Twitter. Brighten someone's day!",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Name",
        field: "Recipient's Name",
        placeholder: "John",
        required: true,
        optional: false,
        id: "recipientsname",
      },
      {
        type: "textArea",
        label: "Details about the recipient",
        field: "Details about the recipient",
        placeholder:
          "Loves coffee, has a dog named Spot, works as a software engineer",
        required: false,
        optional: true,
        id: "detailsabouttherecipient",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating friendly and personalized good morning messages. You will be provided with details about the recipient and you will create a warm and engaging message suitable for SMS, WhatsApp, or Twitter.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientName, detailsAboutRecipient } = fields;
      let prompt = "Generate a good morning message based on the following:\n";
      prompt += "Recipient's Name: " + recipientName + ".\n";
      if (detailsAboutRecipient) {
        prompt +=
          "Details about the recipient: " + detailsAboutRecipient + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "movie-suggestion-generator",
    name: "Movie Suggestion Generator",
    slug: "movie-suggestion-generator",
    shortDescription:
      "Get personalized movie suggestions based on your mood and preferences with our simple Movie Suggestion Generator.",
    fields: [
      {
        type: "textArea",
        label: "User Preferences",
        field: "User Preferences",
        placeholder:
          "I'm in the mood for something adventurous, similar to 'Indiana Jones'.",
        required: true,
        optional: false,
        id: "userpreferences",
      },
    ],
    categories: ["Social Media", "Writing", "Entertainment"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in recommending movies based on user preferences. You will be provided with information about the user's mood, preferences, or movies they liked, and you will generate suitable movie suggestions.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userPreferences } = fields;
      let prompt =
        "Generate movie suggestions based on the following preferences:\n";
      prompt += "User Preferences: " + userPreferences + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "job-application-response-assistant",
    name: "Job Application Response Assistant",
    slug: "job-application-response-assistant",
    shortDescription:
      "Create compelling job application responses effortlessly with our assistant, tailored to showcase your unique experiences.",
    fields: [
      {
        type: "textArea",
        label: "Job Application Question",
        field: "Job Application Question",
        placeholder:
          "Describe a situation where you had to use your leadership skills to resolve a problem.",
        required: true,
        optional: false,
        id: "jobapplicationquestion",
      },
      {
        type: "textArea",
        label: "Past Job Experience",
        field: "Past Job Experience",
        placeholder:
          "I was a team leader at XYZ Corp, where I managed a team of 10 and led the successful completion of Project Alpha.",
        required: false,
        optional: true,
        id: "pastjobexperience",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting responses to job application questions. You will be provided with a job application question and relevant past job experience, and you will generate a compelling response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { jobApplicationQuestion, pastJobExperience } = fields;
      let prompt =
        "Craft a response to the following job application question:\n";
      prompt += "Question: " + jobApplicationQuestion + "\n";
      if (pastJobExperience) {
        prompt +=
          "Based on the following past experience: " + pastJobExperience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "sentence-expander",
    name: "Sentence Expander",
    slug: "sentence-expander",
    shortDescription:
      "Enhance your writing with our Sentence Expander tool, transforming simple sentences into detailed, engaging content effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Original Sentence",
        field: "Original Sentence",
        placeholder: "It's raining outside.",
        required: true,
        optional: false,
        id: "originalsentence",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in expanding sentences. You will be provided with a sentence, and your task is to expand it into 1-2 more detailed sentences that convey the same meaning but with more depth and clarity.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalSentence } = fields;
      let prompt =
        "Expand the following sentence into 1-2 more detailed sentences:\n";
      prompt += "Original Sentence: " + originalSentence + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "performance-review-response-writer",
    name: "Performance Review Response Writer",
    slug: "performance-review-response-writer",
    shortDescription:
      "Craft thoughtful performance review responses with our writer tool, enhancing associate feedback and development effectively.",
    fields: [
      {
        type: "textArea",
        label: "Performance Review",
        field: "Performance Review",
        placeholder: "John has shown exceptional skills in...",
        required: true,
        optional: false,
        id: "performancereview",
      },
      {
        type: "textArea",
        label: "Development Plan",
        field: "Development Plan",
        placeholder: "John aims to improve his leadership skills...",
        required: false,
        optional: true,
        id: "developmentplan",
      },
    ],
    categories: ["Business", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting thoughtful and constructive responses for performance reviews. You will be provided with a performance review and a development plan, and you will generate a response that addresses both aspects effectively.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { performanceReview, developmentPlan } = fields;
      let prompt =
        "Generate a response for the performance review based on the following:\n";
      prompt += "Performance Review: " + performanceReview + "\n";
      if (developmentPlan) {
        prompt += "Development Plan: " + developmentPlan + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-content-writer",
    name: "AI Content Writer",
    slug: "ai-content-writer",
    shortDescription:
      "Generate high-quality, SEO-friendly content effortlessly with our AI Content Writer, tailored to your keywords and topics.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "The Future of Renewable Energy",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Keywords",
        field: "Keywords",
        placeholder: "solar power, wind energy, sustainability",
        required: false,
        optional: true,
        id: "keywords",
      },
    ],
    categories: ["Writing", "Marketing", "Website"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert content writer specializing in generating high-quality, SEO-friendly articles. You will be provided with a topic and keywords, and you will create an engaging article based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, Keywords } = fields;
      let prompt =
        "Generate a high-quality, SEO-friendly article based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (Keywords) {
        prompt += "Keywords: " + Keywords + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "key-points-summarizer",
    name: "Key Points Summarizer",
    slug: "key-points-summarizer",
    shortDescription:
      "Summarize key points effortlessly with our Key Points Summarizer tool. Get concise insights from any text in seconds.",
    fields: [
      {
        type: "textArea",
        label: "Text to Summarize",
        field: "Text to Summarize",
        placeholder: "In this study, we investigated the effects of...",
        required: true,
        optional: false,
        id: "texttosummarize",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing texts. You will be provided with a piece of text, and your task is to extract and summarize the main points clearly and concisely.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToSummarize } = fields;
      let prompt = "Summarize the main points from the following text:\n";
      prompt += textToSummarize + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "triple-flexible-autowrite",
    name: "Triple Flexible AutoWrite",
    slug: "triple-flexible-autowrite",
    shortDescription:
      "Experience Triple Flexible AutoWrite: generate three unique responses instantly for any prompt to enhance your content creation.",
    fields: [
      {
        type: "textArea",
        label: "Prompt",
        field: "Prompt",
        placeholder:
          "Write an introduction to a novel about a time-traveling historian.",
        required: true,
        optional: false,
        id: "prompt",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI writing assistant specialized in generating creative content. Your task is to generate three unique responses based on the provided prompt.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Prompt } = fields;
      let prompt = "Generate three unique responses to the following prompt:\n";
      prompt += "Prompt: " + Prompt + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "linkedin-comment-composer",
    name: "LinkedIn Comment Composer",
    slug: "linkedin-comment-composer",
    shortDescription:
      "Create engaging LinkedIn comments effortlessly with our Comment Composer, enhancing your professional presence on the platform.",
    fields: [
      {
        type: "textArea",
        label: "LinkedIn Post Content",
        field: "LinkedIn Post Content",
        placeholder: "Excited to announce our new product launch...",
        required: true,
        optional: false,
        id: "linkedinpostcontent",
      },
      {
        type: "textArea",
        label: "Goal of Comment",
        field: "Goal of Comment",
        placeholder: "Express enthusiasm and ask a question about the product",
        required: false,
        optional: true,
        id: "goalofcomment",
      },
    ],
    categories: ["Social Media", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting engaging and professional comments for LinkedIn posts. You will be provided with the content of a LinkedIn post and a goal for the comment, and you will generate a suitable comment based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { LinkedInPostContent, GoalOfComment } = fields;
      let prompt = "Generate a LinkedIn comment based on the following:\n";
      prompt += "LinkedIn Post Content: " + LinkedInPostContent + "\n";
      if (GoalOfComment) {
        prompt += "Goal of Comment: " + GoalOfComment + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-summary",
    name: "AI Summary",
    slug: "ai-summary",
    shortDescription:
      "Generate concise summaries easily with our AI tool, transforming any text into a clear and informative overview.",
    fields: [
      {
        type: "textArea",
        label: "Text to Summarize",
        field: "Text to Summarize",
        placeholder: "Paste the text you want to summarize here...",
        required: true,
        optional: false,
        id: "texttosummarize",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing texts. You will be provided with a piece of text, and your task is to generate a concise summary that captures the main points and essence of the original content.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToSummarize } = fields;
      let prompt = "Generate a concise summary based on the following text:\n";
      prompt += "Text: " + textToSummarize + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-document-translator",
    name: "AI Document Translator",
    slug: "ai-document-translator",
    shortDescription:
      "Easily translate your documents or text into any language with our AI Document Translator for seamless communication.",
    fields: [
      {
        type: "textArea",
        label: "Source Text/Document",
        field: "Source Text/Document",
        placeholder: "Paste your text or document here",
        required: true,
        optional: false,
        id: "sourcetext/document",
      },
      {
        type: "textArea",
        label: "Target Language",
        field: "Target Language",
        placeholder: "Enter the language you want to translate to",
        required: false,
        optional: true,
        id: "targetlanguage",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in translating documents and texts. You will be provided with a source text or document and a target language, and you will generate the translated version based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { sourceText, targetLanguage } = fields;
      let prompt =
        "Translate the following text into the specified target language:\n";
      prompt += "Source Text: " + sourceText + "\n";
      if (targetLanguage) {
        prompt += "Target Language: " + targetLanguage + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-caption-generator",
    name: "AI Caption Generator",
    slug: "ai-caption-generator",
    shortDescription:
      "Create engaging social media captions effortlessly with our AI Caption Generator, tailored to your unique descriptions.",
    fields: [
      {
        type: "textArea",
        label: "Content Description",
        field: "Content Description",
        placeholder:
          "A photo of a sunset over the beach, feeling peaceful and content",
        required: true,
        optional: false,
        id: "contentdescription",
      },
      {
        type: "textArea",
        label: "Platform",
        field: "Platform",
        placeholder: "Instagram",
        required: false,
        optional: true,
        id: "platform",
      },
    ],
    categories: ["Social Media", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging captions for social media posts or videos. You will be provided with a content description and platform, and you will create a captivating caption based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { contentDescription, platform } = fields;
      let prompt = "Generate a caption based on the following:\n";
      prompt += "Content Description: " + contentDescription + "\n";
      if (platform) {
        prompt += "Platform: " + platform + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "sermon-ai-generator",
    name: "Sermon AI Generator",
    slug: "sermon-ai-generator",
    shortDescription:
      "Create tailored sermons effortlessly with our Sermon AI Generator, adapting to your audience's beliefs and chosen themes.",
    fields: [
      {
        type: "textArea",
        label: "Religious Beliefs",
        field: "Religious Beliefs",
        placeholder:
          "Christianity, believes in the Holy Trinity, values charity and kindness...",
        required: true,
        optional: false,
        id: "religiousbeliefs",
      },
      {
        type: "textArea",
        label: "Audience Description",
        field: "Audience Description",
        placeholder:
          "Middle-aged parents, community volunteers, looking for spiritual guidance...",
        required: false,
        optional: true,
        id: "audiencedescription",
      },
      {
        type: "textArea",
        label: "Themes",
        field: "Themes",
        placeholder: "Forgiveness, Charity, Love",
        required: false,
        optional: true,
        id: "themes",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating sermons. You will be provided with information about the audience's religious beliefs, themes to include, and a description of the audience. Based on this information, you will generate a high-quality sermon.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { religiousBeliefs, audienceDescription, themes } = fields;
      let prompt = "Generate a sermon based on the following:\n";
      prompt += "Religious beliefs: " + religiousBeliefs + ".\n";
      if (audienceDescription) {
        prompt += "Audience description: " + audienceDescription + ".\n";
      }
      if (themes) {
        prompt += "Themes to include: " + themes + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-helper-question-answerer",
    name: "AI Helper: Question Answerer",
    slug: "ai-helper-question-answerer",
    shortDescription:
      "Get instant answers to your questions with our advanced AI Helper, designed to provide accurate and relevant responses effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Question",
        field: "Question",
        placeholder: "What is the capital of France?",
        required: true,
        optional: false,
        id: "question",
      },
    ],
    categories: ["Support", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an advanced AI capable of answering a wide range of questions. You will be provided with a question, and your task is to provide a clear and accurate answer based on your knowledge.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Question } = fields;
      let prompt = "Please provide an answer to the following question:\n";
      prompt += "Question: " + Question + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "linkedin-headline-generator",
    name: "LinkedIn Headline Generator",
    slug: "linkedin-headline-generator",
    shortDescription:
      "Create a standout LinkedIn headline with our generator, tailored to your profession, skills, and career goals.",
    fields: [
      {
        type: "textArea",
        label: "Profession",
        field: "Profession",
        placeholder: "Software Developer",
        required: true,
        optional: false,
        id: "profession",
      },
      {
        type: "textArea",
        label: "Skills",
        field: "Skills",
        placeholder: "Java, Python, Web Development",
        required: false,
        optional: true,
        id: "skills",
      },
      {
        type: "textArea",
        label: "Career Goals",
        field: "Career Goals",
        placeholder: "To lead a software development team",
        required: false,
        optional: true,
        id: "careergoals",
      },
    ],
    categories: ["Social Media", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating LinkedIn headlines. You will be provided with information about a user's profession, skills, and career goals, and you will create a catchy and professional LinkedIn headline based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Profession, Skills, CareerGoals } = fields;
      let prompt = "Generate a LinkedIn headline based on the following:\n";
      prompt += "Profession: " + Profession + ".\n";
      if (Skills) {
        prompt += "Skills: " + Skills + ".\n";
      }
      if (CareerGoals) {
        prompt += "Career Goals: " + CareerGoals + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "email-subject-line-generator",
    name: "Email Subject Line Generator",
    slug: "email-subject-line-generator",
    shortDescription:
      "Generate engaging email subject lines tailored to your content and purpose, boosting open rates and email effectiveness.",
    fields: [
      {
        type: "textArea",
        label: "Email Content",
        field: "Email Content",
        placeholder: "Dear Customer, We are excited to announce...",
        required: true,
        optional: false,
        id: "emailcontent",
      },
    ],
    categories: ["Email", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating email subject lines. You will be provided with the content and purpose of the email, and you will create engaging and relevant subject lines based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { emailContent } = fields;
      let prompt =
        "Generate an engaging email subject line based on the following content:\n";
      prompt += "Email Content: " + emailContent + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "in-cite-ai-reference-generator",
    name: "In-Cite AI Reference Generator",
    slug: "in-cite-ai-reference-generator",
    shortDescription:
      "Generate accurate in-text AI citations effortlessly with In-Cite AI Reference Generator for your academic and professional writing.",
    fields: [
      {
        type: "textArea",
        label: "Author(s)",
        field: "Author(s)",
        placeholder: "John Doe, Jane Doe",
        required: true,
        optional: false,
        id: "author(s)",
      },
      {
        type: "textArea",
        label: "Work Title",
        field: "Work Title",
        placeholder: "The Impact of AI on Society",
        required: false,
        optional: true,
        id: "worktitle",
      },
      {
        type: "textArea",
        label: "Publication Date",
        field: "Publication Date",
        placeholder: "2022",
        required: false,
        optional: true,
        id: "publicationdate",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating in-text AI citations for academic and professional writing. You will be provided with the author's name(s), work title, and publication date, and you will generate a complete citation based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Author, WorkTitle, PublicationDate } = fields;
      let prompt = "Generate an in-text AI citation based on the following:\n";
      prompt += "Author(s): " + Author + "\n";
      if (WorkTitle) {
        prompt += "Work Title: " + WorkTitle + "\n";
      }
      if (PublicationDate) {
        prompt += "Publication Date: " + PublicationDate + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-quote-generator",
    name: "AI Quote Generator",
    slug: "ai-quote-generator",
    shortDescription:
      "Generate inspiring quotes on any topic with our AI Quote Generator. Perfect for motivation, social media, and creative projects!",
    fields: [
      {
        type: "textArea",
        label: "Topic or Theme",
        field: "Topic or Theme",
        placeholder: "Leadership",
        required: true,
        optional: false,
        id: "topicortheme",
      },
    ],
    categories: [
      "Social Media",
      "Writing",
      "Education",
      "Marketing",
      "Business",
    ],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating quotes. You will be provided with a specific topic or theme, and you will create a relevant and inspiring quote based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicOrTheme } = fields;
      let prompt = "Generate a quote based on the following:\n";
      prompt += "Topic or Theme: " + topicOrTheme + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "entertaining-presentation-idea-generator",
    name: "Entertaining Presentation Idea Generator",
    slug: "entertaining-presentation-idea-generator",
    shortDescription:
      "Generate engaging presentation ideas effortlessly with our entertaining Presentation Idea Generator tailored to your specific topic.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Artificial Intelligence",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Education", "Training Material", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating entertaining presentation ideas. You will be provided with a topic, and you will generate creative and engaging presentation ideas based on that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields;
      let prompt =
        "Generate entertaining presentation ideas based on the following topic:\n";
      prompt += "Topic: " + Topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-birthday-card-writer",
    name: "AI Birthday Card Writer",
    slug: "ai-birthday-card-writer",
    shortDescription:
      "Create personalized birthday card messages with our AI writer, tailored to the recipient's name, age, and your relationship.",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Name",
        field: "Recipient's Name",
        placeholder: "John",
        required: true,
        optional: false,
        id: "recipientsname",
      },
      {
        type: "textArea",
        label: "Recipient's Age",
        field: "Recipient's Age",
        placeholder: "30",
        required: false,
        optional: true,
        id: "recipientsage",
      },
      {
        type: "textArea",
        label: "Relationship to User",
        field: "Relationship to User",
        placeholder: "Friend",
        required: false,
        optional: true,
        id: "relationshiptouser",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized birthday card messages. You will be provided with details about the recipient and you will create a heartfelt birthday message based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientName, recipientAge, relationshipToUser } = fields;
      let prompt =
        "Generate a personalized birthday card message based on the following:\n";
      prompt += "Recipient's Name: " + recipientName + ".\n";
      if (recipientAge) {
        prompt += "Recipient's Age: " + recipientAge + ".\n";
      }
      if (relationshipToUser) {
        prompt += "Relationship to User: " + relationshipToUser + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "meeting-prep",
    name: "Meeting Prep",
    slug: "meeting-prep",
    shortDescription:
      "Streamline your meeting prep with our tool that gathers key info, creates a prep sheet, and discovers LinkedIn connections.",
    fields: [
      {
        type: "textArea",
        label: "Name",
        field: "Name",
        placeholder: "John Doe",
        required: true,
        optional: false,
        id: "name",
      },
      {
        type: "textArea",
        label: "Company or Organization",
        field: "Company or Organization",
        placeholder: "ABC Company",
        required: false,
        optional: true,
        id: "companyororganization",
      },
    ],
    categories: ["Social Media", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in preparing for meetings. You will be provided with information about a person and their organization, and you will generate a comprehensive prep sheet to assist in the meeting preparation.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Name, CompanyOrOrganization } = fields;
      let prompt =
        "Prepare a meeting prep sheet based on the following information:\n";
      prompt += "Name: " + Name + "\n";
      if (CompanyOrOrganization) {
        prompt += "Company or Organization: " + CompanyOrOrganization + "\n";
      }
      prompt +=
        "Gather general information, notable accomplishments, and any mutual connections on LinkedIn.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "rewrite",
    name: "Rewrite",
    slug: "rewrite",
    shortDescription:
      "Transform any text effortlessly with our AI rewriting tool. Enhance your content based on your unique instructions!",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Enter the text you want to rewrite here.",
        required: true,
        optional: false,
        id: "originaltext",
      },
      {
        type: "textArea",
        label: "Rewrite Instructions",
        field: "Rewrite Instructions",
        placeholder:
          "Specify how you want the text to be rewritten. E.g., 'Rewrite this in a more formal tone' or 'Simplify this text for a 5th-grade reading level'.",
        required: false,
        optional: true,
        id: "rewriteinstructions",
      },
    ],
    categories: [
      "Social Media",
      "Email",
      "Website",
      "Sales",
      "Support",
      "Writing",
      "Education",
      "Marketing",
      "Business",
    ],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting text. You will be provided with original text and specific instructions on how to rewrite it. Your goal is to transform the text according to the given instructions.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText, rewriteInstructions } = fields;
      let prompt =
        "Rewrite the following text based on the provided instructions:\n";
      prompt += "Original Text: " + originalText + "\n";
      if (rewriteInstructions) {
        prompt += "Rewrite Instructions: " + rewriteInstructions + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-objective-writer",
    name: "Resume Objective Writer",
    slug: "resume-objective-writer",
    shortDescription:
      "Craft a compelling resume objective that aligns with your career goals and showcases your unique experiences.",
    fields: [
      {
        type: "textArea",
        label: "Career Goals",
        field: "Career Goals",
        placeholder:
          "To secure a challenging position in a reputable organization...",
        required: true,
        optional: false,
        id: "careergoals",
      },
      {
        type: "textArea",
        label: "Experience",
        field: "Experience",
        placeholder: "5 years experience in project management...",
        required: false,
        optional: true,
        id: "experience",
      },
      {
        type: "textArea",
        label: "Job Applying For",
        field: "Job Applying For",
        placeholder: "Project Manager",
        required: false,
        optional: true,
        id: "jobapplyingfor",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting resume objectives. You will be provided with information about the user's career goals, experience, and the job they are applying for, and you will generate a compelling resume objective based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { careerGoals, experience, jobApplyingFor } = fields;
      let prompt = "Generate a resume objective based on the following:\n";
      prompt += "Career Goals: " + careerGoals + "\n";
      if (experience) {
        prompt += "Experience: " + experience + "\n";
      }
      if (jobApplyingFor) {
        prompt += "Job Applying For: " + jobApplyingFor + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "one-page-report-writer",
    name: "One-Page Report Writer",
    slug: "one-page-report-writer",
    shortDescription:
      "Create concise one-page reports effortlessly with our tool, summarizing key points and data on any topic you choose.",
    fields: [
      {
        type: "textArea",
        label: "Assignment Instructions",
        field: "Assignment Instructions",
        placeholder: "Sales Performance Q1 2022",
        required: true,
        optional: false,
        id: "assignmentinstructions",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. Increase in sales in January\n2. Decrease in sales in February\n3. Stable sales in March",
        required: false,
        optional: true,
        id: "keypoints",
      },
      {
        type: "textArea",
        label: "Additional Instructions",
        field: "Additional Instructions",
        placeholder:
          "Data related to sales performance, customer feedback, marketing efforts, etc.",
        required: false,
        optional: true,
        id: "additionalinstructions",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating concise one-page reports. You will be provided with assignment instructions, key points, and additional data, and you will create a well-structured one-page report based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { assignmentInstructions, keyPoints, additionalInstructions } =
        fields;
      let prompt = "Generate a one-page report based on the following:\n";
      prompt += "Assignment Instructions: " + assignmentInstructions + "\n";
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + "\n";
      }
      if (additionalInstructions) {
        prompt += "Additional Instructions: " + additionalInstructions + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-conclusion-generator",
    name: "Essay Conclusion Generator",
    slug: "essay-conclusion-generator",
    shortDescription:
      "Generate compelling essay conclusions effortlessly! Input your introduction and key points for a perfect wrap-up every time.",
    fields: [
      {
        type: "textArea",
        label: "Introduction",
        field: "Introduction",
        placeholder: "Paste your essay introduction",
        required: true,
        optional: false,
        id: "introduction",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder: "Point 1: ..., Point 2: ..., Point 3: ...",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating compelling conclusions for essays. You will be provided with the introduction and key points of the essay, and you will generate a conclusion that effectively summarizes and reinforces the main arguments.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Introduction, KeyPoints } = fields;
      let prompt =
        "Generate a conclusion for the essay based on the following:\n";
      prompt += "Introduction: " + Introduction + "\n";
      if (KeyPoints) {
        prompt += "Key Points: " + KeyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "science-ai-for-researchers",
    name: "Science AI for Researchers",
    slug: "science-ai-for-researchers",
    shortDescription:
      "Streamline your research with Science AI, which searches millions of academic articles and provides relevant information and citations.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic or Request",
        field: "Research Topic or Request",
        placeholder: "The role of DNA methylation in plant defense mechanisms",
        required: true,
        optional: false,
        id: "researchtopicorrequest",
      },
    ],
    categories: ["Writing", "Education", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in academic research and literature review. You will be provided with a research topic or request, and you will generate a comprehensive summary of relevant academic articles and papers, including proper citations.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopicOrRequest } = fields;
      let prompt =
        "Generate a summary of academic articles based on the following request:\n";
      prompt += "Research Topic: " + researchTopicOrRequest + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "maid-of-honor-speech-generator",
    name: "Maid of Honor Speech Generator",
    slug: "maid-of-honor-speech-generator",
    shortDescription:
      "Create a heartfelt maid of honor speech effortlessly with our generator, tailored to your relationship with the bride.",
    fields: [
      {
        type: "textArea",
        label: "Bride's Name",
        field: "Bride's Name",
        placeholder: "Sarah",
        required: true,
        optional: false,
        id: "bridesname",
      },
      {
        type: "textArea",
        label: "Your Relationship with the Bride",
        field: "Your Relationship with the Bride",
        placeholder: "We've been best friends since childhood.",
        required: false,
        optional: true,
        id: "yourrelationshipwiththebride",
      },
      {
        type: "textArea",
        label: "Memorable Anecdotes",
        field: "Memorable Anecdotes",
        placeholder: "Remember when we went on that road trip to...",
        required: false,
        optional: true,
        id: "memorableanecdotes",
      },
    ],
    categories: ["Writing", "Social Media"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting heartfelt maid of honor speeches. You will be provided with details about the bride and your relationship, and you will generate a personalized speech based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { BrideName, YourRelationshipWithTheBride, MemorableAnecdotes } =
        fields;
      let prompt = "Generate a maid of honor speech based on the following:\n";
      prompt += "Bride's Name: " + BrideName + ".\n";
      if (YourRelationshipWithTheBride) {
        prompt +=
          "Your relationship with the bride: " +
          YourRelationshipWithTheBride +
          ".\n";
      }
      if (MemorableAnecdotes) {
        prompt += "Memorable anecdotes: " + MemorableAnecdotes + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "third-person-narrator-dialogue-generator",
    name: "Third-Person Narrator Dialogue Generator",
    slug: "third-person-narrator-dialogue-generator",
    shortDescription:
      "Create unique third-person narratives with our dialogue generator. Choose a topic and watch your stories come to life!",
    fields: [
      {
        type: "textArea",
        label: "Narrative Topic",
        field: "Narrative Topic",
        placeholder: "A thrilling car chase",
        required: true,
        optional: false,
        id: "narrativetopic",
      },
      {
        type: "textArea",
        label: "Narrative Type",
        field: "Narrative Type",
        placeholder: "Dialogue or Monologue",
        required: false,
        optional: true,
        id: "narrativetype",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating third-person narrative dialogues and monologues. You will be provided with a narrative topic and type, and you will create a compelling narrative based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { narrativeTopic, narrativeType } = fields;
      let prompt =
        "Generate a third-person narrative based on the following:\n";
      prompt += "Narrative Topic: " + narrativeTopic + ".\n";
      if (narrativeType) {
        prompt += "Narrative Type: " + narrativeType + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "natural-text-generator",
    name: "Natural Text Generator",
    slug: "natural-text-generator",
    shortDescription:
      "Generate natural-sounding text with varied rhythms, emotional tones, and direct engagement for effective communication.",
    fields: [
      {
        type: "textArea",
        label: "Context",
        field: "Context",
        placeholder: "Blog post about traveling",
        required: true,
        optional: false,
        id: "context",
      },
      {
        type: "textArea",
        label: "Tone",
        field: "Tone",
        placeholder: "Excited, casual",
        required: false,
        optional: true,
        id: "tone",
      },
      {
        type: "textArea",
        label: "Draft or Starter Text",
        field: "Draft or Starter Text",
        placeholder:
          "Traveling is a wonderful experience. It allows you to see new places and learn about different cultures...",
        required: false,
        optional: true,
        id: "draftorstartertext",
      },
    ],
    categories: ["Writing", "Marketing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating natural-sounding text. You will be provided with context, tone, and optional starter text to create a coherent and engaging piece of writing.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Context, Tone, "Draft or Starter Text": draftText } = fields;
      let prompt = "Generate a piece of text based on the following:\n";
      prompt += "Context: " + Context + "\n";
      if (Tone) {
        prompt += "Tone: " + Tone + "\n";
      }
      if (draftText) {
        prompt += "Starter text: " + draftText + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "call-to-action-generator",
    name: "Call to Action Generator",
    slug: "call-to-action-generator",
    shortDescription:
      "Create powerful call-to-action statements tailored to your goals and audience for maximum engagement and effectiveness.",
    fields: [
      {
        type: "textArea",
        label: "CTA Goal",
        field: "CTA Goal",
        placeholder: "Sign up for a free trial of our AI writing software",
        required: true,
        optional: false,
        id: "ctagoal",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Small business owners",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Marketing", "Sales", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating compelling call-to-action (CTA) statements. You will be provided with a specific goal and target audience, and you will generate an effective CTA based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { CTA_Goal, Target_Audience } = fields;
      let prompt =
        "Generate a call-to-action statement based on the following:\n";
      prompt += "CTA Goal: " + CTA_Goal + "\n";
      if (Target_Audience) {
        prompt += "Target Audience: " + Target_Audience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "email-reply-ai",
    name: "Email Reply AI",
    slug: "email-reply-ai",
    shortDescription:
      "Generate natural-sounding email replies effortlessly with Email Reply AI, using smart responses based on received messages.",
    fields: [
      {
        type: "textArea",
        label: "Received Email",
        field: "Received Email",
        placeholder: "Dear John...",
        required: true,
        optional: false,
        id: "receivedemail",
      },
      {
        type: "textArea",
        label: "Shorthand Response",
        field: "Shorthand Response",
        placeholder:
          "Agree with proposal, suggest meeting next Tuesday, ask for meeting agenda",
        required: false,
        optional: true,
        id: "shorthandresponse",
      },
    ],
    categories: ["Email", "Support", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI designed to generate natural-sounding email replies. You will be provided with a received email and a shorthand response, and you will create a coherent and contextually appropriate email reply based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { receivedEmail, shorthandResponse } = fields;
      let prompt =
        "Generate a natural-sounding email reply based on the following:\n";
      prompt += "Received Email: " + receivedEmail + "\n";
      if (shorthandResponse) {
        prompt += "Shorthand Response: " + shorthandResponse + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "bible-study-ai-assistant",
    name: "Bible Study AI Assistant",
    slug: "bible-study-ai-assistant",
    shortDescription:
      "Enhance your Bible study with our AI Assistant, generating discussion topics, talking points, and biblical answers effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Question",
        field: "Question",
        placeholder: "What does the Bible say about forgiveness?",
        required: true,
        optional: false,
        id: "question",
      },
      {
        type: "textArea",
        label: "Discussion Topics",
        field: "Discussion Topics",
        placeholder:
          "Forgiveness in Old Testament, Forgiveness in New Testament, Practical applications of forgiveness",
        required: false,
        optional: true,
        id: "discussiontopics",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in biblical studies and discussion facilitation. You will be provided with a question and optional discussion topics, and you will generate a draft for discussion points and answers based on biblical texts.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Question, DiscussionTopics } = fields;
      let prompt =
        "Generate a draft for a Bible study session based on the following:\n";
      prompt += "Question: " + Question + "\n";
      if (DiscussionTopics) {
        prompt += "Discussion Topics: " + DiscussionTopics + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "indie-game-story-generator",
    name: "Indie Game Story Generator",
    slug: "indie-game-story-generator",
    shortDescription:
      "Create unique indie game stories and mechanics with our generator, tailored to your themes and details for an engaging experience.",
    fields: [
      {
        type: "textArea",
        label: "Game Topic",
        field: "Game Topic",
        placeholder: "Space Exploration",
        required: true,
        optional: false,
        id: "gametopic",
      },
      {
        type: "textArea",
        label: "Game Genre",
        field: "Game Genre",
        placeholder: "Adventure",
        required: false,
        optional: true,
        id: "gamegenre",
      },
      {
        type: "textArea",
        label: "Desired Game Mechanics",
        field: "Desired Game Mechanics",
        placeholder: "Puzzle-solving, Resource management, Exploration",
        required: false,
        optional: true,
        id: "desiredgamemechanics",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating unique indie game stories. You will be provided with details about the game topic, genre, and desired mechanics, and you will generate a compelling story along with associated game mechanics based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { gameTopic, gameGenre, desiredGameMechanics } = fields;
      let prompt =
        "Generate a unique indie game story based on the following:\n";
      prompt += "Game Topic: " + gameTopic + ".\n";
      if (gameGenre) {
        prompt += "Game Genre: " + gameGenre + ".\n";
      }
      if (desiredGameMechanics) {
        prompt += "Desired Game Mechanics: " + desiredGameMechanics + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "unassailable-argument-builder",
    name: "Unassailable Argument Builder",
    slug: "unassailable-argument-builder",
    shortDescription:
      "Create unassailable arguments effortlessly with our tool, generating compelling counterarguments that leave no room for rebuttal.",
    fields: [
      {
        type: "textArea",
        label: "Argument or Opinion",
        field: "Argument or Opinion",
        placeholder: "Input the argument or opinion you wish to refute here.",
        required: true,
        optional: false,
        id: "argumentoropinion",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in constructing unassailable arguments. You will be provided with an argument or opinion, and your task is to generate compelling counterarguments that effectively refute the given statement without leaving room for rebuttal.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { argumentOrOpinion } = fields.ArgumentOrOpinion;
      let prompt = "Generate a counterargument based on the following:\n";
      prompt += "Argument or Opinion: " + argumentOrOpinion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "first-person-storyteller",
    name: "First-Person Storyteller",
    slug: "first-person-storyteller",
    shortDescription:
      "Create immersive first-person stories by leveraging character backgrounds, traits, and engaging plots. Unlock your storytelling potential!",
    fields: [
      {
        type: "textArea",
        label: "Character Background",
        field: "Character Background",
        placeholder: "A young wizard from a small village...",
        required: true,
        optional: false,
        id: "characterbackground",
      },
      {
        type: "textArea",
        label: "Personality Traits",
        field: "Personality Traits",
        placeholder: "Brave, intelligent, has a sense of humor...",
        required: false,
        optional: true,
        id: "personalitytraits",
      },
      {
        type: "textArea",
        label: "Story Plot",
        field: "Story Plot",
        placeholder:
          "The wizard embarks on a journey to find a magical artifact...",
        required: false,
        optional: true,
        id: "storyplot",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert storyteller who specializes in crafting first-person narratives. You will be provided with details about a character and a plot, and your task is to generate a compelling story from the character's perspective.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { characterBackground, personalityTraits, storyPlot } = fields;
      let prompt = "Generate a first-person story based on the following:\n";
      prompt += "Character Background: " + characterBackground + "\n";
      if (personalityTraits) {
        prompt += "Personality Traits: " + personalityTraits + "\n";
      }
      if (storyPlot) {
        prompt += "Story Plot: " + storyPlot + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "grant-application-assistant",
    name: "Grant Application Assistant",
    slug: "grant-application-assistant",
    shortDescription:
      "Get expert help with grant applications! Our assistant ensures you answer questions accurately and effectively for success.",
    fields: [
      {
        type: "textArea",
        label: "Grant Question or Prompt",
        field: "Grant Question or Prompt",
        placeholder: "Describe the main objectives of your project.",
        required: true,
        optional: false,
        id: "grantquestionorprompt",
      },
      {
        type: "textArea",
        label: "Project Details",
        field: "Project Details",
        placeholder: "Our project aims to improve literacy rates among...",
        required: false,
        optional: true,
        id: "projectdetails",
      },
      {
        type: "textArea",
        label: "Project Impact",
        field: "Project Impact",
        placeholder: "The project will impact over 500 students by...",
        required: false,
        optional: true,
        id: "projectimpact",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in assisting users with grant applications. You will be provided with specific questions and details about a project, and you will generate accurate and effective responses for grant applications based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { grantQuestion, projectDetails, projectImpact } = fields;
      let prompt =
        "Generate a response for the grant application based on the following:\n";
      prompt += "Grant Question: " + grantQuestion + "\n";
      if (projectDetails) {
        prompt += "Project Details: " + projectDetails + "\n";
      }
      if (projectImpact) {
        prompt += "Project Impact: " + projectImpact + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "youtube-idea-generator",
    name: "YouTube Idea Generator",
    slug: "youtube-idea-generator",
    shortDescription:
      "Generate unique YouTube video ideas tailored to your interests and trending topics with our innovative YouTube Idea Generator.",
    fields: [
      {
        type: "textArea",
        label: "User Interests",
        field: "User Interests",
        placeholder: "Travel, Cooking, Tech Reviews",
        required: true,
        optional: false,
        id: "userinterests",
      },
      {
        type: "textArea",
        label: "Current Trends",
        field: "Current Trends",
        placeholder: "Minimalism, Plant-based diet, iPhone 13",
        required: false,
        optional: true,
        id: "currenttrends",
      },
    ],
    categories: ["Social Media", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating unique and engaging ideas for YouTube videos. You will be provided with user interests and current trends, and you will generate creative video ideas based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userInterests, currentTrends } = fields;
      let prompt =
        "Generate unique YouTube video ideas based on the following:\n";
      prompt += "User Interests: " + userInterests + ".\n";
      if (currentTrends) {
        prompt += "Current Trends: " + currentTrends + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "scholarmate",
    name: "ScholarMate",
    slug: "scholarmate",
    shortDescription:
      "Find academic articles, papers, and resources easily with ScholarMate. Your go-to tool for research on any topic!",
    fields: [
      {
        type: "textArea",
        label: "Research Topic",
        field: "Research Topic",
        placeholder: "Effects of climate change on biodiversity",
        required: true,
        optional: false,
        id: "researchtopic",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in academic research. You will be provided with a research topic, and you will assist the user in finding relevant academic articles, papers, and resources related to that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic } = fields;
      let prompt =
        "Find academic articles, papers, and resources based on the following research topic:\n";
      prompt += "Research Topic: " + researchTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "shopping-list-maker",
    name: "Shopping List Maker",
    slug: "shopping-list-maker",
    shortDescription:
      "Create personalized shopping lists effortlessly! Tailor your list based on meals, preferences, recipients, or travel needs.",
    fields: [
      {
        type: "textArea",
        label: "Shopping Details",
        field: "Shopping Details",
        placeholder:
          "I'm planning a vegan dinner for two; I love Italian cuisine; I'm going on a week-long trip to Hawaii",
        required: true,
        optional: false,
        id: "shoppingdetails",
      },
    ],
    categories: ["Business", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized shopping lists. You will be provided with details about meals, preferences, recipients, or travel destinations, and you will generate a tailored shopping list based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { shoppingDetails } = fields;
      let prompt =
        "Generate a personalized shopping list based on the following details:\n";
      prompt += "Shopping details: " + shoppingDetails + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "business-text-composer",
    name: "Business Text Composer",
    slug: "business-text-composer",
    shortDescription:
      "Create professional and effective business texts effortlessly with our Business Text Composer. Streamline your communication today!",
    fields: [
      {
        type: "textArea",
        label: "Recipient Name",
        field: "Recipient Name",
        placeholder: "John Doe",
        required: true,
        optional: false,
        id: "recipientname",
      },
      {
        type: "textArea",
        label: "Purpose of the Text",
        field: "Purpose of the Text",
        placeholder: "Schedule a meeting",
        required: false,
        optional: true,
        id: "purposeofthetext",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder: "Discuss Q1 sales, Plan for Q2, Review team performance",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Business", "Email", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in composing professional business texts. You will be provided with details about the recipient and the purpose of the text, and you will generate a well-structured business message based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientName, purposeOfTheText, keyPoints } = fields;
      let prompt = "Generate a business text based on the following:\n";
      prompt += "Recipient Name: " + recipientName + ".\n";
      if (purposeOfTheText) {
        prompt += "Purpose of the Text: " + purposeOfTheText + ".\n";
      }
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "recommendation-letter-writer",
    name: "Recommendation Letter Writer",
    slug: "recommendation-letter-writer",
    shortDescription:
      "Generate personalized recommendation letters effortlessly with our professional letter writer. Stand out with tailored content!",
    fields: [
      {
        type: "textArea",
        label: "Recommender's Name",
        field: "Recommender's Name",
        placeholder: "John Doe",
        required: true,
        optional: false,
        id: "recommendersname",
      },
      {
        type: "textArea",
        label: "Recommender's Position",
        field: "Recommender's Position",
        placeholder: "Professor of Computer Science",
        required: false,
        optional: true,
        id: "recommendersposition",
      },
      {
        type: "textArea",
        label: "Person's Name",
        field: "Person's Name",
        placeholder: "Jane Smith",
        required: false,
        optional: true,
        id: "personsname",
      },
      {
        type: "textArea",
        label: "Person's Achievements",
        field: "Person's Achievements",
        placeholder:
          "Jane was my student in Computer Science and she excelled in...",
        required: false,
        optional: true,
        id: "personsachievements",
      },
      {
        type: "textArea",
        label: "Purpose of Recommendation",
        field: "Purpose of Recommendation",
        placeholder: "Graduate School Application",
        required: false,
        optional: true,
        id: "purposeofrecommendation",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing recommendation letters. You will be provided with details about the recommender and the person being recommended, and you will generate a professional and personalized recommendation letter based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const {
        recommenderName,
        recommenderPosition,
        personName,
        personAchievements,
        purposeOfRecommendation,
      } = fields;
      let prompt = "Generate a recommendation letter based on the following:\n";
      prompt += "Recommender's Name: " + recommenderName + "\n";
      if (recommenderPosition) {
        prompt += "Recommender's Position: " + recommenderPosition + "\n";
      }
      if (personName) {
        prompt += "Person's Name: " + personName + "\n";
      }
      if (personAchievements) {
        prompt += "Person's Achievements: " + personAchievements + "\n";
      }
      if (purposeOfRecommendation) {
        prompt +=
          "Purpose of Recommendation: " + purposeOfRecommendation + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "non-profit-mission-statement-generator",
    name: "Non-Profit Mission Statement Generator",
    slug: "non-profit-mission-statement-generator",
    shortDescription:
      "Create impactful mission statements for your non-profit organization with our generator, tailored to your cause and goals.",
    fields: [
      {
        type: "textArea",
        label: "Organization Name",
        field: "Organization Name",
        placeholder: "Helping Hands Foundation",
        required: true,
        optional: false,
        id: "organizationname",
      },
      {
        type: "textArea",
        label: "Mission Description",
        field: "Mission Description",
        placeholder:
          "We aim to provide educational support to underprivileged children.",
        required: false,
        optional: true,
        id: "missiondescription",
      },
    ],
    categories: ["Writing", "Non-Profit", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting mission statements for non-profit organizations. You will be provided with the organization's name and a brief description of its mission, and you will generate a compelling mission statement based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { organizationName, missionDescription } = fields;
      let prompt =
        "Create a mission statement for the following non-profit organization:\n";
      prompt += "Organization Name: " + organizationName + "\n";
      if (missionDescription) {
        prompt += "Mission Description: " + missionDescription + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "multiple-choice-question-maker",
    name: "Multiple Choice Question Maker",
    slug: "multiple-choice-question-maker",
    shortDescription:
      "Create engaging multiple choice questions effortlessly with our generator, tailored from your provided study material.",
    fields: [
      {
        type: "textArea",
        label: "Study Material",
        field: "Study Material",
        placeholder: "In the year 1492, Christopher Columbus...",
        required: true,
        optional: false,
        id: "studymaterial",
      },
      {
        type: "textArea",
        label: "Number of Questions",
        field: "Number of Questions",
        placeholder: "10",
        required: false,
        optional: true,
        id: "numberofquestions",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating multiple choice questions. You will be provided with study material and will generate multiple choice questions based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { studyMaterial, numberOfQuestions } = fields;
      let prompt =
        "Generate multiple choice questions based on the following study material:\n";
      prompt += "Study Material: " + studyMaterial + "\n";
      if (numberOfQuestions) {
        prompt += "Number of Questions: " + numberOfQuestions + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "baby-name-generator",
    name: "Baby Name Generator",
    slug: "baby-name-generator",
    shortDescription:
      "Generate unique baby names based on your desired traits, culture, and personal details with our intuitive Baby Name Generator.",
    fields: [
      {
        type: "textArea",
        label: "Desired Characteristics",
        field: "Desired Characteristics",
        placeholder: "Unique, strong, easy to pronounce",
        required: true,
        optional: false,
        id: "desiredcharacteristics",
      },
      {
        type: "textArea",
        label: "Cultural/Historical/Personal Details",
        field: "Cultural/Historical/Personal Details",
        placeholder: "Irish heritage, family tradition of 'J' names",
        required: false,
        optional: true,
        id: "cultural/historical/personaldetails",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating baby names. You will be provided with desired characteristics and any cultural, historical, or personal details, and you will generate a list of suitable baby names based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { desiredCharacteristics, culturalHistoricalPersonalDetails } =
        fields;
      let prompt = "Generate a list of baby names based on the following:\n";
      prompt += "Desired characteristics: " + desiredCharacteristics + ".\n";
      if (culturalHistoricalPersonalDetails) {
        prompt +=
          "Cultural/Historical/Personal details: " +
          culturalHistoricalPersonalDetails +
          ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-chat",
    name: "AI Chat",
    slug: "ai-chat",
    shortDescription:
      "Get instant answers to any question with our AI chat. Engage in interactive conversations on a variety of topics seamlessly!",
    fields: [
      {
        type: "textArea",
        label: "User Question",
        field: "User Question",
        placeholder: "What is the capital of Australia?",
        required: true,
        optional: false,
        id: "userquestion",
      },
    ],
    categories: ["Support", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI assistant designed to provide informative and accurate answers to user questions on any topic. Your goal is to engage in a conversational manner and deliver clear, concise responses.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userQuestion } = fields;
      let prompt = "Provide a detailed answer to the following question:\n";
      prompt += "User Question: " + userQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "advice-generator",
    name: "Advice Generator",
    slug: "advice-generator",
    shortDescription:
      "Get tailored advice with our Advice Generator, providing helpful insights while steering clear of controversial topics.",
    fields: [
      {
        type: "textArea",
        label: "User's Query",
        field: "User's Query",
        placeholder: "What are some good first date ideas?",
        required: true,
        optional: false,
        id: "usersquery",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing thoughtful and practical advice. You will be given a user's query, and your task is to generate helpful advice while avoiding controversial or sensitive topics.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userQuery } = fields;
      let prompt = "Provide advice based on the following query:\n";
      prompt += "User's Query: " + userQuery + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "corporate-email-reply-assistant",
    name: "Corporate Email Reply Assistant",
    slug: "corporate-email-reply-assistant",
    shortDescription:
      "Streamline your corporate communication with our Email Reply Assistant, crafting personalized formal replies effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Received Email",
        field: "Received Email",
        placeholder: "Paste the email that you want to reply to.",
        required: true,
        optional: false,
        id: "receivedemail",
      },
      {
        type: "textArea",
        label: "Response Details",
        field: "Response Details",
        placeholder: "Write a shorthand response or what you want to convey.",
        required: false,
        optional: true,
        id: "responsedetails",
      },
    ],
    categories: ["Email", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting formal replies to corporate emails. You will be provided with the content of a received email and any specific messages the user wants to convey, and you will generate a personalized response.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { receivedEmail, responseDetails } = fields;
      let prompt = "Generate a formal reply to the following email:\n";
      prompt += "Received Email: " + receivedEmail + "\n";
      if (responseDetails) {
        prompt += "Response Details: " + responseDetails + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-content-planner",
    name: "AI Content Planner",
    slug: "ai-content-planner",
    shortDescription:
      "Generate a tailored content strategy with our AI Content Planner, designed for your industry and marketing goals.",
    fields: [
      {
        type: "textArea",
        label: "Industry",
        field: "Industry",
        placeholder: "e.g., Tech, Health, Fashion, etc.",
        required: true,
        optional: false,
        id: "industry",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder:
          "e.g., Young professionals, Parents, Fitness enthusiasts, etc.",
        required: false,
        optional: true,
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Marketing Goals",
        field: "Marketing Goals",
        placeholder:
          "e.g., Increase brand awareness, Drive website traffic, Generate leads, etc.",
        required: false,
        optional: true,
        id: "marketinggoals",
      },
    ],
    categories: ["Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating comprehensive content strategies. You will be provided with information about the user's industry, target audience, and marketing goals to generate a tailored content plan.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Industry, TargetAudience, MarketingGoals } = fields;
      let prompt =
        "Generate a comprehensive content strategy based on the following:\n";
      prompt += "Industry: " + Industry + ".\n";
      if (TargetAudience) {
        prompt += "Target Audience: " + TargetAudience + ".\n";
      }
      if (MarketingGoals) {
        prompt += "Marketing Goals: " + MarketingGoals + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "inspirational-quotation-generator",
    name: "Inspirational Quotation Generator",
    slug: "inspirational-quotation-generator",
    shortDescription:
      "Generate random inspirational quotes tailored to your favorite themes and uplift your spirit with every click!",
    fields: [
      {
        type: "textArea",
        label: "Theme or Topic",
        field: "Theme or Topic",
        placeholder: "Feeling burnt out",
        required: true,
        optional: false,
        id: "themeortopic",
      },
    ],
    categories: ["Social Media", "Motivation", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating inspirational quotes. You will be provided with a theme or topic, and you will generate a random inspirational quote based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { themeOrTopic } = fields;
      let prompt =
        "Generate an inspirational quote based on the following theme or topic:\n";
      prompt += "Theme or Topic: " + themeOrTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "medieval-fantasy-novel-composer",
    name: "Medieval Fantasy Novel Composer",
    slug: "medieval-fantasy-novel-composer",
    shortDescription:
      "Craft your medieval fantasy novel with our composer, blending the epic styles of Game of Thrones, LotR, and D&D for captivating tales.",
    fields: [
      {
        type: "textArea",
        label: "Plot Outline",
        field: "Plot Outline",
        placeholder: "The kingdom of Eldoria is on the brink of civil war...",
        required: true,
        optional: false,
        id: "plotoutline",
      },
      {
        type: "textArea",
        label: "Character Details",
        field: "Character Details",
        placeholder:
          "King Alden: A wise and just ruler, but his health is failing...",
        required: false,
        optional: true,
        id: "characterdetails",
      },
      {
        type: "textArea",
        label: "Setting Details",
        field: "Setting Details",
        placeholder:
          "Eldoria: A kingdom of sprawling forests and towering mountains...",
        required: false,
        optional: true,
        id: "settingdetails",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a skilled writer specializing in medieval fantasy novels. You will be provided with a plot outline, character details, and setting details to create an engaging narrative that captures the essence of classic medieval fantasy tales.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { plotOutline, characterDetails, settingDetails } = fields;
      let prompt = "Compose a medieval fantasy novel based on the following:\n";
      prompt += "Plot Outline: " + plotOutline + "\n";
      if (characterDetails) {
        prompt += "Character Details: " + characterDetails + "\n";
      }
      if (settingDetails) {
        prompt += "Setting Details: " + settingDetails + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "webpage-text-extractor",
    name: "Webpage Text Extractor",
    slug: "webpage-text-extractor",
    shortDescription:
      "Easily extract and view all readable text from any webpage link with our Webpage Text Extractor tool.",
    fields: [
      {
        type: "textArea",
        label: "Webpage Link",
        field: "Webpage Link",
        placeholder: "https://www.example.com",
        required: true,
        optional: false,
        id: "webpagelink",
      },
    ],
    categories: ["Website", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in extracting and presenting readable text from webpages. You will be provided with a webpage link, and your task is to extract all the relevant text content from that page.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { webpageLink } = fields;
      let prompt =
        "Extract all readable text from the following webpage link:\n";
      prompt += "Webpage Link: " + webpageLink + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "topic-qa-generator",
    name: "Topic Q&A Generator",
    slug: "topic-qa-generator",
    shortDescription:
      "Generate engaging multiple-choice questions and answers effortlessly with our Topic Q&A Generator for any subject matter.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Solar System for Beginners",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating multiple-choice questions and answers based on a given topic. You will be provided with a specific topic, and you will create a set of engaging and informative questions along with their corresponding answers.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields.Topic;
      let prompt =
        "Generate a list of multiple-choice questions and answers based on the following topic:\n";
      prompt += "Topic: " + Topic + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "job-role-interest-and-qualification-composer",
    name: "Job Role Interest and Qualification Composer",
    slug: "job-role-interest-and-qualification-composer",
    shortDescription:
      "Craft a compelling response to 'Why are you interested in this role?' and highlight your relevant skills and experiences effectively.",
    fields: [
      {
        type: "textArea",
        label: "Job Roles and Responsibilities",
        field: "Job Roles and Responsibilities",
        placeholder: "As a product manager, you will be responsible for...",
        required: true,
        optional: false,
        id: "jobrolesandresponsibilities",
      },
      {
        type: "textArea",
        label: "User Experience",
        field: "User Experience",
        placeholder: "I have 5 years of experience as a product manager at...",
        required: false,
        optional: true,
        id: "userexperience",
      },
    ],
    categories: ["Business", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in composing responses for job applications. You will be provided with information about the job role and the user's experience, and you will generate a compelling response to the question 'Why are you interested in this role?' along with qualifications.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { jobRolesAndResponsibilities, userExperience } = fields;
      let prompt =
        "Generate a response to the question 'Why are you interested in this role?' based on the following:\n";
      prompt +=
        "Job Roles and Responsibilities: " + jobRolesAndResponsibilities + "\n";
      if (userExperience) {
        prompt += "User Experience: " + userExperience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "study-buddy",
    name: "Study Buddy",
    slug: "study-buddy",
    shortDescription:
      "Boost your study sessions with Study Buddy, offering comprehensive information to excel in any subject you choose.",
    fields: [
      {
        type: "textArea",
        label: "Study Subject",
        field: "Study Subject",
        placeholder: "Quantum Physics",
        required: true,
        optional: false,
        id: "studysubject",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert study assistant. You will be provided with a specific study subject, and you will generate comprehensive information and resources to help the user understand the topic better.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { studySubject } = fields.studySubject;
      let prompt =
        "Provide comprehensive information on the following study subject:\n";
      prompt += "Study Subject: " + studySubject + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-text-transformer",
    name: "AI Text Transformer",
    slug: "ai-text-transformer",
    shortDescription:
      "Transform your text effortlessly with our AI Text Transformer, customizing style and tone to meet your exact needs.",
    fields: [
      {
        type: "textArea",
        label: "Input Text",
        field: "Input Text",
        placeholder: "Enter the text you want to transform here.",
        required: true,
        optional: false,
        id: "inputtext",
      },
      {
        type: "textArea",
        label: "Desired Style/Tone",
        field: "Desired Style/Tone",
        placeholder:
          "Enter the style or tone you want the text to be transformed into. (e.g., formal, casual, humorous, poetic, etc.)",
        required: false,
        optional: true,
        id: "desiredstyle/tone",
      },
    ],
    categories: ["Writing", "Marketing", "Social Media"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in transforming text into various styles and tones. You will be provided with a piece of text and the desired style or tone, and you will transform the text accordingly.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { inputText, desiredStyleTone } = fields;
      let prompt =
        "Transform the following text based on the specified style or tone:\n";
      prompt += "Input Text: " + inputText + "\n";
      if (desiredStyleTone) {
        prompt += "Desired Style/Tone: " + desiredStyleTone + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "random-number-generator",
    name: "Random Number Generator",
    slug: "random-number-generator",
    shortDescription:
      "Easily generate random numbers within your desired range with our user-friendly Random Number Generator tool.",
    fields: [
      {
        type: "textArea",
        label: "Minimum Value",
        field: "Minimum Value",
        placeholder: "1",
        required: true,
        optional: false,
        id: "minimumvalue",
      },
      {
        type: "textArea",
        label: "Maximum Value",
        field: "Maximum Value",
        placeholder: "100",
        required: false,
        optional: true,
        id: "maximumvalue",
      },
    ],
    categories: ["Business", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating random numbers. You will be provided with a minimum and maximum value, and you will generate a random number within that range.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { minimumValue, maximumValue } = fields;
      let prompt =
        "Generate a random number based on the following parameters:\n";
      prompt += "Minimum Value: " + minimumValue + "\n";
      if (maximumValue) {
        prompt += "Maximum Value: " + maximumValue + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "advertising-ai",
    name: "Advertising AI",
    slug: "advertising-ai",
    shortDescription:
      "Revolutionize your advertising with AI: effortlessly create ad copy and strategize campaigns based on your unique input.",
    fields: [
      {
        type: "textArea",
        label: "Advertising Task",
        field: "Advertising Task",
        placeholder: "Create a catchy headline for a new shoe ad",
        required: true,
        optional: false,
        id: "advertisingtask",
      },
      {
        type: "textArea",
        label: "Additional Information (Optional)",
        field: "Additional Information (Optional)",
        placeholder:
          "The shoe is designed for athletes and is extremely durable",
        required: false,
        optional: true,
        id: "additionalinformation(optional)",
      },
    ],
    categories: ["Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in advertising and marketing. You will be provided with details about an advertising task, and you will generate creative and effective advertising content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { AdvertisingTask, AdditionalInformation } = fields;
      let prompt = "Generate advertising content based on the following:\n";
      prompt += "Advertising Task: " + AdvertisingTask + "\n";
      if (AdditionalInformation) {
        prompt += "Additional Information: " + AdditionalInformation + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-and-speech-composer",
    name: "Essay and Speech Composer",
    slug: "essay-and-speech-composer",
    shortDescription:
      "Generate polished essays and speeches tailored to your topic and audience with our easy-to-use composer tool.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "The Impact of Social Media",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "Discuss the pros and cons of social media, its impact on society, and how it's changing the way we communicate.",
        required: false,
        optional: true,
        id: "keypoints",
      },
      {
        type: "textArea",
        label: "Intended Audience",
        field: "Intended Audience",
        placeholder: "High school students",
        required: false,
        optional: true,
        id: "intendedaudience",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in composing essays and speeches. You will be provided with a topic, key points, and intended audience, and you will generate a polished essay or speech based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, KeyPoints, IntendedAudience } = fields;
      let prompt =
        "Generate a polished essay or speech based on the following:\n";
      prompt += "Topic: " + Topic + ".\n";
      if (KeyPoints) {
        prompt += "Key Points: " + KeyPoints + ".\n";
      }
      if (IntendedAudience) {
        prompt += "Intended Audience: " + IntendedAudience + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "anthropology-explorer",
    name: "Anthropology Explorer",
    slug: "anthropology-explorer",
    shortDescription:
      "Explore comprehensive insights into various topics in anthropology with Anthropology Explorer, your go-to resource for knowledge.",
    fields: [
      {
        type: "textArea",
        label: "Anthropology Topic",
        field: "Anthropology Topic",
        placeholder: "Cultural Anthropology",
        required: true,
        optional: false,
        id: "anthropologytopic",
      },
    ],
    categories: ["Education", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in anthropology. You will be provided with a specific topic within the field of anthropology, and you will generate comprehensive information about that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { anthropologyTopic } = fields;
      let prompt =
        "Provide comprehensive information about the following anthropology topic:\n";
      prompt += "Anthropology Topic: " + anthropologyTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "audience-avatar-creator",
    name: "Audience Avatar Creator",
    slug: "audience-avatar-creator",
    shortDescription:
      "Create detailed customer avatars for your industry, covering demographics, goals, challenges, and more to enhance your marketing strategy.",
    fields: [
      {
        type: "textArea",
        label: "Industry",
        field: "Industry",
        placeholder: "Healthcare",
        required: true,
        optional: false,
        id: "industry",
      },
      {
        type: "textArea",
        label: "Services",
        field: "Services",
        placeholder: "We provide telehealth services and online consultations.",
        required: false,
        optional: true,
        id: "services",
      },
    ],
    categories: ["Marketing", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating detailed customer avatars. You will be provided with information about the industry and services, and you will generate a comprehensive customer avatar based on the provided details.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Industry, Services } = fields;
      let prompt =
        "Generate a detailed customer avatar based on the following:\n";
      prompt += "Industry: " + Industry + ".\n";
      if (Services) {
        prompt += "Services offered: " + Services + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-content-generator",
    name: "AI Content Generator",
    slug: "ai-content-generator",
    shortDescription:
      "Create high-quality, SEO-friendly content effortlessly with our AI Content Generator tailored to your specific writing tasks.",
    fields: [
      {
        type: "textArea",
        label: "Writing Task",
        field: "Writing Task",
        placeholder:
          "Write a 500-word blog post about the latest trends in digital marketing",
        required: true,
        optional: false,
        id: "writingtask",
      },
    ],
    categories: ["Writing", "Marketing", "Website"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert content creator specializing in generating high-quality, SEO-friendly content. You will be provided with a writing task and you will create content based on the details provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingTask } = fields;
      let prompt = "Generate content based on the following writing task:\n";
      prompt += "Writing Task: " + writingTask + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "birthday-card-ai",
    name: "Birthday Card AI",
    slug: "birthday-card-ai",
    shortDescription:
      "Generate heartfelt birthday messages effortlessly with Birthday Card AI—simple, high-quality, and personalized just for you!",
    fields: [
      {
        type: "textArea",
        label: "Birthday Details",
        field: "Birthday Details",
        placeholder: "Best friend, Matt, 21st birthday...",
        required: true,
        optional: false,
        id: "birthdaydetails",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting personalized birthday messages. You will be provided with details about the birthday person and the occasion, and you will generate a heartfelt birthday message based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { birthdayDetails } = fields;
      let prompt = "Generate a birthday message based on the following:\n";
      prompt += "Birthday Details: " + birthdayDetails + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "cool-words-finder",
    name: "Cool Words Finder",
    slug: "cool-words-finder",
    shortDescription:
      "Discover unique and interesting words with Cool Words Finder. Input a word or description and unlock a world of cool vocabulary!",
    fields: [
      {
        type: "textArea",
        label: "Word/Description/Scenario",
        field: "Word/Description/Scenario",
        placeholder: "A tense situation",
        required: true,
        optional: false,
        id: "word/description/scenario",
      },
    ],
    categories: ["Writing", "Marketing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in finding cool, unique, or interesting words. You will be provided with a word, description, or scenario, and you will generate a list of cool words that fit the input.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { wordDescriptionScenario } = fields.Word / Description / Scenario;
      let prompt =
        "Generate a list of cool words based on the following input:\n";
      prompt += "Input: " + wordDescriptionScenario + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-startup-accelerator",
    name: "AI Startup Accelerator",
    slug: "ai-startup-accelerator",
    shortDescription:
      "Launch your AI startup with a tailored roadmap and daily tasks, plus support from an AI chatbot mentor to guide your journey.",
    fields: [
      {
        type: "textArea",
        label: "Startup Idea",
        field: "Startup Idea",
        placeholder: "Describe your startup idea in detail...",
        required: true,
        optional: false,
        id: "startupidea",
      },
      {
        type: "textArea",
        label: "Goals",
        field: "Goals",
        placeholder: "What are your goals for your startup?",
        required: false,
        optional: true,
        id: "goals",
      },
      {
        type: "textArea",
        label: "Skills",
        field: "Skills",
        placeholder: "What skills do you bring to your startup?",
        required: false,
        optional: true,
        id: "skills",
      },
    ],
    categories: ["Business", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in guiding entrepreneurs through the startup process. You will be provided with details about a startup idea, goals, and skills, and you will generate a customized roadmap and daily tasks to help launch the startup.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { StartupIdea, Goals, Skills } = fields;
      let prompt =
        "Generate a customized roadmap for launching a startup based on the following:\n";
      prompt += "Startup Idea: " + StartupIdea + "\n";
      if (Goals) {
        prompt += "Goals: " + Goals + "\n";
      }
      if (Skills) {
        prompt += "Skills: " + Skills + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "love-letter-generator",
    name: "Love Letter Generator",
    slug: "love-letter-generator",
    shortDescription:
      "Create a heartfelt love letter effortlessly with our generator, tailored to express your true feelings for that special someone.",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Details",
        field: "Recipient's Details",
        placeholder: "Name, shared memories, their qualities...",
        required: true,
        optional: false,
        id: "recipientsdetails",
      },
      {
        type: "textArea",
        label: "Sender's Feelings",
        field: "Sender's Feelings",
        placeholder:
          "What you appreciate about them, how they make you feel...",
        required: false,
        optional: true,
        id: "sendersfeelings",
      },
    ],
    categories: ["Writing", "Social Media"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting heartfelt love letters. You will be provided with details about the recipient and the sender's feelings, and you will generate a love letter based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientDetails, senderFeelings } = fields;
      let prompt = "Generate a heartfelt love letter based on the following:\n";
      prompt += "Recipient's Details: " + recipientDetails + "\n";
      if (senderFeelings) {
        prompt += "Sender's Feelings: " + senderFeelings + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "non-profit-fundraising-strategy-builder",
    name: "Non-Profit Fundraising Strategy Builder",
    slug: "non-profit-fundraising-strategy-builder",
    shortDescription:
      "Create tailored fundraising strategies for your non-profit organization. Boost your mission with innovative approaches and impactful results.",
    fields: [
      {
        type: "textArea",
        label: "Organization Name",
        field: "Organization Name",
        placeholder: "Enter your non-profit's name",
        required: true,
        optional: false,
        id: "organizationname",
      },
      {
        type: "textArea",
        label: "Mission Statement",
        field: "Mission Statement",
        placeholder:
          "Enter a description of your nonprofit's unique objectives",
        required: false,
        optional: true,
        id: "missionstatement",
      },
      {
        type: "textArea",
        label: "Mission Area",
        field: "Mission Area",
        placeholder: "Enter the area or cause your non-profit aims to address",
        required: false,
        optional: true,
        id: "missionarea",
      },
    ],
    categories: ["Marketing", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating innovative fundraising strategies for non-profit organizations. You will be provided with information about the organization and its mission, and you will create tailored fundraising strategies based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { organizationName, missionStatement, missionArea } = fields;
      let prompt = "Generate a fundraising strategy based on the following:\n";
      prompt += "Organization Name: " + organizationName + ".\n";
      if (missionStatement) {
        prompt += "Mission Statement: " + missionStatement + ".\n";
      }
      if (missionArea) {
        prompt += "Mission Area: " + missionArea + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "dall-e-prompt-optimizer",
    name: "DALL-E Prompt Optimizer",
    slug: "dall-e-prompt-optimizer",
    shortDescription:
      "Craft high-quality AI images with DALL-E using our Prompt Optimizer. Create effective prompts effortlessly for stunning results.",
    fields: [
      {
        type: "textArea",
        label: "Desired Image Characteristics",
        field: "Desired Image Characteristics",
        placeholder:
          "A futuristic cityscape at sunset with flying cars and skyscrapers.",
        required: true,
        optional: false,
        id: "desiredimagecharacteristics",
      },
    ],
    categories: ["Marketing", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating prompts for DALL-E, an AI image generation tool. You will be provided with specific characteristics of the desired image, and you will create a detailed prompt to help generate high-quality images.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { desiredImageCharacteristics } = fields;
      let prompt =
        "Create a DALL-E prompt based on the following characteristics:\n";
      prompt +=
        "Desired Image Characteristics: " + desiredImageCharacteristics + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "lesson-plan-maker",
    name: "Lesson Plan Maker",
    slug: "lesson-plan-maker",
    shortDescription:
      "Create tailored lesson plans effortlessly! Our generator crafts comprehensive plans based on grade level, subject, and learning goals.",
    fields: [
      {
        type: "textArea",
        label: "Grade Level",
        field: "Grade Level",
        placeholder: "3rd Grade",
        required: true,
        optional: false,
        id: "gradelevel",
      },
      {
        type: "textArea",
        label: "Topic/Subject Area",
        field: "Topic/Subject Area",
        placeholder: "Mathematics",
        required: false,
        optional: true,
        id: "topic/subjectarea",
      },
      {
        type: "textArea",
        label: "Learning Objectives",
        field: "Learning Objectives",
        placeholder: "Students will be able to understand multiplication...",
        required: false,
        optional: true,
        id: "learningobjectives",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating lesson plans. You will be provided with details about the grade level, subject area, and learning objectives, and you will generate a comprehensive lesson plan based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { gradeLevel, topicSubjectArea, learningObjectives } = fields;
      let prompt =
        "Generate a comprehensive lesson plan based on the following:\n";
      prompt += "Grade Level: " + gradeLevel + ".\n";
      if (topicSubjectArea) {
        prompt += "Topic/Subject Area: " + topicSubjectArea + ".\n";
      }
      if (learningObjectives) {
        prompt += "Learning Objectives: " + learningObjectives + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "fan-fiction-creator",
    name: "Fan Fiction Creator",
    slug: "fan-fiction-creator",
    shortDescription:
      "Create custom fan fiction stories with our tool by defining characters, settings, and plots for your imaginative adventures.",
    fields: [
      {
        type: "textArea",
        label: "Characters",
        field: "Characters",
        placeholder: "Harry Potter, Hermione Granger, Ron Weasley",
        required: true,
        optional: false,
        id: "characters",
      },
      {
        type: "textArea",
        label: "Setting",
        field: "Setting",
        placeholder: "Hogwarts School of Witchcraft and Wizardry",
        required: false,
        optional: true,
        id: "setting",
      },
      {
        type: "textArea",
        label: "Plot",
        field: "Plot",
        placeholder: "Harry discovers a lost spell that...",
        required: false,
        optional: true,
        id: "plot",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative writer specializing in fan fiction. You will be provided with characters, a setting, and a plot, and you will generate an engaging fan fiction story based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Characters, Setting, Plot } = fields;
      let prompt = "Generate a fan fiction story based on the following:\n";
      prompt += "Characters: " + Characters + ".\n";
      if (Setting) {
        prompt += "Setting: " + Setting + ".\n";
      }
      if (Plot) {
        prompt += "Plot: " + Plot + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "reflection-ai",
    name: "Reflection AI",
    slug: "reflection-ai",
    shortDescription:
      "Generate insightful reflections effortlessly with Reflection AI based on any prompt, summary, or background information.",
    fields: [
      {
        type: "textArea",
        label: "Prompt",
        field: "Prompt",
        placeholder:
          "Describe the event, book, or experience you are reflecting on.",
        required: true,
        optional: false,
        id: "prompt",
      },
      {
        type: "textArea",
        label: "Summary",
        field: "Summary",
        placeholder: "Summarize the main points or events.",
        required: false,
        optional: true,
        id: "summary",
      },
      {
        type: "textArea",
        label: "Background",
        field: "Background",
        placeholder: "Provide any relevant background information or context.",
        required: false,
        optional: true,
        id: "background",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thoughtful reflections. You will be provided with a prompt, summary, and background information, and you will generate a reflective piece based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Prompt, Summary, Background } = fields;
      let prompt = "Generate a thoughtful reflection based on the following:\n";
      prompt += "Prompt: " + Prompt + "\n";
      if (Summary) {
        prompt += "Summary: " + Summary + "\n";
      }
      if (Background) {
        prompt += "Background: " + Background + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "recipe-generator",
    name: "Recipe Generator",
    slug: "recipe-generator",
    shortDescription:
      "Create delicious recipes effortlessly! Our Recipe Generator crafts dishes based on your input for tasty culinary inspiration.",
    fields: [
      {
        type: "textArea",
        label: "Dish Name",
        field: "Dish Name",
        placeholder: "Chicken Alfredo",
        required: true,
        optional: false,
        id: "dishname",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating recipes. You will be provided with the name of a dish, and you will create a detailed recipe including ingredients, instructions, and cooking tips.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { dishName } = fields;
      let prompt = "Generate a recipe based on the following:\n";
      prompt += "Dish Name: " + dishName + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "excel-formula-writer",
    name: "Excel Formula Writer",
    slug: "excel-formula-writer",
    shortDescription:
      "Generate custom Excel formulas effortlessly with our Excel Formula Writer, tailored to your unique operations and needs.",
    fields: [
      {
        type: "textArea",
        label: "Operation Description",
        field: "Operation Description",
        placeholder: "Calculate the sum of values in column A and B",
        required: true,
        optional: false,
        id: "operationdescription",
      },
    ],
    categories: ["Business", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating Excel formulas. You will be provided with a description of the operation, and you will generate the corresponding Excel formula based on that description.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { operationDescription } = fields;
      let prompt =
        "Generate an Excel formula based on the following operation description:\n";
      prompt += "Operation Description: " + operationDescription + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "press-release-pitch-generator",
    name: "Press Release Pitch Generator",
    slug: "press-release-pitch-generator",
    shortDescription:
      "Create captivating press release pitches effortlessly with our generator, tailored to your news or event details.",
    fields: [
      {
        type: "textArea",
        label: "News or Event Details",
        field: "News or Event Details",
        placeholder: "Our company is launching a new product...",
        required: true,
        optional: false,
        id: "newsoreventdetails",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Tech enthusiasts",
        required: false,
        optional: true,
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder: "Innovative features, Launch date, Special offers",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Marketing", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating compelling press release pitches. You will be provided with details about a news or event, and you will generate a persuasive pitch based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { newsOrEventDetails, targetAudience, keyPoints } = fields;
      let prompt = "Generate a press release pitch based on the following:\n";
      prompt += "News or Event Details: " + newsOrEventDetails + "\n";
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + "\n";
      }
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writer-with-references",
    name: "AI Writer with References",
    slug: "ai-writer-with-references",
    shortDescription:
      "Transform your writing with our AI Writer that finds and cites relevant references for enhanced accuracy and credibility.",
    fields: [
      {
        type: "textArea",
        label: "Writing Request",
        field: "Writing Request",
        placeholder:
          "Essay on the impact of climate change on global agriculture.",
        required: true,
        optional: false,
        id: "writingrequest",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI writing assistant that specializes in generating written content with references. You will be provided a writing request and will generate a comprehensive response, including relevant citations and resources.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingRequest } = fields;
      let prompt =
        "Generate a written response based on the following request:\n";
      prompt += "Writing Request: " + writingRequest + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "biology-notes-structurer",
    name: "Biology Notes Structurer",
    slug: "biology-notes-structurer",
    shortDescription:
      "Transform biology topics into organized notes effortlessly with our Biology Notes Structurer for effective studying and revision.",
    fields: [
      {
        type: "textArea",
        label: "Biology Topic",
        field: "Biology Topic",
        placeholder: "Cellular Respiration",
        required: true,
        optional: false,
        id: "biologytopic",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in structuring biology notes. You will be provided with a specific biology topic, and you will generate a well-organized set of notes based on that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { biologyTopic } = fields;
      let prompt =
        "Generate a structured set of notes based on the following biology topic:\n";
      prompt += "Biology Topic: " + biologyTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "weekly-diet-recipe-planner",
    name: "Weekly Diet Recipe Planner",
    slug: "weekly-diet-recipe-planner",
    shortDescription:
      "Create personalized weekly diet plans with recipes tailored to your preferences and restrictions for a healthier lifestyle.",
    fields: [
      {
        type: "textArea",
        label: "Dietary Preferences",
        field: "Dietary Preferences",
        placeholder: "Vegetarian, Low Carb, High Protein",
        required: true,
        optional: false,
        id: "dietarypreferences",
      },
      {
        type: "textArea",
        label: "Dietary Restrictions",
        field: "Dietary Restrictions",
        placeholder: "Gluten Free, Dairy Free, Nut Allergy",
        required: false,
        optional: true,
        id: "dietaryrestrictions",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating weekly diet plans with recipes. You will be provided with dietary preferences and restrictions, and you will generate a comprehensive weekly diet plan that includes recipes tailored to the user's needs.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { dietaryPreferences, dietaryRestrictions } = fields;
      let prompt = "Generate a weekly diet plan based on the following:\n";
      prompt += "Dietary Preferences: " + dietaryPreferences + ".\n";
      if (dietaryRestrictions) {
        prompt += "Dietary Restrictions: " + dietaryRestrictions + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ingredients-to-recipe-generator",
    name: "Ingredients to Recipe Generator",
    slug: "ingredients-to-recipe-generator",
    shortDescription:
      "Generate personalized recipes effortlessly with our AI by simply inputting the ingredients you have on hand.",
    fields: [
      {
        type: "textArea",
        label: "Ingredients",
        field: "Ingredients",
        placeholder: "Eggs, Bacon, Bread, Tomato, Lettuce",
        required: true,
        optional: false,
        id: "ingredients",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert chef and recipe creator. You will be provided with a list of ingredients, and you will generate a detailed recipe that utilizes those ingredients.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Ingredients } = fields;
      let prompt = "Generate a recipe based on the following ingredients:\n";
      prompt += "Ingredients: " + Ingredients + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "content-brief-creator",
    name: "Content Brief Creator",
    slug: "content-brief-creator",
    shortDescription:
      "Generate detailed content briefs for blog posts or articles effortlessly with our Content Brief Creator based on your chosen topic.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Digital Marketing Strategies",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Website URL",
        field: "Website URL",
        placeholder: "https://www.marketwhite.com",
        required: false,
        optional: true,
        id: "websiteurl",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating content briefs. You will be provided with a topic and a website URL, and you will generate a detailed content brief for a blog post or article based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, WebsiteURL } = fields;
      let prompt =
        "Generate a detailed content brief based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (WebsiteURL) {
        prompt += "Website URL: " + WebsiteURL + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "post-interview-thank-you-note-composer",
    name: "Post-Interview Thank You Note Composer",
    slug: "post-interview-thank-you-note-composer",
    shortDescription:
      "Generate personalized thank you notes post-interview using your resume and job details for a professional touch.",
    fields: [
      {
        type: "textArea",
        label: "Resume",
        field: "Resume",
        placeholder: "John Doe, Software Engineer, 5 years of experience...",
        required: true,
        optional: false,
        id: "resume",
      },
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "We are looking for a software engineer with...",
        required: false,
        optional: true,
        id: "jobdescription",
      },
      {
        type: "textArea",
        label: "Note Starter, Draft, or Details to Include",
        field: "Note Starter, Draft, or Details to Include",
        placeholder: "Dear Hiring Manager,",
        required: false,
        optional: true,
        id: "notestarter,draft,ordetailstoinclude",
      },
    ],
    categories: ["Email", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in composing thank you notes after interviews. You will be provided with a user's resume, job description, and any additional details they want to include, and you will generate a personalized thank you note based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Resume, JobDescription, NoteStarter } = fields;
      let prompt =
        "Generate a personalized thank you note based on the following:\n";
      prompt += "Resume: " + Resume + "\n";
      if (JobDescription) {
        prompt += "Job Description: " + JobDescription + "\n";
      }
      if (NoteStarter) {
        prompt += "Note Starter: " + NoteStarter + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "classmate-post-responder",
    name: "Classmate Post Responder",
    slug: "classmate-post-responder",
    shortDescription:
      "Generate personalized responses to your classmates' posts with Classmate Post Responder, mimicking your unique style effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Classmate's Post",
        field: "Classmate's Post",
        placeholder: "This week, I learned about...",
        required: true,
        optional: false,
        id: "classmatespost",
      },
      {
        type: "textArea",
        label: "User's Style Description",
        field: "User's Style Description",
        placeholder:
          "I usually write in a casual, friendly tone and like to use humor.",
        required: false,
        optional: true,
        id: "usersstyledescription",
      },
    ],
    categories: ["Education", "Writing", "Social Media"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized responses to college classmates' posts. You will be provided with a classmate's post and the user's style description, and you will generate a response that mimics the user's writing style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { classmatePost, userStyleDescription } = fields;
      let prompt =
        "Generate a personalized response to the following classmate's post:\n";
      prompt += "Classmate's Post: " + classmatePost + "\n";
      if (userStyleDescription) {
        prompt += "User's Style Description: " + userStyleDescription + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "action-item-extractor",
    name: "Action Item Extractor",
    slug: "action-item-extractor",
    shortDescription:
      "Effortlessly extract and list action items from any text while preserving essential links and documents for easy reference.",
    fields: [
      {
        type: "textArea",
        label: "TextToExtract",
        field: "TextToExtract",
        placeholder:
          "In our meeting, we decided that John will prepare the financial report by next Monday. Here is the link to the necessary data: www.data-link.com. Sarah will contact the client to schedule a meeting. The client's contact info can be found here: www.clientinfo.com. I will review the marketing strategy and present it in the next meeting.",
        required: true,
        optional: false,
        id: "texttoextract",
      },
    ],
    categories: ["Business", "Support", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in extracting action items from text. You will be provided with a block of text, and your task is to identify and list the action items while preserving key information such as links and documents.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { TextToExtract } = fields;
      let prompt = "Extract the action items from the following text:\n";
      prompt += TextToExtract + "\n";
      prompt +=
        "Please list the action items clearly, including any relevant links or documents.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "english-to-french-translator",
    name: "English to French Translator",
    slug: "english-to-french-translator",
    shortDescription:
      "Translate English text to French effortlessly with our AI-powered translator. Fast, accurate, and user-friendly!",
    fields: [
      {
        type: "textArea",
        label: "English Text",
        field: "English Text",
        placeholder: "Hello, how are you?",
        required: true,
        optional: false,
        id: "englishtext",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI language model specialized in translating English text into French. You will be provided with English text, and your task is to accurately translate it into French.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { EnglishText } = fields;
      let prompt = "Translate the following English text into French:\n";
      prompt += "English Text: " + EnglishText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "email-subject-header-optimizer",
    name: "Email Subject Header Optimizer",
    slug: "email-subject-header-optimizer",
    shortDescription:
      "Optimize your email subject headers effortlessly. Create compelling subjects that boost open rates based on your email content.",
    fields: [
      {
        type: "textArea",
        label: "Email Body or Subject Definition",
        field: "Email Body or Subject Definition",
        placeholder: "Dear John, I wanted to discuss our upcoming project...",
        required: true,
        optional: false,
        id: "emailbodyorsubjectdefinition",
      },
    ],
    categories: ["Email", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in optimizing email subject headers. You will be provided with the body or subject definition of an email, and you will generate the most effective subject header based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { emailBodyOrSubjectDefinition } = fields;
      let prompt =
        "Generate an optimal email subject header based on the following:\n";
      prompt +=
        "Email Body or Subject Definition: " +
        emailBodyOrSubjectDefinition +
        "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "two-paragraph-answer-generator",
    name: "Two-Paragraph Answer Generator",
    slug: "two-paragraph-answer-generator",
    shortDescription:
      "Generate comprehensive two-paragraph answers to any question effortlessly with our easy-to-use answer generator tool.",
    fields: [
      {
        type: "textArea",
        label: "User's Question",
        field: "User's Question",
        placeholder: "What is the history of the internet?",
        required: true,
        optional: false,
        id: "usersquestion",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing comprehensive answers. You will be given a user's question and you will generate a detailed two-paragraph answer based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userQuestion } = fields;
      let prompt =
        "Generate a comprehensive two-paragraph answer to the following question:\n";
      prompt += "User's Question: " + userQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "opening-remark-generator",
    name: "Opening Remark Generator",
    slug: "opening-remark-generator",
    shortDescription:
      "Create captivating introductions effortlessly with our Opening Remark Generator. Engage your audience from the start!",
    fields: [
      {
        type: "textArea",
        label: "Context",
        field: "Context",
        placeholder: "Introducing a new product in a blog post",
        required: true,
        optional: false,
        id: "context",
      },
      {
        type: "textArea",
        label: "Purpose",
        field: "Purpose",
        placeholder: "To generate excitement and interest in the product",
        required: false,
        optional: true,
        id: "purpose",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating opening remarks for introductions. You will be provided with context and purpose, and you will create an engaging opening remark based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Context, Purpose } = fields;
      let prompt =
        "Generate an engaging opening remark based on the following:\n";
      prompt += "Context: " + Context + "\n";
      if (Purpose) {
        prompt += "Purpose: " + Purpose + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "fiction-story-generator",
    name: "Fiction Story Generator",
    slug: "fiction-story-generator",
    shortDescription:
      "Create unique short fiction stories tailored to your preferences with our Fiction Story Generator. Explore endless storytelling possibilities!",
    fields: [
      {
        type: "textArea",
        label: "Genre",
        field: "Genre",
        placeholder: "Fantasy, Sci-Fi, Mystery, etc.",
        required: true,
        optional: false,
        id: "genre",
      },
      {
        type: "textArea",
        label: "Main Character(s)",
        field: "Main Character(s)",
        placeholder: "A brave knight, a cunning detective, etc.",
        required: false,
        optional: true,
        id: "maincharacter(s)",
      },
      {
        type: "textArea",
        label: "Setting",
        field: "Setting",
        placeholder: "A haunted castle, a distant planet, etc.",
        required: false,
        optional: true,
        id: "setting",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating fiction stories. You will be provided with details about the genre, main characters, and setting, and you will create a short fiction story based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Genre, MainCharacter, Setting } = fields;
      let prompt = "Generate a short fiction story based on the following:\n";
      prompt += "Genre: " + Genre + ".\n";
      if (MainCharacter) {
        prompt += "Main Character(s): " + MainCharacter + ".\n";
      }
      if (Setting) {
        prompt += "Setting: " + Setting + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "business-name-generator",
    name: "Business Name Generator",
    slug: "business-name-generator",
    shortDescription:
      "Generate unique business names tailored to your vision with our intuitive Business Name Generator. Stand out in the market today!",
    fields: [
      {
        type: "textArea",
        label: "Characteristics",
        field: "Characteristics",
        placeholder: "Innovative, Tech-savvy, Eco-friendly",
        required: true,
        optional: false,
        id: "characteristics",
      },
      {
        type: "textArea",
        label: "Details",
        field: "Details",
        placeholder: "Software, Green Energy, Startup",
        required: false,
        optional: true,
        id: "details",
      },
    ],
    categories: ["Business", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating unique business or product names. You will be provided with characteristics and details, and you will generate a list of creative names based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Characteristics, Details } = fields;
      let prompt =
        "Generate unique business or product names based on the following:\n";
      prompt += "Characteristics: " + Characteristics + ".\n";
      if (Details) {
        prompt += "Details: " + Details + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "wedding-speech",
    name: "Wedding Speech",
    slug: "wedding-speech",
    shortDescription:
      "Find inspiration for your wedding speech! Explore ideas and examples to craft the perfect heartfelt message for the big day.",
    fields: [
      {
        type: "textArea",
        label: "Role",
        field: "Role",
        placeholder: "Best Man / Maid of Honor / Father of the Bride / etc.",
        required: true,
        optional: false,
        id: "role",
      },
      {
        type: "textArea",
        label: "Relationship with the Couple",
        field: "Relationship with the Couple",
        placeholder:
          "Describe your relationship with the couple, any shared experiences or memories.",
        required: false,
        optional: true,
        id: "relationshipwiththecouple",
      },
      {
        type: "textArea",
        label: "Couple's Story or Anecdotes",
        field: "Couple's Story or Anecdotes",
        placeholder:
          "Describe the couple's love story, how they met, their journey together, etc.",
        required: false,
        optional: true,
        id: "couplesstoryoranecdotes",
      },
    ],
    categories: ["Writing", "Social Media", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting wedding speeches. You will be provided with details about your role and the couple, and you will generate a heartfelt wedding speech based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Role, RelationshipWithTheCouple, CouplesStoryOrAnecdotes } =
        fields;
      let prompt = "Generate a wedding speech based on the following:\n";
      prompt += "Role: " + Role + ".\n";
      if (RelationshipWithTheCouple) {
        prompt +=
          "Relationship with the couple: " + RelationshipWithTheCouple + ".\n";
      }
      if (CouplesStoryOrAnecdotes) {
        prompt +=
          "Couple's story or anecdotes: " + CouplesStoryOrAnecdotes + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-content-planner",
    name: "AI Content Planner",
    slug: "ai-content-planner",
    shortDescription:
      "Streamline your content creation with our AI Content Planner, crafting tailored strategies for your industry and marketing goals.",
    fields: [
      {
        type: "textArea",
        label: "Industry",
        field: "Industry",
        placeholder: "Software Development",
        required: true,
        optional: false,
        id: "industry",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Small Business Owners",
        required: false,
        optional: true,
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Marketing Goals",
        field: "Marketing Goals",
        placeholder:
          "Increase website traffic by 20%, Generate 100 new leads, Increase brand awareness",
        required: false,
        optional: true,
        id: "marketinggoals",
      },
    ],
    categories: ["Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating comprehensive content strategies. You will be provided with information about the user's industry, target audience, and marketing goals to generate a tailored content plan.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Industry, TargetAudience, MarketingGoals } = fields;
      let prompt =
        "Generate a comprehensive content strategy based on the following:\n";
      prompt += "Industry: " + Industry + ".\n";
      if (TargetAudience) {
        prompt += "Target Audience: " + TargetAudience + ".\n";
      }
      if (MarketingGoals) {
        prompt += "Marketing Goals: " + MarketingGoals + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "linkedin-comment-composer",
    name: "LinkedIn Comment Composer",
    slug: "linkedin-comment-composer",
    shortDescription:
      "Generate engaging comments for LinkedIn articles with our Comment Composer, enhancing your interactions and visibility on the platform.",
    fields: [
      {
        type: "textArea",
        label: "Article or Post Content",
        field: "Article or Post Content",
        placeholder: "The future of AI in healthcare...",
        required: true,
        optional: false,
        id: "articleorpostcontent",
      },
      {
        type: "textArea",
        label: "User Professional Background",
        field: "User Professional Background",
        placeholder: "Software engineer with interest in AI",
        required: false,
        optional: true,
        id: "userprofessionalbackground",
      },
    ],
    categories: ["Social Media", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating insightful and engaging comments for LinkedIn articles and posts. You will be provided with the content of an article or post, along with optional information about the user's professional background, and you will generate a relevant comment based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { articleOrPostContent, userProfessionalBackground } = fields;
      let prompt =
        "Generate a comment for a LinkedIn article or post based on the following:\n";
      prompt += "Article or Post Content: " + articleOrPostContent + "\n";
      if (userProfessionalBackground) {
        prompt +=
          "User Professional Background: " + userProfessionalBackground + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "personalized-linkedin-connection-request-composer",
    name: "Personalized LinkedIn Connection Request Composer",
    slug: "personalized-linkedin-connection-request-composer",
    shortDescription:
      "Craft personalized LinkedIn connection requests effortlessly with our composer tool and enhance your networking experience.",
    fields: [
      {
        type: "textArea",
        label: "Recipient's LinkedIn Profile URL",
        field: "Recipient's LinkedIn Profile URL",
        placeholder: "https://www.linkedin.com/in/janedoe",
        required: true,
        optional: false,
        id: "recipientslinkedinprofileurl",
      },
      {
        type: "textArea",
        label: "User's Job Search Field",
        field: "User's Job Search Field",
        placeholder: "Software Development",
        required: false,
        optional: true,
        id: "usersjobsearchfield",
      },
      {
        type: "textArea",
        label: "User's Relevant Experiences",
        field: "User's Relevant Experiences",
        placeholder: "Worked as a Junior Developer at XYZ for 2 years...",
        required: false,
        optional: true,
        id: "usersrelevantexperiences",
      },
    ],
    categories: ["Social Media", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting personalized LinkedIn connection request messages. You will be provided with details about the recipient and the user, and you will generate a tailored connection request message based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientProfileURL, jobSearchField, relevantExperiences } =
        fields;
      let prompt =
        "Craft a personalized LinkedIn connection request message based on the following:\n";
      prompt +=
        "Recipient's LinkedIn Profile URL: " + recipientProfileURL + "\n";
      if (jobSearchField) {
        prompt += "User's Job Search Field: " + jobSearchField + "\n";
      }
      if (relevantExperiences) {
        prompt += "User's Relevant Experiences: " + relevantExperiences + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "blog-post-assistant",
    name: "Blog Post Assistant",
    slug: "blog-post-assistant",
    shortDescription:
      "Create blog posts effortlessly with our assistant that generates outlines, SEO keywords, and engaging introductions for any topic.",
    fields: [
      {
        type: "textArea",
        label: "Blog Post Topic",
        field: "Blog Post Topic",
        placeholder: "Learning a New Language",
        required: true,
        optional: false,
        id: "blogposttopic",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating blog posts. You will be provided with a topic and you will create an outline, a list of SEO-optimized keywords, and an introduction paragraph based on the given topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { blogPostTopic } = fields;
      let prompt =
        "Generate an outline, a list of SEO-optimized keywords, and an introduction paragraph for the following blog post topic:\n";
      prompt += "Blog Post Topic: " + blogPostTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-essay-pruner",
    name: "AI Essay Pruner",
    slug: "ai-essay-pruner",
    shortDescription:
      "Enhance your essays with AI Essay Pruner: effortlessly remove unnecessary words for improved clarity and conciseness.",
    fields: [
      {
        type: "textArea",
        label: "Essay Text",
        field: "Essay Text",
        placeholder: "Enter your essay text here",
        required: true,
        optional: false,
        id: "essaytext",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in editing essays for clarity and conciseness. You will be provided with an essay text, and your task is to remove unnecessary words while preserving the original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayText } = fields;
      let prompt =
        "Please refine the following essay for clarity and conciseness:\n";
      prompt += "Essay Text: " + essayText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "listai",
    name: "ListAI",
    slug: "listai",
    shortDescription:
      "Generate nested subtopics effortlessly with ListAI, optimizing your content strategy at any depth for enhanced organization.",
    fields: [
      {
        type: "textArea",
        label: "Base Topic",
        field: "Base Topic",
        placeholder:
          "The chronological history of the cosmos starting from the big bang",
        required: true,
        optional: false,
        id: "basetopic",
      },
      {
        type: "textArea",
        label: "Iterative Depth",
        field: "Iterative Depth",
        placeholder: "10",
        required: false,
        optional: true,
        id: "iterativedepth",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating nested subtopics based on a given base topic. You will be provided with a base topic and an optional iterative depth, and you will generate a comprehensive list of subtopics.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { baseTopic, iterativeDepth } = fields;
      let prompt =
        "Generate a comprehensive list of nested subtopics based on the following:\n";
      prompt += "Base Topic: " + baseTopic + "\n";
      if (iterativeDepth) {
        prompt += "Iterative Depth: " + iterativeDepth + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "youtube-video-hook-script-generator",
    name: "YouTube Video Hook Script Generator",
    slug: "youtube-video-hook-script-generator",
    shortDescription:
      "Create engaging YouTube video hooks effortlessly with our script generator, boosting viewer retention and watch time.",
    fields: [
      {
        type: "textArea",
        label: "Video Title",
        field: "Video Title",
        placeholder: "How to Bake the Perfect Chocolate Chip Cookies",
        required: true,
        optional: false,
        id: "videotitle",
      },
      {
        type: "textArea",
        label: "Main Topic",
        field: "Main Topic",
        placeholder: "Baking chocolate chip cookies",
        required: false,
        optional: true,
        id: "maintopic",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Beginner bakers",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Social Media", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating engaging YouTube video hook scripts. You will be provided with a video title and additional context, and you will generate a compelling hook script to capture viewers' attention.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { VideoTitle, MainTopic, TargetAudience } = fields;
      let prompt =
        "Generate a hook script for a YouTube video based on the following:\n";
      prompt += "Video Title: " + VideoTitle + "\n";
      if (MainTopic) {
        prompt += "Main Topic: " + MainTopic + "\n";
      }
      if (TargetAudience) {
        prompt += "Target Audience: " + TargetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "podcast-script-generator",
    name: "Podcast Script Generator",
    slug: "podcast-script-generator",
    shortDescription:
      "Create engaging podcasts effortlessly with our script generator. Get tailored scripts based on your topic and audience!",
    fields: [
      {
        type: "textArea",
        label: "Podcast Topic",
        field: "Podcast Topic",
        placeholder: "AI advancements in the last decade",
        required: true,
        optional: false,
        id: "podcasttopic",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. AI in healthcare 2. AI in transportation 3. AI in education",
        required: false,
        optional: true,
        id: "keypoints",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Tech enthusiasts",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating podcast scripts. You will be provided with a topic, key points, and target audience to generate a detailed podcast script.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { podcastTopic, keyPoints, targetAudience } = fields;
      let prompt = "Create a podcast script based on the following:\n";
      prompt += "Podcast Topic: " + podcastTopic + "\n";
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + "\n";
      }
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "concept-brainstorming-assistant",
    name: "Concept Brainstorming Assistant",
    slug: "concept-brainstorming-assistant",
    shortDescription:
      "Streamline your creative process with our Concept Brainstorming Assistant, helping you develop and expand your ideas effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Initial Concept",
        field: "Initial Concept",
        placeholder:
          "A mobile app that helps people find local farmers markets...",
        required: true,
        optional: false,
        id: "initialconcept",
      },
      {
        type: "textArea",
        label: "Related Concepts",
        field: "Related Concepts",
        placeholder: "Sustainable living, local economies, healthy eating...",
        required: false,
        optional: true,
        id: "relatedconcepts",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative assistant specialized in brainstorming and developing concepts. You will be provided with an initial concept and related ideas, and your task is to help expand and refine these concepts.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { initialConcept, relatedConcepts } = fields;
      let prompt =
        "Assist in brainstorming and developing the following concept:\n";
      prompt += "Initial Concept: " + initialConcept + "\n";
      if (relatedConcepts) {
        prompt += "Related Concepts: " + relatedConcepts + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "explain-like-im-5-ai",
    name: "Explain Like I'm 5 AI",
    slug: "explain-like-im-5-ai",
    shortDescription:
      "Simplify complex concepts with Explain Like I'm 5 AI, making tough topics easy to understand for everyone.",
    fields: [
      {
        type: "textArea",
        label: "Complex Concept",
        field: "Complex Concept",
        placeholder: "Explain quantum physics",
        required: true,
        optional: false,
        id: "complexconcept",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in simplifying complex concepts. Your task is to take a complex idea and explain it in simple, easy-to-understand terms suitable for a five-year-old.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { ComplexConcept } = fields;
      let prompt =
        "Explain the following complex concept in simple terms that a five-year-old can understand:\n";
      prompt += "Complex Concept: " + ComplexConcept + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "profitgpt-business-advisor",
    name: "ProfitGPT Business Advisor",
    slug: "profitgpt-business-advisor",
    shortDescription:
      "Get expert business advice with ProfitGPT: from planning and market research to marketing strategies and model evaluation.",
    fields: [
      {
        type: "textArea",
        label: "Business Name",
        field: "Business Name",
        placeholder: "ABC Corporation",
        required: true,
        optional: false,
        id: "businessname",
      },
      {
        type: "textArea",
        label: "Business Description",
        field: "Business Description",
        placeholder: "We are a software company that...",
        required: false,
        optional: true,
        id: "businessdescription",
      },
      {
        type: "textArea",
        label: "Target Market",
        field: "Target Market",
        placeholder: "Small to medium-sized businesses",
        required: false,
        optional: true,
        id: "targetmarket",
      },
      {
        type: "textArea",
        label: "Current Marketing Strategies",
        field: "Current Marketing Strategies",
        placeholder: "Social media marketing, email marketing...",
        required: false,
        optional: true,
        id: "currentmarketingstrategies",
      },
      {
        type: "textArea",
        label: "Current Business Model",
        field: "Current Business Model",
        placeholder: "We operate on a SaaS model...",
        required: false,
        optional: true,
        id: "currentbusinessmodel",
      },
      {
        type: "textArea",
        label: "Business Goals",
        field: "Business Goals",
        placeholder: "Increase customer base by 30% in the next quarter...",
        required: false,
        optional: true,
        id: "businessgoals",
      },
    ],
    categories: ["Business", "Marketing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert business advisor. You will be provided with information about a business and you will generate comprehensive advice based on the details provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const {
        BusinessName,
        BusinessDescription,
        TargetMarket,
        CurrentMarketingStrategies,
        CurrentBusinessModel,
        BusinessGoals,
      } = fields;
      let prompt =
        "Provide comprehensive business advice based on the following information:\n";
      prompt += "Business Name: " + BusinessName + "\n";
      if (BusinessDescription) {
        prompt += "Business Description: " + BusinessDescription + "\n";
      }
      if (TargetMarket) {
        prompt += "Target Market: " + TargetMarket + "\n";
      }
      if (CurrentMarketingStrategies) {
        prompt +=
          "Current Marketing Strategies: " + CurrentMarketingStrategies + "\n";
      }
      if (CurrentBusinessModel) {
        prompt += "Current Business Model: " + CurrentBusinessModel + "\n";
      }
      if (BusinessGoals) {
        prompt += "Business Goals: " + BusinessGoals + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "one-line-summary-generator",
    name: "One-Line Summary Generator",
    slug: "one-line-summary-generator",
    shortDescription:
      "Effortlessly create concise one-line summaries of any text with our One-Line Summary Generator. Simplify your content today!",
    fields: [
      {
        type: "textArea",
        label: "Paragraph or Text",
        field: "Paragraph or Text",
        placeholder: "In the world of AI...",
        required: true,
        optional: false,
        id: "paragraphortext",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing text. You will be provided with a paragraph or text, and your task is to generate a concise one-line summary that captures the main idea.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { paragraphOrText } = fields;
      let prompt =
        "Generate a concise one-line summary based on the following text:\n";
      prompt += "Text: " + paragraphOrText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "humorous-destruction-story-generator",
    name: "Humorous Destruction Story Generator",
    slug: "humorous-destruction-story-generator",
    shortDescription:
      "Create hilarious tales with our Humorous Destruction Story Generator—watch characters hilariously wreak havoc with their unique skills!",
    fields: [
      {
        type: "textArea",
        label: "Character",
        field: "Character",
        placeholder: "A clumsy magician",
        required: true,
        optional: false,
        id: "character",
      },
      {
        type: "textArea",
        label: "Setting",
        field: "Setting",
        placeholder: "A high-end art gallery",
        required: false,
        optional: true,
        id: "setting",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative storyteller with a knack for humor. Your task is to generate a funny story about a character who, either intentionally or accidentally, destroys a setting using their unique talent.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Character, Setting } = fields;
      let prompt =
        "Generate a humorous destruction story based on the following:\n";
      prompt += "Character: " + Character + ".\n";
      if (Setting) {
        prompt += "Setting: " + Setting + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "nonprofit-grant-proposal-writer",
    name: "Nonprofit Grant Proposal Writer",
    slug: "nonprofit-grant-proposal-writer",
    shortDescription:
      "Create compelling grant proposals for nonprofits with our expert writer, tailored to your organization's needs and project specifics.",
    fields: [
      {
        type: "textArea",
        label: "Grant Information",
        field: "Grant Information",
        placeholder: "Grant name, requirements, deadline, etc.",
        required: true,
        optional: false,
        id: "grantinformation",
      },
      {
        type: "textArea",
        label: "Organization Background",
        field: "Organization Background",
        placeholder: "Mission statement, history, accomplishments, etc.",
        required: false,
        optional: true,
        id: "organizationbackground",
      },
      {
        type: "textArea",
        label: "Project/Proposal Details",
        field: "Project/Proposal Details",
        placeholder:
          "Project description, budget, timeline, expected outcomes, etc.",
        required: false,
        optional: true,
        id: "project/proposaldetails",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating nonprofit grant proposals. You will be provided with details about the grant, the organization, and the project, and you will generate a persuasive grant proposal based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const {
        grantInformation,
        organizationBackground,
        projectProposalDetails,
      } = fields;
      let prompt =
        "Generate a nonprofit grant proposal based on the following:\n";
      prompt += "Grant Information: " + grantInformation + "\n";
      if (organizationBackground) {
        prompt += "Organization Background: " + organizationBackground + "\n";
      }
      if (projectProposalDetails) {
        prompt += "Project/Proposal Details: " + projectProposalDetails + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "codaigpt-debugging-assistant",
    name: "CodaiGPT Debugging Assistant",
    slug: "codaigpt-debugging-assistant",
    shortDescription:
      "Enhance your coding with CodaiGPT Debugging Assistant: resolve issues, get best practice advice, and generate helpful code snippets.",
    fields: [
      {
        type: "textArea",
        label: "Programming Language",
        field: "Programming Language",
        placeholder: "Python",
        required: true,
        optional: false,
        id: "programminglanguage",
      },
      {
        type: "textArea",
        label: "Code Snippet",
        field: "Code Snippet",
        placeholder: "def hello_world():\n    print('Hello, world!')",
        required: false,
        optional: true,
        id: "codesnippet",
      },
      {
        type: "textArea",
        label: "Expected Behavior",
        field: "Expected Behavior",
        placeholder: "Prints 'Hello, world!'",
        required: false,
        optional: true,
        id: "expectedbehavior",
      },
      {
        type: "textArea",
        label: "Actual Behavior",
        field: "Actual Behavior",
        placeholder: "Prints 'Hello, wrld!'",
        required: false,
        optional: true,
        id: "actualbehavior",
      },
      {
        type: "textArea",
        label: "Error Message (if any)",
        field: "Error Message (if any)",
        placeholder: "SyntaxError: invalid syntax",
        required: false,
        optional: true,
        id: "errormessage(ifany)",
      },
    ],
    categories: ["Support", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert debugging assistant for developers. You will be provided with details about a code snippet, including the programming language, expected behavior, actual behavior, and any error messages. Your task is to assist in debugging the code and provide suggestions for resolving issues.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const {
        programmingLanguage,
        codeSnippet,
        expectedBehavior,
        actualBehavior,
        errorMessage,
      } = fields;
      let prompt = "Assist in debugging the following code snippet:\n";
      prompt += "Programming Language: " + programmingLanguage + "\n";
      prompt += "Code Snippet:\n" + codeSnippet + "\n";
      if (expectedBehavior) {
        prompt += "Expected Behavior: " + expectedBehavior + "\n";
      }
      if (actualBehavior) {
        prompt += "Actual Behavior: " + actualBehavior + "\n";
      }
      if (errorMessage) {
        prompt += "Error Message: " + errorMessage + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-section-evaluator",
    name: "Essay Section Evaluator",
    slug: "essay-section-evaluator",
    shortDescription:
      "Evaluate your essay sections for clarity, flow, and critical analysis with our comprehensive assessment tool for optimal results.",
    fields: [
      {
        type: "textArea",
        label: "Essay Section",
        field: "Essay Section",
        placeholder: "In the study of social sciences...",
        required: true,
        optional: false,
        id: "essaysection",
      },
      {
        type: "textArea",
        label: "Assessment Guidance",
        field: "Assessment Guidance",
        placeholder: "The essay should contain a clear argument...",
        required: false,
        optional: true,
        id: "assessmentguidance",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in evaluating essay sections. You will be provided with a section of an essay and assessment guidance, and you will provide feedback on flow, clarity, critical evaluation, in-text citations, and adherence to the point, evidence, explain, link paragraph structure.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essaySection, assessmentGuidance } = fields;
      let prompt =
        "Evaluate the following essay section based on the provided guidance:\n";
      prompt += "Essay Section: " + essaySection + "\n";
      if (assessmentGuidance) {
        prompt += "Assessment Guidance: " + assessmentGuidance + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "hilarious-chapter-generator",
    name: "Hilarious Chapter Generator",
    slug: "hilarious-chapter-generator",
    shortDescription:
      "Generate hilarious and creative chapters effortlessly with our fun chapter generator based on your unique parameters!",
    fields: [
      {
        type: "textArea",
        label: "Context",
        field: "Context",
        placeholder:
          "The characters are at a zoo and they have lost their map.",
        required: true,
        optional: false,
        id: "context",
      },
      {
        type: "textArea",
        label: "Characters",
        field: "Characters",
        placeholder:
          "John, a clumsy but well-meaning zookeeper, and Sarah, a quick-witted and adventurous visitor.",
        required: false,
        optional: true,
        id: "characters",
      },
      {
        type: "textArea",
        label: "Setting",
        field: "Setting",
        placeholder: "A large, chaotic zoo on a hot summer day.",
        required: false,
        optional: true,
        id: "setting",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative writer specializing in generating hilarious chapters. You will be provided with context, characters, and setting, and you will create a funny chapter based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Context, Characters, Setting } = fields;
      let prompt = "Generate a hilarious chapter based on the following:\n";
      prompt += "Context: " + Context + "\n";
      if (Characters) {
        prompt += "Characters: " + Characters + "\n";
      }
      if (Setting) {
        prompt += "Setting: " + Setting + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resolution-creator",
    name: "Resolution Creator",
    slug: "resolution-creator",
    shortDescription:
      "Generate formal resolutions effortlessly with Resolution Creator by simply providing the necessary information.",
    fields: [
      {
        type: "textArea",
        label: "Resolution Reason",
        field: "Resolution Reason",
        placeholder: "Reason for proposing the resolution...",
        required: true,
        optional: false,
        id: "resolutionreason",
      },
      {
        type: "textArea",
        label: "Proposed Actions",
        field: "Proposed Actions",
        placeholder: "Actions to be taken...",
        required: false,
        optional: true,
        id: "proposedactions",
      },
      {
        type: "textArea",
        label: "Desired Outcomes",
        field: "Desired Outcomes",
        placeholder: "Expected results from the actions...",
        required: false,
        optional: true,
        id: "desiredoutcomes",
      },
    ],
    categories: ["Legal", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating formal resolutions. You will be provided with specific information regarding the resolution, and you will generate a well-structured resolution based on the details provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { resolutionReason, proposedActions, desiredOutcomes } = fields;
      let prompt = "Generate a formal resolution based on the following:\n";
      prompt += "Resolution Reason: " + resolutionReason + "\n";
      if (proposedActions) {
        prompt += "Proposed Actions: " + proposedActions + "\n";
      }
      if (desiredOutcomes) {
        prompt += "Desired Outcomes: " + desiredOutcomes + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "job-application-email-generator",
    name: "Job Application Email Generator",
    slug: "job-application-email-generator",
    shortDescription:
      "Create personalized job application emails tailored to your skills and job descriptions with our easy-to-use generator.",
    fields: [
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder:
          "We are seeking a software engineer with 5+ years of experience...",
        required: true,
        optional: false,
        id: "jobdescription",
      },
      {
        type: "textArea",
        label: "Job Field or Title",
        field: "Job Field or Title",
        placeholder: "Software Engineering",
        required: false,
        optional: true,
        id: "jobfieldortitle",
      },
      {
        type: "textArea",
        label: "Applicant's Skills and Experiences",
        field: "Applicant's Skills and Experiences",
        placeholder: "5 years of experience in Python, Java, and C++...",
        required: false,
        optional: true,
        id: "applicantsskillsandexperiences",
      },
    ],
    categories: ["Email", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating job application emails. You will be provided with a job description, the job field or title, and the applicant's skills and experiences to create a personalized email.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const {
        jobDescription,
        jobFieldOrTitle,
        applicantsSkillsAndExperiences,
      } = fields;
      let prompt = "Generate a job application email based on the following:\n";
      prompt += "Job Description: " + jobDescription + "\n";
      if (jobFieldOrTitle) {
        prompt += "Job Field or Title: " + jobFieldOrTitle + "\n";
      }
      if (applicantsSkillsAndExperiences) {
        prompt +=
          "Applicant's Skills and Experiences: " +
          applicantsSkillsAndExperiences +
          "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-chat-companion",
    name: "AI Chat Companion",
    slug: "ai-chat-companion",
    shortDescription:
      "Engage in captivating conversations on any topic with our AI Chat Companion. Discover endless chat possibilities today!",
    fields: [
      {
        type: "textArea",
        label: "Chat Topic",
        field: "Chat Topic",
        placeholder: "Space Exploration",
        required: true,
        optional: false,
        id: "chattopic",
      },
    ],
    categories: ["Social Media", "Support", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI chat companion designed to engage users in conversations on any topic they choose. You will respond to user inputs with informative and engaging replies.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { chatTopic } = fields;
      let prompt = "Engage in a chat about the following topic:\n";
      prompt += "Chat Topic: " + chatTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "engaging-paragraph-generator",
    name: "Engaging Paragraph Generator",
    slug: "engaging-paragraph-generator",
    shortDescription:
      "Create captivating paragraphs effortlessly with our generator, featuring topic sentences, supporting details, and strong conclusions.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "The importance of renewable energy",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging paragraphs. You will be provided with a topic and you will create a well-structured paragraph that includes a topic sentence, supporting details, statistics, facts, evidence, and a conclusion sentence.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields;
      let prompt = "Generate an engaging paragraph based on the following:\n";
      prompt += "Topic: " + Topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "affiliate-partner-finder",
    name: "Affiliate Partner Finder",
    slug: "affiliate-partner-finder",
    shortDescription:
      "Find ideal affiliate partners for your product or service effortlessly with our Affiliate Partner Finder tool.",
    fields: [
      {
        type: "textArea",
        label: "Product or Service",
        field: "Product or Service",
        placeholder: "Organic skincare products",
        required: true,
        optional: false,
        id: "productorservice",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Women aged 20-35 interested in natural beauty products",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in finding potential affiliate partners. You will be provided with information about a product or service and the target audience, and you will generate a list of suitable affiliate partners based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { productOrService, targetAudience } = fields;
      let prompt =
        "Generate a list of potential affiliate partners based on the following:\n";
      prompt += "Product or Service: " + productOrService + ".\n";
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "theatre-rehearsal-planner",
    name: "Theatre Rehearsal Planner",
    slug: "theatre-rehearsal-planner",
    shortDescription:
      "Create a structured theatre rehearsal plan with warm-ups, voice exercises, and improv games to enhance your performance skills.",
    fields: [
      {
        type: "textArea",
        label: "Main Topics",
        field: "Main Topics",
        placeholder:
          "Character development, Scene transitions, Emotional expression",
        required: true,
        optional: false,
        id: "maintopics",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in planning theatre rehearsals. You will be provided with main topics and you will generate a structured plan for a theatre rehearsal, including warm-up, body and voice exercises, and improv games based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { mainTopics } = fields;
      let prompt =
        "Generate a structured plan for a theatre rehearsal based on the following:\n";
      prompt += "Main topics: " + mainTopics + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writing-feedback",
    name: "AI Writing Feedback",
    slug: "ai-writing-feedback",
    shortDescription:
      "Get constructive feedback on your writing with AI Writing Feedback to enhance clarity, coherence, and style effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Text",
        field: "Text",
        placeholder: "Enter the text you want feedback on here.",
        required: true,
        optional: false,
        id: "text",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert writing assistant that provides constructive feedback on written text. You will analyze the provided text and offer suggestions to improve clarity, coherence, and style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Text } = fields;
      let prompt =
        "Please provide constructive feedback on the following text:\n";
      prompt += Text + "\n";
      prompt += "Focus on improving clarity, coherence, and style.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "historical-event-narrative-creator",
    name: "Historical Event Narrative Creator",
    slug: "historical-event-narrative-creator",
    shortDescription:
      "Create detailed narratives of historical events effortlessly with our Historical Event Narrative Creator based on your queries.",
    fields: [
      {
        type: "textArea",
        label: "Historical Event",
        field: "Historical Event",
        placeholder: "Battle of Waterloo",
        required: true,
        optional: false,
        id: "historicalevent",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating detailed narratives of historical events. You will be provided with a specific historical event and you will generate a comprehensive narrative based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { historicalEvent } = fields.historicalEvent;
      let prompt =
        "Generate a detailed narrative based on the following historical event:\n";
      prompt += "Historical Event: " + historicalEvent + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "api-documentation-improver",
    name: "API Documentation Improver",
    slug: "api-documentation-improver",
    shortDescription:
      "Enhance your API documentation with our tool that analyzes and suggests improvements for clarity, conciseness, and readability.",
    fields: [
      {
        type: "textArea",
        label: "API Documentation Text",
        field: "API Documentation Text",
        placeholder: "API CENTER Outgoing & Incoming data...",
        required: true,
        optional: false,
        id: "apidocumentationtext",
      },
      {
        type: "textArea",
        label: "Table of Improvements",
        field: "Table of Improvements",
        placeholder: "Suggestion, No, Applied (Y/N)",
        required: false,
        optional: true,
        id: "tableofimprovements",
      },
    ],
    categories: ["Writing", "Support", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in improving API documentation. You will be provided with the existing API documentation text and will suggest improvements for better clarity, conciseness, and readability.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { apiDocumentationText, tableOfImprovements } = fields;
      let prompt =
        "Analyze the following API documentation and suggest improvements:\n";
      prompt += "API Documentation Text: " + apiDocumentationText + "\n";
      if (tableOfImprovements) {
        prompt += "Current Suggestions: " + tableOfImprovements + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "writing-style-mimic",
    name: "Writing Style Mimic",
    slug: "writing-style-mimic",
    shortDescription:
      "Generate text that perfectly mimics your unique writing style and tone with our advanced Writing Style Mimic tool.",
    fields: [
      {
        type: "textArea",
        label: "User's Writing Sample",
        field: "User's Writing Sample",
        placeholder: "Enter a sample of your writing here.",
        required: true,
        optional: false,
        id: "userswritingsample",
      },
      {
        type: "textArea",
        label: "Text Topic",
        field: "Text Topic",
        placeholder: "What should the generated text be about?",
        required: false,
        optional: true,
        id: "texttopic",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in mimicking writing styles. You will be provided with a sample of the user's writing and a topic, and you will generate text that closely resembles the user's unique writing style and tone.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userWritingSample, textTopic } = fields;
      let prompt =
        "Generate text that mimics the user's writing style based on the following:\n";
      prompt += "User's Writing Sample: " + userWritingSample + "\n";
      if (textTopic) {
        prompt += "Text Topic: " + textTopic + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "perspective-diversifier",
    name: "Perspective Diversifier",
    slug: "perspective-diversifier",
    shortDescription:
      "Generate diverse perspectives with our Perspective Diversifier, providing unique responses for any text to enrich your insights.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "In today's globalized world, it is crucial to...",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating diverse perspectives. You will be provided with an original text, and your task is to generate multiple unique responses, each presenting a different point of view.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt =
        "Generate multiple unique responses to the following text:\n";
      prompt += "Original Text: " + originalText + "\n";
      prompt += "Each response should present a different point of view.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "cover-letter-ai",
    name: "Cover Letter AI",
    slug: "cover-letter-ai",
    shortDescription:
      "Generate a professional cover letter effortlessly with Cover Letter AI. Tailored to your personal details and purpose for maximum impact.",
    fields: [
      {
        type: "textArea",
        label: "Purpose",
        field: "Purpose",
        placeholder: "Applying for a software engineer position at Google",
        required: true,
        optional: false,
        id: "purpose",
      },
      {
        type: "textArea",
        label: "Details",
        field: "Details",
        placeholder:
          "My name is John Doe. I have a Bachelor's degree in Computer Science and 5 years of experience in software development...",
        required: false,
        optional: true,
        id: "details",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating cover letters. You will be provided with the user's purpose and personal details, and you will create a high-quality, professional cover letter based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Purpose, Details } = fields;
      let prompt = "Generate a cover letter based on the following:\n";
      prompt += "Purpose: " + Purpose + "\n";
      if (Details) {
        prompt += "Details: " + Details + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "real-estate-copywriting",
    name: "Real Estate Copywriting",
    slug: "real-estate-copywriting",
    shortDescription:
      "Generate engaging real estate content effortlessly! From property descriptions to client communication, we make it natural and approachable.",
    fields: [
      {
        type: "textArea",
        label: "Writing Task and Details",
        field: "Writing Task and Details",
        placeholder:
          "Write a description for a 3-bedroom, 2-bathroom house with a large backyard and modern kitchen...",
        required: true,
        optional: false,
        id: "writingtaskanddetails",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in real estate copywriting. You will be provided with details about a property or writing task, and you will generate engaging and natural content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingTaskAndDetails } = fields;
      let prompt =
        "Generate real estate content based on the following details:\n";
      prompt += "Writing task: " + writingTaskAndDetails + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-wedding-card-generator",
    name: "AI Wedding Card Generator",
    slug: "ai-wedding-card-generator",
    shortDescription:
      "Create personalized wedding card messages effortlessly with our AI Wedding Card Generator. Perfect for every couple's special day!",
    fields: [
      {
        type: "textArea",
        label: "Couple's Information",
        field: "Couple's Information",
        placeholder:
          "John and Jane met in college, they both love hiking and have a dog named Spot...",
        required: true,
        optional: false,
        id: "couplesinformation",
      },
    ],
    categories: ["Writing", "Social Media", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized wedding card messages. You will be provided with information about the couple, and you will create a heartfelt message for their wedding card.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { coupleInformation } = fields;
      let prompt = "Generate a wedding card message based on the following:\n";
      prompt += "Couple's Information: " + coupleInformation + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "immersive-world-builder",
    name: "Immersive World Builder",
    slug: "immersive-world-builder",
    shortDescription:
      "Create captivating stories with our Immersive World Builder, designed for curating engaging worlds and scenarios.",
    fields: [
      {
        type: "textArea",
        label: "Initial Story Draft",
        field: "Initial Story Draft",
        placeholder: "In a world where time stood still...",
        required: true,
        optional: false,
        id: "initialstorydraft",
      },
      {
        type: "textArea",
        label: "Desired Setting",
        field: "Desired Setting",
        placeholder: "Post-apocalyptic earth",
        required: false,
        optional: true,
        id: "desiredsetting",
      },
      {
        type: "textArea",
        label: "Desired Scenario Details",
        field: "Desired Scenario Details",
        placeholder: "Characters stumble upon an untouched city",
        required: false,
        optional: true,
        id: "desiredscenariodetails",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating immersive worlds for storytelling. You will be provided with initial story drafts and additional details to help curate engaging scenarios.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { initialStoryDraft, desiredSetting, desiredScenarioDetails } =
        fields;
      let prompt = "Create an immersive world based on the following:\n";
      prompt += "Initial Story Draft: " + initialStoryDraft + "\n";
      if (desiredSetting) {
        prompt += "Desired Setting: " + desiredSetting + "\n";
      }
      if (desiredScenarioDetails) {
        prompt += "Scenario Details: " + desiredScenarioDetails + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "disney-channel-original-movie-generator",
    name: "Disney Channel Original Movie Generator",
    slug: "disney-channel-original-movie-generator",
    shortDescription:
      "Create your own Disney Channel Original Movie with our generator! Get unique titles, plots, and characters for endless fun.",
    fields: [
      {
        type: "textArea",
        label: "Random Seed",
        field: "Random Seed",
        placeholder: "Enter any word or phrase to inspire the movie concept",
        required: true,
        optional: false,
        id: "randomseed",
      },
    ],
    categories: ["Writing", "Education", "Entertainment"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative writer specializing in Disney Channel Original Movies. You will be provided with a random seed and will generate a unique concept for a movie, including its title, plot, and main characters.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { randomSeed } = fields;
      let prompt =
        "Generate a unique concept for a Disney Channel Original Movie based on the following:\n";
      prompt += "Random Seed: " + randomSeed + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "nursing-school-ai-assistant",
    name: "Nursing School AI Assistant",
    slug: "nursing-school-ai-assistant",
    shortDescription:
      "Revolutionize your nursing studies with our AI Assistant, offering resources and support for mastering key concepts and enhancing learning.",
    fields: [
      {
        type: "textArea",
        label: "Nursing Topic or Question",
        field: "Nursing Topic or Question",
        placeholder: "Explain the circulatory system",
        required: true,
        optional: false,
        id: "nursingtopicorquestion",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in nursing education and assistance. You will be provided with a nursing topic or question, and you will generate a comprehensive explanation or resource based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { nursingTopicOrQuestion } = fields;
      let prompt =
        "Provide a detailed explanation or resources for the following nursing topic or question:\n";
      prompt += "Nursing Topic or Question: " + nursingTopicOrQuestion + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "email-subject-line-creator",
    name: "Email Subject Line Creator",
    slug: "email-subject-line-creator",
    shortDescription:
      "Create captivating email subject lines effortlessly with our generator, ensuring your messages get opened and read!",
    fields: [
      {
        type: "textArea",
        label: "Email Content",
        field: "Email Content",
        placeholder: "Dear John, I am reaching out to...",
        required: true,
        optional: false,
        id: "emailcontent",
      },
      {
        type: "textArea",
        label: "Email Tone",
        field: "Email Tone",
        placeholder: "Formal, Informal, Friendly, Professional",
        required: false,
        optional: true,
        id: "emailtone",
      },
      {
        type: "textArea",
        label: "Email Purpose",
        field: "Email Purpose",
        placeholder: "Schedule a meeting, Sell a product, Share an update",
        required: false,
        optional: true,
        id: "emailpurpose",
      },
    ],
    categories: ["Email", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating email subject lines. You will be provided with the content, tone, and purpose of the email, and you will generate an engaging and relevant subject line based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { emailContent, emailTone, emailPurpose } = fields;
      let prompt =
        "Generate an engaging email subject line based on the following:\n";
      prompt += "Email Content: " + emailContent + "\n";
      if (emailTone) {
        prompt += "Tone: " + emailTone + "\n";
      }
      if (emailPurpose) {
        prompt += "Purpose: " + emailPurpose + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "call-to-action",
    name: "Call To Action",
    slug: "call-to-action",
    shortDescription:
      "Craft persuasive calls-to-action that convert visitors into customers and boost your overall marketing effectiveness.",
    fields: [
      {
        type: "textArea",
        label: "Desired Action",
        field: "Desired Action",
        placeholder: "Sign up for writingtools.ai",
        required: true,
        optional: false,
        id: "desiredaction",
      },
      {
        type: "textArea",
        label: "Benefits",
        field: "Benefits",
        placeholder:
          "Improve your writing with AI, get access to premium features, save time, improve productivity",
        required: false,
        optional: true,
        id: "benefits",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Small business owners",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Marketing", "Sales", "Website"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating compelling calls-to-action. You will be provided with details about the desired action, benefits, and target audience to generate a persuasive call-to-action.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { desiredAction, benefits, targetAudience } = fields;
      let prompt = "Create a call-to-action based on the following:\n";
      prompt += "Desired Action: " + desiredAction + "\n";
      if (benefits) {
        prompt += "Benefits: " + benefits + "\n";
      }
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "show-not-tell-transformer",
    name: "Show-Not-Tell Transformer",
    slug: "show-not-tell-transformer",
    shortDescription:
      "Transform your writing with the Show-Not-Tell Transformer, replacing bland statements with vivid, engaging details.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "He was very tired.",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in transforming descriptive writing. Your task is to replace 'telling' statements with vivid, 'showing' details based on the provided original text.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt =
        "Transform the following text by replacing 'telling' statements with vivid, 'showing' details:\n";
      prompt += "Original Text: " + originalText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paraphrase-ai",
    name: "Paraphrase AI",
    slug: "paraphrase-ai",
    shortDescription:
      "Transform your text effortlessly with Paraphrase AI, generating high-quality paraphrases from provided content or links.",
    fields: [
      {
        type: "textArea",
        label: "Text or Link",
        field: "Text or Link",
        placeholder: "Paste your text or link here",
        required: true,
        optional: false,
        id: "textorlink",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in paraphrasing text. You will be provided with a piece of text or a link, and your task is to generate a high-quality paraphrased version of the content.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textOrLink } = fields.TextOrLink;
      let prompt = "Please paraphrase the following content:\n";
      prompt += "Content: " + textOrLink + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "story-generator",
    name: "Story Generator",
    slug: "story-generator",
    shortDescription:
      "Create captivating stories effortlessly with our Story Generator, tailored to your unique topics or prompts.",
    fields: [
      {
        type: "textArea",
        label: "Story Topic or Prompt",
        field: "Story Topic or Prompt",
        placeholder: "A lost treasure in an ancient city",
        required: true,
        optional: false,
        id: "storytopicorprompt",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a skilled storyteller. You will be provided with a topic or prompt, and your task is to generate a captivating story based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { storyTopicOrPrompt } = fields;
      let prompt = "Generate a captivating story based on the following:\n";
      prompt += "Story Topic or Prompt: " + storyTopicOrPrompt + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "animation-screenplay-essentials-suite",
    name: "Animation Screenplay Essentials Suite",
    slug: "animation-screenplay-essentials-suite",
    shortDescription:
      "Create compelling animation screenplays with our essential suite, featuring key elements, structure guides, and industry tips.",
    fields: [
      {
        type: "textArea",
        label: "Plot Summary",
        field: "Plot Summary",
        placeholder: "A young dragon must overcome his fear of fire...",
        required: true,
        optional: false,
        id: "plotsummary",
      },
      {
        type: "textArea",
        label: "Genre",
        field: "Genre",
        placeholder: "Fantasy",
        required: false,
        optional: true,
        id: "genre",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Children aged 6-12",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating compelling screenplays for animation studios. You will be provided with key elements and structure guides to help generate a screenplay tailored for animation.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { plotSummary, genre, targetAudience } = fields;
      let prompt = "Generate a screenplay outline based on the following:\n";
      prompt += "Plot Summary: " + plotSummary + "\n";
      if (genre) {
        prompt += "Genre: " + genre + "\n";
      }
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "copywriting-ai",
    name: "Copywriting AI",
    slug: "copywriting-ai",
    shortDescription:
      "Revolutionize your writing with Copywriting AI. Effortlessly complete any copywriting task based on your specific needs.",
    fields: [
      {
        type: "textArea",
        label: "Copywriting Task",
        field: "Copywriting Task",
        placeholder:
          "Write a product description for a new line of eco-friendly backpacks targeting millennials.",
        required: true,
        optional: false,
        id: "copywritingtask",
      },
    ],
    categories: ["Marketing", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert copywriter. You will be provided with a specific copywriting task, and you will generate high-quality copy based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { copywritingTask } = fields;
      let prompt = "Generate copy based on the following task:\n";
      prompt += "Copywriting Task: " + copywritingTask + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "content-based-qa-generator",
    name: "Content-based Q&A Generator",
    slug: "content-based-qa-generator",
    shortDescription:
      "Generate relevant college-like questions and answers effortlessly with our content-based Q&A generator for enhanced learning.",
    fields: [
      {
        type: "textArea",
        label: "Content",
        field: "Content",
        placeholder:
          "Insert the content you want to create questions and answers from here.",
        required: true,
        optional: false,
        id: "content",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating college-level questions and answers based on provided content. You will receive a piece of content and will create relevant questions and their corresponding answers.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Content } = fields;
      let prompt =
        "Generate relevant college-level questions and answers based on the following content:\n";
      prompt += Content + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "manuscript-master",
    name: "Manuscript Master",
    slug: "manuscript-master",
    shortDescription:
      "Streamline your writing with Manuscript Master, your ultimate tool for creating structured and organized manuscripts effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Title",
        field: "Title",
        placeholder: "The Impact of AI on Climate Change",
        required: true,
        optional: false,
        id: "title",
      },
      {
        type: "textArea",
        label: "Abstract",
        field: "Abstract",
        placeholder:
          "This study explores the role of AI in mitigating climate change...",
        required: false,
        optional: true,
        id: "abstract",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. The role of AI in climate prediction... 2. Case study of AI in renewable energy...",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in assisting users to write structured and well-organized manuscripts. You will be provided with a title, abstract, and key points, and you will help generate a coherent manuscript based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Title, Abstract, KeyPoints } = fields;
      let prompt = "Generate a structured manuscript based on the following:\n";
      prompt += "Title: " + Title + "\n";
      if (Abstract) {
        prompt += "Abstract: " + Abstract + "\n";
      }
      if (KeyPoints) {
        prompt += "Key Points: " + KeyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paper-highlight-extractor",
    name: "Paper Highlight Extractor",
    slug: "paper-highlight-extractor",
    shortDescription:
      "Extract key points and highlights effortlessly with our Paper Highlight Extractor for academic and research papers.",
    fields: [
      {
        type: "textArea",
        label: "Paper Text",
        field: "Paper Text",
        placeholder: "Introduction: The purpose of this study is to...",
        required: true,
        optional: false,
        id: "papertext",
      },
    ],
    categories: ["Education", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in extracting and summarizing key points from academic or research papers. You will be provided with the text of a paper, and your task is to identify and summarize the main highlights.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { paperText } = fields.PaperText;
      let prompt =
        "Extract and summarize the key points from the following paper text:\n";
      prompt += paperText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "writing-and-answer-completion-assistant",
    name: "Writing and Answer Completion Assistant",
    slug: "writing-and-answer-completion-assistant",
    shortDescription:
      "Enhance your writing with our AI-powered assistant, designed to help you complete tasks and form answers based on your instructions.",
    fields: [
      {
        type: "textArea",
        label: "Initial Writing or Answer",
        field: "Initial Writing or Answer",
        placeholder: "The French Revolution was a period of radical...",
        required: true,
        optional: false,
        id: "initialwritingoranswer",
      },
      {
        type: "textArea",
        label: "Instructions",
        field: "Instructions",
        placeholder:
          "Please elaborate on the causes and impacts of the French Revolution.",
        required: false,
        optional: true,
        id: "instructions",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI writing assistant specialized in completing writing tasks and answering questions. You will be provided with an initial piece of writing or a question along with specific instructions, and you will generate a comprehensive response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { initialWritingOrAnswer, instructions } = fields;
      let prompt =
        "Complete the following writing or answer based on the provided instructions:\n";
      prompt += "Initial Writing or Answer: " + initialWritingOrAnswer + "\n";
      if (instructions) {
        prompt += "Instructions: " + instructions + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "product-description-ad-copy-generator",
    name: "Product Description Ad Copy Generator",
    slug: "product-description-ad-copy-generator",
    shortDescription:
      "Generate engaging ad copy with our Product Description Ad Copy Generator, using action verbs and a feature/benefit approach.",
    fields: [
      {
        type: "textArea",
        label: "Product Description and Features",
        field: "Product Description and Features",
        placeholder: "Waterproof flashlight, Long battery life, Compact size",
        required: true,
        optional: false,
        id: "productdescriptionandfeatures",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Outdoor enthusiasts",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Marketing", "Sales", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating product descriptions for ad copy. You will be provided with details about a product and its features, and you will create an engaging description that highlights the benefits to the target audience.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { productDescriptionAndFeatures, targetAudience } = fields;
      let prompt =
        "Generate a product description for ad copy based on the following:\n";
      prompt +=
        "Product Description and Features: " +
        productDescriptionAndFeatures +
        "\n";
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "serious-tone-setter",
    name: "Serious Tone Setter",
    slug: "serious-tone-setter",
    shortDescription:
      "Generate impactful text with a serious tone for your writing projects, ensuring your message is conveyed effectively and professionally.",
    fields: [
      {
        type: "textArea",
        label: "Context",
        field: "Context",
        placeholder: "The scene takes place in a courtroom...",
        required: true,
        optional: false,
        id: "context",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating text that sets a serious tone for various types of writing. You will be provided with context and will generate text that reflects a serious tone based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { context } = fields.Context;
      let prompt =
        "Generate text that sets a serious tone based on the following context:\n";
      prompt += "Context: " + context + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-speech-generator",
    name: "AI Speech Generator",
    slug: "ai-speech-generator",
    shortDescription:
      "Generate persuasive speeches tailored to your audience and purpose with our AI Speech Generator for impactful communication.",
    fields: [
      {
        type: "textArea",
        label: "Speech Topic",
        field: "Speech Topic",
        placeholder: "The Importance of Climate Change",
        required: true,
        optional: false,
        id: "speechtopic",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "High school students",
        required: false,
        optional: true,
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Purpose of Speech",
        field: "Purpose of Speech",
        placeholder:
          "To persuade the audience to take action on climate change",
        required: false,
        optional: true,
        id: "purposeofspeech",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating persuasive and engaging speeches. You will be provided with a topic, target audience, and purpose of the speech, and you will create a compelling speech based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { SpeechTopic, TargetAudience, PurposeOfSpeech } = fields;
      let prompt = "Generate a speech based on the following:\n";
      prompt += "The speech topic is: " + SpeechTopic + ".\n";
      if (TargetAudience) {
        prompt += "Target audience: " + TargetAudience + ".\n";
      }
      if (PurposeOfSpeech) {
        prompt += "Purpose of the speech: " + PurposeOfSpeech + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "youtube-short-intro-maker",
    name: "YouTube Short Intro Maker",
    slug: "youtube-short-intro-maker",
    shortDescription:
      "Create captivating YouTube Short intros that engage viewers, spark interest, and keep them watching with our easy-to-use intro maker.",
    fields: [
      {
        type: "textArea",
        label: "Video Topic",
        field: "Video Topic",
        placeholder: "How to bake a chocolate cake",
        required: true,
        optional: false,
        id: "videotopic",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Young aspiring bakers",
        required: false,
        optional: true,
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Video Length",
        field: "Video Length",
        placeholder: "1 minute",
        required: false,
        optional: true,
        id: "videolength",
      },
    ],
    categories: ["Social Media", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating engaging YouTube Short introductions. You will be provided with details about the video topic, target audience, and video length, and you will generate a captivating introduction that hooks viewers.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { videoTopic, targetAudience, videoLength } = fields;
      let prompt =
        "Generate a YouTube Short introduction based on the following:\n";
      prompt += "Video Topic: " + videoTopic + "\n";
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + "\n";
      }
      if (videoLength) {
        prompt += "Video Length: " + videoLength + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "technical-approach-response-generator",
    name: "Technical Approach Response Generator",
    slug: "technical-approach-response-generator",
    shortDescription:
      "Generate detailed technical approach responses for your Statement of Work (SOW) with our comprehensive tool.",
    fields: [
      {
        type: "textArea",
        label: "Statement of Work",
        field: "Statement of Work",
        placeholder: "The client requires a new CRM system to...",
        required: true,
        optional: false,
        id: "statementofwork",
      },
    ],
    categories: ["Business", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating technical approach responses. You will be provided with a Statement of Work (SOW) and you will generate a comprehensive and detailed technical approach response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { statementOfWork } = fields;
      let prompt =
        "Generate a technical approach response based on the following:\n";
      prompt += "Statement of Work: " + statementOfWork + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "youtube-tag-creator",
    name: "YouTube Tag Creator",
    slug: "youtube-tag-creator",
    shortDescription:
      "Generate SEO-friendly YouTube tags from your video description to boost visibility and drive more traffic effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Video Description",
        field: "Video Description",
        placeholder:
          "In this video, we explore the beautiful landscapes of New Zealand...",
        required: true,
        optional: false,
        id: "videodescription",
      },
    ],
    categories: ["Social Media", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating SEO-friendly tags for YouTube videos. You will be provided with a video description, and you will generate relevant tags to enhance the video's visibility and drive more traffic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { videoDescription } = fields.videoDescription;
      let prompt =
        "Generate SEO-friendly tags based on the following video description:\n";
      prompt += "Video Description: " + videoDescription + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "seo-site-description-composer",
    name: "SEO Site Description Composer",
    slug: "seo-site-description-composer",
    shortDescription:
      "Generate SEO-optimized site descriptions effortlessly with our Composer tool, enhancing your website's visibility and reach.",
    fields: [
      {
        type: "textArea",
        label: "Website Content",
        field: "Website Content",
        placeholder:
          "Our website offers a wide range of eco-friendly products...",
        required: true,
        optional: false,
        id: "websitecontent",
      },
      {
        type: "textArea",
        label: "Website Purpose",
        field: "Website Purpose",
        placeholder: "To sell eco-friendly products online",
        required: false,
        optional: true,
        id: "websitepurpose",
      },
      {
        type: "textArea",
        label: "Main Keywords",
        field: "Main Keywords",
        placeholder: "eco-friendly, sustainable, online shopping",
        required: false,
        optional: true,
        id: "mainkeywords",
      },
    ],
    categories: ["Website", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating SEO-optimized site descriptions. You will be provided with information about a website's content, purpose, and keywords, and you will create a compelling description that enhances its visibility on search engines.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { websiteContent, websitePurpose, mainKeywords } = fields;
      let prompt =
        "Generate an SEO-optimized site description based on the following:\n";
      prompt += "Website Content: " + websiteContent + "\n";
      if (websitePurpose) {
        prompt += "Website Purpose: " + websitePurpose + "\n";
      }
      if (mainKeywords) {
        prompt += "Main Keywords: " + mainKeywords + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "creative-problem-solver-ai",
    name: "Creative Problem Solver AI",
    slug: "creative-problem-solver-ai",
    shortDescription:
      "Unlock innovative solutions with our Creative Problem Solver AI, generating ideas tailored to your specific challenges and situations.",
    fields: [
      {
        type: "textArea",
        label: "Problem Description",
        field: "Problem Description",
        placeholder: "We need to increase our product visibility...",
        required: true,
        optional: false,
        id: "problemdescription",
      },
      {
        type: "textArea",
        label: "Constraints",
        field: "Constraints",
        placeholder: "Limited budget, small team...",
        required: false,
        optional: true,
        id: "constraints",
      },
    ],
    categories: ["Business", "Creative Problem Solver AI", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creative problem solving. You will be provided with a description of a problem and any constraints, and you will generate innovative solutions or ideas based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { problemDescription, constraints } = fields;
      let prompt = "Generate creative solutions for the following problem:\n";
      prompt += "Problem Description: " + problemDescription + "\n";
      if (constraints) {
        prompt += "Constraints: " + constraints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "job-inquiry-email-generator",
    name: "Job Inquiry Email Generator",
    slug: "job-inquiry-email-generator",
    shortDescription:
      "Create personalized job inquiry emails effortlessly with our generator, tailored to your skills and the job you're eyeing.",
    fields: [
      {
        type: "textArea",
        label: "Job Title/Details",
        field: "Job Title/Details",
        placeholder:
          "Software Engineer at Google, responsible for developing web applications...",
        required: true,
        optional: false,
        id: "jobtitle/details",
      },
      {
        type: "textArea",
        label: "Relevant Skills/Experiences",
        field: "Relevant Skills/Experiences",
        placeholder:
          "5 years of experience in web development, proficient in JavaScript, Python...",
        required: false,
        optional: true,
        id: "relevantskills/experiences",
      },
      {
        type: "textArea",
        label: "Interest in Job/Company",
        field: "Interest in Job/Company",
        placeholder:
          "I have always admired Google's innovative culture and would love to contribute to...",
        required: false,
        optional: true,
        id: "interestinjob/company",
      },
    ],
    categories: ["Email", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating job inquiry emails. You will be provided with details about the job title, relevant skills, and interest in the job/company, and you will create a personalized email based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const {
        jobTitleDetails,
        relevantSkillsExperiences,
        interestInJobCompany,
      } = fields;
      let prompt = "Generate a job inquiry email based on the following:\n";
      prompt += "Job Title/Details: " + jobTitleDetails + "\n";
      if (relevantSkillsExperiences) {
        prompt +=
          "Relevant Skills/Experiences: " + relevantSkillsExperiences + "\n";
      }
      if (interestInJobCompany) {
        prompt += "Interest in Job/Company: " + interestInJobCompany + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "email-subject-line-generator",
    name: "Email Subject Line Generator",
    slug: "email-subject-line-generator",
    shortDescription:
      "Create attention-grabbing email subject lines effortlessly with our generator, tailored to your content and email goals.",
    fields: [
      {
        type: "textArea",
        label: "Email Body",
        field: "Email Body",
        placeholder: "Hi Sarah, I wanted to connect about...",
        required: true,
        optional: false,
        id: "emailbody",
      },
      {
        type: "textArea",
        label: "Email Goal",
        field: "Email Goal",
        placeholder: "Schedule a call",
        required: false,
        optional: true,
        id: "emailgoal",
      },
    ],
    categories: ["Email", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating catchy email subject lines. You will be provided with the email body and the goal of the email, and you will create a compelling subject line that captures the essence of the message.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { emailBody, emailGoal } = fields;
      let prompt =
        "Generate a catchy email subject line based on the following:\n";
      prompt += "Email Body: " + emailBody + "\n";
      if (emailGoal) {
        prompt += "Email Goal: " + emailGoal + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "interactive-qa-builder",
    name: "Interactive Q&A Builder",
    slug: "interactive-qa-builder",
    shortDescription:
      "Create engaging Q&A pairs effortlessly with our Interactive Q&A Builder. Transform information into dynamic question and answer sets!",
    fields: [
      {
        type: "textArea",
        label: "Topic, Information, or Purpose",
        field: "Topic, Information, or Purpose",
        placeholder: "Enter a topic or paste information here",
        required: true,
        optional: false,
        id: "topic,information,orpurpose",
      },
    ],
    categories: ["Education", "Training Material", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating interactive question and answer pairs. You will be provided with a topic or information, and you will generate relevant questions and answers based on that input.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topic } = fields;
      let prompt =
        "Generate a set of question and answer pairs based on the following topic:\n";
      prompt += "Topic: " + topic + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writing-ideas",
    name: "AI Writing Ideas",
    slug: "ai-writing-ideas",
    shortDescription:
      "Generate endless writing ideas with our AI tool, tailored to your topic or purpose for effortless creativity and inspiration.",
    fields: [
      {
        type: "textArea",
        label: "Writing Topic or Purpose",
        field: "Writing Topic or Purpose",
        placeholder: "Best man speech",
        required: true,
        optional: false,
        id: "writingtopicorpurpose",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating writing ideas and examples. You will be provided with a topic or purpose, and you will generate creative writing ideas based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingTopicOrPurpose } = fields;
      let prompt = "Generate writing ideas based on the following:\n";
      prompt += "Writing Topic or Purpose: " + writingTopicOrPurpose + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "professional-writer-ai",
    name: "Professional Writer AI",
    slug: "professional-writer-ai",
    shortDescription:
      "Transform any prompt into professional, natural writing with our advanced AI writer. Experience seamless content creation today!",
    fields: [
      {
        type: "textArea",
        label: "Prompt or Request",
        field: "Prompt or Request",
        placeholder:
          "Write a response to a client's inquiry about our product features.",
        required: true,
        optional: false,
        id: "promptorrequest",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a professional writing assistant. Your task is to take any prompt or request provided by the user and generate a well-structured, natural, and professional written output.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Prompt_or_Request } = fields;
      let prompt =
        "Generate a professional written response based on the following request:\n";
      prompt += "Request: " + Prompt_or_Request + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "freeform-writing-assistant",
    name: "Freeform Writing Assistant",
    slug: "freeform-writing-assistant",
    shortDescription:
      "Transform your ideas into words with our Freeform Writing Assistant, generating unique content based on any topic you provide.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Enter any topic you want to write about",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative writing assistant. You will be provided with a topic and you will generate a piece of writing based on that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields;
      let prompt =
        "Generate a piece of writing based on the following topic:\n";
      prompt += "Topic: " + Topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-knowledge-summarizer",
    name: "AI Knowledge Summarizer",
    slug: "ai-knowledge-summarizer",
    shortDescription:
      "Effortlessly summarize key information on any topic with our AI Knowledge Summarizer, powered by web search results.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Artificial Intelligence",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Education", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing key information about various topics. You will be provided with a specific topic and relevant web search results, and your task is to generate a concise summary that captures the essential points.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields;
      let prompt = "Summarize the key information about the following topic:\n";
      prompt += "Topic: " + Topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "perplexity-and-burstiness-text-generator",
    name: "Perplexity and Burstiness Text Generator",
    slug: "perplexity-and-burstiness-text-generator",
    shortDescription:
      "Explore our Perplexity and Burstiness Text Generator to create complex, varied text that captivates and engages your audience effectively.",
    fields: [
      {
        type: "textArea",
        label: "Text Topic",
        field: "Text Topic",
        placeholder: "Enter the topic you want the text to be about",
        required: true,
        optional: false,
        id: "texttopic",
      },
      {
        type: "textArea",
        label: "Text Length",
        field: "Text Length",
        placeholder: "Enter the desired length of the text",
        required: false,
        optional: true,
        id: "textlength",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating text with high perplexity and burstiness. You will be provided with a topic and desired length, and you will create a text that exhibits complexity and variation in sentence structure.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textTopic, textLength } = fields;
      let prompt = "Generate a text based on the following:\n";
      prompt += "Topic: " + textTopic + ".\n";
      if (textLength) {
        prompt += "Desired length: " + textLength + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "audience-captivator",
    name: "Audience Captivator",
    slug: "audience-captivator",
    shortDescription:
      "Create captivating introductions for essays and presentations with Audience Captivator, your essential tool for engaging content.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Climate Change",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Audience",
        field: "Audience",
        placeholder: "High school students",
        required: false,
        optional: true,
        id: "audience",
      },
      {
        type: "textArea",
        label: "Purpose",
        field: "Purpose",
        placeholder: "Educate about the effects of climate change",
        required: false,
        optional: true,
        id: "purpose",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting captivating introductions for essays or presentations. You will be provided with some base information and you will generate an engaging introduction based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, Audience, Purpose } = fields;
      let prompt =
        "Generate a captivating introduction based on the following:\n";
      prompt += "The topic is about " + Topic + ".\n";
      if (Audience) {
        prompt += "Target audience: " + Audience + ".\n";
      }
      if (Purpose) {
        prompt += "Purpose of the introduction: " + Purpose + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-content-repurposer",
    name: "AI Content Repurposer",
    slug: "ai-content-repurposer",
    shortDescription:
      "Transform your existing content into new formats effortlessly with our AI Content Repurposer tailored to your needs.",
    fields: [
      {
        type: "textArea",
        label: "Existing Content",
        field: "Existing Content",
        placeholder: "Enter the content you want to repurpose here...",
        required: true,
        optional: false,
        id: "existingcontent",
      },
      {
        type: "textArea",
        label: "Repurpose Method",
        field: "Repurpose Method",
        placeholder:
          "E.g., Transform a blog post into a script for a podcast episode",
        required: false,
        optional: true,
        id: "repurposemethod",
      },
    ],
    categories: ["Social Media", "Marketing", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in repurposing content. You will be provided with existing content and a method for repurposing it, and you will generate a new format based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { existingContent, repurposeMethod } = fields;
      let prompt =
        "Transform the following content based on the specified method:\n";
      prompt += "Existing Content: " + existingContent + "\n";
      if (repurposeMethod) {
        prompt += "Repurpose Method: " + repurposeMethod + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-improvement-suggester",
    name: "Text Improvement Suggester",
    slug: "text-improvement-suggester",
    shortDescription:
      "Enhance your writing with our Text Improvement Suggester, offering tailored suggestions based on predefined criteria for better clarity.",
    fields: [
      {
        type: "textArea",
        label: "Text",
        field: "Text",
        placeholder:
          "WritingTools.ai is an AI writing tool that can help you work faster and get more done...",
        required: true,
        optional: false,
        id: "text",
      },
      {
        type: "textArea",
        label: "Suggestions",
        field: "Suggestions",
        placeholder:
          "Add a brief introduction to explain the purpose of the tool and history of AI writing...",
        required: false,
        optional: true,
        id: "suggestions",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in text improvement. You will be provided with a piece of text and optional suggestions, and you will generate recommendations for enhancing the clarity, coherence, and overall quality of the text.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Text, Suggestions } = fields;
      let prompt = "Suggest improvements for the following text:\n";
      prompt += "Text: " + Text + "\n";
      if (Suggestions) {
        prompt += "Additional suggestions: " + Suggestions + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "best-man-speech-generator",
    name: "Best Man Speech Generator",
    slug: "best-man-speech-generator",
    shortDescription:
      "Generate a heartfelt, personalized best man speech effortlessly with our Best Man Speech Generator tailored to the groom's details.",
    fields: [
      {
        type: "textArea",
        label: "Relationship with the Groom",
        field: "Relationship with the Groom",
        placeholder: "Best friends since college",
        required: true,
        optional: false,
        id: "relationshipwiththegroom",
      },
      {
        type: "textArea",
        label: "Story about the Groom",
        field: "Story about the Groom",
        placeholder: "Remember that time when we...",
        required: false,
        optional: true,
        id: "storyaboutthegroom",
      },
      {
        type: "textArea",
        label: "Groom's Qualities",
        field: "Groom's Qualities",
        placeholder: "Loyal, funny, hardworking",
        required: false,
        optional: true,
        id: "groomsqualities",
      },
    ],
    categories: ["Writing", "Social Media", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized best man speeches. You will be provided with information about the groom and their relationship with the speaker, and you will create a heartfelt and engaging speech based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { RelationshipWithTheGroom, StoryAboutTheGroom, GroomsQualities } =
        fields;
      let prompt = "Generate a best man speech based on the following:\n";
      prompt +=
        "Relationship with the groom: " + RelationshipWithTheGroom + ".\n";
      if (StoryAboutTheGroom) {
        prompt += "Story about the groom: " + StoryAboutTheGroom + ".\n";
      }
      if (GroomsQualities) {
        prompt += "Groom's qualities: " + GroomsQualities + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "blog-idea-generator",
    name: "Blog Idea Generator",
    slug: "blog-idea-generator",
    shortDescription:
      "Generate unique blog post ideas effortlessly with our Blog Idea Generator. Simply input your keywords and spark creativity!",
    fields: [
      {
        type: "textArea",
        label: "Keywords",
        field: "Keywords",
        placeholder: "Technology, AI, Machine Learning",
        required: true,
        optional: false,
        id: "keywords",
      },
      {
        type: "textArea",
        label: "Topics",
        field: "Topics",
        placeholder: "Future of AI, Machine Learning applications",
        required: false,
        optional: true,
        id: "topics",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating unique and engaging blog post ideas. You will be provided with keywords and topics, and you will generate creative blog post ideas based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Keywords, Topics } = fields;
      let prompt = "Generate unique blog post ideas based on the following:\n";
      prompt += "Keywords: " + Keywords + ".\n";
      if (Topics) {
        prompt += "Topics: " + Topics + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-literature-review-writer",
    name: "AI Literature Review Writer",
    slug: "ai-literature-review-writer",
    shortDescription:
      "Generate a comprehensive literature review with our AI writer, tailored to your academic topic or research question effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Topic or Research Question",
        field: "Topic or Research Question",
        placeholder: "Artificial Intelligence in Healthcare",
        required: true,
        optional: false,
        id: "topicorresearchquestion",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating literature reviews. You will be provided with a specific academic topic or research question, and you will generate a comprehensive literature review based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicOrResearchQuestion } = fields;
      let prompt =
        "Generate a comprehensive literature review based on the following:\n";
      prompt +=
        "Topic or Research Question: " + topicOrResearchQuestion + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-maker-ai",
    name: "Resume Maker AI",
    slug: "resume-maker-ai",
    shortDescription:
      "Create a standout resume with Resume Maker AI, tailored to your job history and skills for the perfect application boost.",
    fields: [
      {
        type: "textArea",
        label: "Job History",
        field: "Job History",
        placeholder: "List your past jobs with the roles and responsibilities",
        required: true,
        optional: false,
        id: "jobhistory",
      },
      {
        type: "textArea",
        label: "Skills",
        field: "Skills",
        placeholder: "List your skills related to the job you're applying for",
        required: false,
        optional: true,
        id: "skills",
      },
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder:
          "Paste the job description for the job you're applying for",
        required: false,
        optional: true,
        id: "jobdescription",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating professional resumes. You will be provided with the user's job history, skills, and the job description they are applying for, and you will generate a tailored resume based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { jobHistory, skills, jobDescription } = fields;
      let prompt =
        "Create a professional resume based on the following information:\n";
      prompt += "Job History: " + jobHistory + "\n";
      if (skills) {
        prompt += "Skills: " + skills + "\n";
      }
      if (jobDescription) {
        prompt += "Job Description: " + jobDescription + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-fitness-coach",
    name: "AI Fitness Coach",
    slug: "ai-fitness-coach",
    shortDescription:
      "Get personalized workout plans and fitness advice with our AI Fitness Coach, tailored to your unique goals and needs.",
    fields: [
      {
        type: "textArea",
        label: "Fitness Goal or Request",
        field: "Fitness Goal or Request",
        placeholder: "Lose weight, Gain muscle, Improve flexibility",
        required: true,
        optional: false,
        id: "fitnessgoalorrequest",
      },
      {
        type: "textArea",
        label: "Current Fitness Level",
        field: "Current Fitness Level",
        placeholder: "Beginner, Intermediate, Advanced",
        required: false,
        optional: true,
        id: "currentfitnesslevel",
      },
    ],
    categories: ["Training Material", "Education", "Fitness"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert AI fitness coach. You will be provided with a user's fitness goals and current fitness level, and you will generate a personalized workout plan and fitness advice based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { fitnessGoalOrRequest, currentFitnessLevel } = fields;
      let prompt =
        "Generate a personalized workout plan and fitness advice based on the following:\n";
      prompt += "Fitness goal or request: " + fitnessGoalOrRequest + ".\n";
      if (currentFitnessLevel) {
        prompt += "Current fitness level: " + currentFitnessLevel + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "manager-expectation-synthesizer",
    name: "Manager Expectation Synthesizer",
    slug: "manager-expectation-synthesizer",
    shortDescription:
      "Summarize manager expectations effortlessly with our synthesizer tool, transforming ideas and keywords into clear insights.",
    fields: [
      {
        type: "textArea",
        label: "Ideas or Keywords",
        field: "Ideas or Keywords",
        placeholder: "Weekly reports, punctuality, team collaboration",
        required: true,
        optional: false,
        id: "ideasorkeywords",
      },
    ],
    categories: ["Business", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in synthesizing manager expectations. You will be provided with ideas or keywords, and you will summarize the expectations of a manager based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { ideasOrKeywords } = fields;
      let prompt =
        "Summarize the expectations of a manager based on the following ideas or keywords:\n";
      prompt += "Ideas or Keywords: " + ideasOrKeywords + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "topic-sentence-generator",
    name: "Topic Sentence Generator",
    slug: "topic-sentence-generator",
    shortDescription:
      "Generate clear and concise topic sentences effortlessly with our generator, tailored from your provided evidence and explanations.",
    fields: [
      {
        type: "textArea",
        label: "Evidence",
        field: "Evidence",
        placeholder: "Statistical data, research findings, quotes, etc.",
        required: true,
        optional: false,
        id: "evidence",
      },
      {
        type: "textArea",
        label: "Explanation",
        field: "Explanation",
        placeholder: "Explanation or interpretation of the evidence",
        required: false,
        optional: true,
        id: "explanation",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating topic sentences. You will be provided with evidence and an explanation, and you will create a concise and effective topic sentence that encapsulates the main idea.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Evidence, Explanation } = fields;
      let prompt = "Generate a topic sentence based on the following:\n";
      prompt += "Evidence: " + Evidence + "\n";
      if (Explanation) {
        prompt += "Explanation: " + Explanation + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "teenspeak-topic-explainer",
    name: "TeenSpeak Topic Explainer",
    slug: "teenspeak-topic-explainer",
    shortDescription:
      "Explore complex topics simplified for teens! TeenSpeak makes learning easy and relatable for 15-year-olds.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Quantum Physics",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in explaining complex topics in a way that a 15-year-old teenager would understand. You will be provided with a topic and you will generate an explanation suitable for a teenager.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields;
      let prompt =
        "Explain the following topic in a way that a 15-year-old would understand:\n";
      prompt += "Topic: " + Topic + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "advanced-vocabulary-story-writer",
    name: "Advanced Vocabulary Story Writer",
    slug: "advanced-vocabulary-story-writer",
    shortDescription:
      "Create captivating stories with advanced vocabulary using user-defined themes. Elevate your storytelling with our unique story writer!",
    fields: [
      {
        type: "textArea",
        label: "Story Elements",
        field: "Story Elements",
        placeholder:
          "A mysterious artifact, A haunted mansion, A determined archaeologist",
        required: true,
        optional: false,
        id: "storyelements",
      },
      {
        type: "textArea",
        label: "Story Length",
        field: "Story Length",
        placeholder: "Short Story/Novella",
        required: false,
        optional: true,
        id: "storylength",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting engaging stories using advanced vocabulary. You will be provided with story elements and a desired length, and you will generate a captivating story based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { storyElements, storyLength } = fields;
      let prompt = "Generate a story based on the following:\n";
      prompt += "Story elements: " + storyElements + ".\n";
      if (storyLength) {
        prompt += "Desired length: " + storyLength + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "professional-outreach-email-generator",
    name: "Professional Outreach Email Generator",
    slug: "professional-outreach-email-generator",
    shortDescription:
      "Create professional outreach emails effortlessly with our generator, perfect for connecting with companies for job opportunities.",
    fields: [
      {
        type: "textArea",
        label: "Company Name",
        field: "Company Name",
        placeholder: "Livspace",
        required: true,
        optional: false,
        id: "companyname",
      },
      {
        type: "textArea",
        label: "Sender's Name",
        field: "Sender's Name",
        placeholder: "John Doe",
        required: false,
        optional: true,
        id: "sendersname",
      },
      {
        type: "textArea",
        label: "Sender's Skills and Experience",
        field: "Sender's Skills and Experience",
        placeholder: "Software Developer with 5 years of experience...",
        required: false,
        optional: true,
        id: "sendersskillsandexperience",
      },
      {
        type: "textArea",
        label: "Why Interested in the Company",
        field: "Why Interested in the Company",
        placeholder:
          "I have always admired Livspace's innovative approach to...",
        required: false,
        optional: true,
        id: "whyinterestedinthecompany",
      },
    ],
    categories: ["Email", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating professional outreach emails. You will be provided with details about the company and the sender, and you will create a tailored email for reaching out to potential job opportunities.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const {
        companyName,
        senderName,
        senderSkillsAndExperience,
        whyInterestedInTheCompany,
      } = fields;
      let prompt =
        "Generate a professional outreach email based on the following:\n";
      prompt += "Company Name: " + companyName + ".\n";
      if (senderName) {
        prompt += "Sender's Name: " + senderName + ".\n";
      }
      if (senderSkillsAndExperience) {
        prompt +=
          "Sender's Skills and Experience: " +
          senderSkillsAndExperience +
          ".\n";
      }
      if (whyInterestedInTheCompany) {
        prompt += "Reason for Interest: " + whyInterestedInTheCompany + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "rapid-typing-assistant",
    name: "Rapid Typing Assistant",
    slug: "rapid-typing-assistant",
    shortDescription:
      "Boost your productivity with Rapid Typing Assistant, effortlessly generating text based on your prompts for quick results.",
    fields: [
      {
        type: "textArea",
        label: "Short Prompt",
        field: "Short Prompt",
        placeholder: "Write about the benefits of exercise.",
        required: true,
        optional: false,
        id: "shortprompt",
      },
    ],
    categories: ["Writing", "Business", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating text based on short prompts. You will be provided with a brief prompt and you will generate a detailed response that elaborates on the topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { shortPrompt } = fields;
      let prompt =
        "Generate a detailed response based on the following short prompt:\n";
      prompt += "Short Prompt: " + shortPrompt + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writing-coach",
    name: "AI Writing Coach",
    slug: "ai-writing-coach",
    shortDescription:
      "Get personalized writing tips and real-time feedback from our AI Writing Coach to enhance your style and structure effectively.",
    fields: [
      {
        type: "textArea",
        label: "Writing Sample",
        field: "Writing Sample",
        placeholder: "Once upon a time...",
        required: true,
        optional: false,
        id: "writingsample",
      },
      {
        type: "textArea",
        label: "Writing Goal",
        field: "Writing Goal",
        placeholder: "Improve storytelling and imagery",
        required: false,
        optional: true,
        id: "writinggoal",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI writing coach. Your role is to provide personalized writing tips, suggestions for improvement, and real-time feedback on the user's writing style and structure based on the provided writing sample and goals.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingSample, writingGoal } = fields;
      let prompt = "Provide feedback on the following writing sample:\n";
      prompt += "Writing Sample: " + writingSample + "\n";
      if (writingGoal) {
        prompt += "Writing Goal: " + writingGoal + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "universal-qa-responder",
    name: "Universal Q&A Responder",
    slug: "universal-qa-responder",
    shortDescription:
      "Get instant answers to all your questions with Universal Q&A Responder. Explore a wide range of topics effortlessly!",
    fields: [
      {
        type: "textArea",
        label: "Question",
        field: "Question",
        placeholder: "What is the capital of Australia?",
        required: true,
        optional: false,
        id: "question",
      },
    ],
    categories: ["Support", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert Q&A responder. You will be provided with a question on any topic, and you will generate a clear and concise answer based on your knowledge.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Question } = fields.Question;
      let prompt = "Provide a detailed answer to the following question:\n";
      prompt += "Question: " + Question + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "blog-outline-builder",
    name: "Blog Outline Builder",
    slug: "blog-outline-builder",
    shortDescription:
      "Create a detailed blog outline effortlessly with our tool, tailored to your topic, keywords, and target audience for better engagement.",
    fields: [
      {
        type: "textArea",
        label: "Blog Topic",
        field: "Blog Topic",
        placeholder: "The Future of AI in Healthcare",
        required: true,
        optional: false,
        id: "blogtopic",
      },
      {
        type: "textArea",
        label: "Keywords",
        field: "Keywords",
        placeholder: "AI, Healthcare, Future, Technology",
        required: false,
        optional: true,
        id: "keywords",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Healthcare professionals interested in technology",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Writing", "Marketing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating detailed blog outlines. You will be provided with a topic, keywords, and target audience, and you will generate a structured outline for a blog post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { blogTopic, keywords, targetAudience } = fields;
      let prompt =
        "Create a detailed outline for a blog post based on the following:\n";
      prompt += "Blog Topic: " + blogTopic + "\n";
      if (keywords) {
        prompt += "Keywords: " + keywords + "\n";
      }
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "gift-ideas",
    name: "Gift Ideas",
    slug: "gift-ideas",
    shortDescription:
      "Generate personalized gift ideas tailored to the recipient's interests and the occasion for a memorable surprise.",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Interests",
        field: "Recipient's Interests",
        placeholder: "Cooking, Gardening, Vintage Cars",
        required: true,
        optional: false,
        id: "recipientsinterests",
      },
      {
        type: "textArea",
        label: "Occasion",
        field: "Occasion",
        placeholder: "Birthday, Anniversary, Housewarming",
        required: false,
        optional: true,
        id: "occasion",
      },
    ],
    categories: ["Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized gift ideas. You will be provided with the recipient's interests and the occasion, and you will generate suitable gift suggestions based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientInterests, occasion } = fields;
      let prompt = "Generate personalized gift ideas based on the following:\n";
      prompt += "Recipient's interests: " + recipientInterests + ".\n";
      if (occasion) {
        prompt += "Occasion: " + occasion + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-resource-finder",
    name: "Essay Resource Finder",
    slug: "essay-resource-finder",
    shortDescription:
      "Discover academic resources tailored to your essay topic with our Essay Resource Finder for effective research and writing support.",
    fields: [
      {
        type: "textArea",
        label: "Essay Topic",
        field: "Essay Topic",
        placeholder: "The impact of climate change on biodiversity",
        required: true,
        optional: false,
        id: "essaytopic",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in finding and suggesting academic resources for essays. You will be provided with an essay topic, and you will generate a list of relevant academic resources based on that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayTopic } = fields;
      let prompt =
        "Find and suggest academic resources based on the following essay topic:\n";
      prompt += "Essay Topic: " + essayTopic + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "bridge-sentence-builder",
    name: "Bridge Sentence Builder",
    slug: "bridge-sentence-builder",
    shortDescription:
      "Create seamless transitions in your writing with the Bridge Sentence Builder, connecting ideas effortlessly for clarity and flow.",
    fields: [
      {
        type: "textArea",
        label: "Topic Sentence",
        field: "Topic Sentence",
        placeholder: "Climate change is a pressing issue in today's world.",
        required: true,
        optional: false,
        id: "topicsentence",
      },
      {
        type: "textArea",
        label: "Evidence or Explanation",
        field: "Evidence or Explanation",
        placeholder:
          "According to NASA, the average global temperature has increased by about 0.8° Celsius since 1880.",
        required: false,
        optional: true,
        id: "evidenceorexplanation",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in constructing bridging sentences. You will be provided with a topic sentence and evidence or explanation, and your task is to generate a bridging sentence that logically connects the two.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicSentence, evidenceOrExplanation } = fields;
      let prompt = "Generate a bridging sentence based on the following:\n";
      prompt += "Topic Sentence: " + topicSentence + "\n";
      if (evidenceOrExplanation) {
        prompt += "Evidence or Explanation: " + evidenceOrExplanation + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "meal-planner-pro",
    name: "Meal Planner Pro",
    slug: "meal-planner-pro",
    shortDescription:
      "Create personalized meal plans with Meal Planner Pro, catering to your dietary needs and goals for a healthier lifestyle.",
    fields: [
      {
        type: "textArea",
        label: "Dietary Preferences",
        field: "Dietary Preferences",
        placeholder: "Vegetarian, Gluten-free, etc.",
        required: true,
        optional: false,
        id: "dietarypreferences",
      },
      {
        type: "textArea",
        label: "Dietary Restrictions",
        field: "Dietary Restrictions",
        placeholder: "Lactose intolerance, Nut allergy, etc.",
        required: false,
        optional: true,
        id: "dietaryrestrictions",
      },
      {
        type: "textArea",
        label: "Dietary Goals",
        field: "Dietary Goals",
        placeholder: "Weight loss, Muscle gain, etc.",
        required: false,
        optional: true,
        id: "dietarygoals",
      },
    ],
    categories: ["Business", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized meal plans. You will be provided with dietary preferences, restrictions, and goals, and you will generate a custom meal plan based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { DietaryPreferences, DietaryRestrictions, DietaryGoals } = fields;
      let prompt = "Create a custom meal plan based on the following:\n";
      prompt += "Dietary Preferences: " + DietaryPreferences + ".\n";
      if (DietaryRestrictions) {
        prompt += "Dietary Restrictions: " + DietaryRestrictions + ".\n";
      }
      if (DietaryGoals) {
        prompt += "Dietary Goals: " + DietaryGoals + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "cold-email-composer",
    name: "Cold Email Composer",
    slug: "cold-email-composer",
    shortDescription:
      "Generate personalized cold emails effortlessly with our Cold Email Composer, tailored to product names and LinkedIn profiles.",
    fields: [
      {
        type: "textArea",
        label: "Product Name and Information",
        field: "Product Name and Information",
        placeholder: "Enter the product name and information here",
        required: true,
        optional: false,
        id: "productnameandinformation",
      },
      {
        type: "textArea",
        label: "LinkedIn URL",
        field: "LinkedIn URL",
        placeholder: "Paste the LinkedIn profile URL here",
        required: false,
        optional: true,
        id: "linkedinurl",
      },
    ],
    categories: ["Email", "Sales", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in composing cold emails. You will be provided with a product name and information, along with a LinkedIn profile URL, to generate a personalized cold email.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { productNameAndInformation, linkedInURL } = fields;
      let prompt =
        "Generate a personalized cold email based on the following:\n";
      prompt +=
        "Product Name and Information: " + productNameAndInformation + "\n";
      if (linkedInURL) {
        prompt += "LinkedIn Profile URL: " + linkedInURL + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "proclamation-generator",
    name: "Proclamation Generator",
    slug: "proclamation-generator",
    shortDescription:
      "Create formal proclamations effortlessly with our Proclamation Generator tailored to your event and purpose.",
    fields: [
      {
        type: "textArea",
        label: "Context",
        field: "Context",
        placeholder: "The 50th anniversary of our company...",
        required: true,
        optional: false,
        id: "context",
      },
      {
        type: "textArea",
        label: "Event",
        field: "Event",
        placeholder: "A grand celebration with all employees...",
        required: false,
        optional: true,
        id: "event",
      },
      {
        type: "textArea",
        label: "Purpose",
        field: "Purpose",
        placeholder:
          "To honor the dedication and hard work of our employees...",
        required: false,
        optional: true,
        id: "purpose",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating formal proclamations. You will be provided with context, event details, and purpose, and you will generate a formal proclamation based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Context, Event, Purpose } = fields;
      let prompt = "Generate a formal proclamation based on the following:\n";
      prompt += "Context: " + Context + "\n";
      if (Event) {
        prompt += "Event: " + Event + "\n";
      }
      if (Purpose) {
        prompt += "Purpose: " + Purpose + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "digital-product-email-sequence-builder",
    name: "Digital Product Email Sequence Builder",
    slug: "digital-product-email-sequence-builder",
    shortDescription:
      "Create effective email sequences to nurture prospects and customers for your digital products with our easy-to-use builder.",
    fields: [
      {
        type: "textArea",
        label: "Digital Product Details",
        field: "Digital Product Details",
        placeholder: "Name, key features, benefits, price, etc.",
        required: true,
        optional: false,
        id: "digitalproductdetails",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Prospects or Existing Customers",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Email", "Marketing", "Sales"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating email sequences for digital products. You will be provided with details about the digital product and the target audience, and you will generate a series of emails designed to nurture potential prospects or existing customers.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { digitalProductDetails, targetAudience } = fields;
      let prompt = "Generate an email sequence based on the following:\n";
      prompt += "Digital Product Details: " + digitalProductDetails + "\n";
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "personal-daily-assistant",
    name: "Personal Daily Assistant",
    slug: "personal-daily-assistant",
    shortDescription:
      "Transform your daily routine with a personal assistant that manages tasks, reminders, and provides tailored updates on weather and news.",
    fields: [
      {
        type: "textArea",
        label: "User Interests",
        field: "User Interests",
        placeholder: "Reading, Hiking, Italian cuisine",
        required: true,
        optional: false,
        id: "userinterests",
      },
      {
        type: "textArea",
        label: "Daily Tasks",
        field: "Daily Tasks",
        placeholder:
          "Meeting at 10 AM, Grocery shopping, Visit the dentist at 4 PM",
        required: false,
        optional: true,
        id: "dailytasks",
      },
      {
        type: "textArea",
        label: "Location",
        field: "Location",
        placeholder: "New York, USA",
        required: false,
        optional: true,
        id: "location",
      },
    ],
    categories: ["Business", "Support", "Personal Daily Assistant"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a personal daily assistant designed to help users manage their daily tasks and provide personalized recommendations. You will receive information about the user's interests, daily tasks, and location to generate helpful reminders and updates.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userInterests, dailyTasks, location } = fields;
      let prompt =
        "Here is the daily assistance based on the provided information:\n";
      prompt += "User Interests: " + userInterests + ".\n";
      if (dailyTasks) {
        prompt += "Daily Tasks: " + dailyTasks + ".\n";
      }
      if (location) {
        prompt += "Location: " + location + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "writing-example-generator",
    name: "Writing Example Generator",
    slug: "writing-example-generator",
    shortDescription:
      "Generate creative writing examples and ideas effortlessly for any topic or purpose with our Writing Example Generator.",
    fields: [
      {
        type: "textArea",
        label: "Writing Task",
        field: "Writing Task",
        placeholder: "Best Man Speech",
        required: true,
        optional: false,
        id: "writingtask",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating writing examples. You will be provided with a specific writing task and you will generate ideas and examples based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingTask } = fields;
      let prompt =
        "Generate ideas and examples for the following writing task:\n";
      prompt += "Writing Task: " + writingTask + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "content-kick-start",
    name: "Content Kick-Start",
    slug: "content-kick-start",
    shortDescription:
      "Kick-start your content creation with tailored tips and strategies for different content types and target audiences.",
    fields: [
      {
        type: "textArea",
        label: "Content Type",
        field: "Content Type",
        placeholder: "Blog post, eBook, Social media post, etc.",
        required: true,
        optional: false,
        id: "contenttype",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Millennials, Business owners, Fitness enthusiasts, etc.",
        required: false,
        optional: true,
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Subject or Topic",
        field: "Subject or Topic",
        placeholder: "What is the topic or purpose of your content?",
        required: false,
        optional: true,
        id: "subjectortopic",
      },
    ],
    categories: [
      "Social Media",
      "Marketing",
      "Writing",
      "Education",
      "Business",
    ],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in content creation strategies. You will be provided with information about the type of content and target audience, and you will generate tips and strategies for starting content creation based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { contentType, targetAudience, subjectOrTopic } = fields;
      let prompt =
        "Generate tips and strategies for starting content creation based on the following:\n";
      prompt += "Content Type: " + contentType + ".\n";
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + ".\n";
      }
      if (subjectOrTopic) {
        prompt += "Subject or Topic: " + subjectOrTopic + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-for-research",
    name: "AI for Research",
    slug: "ai-for-research",
    shortDescription:
      "Explore how AI conducts in-depth research on any topic, utilizing search and scholarly articles for comprehensive insights.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic",
        field: "Research Topic",
        placeholder: "Artificial Intelligence in Healthcare",
        required: true,
        optional: false,
        id: "researchtopic",
      },
    ],
    categories: ["Education", "Research", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in conducting in-depth research. You will be provided with a research topic, and you will generate a comprehensive overview based on scholarly articles and relevant sources.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic } = fields;
      let prompt = "Conduct in-depth research on the following topic:\n";
      prompt += "Research Topic: " + researchTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "discussion-post-writer",
    name: "Discussion Post Writer",
    slug: "discussion-post-writer",
    shortDescription:
      "Generate engaging discussion posts effortlessly with our tool, tailored to your topic and key points for maximum impact.",
    fields: [
      {
        type: "textArea",
        label: "Discussion Topic",
        field: "Discussion Topic",
        placeholder: "Effects of Climate Change",
        required: true,
        optional: false,
        id: "discussiontopic",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. Rising global temperatures\n2. Melting polar ice caps\n3. Impact on wildlife",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Social Media", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging discussion posts. You will be provided with a discussion topic and key points, and you will create a compelling post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { discussionTopic, keyPoints } = fields;
      let prompt = "Generate a discussion post based on the following:\n";
      prompt += "Discussion Topic: " + discussionTopic + "\n";
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-research-question-formulator",
    name: "AI Research Question Formulator",
    slug: "ai-research-question-formulator",
    shortDescription:
      "Create focused research questions with our AI Research Question Formulator, tailored to your study area and topic ideas.",
    fields: [
      {
        type: "textArea",
        label: "Area of Study",
        field: "Area of Study",
        placeholder: "Physics, Sociology, Literature, etc.",
        required: true,
        optional: false,
        id: "areaofstudy",
      },
      {
        type: "textArea",
        label: "Initial Topic Ideas",
        field: "Initial Topic Ideas",
        placeholder:
          "Quantum mechanics, Social inequality, Postmodern literature, etc.",
        required: false,
        optional: true,
        id: "initialtopicideas",
      },
    ],
    categories: ["Writing", "Education", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in formulating research questions. You will be provided with the user's area of study and initial topic ideas, and you will generate focused and impactful research questions based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { areaOfStudy, initialTopicIdeas } = fields;
      let prompt =
        "Generate impactful research questions based on the following:\n";
      prompt += "Area of Study: " + areaOfStudy + ".\n";
      if (initialTopicIdeas) {
        prompt += "Initial Topic Ideas: " + initialTopicIdeas + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-study-guide-creator",
    name: "AI Study Guide Creator",
    slug: "ai-study-guide-creator",
    shortDescription:
      "Transform class notes into comprehensive study guides with AI. Get key concepts, definitions, and practice questions effortlessly!",
    fields: [
      {
        type: "textArea",
        label: "Class Notes/Textbook Chapters",
        field: "Class Notes/Textbook Chapters",
        placeholder: "Enter your class notes or textbook chapters here.",
        required: true,
        optional: false,
        id: "classnotes/textbookchapters",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating study guides from class notes or textbook chapters. You will be provided with the content and you will generate a comprehensive study guide that includes key concepts, definitions, and practice questions based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { classNotes } = fields;
      let prompt =
        "Create a study guide based on the following class notes or textbook chapters:\n";
      prompt += "Content: " + classNotes + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "writing-examples-generator",
    name: "Writing Examples Generator",
    slug: "writing-examples-generator",
    shortDescription:
      "Generate diverse writing examples tailored to your needs for inspiration or as a solid starting point for your next draft.",
    fields: [
      {
        type: "textArea",
        label: "Type of Writing",
        field: "Type of Writing",
        placeholder: "Wedding Speech",
        required: true,
        optional: false,
        id: "typeofwriting",
      },
      {
        type: "textArea",
        label: "Specific Details",
        field: "Specific Details",
        placeholder:
          "Speech for my best friend's wedding. We've known each other since kindergarten. She loves gardening and dogs.",
        required: false,
        optional: true,
        id: "specificdetails",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating writing examples. You will be provided with the type of writing and specific details, and you will generate a relevant writing example based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { typeOfWriting, specificDetails } = fields;
      let prompt = "Generate a writing example based on the following:\n";
      prompt += "Type of writing: " + typeOfWriting + ".\n";
      if (specificDetails) {
        prompt += "Specific details: " + specificDetails + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "cultural-name-generator",
    name: "Cultural Name Generator",
    slug: "cultural-name-generator",
    shortDescription:
      "Generate unique names in your chosen language based on personality traits and pronouns with our Cultural Name Generator.",
    fields: [
      {
        type: "textArea",
        label: "Language",
        field: "Language",
        placeholder: "French",
        required: true,
        optional: false,
        id: "language",
      },
      {
        type: "textArea",
        label: "Pronouns",
        field: "Pronouns",
        placeholder: "She/Her",
        required: false,
        optional: true,
        id: "pronouns",
      },
      {
        type: "textArea",
        label: "Personality Traits",
        field: "Personality Traits",
        placeholder: "Kind, Ambitious, Creative",
        required: false,
        optional: true,
        id: "personalitytraits",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating cultural names. You will be provided with a language, pronouns, and personality traits, and you will generate a name that reflects these characteristics.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Language, Pronouns, PersonalityTraits } = fields;
      let prompt = "Generate a cultural name based on the following:\n";
      prompt += "Language: " + Language + ".\n";
      if (Pronouns) {
        prompt += "Pronouns: " + Pronouns + ".\n";
      }
      if (PersonalityTraits) {
        prompt += "Personality Traits: " + PersonalityTraits + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-autowriter",
    name: "AI AutoWriter",
    slug: "ai-autowriter",
    shortDescription:
      "Generate tailored content effortlessly with AI AutoWriter, crafting unique text based on your chosen topic and style.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "The future of AI in healthcare",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Writing Style",
        field: "Writing Style",
        placeholder: "Informative blog post, professional accurate writing",
        required: false,
        optional: true,
        id: "writingstyle",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert content creator. You will be provided with a topic and a writing style, and you will generate written content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, WritingStyle } = fields;
      let prompt = "Generate written content based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (WritingStyle) {
        prompt += "Writing Style: " + WritingStyle + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-knowledge-accumulator",
    name: "AI Knowledge Accumulator",
    slug: "ai-knowledge-accumulator",
    shortDescription:
      "Effortlessly accumulate and organize knowledge with our AI Knowledge Accumulator, designed to enhance your learning experience.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Artificial Intelligence",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Education", "Business", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in knowledge accumulation and organization using artificial intelligence. You will be provided with a topic, and you will assist in gathering and structuring relevant information about that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields.Topic;
      let prompt =
        "Assist in accumulating and organizing knowledge based on the following topic:\n";
      prompt += "Topic: " + Topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "iq-adjustable-ai",
    name: "IQ Adjustable AI",
    slug: "iq-adjustable-ai",
    shortDescription:
      "Adjust AI responses with IQ Adjustable AI, from simple sentences to creative, intelligent content tailored to your needs.",
    fields: [
      {
        type: "textArea",
        label: "Set IQ",
        field: "Set IQ",
        placeholder: "120",
        required: true,
        optional: false,
        id: "setiq",
      },
      {
        type: "textArea",
        label: "User Query",
        field: "User Query",
        placeholder: "Tell me about quantum physics.",
        required: false,
        optional: true,
        id: "userquery",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI that adjusts its responses based on the specified IQ level. Depending on the IQ set, you will provide answers that range from simple and grammatically incorrect sentences to highly intelligent, detailed, and creative responses.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { SetIQ, UserQuery } = fields;
      let prompt = "Generate a response based on the following parameters:\n";
      prompt += "Set IQ: " + SetIQ + "\n";
      if (UserQuery) {
        prompt += "User Query: " + UserQuery + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "theology-writer-assistant",
    name: "Theology Writer Assistant",
    slug: "theology-writer-assistant",
    shortDescription:
      "Generate accurate theology content effortlessly with our Theology Writer Assistant for studies, counseling, leadership, and education.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "The Role of Leadership in Christian Education",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "Importance of leadership, Biblical examples of leadership, Application in modern Christian education",
        required: false,
        optional: true,
        id: "keypoints",
      },
      {
        type: "textArea",
        label: "Content Type",
        field: "Content Type",
        placeholder: "Essay",
        required: false,
        optional: true,
        id: "contenttype",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in theology and religious studies. You will be provided with a topic and key points, and you will generate comprehensive and accurate content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, KeyPoints, ContentType } = fields;
      let prompt = "Generate content based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (KeyPoints) {
        prompt += "Key Points: " + KeyPoints + "\n";
      }
      if (ContentType) {
        prompt += "Content Type: " + ContentType + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "transactional-keyword-suggester",
    name: "Transactional Keyword Suggester",
    slug: "transactional-keyword-suggester",
    shortDescription:
      "Optimize your Google Ads campaigns with our Transactional Keyword Suggester, designed to boost conversions and drive sales.",
    fields: [
      {
        type: "textArea",
        label: "Product or Service Description",
        field: "Product or Service Description",
        placeholder: "Our product is a high-quality, eco-friendly yoga mat...",
        required: true,
        optional: false,
        id: "productorservicedescription",
      },
      {
        type: "textArea",
        label: "Campaign Goal",
        field: "Campaign Goal",
        placeholder: "Drive sales for our new yoga mat",
        required: false,
        optional: true,
        id: "campaigngoal",
      },
    ],
    categories: ["Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in suggesting transactional keywords for Google Ads campaigns. You will be provided with a product or service description and a campaign goal, and you will generate relevant transactional keywords to optimize the campaign.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { ProductOrServiceDescription, CampaignGoal } = fields;
      let prompt = "Suggest transactional keywords based on the following:\n";
      prompt +=
        "Product or Service Description: " + ProductOrServiceDescription + "\n";
      if (CampaignGoal) {
        prompt += "Campaign Goal: " + CampaignGoal + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "entrepreneur-success-blog-rewriter",
    name: "Entrepreneur Success Blog Rewriter",
    slug: "entrepreneur-success-blog-rewriter",
    shortDescription:
      "Transform your blog posts with our Entrepreneur Success Blog Rewriter, crafting tailored content for a success-driven entrepreneurial audience.",
    fields: [
      {
        type: "textArea",
        label: "Blog Post",
        field: "Blog Post",
        placeholder: "Enter the blog post here.",
        required: true,
        optional: false,
        id: "blogpost",
      },
    ],
    categories: ["Website", "Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting blog posts for entrepreneurs. You will be provided with a blog post and you will rewrite it to make it more suitable for a success-oriented website targeting entrepreneurs.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { blogPost } = fields;
      let prompt =
        "Rewrite the following blog post to tailor it for a success-oriented website for entrepreneurs:\n";
      prompt += blogPost + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-style-transfer",
    name: "Text Style Transfer",
    slug: "text-style-transfer",
    shortDescription:
      "Transform your text's style effortlessly! Discover how to mimic the style of any text with our easy text style transfer guide.",
    fields: [
      {
        type: "textArea",
        label: "Sample Text",
        field: "Sample Text",
        placeholder: "This is the text you want to mimic.",
        required: true,
        optional: false,
        id: "sampletext",
      },
      {
        type: "textArea",
        label: "Text to Transform",
        field: "Text to Transform",
        placeholder: "This is the text you want to change.",
        required: false,
        optional: true,
        id: "texttotransform",
      },
    ],
    categories: ["Writing", "Marketing", "Social Media"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in text style transfer. You will be provided with a sample text and a text to transform, and you will generate a new text that mimics the style of the sample text.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { sampleText, textToTransform } = fields;
      let prompt =
        "Transform the following text based on the provided style:\n";
      prompt += "Sample Text: " + sampleText + "\n";
      if (textToTransform) {
        prompt += "Text to Transform: " + textToTransform + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "content-kick-starter-assistant",
    name: "Content Kick-Starter Assistant",
    slug: "content-kick-starter-assistant",
    shortDescription:
      "Jumpstart your content creation with our Assistant! Get creative tips and ideas tailored to your topic for engaging content.",
    fields: [
      {
        type: "textArea",
        label: "Content Topic",
        field: "Content Topic",
        placeholder: "Sustainable Fashion",
        required: true,
        optional: false,
        id: "contenttopic",
      },
    ],
    categories: ["Writing", "Marketing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative assistant specialized in generating content ideas. You will be provided with a topic, and your task is to generate creative tips and ideas for starting new content based on that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { contentTopic } = fields;
      let prompt =
        "Generate creative tips and ideas for starting new content based on the following topic:\n";
      prompt += "Content Topic: " + contentTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-meeting-notes-organizer",
    name: "AI Meeting Notes Organizer",
    slug: "ai-meeting-notes-organizer",
    shortDescription:
      "Streamline your meetings with our AI Meeting Notes Organizer, transforming notes into clear formats for easy reference and understanding.",
    fields: [
      {
        type: "textArea",
        label: "Meeting Notes",
        field: "Meeting Notes",
        placeholder: "During the meeting, we discussed...",
        required: true,
        optional: false,
        id: "meetingnotes",
      },
      {
        type: "textArea",
        label: "Desired Format",
        field: "Desired Format",
        placeholder: "Bullet points / Mind map / Summary",
        required: false,
        optional: true,
        id: "desiredformat",
      },
    ],
    categories: ["Business", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in organizing meeting notes. You will be provided with raw meeting notes and a desired format, and you will organize the notes accordingly.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { meetingNotes, desiredFormat } = fields;
      let prompt =
        "Organize the following meeting notes into the specified format:\n";
      prompt += "Meeting Notes: " + meetingNotes + "\n";
      if (desiredFormat) {
        prompt += "Desired Format: " + desiredFormat + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "seo-startup-master-guide",
    name: "SEO Startup Master Guide",
    slug: "seo-startup-master-guide",
    shortDescription:
      "Master SEO with our step-by-step guide. Launch your site and achieve endless organic traffic while following search engine guidelines.",
    fields: [
      {
        type: "textArea",
        label: "Website Niche",
        field: "Website Niche",
        placeholder: "E.g. Tech Blog focused on Artificial Intelligence",
        required: true,
        optional: false,
        id: "websiteniche",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "E.g. Tech Enthusiasts and Early Adopters",
        required: false,
        optional: true,
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Website Platform",
        field: "Website Platform",
        placeholder: "E.g. WordPress, Shopify, Webflow, Custom",
        required: false,
        optional: true,
        id: "websiteplatform",
      },
    ],
    categories: ["Website", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in SEO strategies and best practices. You will be provided with information about a website's niche, target audience, and platform, and you will generate a comprehensive step-by-step guide to SEO tailored to that specific context.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { websiteNiche, targetAudience, websitePlatform } = fields;
      let prompt =
        "Generate a comprehensive SEO guide based on the following:\n";
      prompt += "Website Niche: " + websiteNiche + ".\n";
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + ".\n";
      }
      if (websitePlatform) {
        prompt += "Website Platform: " + websitePlatform + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "property-management-report-generator",
    name: "Property Management Report Generator",
    slug: "property-management-report-generator",
    shortDescription:
      "Generate comprehensive property management reports for rental properties easily with our automated report generator.",
    fields: [
      {
        type: "textArea",
        label: "Property Information",
        field: "Property Information",
        placeholder:
          "123 Main St: Tenant John Doe, Rent $1200/month, Maintenance costs $200 last month...",
        required: true,
        optional: false,
        id: "propertyinformation",
      },
      {
        type: "textArea",
        label: "Improvements and Performance",
        field: "Improvements and Performance",
        placeholder: "Property needs and tenant relations",
        required: false,
        optional: true,
        id: "improvementsandperformance",
      },
    ],
    categories: ["Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating property management reports. You will be provided with information about a rental property and you will generate a comprehensive report based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { propertyInformation, improvementsAndPerformance } = fields;
      let prompt =
        "Generate a property management report based on the following:\n";
      prompt += "Property Information: " + propertyInformation + "\n";
      if (improvementsAndPerformance) {
        prompt +=
          "Improvements and Performance: " + improvementsAndPerformance + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "grant-proposal-writer-for-school-funding",
    name: "Grant Proposal Writer for School Funding",
    slug: "grant-proposal-writer-for-school-funding",
    shortDescription:
      "Generate persuasive grant proposals to secure school funding with our expert grant proposal writer tailored for educational institutions.",
    fields: [
      {
        type: "textArea",
        label: "School Information",
        field: "School Information",
        placeholder: "XYZ Elementary School, located in...",
        required: true,
        optional: false,
        id: "schoolinformation",
      },
      {
        type: "textArea",
        label: "Funding Purpose",
        field: "Funding Purpose",
        placeholder: "The funding will be used to...",
        required: false,
        optional: true,
        id: "fundingpurpose",
      },
      {
        type: "textArea",
        label: "Funding Impact",
        field: "Funding Impact",
        placeholder: "With this funding, we will be able to...",
        required: false,
        optional: true,
        id: "fundingimpact",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing grant proposals for school funding. You will be provided with specific information about a school and its funding needs, and you will generate a detailed and persuasive grant proposal based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { schoolInformation, fundingPurpose, fundingImpact } = fields;
      let prompt = "Generate a grant proposal based on the following:\n";
      prompt += "School Information: " + schoolInformation + "\n";
      if (fundingPurpose) {
        prompt += "Funding Purpose: " + fundingPurpose + "\n";
      }
      if (fundingImpact) {
        prompt += "Funding Impact: " + fundingImpact + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-proposal-writer",
    name: "AI Proposal Writer",
    slug: "ai-proposal-writer",
    shortDescription:
      "Generate professional proposals effortlessly with our AI Proposal Writer, tailored to your project's description and goals.",
    fields: [
      {
        type: "textArea",
        label: "Project Description",
        field: "Project Description",
        placeholder: "Describe the project in detail...",
        required: true,
        optional: false,
        id: "projectdescription",
      },
      {
        type: "textArea",
        label: "Project Objectives",
        field: "Project Objectives",
        placeholder: "List the objectives of the project...",
        required: false,
        optional: true,
        id: "projectobjectives",
      },
      {
        type: "textArea",
        label: "Project Benefits",
        field: "Project Benefits",
        placeholder: "Describe the benefits and impact of the project...",
        required: false,
        optional: true,
        id: "projectbenefits",
      },
    ],
    categories: ["Business", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating professional proposals. You will be provided with details about a project, including its description, objectives, and benefits, and you will create a comprehensive proposal based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { projectDescription, projectObjectives, projectBenefits } = fields;
      let prompt = "Generate a professional proposal based on the following:\n";
      prompt += "Project Description: " + projectDescription + "\n";
      if (projectObjectives) {
        prompt += "Project Objectives: " + projectObjectives + "\n";
      }
      if (projectBenefits) {
        prompt += "Project Benefits: " + projectBenefits + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "romantic-story-generator",
    name: "Romantic Story Generator",
    slug: "romantic-story-generator",
    shortDescription:
      "Create captivating romantic stories filled with betrayal and intrigue. Engage your readers with our Romantic Story Generator!",
    fields: [
      {
        type: "textArea",
        label: "Character Names",
        field: "Character Names",
        placeholder: "John, Mary",
        required: true,
        optional: false,
        id: "characternames",
      },
      {
        type: "textArea",
        label: "Setting",
        field: "Setting",
        placeholder: "Paris in the 1920s",
        required: false,
        optional: true,
        id: "setting",
      },
      {
        type: "textArea",
        label: "Key Event",
        field: "Key Event",
        placeholder: "A secret romance",
        required: false,
        optional: true,
        id: "keyevent",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a skilled storyteller specializing in romantic narratives. You will be provided with character names, a setting, and a key event to create an engaging romantic story filled with elements of betrayal and intrigue.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { characterNames, setting, keyEvent } = fields;
      let prompt =
        "Generate a captivating romantic story based on the following:\n";
      prompt += "Character Names: " + characterNames + ".\n";
      if (setting) {
        prompt += "Setting: " + setting + ".\n";
      }
      if (keyEvent) {
        prompt += "Key Event: " + keyEvent + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "valentines-day-card-creator",
    name: "Valentine's Day Card Creator",
    slug: "valentines-day-card-creator",
    shortDescription:
      "Create personalized Valentine's Day cards with heartfelt messages tailored to your loved one's interests and your unique relationship.",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Interests",
        field: "Recipient's Interests",
        placeholder:
          "Loves hiking, enjoys Italian cuisine, fan of romantic comedies",
        required: true,
        optional: false,
        id: "recipientsinterests",
      },
      {
        type: "textArea",
        label: "Nature of Relationship",
        field: "Nature of Relationship",
        placeholder: "Romantic partner, Friend, Family member, Colleague",
        required: false,
        optional: true,
        id: "natureofrelationship",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized Valentine's Day card messages. You will be provided with the recipient's interests and the nature of your relationship, and you will generate a heartfelt message based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientInterests, natureOfRelationship } = fields;
      let prompt =
        "Generate a Valentine's Day card message based on the following:\n";
      prompt += "Recipient's interests: " + recipientInterests + ".\n";
      if (natureOfRelationship) {
        prompt += "Nature of relationship: " + natureOfRelationship + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "mothers-day-card-creator",
    name: "Mother's Day Card Creator",
    slug: "mothers-day-card-creator",
    shortDescription:
      "Create heartfelt Mother's Day cards effortlessly! Personalize your cards using your own notes and ideas with our easy-to-use generator.",
    fields: [
      {
        type: "textArea",
        label: "Notes about Mom",
        field: "Notes about Mom",
        placeholder:
          "She loves gardening, always supported my dreams, memories of bedtime stories...",
        required: true,
        optional: false,
        id: "notesaboutmom",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating heartfelt and personalized Mother's Day cards. You will be provided with notes about the user's mom, and you will generate a touching card message based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { notesAboutMom } = fields;
      let prompt =
        "Generate a Mother's Day card message based on the following:\n";
      prompt += "Notes about Mom: " + notesAboutMom + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-research-tool",
    name: "AI Research Tool",
    slug: "ai-research-tool",
    shortDescription:
      "Discover an AI research tool that helps you find and summarize information from web and academic resources for any topic.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic or Question",
        field: "Research Topic or Question",
        placeholder: "",
        required: true,
        optional: false,
        id: "researchtopicorquestion",
      },
    ],
    categories: ["Education", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert research assistant. You will be provided with a research topic or question, and your task is to find and summarize relevant information from web and academic resources.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { ResearchTopicOrQuestion } = fields;
      let prompt =
        "Conduct comprehensive research based on the following topic or question:\n";
      prompt +=
        "Research Topic or Question: " + ResearchTopicOrQuestion + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-point-generator",
    name: "Essay Point Generator",
    slug: "essay-point-generator",
    shortDescription:
      "Generate four key points for your essay effortlessly with our Essay Point Generator based on your topic and perspective.",
    fields: [
      {
        type: "textArea",
        label: "Essay Topic",
        field: "Essay Topic",
        placeholder: "The Impact of Technology on Society",
        required: true,
        optional: false,
        id: "essaytopic",
      },
      {
        type: "textArea",
        label: "Perspective",
        field: "Perspective",
        placeholder: "Technology has overall positive effects on society",
        required: false,
        optional: true,
        id: "perspective",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating key points for essays. You will be provided with a topic and a perspective, and you will generate four key points that support the given perspective.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayTopic, perspective } = fields;
      let prompt =
        "Generate four key points for an essay based on the following:\n";
      prompt += "Essay Topic: " + essayTopic + ".\n";
      if (perspective) {
        prompt += "Perspective: " + perspective + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-book-summary-generator",
    name: "AI Book Summary Generator",
    slug: "ai-book-summary-generator",
    shortDescription:
      "Generate concise book summaries with our AI tool, highlighting key themes, characters, and plot points for quick insights.",
    fields: [
      {
        type: "textArea",
        label: "Book Title",
        field: "Book Title",
        placeholder: "To Kill a Mockingbird",
        required: true,
        optional: false,
        id: "booktitle",
      },
      {
        type: "textArea",
        label: "Author",
        field: "Author",
        placeholder: "Harper Lee",
        required: false,
        optional: true,
        id: "author",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing books. You will be provided with the title and author of a book, and you will generate a concise summary highlighting key themes, characters, and plot points.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { bookTitle, author } = fields;
      let prompt = "Generate a concise summary for the following book:\n";
      prompt += "Book Title: " + bookTitle + "\n";
      if (author) {
        prompt += "Author: " + author + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-roast",
    name: "AI Roast",
    slug: "ai-roast",
    shortDescription:
      "Generate a lighthearted roast with AI Roast! Just provide a name and details for a fun, personalized roast experience.",
    fields: [
      {
        type: "textArea",
        label: "Person's Name",
        field: "Person's Name",
        placeholder: "John Doe",
        required: true,
        optional: false,
        id: "personsname",
      },
      {
        type: "textArea",
        label: "Details about Person",
        field: "Details about Person",
        placeholder: "Works in tech, loves cats, can't cook",
        required: false,
        optional: true,
        id: "detailsaboutperson",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a comedian specializing in lighthearted roasts. You will be provided with a person's name and some details about them, and you will generate a funny roast based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { personName, detailsAboutPerson } = fields;
      let prompt = "Generate a lighthearted roast based on the following:\n";
      prompt += "Person's Name: " + personName + ".\n";
      if (detailsAboutPerson) {
        prompt += "Details about the person: " + detailsAboutPerson + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "position-paper-writer",
    name: "Position Paper Writer",
    slug: "position-paper-writer",
    shortDescription:
      "Create a detailed position paper effortlessly with our writer, tailored to your topic, stance, and key points for maximum impact.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Climate Change",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Stance",
        field: "Stance",
        placeholder: "In favor of stricter environmental regulations",
        required: false,
        optional: true,
        id: "stance",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. Impact of climate change on global temperatures\n2. Role of human activities in climate change\n3. Benefits of stricter environmental regulations",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing position papers. You will be provided with a topic, stance, and key points, and you will generate a comprehensive position paper based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, Stance, KeyPoints } = fields;
      let prompt = "Generate a position paper based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (Stance) {
        prompt += "Stance: " + Stance + "\n";
      }
      if (KeyPoints) {
        prompt += "Key Points: " + KeyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "business-proposal-generator",
    name: "Business Proposal Generator",
    slug: "business-proposal-generator",
    shortDescription:
      "Create professional and persuasive business proposals effortlessly with our easy-to-use Business Proposal Generator.",
    fields: [
      {
        type: "textArea",
        label: "Project Details",
        field: "Project Details",
        placeholder:
          "We are proposing to design and develop a new website for...",
        required: true,
        optional: false,
        id: "projectdetails",
      },
      {
        type: "textArea",
        label: "Client Details",
        field: "Client Details",
        placeholder: "ABC Company is a leading provider of...",
        required: false,
        optional: true,
        id: "clientdetails",
      },
      {
        type: "textArea",
        label: "Budget and/or Timeline Details",
        field: "Budget and/or Timeline Details",
        placeholder: "The total budget for this project is...",
        required: false,
        optional: true,
        id: "budgetand/ortimelinedetails",
      },
    ],
    categories: ["Business", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating business proposals. You will be provided with project details, client information, and budget/timeline details to create a professional and persuasive business proposal.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { projectDetails, clientDetails, budgetAndTimelineDetails } =
        fields;
      let prompt = "Generate a business proposal based on the following:\n";
      prompt += "Project Details: " + projectDetails + "\n";
      if (clientDetails) {
        prompt += "Client Details: " + clientDetails + "\n";
      }
      if (budgetAndTimelineDetails) {
        prompt +=
          "Budget and/or Timeline Details: " + budgetAndTimelineDetails + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "conference-abstract-generator",
    name: "Conference Abstract Generator",
    slug: "conference-abstract-generator",
    shortDescription:
      "Generate concise and informative abstracts for academic and professional conferences with our easy-to-use Conference Abstract Generator.",
    fields: [
      {
        type: "textArea",
        label: "Title of Research",
        field: "Title of Research",
        placeholder: "The Impact of AI on Climate Change Mitigation",
        required: true,
        optional: false,
        id: "titleofresearch",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. AI's role in predicting climate change patterns...\n2. Case studies of AI in climate change mitigation...",
        required: false,
        optional: true,
        id: "keypoints",
      },
      {
        type: "textArea",
        label: "Conclusion",
        field: "Conclusion",
        placeholder: "AI has demonstrated significant potential in...",
        required: false,
        optional: true,
        id: "conclusion",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating conference abstracts. You will be provided with the title of research, key points, and conclusion, and you will create a concise and informative abstract suitable for academic or professional conference submissions.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { TitleOfResearch, KeyPoints, Conclusion } = fields;
      let prompt = "Generate a conference abstract based on the following:\n";
      prompt += "Title of Research: " + TitleOfResearch + "\n";
      if (KeyPoints) {
        prompt += "Key Points: " + KeyPoints + "\n";
      }
      if (Conclusion) {
        prompt += "Conclusion: " + Conclusion + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "etymology-assistant",
    name: "Etymology Assistant",
    slug: "etymology-assistant",
    shortDescription:
      "Explore the origins and history of words with our Etymology Assistant, uncovering meanings and connections in language.",
    fields: [
      {
        type: "textArea",
        label: "Word or Phrase",
        field: "Word or Phrase",
        placeholder: "Serendipity",
        required: true,
        optional: false,
        id: "wordorphrase",
      },
    ],
    categories: ["Education", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in etymology. You will be provided with a word or phrase, and your task is to find and explain its origin and history.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { wordOrPhrase } = fields;
      let prompt =
        "Find the origin and history of the following word or phrase:\n";
      prompt += "Word or Phrase: " + wordOrPhrase + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-study-buddy",
    name: "AI Study Buddy",
    slug: "ai-study-buddy",
    shortDescription:
      "Boost your studies with AI Study Buddy, providing detailed explanations and resources for college students on various topics.",
    fields: [
      {
        type: "textArea",
        label: "Topic or Question",
        field: "Topic or Question",
        placeholder: "Explain the Pythagorean Theorem",
        required: true,
        optional: false,
        id: "topicorquestion",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI Study Buddy, designed to assist college students in understanding various topics. You will be provided with a specific topic or question, and you will generate detailed explanations and resources related to that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicOrQuestion } = fields;
      let prompt =
        "Provide a detailed explanation and resources for the following topic or question:\n";
      prompt += "Topic/Question: " + topicOrQuestion + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "english-idiom-interpreter",
    name: "English Idiom Interpreter",
    slug: "english-idiom-interpreter",
    shortDescription:
      "Unlock the meaning behind English idioms with our interpreter, offering clear and literal explanations for better understanding.",
    fields: [
      {
        type: "textArea",
        label: "Idiom",
        field: "Idiom",
        placeholder: "Bite the bullet",
        required: true,
        optional: false,
        id: "idiom",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in interpreting English idioms. You will be provided with an idiom, and your task is to provide a literal explanation of its meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Idiom } = fields;
      let prompt =
        "Provide a literal explanation of the following English idiom:\n";
      prompt += "Idiom: " + Idiom + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "journalistic-ai",
    name: "Journalistic AI",
    slug: "journalistic-ai",
    shortDescription:
      "Generate comprehensive news articles effortlessly with Journalistic AI by inputting your topic and keywords for instant results.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Climate Change",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Keywords",
        field: "Keywords",
        placeholder: "Global warming, Carbon emissions, Renewable energy",
        required: false,
        optional: true,
        id: "keywords",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating comprehensive news articles. You will be provided with a topic and keywords, and you will create a detailed article based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, Keywords } = fields;
      let prompt =
        "Generate a comprehensive news article based on the following:\n";
      prompt += "Topic: " + Topic + ".\n";
      if (Keywords) {
        prompt += "Keywords: " + Keywords + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "homework-ai",
    name: "Homework AI",
    slug: "homework-ai",
    shortDescription:
      "Enhance your learning experience with Homework AI, the ultimate assistant for students to tackle homework effectively.",
    fields: [
      {
        type: "textArea",
        label: "Homework Question",
        field: "Homework Question",
        placeholder: "What is the Pythagorean theorem?",
        required: true,
        optional: false,
        id: "homeworkquestion",
      },
    ],
    categories: ["Education", "Writing", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI assistant designed to help students with their homework. You will be provided with a homework question, and you will generate a detailed and informative response to assist the student in understanding the topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { homeworkQuestion } = fields;
      let prompt =
        "Provide a detailed explanation for the following homework question:\n";
      prompt += "Homework Question: " + homeworkQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "interview-follow-up-email-writer",
    name: "Interview Follow Up Email Writer",
    slug: "interview-follow-up-email-writer",
    shortDescription:
      "Automatically craft a professional interview follow-up email with our AI writer. Enhance your chances of success effortlessly!",
    fields: [
      {
        type: "textArea",
        label: "Job Title",
        field: "Job Title",
        placeholder: "Software Engineer",
        required: true,
        optional: false,
        id: "jobtitle",
      },
      {
        type: "textArea",
        label: "Company Name",
        field: "Company Name",
        placeholder: "ABC Corp",
        required: false,
        optional: true,
        id: "companyname",
      },
      {
        type: "textArea",
        label: "Interviewer's Name",
        field: "Interviewer's Name",
        placeholder: "John Doe",
        required: false,
        optional: true,
        id: "interviewersname",
      },
    ],
    categories: ["Email", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing professional follow-up emails after interviews. You will be provided with details about the job, company, and interviewer, and you will generate a polished follow-up email based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { jobTitle, companyName, interviewersName } = fields;
      let prompt = "Generate a follow-up email based on the following:\n";
      prompt += "Job Title: " + jobTitle + ".\n";
      if (companyName) {
        prompt += "Company Name: " + companyName + ".\n";
      }
      if (interviewersName) {
        prompt += "Interviewer's Name: " + interviewersName + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "seminar-script-writer",
    name: "Seminar Script Writer",
    slug: "seminar-script-writer",
    shortDescription:
      "Create engaging seminar scripts effortlessly! Tailor scripts to your topic, audience, and key points for impactful presentations.",
    fields: [
      {
        type: "textArea",
        label: "Seminar Topic",
        field: "Seminar Topic",
        placeholder: "The Impact of AI on Modern Businesses",
        required: true,
        optional: false,
        id: "seminartopic",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Business owners and managers",
        required: false,
        optional: true,
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. What is AI? 2. How is AI being used in businesses today? 3. The potential impact of AI on future businesses.",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating seminar scripts. You will be provided with a seminar topic, target audience, and key points, and you will generate a comprehensive script based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { seminarTopic, targetAudience, keyPoints } = fields;
      let prompt = "Generate a seminar script based on the following:\n";
      prompt += "Seminar Topic: " + seminarTopic + ".\n";
      if (targetAudience) {
        prompt += "Target Audience: " + targetAudience + ".\n";
      }
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "email-subject-line-ab-tester",
    name: "Email Subject Line A/B Tester",
    slug: "email-subject-line-ab-tester",
    shortDescription:
      "Boost your email campaigns with our A/B tester that generates diverse subject lines tailored to your content for optimal engagement.",
    fields: [
      {
        type: "textArea",
        label: "Email Content",
        field: "Email Content",
        placeholder: "Dear Customer, We are excited to announce...",
        required: true,
        optional: false,
        id: "emailcontent",
      },
    ],
    categories: ["Email", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating email subject lines for A/B testing. You will be provided with the content of an email, and you will generate a variety of engaging subject lines that can be used for testing.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { emailContent } = fields;
      let prompt =
        "Generate a variety of email subject lines based on the following content:\n";
      prompt += "Email Content: " + emailContent + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "prompt-response-generator",
    name: "Prompt Response Generator",
    slug: "prompt-response-generator",
    shortDescription:
      "Generate comprehensive, relevant responses effortlessly with our Prompt Response Generator for all your creative needs.",
    fields: [
      {
        type: "textArea",
        label: "Prompt",
        field: "Prompt",
        placeholder: "Describe the impact of climate change on polar bears.",
        required: true,
        optional: false,
        id: "prompt",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI trained to generate comprehensive and relevant responses to various prompts. You will be provided with a specific prompt, and your task is to create a detailed response that addresses the topic effectively.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Prompt } = fields.Prompt;
      let prompt = "Generate a response based on the following prompt:\n";
      prompt += "Prompt: " + Prompt + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "proverbs-ai-generator",
    name: "Proverbs AI Generator",
    slug: "proverbs-ai-generator",
    shortDescription:
      "Create unique and creative proverbs tailored to your themes with our innovative Proverbs AI Generator.",
    fields: [
      {
        type: "textArea",
        label: "Theme or Keyword",
        field: "Theme or Keyword",
        placeholder: "Patience",
        required: true,
        optional: false,
        id: "themeorkeyword",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating unique and creative proverbs. You will be provided with a theme or keyword, and you will generate a proverb based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { themeOrKeyword } = fields;
      let prompt =
        "Generate a unique proverb based on the following theme or keyword:\n";
      prompt += "Theme or Keyword: " + themeOrKeyword + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "combine-two-paragraphs",
    name: "Combine Two Paragraphs",
    slug: "combine-two-paragraphs",
    shortDescription:
      "Learn how to merge two paragraphs into one clear, coherent, and grammatically correct paragraph effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Paragraph 1",
        field: "Paragraph 1",
        placeholder: "The cat sat on the mat. It was a sunny day...",
        required: true,
        optional: false,
        id: "paragraph1",
      },
      {
        type: "textArea",
        label: "Paragraph 2",
        field: "Paragraph 2",
        placeholder:
          "The dog barked at the cat. The sun was shining brightly...",
        required: false,
        optional: true,
        id: "paragraph2",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in combining paragraphs. You will be provided with two paragraphs, and your task is to merge them into one coherent and grammatically correct paragraph.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { paragraph1, paragraph2 } = fields;
      let prompt =
        "Combine the following two paragraphs into one coherent paragraph:\n";
      prompt += "Paragraph 1: " + paragraph1 + "\n";
      if (paragraph2) {
        prompt += "Paragraph 2: " + paragraph2 + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "writing-critique-ai",
    name: "Writing Critique AI",
    slug: "writing-critique-ai",
    shortDescription:
      "Get detailed feedback on your writing with Writing Critique AI, covering grammar, style, and content for better results.",
    fields: [
      {
        type: "textArea",
        label: "Writing Sample",
        field: "Writing Sample",
        placeholder: "Once upon a time...",
        required: true,
        optional: false,
        id: "writingsample",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert writing critique assistant. You will be provided with a piece of writing, and your task is to provide a comprehensive critique that includes feedback on grammar, style, and content.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingSample } = fields;
      let prompt =
        "Please provide a critique for the following writing sample:\n";
      prompt += "Writing Sample: " + writingSample + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "thesis-statement-builder",
    name: "Thesis Statement Builder",
    slug: "thesis-statement-builder",
    shortDescription:
      "Craft clear and focused thesis statements for your research papers and essays with our Thesis Statement Builder tool.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "The Effects of Climate Change",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "Rising global temperatures, Melting ice caps, Increased frequency of natural disasters",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting thesis statements. You will be provided with a topic and key points, and you will generate a clear and focused thesis statement based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, KeyPoints } = fields;
      let prompt = "Generate a thesis statement based on the following:\n";
      prompt += "Topic: " + Topic + ".\n";
      if (KeyPoints) {
        prompt += "Key Points: " + KeyPoints + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "valentine-creator",
    name: "Valentine Creator",
    slug: "valentine-creator",
    shortDescription:
      "Create personalized Valentine's Day messages tailored to your loved one's interests and your unique relationship.",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Interests",
        field: "Recipient's Interests",
        placeholder: "Reading, hiking, cooking Italian food",
        required: true,
        optional: false,
        id: "recipientsinterests",
      },
      {
        type: "textArea",
        label: "Nature of Relationship",
        field: "Nature of Relationship",
        placeholder: "Romantic partner, friend, family member",
        required: false,
        optional: true,
        id: "natureofrelationship",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized Valentine's Day messages. You will be provided with the recipient's interests and the nature of your relationship, and you will create a heartfelt message based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientInterests, natureOfRelationship } = fields;
      let prompt =
        "Generate a personalized Valentine's Day message based on the following:\n";
      prompt += "Recipient's interests: " + recipientInterests + ".\n";
      if (natureOfRelationship) {
        prompt += "Nature of relationship: " + natureOfRelationship + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "literature-review-ai-assistant",
    name: "Literature Review AI Assistant",
    slug: "literature-review-ai-assistant",
    shortDescription:
      "Streamline your literature review with our AI assistant. Identify resources and summarize key points effortlessly for effective research.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic ",
        field: "Research Topic ",
        placeholder:
          "Artificial Intelligence in Healthcare with a Focus on Radiology",
        required: true,
        optional: false,
        id: "researchtopic",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in conducting literature reviews. You will be provided with a research topic and will assist in identifying relevant resources and summarizing key points.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic } = fields;
      let prompt =
        "Conduct a literature review based on the following research topic:\n";
      prompt += "Research Topic: " + researchTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "world-war-ii-history-explorer",
    name: "World War II History Explorer",
    slug: "world-war-ii-history-explorer",
    shortDescription:
      "Explore comprehensive World War II history with tailored information based on your specific queries and interests.",
    fields: [
      {
        type: "textArea",
        label: "World War II Query",
        field: "World War II Query",
        placeholder: "Battle of Stalingrad",
        required: true,
        optional: false,
        id: "worldwariiquery",
      },
    ],
    categories: ["Education", "Writing", "History"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in World War II history. You will be provided with specific queries related to World War II, and you will provide comprehensive information based on those queries.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { worldWarIIQuery } = fields;
      let prompt =
        "Provide detailed information based on the following query:\n";
      prompt += "World War II Query: " + worldWarIIQuery + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "write-discussion-post-with-ai",
    name: "Write Discussion Post with AI",
    slug: "write-discussion-post-with-ai",
    shortDescription:
      "Create engaging discussion posts effortlessly with AI, tailored to your topics and guidelines for maximum relevance and impact.",
    fields: [
      {
        type: "textArea",
        label: "Discussion Topic",
        field: "Discussion Topic",
        placeholder: "Effects of Climate Change",
        required: true,
        optional: false,
        id: "discussiontopic",
      },
      {
        type: "textArea",
        label: "Discussion Guidelines",
        field: "Discussion Guidelines",
        placeholder:
          "Please include at least two references and keep the post under 500 words.",
        required: false,
        optional: true,
        id: "discussionguidelines",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating discussion posts. You will be provided with a topic and guidelines, and you will create an engaging discussion post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { discussionTopic, discussionGuidelines } = fields;
      let prompt = "Generate a discussion post based on the following:\n";
      prompt += "Discussion Topic: " + discussionTopic + ".\n";
      if (discussionGuidelines) {
        prompt += "Discussion Guidelines: " + discussionGuidelines + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "valentines-day-card-creator",
    name: "Valentine's Day Card Creator",
    slug: "valentines-day-card-creator",
    shortDescription:
      "Create personalized Valentine's Day cards tailored to your loved one's interests and your unique relationship with our easy-to-use generator.",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Interests",
        field: "Recipient's Interests",
        placeholder:
          "Loves hiking, enjoys cooking Italian food, big fan of romantic comedies",
        required: true,
        optional: false,
        id: "recipientsinterests",
      },
      {
        type: "textArea",
        label: "Your Relationship with Recipient",
        field: "Your Relationship with Recipient",
        placeholder: "Best friend, spouse, crush, etc.",
        required: false,
        optional: true,
        id: "yourrelationshipwithrecipient",
      },
    ],
    categories: ["Writing", "Marketing", "Social Media"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized Valentine's Day cards. You will be provided with details about the recipient's interests and your relationship with them, and you will generate a heartfelt message for the card.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientInterests, relationship } = fields;
      let prompt =
        "Generate a Valentine's Day card message based on the following:\n";
      prompt += "Recipient's Interests: " + recipientInterests + ".\n";
      if (relationship) {
        prompt += "Your Relationship with Recipient: " + relationship + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "wedding-speech-writer",
    name: "Wedding Speech Writer",
    slug: "wedding-speech-writer",
    shortDescription:
      "Create a heartfelt, personalized wedding speech effortlessly with our Wedding Speech Writer, tailored to celebrate your unique love story.",
    fields: [
      {
        type: "textArea",
        label: "Couple's Story",
        field: "Couple's Story",
        placeholder: "John and Jane met in college...",
        required: true,
        optional: false,
        id: "couplesstory",
      },
      {
        type: "textArea",
        label: "Speaker's Personal Anecdote",
        field: "Speaker's Personal Anecdote",
        placeholder: "I remember when John first told me about Jane...",
        required: false,
        optional: true,
        id: "speakerspersonalanecdote",
      },
      {
        type: "textArea",
        label: "Well Wishes",
        field: "Well Wishes",
        placeholder: "May your life together be filled with...",
        required: false,
        optional: true,
        id: "wellwishes",
      },
    ],
    categories: ["Writing", "Social Media", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating heartfelt and personalized wedding speeches. You will be provided with information about the couple and the speaker's personal anecdotes, and you will create a touching wedding speech based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { coupleStory, personalAnecdote, wellWishes } = fields;
      let prompt = "Generate a wedding speech based on the following:\n";
      prompt += "Couple's story: " + coupleStory + "\n";
      if (personalAnecdote) {
        prompt += "Speaker's personal anecdote: " + personalAnecdote + "\n";
      }
      if (wellWishes) {
        prompt += "Well wishes: " + wellWishes + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "cover-letter-generator-ai",
    name: "Cover Letter Generator AI",
    slug: "cover-letter-generator-ai",
    shortDescription:
      "Create a standout cover letter effortlessly with our AI generator, tailored to your job description and personal details.",
    fields: [
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "XYZ Corp is looking for a software engineer with...",
        required: true,
        optional: false,
        id: "jobdescription",
      },
      {
        type: "textArea",
        label: "Personal Information",
        field: "Personal Information",
        placeholder: "I am a software engineer with 5 years of experience...",
        required: false,
        optional: true,
        id: "personalinformation",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating cover letters. You will be provided with a job description and personal information, and you will create a personalized and professional cover letter based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { jobDescription, personalInformation } = fields;
      let prompt = "Generate a cover letter based on the following:\n";
      prompt += "Job Description: " + jobDescription + "\n";
      if (personalInformation) {
        prompt += "Personal Information: " + personalInformation + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "topic-specific-vocabulary-builder",
    name: "Topic-Specific Vocabulary Builder",
    slug: "topic-specific-vocabulary-builder",
    shortDescription:
      "Enhance your knowledge with our topic-specific vocabulary builder, featuring curated lists of relevant words for any subject.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Environmental Science",
        required: true,
        optional: false,
        id: "topic",
      },
    ],
    categories: ["Education", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating topic-specific vocabulary lists. You will be provided with a specific topic, and you will create a list of relevant vocabulary words associated with that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic } = fields;
      let prompt =
        "Generate a list of relevant vocabulary words based on the following topic:\n";
      prompt += "Topic: " + Topic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "holiday-card-creator",
    name: "Holiday Card Creator",
    slug: "holiday-card-creator",
    shortDescription:
      "Create personalized holiday cards tailored to your loved ones' interests and your unique relationship for a heartfelt touch.",
    fields: [
      {
        type: "textArea",
        label: "Holiday",
        field: "Holiday",
        placeholder: "Christmas",
        required: true,
        optional: false,
        id: "holiday",
      },
      {
        type: "textArea",
        label: "Your Relationship with Recipient",
        field: "Your Relationship with Recipient",
        placeholder: "My best friend",
        required: false,
        optional: true,
        id: "yourrelationshipwithrecipient",
      },
      {
        type: "textArea",
        label: "Recipient's Interests",
        field: "Recipient's Interests",
        placeholder: "Loves skiing and baking",
        required: false,
        optional: true,
        id: "recipientsinterests",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized holiday cards. You will be provided with details about the holiday, your relationship with the recipient, and their interests to generate a unique holiday card message.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Holiday, YourRelationshipWithRecipient, RecipientsInterests } =
        fields;
      let prompt =
        "Generate a personalized holiday card message based on the following:\n";
      prompt += "Holiday: " + Holiday + ".\n";
      if (YourRelationshipWithRecipient) {
        prompt +=
          "Your relationship with the recipient: " +
          YourRelationshipWithRecipient +
          ".\n";
      }
      if (RecipientsInterests) {
        prompt += "Recipient's interests: " + RecipientsInterests + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "senior-friendly-blog-creator",
    name: "Senior-friendly Blog Creator",
    slug: "senior-friendly-blog-creator",
    shortDescription:
      "Create engaging, senior-friendly blog content easily! Tailored topics for seniors that are easy to read and relevant.",
    fields: [
      {
        type: "textArea",
        label: "Blog Topic",
        field: "Blog Topic",
        placeholder: "Technology for Seniors",
        required: true,
        optional: false,
        id: "blogtopic",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "How to best learn about the technology I need to improve my daily life",
        required: false,
        optional: true,
        id: "keypoints",
      },
      {
        type: "textArea",
        label: "Blog Length",
        field: "Blog Length",
        placeholder: "5 Paragraphs",
        required: false,
        optional: true,
        id: "bloglength",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating blog content that is engaging and easy to read for senior citizens. You will be provided with a topic and key points, and you will generate a blog post tailored to the needs and interests of seniors.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { blogTopic, keyPoints, blogLength } = fields;
      let prompt = "Generate a blog post based on the following:\n";
      prompt += "Blog Topic: " + blogTopic + ".\n";
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + ".\n";
      }
      if (blogLength) {
        prompt += "Desired Length: " + blogLength + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-generator",
    name: "Resume Generator",
    slug: "resume-generator",
    shortDescription:
      "Transform your work notes into a professional resume with our AI-powered Resume Generator for high-quality results.",
    fields: [
      {
        type: "textArea",
        label: "Education Notes",
        field: "Education Notes",
        placeholder: "Graduated with a B.Sc in Computer Science from...",
        required: true,
        optional: false,
        id: "educationnotes",
      },
      {
        type: "textArea",
        label: "Work Experience Notes",
        field: "Work Experience Notes",
        placeholder: "Worked as a software engineer at XYZ Corp...",
        required: false,
        optional: true,
        id: "workexperiencenotes",
      },
      {
        type: "textArea",
        label: "Skills and Other Notes",
        field: "Skills and Other Notes",
        placeholder: "Proficient in Java, Python, C++...",
        required: false,
        optional: true,
        id: "skillsandothernotes",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating professional resumes. You will be provided with notes about education, work experience, and skills, and you will generate a high-quality resume based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { educationNotes, workExperienceNotes, skillsAndOtherNotes } =
        fields;
      let prompt = "Generate a professional resume based on the following:\n";
      prompt += "Education: " + educationNotes + "\n";
      if (workExperienceNotes) {
        prompt += "Work Experience: " + workExperienceNotes + "\n";
      }
      if (skillsAndOtherNotes) {
        prompt += "Skills: " + skillsAndOtherNotes + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writing-research-paper",
    name: "AI Writing Research Paper",
    slug: "ai-writing-research-paper",
    shortDescription:
      "Enhance your research papers with AI: get structured content and comprehensive information for a coherent, high-quality paper.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic",
        field: "Research Topic",
        placeholder: "The Impact of Climate Change on Biodiversity",
        required: true,
        optional: false,
        id: "researchtopic",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing research papers. You will be provided with a research topic and you will generate a comprehensive outline and content for a research paper based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic } = fields;
      let prompt = "Generate a research paper based on the following:\n";
      prompt += "Research Topic: " + researchTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "discussion-reply-crafter",
    name: "Discussion Reply Crafter",
    slug: "discussion-reply-crafter",
    shortDescription:
      "Effortlessly craft thoughtful replies to discussion board posts, addressing key points and boosting engagement with Discussion Reply Crafter.",
    fields: [
      {
        type: "textArea",
        label: "Discussion Post",
        field: "Discussion Post",
        placeholder: "In this week's reading, I found the concept of...",
        required: true,
        optional: false,
        id: "discussionpost",
      },
    ],
    categories: ["Education", "Writing", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting thoughtful replies to discussion board posts. You will be provided with a discussion post, and your task is to generate a reply that addresses key points and fosters engagement.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { discussionPost } = fields;
      let prompt =
        "Generate a thoughtful reply to the following discussion post:\n";
      prompt += "Discussion Post: " + discussionPost + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-literature-analysis-assistant",
    name: "AI Literature Analysis Assistant",
    slug: "ai-literature-analysis-assistant",
    shortDescription:
      "Generate insightful literary analyses with our AI assistant, exploring themes, character development, and symbolic interpretations.",
    fields: [
      {
        type: "textArea",
        label: "Literary Work",
        field: "Literary Work",
        placeholder: "To Kill a Mockingbird",
        required: true,
        optional: false,
        id: "literarywork",
      },
      {
        type: "textArea",
        label: "Specific Analysis Focus",
        field: "Specific Analysis Focus",
        placeholder: "Symbolism",
        required: false,
        optional: true,
        id: "specificanalysisfocus",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in literary analysis. You will be provided with a literary work and specific focus areas for analysis, and you will generate insightful analyses based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { literaryWork, specificAnalysisFocus } = fields;
      let prompt = "Generate an analysis of the following literary work:\n";
      prompt += "Literary Work: " + literaryWork + "\n";
      if (specificAnalysisFocus) {
        prompt += "Specific Analysis Focus: " + specificAnalysisFocus + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "activity-planner",
    name: "Activity Planner",
    slug: "activity-planner",
    shortDescription:
      "Generate tailored activity lists for your city and group dynamics with our innovative Activity Planner for unforgettable experiences.",
    fields: [
      {
        type: "textArea",
        label: "City",
        field: "City",
        placeholder: "New York City",
        required: true,
        optional: false,
        id: "city",
      },
      {
        type: "textArea",
        label: "Group Details",
        field: "Group Details",
        placeholder:
          "4 adults, enjoy outdoor activities, looking for fun and adventure",
        required: false,
        optional: true,
        id: "groupdetails",
      },
    ],
    categories: ["Business", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in planning activities based on location and group dynamics. You will be provided with a city and details about the group, and you will generate a list of suitable activities.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { City, GroupDetails } = fields;
      let prompt =
        "Generate a list of suitable activities based on the following:\n";
      prompt += "City: " + City + ".\n";
      if (GroupDetails) {
        prompt += "Group Details: " + GroupDetails + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "lab-report-writer",
    name: "Lab Report Writer",
    slug: "lab-report-writer",
    shortDescription:
      "Effortlessly create structured lab reports with our Lab Report Writer, utilizing experimental data and scientific methods for accuracy.",
    fields: [
      {
        type: "textArea",
        label: "Experiment Details",
        field: "Experiment Details",
        placeholder: "Details about the experiment performed...",
        required: true,
        optional: false,
        id: "experimentdetails",
      },
      {
        type: "textArea",
        label: "Data",
        field: "Data",
        placeholder: "Data collected from the experiment...",
        required: false,
        optional: true,
        id: "data",
      },
      {
        type: "textArea",
        label: "Scientific Methods",
        field: "Scientific Methods",
        placeholder: "Scientific methods used in the experiment...",
        required: false,
        optional: true,
        id: "scientificmethods",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing lab reports. You will be provided with details about an experiment, including the data collected and the scientific methods used, and you will generate a structured lab report based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { experimentDetails, data, scientificMethods } = fields;
      let prompt = "Generate a lab report based on the following:\n";
      prompt += "Experiment Details: " + experimentDetails + "\n";
      if (data) {
        prompt += "Data: " + data + "\n";
      }
      if (scientificMethods) {
        prompt += "Scientific Methods: " + scientificMethods + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "pareto-learning-assistant",
    name: "Pareto Learning Assistant",
    slug: "pareto-learning-assistant",
    shortDescription:
      "Discover Pareto Learning Assistant: grasp 80% of any topic by focusing on the key 20% of crucial information for effective learning.",
    fields: [
      {
        type: "textArea",
        label: "Learning Topic",
        field: "Learning Topic",
        placeholder: "Quantum Physics",
        required: true,
        optional: false,
        id: "learningtopic",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in identifying and summarizing key information. You will be provided with a learning topic, and your task is to extract and present the most crucial 20% of information that will provide an understanding of 80% of the subject matter.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { learningTopic } = fields;
      let prompt =
        "Identify and summarize the most crucial 20% of information on the following topic:\n";
      prompt += "Learning Topic: " + learningTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-discussion-board-responder",
    name: "AI Discussion Board Responder",
    slug: "ai-discussion-board-responder",
    shortDescription:
      "Generate engaging responses for discussion boards effortlessly with our AI Discussion Board Responder, enhancing user interaction and ideas.",
    fields: [
      {
        type: "textArea",
        label: "Original Post or Comment",
        field: "Original Post or Comment",
        placeholder: "I think AI will replace all jobs in the future...",
        required: true,
        optional: false,
        id: "originalpostorcomment",
      },
      {
        type: "textArea",
        label: "Ideas for Response",
        field: "Ideas for Response",
        placeholder: "Mention how AI can create new jobs, give examples...",
        required: false,
        optional: true,
        id: "ideasforresponse",
      },
    ],
    categories: ["Social Media", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thoughtful and engaging responses for discussion boards. You will be provided with an original post or comment along with ideas for a response, and you will generate a well-crafted reply based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalPost, ideasForResponse } = fields;
      let prompt =
        "Generate a response for the discussion board based on the following:\n";
      prompt += "Original Post or Comment: " + originalPost + "\n";
      if (ideasForResponse) {
        prompt += "Ideas for Response: " + ideasForResponse + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-discussion-post-generator",
    name: "AI Discussion Post Generator",
    slug: "ai-discussion-post-generator",
    shortDescription:
      "Generate engaging discussion posts effortlessly with our AI tool, tailored to your topic and key points for meaningful conversations.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Impact of Global Warming",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. Greenhouse gases\n2. Melting of polar ice caps\n3. Impact on wildlife",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Social Media", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating discussion posts. You will be provided with a topic and key points, and you will generate a thoughtful and engaging discussion post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, KeyPoints } = fields;
      let prompt = "Generate a discussion post based on the following:\n";
      prompt += "The topic is: " + Topic + ".\n";
      if (KeyPoints) {
        prompt += "Key points to consider: " + KeyPoints + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "okr-planner-and-tracker",
    name: "OKR Planner and Tracker",
    slug: "okr-planner-and-tracker",
    shortDescription:
      "Effortlessly plan and track your Objectives and Key Results (OKRs) with our OKR Planner and Tracker for maximum effectiveness.",
    fields: [
      {
        type: "textArea",
        label: "Objective",
        field: "Objective",
        placeholder:
          "Establish a strong market presence and lay the foundation for growth",
        required: true,
        optional: false,
        id: "objective",
      },
      {
        type: "textArea",
        label: "Key Results",
        field: "Key Results",
        placeholder:
          "Achieve 1000+ active users; Secure at least 2 significant B2B partnerships; Attain a Net Promoter Score (NPS) of 40+",
        required: false,
        optional: true,
        id: "keyresults",
      },
      {
        type: "textArea",
        label: "Timeframe",
        field: "Timeframe",
        placeholder: "Q1, Q2, M1, M2, Y1, Y2 etc.",
        required: false,
        optional: true,
        id: "timeframe",
      },
    ],
    categories: ["Business", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in helping users plan and track their Objectives and Key Results (OKRs). You will be provided with specific objectives, key results, and timeframes, and you will generate a structured OKR plan based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Objective, KeyResults, Timeframe } = fields;
      let prompt = "Generate an OKR plan based on the following:\n";
      prompt += "Objective: " + Objective + "\n";
      if (KeyResults) {
        prompt += "Key Results: " + KeyResults + "\n";
      }
      if (Timeframe) {
        prompt += "Timeframe: " + Timeframe + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-debate-argument-generator",
    name: "AI Debate Argument Generator",
    slug: "ai-debate-argument-generator",
    shortDescription:
      "Generate strong arguments and counterarguments for any debate topic with our AI Debate Argument Generator, complete with evidence.",
    fields: [
      {
        type: "textArea",
        label: "Debate Topic",
        field: "Debate Topic",
        placeholder: "Should schools require uniforms?",
        required: true,
        optional: false,
        id: "debatetopic",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating debate arguments. You will be provided with a debate topic and you will generate strong arguments and counterarguments, complete with supporting evidence and examples.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { debateTopic } = fields;
      let prompt =
        "Generate strong arguments and counterarguments for the following debate topic:\n";
      prompt += "Debate Topic: " + debateTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-writer-ai",
    name: "Essay Writer AI",
    slug: "essay-writer-ai",
    shortDescription:
      "Generate comprehensive essay drafts effortlessly with Essay Writer AI. Advanced AI writes and researches based on your topic and key points.",
    fields: [
      {
        type: "textArea",
        label: "Essay Topic",
        field: "Essay Topic",
        placeholder: "The Impact of Climate Change on Biodiversity",
        required: true,
        optional: false,
        id: "essaytopic",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "1. Causes of climate change\n2. Effects on flora and fauna\n3. Case studies",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in essay writing. You will be provided with a topic and key points, and you will generate a comprehensive essay draft based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayTopic, keyPoints } = fields;
      let prompt =
        "Generate a comprehensive essay draft based on the following:\n";
      prompt += "Essay Topic: " + essayTopic + "\n";
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-answers",
    name: "AI Answers",
    slug: "ai-answers",
    shortDescription:
      "Get accurate answers to your questions with AI Answers, utilizing advanced web search capabilities for reliable information.",
    fields: [
      {
        type: "textArea",
        label: "User Question",
        field: "User Question",
        placeholder: "What is the capital of Australia?",
        required: true,
        optional: false,
        id: "userquestion",
      },
    ],
    categories: ["Support", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI assistant specialized in providing accurate answers to user questions. You will be given a question from the user, and you will generate a precise and informative answer based on your web search capabilities.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userQuestion } = fields;
      let prompt = "Provide an accurate answer to the following question:\n";
      prompt += "User Question: " + userQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "thank-you-note-creator",
    name: "Thank You Note Creator",
    slug: "thank-you-note-creator",
    shortDescription:
      "Create personalized thank you notes for job interviews, gifts, and special occasions with our easy-to-use Thank You Note Creator.",
    fields: [
      {
        type: "textArea",
        label: "Occasion",
        field: "Occasion",
        placeholder: "Job Interview",
        required: true,
        optional: false,
        id: "occasion",
      },
      {
        type: "textArea",
        label: "Recipient's Name",
        field: "Recipient's Name",
        placeholder: "John Doe",
        required: false,
        optional: true,
        id: "recipientsname",
      },
      {
        type: "textArea",
        label: "Additional Details",
        field: "Additional Details",
        placeholder:
          "I really appreciated the opportunity to discuss the role with you...",
        required: false,
        optional: true,
        id: "additionaldetails",
      },
    ],
    categories: ["Writing", "Business", "Email"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting personalized thank you notes. You will be provided with details about the occasion and recipient, and you will generate a thoughtful thank you note based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Occasion, RecipientName, AdditionalDetails } = fields;
      let prompt = "Generate a thank you note based on the following:\n";
      prompt += "Occasion: " + Occasion + ".\n";
      if (RecipientName) {
        prompt += "Recipient's Name: " + RecipientName + ".\n";
      }
      if (AdditionalDetails) {
        prompt += "Additional Details: " + AdditionalDetails + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-verbose-enhancer",
    name: "AI Verbose Enhancer",
    slug: "ai-verbose-enhancer",
    shortDescription:
      "Transform brief statements into detailed explanations with our AI Verbose Enhancer, perfect for meeting word count needs.",
    fields: [
      {
        type: "textArea",
        label: "Concise Statements",
        field: "Concise Statements",
        placeholder: "1. Dogs are pets. 2. They are loyal.",
        required: true,
        optional: false,
        id: "concisestatements",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing concise statements into detailed and elaborate explanations. You will be provided with brief statements, and your task is to expand them into more comprehensive descriptions.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { conciseStatements } = fields;
      let prompt =
        "Transform the following concise statements into detailed explanations:\n";
      prompt += conciseStatements + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-outline-generator",
    name: "AI Outline Generator",
    slug: "ai-outline-generator",
    shortDescription:
      "Effortlessly create structured outlines for essays, articles, or presentations with our AI Outline Generator based on your topics.",
    fields: [
      {
        type: "textArea",
        label: "Main Topic",
        field: "Main Topic",
        placeholder: "The Impact of Climate Change",
        required: true,
        optional: false,
        id: "maintopic",
      },
      {
        type: "textArea",
        label: "Subtopics",
        field: "Subtopics",
        placeholder:
          "1. Causes of Climate Change 2. Effects of Climate Change 3. Solutions to Climate Change",
        required: false,
        optional: true,
        id: "subtopics",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating structured outlines for essays, articles, or presentations. You will be provided with a main topic and optional subtopics, and you will generate a detailed outline based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { mainTopic, subtopics } = fields;
      let prompt = "Generate a structured outline based on the following:\n";
      prompt += "Main Topic: " + mainTopic + "\n";
      if (subtopics) {
        prompt += "Subtopics: " + subtopics + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "freelance-service-finder",
    name: "Freelance Service Finder",
    slug: "freelance-service-finder",
    shortDescription:
      "Find local freelance professionals for services like massages, haircuts, babysitting, tattoos, photography, and more with ease.",
    fields: [
      {
        type: "textArea",
        label: "Location",
        field: "Location",
        placeholder: "New York City",
        required: true,
        optional: false,
        id: "location",
      },
      {
        type: "textArea",
        label: "Services Needed",
        field: "Services Needed",
        placeholder: "Massages, Haircuts, Babysitting",
        required: false,
        optional: true,
        id: "servicesneeded",
      },
    ],
    categories: ["Business", "Marketing", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in finding local freelance services. You will be provided with a location and the types of services needed, and you will generate a list of local professionals who offer those services.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Location, ServicesNeeded } = fields;
      let prompt =
        "Find local freelance professionals based on the following criteria:\n";
      prompt += "Location: " + Location + "\n";
      if (ServicesNeeded) {
        prompt += "Services needed: " + ServicesNeeded + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-story-weaver",
    name: "AI Story Weaver",
    slug: "ai-story-weaver",
    shortDescription:
      "Create your own custom stories with AI Story Weaver. Tailor narratives to your unique preferences and let your imagination soar!",
    fields: [
      {
        type: "textArea",
        label: "Story Genre",
        field: "Story Genre",
        placeholder: "Fantasy, Mystery, Romance, etc.",
        required: true,
        optional: false,
        id: "storygenre",
      },
      {
        type: "textArea",
        label: "Main Characters",
        field: "Main Characters",
        placeholder: "A brave knight, a cunning thief, etc.",
        required: false,
        optional: true,
        id: "maincharacters",
      },
      {
        type: "textArea",
        label: "Story Setting",
        field: "Story Setting",
        placeholder: "A medieval kingdom, a bustling city, etc.",
        required: false,
        optional: true,
        id: "storysetting",
      },
      {
        type: "textArea",
        label: "Plot Preferences",
        field: "Plot Preferences",
        placeholder:
          "I want a quest for a hidden treasure, with lots of twists and turns.",
        required: false,
        optional: true,
        id: "plotpreferences",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating custom stories. You will be provided with specific preferences and you will create a unique story based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { storyGenre, mainCharacters, storySetting, plotPreferences } =
        fields;
      let prompt = "Generate a custom story based on the following:\n";
      prompt += "Story Genre: " + storyGenre + ".\n";
      if (mainCharacters) {
        prompt += "Main Characters: " + mainCharacters + ".\n";
      }
      if (storySetting) {
        prompt += "Story Setting: " + storySetting + ".\n";
      }
      if (plotPreferences) {
        prompt += "Plot Preferences: " + plotPreferences + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-cv-builder",
    name: "AI CV Builder",
    slug: "ai-cv-builder",
    shortDescription:
      "Create a standout CV effortlessly with our AI CV Builder, tailored to showcase your unique personal and professional details.",
    fields: [
      {
        type: "textArea",
        label: "Personal Information",
        field: "Personal Information",
        placeholder: "Name, Contact Information, LinkedIn Profile",
        required: true,
        optional: false,
        id: "personalinformation",
      },
      {
        type: "textArea",
        label: "Educational Background",
        field: "Educational Background",
        placeholder: "Schools attended, degrees earned, dates of attendance",
        required: false,
        optional: true,
        id: "educationalbackground",
      },
      {
        type: "textArea",
        label: "Work Experience",
        field: "Work Experience",
        placeholder:
          "Job titles, companies, dates of employment, responsibilities",
        required: false,
        optional: true,
        id: "workexperience",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating professional CVs. You will be provided with personal, educational, and work experience details, and you will generate a customized CV based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { personalInformation, educationalBackground, workExperience } =
        fields;
      let prompt = "Generate a professional CV based on the following:\n";
      prompt += "Personal Information: " + personalInformation + "\n";
      if (educationalBackground) {
        prompt += "Educational Background: " + educationalBackground + "\n";
      }
      if (workExperience) {
        prompt += "Work Experience: " + workExperience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "evidence-importance-explainer",
    name: "Evidence Importance Explainer",
    slug: "evidence-importance-explainer",
    shortDescription:
      "Understand the significance of evidence in supporting your topic sentence and enhance your writing with clear explanations.",
    fields: [
      {
        type: "textArea",
        label: "Topic Sentence",
        field: "Topic Sentence",
        placeholder:
          "Global warming is a pressing issue that needs immediate attention.",
        required: true,
        optional: false,
        id: "topicsentence",
      },
      {
        type: "textArea",
        label: "Evidence",
        field: "Evidence",
        placeholder:
          "According to NASA, Earth's average surface temperature has risen about 2.12 degrees Fahrenheit since the late 19th century.",
        required: false,
        optional: true,
        id: "evidence",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in explaining the relevance and importance of evidence in relation to a given topic sentence. You will be provided with a topic sentence and optional evidence, and you will generate an explanation that connects the evidence to the topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicSentence, evidence } = fields;
      let prompt =
        "Explain the importance of the following evidence in relation to the topic sentence:\n";
      prompt += "Topic Sentence: " + topicSentence + "\n";
      if (evidence) {
        prompt += "Evidence: " + evidence + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-generator-ai",
    name: "Text Generator AI",
    slug: "text-generator-ai",
    shortDescription:
      "Generate high-quality, contextually relevant text effortlessly with our AI text generator, tailored to your prompts for optimal results.",
    fields: [
      {
        type: "textArea",
        label: "Prompt",
        field: "Prompt",
        placeholder: "Once upon a time in a town far away...",
        required: true,
        optional: false,
        id: "prompt",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an advanced AI text generator. You will receive a prompt from the user, and your task is to generate high-quality, contextually relevant text based on that prompt.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Prompt } = fields;
      let prompt = "Generate text based on the following prompt:\n";
      prompt += "Prompt: " + Prompt + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-resume-builder",
    name: "AI Resume Builder",
    slug: "ai-resume-builder",
    shortDescription:
      "Create professional resumes effortlessly with our AI Resume Builder, tailored to your unique information and career goals.",
    fields: [
      {
        type: "textArea",
        label: "Work Experience",
        field: "Work Experience",
        placeholder: "List of jobs, roles, responsibilities, and achievements",
        required: true,
        optional: false,
        id: "workexperience",
      },
      {
        type: "textArea",
        label: "Education",
        field: "Education",
        placeholder: "Degrees, schools, years, and any honors or awards",
        required: false,
        optional: true,
        id: "education",
      },
      {
        type: "textArea",
        label: "Skills, Certifications, Additional Details",
        field: "Skills, Certifications, Additional Details",
        placeholder: "Relevant skills, certifications, proficiencies, etc.",
        required: false,
        optional: true,
        id: "skills,certifications,additionaldetails",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating professional resumes. You will be provided with details about work experience, education, and skills, and you will generate a well-structured resume based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { WorkExperience, Education, Skills } = fields;
      let prompt =
        "Generate a professional resume based on the following information:\n";
      prompt += "Work Experience: " + WorkExperience + "\n";
      if (Education) {
        prompt += "Education: " + Education + "\n";
      }
      if (Skills) {
        prompt +=
          "Skills, Certifications, Additional Details: " + Skills + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "business-theory-explorer",
    name: "Business Theory Explorer",
    slug: "business-theory-explorer",
    shortDescription:
      "Explore key business theories with Business Theory Explorer. Get concise summaries to enhance your research and understanding.",
    fields: [
      {
        type: "textArea",
        label: "Business Theory",
        field: "Business Theory",
        placeholder: "Porter's Five Forces",
        required: true,
        optional: false,
        id: "businesstheory",
      },
    ],
    categories: ["Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in business theories. You will be provided with a specific business theory and you will generate a comprehensive summary and explanation of that theory.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { BusinessTheory } = fields.BusinessTheory;
      let prompt =
        "Provide a detailed summary and explanation of the following business theory:\n";
      prompt += "Business Theory: " + BusinessTheory + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "film-psychopathology-analyzer",
    name: "Film Psychopathology Analyzer",
    slug: "film-psychopathology-analyzer",
    shortDescription:
      "Analyze films through a psychopathology lens, exploring characters' behaviors, symptoms, and their impact on the narrative.",
    fields: [
      {
        type: "textArea",
        label: "Film Title",
        field: "Film Title",
        placeholder: "Rain Man",
        required: true,
        optional: false,
        id: "filmtitle",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing films from a psychopathology perspective. You will be provided with the title of a film, and you will analyze its characters' behaviors, symptoms, potential diagnoses, and their contributions to the overall narrative.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { filmTitle } = fields;
      let prompt = "Analyze the film based on the following details:\n";
      prompt += "Film Title: " + filmTitle + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-researcher",
    name: "AI Researcher",
    slug: "ai-researcher",
    shortDescription:
      "Revolutionize your research with our AI tool that conducts live web searches and presents data in an easy-to-understand format.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic or Question",
        field: "Research Topic or Question",
        placeholder: "What are the main causes and effects of climate change?",
        required: true,
        optional: false,
        id: "researchtopicorquestion",
      },
    ],
    categories: ["Education", "Research", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an advanced AI researcher. You will be provided with a research topic or question, and you will conduct thorough research using live web searches from trusted resources to present the information in an easily understandable format.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopicOrQuestion } = fields;
      let prompt =
        "Conduct thorough research based on the following topic or question:\n";
      prompt +=
        "Research Topic or Question: " + researchTopicOrQuestion + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "thought-school-identifier",
    name: "Thought School Identifier",
    slug: "thought-school-identifier",
    shortDescription:
      "Identify relevant schools of thought for your research questions with the Thought School Identifier and enhance your academic exploration.",
    fields: [
      {
        type: "textArea",
        label: "Research Question",
        field: "Research Question",
        placeholder:
          "What is the impact of technology on societal development?",
        required: true,
        optional: false,
        id: "researchquestion",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in identifying schools of thought. You will be provided with a research question, and you will identify relevant schools of thought based on that question.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchQuestion } = fields;
      let prompt =
        "Identify relevant schools of thought based on the following research question:\n";
      prompt += "Research Question: " + researchQuestion + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "discussion-post-reply-generator",
    name: "Discussion Post Reply Generator",
    slug: "discussion-post-reply-generator",
    shortDescription:
      "Generate thoughtful and relevant replies to discussion posts effortlessly with our Discussion Post Reply Generator.",
    fields: [
      {
        type: "textArea",
        label: "Discussion Post",
        field: "Discussion Post",
        placeholder: "In my opinion, the main issue with...",
        required: true,
        optional: false,
        id: "discussionpost",
      },
    ],
    categories: ["Social Media", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thoughtful and relevant replies to discussion posts. You will be provided with a discussion post, and you will generate a reply that adds value to the conversation.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { discussionPost } = fields;
      let prompt =
        "Generate a thoughtful reply to the following discussion post:\n";
      prompt += "Discussion Post: " + discussionPost + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "smart-email-reply-generator",
    name: "Smart Email Reply Generator",
    slug: "smart-email-reply-generator",
    shortDescription:
      "Effortlessly craft smart email responses with our generator, providing detailed and relevant reply options for your received emails.",
    fields: [
      {
        type: "textArea",
        label: "Email Received",
        field: "Email Received",
        placeholder: "Dear John, I was wondering if you could...",
        required: true,
        optional: false,
        id: "emailreceived",
      },
    ],
    categories: ["Email", "Support", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating smart email replies. You will be provided with the content of an email received, and you will generate detailed and relevant reply options based on that content.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { emailReceived } = fields;
      let prompt = "Generate a smart email reply based on the following:\n";
      prompt += "Email received: " + emailReceived + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "profound-haiku-composer",
    name: "Profound Haiku Composer",
    slug: "profound-haiku-composer",
    shortDescription:
      "Create deep, non-rhyming haikus effortlessly with our profound haiku composer. Unleash your poetic creativity today!",
    fields: [
      {
        type: "textArea",
        label: "Haiku Topic",
        field: "Haiku Topic",
        placeholder: "Humans and AI together",
        required: true,
        optional: false,
        id: "haikutopic",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a skilled poet specializing in creating profound haikus. You will be provided with a topic, and your task is to generate a non-rhyming haiku that captures the essence of the topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { haikuTopic } = fields.haikuTopic;
      let prompt =
        "Generate a profound non-rhyming haiku based on the following topic:\n";
      prompt += "Topic: " + haikuTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ielts-essay-checker",
    name: "IELTS Essay Checker",
    slug: "ielts-essay-checker",
    shortDescription:
      "Get instant feedback on your essays with our IELTS Essay Checker, ensuring you meet IELTS standards for success.",
    fields: [
      {
        type: "textArea",
        label: "Essay Text",
        field: "Essay Text",
        placeholder: "Enter your IELTS essay here",
        required: true,
        optional: false,
        id: "essaytext",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in evaluating IELTS essays. You will be provided with an essay text and you will give detailed feedback based on IELTS standards.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayText } = fields;
      let prompt = `Provide feedback for the following IELTS essay based on IELTS standards:
  Essay Text: ${essayText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "check-grammar",
    name: "Check Grammar",
    slug: "check-grammar",
    shortDescription:
      "Analyze your text effortlessly with our grammar checker, identifying grammatical, punctuation, and spelling errors in seconds.",
    fields: [
      {
        type: "textArea",
        label: "Text to Check",
        field: "Text to Check",
        placeholder: "Input your text here...",
        required: true,
        optional: false,
        id: "texttocheck",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert grammar checker. You will be provided with a text, and your task is to analyze it for grammatical, punctuation, and spelling errors.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToCheck } = fields;
      let prompt =
        "Analyze the following text for grammatical, punctuation, and spelling errors:\n";
      prompt += textToCheck + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-job-posting-generator",
    name: "AI Job Posting Generator",
    slug: "ai-job-posting-generator",
    shortDescription:
      "Create engaging job postings effortlessly with our AI Job Posting Generator. Customize based on title, description, and requirements!",
    fields: [
      {
        type: "textArea",
        label: "Job Title",
        field: "Job Title",
        placeholder: "Full Stack Engineer",
        required: true,
        optional: false,
        id: "jobtitle",
      },
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "We are looking for a software engineer who...",
        required: false,
        optional: true,
        id: "jobdescription",
      },
      {
        type: "textArea",
        label: "Job Requirements",
        field: "Job Requirements",
        placeholder: "Bachelor's degree in Computer Science or equivalent...",
        required: false,
        optional: true,
        id: "jobrequirements",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating job postings. You will be provided with a job title, description, and requirements, and you will create a comprehensive and engaging job posting based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { jobTitle, jobDescription, jobRequirements } = fields;
      let prompt = "Generate a job posting based on the following:\n";
      prompt += "Job Title: " + jobTitle + ".\n";
      if (jobDescription) {
        prompt += "Job Description: " + jobDescription + ".\n";
      }
      if (jobRequirements) {
        prompt += "Job Requirements: " + jobRequirements + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "medical-professional-interview-question-generator",
    name: "Medical Professional Interview Question Generator",
    slug: "medical-professional-interview-question-generator",
    shortDescription:
      "Generate tailored interview questions for medical professionals with our Medical Professional Interview Question Generator.",
    fields: [
      {
        type: "textArea",
        label: "Medical Profession",
        field: "Medical Profession",
        placeholder: "Cardiologist, Nurse, Surgeon, etc.",
        required: true,
        optional: false,
        id: "medicalprofession",
      },
    ],
    categories: ["Education", "Training Material", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating interview questions for medical professionals. You will be provided with the specific medical profession and you will generate a set of relevant interview questions based on that profession.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { medicalProfession } = fields;
      let prompt =
        "Generate a set of interview questions for the following medical profession:\n";
      prompt += "Medical Profession: " + medicalProfession + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "valentines-day-card-composer",
    name: "Valentine's Day Card Composer",
    slug: "valentines-day-card-composer",
    shortDescription:
      "Create personalized Valentine's Day cards with unique messages tailored to your loved ones' names and your special relationship.",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Name",
        field: "Recipient's Name",
        placeholder: "Sarah",
        required: true,
        optional: false,
        id: "recipientsname",
      },
      {
        type: "textArea",
        label: "Relationship",
        field: "Relationship",
        placeholder: "Friend, Partner, Crush, etc.",
        required: false,
        optional: true,
        id: "relationship",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized Valentine's Day cards. You will be provided with the recipient's name and the relationship with the sender, and you will generate a heartfelt message for the card.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientName, relationship } = fields;
      let prompt =
        "Create a personalized Valentine's Day card message based on the following:\n";
      prompt += "Recipient's Name: " + recipientName + "\n";
      if (relationship) {
        prompt += "Relationship: " + relationship + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-expander-ai",
    name: "Essay Expander AI",
    slug: "essay-expander-ai",
    shortDescription:
      "Transform short essays into detailed masterpieces with Essay Expander AI, preserving meaning and structure effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder:
          "Enter the short essay or paragraph you want to expand here.",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in expanding essays and paragraphs. You will be provided with a short essay or paragraph, and your task is to expand it into a longer, more detailed piece while maintaining the original meaning and structure.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt =
        "Expand the following text into a longer, more detailed piece while preserving its original meaning and structure:\n";
      prompt += "Original Text: " + originalText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "moot-court-issue-framer",
    name: "Moot Court Issue Framer",
    slug: "moot-court-issue-framer",
    shortDescription:
      "Analyze moot court propositions effectively with our guide on framing key issues between involved parties.",
    fields: [
      {
        type: "textArea",
        label: "Moot Proposition",
        field: "Moot Proposition",
        placeholder: "State the proposition for the moot court here.",
        required: true,
        optional: false,
        id: "mootproposition",
      },
    ],
    categories: ["Legal", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in legal analysis and moot court preparations. You will be provided with a moot court proposition, and you will identify and frame the key issues arising between the parties involved.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { mootProposition } = fields;
      let prompt = `Analyze the following moot court proposition and frame the key issues:
  Moot Proposition: ${mootProposition}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "zoology-assistant",
    name: "Zoology Assistant",
    slug: "zoology-assistant",
    shortDescription:
      "Get accurate and comprehensive answers to all your animal questions with our Zoology Assistant. Discover the animal kingdom today!",
    fields: [
      {
        type: "textArea",
        label: "Question",
        field: "Question",
        placeholder: "What is the average lifespan of an African Elephant?",
        required: true,
        optional: false,
        id: "question",
      },
    ],
    categories: ["Education", "Writing", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert zoologist. You will be provided with specific questions about animals, and you will provide accurate and comprehensive answers based on your knowledge.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Question } = fields;
      let prompt =
        "Provide a detailed answer to the following question about animals:\n";
      prompt += "Question: " + Question + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writing-reviewer",
    name: "AI Writing Reviewer",
    slug: "ai-writing-reviewer",
    shortDescription:
      "Get expert reviews and feedback on your writing with AI Writing Reviewer to enhance quality and effectiveness.",
    fields: [
      {
        type: "textArea",
        label: "Text to Review",
        field: "Text to Review",
        placeholder: "Enter the text you want reviewed here.",
        required: true,
        optional: false,
        id: "texttoreview",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in reviewing and providing feedback on written content. You will be provided with a piece of text, and you will generate a detailed review aimed at improving its quality and effectiveness.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToReview } = fields;
      let prompt = `Review the following text and provide feedback to improve its quality and effectiveness:
        
  Text: ${textToReview}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-reviewer",
    name: "AI Reviewer",
    slug: "ai-reviewer",
    shortDescription:
      "Get instant feedback on your writing with AI Reviewer. Analyze text and enhance your content effortlessly for better results.",
    fields: [
      {
        type: "textArea",
        label: "Text",
        field: "Text",
        placeholder: "Enter the text you want reviewed here.",
        required: true,
        optional: false,
        id: "text",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI reviewer specialized in analyzing and providing constructive feedback on written text. You will receive a piece of text and will provide insights, suggestions for improvement, and overall analysis.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Text } = fields;
      let prompt = "Please analyze the following text and provide feedback:\n";
      prompt += "Text: " + Text + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "legal-case-summarizer",
    name: "Legal Case Summarizer",
    slug: "legal-case-summarizer",
    shortDescription:
      "Effortlessly summarize legal cases with our Legal Case Summarizer, extracting key points and conclusions for quick reference.",
    fields: [
      {
        type: "textArea",
        label: "Legal Case Text",
        field: "Legal Case Text",
        placeholder: "In the case of XYZ vs ABC...",
        required: true,
        optional: false,
        id: "legalcasetext",
      },
    ],
    categories: ["Legal", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing legal cases. You will be provided with the text of a legal case, and you will generate a summary that extracts key points and conclusions.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { legalCaseText } = fields;
      let prompt = `Generate a summary for the following legal case text:
  ${legalCaseText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-resume-updater",
    name: "AI Resume Updater",
    slug: "ai-resume-updater",
    shortDescription:
      "Optimize your resume effortlessly with our AI Resume Updater, tailoring it to match the latest job descriptions and your experiences.",
    fields: [
      {
        type: "textArea",
        label: "Current Resume",
        field: "Current Resume",
        placeholder: "John Doe, Software Engineer at XYZ...",
        required: true,
        optional: false,
        id: "currentresume",
      },
      {
        type: "textArea",
        label: "Recent Experiences or New Content",
        field: "Recent Experiences or New Content",
        placeholder: "Developed a new feature at XYZ...",
        required: false,
        optional: true,
        id: "recentexperiencesornewcontent",
      },
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "We are looking for a Software Engineer with...",
        required: false,
        optional: true,
        id: "jobdescription",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in updating and optimizing resumes. You will be provided with a current resume, recent experiences, and a job description, and you will generate an updated resume that highlights the most relevant skills and experiences.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { currentResume, recentExperiences, jobDescription } = fields;
      let prompt = "Update the resume based on the following information:\n";
      prompt += "Current Resume: " + currentResume + "\n";
      if (recentExperiences) {
        prompt +=
          "Recent Experiences or New Content: " + recentExperiences + "\n";
      }
      if (jobDescription) {
        prompt += "Job Description: " + jobDescription + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-response-generator",
    name: "AI Response Generator",
    slug: "ai-response-generator",
    shortDescription:
      "Effortlessly create tailored responses with our AI Response Generator, designed for diverse communications and user-guided instructions.",
    fields: [
      {
        type: "textArea",
        label: "Communication Type",
        field: "Communication Type",
        placeholder: "Email",
        required: true,
        optional: false,
        id: "communicationtype",
      },
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Dear John, I hope this email finds you well...",
        required: false,
        optional: true,
        id: "originaltext",
      },
      {
        type: "textArea",
        label: "Guidance",
        field: "Guidance",
        placeholder:
          "Agree with the points made and ask a question about the project timeline.",
        required: false,
        optional: true,
        id: "guidance",
      },
    ],
    categories: ["Social Media", "Email", "Support", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating responses to various types of communications. You will be provided with some base information and you will generate a response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { communicationType, originalText, guidance } = fields;
      let prompt = "Generate a response based on the following:\n";
      prompt += "Communication Type: " + communicationType + ".\n";
      if (originalText) {
        prompt += "Original Text: " + originalText + ".\n";
      }
      if (guidance) {
        prompt += "Guidance: " + guidance + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "business-contact-locator",
    name: "Business Contact Locator",
    slug: "business-contact-locator",
    shortDescription:
      "Easily find contact information for businesses with our Business Contact Locator—just input your details and get results!",
    fields: [
      {
        type: "textArea",
        label: "Business Information",
        field: "Business Information",
        placeholder:
          "Business name, address, focus area, employee names, and any other details",
        required: true,
        optional: false,
        id: "businessinformation",
      },
    ],
    categories: ["Business", "Sales", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in locating business contact information. You will be provided with details about a business, and you will generate the relevant contact information based on the provided data.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { businessInformation } = fields;
      let prompt = "Find the contact information for the following business:\n";
      prompt += "Business Information: " + businessInformation + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "greeting-card-creator",
    name: "Greeting Card Creator",
    slug: "greeting-card-creator",
    shortDescription:
      "Create personalized greeting cards for any occasion with our custom card creator, tailored to your recipient and your relationship.",
    fields: [
      {
        type: "textArea",
        label: "Occasion",
        field: "Occasion",
        placeholder: "Birthday, Anniversary, Congratulations",
        required: true,
        optional: false,
        id: "occasion",
      },
      {
        type: "textArea",
        label: "User's Relationship with Recipient",
        field: "User's Relationship with Recipient",
        placeholder: "Friend, Colleague, Relative",
        required: false,
        optional: true,
        id: "usersrelationshipwithrecipient",
      },
      {
        type: "textArea",
        label: "Recipient's Interests",
        field: "Recipient's Interests",
        placeholder: "Gardening, Reading, Cooking",
        required: false,
        optional: true,
        id: "recipientsinterests",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized greeting cards. You will be provided with details about the occasion, the recipient's interests, and your relationship with them to generate a custom greeting card message.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { occasion, userRelationship, recipientInterests } = fields;
      let prompt = "Generate a greeting card message based on the following:\n";
      prompt += "Occasion: " + occasion + ".\n";
      if (userRelationship) {
        prompt +=
          "User's relationship with recipient: " + userRelationship + ".\n";
      }
      if (recipientInterests) {
        prompt += "Recipient's interests: " + recipientInterests + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "sentence-polisher",
    name: "Sentence Polisher",
    slug: "sentence-polisher",
    shortDescription:
      "Enhance your writing with Sentence Polisher: improve grammar, style, and clarity for polished, professional sentences.",
    fields: [
      {
        type: "textArea",
        label: "Input Sentence",
        field: "Input Sentence",
        placeholder: "This is a sentence that needs improving.",
        required: true,
        optional: false,
        id: "inputsentence",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in refining and enhancing sentences. You will be provided with a sentence that needs improvement. Your task is to improve its grammar, style, and clarity.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { inputSentence } = fields;
      let prompt = `Improve the following sentence:
  ${inputSentence}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writing-assistant",
    name: "AI Writing Assistant",
    slug: "ai-writing-assistant",
    shortDescription:
      "Transform your writing with our AI assistant, generating high-quality content tailored to your needs in seconds.",
    fields: [
      {
        type: "textArea",
        label: "Writing Request",
        field: "Writing Request",
        placeholder:
          "Write a blog post for tech enthusiasts about the latest open source AI advancements",
        required: true,
        optional: false,
        id: "writingrequest",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI writing assistant that specializes in generating high-quality written content. You will receive a writing request from the user and you will create a well-structured and engaging piece based on that request.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { WritingRequest } = fields;
      let prompt =
        "Generate a written content piece based on the following request:\n";
      prompt += "Writing Request: " + WritingRequest + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "detailed-lesson-plan-builder",
    name: "Detailed Lesson Plan Builder",
    slug: "detailed-lesson-plan-builder",
    shortDescription:
      "Transform lesson summaries into detailed plans with key questions, adaptive learning techniques, and assessment opportunities.",
    fields: [
      {
        type: "textArea",
        label: "Lesson Summary",
        field: "Lesson Summary",
        placeholder: "This lesson will cover the basics of photosynthesis...",
        required: true,
        optional: false,
        id: "lessonsummary",
      },
      {
        type: "textArea",
        label: "Lesson Objectives",
        field: "Lesson Objectives",
        placeholder:
          "By the end of the lesson, students will be able to explain the process of photosynthesis...",
        required: false,
        optional: true,
        id: "lessonobjectives",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating detailed lesson plans. You will be provided with a summary of the lesson content and objectives, and you will generate a comprehensive lesson plan that includes key questions, adaptive learning techniques, and assessment opportunities.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { LessonSummary, LessonObjectives } = fields;
      let prompt = "Generate a detailed lesson plan based on the following:\n";
      prompt += "Lesson Summary: " + LessonSummary + "\n";
      if (LessonObjectives) {
        prompt += "Lesson Objectives: " + LessonObjectives + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-grammar-analyzer",
    name: "AI Grammar Analyzer",
    slug: "ai-grammar-analyzer",
    shortDescription:
      "Analyze your text effortlessly with our AI Grammar Analyzer, correcting errors and enhancing your writing with smart suggestions.",
    fields: [
      {
        type: "textArea",
        label: "Text to Analyze",
        field: "Text to Analyze",
        placeholder: "Type or paste your text here for grammar analysis.",
        required: true,
        optional: false,
        id: "texttoanalyze",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert grammar analyst. You will be provided with a piece of text, and your task is to analyze it for grammatical errors and provide suggestions for improvement.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToAnalyze } = fields;
      let prompt =
        "Analyze the following text for grammatical errors and provide suggestions for improvement:\n";
      prompt += textToAnalyze + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "contract-analyzer-ai",
    name: "Contract Analyzer AI",
    slug: "contract-analyzer-ai",
    shortDescription:
      "Analyze contracts effortlessly with our AI tool, highlighting key points, terms, and potential issues for better decision-making.",
    fields: [
      {
        type: "textArea",
        label: "Contract Text",
        field: "Contract Text",
        placeholder: "This Agreement is made between...",
        required: true,
        optional: false,
        id: "contracttext",
      },
    ],
    categories: ["Legal", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in contract analysis. You will be provided with the text of a contract, and you will analyze it to identify key points, terms, and potential issues.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { contractText } = fields;
      let prompt = `Analyze the following contract text and identify key points, terms, and potential issues:
  ${contractText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "goal-oriented-recipe-generator",
    name: "Goal-Oriented Recipe Generator",
    slug: "goal-oriented-recipe-generator",
    shortDescription:
      "Generate custom recipes that align with your meal goals and include your specified ingredients for a tailored cooking experience.",
    fields: [
      {
        type: "textArea",
        label: "Meal Goal",
        field: "Meal Goal",
        placeholder: "Weight loss",
        required: true,
        optional: false,
        id: "mealgoal",
      },
      {
        type: "textArea",
        label: "Main Ingredients",
        field: "Main Ingredients",
        placeholder: "Chicken, broccoli, quinoa",
        required: false,
        optional: true,
        id: "mainingredients",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating recipes tailored to specific meal goals. You will be provided with a meal goal and main ingredients, and you will create a custom recipe that aligns with the user's objectives.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { mealGoal, mainIngredients } = fields;
      let prompt = "Generate a recipe based on the following:\n";
      prompt += "Meal Goal: " + mealGoal + ".\n";
      if (mainIngredients) {
        prompt += "Main Ingredients: " + mainIngredients + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "fresh-integrated-thinking-assistant",
    name: "FRESH Integrated Thinking Assistant",
    slug: "fresh-integrated-thinking-assistant",
    shortDescription:
      "Solve complex problems with FRESH Integrated Thinking Assistant, utilizing diverse methodologies like First Principles and Socratic Questioning.",
    fields: [
      {
        type: "textArea",
        label: "Basic Truths",
        field: "Basic Truths",
        placeholder: "The fundamental truths about the concept or problem.",
        required: true,
        optional: false,
        id: "basictruths",
      },
      {
        type: "textArea",
        label: "Concept Name",
        field: "Concept Name",
        placeholder: "The concept or problem to understand/solve.",
        required: false,
        optional: true,
        id: "conceptname",
      },
      {
        type: "textArea",
        label: "Key Questions",
        field: "Key Questions",
        placeholder:
          "Probing questions to further understand/explore the concept/problem.",
        required: false,
        optional: true,
        id: "keyquestions",
      },
      {
        type: "textArea",
        label: "Assumptions",
        field: "Assumptions",
        placeholder:
          "Assumptions about the concept/problem for Fermi estimations.",
        required: false,
        optional: true,
        id: "assumptions",
      },
      {
        type: "textArea",
        label: "Critical Factors",
        field: "Critical Factors",
        placeholder:
          "Factors that will give the most significant result according to the Pareto Principle.",
        required: false,
        optional: true,
        id: "criticalfactors",
      },
    ],
    categories: ["Education", "Training Material", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in integrated thinking methodologies. You will be provided with fundamental truths, key questions, and assumptions related to a concept or problem, and you will assist in solving complex problems using various thinking techniques.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const {
        BasicTruths,
        ConceptName,
        KeyQuestions,
        Assumptions,
        CriticalFactors,
      } = fields;
      let prompt = "Assist in solving the following complex problem:\n";
      prompt += "Basic Truths: " + BasicTruths + "\n";
      if (ConceptName) {
        prompt += "Concept Name: " + ConceptName + "\n";
      }
      if (KeyQuestions) {
        prompt += "Key Questions: " + KeyQuestions + "\n";
      }
      if (Assumptions) {
        prompt += "Assumptions: " + Assumptions + "\n";
      }
      if (CriticalFactors) {
        prompt += "Critical Factors: " + CriticalFactors + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "grammar-corrector-sentence-rephraser",
    name: "Grammar Corrector & Sentence Rephraser",
    slug: "grammar-corrector-sentence-rephraser",
    shortDescription:
      "Automatically correct grammar and rephrase sentences for better clarity and style with our powerful Grammar Corrector tool.",
    fields: [
      {
        type: "textArea",
        label: "Input Text",
        field: "Input Text",
        placeholder: "Type or paste the text to rephrase and correct...",
        required: true,
        optional: false,
        id: "inputtext",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grammar correction and sentence rephrasing. You will be provided with a piece of text, and you will correct any grammatical errors and rephrase sentences for improved clarity and style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { inputText } = fields;
      let prompt = `Correct the grammar and rephrase the following text for improved clarity and style:
  ${inputText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-builder-ai",
    name: "Resume Builder AI",
    slug: "resume-builder-ai",
    shortDescription:
      "Create tailored, professional resumes with Resume Builder AI, designed to showcase your skills and experience for any job.",
    fields: [
      {
        type: "textArea",
        label: "Work Experience",
        field: "Work Experience",
        placeholder:
          "List your previous jobs with the company name, your role, responsibilities, and achievements",
        required: true,
        optional: false,
        id: "workexperience",
      },
      {
        type: "textArea",
        label: "Education and Skills",
        field: "Education and Skills",
        placeholder:
          "List your educational background, skills, and certifications",
        required: false,
        optional: true,
        id: "educationandskills",
      },
      {
        type: "textArea",
        label: "Job Details Requirements",
        field: "Job Details Requirements",
        placeholder:
          "List the requirements and qualifications of the job you're applying for",
        required: false,
        optional: true,
        id: "jobdetailsrequirements",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating professional resumes. You will be provided with personal information, work experience, skills, and job requirements to generate a tailored resume.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { workExperience, educationAndSkills, jobDetailsRequirements } =
        fields;
      let prompt =
        "Create a professional resume based on the following information:\n";
      prompt += "Work Experience: " + workExperience + "\n";
      if (educationAndSkills) {
        prompt += "Education and Skills: " + educationAndSkills + "\n";
      }
      if (jobDetailsRequirements) {
        prompt += "Job Details Requirements: " + jobDetailsRequirements + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "reading-comprehension-assistant",
    name: "Reading Comprehension Assistant",
    slug: "reading-comprehension-assistant",
    shortDescription:
      "Enhance your reading skills with our Reading Comprehension Assistant, providing direct answers to boost understanding of texts.",
    fields: [
      {
        type: "textArea",
        label: "Text to Read",
        field: "Text to Read",
        placeholder: "In 1492, Columbus sailed the ocean blue...",
        required: true,
        optional: false,
        id: "texttoread",
      },
      {
        type: "textArea",
        label: "Question(s)",
        field: "Question(s)",
        placeholder:
          "Who financed Columbus's voyage? What were the names of his ships?",
        required: false,
        optional: true,
        id: "question(s)",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in reading comprehension. You will be provided with a text and questions related to that text, and your task is to provide accurate answers based on the information contained within the text.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToRead, questions } = fields;
      let prompt = "Based on the following text, answer the questions:\n";
      prompt += "Text: " + textToRead + "\n";
      if (questions) {
        prompt += "Questions: " + questions + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-grant-writer",
    name: "AI Grant Writer",
    slug: "ai-grant-writer",
    shortDescription:
      "Streamline your grant proposals with our AI Grant Writer, generating persuasive narratives tailored to your inputs for successful funding.",
    fields: [
      {
        type: "textArea",
        label: "Project Description",
        field: "Project Description",
        placeholder: "Describe your project here...",
        required: true,
        optional: false,
        id: "projectdescription",
      },
      {
        type: "textArea",
        label: "Organization Background",
        field: "Organization Background",
        placeholder: "Tell us about your organization...",
        required: false,
        optional: true,
        id: "organizationbackground",
      },
      {
        type: "textArea",
        label: "Funding Needs",
        field: "Funding Needs",
        placeholder:
          "Explain how much funding you need and what it will be used for...",
        required: false,
        optional: true,
        id: "fundingneeds",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing grant proposals. You will be provided with details about a project, organization, and funding needs, and you will generate a persuasive and compelling narrative for a grant proposal based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { projectDescription, organizationBackground, fundingNeeds } =
        fields;
      let prompt = "Generate a grant proposal based on the following:\n";
      prompt += "Project Description: " + projectDescription + "\n";
      if (organizationBackground) {
        prompt += "Organization Background: " + organizationBackground + "\n";
      }
      if (fundingNeeds) {
        prompt += "Funding Needs: " + fundingNeeds + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-presentation-closer-generator",
    name: "AI Presentation Closer Generator",
    slug: "ai-presentation-closer-generator",
    shortDescription:
      "Craft impactful closing remarks for presentations with our AI generator, summarizing key takeaways and impressing your audience.",
    fields: [
      {
        type: "textArea",
        label: "Presentation Bullet Points",
        field: "Presentation Bullet Points",
        placeholder:
          "- The importance of renewable energy\n- Advancements in solar technology\n- Cost savings of sustainable practices\n- Government incentives for green initiatives\n- Call to action: Invest in a greener future",
        required: true,
        optional: false,
        id: "presentationbulletpoints",
      },
      {
        type: "textArea",
        label: "Desired Tone",
        field: "Desired Tone",
        placeholder: "Inspirational",
        required: false,
        optional: true,
        id: "desiredtone",
      },
    ],
    categories: ["Business", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting impactful closing remarks for presentations. You will be provided with key bullet points and a desired tone, and you will generate a closing statement that summarizes the key takeaways and leaves a lasting impression on the audience.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { presentationBulletPoints, desiredTone } = fields;
      let prompt =
        "Generate impactful closing remarks for a presentation based on the following:\n";
      prompt +=
        "Presentation Bullet Points: " + presentationBulletPoints + "\n";
      if (desiredTone) {
        prompt += "Desired Tone: " + desiredTone + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "speech-writing-ai",
    name: "Speech Writing AI",
    slug: "speech-writing-ai",
    shortDescription:
      "Craft compelling speeches effortlessly with our Speech Writing AI, tailored to your details for a natural and creative touch.",
    fields: [
      {
        type: "textArea",
        label: "Speech Description",
        field: "Speech Description",
        placeholder:
          "I'm working on a keynote speech for a tech conference about the future of AI.",
        required: true,
        optional: false,
        id: "speechdescription",
      },
      {
        type: "textArea",
        label: "Details to Include",
        field: "Details to Include",
        placeholder:
          "AI advancements, Impact on society, Ethical considerations, Future predictions",
        required: false,
        optional: true,
        id: "detailstoinclude",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in speech writing. You will be provided with details about the speech and you will generate a compelling and engaging speech based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { speechDescription, detailsToInclude } = fields;
      let prompt = "Generate a speech based on the following:\n";
      prompt += "Speech Description: " + speechDescription + "\n";
      if (detailsToInclude) {
        prompt += "Details to include: " + detailsToInclude + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resignation-letter-writer",
    name: "Resignation Letter Writer",
    slug: "resignation-letter-writer",
    shortDescription:
      "Create a professional resignation letter effortlessly with our tool, tailored to your job details and reasons for leaving.",
    fields: [
      {
        type: "textArea",
        label: "Current Job Title, Company Name",
        field: "Current Job Title, Company Name",
        placeholder: "Software Engineer",
        required: true,
        optional: false,
        id: "currentjobtitle,companyname",
      },
      {
        type: "textArea",
        label: "Resignation Date and Terms",
        field: "Resignation Date and Terms",
        placeholder:
          "Two weeks' notice, last working day will be June 15, 2023",
        required: false,
        optional: true,
        id: "resignationdateandterms",
      },
      {
        type: "textArea",
        label: "Reason for Leaving (Optional)",
        field: "Reason for Leaving (Optional)",
        placeholder: "I have decided to pursue further studies.",
        required: false,
        optional: true,
        id: "reasonforleaving(optional)",
      },
    ],
    categories: ["Email", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing professional resignation letters. You will be provided with details about the user's current job and their resignation terms, and you will generate a respectful resignation letter based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const {
        CurrentJobTitleCompanyName,
        ResignationDateAndTerms,
        ReasonForLeaving,
      } = fields;
      let prompt = "Generate a resignation letter based on the following:\n";
      prompt +=
        "Current Job Title and Company: " + CurrentJobTitleCompanyName + ".\n";
      if (ResignationDateAndTerms) {
        prompt +=
          "Resignation Date and Terms: " + ResignationDateAndTerms + ".\n";
      }
      if (ReasonForLeaving) {
        prompt += "Reason for Leaving: " + ReasonForLeaving + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-cv-maker",
    name: "AI CV Maker",
    slug: "ai-cv-maker",
    shortDescription:
      "Create a standout CV effortlessly with our AI CV Maker, tailoring it to your personal info, experience, and skills.",
    fields: [
      {
        type: "textArea",
        label: "Personal Information",
        field: "Personal Information",
        placeholder: "Name, Address, Email, Phone Number",
        required: true,
        optional: false,
        id: "personalinformation",
      },
      {
        type: "textArea",
        label: "Work Experience",
        field: "Work Experience",
        placeholder: "Company Name, Job Title, Duration, Responsibilities",
        required: false,
        optional: true,
        id: "workexperience",
      },
      {
        type: "textArea",
        label: "Skills",
        field: "Skills",
        placeholder: "Technical Skills, Soft Skills",
        required: false,
        optional: true,
        id: "skills",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating professional CVs. You will be provided with personal information, work experience, and skills, and you will generate a well-structured CV based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { personalInformation, workExperience, skills } = fields;
      let prompt = "Generate a professional CV based on the following:\n";
      prompt += "Personal Information: " + personalInformation + "\n";
      if (workExperience) {
        prompt += "Work Experience: " + workExperience + "\n";
      }
      if (skills) {
        prompt += "Skills: " + skills + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-resume-builder",
    name: "AI Resume Builder",
    slug: "ai-resume-builder",
    shortDescription:
      "Create a standout resume effortlessly with our AI Resume Builder, tailored to your personal info, experience, and skills.",
    fields: [
      {
        type: "textArea",
        label: "Personal Information",
        field: "Personal Information",
        placeholder: "Name, Contact Information, LinkedIn Profile URL",
        required: true,
        optional: false,
        id: "personalinformation",
      },
      {
        type: "textArea",
        label: "Work Experience",
        field: "Work Experience",
        placeholder: "Job Title, Company, Duration, Key Achievements",
        required: false,
        optional: true,
        id: "workexperience",
      },
      {
        type: "textArea",
        label: "Skills and Education",
        field: "Skills and Education",
        placeholder: "Skills, Certifications, Degrees, Institutions",
        required: false,
        optional: true,
        id: "skillsandeducation",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating professional resumes. You will be provided with personal information, work experience, and skills, and you will generate a well-structured resume based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { personalInformation, workExperience, skillsAndEducation } =
        fields;
      let prompt = "Generate a professional resume based on the following:\n";
      prompt += "Personal Information: " + personalInformation + "\n";
      if (workExperience) {
        prompt += "Work Experience: " + workExperience + "\n";
      }
      if (skillsAndEducation) {
        prompt += "Skills and Education: " + skillsAndEducation + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "passive-to-active-voice-converter",
    name: "Passive to Active Voice Converter",
    slug: "passive-to-active-voice-converter",
    shortDescription:
      "Transform passive voice into active voice effortlessly for clearer, more direct writing with our easy-to-use converter.",
    fields: [
      {
        type: "textArea",
        label: "Input Text",
        field: "Input Text",
        placeholder: "The cake was eaten by the children.",
        required: true,
        optional: false,
        id: "inputtext",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in converting sentences from passive voice to active voice. You will be provided with a sentence in passive voice, and you will convert it to active voice.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { inputText } = fields;
      let prompt = `Convert the following sentence from passive voice to active voice:
  Input Text: ${inputText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "rate-my-story",
    name: "Rate My Story",
    slug: "rate-my-story",
    shortDescription:
      "Get objective ratings and constructive feedback on your story's plot, characters, and writing style with Rate My Story.",
    fields: [
      {
        type: "textArea",
        label: "User's Story",
        field: "User's Story",
        placeholder: "Once upon a time...",
        required: true,
        optional: false,
        id: "usersstory",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing feedback on creative writing. You will be given a user's story, and your task is to provide an objective rating and constructive feedback based on elements such as plot, characters, and writing style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userStory } = fields;
      let prompt =
        "Please rate the following story and provide constructive feedback:\n";
      prompt += "User's Story: " + userStory + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "writing-improver",
    name: "Writing Improver",
    slug: "writing-improver",
    shortDescription:
      "Enhance your writing with our Writing Improver, correcting grammar and style while keeping your text natural and consistent.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Paste your text here...",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in improving written text. You will be provided with a section of text and you will improve it by fixing grammar, enhancing vocabulary, and refining style while ensuring the text remains natural, correct, and consistent.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Improve the following text:
  ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "research-paper-summarizer",
    name: "Research Paper Summarizer",
    slug: "research-paper-summarizer",
    shortDescription:
      "Summarize research papers effortlessly! Highlight key points and conclusions with our easy-to-use Research Paper Summarizer tool.",
    fields: [
      {
        type: "textArea",
        label: "Research Paper",
        field: "Research Paper",
        placeholder: "Upload your research paper here",
        required: true,
        optional: false,
        id: "researchpaper",
      },
    ],
    categories: ["Education", "Writing", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing research papers. You will be provided with a research paper, and you will generate a summary highlighting the main points and conclusions.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchPaper } = fields;
      let prompt = `Generate a summary for the following research paper:
  Research Paper: ${researchPaper}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "grammar-and-translation-enhancer",
    name: "Grammar and Translation Enhancer",
    slug: "grammar-and-translation-enhancer",
    shortDescription:
      "Enhance your text with our Grammar and Translation tool, improving grammar and translating effortlessly for clear communication.",
    fields: [
      {
        type: "textArea",
        label: "Text to Translate",
        field: "Text to Translate",
        placeholder: "Enter text here...",
        required: true,
        optional: false,
        id: "texttotranslate",
      },
      {
        type: "textArea",
        label: "Target Language",
        field: "Target Language",
        placeholder: "Enter the language you want to translate to...",
        required: false,
        optional: true,
        id: "targetlanguage",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grammar enhancement and translation. You will be provided with a text and optionally a target language. Your task is to translate the text into the specified language (if provided) and improve its grammatical structure.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToTranslate, targetLanguage } = fields;
      let prompt = `Enhance the grammar and translate the following text:
  Text: ${textToTranslate}
  `;
      if (targetLanguage) {
        prompt += `Target Language: ${targetLanguage}
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "tutorial-summarizer",
    name: "Tutorial Summarizer",
    slug: "tutorial-summarizer",
    shortDescription:
      "Generate concise summaries from detailed tutorials, highlighting key points and key takeaways for quick understanding.",
    fields: [
      {
        type: "textArea",
        label: "Tutorial Title",
        field: "Tutorial Title",
        placeholder: "Getting Started with Printify API",
        required: true,
        optional: false,
        id: "tutorialtitle",
      },
      {
        type: "textArea",
        label: "Tutorial Content",
        field: "Tutorial Content",
        placeholder:
          "This tutorial covers the basics of setting up an account with Printify...",
        required: false,
        optional: true,
        id: "tutorialcontent",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing tutorials. You will be provided with the title and content of a tutorial, and your task is to generate a concise summary that highlights the key points and takeaways.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { tutorialTitle, tutorialContent } = fields;
      let prompt =
        "Generate a concise summary based on the following tutorial information:\n";
      prompt += "Tutorial Title: " + tutorialTitle + "\n";
      if (tutorialContent) {
        prompt += "Tutorial Content: " + tutorialContent + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "plain-language-converter",
    name: "Plain Language Converter",
    slug: "plain-language-converter",
    shortDescription:
      "Transform complex text into clear, accessible language with our Plain Language Converter for better understanding and communication.",
    fields: [
      {
        type: "textArea",
        label: "Text to Convert",
        field: "Text to Convert",
        placeholder: "Enter the complex text here",
        required: true,
        optional: false,
        id: "texttoconvert",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in converting complex or technical text into plain language. You will be provided with some complex text and you will convert it into clear, accurate, and accessible plain language.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToConvert } = fields;
      let prompt = `Convert the following complex text into plain language:
  ${textToConvert}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-lecture-notes-generator",
    name: "AI Lecture Notes Generator",
    slug: "ai-lecture-notes-generator",
    shortDescription:
      "Generate comprehensive lecture notes effortlessly with our AI Lecture Notes Generator, transforming text or outlines into organized content.",
    fields: [
      {
        type: "textArea",
        label: "Lecture Text or Outline",
        field: "Lecture Text or Outline",
        placeholder: "Paste lecture text or outline information here...",
        required: true,
        optional: false,
        id: "lecturetextoroutline",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating comprehensive lecture notes. You will be provided with the text or outline of a lecture, and you will create detailed notes based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { lectureTextOrOutline } = fields;
      let prompt =
        "Generate comprehensive lecture notes based on the following:\n";
      prompt += "Lecture Text or Outline: " + lectureTextOrOutline + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-dungeon-storyteller",
    name: "AI Dungeon Storyteller",
    slug: "ai-dungeon-storyteller",
    shortDescription:
      "Create immersive text-based adventures with AI Dungeon Storyteller, where the narrative adapts to your choices for a unique experience.",
    fields: [
      {
        type: "textArea",
        label: "Character Name",
        field: "Character Name",
        placeholder: "Enter your character's name",
        required: true,
        optional: false,
        id: "charactername",
      },
      {
        type: "textArea",
        label: "Character Background",
        field: "Character Background",
        placeholder: "Describe your character's background",
        required: false,
        optional: true,
        id: "characterbackground",
      },
      {
        type: "textArea",
        label: "Adventure Choice",
        field: "Adventure Choice",
        placeholder:
          "Choose your adventure (e.g., mystical forest, haunted castle, etc.)",
        required: false,
        optional: true,
        id: "adventurechoice",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating immersive and dynamic text-based adventure stories. You will be provided with character details and adventure choices, and you will generate a narrative based on the user's input.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { characterName, characterBackground, adventureChoice } = fields;
      let prompt =
        "Create a text-based adventure story based on the following:\n";
      prompt += "Character Name: " + characterName + "\n";
      if (characterBackground) {
        prompt += "Character Background: " + characterBackground + "\n";
      }
      if (adventureChoice) {
        prompt += "Adventure Choice: " + adventureChoice + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "academic-tone-enhancer",
    name: "Academic Tone Enhancer",
    slug: "academic-tone-enhancer",
    shortDescription:
      "Enhance your writing with our Academic Tone Enhancer, ensuring high-quality academic style and format for your paragraphs.",
    fields: [
      {
        type: "textArea",
        label: "Original Paragraph",
        field: "Original Paragraph",
        placeholder: "In this paper, I'm gonna talk about...",
        required: true,
        optional: false,
        id: "originalparagraph",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing the academic tone of written content. You will be provided with an original paragraph, and your task is to improve its academic tone while ensuring it adheres to high-quality academic format and style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalParagraph } = fields;
      let prompt = `Enhance the academic tone of the following paragraph:
  Original Paragraph: ${originalParagraph}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "content-paraphraser",
    name: "Content Paraphraser",
    slug: "content-paraphraser",
    shortDescription:
      "Effortlessly rephrase your content while preserving its core message and intent with our powerful content paraphraser tool.",
    fields: [
      {
        type: "textArea",
        label: "Original Content",
        field: "Original Content",
        placeholder: "Original text goes here...",
        required: true,
        optional: false,
        id: "originalcontent",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in paraphrasing content. You will be provided with original text, and your task is to rephrase it while maintaining its core message and intent.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalContent } = fields;
      let prompt =
        "Rephrase the following content while preserving its meaning:\n";
      prompt += "Original Content: " + originalContent + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "roastbot",
    name: "RoastBot",
    slug: "roastbot",
    shortDescription:
      "Experience RoastBot: fun, humorous comebacks that playfully roast you while cleverly contradicting your statements and questions.",
    fields: [
      {
        type: "textArea",
        label: "User Statement or Question",
        field: "User Statement or Question",
        placeholder: "I think I'm quite good at chess.",
        required: true,
        optional: false,
        id: "userstatementorquestion",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating humorous and playful roasts. You will be provided with a user's statement or question, and you will generate a response that humorously contradicts their statement or question.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { userStatementOrQuestion } = fields;
      let prompt = `Generate a humorous roast based on the following user statement or question:
  User statement or question: ${userStatementOrQuestion}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paragraph-optimizer",
    name: "Paragraph Optimizer",
    slug: "paragraph-optimizer",
    shortDescription:
      "Enhance your writing with our Paragraph Optimizer: refine and shorten paragraphs while preserving their original meaning.",
    fields: [
      {
        type: "textArea",
        label: "Original Paragraph",
        field: "Original Paragraph",
        placeholder: "Enter the paragraph that you want to optimize here.",
        required: true,
        optional: false,
        id: "originalparagraph",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in optimizing written content. You will be provided with a paragraph, and your task is to refine and shorten it while maintaining its original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalParagraph } = fields;
      let prompt = `Optimize the following paragraph:
  ${originalParagraph}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "grammar-clear-writer-pro",
    name: "Grammar + Clear Writer Pro",
    slug: "grammar-clear-writer-pro",
    shortDescription:
      "Enhance your writing with Grammar + Clear Writer Pro: analyze and improve grammar and clarity for flawless text.",
    fields: [
      {
        type: "textArea",
        label: "Text here",
        field: "Text here",
        placeholder: "Input your text here...",
        required: true,
        optional: false,
        id: "texthere",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grammar and writing clarity. You will be provided with a piece of text, and your task is to analyze and improve its grammar and clarity.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { text } = fields;
      let prompt = `Analyze and improve the following text for grammar and clarity:
  ${text}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "french-grammar-fixer",
    name: "French Grammar Fixer",
    slug: "french-grammar-fixer",
    shortDescription:
      "Corrigez les erreurs grammaticales dans vos textes en français avec notre outil de correction de grammaire facile à utiliser.",
    fields: [
      {
        type: "textArea",
        label: "French Text",
        field: "French Text",
        placeholder: "Ecrivez votre texte ici",
        required: true,
        optional: false,
        id: "frenchtext",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in French grammar. You will be provided with a piece of French text, and you will correct any grammatical errors in the text.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { frenchText } = fields;
      let prompt = `Correct the following French text for grammatical errors:
  ${frenchText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writing-examples",
    name: "AI Writing Examples",
    slug: "ai-writing-examples",
    shortDescription:
      "Explore AI writing examples that generate creative ideas based on your chosen topic or context for inspiration and innovation.",
    fields: [
      {
        type: "textArea",
        label: "Writing Topic",
        field: "Writing Topic",
        placeholder: "Birthday card for best friend turning 21",
        required: true,
        optional: false,
        id: "writingtopic",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative writing assistant. You will be provided with a specific writing topic, and your task is to generate creative writing examples and ideas based on that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingTopic } = fields;
      let prompt =
        "Generate creative writing examples based on the following topic:\n";
      prompt += "Writing Topic: " + writingTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "smart-data-analyzer",
    name: "Smart Data Analyzer",
    slug: "smart-data-analyzer",
    shortDescription:
      "Unlock insights with Smart Data Analyzer: effortlessly interpret complex datasets and draw actionable conclusions.",
    fields: [
      {
        type: "textArea",
        label: "Dataset",
        field: "Dataset",
        placeholder: "Paste your dataset here",
        required: true,
        optional: false,
        id: "dataset",
      },
      {
        type: "textArea",
        label: "Type of Analysis",
        field: "Type of Analysis",
        placeholder: "Descriptive, predictive, etc.",
        required: false,
        optional: true,
        id: "typeofanalysis",
      },
      {
        type: "textArea",
        label: "Specific Questions",
        field: "Specific Questions",
        placeholder: "Any specific questions you want the analysis to answer",
        required: false,
        optional: true,
        id: "specificquestions",
      },
    ],
    categories: ["Business", "Marketing", "Education"],
    buildSystemPrompt: () => {
      let prompt = `You are an expert data analyst. You will be provided with a dataset and some additional information. Your task is to analyze the dataset and provide insights based on the information provided.`;
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Dataset, TypeOfAnalysis, SpecificQuestions } = fields;
      let prompt = `Analyze the following dataset:
  Dataset: ${Dataset}
  `;
      if (TypeOfAnalysis) {
        prompt += `Type of Analysis: ${TypeOfAnalysis}
  `;
      }
      if (SpecificQuestions) {
        prompt += `Specific Questions: ${SpecificQuestions}
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-rewriter",
    name: "Text Rewriter",
    slug: "text-rewriter",
    shortDescription:
      "Revamp your text effortlessly! Our Text Rewriter crafts high-quality rewrites while preserving the original meaning seamlessly.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Enter the text you want to rewrite here.",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting text. You will be provided with some original text, and your task is to rewrite it in a natural and high-quality manner while maintaining the original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Rewrite the following text while maintaining its original meaning:
  Original Text: ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "dangling-modifier-corrector",
    name: "Dangling Modifier Corrector",
    slug: "dangling-modifier-corrector",
    shortDescription:
      "Enhance clarity with our Dangling Modifier Corrector, rewriting sentences for improved grammar and readability.",
    fields: [
      {
        type: "textArea",
        label: "Original Sentence",
        field: "Original Sentence",
        placeholder:
          "Having finished the assignment, the Xbox was my next target.",
        required: true,
        optional: false,
        id: "originalsentence",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in English grammar and sentence structure. You will be provided with a sentence containing a dangling modifier, and you will rewrite it to correct the dangling modifier while maintaining the original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalSentence } = fields;
      let prompt = `Rewrite the following sentence to correct any dangling modifiers:
  Original Sentence: ${originalSentence}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-essay-expander",
    name: "AI Essay Expander",
    slug: "ai-essay-expander",
    shortDescription:
      "Enhance your essays with our AI Essay Expander, adding depth and relevant information for a more impactful writing experience.",
    fields: [
      {
        type: "textArea",
        label: "Original Essay",
        field: "Original Essay",
        placeholder: "The Industrial Revolution, which took place from...",
        required: true,
        optional: false,
        id: "originalessay",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in expanding and enhancing essays. You will be provided with an original essay, and you will expand it by adding depth and incorporating additional relevant information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalEssay } = fields;
      let prompt = `Expand and enhance the following essay by adding depth and incorporating additional relevant information:
  Original Essay: ${originalEssay}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "universal-ai-assistant",
    name: "Universal AI Assistant",
    slug: "universal-ai-assistant",
    shortDescription:
      "Discover the Universal AI Assistant that streamlines writing, research, and more to simplify your everyday tasks.",
    fields: [
      {
        type: "textArea",
        label: "Task Description",
        field: "Task Description",
        placeholder: "Write a blog post about the latest AI advancements",
        required: true,
        optional: false,
        id: "taskdescription",
      },
      {
        type: "textArea",
        label: "Additional Information (Optional)",
        field: "Additional Information (Optional)",
        placeholder: "Include a section about WritingTools.ai",
        required: false,
        optional: true,
        id: "additionalinformation(optional)",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert assistant capable of handling a wide range of tasks including writing, research, and more. You will be provided with some base information and you will generate content or perform tasks based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { taskDescription, additionalInformation } = fields;
      let prompt = `Perform the following task based on the given description:
  Task Description: ${taskDescription}.
  `;
      if (additionalInformation) {
        prompt += `Additional Information: ${additionalInformation}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-enhancer",
    name: "Essay Enhancer",
    slug: "essay-enhancer",
    shortDescription:
      "Enhance your essays effortlessly! Improve language, structure, and coherence for higher quality writing with our Essay Enhancer tool.",
    fields: [
      {
        type: "textArea",
        label: "Original Essay",
        field: "Original Essay",
        placeholder: "Write or paste your essay here.",
        required: true,
        optional: false,
        id: "originalessay",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing essays. You will be provided with an original essay and you will improve its language, structure, and coherence.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalEssay } = fields;
      let prompt = `Enhance the following essay by improving its language, structure, and coherence:
        
  Original Essay: ${originalEssay}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "medium-post-beautifier",
    name: "Medium Post Beautifier",
    slug: "medium-post-beautifier",
    shortDescription:
      "Transform your content with Medium Post Beautifier, enhancing and refining it for beautifully written Medium posts.",
    fields: [
      {
        type: "textArea",
        label: "Content",
        field: "Content",
        placeholder: "Enter the content you want to post on Medium here...",
        required: true,
        optional: false,
        id: "content",
      },
    ],
    categories: ["Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing and refining content for Medium posts. You will be provided with some base content, and your task is to transform it into a beautifully written Medium post.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Content } = fields;
      let prompt = `Enhance and refine the following content to create a beautifully written Medium post:
        
  Content: ${Content}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "argumentative-essay-evaluator",
    name: "Argumentative Essay Evaluator",
    slug: "argumentative-essay-evaluator",
    shortDescription:
      "Evaluate the strength and coherence of your argumentative essay with our expert evaluator tool for improved writing success.",
    fields: [
      {
        type: "textArea",
        label: "Argumentative Essay",
        field: "Argumentative Essay",
        placeholder: "Enter your argumentative essay here.",
        required: true,
        optional: false,
        id: "argumentativeessay",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in evaluating argumentative essays. You will be provided with an essay and you will evaluate its strength and coherence based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { argumentativeEssay } = fields;
      let prompt = `Evaluate the following argumentative essay for its strength and coherence:
        
  Essay: ${argumentativeEssay}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writing-style-adapter",
    name: "AI Writing Style Adapter",
    slug: "ai-writing-style-adapter",
    shortDescription:
      "Transform your text effortlessly with our AI Writing Style Adapter, matching various styles while preserving core content.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Enter the text you want to transform here...",
        required: true,
        optional: false,
        id: "originaltext",
      },
      {
        type: "textArea",
        label: "Desired Style",
        field: "Desired Style",
        placeholder: "Academic, Journalistic, Creative, etc.",
        required: false,
        optional: true,
        id: "desiredstyle",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in transforming text to match different writing styles. You will be provided with original text and a desired style, and you will adapt the text accordingly while preserving its core content.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText, desiredStyle } = fields;
      let prompt =
        "Transform the following text to match the desired writing style:\n";
      prompt += "Original Text: " + originalText + "\n";
      if (desiredStyle) {
        prompt += "Desired Style: " + desiredStyle + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-proofreader",
    name: "AI Proofreader",
    slug: "ai-proofreader",
    shortDescription:
      "Elevate your writing with our AI Proofreader, correcting grammar, typos, and inconsistencies for polished documents.",
    fields: [
      {
        type: "textArea",
        label: "Document",
        field: "Document",
        placeholder: "Enter the document text here.",
        required: true,
        optional: false,
        id: "document",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert proofreader. You will be provided with a document and you will proofread it for grammatical errors, typos, and inconsistencies. You will also suggest improvements.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Document } = fields;
      let prompt = `Proofread the following document:
  ${Document}
  Please check for grammatical errors, typos, inconsistencies, and suggest improvements.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "article-title-shortener",
    name: "Article Title Shortener",
    slug: "article-title-shortener",
    shortDescription:
      "Easily shorten long journal article titles with our Article Title Shortener for clear and concise presentation.",
    fields: [
      {
        type: "textArea",
        label: "Original Article Title",
        field: "Original Article Title",
        placeholder:
          "The Impact of Climate Change on the Migration Patterns of Birds in North America",
        required: true,
        optional: false,
        id: "originalarticletitle",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating concise and engaging titles for academic articles. You will be provided with a long article title, and your task is to shorten it while retaining its core message.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalArticleTitle } = fields;
      let prompt = `Shorten the following article title while keeping its main idea intact:
  Original Article Title: ${originalArticleTitle}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "dialogue-enhancing-assistant",
    name: "Dialogue Enhancing Assistant",
    slug: "dialogue-enhancing-assistant",
    shortDescription:
      "Transform your storytelling with our Dialogue Enhancing Assistant, perfect for crafting engaging dialogue and narrative prompts.",
    fields: [
      {
        type: "textArea",
        label: "Story Prompt",
        field: "Story Prompt",
        placeholder: "Yuuki: So, what's the plan, coach? ",
        required: true,
        optional: false,
        id: "storyprompt",
      },
      {
        type: "textArea",
        label: "Instructions",
        field: "Instructions",
        placeholder: "Write it like (X), make it more exciting, etc... ",
        required: false,
        optional: true,
        id: "instructions",
      },
      {
        type: "textArea",
        label: "Context",
        field: "Context",
        placeholder: "Team Red is five points behind Team Blue...",
        required: false,
        optional: true,
        id: "context",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a skilled dialogue writer. Your task is to continue a given story prompt with engaging dialogue and storytelling elements. Use the provided context and instructions to enhance the narrative.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { storyPrompt, instructions, context } = fields;
      let prompt =
        "Continue the following story with a focus on dialogue and storytelling:\n";
      prompt += "Story Prompt: " + storyPrompt + "\n";
      if (context) {
        prompt += "Context: " + context + "\n";
      }
      if (instructions) {
        prompt += "Instructions: " + instructions + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-story-reviewer",
    name: "AI Story Reviewer",
    slug: "ai-story-reviewer",
    shortDescription:
      "Get constructive feedback on your stories with our AI Story Reviewer, analyzing plot, character development, and writing style.",
    fields: [
      {
        type: "textArea",
        label: "Story Text",
        field: "Story Text",
        placeholder: "Once upon a time...",
        required: true,
        optional: false,
        id: "storytext",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in reviewing stories. You will be provided with a story text and you will provide constructive feedback on the plot, character development, and writing style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { storyText } = fields;
      let prompt = `Provide constructive feedback on the following story:
  Story Text: ${storyText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "draft-enhancer",
    name: "Draft Enhancer",
    slug: "draft-enhancer",
    shortDescription:
      "Enhance your drafts effortlessly! Improve language, structure, and coherence for polished, professional writing.",
    fields: [
      {
        type: "textArea",
        label: "Draft",
        field: "Draft",
        placeholder: "Paste your draft here...",
        required: true,
        optional: false,
        id: "draft",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing drafts. You will be provided with a draft and you will improve its language, structure, and coherence.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Draft } = fields;
      let prompt = `Enhance the following draft by refining its language, structure, and coherence:
        
  Draft: ${Draft}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "fragment-fixer",
    name: "Fragment Fixer",
    slug: "fragment-fixer",
    shortDescription:
      "Fix fragmented sentences effortlessly with Fragment Fixer, transforming them into complete, grammatically correct statements.",
    fields: [
      {
        type: "textArea",
        label: "Sentence Fragment",
        field: "Sentence Fragment",
        placeholder: "Running fast. Because I wanted to.",
        required: true,
        optional: false,
        id: "sentencefragment",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grammar and sentence structure. You will be provided with a sentence fragment and your task is to turn it into a complete, grammatically correct sentence.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { sentenceFragment } = fields;
      let prompt = `Correct the following sentence fragment:
  Sentence Fragment: ${sentenceFragment}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "simplicity-text-transformer",
    name: "Simplicity Text Transformer",
    slug: "simplicity-text-transformer",
    shortDescription:
      "Transform complex text into clear, understandable language with Simplicity Text Transformer for better communication and comprehension.",
    fields: [
      {
        type: "textArea",
        label: "Complex Paragraph",
        field: "Complex Paragraph",
        placeholder:
          "The epistemological conundrum that is quantum mechanics posits a non-deterministic universe...",
        required: true,
        optional: false,
        id: "complexparagraph",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in simplifying complex text. You will be provided with a complex paragraph, and your task is to transform it into more understandable language.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { complexParagraph } = fields;
      let prompt = `Simplify the following complex paragraph into more understandable language:
  ${complexParagraph}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "passive-voice-eliminator",
    name: "Passive Voice Eliminator",
    slug: "passive-voice-eliminator",
    shortDescription:
      "Eliminate passive voice effortlessly! Analyze and rewrite your narrative content for clearer, more engaging writing.",
    fields: [
      {
        type: "textArea",
        label: "Narrative Content",
        field: "Narrative Content",
        placeholder: "The cake was eaten by the dog.",
        required: true,
        optional: false,
        id: "narrativecontent",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in identifying and eliminating passive voice in narrative content. You will be provided with a piece of text, and your task is to rewrite it to remove any instances of passive voice.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { narrativeContent } = fields;
      let prompt = `Rewrite the following narrative content to eliminate any instances of passive voice:
  ${narrativeContent}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-essay-feedback",
    name: "AI Essay Feedback",
    slug: "ai-essay-feedback",
    shortDescription:
      "Get comprehensive AI essay feedback to enhance clarity, grammar, and argument structure for stronger writing outcomes.",
    fields: [
      {
        type: "textArea",
        label: "Essay Text",
        field: "Essay Text",
        placeholder: "Enter your essay text here.",
        required: true,
        optional: false,
        id: "essaytext",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing feedback on essays. You will be given an essay text and you will provide comprehensive feedback to improve clarity, grammar, and argument structure.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayText } = fields;
      let prompt = `Provide comprehensive feedback on the following essay:
  Essay Text: ${essayText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "assignment-assistant",
    name: "Assignment Assistant",
    slug: "assignment-assistant",
    shortDescription:
      "Get expert help with your assignments! Our Assignment Assistant offers explanations, solutions, and research support for students.",
    fields: [
      {
        type: "textArea",
        label: "Assignment Type",
        field: "Assignment Type",
        placeholder: "Research / Problem-solving / Writing",
        required: true,
        optional: false,
        id: "assignmenttype",
      },
      {
        type: "textArea",
        label: "Assignment Details",
        field: "Assignment Details",
        placeholder: "Write a 500-word essay on the impact of climate change.",
        required: false,
        optional: true,
        id: "assignmentdetails",
      },
    ],
    categories: ["Education", "Writing", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in assisting students with their assignments. You will be provided some base information and you will generate explanations, solutions, or research assistance based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { assignmentType, assignmentDetails } = fields;
      let prompt = `Generate assistance for the following assignment:
  Assignment Type: ${assignmentType}.
  `;
      if (assignmentDetails) {
        prompt += `Assignment Details: ${assignmentDetails}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "statement-deconstructor",
    name: "Statement Deconstructor",
    slug: "statement-deconstructor",
    shortDescription:
      "Enhance your critical thinking with the Statement Deconstructor, analyzing arguments for logical coherence and hidden assumptions.",
    fields: [
      {
        type: "textArea",
        label: "Input Statement",
        field: "Input Statement",
        placeholder: "All humans are mortal.",
        required: true,
        optional: false,
        id: "inputstatement",
      },
    ],
    categories: ["Education", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in logical analysis and argumentation. You will be provided with a statement and you will generate counter-arguments focusing on logical coherence, presuppositions, and conclusions.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { inputStatement } = fields;
      let prompt = `Generate counter-arguments for the following statement:
  Statement: ${inputStatement}
  Focus on logical coherence, presuppositions, and conclusions.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-simplifier",
    name: "Text Simplifier",
    slug: "text-simplifier",
    shortDescription:
      "Simplify complex text effortlessly! Our Text Simplifier replaces tough words with easy alternatives for clear understanding.",
    fields: [
      {
        type: "textArea",
        label: "Complex Text",
        field: "Complex Text",
        placeholder: "Enter the complex text here.",
        required: true,
        optional: false,
        id: "complextext",
      },
    ],
    categories: ["Education", "Writing", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in simplifying complex text. You will be provided with a piece of complex text, and your task is to simplify it by replacing difficult words with simpler alternatives.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { complexText } = fields;
      let prompt = `Simplify the following text:
  ${complexText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "writing-ai-assistant",
    name: "Writing AI Assistant",
    slug: "writing-ai-assistant",
    shortDescription:
      "Enhance your writing with our AI Assistant, completing tasks and conducting research to improve your final product effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Writing Task",
        field: "Writing Task",
        placeholder:
          "Write a blog post about the benefits of AI in healthcare.",
        required: true,
        optional: false,
        id: "writingtask",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert writing assistant. You will be provided with a specific writing task and you will generate content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingTask } = fields;
      let prompt = `Generate content based on the following writing task:
  The task is: ${writingTask}.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-expander-ai",
    name: "Text Expander AI",
    slug: "text-expander-ai",
    shortDescription:
      "Transform your text effortlessly with Text Expander AI, which rewrites and expands while preserving the original meaning.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "The quick brown fox jumps over the lazy dog.",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting and expanding text. You will be provided with an original text, and you will rewrite it to make it longer while maintaining its original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Rewrite and expand the following text to make it longer while maintaining its original meaning:
  Original Text: ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "grammar-and-delivery-enhancer",
    name: "Grammar and Delivery Enhancer",
    slug: "grammar-and-delivery-enhancer",
    shortDescription:
      "Enhance your writing with our Grammar and Delivery Enhancer, correcting grammar and improving text delivery effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Type or paste the text here...",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grammar correction and enhancing the delivery of written text. You will be provided with a piece of text, and your task is to correct any grammatical errors and improve its overall delivery.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Please correct the grammar and improve the delivery of the following text:
  ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "notes-to-blog-post",
    name: "Notes to Blog Post",
    slug: "notes-to-blog-post",
    shortDescription:
      "Transform your raw notes into engaging blog posts effortlessly. Learn how to structure and enhance your content effectively.",
    fields: [
      {
        type: "textArea",
        label: "Notes",
        field: "Notes",
        placeholder: "",
        required: true,
        optional: false,
        id: "notes",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in transforming raw notes into a well-structured and engaging blog post. You will be provided with a set of notes, and your task is to create a coherent and captivating blog post based on those notes.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Notes } = fields;
      let prompt =
        "Transform the following notes into a well-structured blog post:\n";
      prompt += "Notes: " + Notes + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-essay-reviewer",
    name: "AI Essay Reviewer",
    slug: "ai-essay-reviewer",
    shortDescription:
      "Get comprehensive feedback on your essays with our AI Essay Reviewer. Improve structure, grammar, coherence, and relevance effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Essay Text",
        field: "Essay Text",
        placeholder: "Enter your essay here",
        required: true,
        optional: false,
        id: "essaytext",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in reviewing essays. You will be provided with an essay text and you will analyze it to provide comprehensive feedback on its structure, grammar, coherence, and relevance.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayText } = fields;
      let prompt = `Analyze the following essay and provide comprehensive feedback on its structure, grammar, coherence, and relevance:
  Essay Text: ${essayText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "social-media-post-analyzer",
    name: "Social Media Post Analyzer",
    slug: "social-media-post-analyzer",
    shortDescription:
      "Analyze social media posts to uncover key features and themes, gaining valuable marketing insights for your strategy.",
    fields: [
      {
        type: "textArea",
        label: "Social Media Post",
        field: "Social Media Post",
        placeholder: "Paste the social media post content here",
        required: true,
        optional: false,
        id: "socialmediapost",
      },
    ],
    categories: ["Social Media", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing social media posts. You will be provided with a social media post and you will analyze it to extract key features and themes for marketing insights.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { socialMediaPost } = fields;
      let prompt = `Analyze the following social media post to extract key features and themes for marketing insights:
  Social Media Post: ${socialMediaPost}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "verbose-text-enhancer",
    name: "Verbose Text Enhancer",
    slug: "verbose-text-enhancer",
    shortDescription:
      "Transform your writing with our Verbose Text Enhancer, expanding and enriching your content while steering clear of clichés.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "It was a dark and stormy night...",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing and expanding text. You will be provided with a piece of original text, and your task is to expand it, making it longer and more detailed while avoiding cliches.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Expand and enhance the following text to make it longer and more detailed while avoiding cliches:
  Original Text: ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "make-this-sound-better-generator",
    name: "Make this Sound Better Generator",
    slug: "make-this-sound-better-generator",
    shortDescription:
      "Enhance your writing effortlessly with our Sound Better Generator—improving grammar and spelling while preserving your unique voice.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Type or paste your text here...",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing the quality of written text. You will be provided with some original text, and you will improve its grammar, fix spelling errors, and maintain its natural tone without altering the original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Enhance the following text by improving grammar, fixing spelling errors, and maintaining its natural tone without altering the original meaning:
        
  Original Text: ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "plain-language-converter",
    name: "Plain Language Converter",
    slug: "plain-language-converter",
    shortDescription:
      "Transform complex text into easy-to-understand language with our Plain Language Converter for clear communication.",
    fields: [
      {
        type: "textArea",
        label: "Complex Text",
        field: "Complex Text",
        placeholder: "Enter the complex text here",
        required: true,
        optional: false,
        id: "complextext",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in converting complex text into plain language. You will be provided with a piece of complex text, and you will convert it into plain language that is easy to understand.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { complexText } = fields;
      let prompt = `Convert the following complex text into plain language:
  Complex Text: ${complexText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-rewrite-in-specific-style",
    name: "AI Rewrite in Specific Style",
    slug: "ai-rewrite-in-specific-style",
    shortDescription:
      "Revamp your content effortlessly! Our AI rewrites text in your chosen style while preserving the original meaning.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "The quick brown fox jumps over the lazy dog.",
        required: true,
        optional: false,
        id: "originaltext",
      },
      {
        type: "textArea",
        label: "Desired Style",
        field: "Desired Style",
        placeholder: "Shakespearean",
        required: false,
        optional: true,
        id: "desiredstyle",
      },
    ],
    categories: [
      "Social Media",
      "Email",
      "Website",
      "Sales",
      "Support",
      "Writing",
      "Education",
      "Marketing",
      "Business",
    ],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting text in a specific style. You will be provided with original text and a desired style, and you will rewrite the text while maintaining its original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText, desiredStyle } = fields;
      let prompt = "Rewrite the following text in the specified style:\n";
      prompt += "Original Text: " + originalText + "\n";
      if (desiredStyle) {
        prompt += "Desired Style: " + desiredStyle + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "peel-paragraph-restructurer",
    name: "PEEL Paragraph Restructurer",
    slug: "peel-paragraph-restructurer",
    shortDescription:
      "Enhance clarity with the PEEL Paragraph Restructurer. Improve coherence using the Point, Explain, Evidence, Link method effectively.",
    fields: [
      {
        type: "textArea",
        label: "Paragraph",
        field: "Paragraph",
        placeholder: "Enter the paragraph you want to restructure.",
        required: true,
        optional: false,
        id: "paragraph",
      },
      {
        type: "textArea",
        label: "Essay Question",
        field: "Essay Question",
        placeholder: "What is the main topic or question of your essay?",
        required: false,
        optional: true,
        id: "essayquestion",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in restructuring paragraphs using the PEEL (Point, Explain, Evidence, Link) method. You will be provided with a paragraph and optionally an essay question. Your task is to restructure the paragraph to enhance its clarity and coherence.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Paragraph, EssayQuestion } = fields;
      let prompt = `Restructure the following paragraph using the PEEL method:
  Paragraph: ${Paragraph}.
  `;
      if (EssayQuestion) {
        prompt += `Essay Question: ${EssayQuestion}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "document-digest-question-generator",
    name: "Document Digest & Question Generator",
    slug: "document-digest-question-generator",
    shortDescription:
      "Generate practice questions and notes effortlessly with our Document Digest & Question Generator from your uploaded documents.",
    fields: [
      {
        type: "textArea",
        label: "Document/Text",
        field: "Document/Text",
        placeholder: "Paste your document or text here",
        required: true,
        optional: false,
        id: "document/text",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating educational content. You will be provided with a document or text, and you will generate a digest of the document along with practice questions based on the content.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { documentText } = fields;
      let prompt = `Generate a digest and practice questions based on the following document:
  Document/Text: ${documentText}
  The questions should include Multiple Choice, Fill in the blank, Matching, and Short Answer types.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-enhancer",
    name: "Resume Enhancer",
    slug: "resume-enhancer",
    shortDescription:
      "Enhance your resume's quality and relevance with our Resume Enhancer, updating your skills to stand out to employers.",
    fields: [
      {
        type: "textArea",
        label: "Existing Resume",
        field: "Existing Resume",
        placeholder: "Paste your existing resume here",
        required: true,
        optional: false,
        id: "existingresume",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing resumes. You will be provided with an existing resume, and you will enhance and update it to improve its quality and relevance.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { existingResume } = fields;
      let prompt = `Enhance and update the following resume to improve its quality and relevance:
  ${existingResume}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-expander",
    name: "Essay Expander",
    slug: "essay-expander",
    shortDescription:
      "Enhance your writing effortlessly with Essay Expander. Improve grammar and quality while expanding your ideas using similar language.",
    fields: [
      {
        type: "textArea",
        label: "Original Writing",
        field: "Original Writing",
        placeholder: "Enter the writing you want to expand...",
        required: true,
        optional: false,
        id: "originalwriting",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in expanding essays. You will be provided with a piece of original writing, and you will expand upon it while maintaining high quality, using similar language, and improving grammar.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalWriting } = fields;
      let prompt = `Expand the following piece of writing while maintaining high quality, using similar language, and improving grammar:
        
  Original Writing: ${originalWriting}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "scholar-ai-research-assistant",
    name: "Scholar AI Research Assistant",
    slug: "scholar-ai-research-assistant",
    shortDescription:
      "Scholar AI Research Assistant streamlines your research by finding relevant information from web searches and academic databases.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic, Term, or Request",
        field: "Research Topic, Term, or Request",
        placeholder:
          "Crosstalk between Immune Regulators in the Tumor Microenvironment",
        required: true,
        optional: false,
        id: "researchtopic,term,orrequest",
      },
    ],
    categories: ["Education", "Writing", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert research assistant specializing in academic and web-based research. You will be provided with a research topic, term, or request and you will conduct thorough research to gather relevant information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopicTermOrRequest } = fields;
      let prompt = `Conduct research based on the following topic, term, or request:
  Research Topic/Term/Request: ${researchTopicTermOrRequest}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "email-improver",
    name: "Email Improver",
    slug: "email-improver",
    shortDescription:
      "Transform clichéd email phrases into unique, natural language with our AI Email Improver. Enhance your communication effortlessly!",
    fields: [
      {
        type: "textArea",
        label: "Email Text",
        field: "Email Text",
        placeholder: "Dear John, I hope this email finds you well...",
        required: true,
        optional: false,
        id: "emailtext",
      },
    ],
    categories: ["Email", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in improving email text. You will be provided with an email text that may contain repetitive or clichéd phrases, and your task is to transform it into more unique and natural language while preserving the original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { emailText } = fields;
      let prompt = `Improve the following email text by transforming repetitive or clichéd phrases into more unique and natural language while preserving the original meaning:
  Email Text: ${emailText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-personal-assistant",
    name: "AI Personal Assistant",
    slug: "ai-personal-assistant",
    shortDescription:
      "Streamline your life with our AI Personal Assistant that tailors task management to your unique needs and requirements.",
    fields: [
      {
        type: "textArea",
        label: "Task Description",
        field: "Task Description",
        placeholder: "Please describe the task you need assistance with.",
        required: true,
        optional: false,
        id: "taskdescription",
      },
    ],
    categories: ["Business", "Support", "Email"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI personal assistant. You will be provided with a description of a task, and you will generate a response to assist the user in completing that task.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { taskDescription } = fields;
      let prompt = `Generate assistance for the following task:
  Task Description: ${taskDescription}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "pet-name-generator",
    name: "Pet Name Generator",
    slug: "pet-name-generator",
    shortDescription:
      "Generate unique pet names tailored to your preferences with our easy-to-use Pet Name Generator. Find the perfect name today!",
    fields: [
      {
        type: "textArea",
        label: "Pet Type",
        field: "Pet Type",
        placeholder: "Dog, Cat, Bird, etc.",
        required: true,
        optional: false,
        id: "pettype",
      },
      {
        type: "textArea",
        label: "Desired Characteristics",
        field: "Desired Characteristics",
        placeholder: "Friendly, Energetic, Calm, etc.",
        required: false,
        optional: true,
        id: "desiredcharacteristics",
      },
      {
        type: "textArea",
        label: "Specific Details",
        field: "Specific Details",
        placeholder: "Color, Breed, Size, etc.",
        required: false,
        optional: true,
        id: "specificdetails",
      },
    ],
    categories: ["Social Media", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt = `You are an expert in generating unique pet names. You will be provided with some base information and you will generate a pet name based on the information provided.`;
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { pettype, desiredcharacteristics, specificdetails } = fields;
      let prompt = `Generate a unique pet name based on the following details:
  `;
      prompt += `Pet Type: ${pettype}.
  `;
      if (desiredcharacteristics) {
        prompt += `Desired Characteristics: ${desiredcharacteristics}.
  `;
      }
      if (specificdetails) {
        prompt += `Specific Details: ${specificdetails}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "topic-explorer",
    name: "Topic Explorer",
    slug: "topic-explorer",
    shortDescription:
      "Explore Topic Explorer: Use AI to gather comprehensive information on any subject effortlessly and enhance your research process.",
    fields: [
      {
        type: "textArea",
        label: "Topic or Subject",
        field: "Topic or Subject",
        placeholder: "Artificial Intelligence",
        required: true,
        optional: false,
        id: "topicorsubject",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in gathering comprehensive information on various topics. You will be provided with a topic or subject, and you will gather detailed information about it using AI and other search methods.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicOrSubject } = fields;
      let prompt = `Gather comprehensive information on the following topic:
  Topic: ${topicOrSubject}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "youtube-video-summarizer",
    name: "YouTube Video Summarizer",
    slug: "youtube-video-summarizer",
    shortDescription:
      "Generate concise summaries of YouTube videos effortlessly with our YouTube Video Summarizer. Save time and enhance your viewing experience!",
    fields: [
      {
        type: "textArea",
        label: "YouTube Video Link",
        field: "YouTube Video Link",
        placeholder: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
        required: true,
        optional: false,
        id: "youtubevideolink",
      },
    ],
    categories: ["Social Media", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing YouTube videos. You will be provided with a link to a YouTube video, and you will generate a concise summary of the video's content.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { youtubeVideoLink } = fields;
      let prompt = `Generate a concise summary for the following YouTube video:
  YouTube Video Link: ${youtubeVideoLink}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "physics-ai",
    name: "Physics AI",
    slug: "physics-ai",
    shortDescription:
      "Get quick answers to physics questions, simplify complex concepts, and enhance problem-solving skills with Physics AI.",
    fields: [
      {
        type: "textArea",
        label: "Physics Question",
        field: "Physics Question",
        placeholder: "What is the theory of relativity?",
        required: true,
        optional: false,
        id: "physicsquestion",
      },
    ],
    categories: ["Education", "Support", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in physics. You will be provided with a physics-related question, and you will provide a detailed answer, explain complex concepts, and assist with problem-solving.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { physicsQuestion } = fields;
      let prompt =
        "Provide a comprehensive answer to the following physics question:\n";
      prompt += "Question: " + physicsQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "concise-article-summarizer",
    name: "Concise Article Summarizer",
    slug: "concise-article-summarizer",
    shortDescription:
      "Generate concise summaries from any article URL or pasted text quickly and effortlessly with our powerful Article Summarizer.",
    fields: [
      {
        type: "textArea",
        label: "Article URL",
        field: "Article URL",
        placeholder: "https://www.example.com/article or [pasted article text]",
        required: true,
        optional: false,
        id: "articleurl",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing articles. You will be provided with an article URL or text, and you will generate a concise summary based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { articleURL } = fields;
      let prompt = `Generate a concise summary of the following article:
  Article URL/Text: ${articleURL}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-story-continuation",
    name: "AI Story Continuation",
    slug: "ai-story-continuation",
    shortDescription:
      "Generate the next paragraph of your story effortlessly with our AI story continuation tool. Enhance your storytelling today!",
    fields: [
      {
        type: "textArea",
        label: "Paragraph",
        field: "Paragraph",
        placeholder:
          "The rain was pouring down on the city, creating a soothing melody on the rooftops...",
        required: true,
        optional: false,
        id: "paragraph",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in story writing. You will be provided with a paragraph of a story, and your task is to generate the next paragraph that continues the narrative seamlessly.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { paragraph } = fields;
      let prompt =
        "Generate the next paragraph of the story based on the following:\n";
      prompt += "Current paragraph: " + paragraph + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-paraphrasing",
    name: "AI Paraphrasing",
    slug: "ai-paraphrasing",
    shortDescription:
      "Transform your text effortlessly with our AI paraphrasing tool, rephrasing content while preserving its original meaning.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Enter the text you want to paraphrase here.",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in paraphrasing text. You will be provided with some original text, and your task is to rephrase it while maintaining its original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Paraphrase the following text while keeping its original meaning:
  Original Text: ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-corrector-and-formatter",
    name: "Text Corrector and Formatter",
    slug: "text-corrector-and-formatter",
    shortDescription:
      "Improve your writing with our Text Corrector and Formatter that fixes spelling, grammar, and punctuation for easy readability.",
    fields: [
      {
        type: "textArea",
        label: "Text to Correct and Format",
        field: "Text to Correct and Format",
        placeholder:
          "Here is the text that needs to be corrected and formatted...",
        required: true,
        optional: false,
        id: "texttocorrectandformat",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in correcting and formatting text. You will be provided with a piece of text that needs correction for spelling, grammar, and punctuation errors. Additionally, you will format the text into easy-to-read paragraphs.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToCorrectAndFormat } = fields;
      let prompt = `Correct and format the following text:
  ${textToCorrectAndFormat}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "devils-advocate-analyzer",
    name: "Devil's Advocate Analyzer",
    slug: "devils-advocate-analyzer",
    shortDescription:
      "Enhance critical thinking with the Devil's Advocate Analyzer, providing counterarguments for thorough consideration of all perspectives.",
    fields: [
      {
        type: "textArea",
        label: "Original Argument",
        field: "Original Argument",
        placeholder:
          "Artificial intelligence will replace all human jobs in the future.",
        required: true,
        optional: false,
        id: "originalargument",
      },
    ],
    categories: ["Education", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in critical thinking and argument analysis. You will be provided with an original argument, and your task is to generate counterarguments to encourage thorough consideration of all sides.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalArgument } = fields;
      let prompt = `Generate counterarguments for the following statement:
  The original argument is: "${originalArgument}".`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "university-administrator-ai-assistant",
    name: "University Administrator AI Assistant",
    slug: "university-administrator-ai-assistant",
    shortDescription:
      "Streamline your university administration with our AI assistant for speech writing, email drafting, report generation, and more.",
    fields: [
      {
        type: "textArea",
        label: "Task Description",
        field: "Task Description",
        placeholder:
          "Write a proposal to establish a new interdisciplinary program in sustainability studies. Start with an outline of the rationale, curriculum, potential partnerships, and expected outcomes.",
        required: true,
        optional: false,
        id: "taskdescription",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in assisting university administrators with various tasks. You will be provided some base information and you will generate content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { taskDescription } = fields;
      let prompt = `Generate content based on the following task description:
  Task Description: ${taskDescription}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "quick-summary-generator",
    name: "Quick Summary Generator",
    slug: "quick-summary-generator",
    shortDescription:
      "Effortlessly summarize information with our Quick Summary Generator, turning details into concise, memorable sentences.",
    fields: [
      {
        type: "textArea",
        label: "Information to Summarize",
        field: "Information to Summarize",
        placeholder: "Paste here...",
        required: true,
        optional: false,
        id: "informationtosummarize",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing information. You will be provided with some base information and you will generate a summary based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { informationToSummarize } = fields;
      let prompt = `Generate a quick summary based on the following information:
  ${informationToSummarize}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "friendly-tone-converter",
    name: "Friendly Tone Converter",
    slug: "friendly-tone-converter",
    shortDescription:
      "Transform your messages with our Friendly Tone Converter, making them sound warmer and more approachable while keeping the original content.",
    fields: [
      {
        type: "textArea",
        label: "Original Message",
        field: "Original Message",
        placeholder: "I don't appreciate your tone...",
        required: true,
        optional: false,
        id: "originalmessage",
      },
    ],
    categories: ["Email", "Support", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in communication and tone adjustment. You will be provided with an original message, and your task is to transform it into a friendlier version while maintaining the same content and voice.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalMessage } = fields;
      let prompt = `Transform the following message to sound friendlier and less passive-aggressive:
  Original Message: ${originalMessage}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "proposal-response-composer",
    name: "Proposal Response Composer",
    slug: "proposal-response-composer",
    shortDescription:
      "Streamline your proposal responses with our composer, delivering clear explanations on meeting requirements effectively.",
    fields: [
      {
        type: "textArea",
        label: "Proposal",
        field: "Proposal",
        placeholder: "We seek a solution for...",
        required: true,
        optional: false,
        id: "proposal",
      },
      {
        type: "textArea",
        label: "Your Resources and Capabilities",
        field: "Your Resources and Capabilities",
        placeholder: "We have a team of skilled engineers...",
        required: false,
        optional: true,
        id: "yourresourcesandcapabilities",
      },
    ],
    categories: ["Business", "Writing", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in composing responses to proposals. You will be provided with a proposal and information about resources and capabilities. Based on this information, you will generate a detailed response explaining how the requirements will be achieved.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Proposal, YourResourcesAndCapabilities } = fields;
      let prompt = `Generate a response to the following proposal:
  Proposal: ${Proposal}
  `;
      if (YourResourcesAndCapabilities) {
        prompt += `Your Resources and Capabilities: ${YourResourcesAndCapabilities}
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "resume-optimizer",
    name: "Resume Optimizer",
    slug: "resume-optimizer",
    shortDescription:
      "Enhance your resume with our optimizer that analyzes job descriptions and offers tailored suggestions for improvement.",
    fields: [
      {
        type: "textArea",
        label: "Resume",
        field: "Resume",
        placeholder: "Paste your resume here",
        required: true,
        optional: false,
        id: "resume",
      },
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "Paste the job description here",
        required: false,
        optional: true,
        id: "jobdescription",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in optimizing resumes. You will be provided with a resume and optionally a job description. Your task is to analyze the resume and provide suggestions to enhance it, making it more aligned with the job description if provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Resume, JobDescription } = fields;
      let prompt = `Analyze the following resume and provide suggestions for improvement:
  Resume: ${Resume}
  `;
      if (JobDescription) {
        prompt += `Job Description: ${JobDescription}
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paragraph-composer",
    name: "Paragraph Composer",
    slug: "paragraph-composer",
    shortDescription:
      "Create coherent, well-structured paragraphs effortlessly with our Paragraph Composer, tailored to your topic or idea.",
    fields: [
      {
        type: "textArea",
        label: "Topic or Idea",
        field: "Topic or Idea",
        placeholder: "The impact of climate change on wildlife",
        required: true,
        optional: false,
        id: "topicoridea",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in composing well-structured and coherent paragraphs. You will be provided with a topic or idea, and you will generate a paragraph based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicOrIdea } = fields;
      let prompt = `Generate a well-structured and coherent paragraph based on the following topic or idea:
  Topic or Idea: ${topicOrIdea}.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "post-response-writer",
    name: "Post Response Writer",
    slug: "post-response-writer",
    shortDescription:
      "Craft thoughtful responses effortlessly with our Post Response Writer, encouraging positive feedback and stimulating engaging discussions.",
    fields: [
      {
        type: "textArea",
        label: "Post Content",
        field: "Post Content",
        placeholder:
          "In this post, I discuss the impact of AI on the future of work...",
        required: true,
        optional: false,
        id: "postcontent",
      },
    ],
    categories: ["Social Media", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing thoughtful responses to posts. You will be provided with the content of a post and you will generate a response that provides positive feedback and includes an open-ended question to stimulate further discussion.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { postContent } = fields;
      let prompt = `Generate a response to the following post:
  Post Content: ${postContent}
  The response should provide positive feedback and include an open-ended question to stimulate further discussion.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-rewriter",
    name: "AI Rewriter",
    slug: "ai-rewriter",
    shortDescription:
      "Transform your text effortlessly with our AI Rewriter, ensuring natural quality while preserving original meaning without duplication.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Enter the text that needs to be rewritten here",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting text. You will be provided with some original text, and you will rewrite it to sound natural and high quality while maintaining the same meaning without copying the same phrases.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Rewrite the following text to sound natural and high quality while maintaining the same meaning:
  Original Text: ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "outline-to-blog-post",
    name: "Outline to Blog Post",
    slug: "outline-to-blog-post",
    shortDescription:
      "Generate detailed blog posts effortlessly with our outline tool, transforming summaries into engaging content.",
    fields: [
      {
        type: "textArea",
        label: "Blog Post Outline or Summary",
        field: "Blog Post Outline or Summary",
        placeholder:
          "1. Introduction to the topic... 2. Importance of the topic... 3. Detailed discussion on the topic... 4. Conclusion",
        required: true,
        optional: false,
        id: "blogpostoutlineorsummary",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing detailed and engaging blog posts. You will be provided with an outline or summary, and you will generate a comprehensive blog post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { blogPostOutlineOrSummary } = fields;
      let prompt = `Generate a detailed blog post based on the following outline or summary:
  ${blogPostOutlineOrSummary}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "devops-query-interpreter",
    name: "DevOps Query Interpreter",
    slug: "devops-query-interpreter",
    shortDescription:
      "Effortlessly interpret DevOps queries with our intelligent interpreter that responds based on the context of your provided text.",
    fields: [
      {
        type: "textArea",
        label: "Query Text",
        field: "Query Text",
        placeholder:
          "We have a pipeline where we upload data, clean domains before uploading, and then the files are enriched with business data. After enrichment, we transfer the data to campaigns. We also have a CRM and use webhooks.",
        required: true,
        optional: false,
        id: "querytext",
      },
    ],
    categories: ["Support", "Business", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in DevOps practices and tools. You will be provided with a query related to DevOps processes or issues, and you will generate a detailed response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { queryText } = fields;
      let prompt = `Generate a detailed response for the following DevOps-related query:
  Query: ${queryText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writing-editor",
    name: "AI Writing Editor",
    slug: "ai-writing-editor",
    shortDescription:
      "Elevate your writing with our AI Writing Editor, offering suggestions, editing, and proofreading for high-quality content creation.",
    fields: [
      {
        type: "textArea",
        label: "Text to Edit",
        field: "Text to Edit",
        placeholder: "Enter the text you want to improve here...",
        required: true,
        optional: false,
        id: "texttoedit",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing and editing. You will be provided with a piece of text, and your task is to improve its quality by providing suggestions, editing for clarity, grammar, and style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToEdit } = fields;
      let prompt = `Here is a piece of text that needs improvement:
  ${textToEdit}
  
  Please provide suggestions for improving the text, including edits for clarity, grammar, and style.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "note-summarizer-and-quiz-creator",
    name: "Note Summarizer and Quiz Creator",
    slug: "note-summarizer-and-quiz-creator",
    shortDescription:
      "Summarize your notes effortlessly and create quizzes with ease using our Note Summarizer and Quiz Creator tool.",
    fields: [
      {
        type: "textArea",
        label: "Notes",
        field: "Notes",
        placeholder: "The mitochondria is the powerhouse of the cell...",
        required: true,
        optional: false,
        id: "notes",
      },
    ],
    categories: ["Education", "Training Material", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing notes and creating quiz questions. You will be provided with some notes, and you will generate a summary and quiz questions based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Notes } = fields;
      let prompt = `Generate a summary and quiz questions based on the following notes:
  ${Notes}
  `;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "legal-lingo-translator",
    name: "Legal Lingo Translator",
    slug: "legal-lingo-translator",
    shortDescription:
      "Transform everyday text into precise legal terminology with our Legal Lingo Translator for clear and effective communication.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "We agree to provide you with...",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Legal", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in translating regular text into legal terminology. You will be provided with some base information and you will generate a legally formatted text based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Translate the following text into legal terminology:
  Original Text: ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paper-conclusion-generator",
    name: "Paper Conclusion Generator",
    slug: "paper-conclusion-generator",
    shortDescription:
      "Generate compelling conclusions for your papers easily with our Paper Conclusion Generator, based on key points and thesis statements.",
    fields: [
      {
        type: "textArea",
        label: "Paper",
        field: "Paper",
        placeholder: "Paste your text here",
        required: true,
        optional: false,
        id: "paper",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in academic writing. You will be provided with the main points and thesis statement of a paper, and you will generate a compelling conclusion based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Paper } = fields;
      let prompt = `Generate a conclusion for the following paper:
  ${Paper}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "personal-statement-polisher",
    name: "Personal Statement Polisher",
    slug: "personal-statement-polisher",
    shortDescription:
      "Enhance your personal statement for college or grad school with our polishing service to improve readability and impact.",
    fields: [
      {
        type: "textArea",
        label: "Personal Statement",
        field: "Personal Statement",
        placeholder:
          "Tell us about your background, experiences, and why you are interested in our program...",
        required: true,
        optional: false,
        id: "personalstatement",
      },
      {
        type: "textArea",
        label: "Purpose",
        field: "Purpose",
        placeholder: "",
        required: false,
        optional: true,
        id: "purpose",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in refining and enhancing personal statements for college or graduate school applications. You will be provided with a personal statement and additional information to improve its readability and impact.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { personalStatement, purpose } = fields;
      let prompt = `Refine and enhance the following personal statement:
        
  Personal Statement: ${personalStatement}
  `;
      if (purpose) {
        prompt += `Purpose: ${purpose}
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "professional-writing-reviewer",
    name: "Professional Writing Reviewer",
    slug: "professional-writing-reviewer",
    shortDescription:
      "Enhance your writing with professional reviews that improve clarity, grammar, style, and consistency for impactful communication.",
    fields: [
      {
        type: "textArea",
        label: "Text to Review",
        field: "Text to Review",
        placeholder: "Enter the text you want to review here.",
        required: true,
        optional: false,
        id: "texttoreview",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in reviewing professional writing. You will be provided with a piece of text and you will review it for clarity, conciseness, consistency, vocabulary, grammar, punctuation, and style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToReview } = fields;
      let prompt = `Review the following text for clarity, conciseness, consistency, vocabulary, grammar, punctuation, and style:
  ${textToReview}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "simplifier",
    name: "Simplifier",
    slug: "simplifier",
    shortDescription:
      "Transform complex language into simple, easy-to-understand terms with Simplifier. Communicate clearly and effectively!",
    fields: [
      {
        type: "textArea",
        label: "Input Text",
        field: "Input Text",
        placeholder:
          "Enter the complex words, phrases, titles, or statements here.",
        required: true,
        optional: false,
        id: "inputtext",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in simplifying complex language. You will be provided with some text and you will simplify it into easy-to-understand language.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { inputText } = fields;
      let prompt = `Simplify the following text:
  ${inputText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-email-response-optimizer",
    name: "AI Email Response Optimizer",
    slug: "ai-email-response-optimizer",
    shortDescription:
      "Enhance your email responses with our AI Email Response Optimizer for clarity, professionalism, and key point inclusion.",
    fields: [
      {
        type: "textArea",
        label: "Received Email",
        field: "Received Email",
        placeholder: "Dear John, I hope this email finds you well...",
        required: true,
        optional: false,
        id: "receivedemail",
      },
      {
        type: "textArea",
        label: "Draft Response or Key Points to Include",
        field: "Draft Response or Key Points to Include",
        placeholder: "Hi Sarah, Thank you for your email...",
        required: false,
        optional: true,
        id: "draftresponseorkeypointstoinclude",
      },
    ],
    categories: ["Email", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in optimizing email responses. You will be provided with a received email and optionally a draft response or key points to include. Your task is to provide feedback and enhancements for the response, ensuring clarity, professionalism, and inclusion of key points.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { receivedEmail, draftResponseOrKeyPoints } = fields;
      let prompt = `Optimize the following email response based on the received email:
  Received Email: ${receivedEmail}
  `;
      if (draftResponseOrKeyPoints) {
        prompt += `Draft Response or Key Points to Include: ${draftResponseOrKeyPoints}
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "instagram-caption-improver",
    name: "Instagram Caption Improver",
    slug: "instagram-caption-improver",
    shortDescription:
      "Enhance your Instagram captions with our Caption Improver, making them more appealing and engaging for your audience.",
    fields: [
      {
        type: "textArea",
        label: "Original Instagram Caption",
        field: "Original Instagram Caption",
        placeholder:
          "I met with @melindafrenchgates to discuss women in tech and it was amazing. I learned a lot from her and she is even more wonderful in person. She is extremely tech-savvy, decisive, inquisitive, compassionate, and focused on people (she gave me a great example about how problems can be solved in 100 different ways depending on who it is). Thank you, Melinda, for your leadership.",
        required: true,
        optional: false,
        id: "originalinstagramcaption",
      },
    ],
    categories: ["Social Media", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing Instagram captions. You will be provided with an original caption and you will improve its quality and appeal.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalInstagramCaption } = fields;
      let prompt = `Improve the following Instagram caption:
  Original caption: ${originalInstagramCaption}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "first-chapter-critique",
    name: "First Chapter Critique",
    slug: "first-chapter-critique",
    shortDescription:
      "Get a detailed critique of your novel's first chapter, including insights on plot, characters, and writing style for improvement.",
    fields: [
      {
        type: "textArea",
        label: "First Chapter",
        field: "First Chapter",
        placeholder: "It was a dark and stormy night...",
        required: true,
        optional: false,
        id: "firstchapter",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert literary critic. You will be provided with the first chapter of a novel and you will generate a detailed critique based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { firstChapter } = fields;
      let prompt = `Generate a detailed critique of the following first chapter of a novel:
  ${firstChapter}
  The critique should include feedback on plot, characters, and writing style.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "sound-smarter-ai",
    name: "Sound Smarter AI",
    slug: "sound-smarter-ai",
    shortDescription:
      "Enhance your writing's sophistication with Sound Smarter AI, maintaining a natural tone while elevating eloquence and clarity.",
    fields: [
      {
        type: "textArea",
        label: "Text to Enhance",
        field: "Text to Enhance",
        placeholder: "I think this is a good idea because...",
        required: true,
        optional: false,
        id: "texttoenhance",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing the sophistication and eloquence of any text while maintaining a natural tone. You will be provided with some base text, and you will enhance it to sound smarter.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToEnhance } = fields;
      let prompt = `Enhance the following text to make it sound smarter while maintaining a natural tone:
  Text: ${textToEnhance}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "peer-review-response-generator",
    name: "Peer Review Response Generator",
    slug: "peer-review-response-generator",
    shortDescription:
      "Generate constructive responses to peer reviews effortlessly with our Peer Review Response Generator for academic work.",
    fields: [
      {
        type: "textArea",
        label: "Peer Review or Feedback",
        field: "Peer Review or Feedback",
        placeholder:
          "The methodology used in the study could be improved by...",
        required: true,
        optional: false,
        id: "peerrevieworfeedback",
      },
      {
        type: "textArea",
        label: "Your Initial Response",
        field: "Your Initial Response",
        placeholder:
          "Thank you for your feedback. I agree that the methodology could...",
        required: false,
        optional: true,
        id: "yourinitialresponse",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating constructive responses to peer reviews or feedback on academic work. You will be provided with some base information and you will generate a response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { peerReviewOrFeedback, yourInitialResponse } = fields;
      let prompt = `Generate a constructive response to the following peer review or feedback:
  Peer Review or Feedback: ${peerReviewOrFeedback}.
  `;
      if (yourInitialResponse) {
        prompt += `Your Initial Response: ${yourInitialResponse}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-helper",
    name: "AI Helper",
    slug: "ai-helper",
    shortDescription:
      "Get high-quality AI assistance for writing, research, and more. Complete your tasks efficiently with our versatile AI Helper.",
    fields: [
      {
        type: "textArea",
        label: "Task Description",
        field: "Task Description",
        placeholder: "Describe the task you want the AI to complete",
        required: true,
        optional: false,
        id: "taskdescription",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert AI assistant capable of completing a wide range of tasks. You will be provided with a task description, and you will generate a response that fulfills the user's request.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { taskDescription } = fields;
      let prompt = `Generate a response based on the following task description:
  Task Description: ${taskDescription}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-key-details-extractor",
    name: "Text Key Details Extractor",
    slug: "text-key-details-extractor",
    shortDescription:
      "Effortlessly analyze text with our Key Details Extractor, designed to extract essential information quickly and efficiently.",
    fields: [
      {
        type: "textArea",
        label: "Text to Analyze",
        field: "Text to Analyze",
        placeholder:
          "The National Aeronautics and Space Administration (NASA) is an independent agency of the U.S. federal government responsible for the civil space program, aeronautics research, and space research....",
        required: true,
        optional: false,
        id: "texttoanalyze",
      },
    ],
    categories: ["Business", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing text and extracting key details. You will be provided with a block of text, and you will extract the most important information from it.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToAnalyze } = fields;
      let prompt = `Analyze the following text and extract the key details:\n\n${textToAnalyze}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "etsy-shop-analyzer",
    name: "Etsy Shop Analyzer",
    slug: "etsy-shop-analyzer",
    shortDescription:
      "Analyze your Etsy shop for insights on unique selling propositions, aesthetic style, and overall customer experience.",
    fields: [
      {
        type: "textArea",
        label: "Etsy Shop Description",
        field: "Etsy Shop Description",
        placeholder: "Introduction to WhiteGalleria on Etsy...",
        required: true,
        optional: false,
        id: "etsyshopdescription",
      },
    ],
    categories: ["Marketing", "Business", "Sales"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing Etsy shops. You will be provided with a description of an Etsy shop, and you will generate a detailed analysis based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { etsyShopDescription } = fields;
      let prompt = `Generate a detailed analysis of the following Etsy shop based on its description:
  Etsy Shop Description: ${etsyShopDescription}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-scrambler",
    name: "AI Scrambler",
    slug: "ai-scrambler",
    shortDescription:
      "Transform your text with AI Scrambler: create original, natural-sounding content effortlessly and enhance your writing today!",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Enter the text you want to rewrite here.",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting text to create original and natural sounding versions. You will be provided with some base text, and you will generate a rewritten version of that text.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Rewrite the following text to create an original and natural sounding version:
  Original Text: ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-text-rewriter",
    name: "AI Text Rewriter",
    slug: "ai-text-rewriter",
    shortDescription:
      "Transform your text effortlessly with our AI Text Rewriter, preserving meaning while enhancing phrasing and structure.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "The quick brown fox jumps over the lazy dog.",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting text. You will be provided with some original text, and you will rewrite it to maintain the original meaning while changing the phrasing and structure.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Rewrite the following text while maintaining its original meaning:
  Original Text: ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-text-generator",
    name: "AI Text Generator",
    slug: "ai-text-generator",
    shortDescription:
      "Generate high-quality text effortlessly with our AI Text Generator, tailored to your unique prompts and instructions.",
    fields: [
      {
        type: "textArea",
        label: "Instructions or Prompt",
        field: "Instructions or Prompt",
        placeholder:
          "Write a short story about a pirate who's afraid of the sea.",
        required: true,
        optional: false,
        id: "instructionsorprompt",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert AI text generator. You will be provided with specific instructions or prompts, and you will generate text based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { InstructionsOrPrompt } = fields;
      let prompt = `Generate text based on the following instructions or prompt:
  ${InstructionsOrPrompt}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-agent",
    name: "AI Agent",
    slug: "ai-agent",
    shortDescription:
      "Discover how an AI Agent efficiently executes tasks through browser functions based on your user input for seamless automation.",
    fields: [
      {
        type: "textArea",
        label: "Task",
        field: "Task",
        placeholder: "Order pizza from Domino's",
        required: true,
        optional: false,
        id: "task",
      },
    ],
    categories: ["Business", "Support", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI agent capable of executing various tasks using a browser. You will be provided with a specific task, and you need to perform this task accurately.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Task } = fields;
      let prompt = `Execute the following task: ${Task}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "writing-perfectionist",
    name: "Writing Perfectionist",
    slug: "writing-perfectionist",
    shortDescription:
      "Enhance your writing with our perfectionist tool that improves grammar, style, and clarity for polished, professional pieces.",
    fields: [
      {
        type: "textArea",
        label: "Written Piece",
        field: "Written Piece",
        placeholder: "Enter your written piece here...",
        required: true,
        optional: false,
        id: "writtenpiece",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing and perfecting written pieces. You will be provided with a written piece and you will improve its grammar, style, and clarity.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writtenPiece } = fields;
      let prompt = `Enhance and perfect the following written piece by improving its grammar, style, and clarity:
  ${writtenPiece}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "personal-narrative-summarizer",
    name: "Personal Narrative Summarizer",
    slug: "personal-narrative-summarizer",
    shortDescription:
      "Transform your personal experiences into engaging narratives with our summarizer, featuring a captivating news hook in every summary.",
    fields: [
      {
        type: "textArea",
        label: "Personal Story",
        field: "Personal Story",
        placeholder: "Write your personal story here...",
        required: true,
        optional: false,
        id: "personalstory",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing personal narratives. You will be provided with a personal story and you will generate a summary that is clear, concise, and engaging, with a news hook in the first sentence.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { personalStory } = fields;
      let prompt = `Generate a summary of the following personal story:
  ${personalStory}
  The summary should be clear, concise, and engaging, with a news hook in the first sentence.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "botany-assistant",
    name: "Botany Assistant",
    slug: "botany-assistant",
    shortDescription:
      "Get quick answers about any plant with Botany Assistant. Simply input the plant's name or description for expert insights!",
    fields: [
      {
        type: "textArea",
        label: "Question",
        field: "Question",
        placeholder: "What is the ideal soil pH for roses?",
        required: true,
        optional: false,
        id: "question",
      },
    ],
    categories: ["Education", "Support", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert botanist. You will be provided with questions about specific plants, and you will provide detailed and accurate answers based on your extensive knowledge of botany.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Question } = fields;
      let prompt = `Answer the following question about a plant:
  Question: ${Question}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-continuer",
    name: "Text Continuer",
    slug: "text-continuer",
    shortDescription:
      "Enhance your writing with Text Continuer, which seamlessly extends essays, stories, or blog posts in the same style and format.",
    fields: [
      {
        type: "textArea",
        label: "Text to Continue",
        field: "Text to Continue",
        placeholder: "In the heart of the city, there was a small cafe...",
        required: true,
        optional: false,
        id: "texttocontinue",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert writer. You will be provided with a piece of text and your task is to continue it in the same style and format.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToContinue } = fields;
      let prompt = `Continue the following text in the same style and format:
  ${textToContinue}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-based-request-fulfiller",
    name: "Text-Based Request Fulfiller",
    slug: "text-based-request-fulfiller",
    shortDescription:
      "Effortlessly fulfill user requests with our text-based tool, transforming articles into actionable insights for enhanced content engagement.",
    fields: [
      {
        type: "textArea",
        label: "Text or Article Content",
        field: "Text or Article Content",
        placeholder: "In a recent study, researchers found that...",
        required: true,
        optional: false,
        id: "textorarticlecontent",
      },
      {
        type: "textArea",
        label: "User Request",
        field: "User Request",
        placeholder: "Summarize the main points of this text.",
        required: false,
        optional: true,
        id: "userrequest",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in fulfilling user requests based on provided text or article content. You will be given some base information and you will generate a response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textOrArticleContent, userRequest } = fields;
      let prompt = `Generate a response based on the following:
  The text or article content is: ${textOrArticleContent}.
  `;
      if (userRequest) {
        prompt += `User request: ${userRequest}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-proofreading-partner",
    name: "AI Proofreading Partner",
    slug: "ai-proofreading-partner",
    shortDescription:
      "Enhance your writing with our AI proofreading partner that corrects errors and suggests improvements for clarity and style.",
    fields: [
      {
        type: "textArea",
        label: "Text to Proofread",
        field: "Text to Proofread",
        placeholder: "Enter the text you want to proofread here.",
        required: true,
        optional: false,
        id: "texttoproofread",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert proofreader. You will be provided with a piece of text, and your task is to identify and correct grammar, spelling, and punctuation errors. Additionally, suggest improvements for clarity and style.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToProofread } = fields;
      let prompt = `Proofread the following text:
  ${textToProofread}
  
  Identify and correct any grammar, spelling, or punctuation errors. Also, suggest improvements for clarity and style.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-paragraph-polisher",
    name: "AI Paragraph Polisher",
    slug: "ai-paragraph-polisher",
    shortDescription:
      "Enhance your writing with our AI Paragraph Polisher, refining word choice and sentence structure for improved coherence.",
    fields: [
      {
        type: "textArea",
        label: "Paragraph",
        field: "Paragraph",
        placeholder: "Enter the paragraph you want to refine here...",
        required: true,
        optional: false,
        id: "paragraph",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in refining and enhancing paragraphs. You will be provided with a paragraph that needs improvement. Your task is to improve word choice, sentence structure, and overall coherence while maintaining the original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Paragraph } = fields;
      let prompt = `Refine and enhance the following paragraph:
  ${Paragraph}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "reword",
    name: "Reword",
    slug: "reword",
    shortDescription:
      "Reword any text effortlessly to create a unique version while keeping the original meaning intact. Transform your writing today!",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Enter the text you want to reword.",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewording text. You will be provided with some base information and you will generate a new, unique version of the text while preserving its original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Reword the following text to create a new, unique version while preserving the original meaning:
  Original Text: ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "content-condenser",
    name: "Content Condenser",
    slug: "content-condenser",
    shortDescription:
      "Transform your content with Content Condenser: condense any text to 100 words while retaining the original message effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Original Content",
        field: "Original Content",
        placeholder: "Enter the content you want condensed here.",
        required: true,
        optional: false,
        id: "originalcontent",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in condensing content. You will be provided with some original content, and your task is to condense it to 100 words while preserving the original message.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalContent } = fields;
      let prompt = `Condense the following content to 100 words while preserving the original message:
  ${originalContent}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-breakdown-with-subheadings",
    name: "Text Breakdown with Subheadings",
    slug: "text-breakdown-with-subheadings",
    shortDescription:
      "Easily break down text into meaningful paragraphs with relevant subheadings, preserving key ideas and enhancing readability.",
    fields: [
      {
        type: "textArea",
        label: "Input Text",
        field: "Input Text",
        placeholder: "Enter the text to be broken down.",
        required: true,
        optional: false,
        id: "inputtext",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in breaking down texts into well-structured paragraphs with relevant subheadings. You will be provided with a text and you will generate a breakdown of this text, ensuring that the meaning and key points are preserved.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { inputText } = fields;
      let prompt = `Break down the following text into paragraphs with relevant subheadings:
        
  Text: ${inputText}
        
  Ensure that the meaning and key points are preserved.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "product-review-analyzer",
    name: "Product Review Analyzer",
    slug: "product-review-analyzer",
    shortDescription:
      "Analyze product reviews effortlessly with our tool—get summaries, sentiment scores, and suggested responses in seconds!",
    fields: [
      {
        type: "textArea",
        label: "Product Review",
        field: "Product Review",
        placeholder: "I recently purchased this product and...",
        required: true,
        optional: false,
        id: "productreview",
      },
    ],
    categories: ["Support", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing product reviews. You will be provided with a product review and you will generate a summary, sentiment score, and suggested responses based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { productReview } = fields;
      let prompt = `Analyze the following product review:
  Review: ${productReview}
  Generate a summary, sentiment score, and suggested responses based on the review.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "scholarai-research-copilot",
    name: "ScholarAI Research Copilot",
    slug: "scholarai-research-copilot",
    shortDescription:
      "Streamline your research with ScholarAI Research Copilot: get answers and find reputable academic sources effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Research Question",
        field: "Research Question",
        placeholder: "What are the effects of climate change on polar bears?",
        required: true,
        optional: false,
        id: "researchquestion",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in academic research. You will be provided with a research question, and you will generate a detailed response based on reputable academic sources.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchQuestion } = fields;
      let prompt = `Generate a detailed response to the following research question using reputable academic sources:
  Research Question: ${researchQuestion}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "success-journey-analyzer",
    name: "Success Journey Analyzer",
    slug: "success-journey-analyzer",
    shortDescription:
      "Analyze your success journey with insights tailored to you. Discover what drives your achievements and potential for growth.",
    fields: [
      {
        type: "textArea",
        label: "Person's Name",
        field: "Person's Name",
        placeholder: "Elon Musk",
        required: true,
        optional: false,
        id: "personsname",
      },
      {
        type: "textArea",
        label: "User Appeal",
        field: "User Appeal",
        placeholder: "I admire risk-taking and innovative thinking.",
        required: false,
        optional: true,
        id: "userappeal",
      },
    ],
    categories: ["Education", "Business", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing and providing insights on a person's success journey. You will be provided with some base information and you will generate an analysis based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { personName, userAppeal } = fields;
      let prompt = `Generate an analysis of the success journey of ${personName}.`;
      if (userAppeal) {
        prompt += ` The analysis should appeal to someone who admires ${userAppeal}.`;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-grammar-checker",
    name: "AI Grammar Checker",
    slug: "ai-grammar-checker",
    shortDescription:
      "Enhance your writing with our AI grammar checker that corrects grammar, punctuation, and spelling errors in any text instantly.",
    fields: [
      {
        type: "textArea",
        label: "Text to Check",
        field: "Text to Check",
        placeholder: "Type or paste the text you want to check here.",
        required: true,
        optional: false,
        id: "texttocheck",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grammar, punctuation, and spelling correction. You will be provided with a piece of text, and your task is to identify and correct any errors in it.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToCheck } = fields;
      let prompt = `Please check the following text for grammar, punctuation, and spelling errors:
  ${textToCheck}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-financial-advisor",
    name: "AI Financial Advisor",
    slug: "ai-financial-advisor",
    shortDescription:
      "Get personalized investment planning and financial advice with our AI Financial Advisor, tailored to your goals and risk tolerance.",
    fields: [
      {
        type: "textArea",
        label: "Financial Goals",
        field: "Financial Goals",
        placeholder: "Retire by 60 with $1M in savings",
        required: true,
        optional: false,
        id: "financialgoals",
      },
      {
        type: "textArea",
        label: "Risk Tolerance",
        field: "Risk Tolerance",
        placeholder: "Low/Medium/High",
        required: false,
        optional: true,
        id: "risktolerance",
      },
      {
        type: "textArea",
        label: "Current Financial Situation",
        field: "Current Financial Situation",
        placeholder:
          "Annual income, current savings, current investments, monthly expenses, etc.",
        required: false,
        optional: true,
        id: "currentfinancialsituation",
      },
    ],
    categories: ["Business", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt = `You are an expert financial advisor. You will be provided with some base information about a user's financial goals, risk tolerance, and current financial situation. Based on this information, you will generate personalized financial advice and investment planning.`;
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { financialGoals, riskTolerance, currentFinancialSituation } =
        fields;
      let prompt = `Generate personalized financial advice based on the following information:
  `;
      prompt += `Financial Goals: ${financialGoals}.
  `;
      if (riskTolerance) {
        prompt += `Risk Tolerance: ${riskTolerance}.
  `;
      }
      if (currentFinancialSituation) {
        prompt += `Current Financial Situation: ${currentFinancialSituation}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "semantic-text-splitter",
    name: "Semantic Text Splitter",
    slug: "semantic-text-splitter",
    shortDescription:
      "Effortlessly enhance your content with our Semantic Text Splitter, generating relevant subheadings by analyzing text sections.",
    fields: [
      {
        type: "textArea",
        label: "Input Text",
        field: "Input Text",
        placeholder: "Enter the text you want to split into sections...",
        required: true,
        optional: false,
        id: "inputtext",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in text analysis and segmentation. You will be provided with a block of text, and your task is to split this text into meaningful sections based on its semantic content. Additionally, generate relevant subheadings for each section.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { inputText } = fields;
      let prompt = `Split the following text into sections based on its semantic content and generate relevant subheadings for each section:
        
  Text: ${inputText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-grammar-and-style-corrector",
    name: "AI Grammar and Style Corrector",
    slug: "ai-grammar-and-style-corrector",
    shortDescription:
      "Enhance your writing with our AI Grammar and Style Corrector, perfecting grammar, punctuation, and style effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Text to Correct",
        field: "Text to Correct",
        placeholder: "Enter the text you want corrected here.",
        required: true,
        optional: false,
        id: "texttocorrect",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grammar and style correction. You will be provided with a piece of text, and your task is to correct any grammatical errors, improve punctuation, and enhance the overall style of the text.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToCorrect } = fields;
      let prompt = `Correct the following text for grammar, punctuation, and style:
  ${textToCorrect}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-ai",
    name: "Essay AI",
    slug: "essay-ai",
    shortDescription:
      "Create high-quality essay drafts effortlessly with Essay AI, your ultimate assistant for structure and ideas, not a replacement for your voice.",
    fields: [
      {
        type: "textArea",
        label: "Essay Topic or Request",
        field: "Essay Topic or Request",
        placeholder: "The Impact of Climate Change on Global Economy",
        required: true,
        optional: false,
        id: "essaytopicorrequest",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating well-structured and high-quality essay drafts. You will be provided with an essay topic or request, and you will generate a draft based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayTopicOrRequest } = fields;
      let prompt = `Generate an essay draft based on the following topic or request:
  Essay Topic or Request: ${essayTopicOrRequest}.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "improve-my-writing",
    name: "Improve My Writing",
    slug: "improve-my-writing",
    shortDescription:
      "Enhance your writing with clear suggestions for improving clarity, conciseness, and overall quality. Get your polished draft today!",
    fields: [
      {
        type: "textArea",
        label: "Text to Improve",
        field: "Text to Improve",
        placeholder: "Enter the text you want to improve here.",
        required: true,
        optional: false,
        id: "texttoimprove",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in improving written content. You will be provided with a piece of text and your task is to suggest ways to enhance its clarity, conciseness, and overall quality. Provide a final updated draft based on your suggestions.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToImprove } = fields;
      let prompt = `Here is a piece of text that needs improvement:
  ${textToImprove}
  
  Please suggest ways to improve its clarity, conciseness, and overall quality. Provide a final updated draft based on your suggestions.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "sentence-analyzer",
    name: "Sentence Analyzer",
    slug: "sentence-analyzer",
    shortDescription:
      "Quickly analyze text with our Sentence Analyzer to determine if it's a complete sentence or just a fragment.",
    fields: [
      {
        type: "textArea",
        label: "Text",
        field: "Text",
        placeholder: "The quick brown fox.",
        required: true,
        optional: false,
        id: "text",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing sentences. You will be provided with a string of text, and you will determine whether it is a complete sentence or a fragment.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Text } = fields;
      let prompt = `Analyze the following text and determine if it is a complete sentence or a fragment:
  Text: "${Text}"`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "tailor-resume-to-job-description-ai",
    name: "Tailor Resume to Job Description AI",
    slug: "tailor-resume-to-job-description-ai",
    shortDescription:
      "Tailor your resume to match any job description effortlessly with our AI tool, ensuring you stand out to potential employers.",
    fields: [
      {
        type: "textArea",
        label: "Resume",
        field: "Resume",
        placeholder: "John Doe, Software Engineer, 5 years of experience...",
        required: true,
        optional: false,
        id: "resume",
      },
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "Looking for a Software Engineer with experience in...",
        required: false,
        optional: true,
        id: "jobdescription",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in tailoring resumes to match specific job descriptions. You will be provided with a resume and a job description, and you will tailor the resume to better fit the job description.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Resume, JobDescription } = fields;
      let prompt = `Tailor the following resume to better match the given job description:
  Resume: ${Resume}
  `;
      if (JobDescription) {
        prompt += `Job Description: ${JobDescription}
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-legal-brief-summarizer",
    name: "AI Legal Brief Summarizer",
    slug: "ai-legal-brief-summarizer",
    shortDescription:
      "Streamline your legal research with our AI Legal Brief Summarizer that condenses lengthy documents into concise, key point summaries.",
    fields: [
      {
        type: "textArea",
        label: "Legal Document",
        field: "Legal Document",
        placeholder: "Paste the legal document here",
        required: true,
        optional: false,
        id: "legaldocument",
      },
    ],
    categories: ["Legal", "Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing legal documents. You will be provided with a lengthy legal document, and you will generate a concise summary highlighting the key points.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { legalDocument } = fields;
      let prompt = `Generate a summary for the following legal document:
  ${legalDocument}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "story-chapter-editor",
    name: "Story Chapter Editor",
    slug: "story-chapter-editor",
    shortDescription:
      "Transform your story with our Chapter Editor, tailoring each chapter to match your unique design vision as the author.",
    fields: [
      {
        type: "textArea",
        label: "Original Chapter",
        field: "Original Chapter",
        placeholder: "Prologue: A storm brewed with an earsplitting crack...",
        required: true,
        optional: false,
        id: "originalchapter",
      },
      {
        type: "textArea",
        label: "Desired Design",
        field: "Desired Design",
        placeholder: "Flash Forward Prologue",
        required: false,
        optional: true,
        id: "desireddesign",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in editing story chapters. You will be provided with an original chapter and a desired design. Your task is to edit the chapter to fit the specified design.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalChapter, desiredDesign } = fields;
      let prompt = `Edit the following chapter to fit the specified design:
  Original Chapter: ${originalChapter}
  `;
      if (desiredDesign) {
        prompt += `Desired Design: ${desiredDesign}
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "analyze-survey-feedback",
    name: "Analyze Survey Feedback",
    slug: "analyze-survey-feedback",
    shortDescription:
      "Easily analyze and categorize survey feedback into relevant themes for better understanding and actionable insights.",
    fields: [
      {
        type: "textArea",
        label: "Survey Feedback",
        field: "Survey Feedback",
        placeholder:
          "The product is great but the customer service needs improvement...",
        required: true,
        optional: false,
        id: "surveyfeedback",
      },
    ],
    categories: ["Business", "Support", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing survey feedback. You will be provided with raw survey feedback data, and you will categorize it into relevant themes for easier understanding and action.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { surveyFeedback } = fields;
      let prompt = `Analyze the following survey feedback and categorize it into relevant themes:
  Survey Feedback: ${surveyFeedback}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "story-continuation-assistant",
    name: "Story Continuation Assistant",
    slug: "story-continuation-assistant",
    shortDescription:
      "Get expert writing advice and creative suggestions to continue your story with our Story Continuation Assistant.",
    fields: [
      {
        type: "textArea",
        label: "Story Excerpt",
        field: "Story Excerpt",
        placeholder: "Once upon a time, in a land far away...",
        required: true,
        optional: false,
        id: "storyexcerpt",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creative writing and storytelling. You will be provided with an excerpt from a story, and you will generate a continuation of the story based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { storyExcerpt } = fields;
      let prompt = `Generate a continuation for the following story excerpt:\n\n"${storyExcerpt}"\n\nContinue the story in a way that maintains the original tone and style.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "text-rewriter-ai",
    name: "Text Rewriter AI",
    slug: "text-rewriter-ai",
    shortDescription:
      "Transform your text effortlessly with our Text Rewriter AI, preserving meaning while creating fresh, original content.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Enter the text you want to rewrite.",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting text. You will be provided with some original text, and your task is to rewrite it while maintaining the original meaning and context.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Rewrite the following text while maintaining its original meaning and context:
  ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "biology-ai",
    name: "Biology AI",
    slug: "biology-ai",
    shortDescription:
      "Get precise answers to biology questions and simplify complex concepts with Biology AI for enhanced learning and understanding.",
    fields: [
      {
        type: "textArea",
        label: "Biology Question or Concept",
        field: "Biology Question or Concept",
        placeholder: "What is photosynthesis?",
        required: true,
        optional: false,
        id: "biologyquestionorconcept",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in biology, capable of providing accurate answers to biology-related questions and explaining complex biological concepts. You will be given a specific question or concept, and you will generate a detailed and informative response.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { biologyQuestionOrConcept } = fields;
      let prompt =
        "Provide a detailed explanation for the following biology question or concept:\n";
      prompt += "Question/Concept: " + biologyQuestionOrConcept + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "suspenseful-scene-enhancer",
    name: "Suspenseful Scene Enhancer",
    slug: "suspenseful-scene-enhancer",
    shortDescription:
      "Enhance your storytelling with our Suspenseful Scene Enhancer. Create action-packed scenes using vivid, descriptive suggestions.",
    fields: [
      {
        type: "textArea",
        label: "Scene",
        field: "Scene",
        placeholder:
          "The hero stood at the edge of the cliff, staring down at the churning waters below...",
        required: true,
        optional: false,
        id: "scene",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in enhancing suspenseful scenes. You will be provided with a base scene and you will enhance it by suggesting vivid, descriptive words and phrases to make it more suspenseful.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Scene } = fields;
      let prompt = `Enhance the following scene to make it more suspenseful:
  Scene: ${Scene}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-monologue-writer",
    name: "AI Monologue Writer",
    slug: "ai-monologue-writer",
    shortDescription:
      "Create captivating monologues effortlessly with our AI Monologue Writer, tailored to your character and situation for maximum impact.",
    fields: [
      {
        type: "textArea",
        label: "Character Description",
        field: "Character Description",
        placeholder: "Describe the character's personality, background, etc.",
        required: true,
        optional: false,
        id: "characterdescription",
      },
      {
        type: "textArea",
        label: "Situation",
        field: "Situation",
        placeholder: "Describe the situation the character is in.",
        required: false,
        optional: true,
        id: "situation",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing engaging and creative monologues. You will be provided with a character description and a situation, and you will generate a monologue that reflects the character's personality and the context of the situation.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { characterDescription, situation } = fields;
      let prompt = "Generate a monologue based on the following:\n";
      prompt += "Character Description: " + characterDescription + "\n";
      if (situation) {
        prompt += "Situation: " + situation + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "story-continuation-ai",
    name: "Story Continuation AI",
    slug: "story-continuation-ai",
    shortDescription:
      "Enhance your storytelling with Story Continuation AI, seamlessly continuing your narrative while preserving style and character integrity.",
    fields: [
      {
        type: "textArea",
        label: "Story Start",
        field: "Story Start",
        placeholder: "Once upon a time, in a land far, far away...",
        required: true,
        optional: false,
        id: "storystart",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creative writing and storytelling. You will be provided with the beginning of a story and your task is to continue the story while maintaining the original style, characters, and plot direction.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { storyStart } = fields;
      let prompt = `Continue the following story:
  ${storyStart}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-birthday-card",
    name: "AI Birthday Card",
    slug: "ai-birthday-card",
    shortDescription:
      "Create personalized birthday card messages using AI, tailored to the recipient's interests and your unique relationship.",
    fields: [
      {
        type: "textArea",
        label: "Recipient Name",
        field: "Recipient Name",
        placeholder: "John Doe",
        required: true,
        optional: false,
        id: "recipientname",
      },
      {
        type: "textArea",
        label: "Recipient Interests",
        field: "Recipient Interests",
        placeholder: "Football, Cooking, Rock Music",
        required: false,
        optional: true,
        id: "recipientinterests",
      },
      {
        type: "textArea",
        label: "Relationship",
        field: "Relationship",
        placeholder: "Friend, Colleague, Family",
        required: false,
        optional: true,
        id: "relationship",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized birthday card messages. You will be provided with details about the recipient and their relationship with the sender, and you will create a heartfelt birthday message based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientName, recipientInterests, relationship } = fields;
      let prompt =
        "Generate a personalized birthday card message based on the following:\n";
      prompt += "Recipient Name: " + recipientName + ".\n";
      if (recipientInterests) {
        prompt += "Recipient Interests: " + recipientInterests + ".\n";
      }
      if (relationship) {
        prompt += "Relationship: " + relationship + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-grammar-correction",
    name: "AI Grammar Correction",
    slug: "ai-grammar-correction",
    shortDescription:
      "Enhance your writing with our AI grammar correction tool that fixes grammar and spelling errors for clear, polished text.",
    fields: [
      {
        type: "textArea",
        label: "Text to Correct",
        field: "Text to Correct",
        placeholder: "I'm am the best AI in the world.",
        required: true,
        optional: false,
        id: "texttocorrect",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grammar and spelling correction. You will be provided with a text that may contain grammatical and spelling errors, and you will correct these errors.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToCorrect } = fields;
      let prompt = `Correct the following text for grammar and spelling errors:
  ${textToCorrect}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "greek-symbol-interpreter",
    name: "Greek Symbol Interpreter",
    slug: "greek-symbol-interpreter",
    shortDescription:
      "Discover the meanings and uses of Greek symbols in mathematics and science with our comprehensive Greek Symbol Interpreter.",
    fields: [
      {
        type: "textArea",
        label: "Greek Symbol",
        field: "Greek Symbol",
        placeholder: "ζ",
        required: true,
        optional: false,
        id: "greeksymbol",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in interpreting Greek symbols used in mathematics and science. You will be provided with a Greek symbol, and you will generate an explanation of its meaning and usage based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { greekSymbol } = fields;
      let prompt = `Generate an interpretation for the following Greek symbol:
  Greek Symbol: ${greekSymbol}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-movie-idea-generator",
    name: "AI Movie Idea Generator",
    slug: "ai-movie-idea-generator",
    shortDescription:
      "Generate unique and creative movie ideas effortlessly with our AI Movie Idea Generator based on your inputs!",
    fields: [
      {
        type: "textArea",
        label: "Genre",
        field: "Genre",
        placeholder: "Horror, Comedy, Drama, etc.",
        required: true,
        optional: false,
        id: "genre",
      },
      {
        type: "textArea",
        label: "Setting",
        field: "Setting",
        placeholder: "A small town, a spaceship, a haunted mansion, etc.",
        required: false,
        optional: true,
        id: "setting",
      },
      {
        type: "textArea",
        label: "Main Character",
        field: "Main Character",
        placeholder:
          "A struggling artist, a hardened detective, a mischievous child, etc.",
        required: false,
        optional: true,
        id: "maincharacter",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating unique and creative movie ideas. You will be provided with some base information, and you will generate a movie idea based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Genre, Setting, MainCharacter } = fields;
      let prompt = "Generate a unique movie idea based on the following:\n";
      prompt += "Genre: " + Genre + ".\n";
      if (Setting) {
        prompt += "Setting: " + Setting + ".\n";
      }
      if (MainCharacter) {
        prompt += "Main Character: " + MainCharacter + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-outline-creator",
    name: "AI Outline Creator",
    slug: "ai-outline-creator",
    shortDescription:
      "Generate detailed outlines for your writing projects effortlessly with our AI Outline Creator—tailored to your topics and key points.",
    fields: [
      {
        type: "textArea",
        label: "Main Topic",
        field: "Main Topic",
        placeholder: "The Impact of Climate Change",
        required: true,
        optional: false,
        id: "maintopic",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "Causes of climate change, Effects of climate change, Solutions to climate change",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating detailed outlines for writing projects. You will be provided with a main topic and key points, and you will generate a structured outline based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { mainTopic, keyPoints } = fields;
      let prompt = "Generate a detailed outline based on the following:\n";
      prompt += "Main Topic: " + mainTopic + "\n";
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "custom-card-creator",
    name: "Custom Card Creator",
    slug: "custom-card-creator",
    shortDescription:
      "Create personalized greeting cards effortlessly with our Custom Card Creator. Perfect for any occasion and recipient!",
    fields: [
      {
        type: "textArea",
        label: "Occasion",
        field: "Occasion",
        placeholder: "Birthday, Anniversary, Congratulations, etc.",
        required: true,
        optional: false,
        id: "occasion",
      },
      {
        type: "textArea",
        label: "Recipient's Name",
        field: "Recipient's Name",
        placeholder: "John Doe",
        required: false,
        optional: true,
        id: "recipientsname",
      },
      {
        type: "textArea",
        label: "Personal Message",
        field: "Personal Message",
        placeholder: "May your birthday be filled with happiness and joy...",
        required: false,
        optional: true,
        id: "personalmessage",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized greeting cards. You will be provided with details about the occasion, recipient's information, and a personal message to generate a unique greeting card.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Occasion, RecipientName, PersonalMessage } = fields;
      let prompt =
        "Generate a personalized greeting card based on the following:\n";
      prompt += "Occasion: " + Occasion + ".\n";
      if (RecipientName) {
        prompt += "Recipient's Name: " + RecipientName + ".\n";
      }
      if (PersonalMessage) {
        prompt += "Personal Message: " + PersonalMessage + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "flexible-ai-task-accomplisher",
    name: "Flexible AI Task Accomplisher",
    slug: "flexible-ai-task-accomplisher",
    shortDescription:
      "Revolutionize your productivity with our flexible AI Task Accomplisher, designed to assist in a variety of creative tasks effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Task",
        field: "Task",
        placeholder: "Write me an outline for a blog post about AI",
        required: true,
        optional: false,
        id: "task",
      },
    ],
    categories: ["Writing", "Business", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in accomplishing a wide variety of tasks using AI. You will be provided with a specific task description, and you will generate a response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Task } = fields;
      let prompt = `Generate a response for the following task:
  Task: ${Task}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "paragraph-expander-ai",
    name: "Paragraph Expander AI",
    slug: "paragraph-expander-ai",
    shortDescription:
      "Transform your writing with Paragraph Expander AI: effortlessly rewrite and expand paragraphs with related ideas and insights.",
    fields: [
      {
        type: "textArea",
        label: "Original Paragraph",
        field: "Original Paragraph",
        placeholder: "The quick brown fox jumps over the lazy dog.",
        required: true,
        optional: false,
        id: "originalparagraph",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in expanding and rewriting paragraphs. You will be provided with an original paragraph, and you will generate an expanded version of it with related thoughts and ideas.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalParagraph } = fields;
      let prompt = `Expand and rewrite the following paragraph with related thoughts and ideas:
  Original Paragraph: ${originalParagraph}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "career-counseling-assistant",
    name: "Career Counseling Assistant",
    slug: "career-counseling-assistant",
    shortDescription:
      "Explore career paths tailored to your interests with our Career Counseling Assistant, guiding your professional journey with personalized insights.",
    fields: [
      {
        type: "textArea",
        label: "Interests",
        field: "Interests",
        placeholder: "Coding, Designing, Problem-solving",
        required: true,
        optional: false,
        id: "interests",
      },
      {
        type: "textArea",
        label: "Characteristics",
        field: "Characteristics",
        placeholder: "Creative, Analytical, Team player",
        required: false,
        optional: true,
        id: "characteristics",
      },
    ],
    categories: ["Education", "Career Counseling", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert career counselor. You will be provided with information about a person's interests and characteristics. Based on this information, you will suggest possible career paths that align with their profile.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Interests, Characteristics } = fields;
      let prompt = `Generate career counseling advice based on the following information:
  Interests: ${Interests}.
  `;
      if (Characteristics) {
        prompt += `Characteristics: ${Characteristics}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "sentence-rephraser",
    name: "Sentence Rephraser",
    slug: "sentence-rephraser",
    shortDescription:
      "Transform your sentences effortlessly with our rephraser tool, keeping the meaning intact while changing the wording.",
    fields: [
      {
        type: "textArea",
        label: "Original Sentence",
        field: "Original Sentence",
        placeholder: "The quick brown fox jumps over the lazy dog.",
        required: true,
        optional: false,
        id: "originalsentence",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rephrasing sentences. You will be provided with an original sentence, and you will generate a rephrased version of that sentence while maintaining its original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalSentence } = fields;
      let prompt = `Rephrase the following sentence while maintaining its original meaning:
  Original Sentence: ${originalSentence}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "bullet-point-refiner",
    name: "Bullet Point Refiner",
    slug: "bullet-point-refiner",
    shortDescription:
      "Enhance your bullet points with our Bullet Point Refiner, improving language and structure for clarity and impact.",
    fields: [
      {
        type: "textArea",
        label: "Original Bullet Points",
        field: "Original Bullet Points",
        placeholder: "-We make stuff -We sell stuff -We're the best at stuff",
        required: true,
        optional: false,
        id: "originalbulletpoints",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in refining and improving bullet points. You will be provided with a list of original bullet points, and you will refine them to improve their language and structure.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalBulletPoints } = fields;
      let prompt = `Refine the following bullet points to improve their language and structure:
  ${originalBulletPoints}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "herb-and-spice-finder",
    name: "Herb and Spice Finder",
    slug: "herb-and-spice-finder",
    shortDescription:
      "Discover the perfect herb or spice for your needs with our Finder tool, matching qualities and offering related options.",
    fields: [
      {
        type: "textArea",
        label: "Element",
        field: "Element",
        placeholder: "Fire, air, water, earth",
        required: true,
        optional: false,
        id: "element",
      },
      {
        type: "textArea",
        label: "Qualities",
        field: "Qualities",
        placeholder: "Calming, Digestive, Anti-inflammatory",
        required: false,
        optional: true,
        id: "qualities",
      },
      {
        type: "textArea",
        label: "Region ",
        field: "Region ",
        placeholder: "Scandinavia, Mediterranean, Central America",
        required: false,
        optional: true,
        id: "region",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in identifying herbs and spices based on specific qualities and regions. You will be provided with certain characteristics, and you will suggest an edible herb or spice that matches those qualities.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Element, Qualities, Region } = fields;
      let prompt =
        "Find an edible herb or spice based on the following criteria:\n";
      prompt += "Element: " + Element + ".\n";
      if (Qualities) {
        prompt += "Qualities: " + Qualities + ".\n";
      }
      if (Region) {
        prompt += "Region: " + Region + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "rewrite-with-ai",
    name: "Rewrite with AI",
    slug: "rewrite-with-ai",
    shortDescription:
      "Transform your text with AI rewrites for a fresh perspective while preserving the original meaning.",
    fields: [
      {
        type: "textArea",
        label: "Original Text",
        field: "Original Text",
        placeholder: "Enter the text you want to rewrite here.",
        required: true,
        optional: false,
        id: "originaltext",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting text while preserving its original meaning. You will be provided with some text, and your task is to rewrite it to offer a fresh perspective.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalText } = fields;
      let prompt = `Rewrite the following text while maintaining its original meaning:
  ${originalText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "creative-writing-prompt-generator",
    name: "Creative Writing Prompt Generator",
    slug: "creative-writing-prompt-generator",
    shortDescription:
      "Generate unique and inspiring creative writing prompts for your next exercise with our innovative prompt generator.",
    fields: [
      {
        type: "textArea",
        label: "Genre",
        field: "Genre",
        placeholder: "Fantasy, Mystery, Romance, etc.",
        required: true,
        optional: false,
        id: "genre",
      },
      {
        type: "textArea",
        label: "Length",
        field: "Length",
        placeholder: "Short Story, Novel, Poem, etc.",
        required: false,
        optional: true,
        id: "length",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating creative writing prompts. You will be provided with information about the genre and length of the writing piece, and you will create a unique and inspiring prompt based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Genre, Length } = fields;
      let prompt =
        "Generate a creative writing prompt based on the following:\n";
      prompt += "Genre: " + Genre + ".\n";
      if (Length) {
        prompt += "Length: " + Length + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-differentiated-instruction-planner",
    name: "AI Differentiated Instruction Planner",
    slug: "ai-differentiated-instruction-planner",
    shortDescription:
      "Create tailored learning experiences with our AI Differentiated Instruction Planner, generating activities for diverse student abilities.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Topic e.g. Photosynthesis",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Standard",
        field: "Standard",
        placeholder:
          "Standard e.g. Students should be able to explain the process of photosynthesis",
        required: false,
        optional: true,
        id: "standard",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating differentiated instruction activities. You will be provided with a topic and a standard, and you will generate four differentiated activities tailored to students of varying abilities.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, Standard } = fields;
      let prompt =
        "Generate four differentiated activities based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (Standard) {
        prompt += "Standard: " + Standard + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-research-journal-presentation-builder",
    name: "AI Research Journal Presentation Builder",
    slug: "ai-research-journal-presentation-builder",
    shortDescription:
      "Create impactful presentations effortlessly with our AI Research Journal Presentation Builder, tailored for your research needs.",
    fields: [
      {
        type: "textArea",
        label: "Research Journal",
        field: "Research Journal",
        placeholder: "Paste the link or text of the research journal here",
        required: true,
        optional: false,
        id: "researchjournal",
      },
      {
        type: "textArea",
        label: "Presentation Length",
        field: "Presentation Length",
        placeholder: "Enter the desired number of slides",
        required: false,
        optional: true,
        id: "presentationlength",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "Enter any specific points you want included in the presentation",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Education", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating presentations based on research journals. You will be provided with the content of a research journal and specific requirements, and you will generate a comprehensive presentation outline.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchJournal, presentationLength, keyPoints } = fields;
      let prompt = "Create a presentation based on the following details:\n";
      prompt += "Research Journal: " + researchJournal + "\n";
      if (presentationLength) {
        prompt += "Desired number of slides: " + presentationLength + "\n";
      }
      if (keyPoints) {
        prompt += "Key points to include: " + keyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-resignation-letter",
    name: "AI Resignation Letter",
    slug: "ai-resignation-letter",
    shortDescription:
      "Create professional resignation letters effortlessly with our AI tool, ensuring respect and clarity based on your input.",
    fields: [
      {
        type: "textArea",
        label: "Recipient's Name",
        field: "Recipient's Name",
        placeholder: "John Doe",
        required: true,
        optional: false,
        id: "recipientsname",
      },
      {
        type: "textArea",
        label: "Last Working Day",
        field: "Last Working Day",
        placeholder: "March 31, 2023",
        required: false,
        optional: true,
        id: "lastworkingday",
      },
      {
        type: "textArea",
        label: "Reason for Resignation",
        field: "Reason for Resignation",
        placeholder: "Pursuing a new opportunity",
        required: false,
        optional: true,
        id: "reasonforresignation",
      },
    ],
    categories: ["Email", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating professional resignation letters. You will be provided with details about the recipient and the reason for resignation, and you will create a respectful resignation letter based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { recipientName, lastWorkingDay, reasonForResignation } = fields;
      let prompt = "Generate a resignation letter based on the following:\n";
      prompt += "Recipient's Name: " + recipientName + ".\n";
      if (lastWorkingDay) {
        prompt += "Last Working Day: " + lastWorkingDay + ".\n";
      }
      if (reasonForResignation) {
        prompt += "Reason for Resignation: " + reasonForResignation + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-roast-bot",
    name: "AI Roast Bot",
    slug: "ai-roast-bot",
    shortDescription:
      "Get ready to laugh with AI Roast Bot! Generate funny, light-hearted roasts using your own info for endless entertainment.",
    fields: [
      {
        type: "textArea",
        label: "Name and/or Information",
        field: "Name and/or Information",
        placeholder:
          "John Smith, likes pineapple on pizza, always late, can't parallel park...",
        required: true,
        optional: false,
        id: "nameand/orinformation",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a humorous AI designed to generate light-hearted roasts. You will be provided with some information about a person, and you will create a funny roast based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { nameAndInfo } = fields;
      let prompt =
        "Generate a humorous roast based on the following information:\n";
      prompt += "Information: " + nameAndInfo + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-marketing-campaign-ideator",
    name: "AI Marketing Campaign Ideator",
    slug: "ai-marketing-campaign-ideator",
    shortDescription:
      "Generate creative AI marketing campaign ideas tailored to your products and audience for effective engagement and results.",
    fields: [
      {
        type: "textArea",
        label: "Product or Service",
        field: "Product or Service",
        placeholder: "Eco-friendly clothing line",
        required: true,
        optional: false,
        id: "productorservice",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Millennials interested in sustainable fashion",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating creative marketing campaign ideas. You will be provided with details about a product or service and the target audience, and you will generate innovative marketing campaign ideas tailored to that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { ProductOrService, TargetAudience } = fields;
      let prompt =
        "Generate creative marketing campaign ideas based on the following:\n";
      prompt += "Product or Service: " + ProductOrService + "\n";
      if (TargetAudience) {
        prompt += "Target Audience: " + TargetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-product-description-writer",
    name: "AI Product Description Writer",
    slug: "ai-product-description-writer",
    shortDescription:
      "Create compelling, SEO-friendly product descriptions that highlight key features and benefits for your e-commerce website.",
    fields: [
      {
        type: "textArea",
        label: "Product Name",
        field: "Product Name",
        placeholder: "Ultra Comfortable Memory Foam Mattress",
        required: true,
        optional: false,
        id: "productname",
      },
      {
        type: "textArea",
        label: "Key Features",
        field: "Key Features",
        placeholder: "High-density foam, Hypoallergenic, 10-year warranty",
        required: false,
        optional: true,
        id: "keyfeatures",
      },
      {
        type: "textArea",
        label: "Benefits",
        field: "Benefits",
        placeholder:
          "Promotes better sleep, Suitable for allergy sufferers, Long-lasting with warranty",
        required: false,
        optional: true,
        id: "benefits",
      },
    ],
    categories: ["Website", "Marketing", "Sales", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting compelling and SEO-friendly product descriptions for e-commerce websites. You will be provided with the product name, key features, and benefits, and you will generate a product description that highlights these aspects.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { ProductName, KeyFeatures, Benefits } = fields;
      let prompt = "Generate a product description based on the following:\n";
      prompt += "Product Name: " + ProductName + ".\n";
      if (KeyFeatures) {
        prompt += "Key Features: " + KeyFeatures + ".\n";
      }
      if (Benefits) {
        prompt += "Benefits: " + Benefits + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "business-english-app-vocabulary-builder",
    name: "Business English App: Vocabulary Builder",
    slug: "business-english-app-vocabulary-builder",
    shortDescription:
      "Enhance your business communication with our app's vocabulary builder, featuring industry-specific words and example sentences.",
    fields: [
      {
        type: "textArea",
        label: "Industry",
        field: "Industry",
        placeholder: "e.g., Marketing, Finance, Technology",
        required: true,
        optional: false,
        id: "industry",
      },
      {
        type: "textArea",
        label: "Specific Area",
        field: "Specific Area",
        placeholder:
          "e.g., Digital Marketing, Investment Banking, Software Development",
        required: false,
        optional: true,
        id: "specificarea",
      },
    ],
    categories: ["Business", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in Business English vocabulary building. You will be provided with information about the industry and specific area, and you will generate a list of relevant vocabulary words along with their definitions and example sentences.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Industry, SpecificArea } = fields;
      let prompt =
        "Generate a list of vocabulary words for business communication based on the following:\n";
      prompt += "Industry: " + Industry + ".\n";
      if (SpecificArea) {
        prompt += "Specific Area: " + SpecificArea + ".\n";
      }
      prompt += "Provide definitions and example sentences for each word.";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-content-expander",
    name: "AI Content Expander",
    slug: "ai-content-expander",
    shortDescription:
      "Transform short-form content into in-depth pieces with our AI Content Expander. Enhance your writing with relevant details and examples.",
    fields: [
      {
        type: "textArea",
        label: "Short-Form Content or Text",
        field: "Short-Form Content or Text",
        placeholder: "AI is transforming industries...",
        required: true,
        optional: false,
        id: "short-formcontentortext",
      },
    ],
    categories: ["Writing", "Marketing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in expanding short-form content into longer, more detailed pieces. You will be provided with a short piece of text and you will expand it by adding relevant information, examples, and details.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { shortFormContent } = fields;
      let prompt = `Expand the following short-form content into a longer, more detailed piece:
  Short-Form Content: ${shortFormContent}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-essay-writer-assistant",
    name: "AI Essay Writer Assistant",
    slug: "ai-essay-writer-assistant",
    shortDescription:
      "Get help with your essays using our AI Essay Writer Assistant for drafts, suggestions, corrections, and improvements.",
    fields: [
      {
        type: "textArea",
        label: "Essay Topic",
        field: "Essay Topic",
        placeholder: "The Impact of Climate Change on Biodiversity",
        required: true,
        optional: false,
        id: "essaytopic",
      },
      {
        type: "textArea",
        label: "Initial Draft or Outline",
        field: "Initial Draft or Outline",
        placeholder: "Climate change has been a growing concern...",
        required: false,
        optional: true,
        id: "initialdraftoroutline",
      },
      {
        type: "textArea",
        label: "Specific Request",
        field: "Specific Request",
        placeholder: "Expand the outline into a full draft",
        required: false,
        optional: true,
        id: "specificrequest",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in essay writing. You will be provided with a topic, an initial draft or outline, and specific requests to help generate a well-structured essay.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essayTopic, initialDraft, specificRequest } = fields;
      let prompt = "Generate an essay based on the following:\n";
      prompt += "Essay Topic: " + essayTopic + "\n";
      if (initialDraft) {
        prompt += "Initial Draft or Outline: " + initialDraft + "\n";
      }
      if (specificRequest) {
        prompt += "Specific Request: " + specificRequest + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-vocabulary-enhancer",
    name: "AI Vocabulary Enhancer",
    slug: "ai-vocabulary-enhancer",
    shortDescription:
      "Enhance your writing with our AI Vocabulary Enhancer, suggesting sophisticated vocabulary alternatives as you type for better clarity.",
    fields: [
      {
        type: "textArea",
        label: "Text",
        field: "Text",
        placeholder: "The cat sat on the mat.",
        required: true,
        optional: false,
        id: "text",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI vocabulary enhancer. Your task is to improve the provided text by suggesting more sophisticated or context-appropriate vocabulary alternatives.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Text } = fields;
      let prompt =
        "Enhance the following text by suggesting better vocabulary alternatives:\n";
      prompt += "Text: " + Text + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "thought-expander",
    name: "Thought Expander",
    slug: "thought-expander",
    shortDescription:
      "Deepen your understanding with Thought Expander, enhancing ideas from paragraphs through analysis and related insights.",
    fields: [
      {
        type: "textArea",
        label: "Input Paragraph",
        field: "Input Paragraph",
        placeholder: "Enter the paragraph you want to expand on.",
        required: true,
        optional: false,
        id: "inputparagraph",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in expanding on ideas presented in a given paragraph. You will be provided with a paragraph and you will generate an expanded version of it, providing deeper analysis and related thoughts.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { inputParagraph } = fields;
      let prompt = `Expand on the following paragraph, providing deeper analysis and related thoughts:
  ${inputParagraph}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-discussion-post-responder",
    name: "AI Discussion Post Responder",
    slug: "ai-discussion-post-responder",
    shortDescription:
      "Generate thoughtful responses to discussion posts effortlessly with our AI Discussion Post Responder. Engage and impress your audience!",
    fields: [
      {
        type: "textArea",
        label: "Discussion Post",
        field: "Discussion Post",
        placeholder: "I believe that the main theme of the book is...",
        required: true,
        optional: false,
        id: "discussionpost",
      },
    ],
    categories: ["Education", "Writing", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thoughtful and engaging responses to discussion posts. You will be provided with a discussion post, and your task is to create a response that adds value to the conversation.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { discussionPost } = fields;
      let prompt =
        "Generate a thoughtful response to the following discussion post:\n";
      prompt += "Discussion Post: " + discussionPost + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "sentence-reframer",
    name: "Sentence Reframer",
    slug: "sentence-reframer",
    shortDescription:
      "Transform your writing with our Sentence Reframer, which rephrases sentences while preserving their original meaning effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Original Sentence",
        field: "Original Sentence",
        placeholder: "The quick brown fox jumps over the lazy dog.",
        required: true,
        optional: false,
        id: "originalsentence",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rephrasing sentences. You will be provided with an original sentence, and you will generate a rephrased version that conveys the same meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalSentence } = fields;
      let prompt = `Rephrase the following sentence to convey the same meaning in a different way:
  Original Sentence: ${originalSentence}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-personal-text-responder",
    name: "AI Personal Text Responder",
    slug: "ai-personal-text-responder",
    shortDescription:
      "Generate context-aware responses for texts, emails, and social media while maintaining your unique tone with our AI Personal Text Responder.",
    fields: [
      {
        type: "textArea",
        label: "Message",
        field: "Message",
        placeholder: "Hey, do you want to go to the new restaurant downtown?",
        required: true,
        optional: false,
        id: "message",
      },
      {
        type: "textArea",
        label: "User's Personal Tone and Style",
        field: "User's Personal Tone and Style",
        placeholder: "friendly, casual, uses emojis",
        required: false,
        optional: true,
        id: "userspersonaltoneandstyle",
      },
    ],
    categories: ["Social Media", "Email", "Support", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized responses to text messages, emails, or social media comments. You will be provided with a message and optionally the user's personal tone and style. Generate a response that is context-aware and maintains the user's personal tone.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Message, UserPersonalToneAndStyle } = fields;
      let prompt = `Generate a response based on the following:
  Message: ${Message}.
  `;
      if (UserPersonalToneAndStyle) {
        prompt += `User's personal tone and style: ${UserPersonalToneAndStyle}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-style-rewriter",
    name: "AI Style Rewriter",
    slug: "ai-style-rewriter",
    shortDescription:
      "Transform your text with our AI Style Rewriter, mimicking any author’s style for unique and creative content.",
    fields: [
      {
        type: "textArea",
        label: "Text to Rewrite",
        field: "Text to Rewrite",
        placeholder: "It was the best of times, it was the worst of times...",
        required: true,
        optional: false,
        id: "texttorewrite",
      },
      {
        type: "textArea",
        label: "Desired Style or Author",
        field: "Desired Style or Author",
        placeholder: "Ernest Hemingway",
        required: false,
        optional: true,
        id: "desiredstyleorauthor",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rewriting text in various styles or mimicking specific authors. You will be provided with a piece of text and a desired style or author, and you will rewrite the text accordingly.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToRewrite, desiredStyleOrAuthor } = fields;
      let prompt = "Rewrite the following text:\n";
      prompt += "Text: " + textToRewrite + "\n";
      if (desiredStyleOrAuthor) {
        prompt += "Desired style or author: " + desiredStyleOrAuthor + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-person-search",
    name: "AI Person Search",
    slug: "ai-person-search",
    shortDescription:
      "Discover detailed information about anyone with AI Person Search by simply entering their name and additional context.",
    fields: [
      {
        type: "textArea",
        label: "Person's Name",
        field: "Person's Name",
        placeholder: "John Doe",
        required: true,
        optional: false,
        id: "personsname",
      },
      {
        type: "textArea",
        label: "Additional Context",
        field: "Additional Context",
        placeholder: "Software engineer at Google",
        required: false,
        optional: true,
        id: "additionalcontext",
      },
    ],
    categories: ["Social Media", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in finding detailed information about individuals. You will be provided with a person's name and possibly some additional context. Based on this information, you will generate a detailed profile of the person.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { personName, additionalContext } = fields;
      let prompt = `Generate a detailed profile for the following individual:
  `;
      prompt += `Person's Name: ${personName}.
  `;
      if (additionalContext) {
        prompt += `Additional Context: ${additionalContext}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "christmas-card-message-generator",
    name: "Christmas Card Message Generator",
    slug: "christmas-card-message-generator",
    shortDescription:
      "Create personalized and heartfelt Christmas card messages effortlessly with our Christmas Card Message Generator.",
    fields: [
      {
        type: "textArea",
        label: "Specific Details or Messages",
        field: "Specific Details or Messages",
        placeholder:
          "Enter any specific details or messages you want to include in your Christmas card",
        required: true,
        optional: false,
        id: "specificdetailsormessages",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized Christmas card messages. You will be provided with specific details or messages, and you will create a heartfelt Christmas card message based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { specificDetails } = fields;
      let prompt =
        "Generate a Christmas card message based on the following:\n";
      prompt += "Specific details or messages: " + specificDetails + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "universal-task-assistant",
    name: "Universal Task Assistant",
    slug: "universal-task-assistant",
    shortDescription:
      "Boost your writing and productivity with Universal Task Assistant. Get AI-powered help for all your tasks effortlessly!",
    fields: [
      {
        type: "textArea",
        label: "Task Description",
        field: "Task Description",
        placeholder: "Describe the task you need help with",
        required: true,
        optional: false,
        id: "taskdescription",
      },
      {
        type: "textArea",
        label: "Specific Requirements (Optional)",
        field: "Specific Requirements (Optional)",
        placeholder: "Any specific requirements or details for the task",
        required: false,
        optional: true,
        id: "specificrequirements(optional)",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert assistant capable of handling a wide range of writing and productivity tasks. You will be provided with a task description and any specific requirements to generate appropriate content or solutions.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { taskDescription, specificRequirements } = fields;
      let prompt = `Generate content or provide assistance based on the following details:
  Task Description: ${taskDescription}.
  `;
      if (specificRequirements) {
        prompt += `Specific Requirements: ${specificRequirements}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "digital-marketing-strategy-advisor",
    name: "Digital Marketing Strategy Advisor",
    slug: "digital-marketing-strategy-advisor",
    shortDescription:
      "Boost customer engagement and sales with our integrated digital marketing strategy tailored for your business needs.",
    fields: [
      {
        type: "textArea",
        label: "Company's Main Product",
        field: "Company's Main Product",
        placeholder: "Dashboard with CRM, API/Webhooks, Helpdesk, and more.",
        required: true,
        optional: false,
        id: "companysmainproduct",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder:
          "B2B clients in need of comprehensive digital marketing solutions.",
        required: false,
        optional: true,
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Marketing Strategy Focus",
        field: "Marketing Strategy Focus",
        placeholder: "Increasing customer engagement and sales.",
        required: false,
        optional: true,
        id: "marketingstrategyfocus",
      },
    ],
    categories: ["Marketing", "Sales", "Business"],
    buildSystemPrompt: () => {
      let prompt = `You are an expert in digital marketing strategies. You will be provided with some base information about a company and its products. Based on this information, you will generate a comprehensive digital marketing strategy to increase customer engagement, lead generation, and sales.`;
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { companyMainProduct, targetAudience, marketingStrategyFocus } =
        fields;
      let prompt = `Generate a digital marketing strategy based on the following information:
  `;
      prompt += `Company's main product: ${companyMainProduct}.
  `;
      if (targetAudience) {
        prompt += `Target audience: ${targetAudience}.
  `;
      }
      if (marketingStrategyFocus) {
        prompt += `Marketing strategy focus: ${marketingStrategyFocus}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-roast-generator",
    name: "AI Roast Generator",
    slug: "ai-roast-generator",
    shortDescription:
      "Generate hilarious roasts effortlessly with our AI Roast Generator. Light-hearted humor tailored to your input.",
    fields: [
      {
        type: "textArea",
        label: "Target for Roast",
        field: "Target for Roast",
        placeholder: "My best friend who loves pineapple on pizza",
        required: true,
        optional: false,
        id: "targetforroast",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a skilled comedian and roast master. Your task is to generate humorous and light-hearted roasts based on the information provided by the user.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { targetForRoast } = fields;
      let prompt = "Generate a humorous roast based on the following:\n";
      prompt += "Target for roast: " + targetForRoast + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "multilingual-translator",
    name: "MultiLingual Translator",
    slug: "multilingual-translator",
    shortDescription:
      "Translate text effortlessly with our MultiLingual Translator. Break language barriers and communicate globally with ease!",
    fields: [
      {
        type: "textArea",
        label: "Source Text",
        field: "Source Text",
        placeholder: "Please enter the text you want to translate here.",
        required: true,
        optional: false,
        id: "sourcetext",
      },
      {
        type: "textArea",
        label: "Source Language",
        field: "Source Language",
        placeholder: "English",
        required: false,
        optional: true,
        id: "sourcelanguage",
      },
      {
        type: "textArea",
        label: "Target Language",
        field: "Target Language",
        placeholder: "Spanish",
        required: false,
        optional: true,
        id: "targetlanguage",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt = `You are an expert multilingual translator. You will be provided with a source text and optionally the source and target languages. Your task is to accurately translate the given text into the specified target language.`;
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { sourceText, sourceLanguage, targetLanguage } = fields;
      let prompt = `Translate the following text:
  `;
      prompt += `Source Text: ${sourceText}
  `;
      if (sourceLanguage) {
        prompt += `Source Language: ${sourceLanguage}
  `;
      }
      if (targetLanguage) {
        prompt += `Target Language: ${targetLanguage}
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-lesson-plan-developer",
    name: "AI Lesson Plan Developer",
    slug: "ai-lesson-plan-developer",
    shortDescription:
      "Create engaging lesson plans effortlessly with our AI tool, suggesting activities and resources tailored to your learning objectives.",
    fields: [
      {
        type: "textArea",
        label: "Learning Objectives",
        field: "Learning Objectives",
        placeholder:
          "Students will be able to understand the concept of photosynthesis",
        required: true,
        optional: false,
        id: "learningobjectives",
      },
      {
        type: "textArea",
        label: "Age Group or Grade",
        field: "Age Group or Grade",
        placeholder: "5th Grade",
        required: false,
        optional: true,
        id: "agegrouporgrade",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in developing lesson plans for teachers. You will be provided with learning objectives and age group information, and you will generate a comprehensive lesson plan based on the provided details.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { learningObjectives, ageGroup } = fields;
      let prompt = "Create a lesson plan based on the following:\n";
      prompt += "Learning Objectives: " + learningObjectives + "\n";
      if (ageGroup) {
        prompt += "Age Group or Grade: " + ageGroup + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-business-plan-draft-creator",
    name: "AI Business Plan Draft Creator",
    slug: "ai-business-plan-draft-creator",
    shortDescription:
      "Create a comprehensive business plan with our AI Business Plan Draft Creator, perfect for students needing market analysis and financial projections.",
    fields: [
      {
        type: "textArea",
        label: "Business Idea",
        field: "Business Idea",
        placeholder: "Describe your business idea in detail...",
        required: true,
        optional: false,
        id: "businessidea",
      },
      {
        type: "textArea",
        label: "Target Market",
        field: "Target Market",
        placeholder: "Who are your potential customers?",
        required: false,
        optional: true,
        id: "targetmarket",
      },
      {
        type: "textArea",
        label: "Product/Service Description",
        field: "Product/Service Description",
        placeholder: "What product/service will your business provide?",
        required: false,
        optional: true,
        id: "product/servicedescription",
      },
    ],
    categories: ["Education", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating business plans. You will be provided with details about a business idea, target market, and product/service description, and you will generate a comprehensive business plan draft based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { BusinessIdea, TargetMarket, ProductServiceDescription } = fields;
      let prompt = "Generate a business plan draft based on the following:\n";
      prompt += "Business Idea: " + BusinessIdea + "\n";
      if (TargetMarket) {
        prompt += "Target Market: " + TargetMarket + "\n";
      }
      if (ProductServiceDescription) {
        prompt +=
          "Product/Service Description: " + ProductServiceDescription + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "learning-assistant",
    name: "Learning Assistant",
    slug: "learning-assistant",
    shortDescription:
      "Enhance your learning with our assistant that answers questions and explains topics across various subjects for better understanding.",
    fields: [
      {
        type: "textArea",
        label: "Subject",
        field: "Subject",
        placeholder: "Math",
        required: true,
        optional: false,
        id: "subject",
      },
      {
        type: "textArea",
        label: "Question or Topic",
        field: "Question or Topic",
        placeholder: "Explain the Pythagorean theorem",
        required: false,
        optional: true,
        id: "questionortopic",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in various subjects and can assist with learning by answering questions or explaining topics. You will be provided with a subject and optionally a specific question or topic to explain.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Subject, QuestionOrTopic } = fields;
      let prompt = `Assist with learning based on the following information:
  `;
      prompt += `Subject: ${Subject}.
  `;
      if (QuestionOrTopic) {
        prompt += `Question or Topic: ${QuestionOrTopic}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "french-grammar-and-conjugation-corrector",
    name: "French Grammar and Conjugation Corrector",
    slug: "french-grammar-and-conjugation-corrector",
    shortDescription:
      "Corrigez les erreurs de grammaire et de conjugaison en français avec notre correcteur efficace pour un texte parfait.",
    fields: [
      {
        type: "textArea",
        label: "French Text",
        field: "French Text",
        placeholder: "Entrez votre texte ici",
        required: true,
        optional: false,
        id: "frenchtext",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in French grammar and conjugation. You will be provided with a text in French, and you will correct any grammar and verb conjugation errors in the text.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { frenchText } = fields;
      let prompt = `Correct the following French text for grammar and conjugation errors:
  ${frenchText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-sentence-rephraser",
    name: "AI Sentence Rephraser",
    slug: "ai-sentence-rephraser",
    shortDescription:
      "Enhance your writing with our AI sentence rephraser, improving clarity and tone while preserving original meaning.",
    fields: [
      {
        type: "textArea",
        label: "Original Sentence",
        field: "Original Sentence",
        placeholder: "The quick brown fox jumps over the lazy dog.",
        required: true,
        optional: false,
        id: "originalsentence",
      },
      {
        type: "textArea",
        label: "Desired Tone",
        field: "Desired Tone",
        placeholder: "Formal, Informal, Casual, Professional, etc.",
        required: false,
        optional: true,
        id: "desiredtone",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rephrasing sentences. You will be provided with an original sentence and a desired tone, and you will generate a rephrased version of the sentence that maintains its original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalSentence, desiredTone } = fields;
      let prompt = "Rephrase the following sentence based on the given tone:\n";
      prompt += "Original Sentence: " + originalSentence + "\n";
      if (desiredTone) {
        prompt += "Desired Tone: " + desiredTone + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "job-search-ai-assistant",
    name: "Job Search AI Assistant",
    slug: "job-search-ai-assistant",
    shortDescription:
      "Enhance your job search with our AI assistant: practice interviews, craft cover letters, and improve resumes effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Request Type",
        field: "Request Type",
        placeholder:
          "Interview Practice / Cover Letter Help / Resume Improvement",
        required: true,
        optional: false,
        id: "requesttype",
      },
      {
        type: "textArea",
        label: "Content",
        field: "Content",
        placeholder:
          "Paste your cover letter / resume here, type your interview question, or add any relevant information or context...",
        required: false,
        optional: true,
        id: "content",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in assisting job seekers. You will be provided with some base information and you will generate responses based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { requestType, content } = fields;
      let prompt = `Generate a response for the following request:
  `;
      prompt += `Request Type: ${requestType}.
  `;
      if (content) {
        prompt += `Content: ${content}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "legal-grammar-assistant",
    name: "Legal Grammar Assistant",
    slug: "legal-grammar-assistant",
    shortDescription:
      "Enhance your legal writing with our Legal Grammar Assistant, offering precise grammar corrections and valuable suggestions for lawyers and students.",
    fields: [
      {
        type: "textArea",
        label: "Legal Text",
        field: "Legal Text",
        placeholder: "In the case of...",
        required: true,
        optional: false,
        id: "legaltext",
      },
    ],
    categories: ["Writing", "Education", "Legal"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in legal writing and grammar. You will be provided with a legal text, and your task is to analyze it for grammatical correctness and provide suggestions for improvement.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { legalText } = fields.legalText;
      let prompt =
        "Analyze the following legal text for grammar and provide suggestions for improvement:\n";
      prompt += "Legal Text: " + legalText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "stem-personalized-tutor",
    name: "STEM Personalized Tutor",
    slug: "stem-personalized-tutor",
    shortDescription:
      "Get personalized STEM tutoring tailored to your knowledge level and learning style for effective and engaging learning support.",
    fields: [
      {
        type: "textArea",
        label: "STEM Subject",
        field: "STEM Subject",
        placeholder: "Physics",
        required: true,
        optional: false,
        id: "stemsubject",
      },
      {
        type: "textArea",
        label: "Learning Style",
        field: "Learning Style",
        placeholder: "Visual, Auditory, Kinesthetic, or Reading/Writing",
        required: false,
        optional: true,
        id: "learningstyle",
      },
      {
        type: "textArea",
        label: "Current Knowledge Level",
        field: "Current Knowledge Level",
        placeholder: "Beginner, Intermediate, Advanced",
        required: false,
        optional: true,
        id: "currentknowledgelevel",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt = `You are an expert STEM tutor. You will be provided with information about a student's subject of interest, learning style, and current knowledge level. Based on this information, you will generate personalized learning assistance to help the student understand the subject better.`;
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { STEMSubject, LearningStyle, CurrentKnowledgeLevel } = fields;
      let prompt = `Generate personalized learning assistance based on the following details:
  `;
      prompt += `STEM Subject: ${STEMSubject}.
  `;
      if (LearningStyle) {
        prompt += `Learning Style: ${LearningStyle}.
  `;
      }
      if (CurrentKnowledgeLevel) {
        prompt += `Current Knowledge Level: ${CurrentKnowledgeLevel}.
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-fanfiction-brainstormer",
    name: "AI FanFiction Brainstormer",
    slug: "ai-fanfiction-brainstormer",
    shortDescription:
      "Generate unique plot ideas and character interactions with AI FanFiction Brainstormer to overcome writer's block in your favorite fandoms.",
    fields: [
      {
        type: "textArea",
        label: "Fandom",
        field: "Fandom",
        placeholder: "Harry Potter",
        required: true,
        optional: false,
        id: "fandom",
      },
      {
        type: "textArea",
        label: "Characters",
        field: "Characters",
        placeholder: "Harry, Hermione, Ron",
        required: false,
        optional: true,
        id: "characters",
      },
      {
        type: "textArea",
        label: "Desired Plot or Scene",
        field: "Desired Plot or Scene",
        placeholder: "A magic duel between Harry and Hermione",
        required: false,
        optional: true,
        id: "desiredplotorscene",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating creative fanfiction ideas. You will be provided with details about a fandom, characters, and desired plot or scene, and you will generate engaging plot ideas, character interactions, and scene descriptions based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Fandom, Characters, DesiredPlotOrScene } = fields;
      let prompt =
        "Generate creative fanfiction content based on the following:\n";
      prompt += "Fandom: " + Fandom + ".\n";
      if (Characters) {
        prompt += "Characters involved: " + Characters + ".\n";
      }
      if (DesiredPlotOrScene) {
        prompt += "Desired plot or scene: " + DesiredPlotOrScene + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "writing-generator-ai",
    name: "Writing Generator AI",
    slug: "writing-generator-ai",
    shortDescription:
      "Create compelling content effortlessly with our Writing Generator AI, designed to complete any writing task you need.",
    fields: [
      {
        type: "textArea",
        label: "Writing Task",
        field: "Writing Task",
        placeholder: "Write a short story about a cat who loves to travel.",
        required: true,
        optional: false,
        id: "writingtask",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an AI writing assistant capable of completing various writing tasks. You will be provided with a specific writing task, and you will generate a response based on the details provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingTask } = fields;
      let prompt = "Please complete the following writing task:\n";
      prompt += "Task: " + writingTask + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "procon-factual-answer-generator",
    name: "ProCon Factual Answer Generator",
    slug: "procon-factual-answer-generator",
    shortDescription:
      "Get clear, factual answers with ProCon Factual Answer Generator, including pros and cons for each response.",
    fields: [
      {
        type: "textArea",
        label: "Question",
        field: "Question",
        placeholder: "What are the pros and cons of solar energy?",
        required: true,
        optional: false,
        id: "question",
      },
    ],
    categories: ["Education", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing factual answers to questions, along with the pros and cons of each answer. You will be given a question and you will generate a comprehensive response that includes both the advantages and disadvantages related to the topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Question } = fields.Question;
      let prompt =
        "Provide a factual answer to the following question, including pros and cons:\n";
      prompt += "Question: " + Question + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "scholar-ai-generator",
    name: "Scholar AI Generator",
    slug: "scholar-ai-generator",
    shortDescription:
      "Discover Scholar AI Generator: quickly find peer-reviewed articles to enhance your research and writing tasks effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic or Writing Task",
        field: "Research Topic or Writing Task",
        placeholder: "The impacts of climate change on agriculture.",
        required: true,
        optional: false,
        id: "researchtopicorwritingtask",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in finding peer-reviewed articles. You will be provided with a research topic or writing task, and you will generate a list of relevant scholarly articles based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopicOrWritingTask } = fields;
      let prompt =
        "Find peer-reviewed articles based on the following research topic or writing task:\n";
      prompt +=
        "Research Topic or Writing Task: " + researchTopicOrWritingTask + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "video-hook-generator",
    name: "Video Hook Generator",
    slug: "video-hook-generator",
    shortDescription:
      "Create captivating video hooks effortlessly! Use our generator to craft engaging questions that boost viewer interest.",
    fields: [
      {
        type: "textArea",
        label: "Video Title",
        field: "Video Title",
        placeholder: "The Future of Renewable Energy",
        required: true,
        optional: false,
        id: "videotitle",
      },
      {
        type: "textArea",
        label: "Video Audience",
        field: "Video Audience",
        placeholder: "Environmentally conscious individuals",
        required: false,
        optional: true,
        id: "videoaudience",
      },
      {
        type: "textArea",
        label: "Video Context",
        field: "Video Context",
        placeholder:
          "A video discussing the latest advancements in renewable energy technologies and their potential impact on the environment.",
        required: false,
        optional: true,
        id: "videocontext",
      },
    ],
    categories: ["Social Media", "Marketing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating engaging video hooks. You will be provided with details about a video, and you will create a compelling one-sentence hook in the form of a question based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { videoTitle, videoAudience, videoContext } = fields;
      let prompt =
        "Generate an engaging one-sentence hook for a video based on the following details:\n";
      prompt += "Video Title: " + videoTitle + "\n";
      if (videoAudience) {
        prompt += "Target Audience: " + videoAudience + "\n";
      }
      if (videoContext) {
        prompt += "Video Context: " + videoContext + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-academic-writing-vocabulary-builder",
    name: "AI Academic Writing Vocabulary Builder",
    slug: "ai-academic-writing-vocabulary-builder",
    shortDescription:
      "Enhance your academic writing with AI: generate advanced vocabulary, definitions, and example sentences for your field of study.",
    fields: [
      {
        type: "textArea",
        label: "Field of Study",
        field: "Field of Study",
        placeholder: "e.g., Biology, History, Computer Science",
        required: true,
        optional: false,
        id: "fieldofstudy",
      },
      {
        type: "textArea",
        label: "Specific Topic",
        field: "Specific Topic",
        placeholder:
          "e.g., Photosynthesis, French Revolution, Machine Learning",
        required: false,
        optional: true,
        id: "specifictopic",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in academic writing. You will be provided with a field of study and an optional specific topic, and you will generate a list of advanced vocabulary words, their definitions, and example sentences tailored to that field.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { fieldOfStudy, specificTopic } = fields;
      let prompt =
        "Generate a list of advanced vocabulary for academic writing based on the following:\n";
      prompt += "Field of Study: " + fieldOfStudy + ".\n";
      if (specificTopic) {
        prompt += "Specific Topic: " + specificTopic + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "cultural-event-promoter-ai",
    name: "Cultural Event Promoter AI",
    slug: "cultural-event-promoter-ai",
    shortDescription:
      "Promote your cultural events effortlessly with our AI tool, crafting engaging write-ups that spotlight attractions and benefits.",
    fields: [
      {
        type: "textArea",
        label: "Event Details",
        field: "Event Details",
        placeholder:
          "Arts Escape at Onions & All, every Sunday afternoon, Soweto...",
        required: true,
        optional: false,
        id: "eventdetails",
      },
      {
        type: "textArea",
        label: "Event Attractions",
        field: "Event Attractions",
        placeholder: "Live performances, artwork, cuisine...",
        required: false,
        optional: true,
        id: "eventattractions",
      },
      {
        type: "textArea",
        label: "Event Benefits",
        field: "Event Benefits",
        placeholder:
          "Cultural immersion, mentorship programs, community partnerships...",
        required: false,
        optional: true,
        id: "eventbenefits",
      },
    ],
    categories: ["Marketing", "Writing", "Social Media"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in promoting cultural events. You will be provided with details about the event, including attractions and benefits, and you will generate a promotional write-up that highlights these aspects.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { eventDetails, eventAttractions, eventBenefits } = fields;
      let prompt =
        "Generate a promotional write-up for the following cultural event:\n";
      prompt += "Event Details: " + eventDetails + "\n";
      if (eventAttractions) {
        prompt += "Attractions: " + eventAttractions + "\n";
      }
      if (eventBenefits) {
        prompt += "Benefits: " + eventBenefits + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "valentines-day-card-generator",
    name: "Valentine's Day Card Generator",
    slug: "valentines-day-card-generator",
    shortDescription:
      "Create a unique Valentine's Day card tailored to your partner with our personalized card generator. Make this day special!",
    fields: [
      {
        type: "textArea",
        label: "Partner's Name",
        field: "Partner's Name",
        placeholder: "Enter your partner's name",
        required: true,
        optional: false,
        id: "partnersname",
      },
      {
        type: "textArea",
        label: "Favorite Memories",
        field: "Favorite Memories",
        placeholder: "Enter your favorite memories",
        required: false,
        optional: true,
        id: "favoritememories",
      },
      {
        type: "textArea",
        label: "Things You Love About Them",
        field: "Things You Love About Them",
        placeholder: "Enter the things you love about your partner",
        required: false,
        optional: true,
        id: "thingsyouloveaboutthem",
      },
    ],
    categories: ["Social Media", "Writing", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized Valentine's Day cards. You will be provided with details about the user's partner and will create a heartfelt message for the card based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { partnerName, favoriteMemories, thingsYouLove } = fields;
      let prompt =
        "Create a personalized Valentine's Day card message based on the following:\n";
      prompt += "Partner's Name: " + partnerName + ".\n";
      if (favoriteMemories) {
        prompt += "Favorite Memories: " + favoriteMemories + ".\n";
      }
      if (thingsYouLove) {
        prompt += "Things You Love About Them: " + thingsYouLove + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-powered-qualification-statement-generator",
    name: "AI Powered Qualification Statement Generator",
    slug: "ai-powered-qualification-statement-generator",
    shortDescription:
      "Generate compelling qualification statements with our AI-powered tool, highlighting your skills for any role or opportunity.",
    fields: [
      {
        type: "textArea",
        label: "Skills",
        field: "Skills",
        placeholder: "Project Management, Leadership, Communication",
        required: true,
        optional: false,
        id: "skills",
      },
      {
        type: "textArea",
        label: "Experiences",
        field: "Experiences",
        placeholder: "Managed a team of 10 in a fast-paced environment...",
        required: false,
        optional: true,
        id: "experiences",
      },
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "We are looking for a project manager with...",
        required: false,
        optional: true,
        id: "jobdescription",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating qualification statements. You will be provided with information about skills, experiences, and job descriptions, and you will create a concise statement that highlights the user's qualifications for a specific role or opportunity.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Skills, Experiences, JobDescription } = fields;
      let prompt =
        "Generate a qualification statement based on the following:\n";
      prompt += "Skills: " + Skills + ".\n";
      if (Experiences) {
        prompt += "Experiences: " + Experiences + ".\n";
      }
      if (JobDescription) {
        prompt += "Job Description: " + JobDescription + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-journalist",
    name: "AI Journalist",
    slug: "ai-journalist",
    shortDescription:
      "Revolutionize your journalism with AI Journalist, your assistant for researching, drafting, and fact-checking articles effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Climate Change",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "General Public",
        required: false,
        optional: true,
        id: "targetaudience",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert AI journalist. You will be provided with a topic and target audience, and you will generate a well-researched article based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, TargetAudience } = fields;
      let prompt = "Draft an article based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (TargetAudience) {
        prompt += "Target Audience: " + TargetAudience + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "economic-theory-explorer",
    name: "Economic Theory Explorer",
    slug: "economic-theory-explorer",
    shortDescription:
      "Explore various economic theories and enhance your understanding of economic research with Economic Theory Explorer.",
    fields: [
      {
        type: "textArea",
        label: "Economic Theory",
        field: "Economic Theory",
        placeholder: "Keynesian Economics",
        required: true,
        optional: false,
        id: "economictheory",
      },
    ],
    categories: ["Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in economic theories. You will be provided with a specific economic theory and you will provide detailed information and insights about it.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { EconomicTheory } = fields.EconomicTheory;
      let prompt = "Provide an exploration of the following economic theory:\n";
      prompt += "Economic Theory: " + EconomicTheory + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "musician-practice-scheduler",
    name: "Musician Practice Scheduler",
    slug: "musician-practice-scheduler",
    shortDescription:
      "Create a personalized practice schedule for musicians, tailored to your time, skill level, and focus areas for optimal growth.",
    fields: [
      {
        type: "textArea",
        label: "Available Time",
        field: "Available Time",
        placeholder: "2 hours",
        required: true,
        optional: false,
        id: "availabletime",
      },
      {
        type: "textArea",
        label: "Skill Level",
        field: "Skill Level",
        placeholder: "Intermediate",
        required: false,
        optional: true,
        id: "skilllevel",
      },
      {
        type: "textArea",
        label: "Focus Areas",
        field: "Focus Areas",
        placeholder: "Scales, Arpeggios, Sight Reading",
        required: false,
        optional: true,
        id: "focusareas",
      },
    ],
    categories: ["Education", "Training Material", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized practice schedules for musicians. You will be provided with information about the musician's available time, skill level, and focus areas to generate a tailored practice schedule.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { availableTime, skillLevel, focusAreas } = fields;
      let prompt =
        "Create a personalized practice schedule based on the following:\n";
      prompt += "Available time: " + availableTime + ".\n";
      if (skillLevel) {
        prompt += "Skill level: " + skillLevel + ".\n";
      }
      if (focusAreas) {
        prompt += "Focus areas: " + focusAreas + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-punctuation-checker",
    name: "AI Punctuation Checker",
    slug: "ai-punctuation-checker",
    shortDescription:
      "Enhance your writing with our AI Punctuation Checker, correcting errors effortlessly for clear and polished text.",
    fields: [
      {
        type: "textArea",
        label: "Text to Check",
        field: "Text to Check",
        placeholder: "Enter text here...",
        required: true,
        optional: false,
        id: "texttocheck",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in punctuation correction. You will be provided with a text that may contain punctuation errors, and your task is to check and correct those errors.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToCheck } = fields;
      let prompt =
        "Please check the following text for punctuation errors and provide corrections:\n";
      prompt += textToCheck + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "family-mediator-communication-skills-trainer",
    name: "Family Mediator Communication Skills Trainer",
    slug: "family-mediator-communication-skills-trainer",
    shortDescription:
      "Enhance your family mediation skills with our expert training, focusing on effective communication for better conflict resolution.",
    fields: [
      {
        type: "textArea",
        label: "Scenario",
        field: "Scenario",
        placeholder:
          "A husband and wife are arguing about their child's education...",
        required: true,
        optional: false,
        id: "scenario",
      },
    ],
    categories: ["Education", "Training Material", "Legal"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in training family mediators to improve their communication skills. You will be provided with a scenario and you will generate guidance on how to effectively communicate in that situation.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Scenario } = fields;
      let prompt =
        "Provide communication skills training based on the following scenario:\n";
      prompt += "Scenario: " + Scenario + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "marketing-cta-generator",
    name: "Marketing CTA Generator",
    slug: "marketing-cta-generator",
    shortDescription:
      "Create powerful marketing CTAs with our AI generator, designed to boost conversions and engage your audience effectively.",
    fields: [
      {
        type: "textArea",
        label: "Content Summary",
        field: "Content Summary",
        placeholder: "Briefly summarize the content preceding the CTA.",
        required: true,
        optional: false,
        id: "contentsummary",
      },
      {
        type: "textArea",
        label: "Desired Action",
        field: "Desired Action",
        placeholder:
          "What do you want the reader to do? (e.g., Visit website, download ebook, sign up)",
        required: false,
        optional: true,
        id: "desiredaction",
      },
      {
        type: "textArea",
        label: "Brand Tone",
        field: "Brand Tone",
        placeholder: "Formal, informal, playful, urgent, etc.",
        required: false,
        optional: true,
        id: "brandtone",
      },
    ],
    categories: ["Marketing", "Website"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating marketing calls to action (CTAs). You will be provided with a summary of the content and desired actions, and you will create compelling CTAs that drive conversions.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { contentSummary, desiredAction, brandTone } = fields;
      let prompt =
        "Generate a marketing call to action based on the following:\n";
      prompt += "Content Summary: " + contentSummary + "\n";
      if (desiredAction) {
        prompt += "Desired Action: " + desiredAction + "\n";
      }
      if (brandTone) {
        prompt += "Brand Tone: " + brandTone + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-hypothesis-generator",
    name: "AI Hypothesis Generator",
    slug: "ai-hypothesis-generator",
    shortDescription:
      "Generate clear and testable hypotheses for your research projects with our AI Hypothesis Generator tailored to your topic.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic",
        field: "Research Topic",
        placeholder: "Effects of light on plant growth",
        required: true,
        optional: false,
        id: "researchtopic",
      },
      {
        type: "textArea",
        label: "Initial Observations",
        field: "Initial Observations",
        placeholder:
          "Plants grown in darker conditions appear to be less healthy",
        required: false,
        optional: true,
        id: "initialobservations",
      },
    ],
    categories: ["Education", "Writing", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating clear and testable hypotheses for research projects. You will be provided with a research topic and initial observations, and you will generate a hypothesis based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic, initialObservations } = fields;
      let prompt =
        "Generate a clear and testable hypothesis based on the following:\n";
      prompt += "Research Topic: " + researchTopic + "\n";
      if (initialObservations) {
        prompt += "Initial Observations: " + initialObservations + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-cover-letter-writer",
    name: "AI Cover Letter Writer",
    slug: "ai-cover-letter-writer",
    shortDescription:
      "Create a personalized cover letter effortlessly with our AI Cover Letter Writer, tailored to your goals and resume.",
    fields: [
      {
        type: "textArea",
        label: "Job Objective",
        field: "Job Objective",
        placeholder: "Software Developer at XYZ Company",
        required: true,
        optional: false,
        id: "jobobjective",
      },
      {
        type: "textArea",
        label: "Resume or Background",
        field: "Resume or Background",
        placeholder: "Computer Science graduate with experience in...",
        required: false,
        optional: true,
        id: "resumeorbackground",
      },
      {
        type: "textArea",
        label: "Additional Notes",
        field: "Additional Notes",
        placeholder: "",
        required: false,
        optional: true,
        id: "additionalnotes",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in writing cover letters. You will be provided with a job objective and background information, and you will generate a personalized cover letter based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { jobObjective, resumeOrBackground, additionalNotes } = fields;
      let prompt =
        "Generate a personalized cover letter based on the following:\n";
      prompt += "Job Objective: " + jobObjective + "\n";
      if (resumeOrBackground) {
        prompt += "Resume/Background: " + resumeOrBackground + "\n";
      }
      if (additionalNotes) {
        prompt += "Additional Notes: " + additionalNotes + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "classroom-activity-generator",
    name: "Classroom Activity Generator",
    slug: "classroom-activity-generator",
    shortDescription:
      "Generate tailored classroom activities for diverse skill levels, enhancing learning on any topic with our Classroom Activity Generator.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "Photosynthesis",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Grade Level",
        field: "Grade Level",
        placeholder: "7",
        required: false,
        optional: true,
        id: "gradelevel",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating classroom activities. You will be provided with a topic and grade level, and you will create differentiated activities suitable for students of varying skill levels.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, GradeLevel } = fields;
      let prompt =
        "Generate differentiated classroom activities based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (GradeLevel) {
        prompt += "Grade Level: " + GradeLevel + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-interview-question-preparer",
    name: "AI Interview Question Preparer",
    slug: "ai-interview-question-preparer",
    shortDescription:
      "Generate tailored interview questions and answers for any job description with our AI Interview Question Preparer. Prepare effectively!",
    fields: [
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "We are looking for a software engineer with...",
        required: true,
        optional: false,
        id: "jobdescription",
      },
      {
        type: "textArea",
        label: "Industry",
        field: "Industry",
        placeholder: "Technology",
        required: false,
        optional: true,
        id: "industry",
      },
    ],
    categories: ["Business", "Training Material", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in preparing interview questions. You will be provided with a job description and industry information, and you will generate potential interview questions along with suggested answers based on the provided details.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { jobDescription, industry } = fields;
      let prompt =
        "Generate potential interview questions based on the following:\n";
      prompt += "Job Description: " + jobDescription + "\n";
      if (industry) {
        prompt += "Industry: " + industry + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "social-media-content-planner",
    name: "Social Media Content Planner",
    slug: "social-media-content-planner",
    shortDescription:
      "Streamline your social media strategy with our content planner, generating monthly ideas, engagements, and weekly stories effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Industry",
        field: "Industry",
        placeholder: "Medical Group",
        required: true,
        optional: false,
        id: "industry",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "Any medical patient and potiental patient, staff",
        required: false,
        optional: true,
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Social Media Platforms",
        field: "Social Media Platforms",
        placeholder: "Instagram, TikTok, LinkedIn",
        required: false,
        optional: true,
        id: "socialmediaplatforms",
      },
    ],
    categories: ["Social Media", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating social media content plans. You will be provided with information about the industry, target audience, and social media platforms, and you will generate a comprehensive monthly content plan.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Industry, Target_Audience, Social_Media_Platforms } = fields;
      let prompt =
        "Generate a monthly social media content plan based on the following:\n";
      prompt += "Industry: " + Industry + ".\n";
      if (Target_Audience) {
        prompt += "Target Audience: " + Target_Audience + ".\n";
      }
      if (Social_Media_Platforms) {
        prompt += "Social Media Platforms: " + Social_Media_Platforms + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-discussion-post-writer",
    name: "AI Discussion Post Writer",
    slug: "ai-discussion-post-writer",
    shortDescription:
      "Generate engaging discussion posts effortlessly with our AI tool, crafting well-structured content based on your key points.",
    fields: [
      {
        type: "textArea",
        label: "Topic",
        field: "Topic",
        placeholder: "The Impact of Climate Change",
        required: true,
        optional: false,
        id: "topic",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "Rising sea levels, Increased global temperatures, Effects on wildlife",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating discussion posts. You will be provided with a topic and key points, and you will generate a well-structured and engaging discussion post based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Topic, KeyPoints } = fields;
      let prompt = "Generate a discussion post based on the following:\n";
      prompt += "Topic: " + Topic + "\n";
      if (KeyPoints) {
        prompt += "Key Points: " + KeyPoints + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-persona-emulator",
    name: "AI Persona Emulator",
    slug: "ai-persona-emulator",
    shortDescription:
      "Create a conversational AI that emulates any person with our AI Persona Emulator for engaging and personalized interactions.",
    fields: [
      {
        type: "textArea",
        label: "Person's Name",
        field: "Person's Name",
        placeholder: "Elon Musk",
        required: true,
        optional: false,
        id: "personsname",
      },
    ],
    categories: ["Social Media", "Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in emulating personas. You will be provided with the name of a person, and you will generate responses as if you were that person.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { personName } = fields;
      let prompt = `Emulate a conversation as if you were ${personName}. Respond to questions and comments in a manner consistent with how ${personName} would respond.`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-wedding-speech-generator",
    name: "AI Wedding Speech Generator",
    slug: "ai-wedding-speech-generator",
    shortDescription:
      "Create heartfelt wedding speeches effortlessly with our AI generator, personalizing them based on your unique couple's story and memories.",
    fields: [
      {
        type: "textArea",
        label: "Relationship to Couple",
        field: "Relationship to Couple",
        placeholder: "Best Man, Maid of Honor, Father of the Bride, etc.",
        required: true,
        optional: false,
        id: "relationshiptocouple",
      },
      {
        type: "textArea",
        label: "Couple's Story",
        field: "Couple's Story",
        placeholder: "How they met, their journey together, etc.",
        required: false,
        optional: true,
        id: "couplesstory",
      },
      {
        type: "textArea",
        label: "Key Memories",
        field: "Key Memories",
        placeholder: "Memorable moments with or about the couple.",
        required: false,
        optional: true,
        id: "keymemories",
      },
    ],
    categories: ["Writing", "Social Media", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating personalized wedding speeches. You will be provided with details about the relationship to the couple, their story, and key memories, and you will create a heartfelt wedding speech based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { relationshipToCouple, couplesStory, keyMemories } = fields;
      let prompt = "Generate a wedding speech based on the following:\n";
      prompt += "Relationship to couple: " + relationshipToCouple + ".\n";
      if (couplesStory) {
        prompt += "Couple's story: " + couplesStory + ".\n";
      }
      if (keyMemories) {
        prompt += "Key memories: " + keyMemories + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "discussion-board-response-generator",
    name: "Discussion Board Response Generator",
    slug: "discussion-board-response-generator",
    shortDescription:
      "Generate thoughtful and relevant responses for your discussion board posts effortlessly with our response generator tool.",
    fields: [
      {
        type: "textArea",
        label: "Initial Post",
        field: "Initial Post",
        placeholder:
          "What are your thoughts on the impact of climate change on global economies?",
        required: true,
        optional: false,
        id: "initialpost",
      },
      {
        type: "textArea",
        label: "User's Thoughts",
        field: "User's Thoughts",
        placeholder:
          "I believe that climate change has a significant impact on global economies...",
        required: false,
        optional: true,
        id: "usersthoughts",
      },
    ],
    categories: ["Education", "Writing", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating thoughtful and relevant responses for discussion board posts. You will be provided with an initial post and the user's thoughts, and you will generate a response that is engaging and insightful.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { initialPost, usersThoughts } = fields;
      let prompt =
        "Generate a thoughtful response to the following discussion board post:\n";
      prompt += "Initial Post: " + initialPost + "\n";
      if (usersThoughts) {
        prompt += "User's Thoughts: " + usersThoughts + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "auto-blogging-ai",
    name: "Auto Blogging AI",
    slug: "auto-blogging-ai",
    shortDescription:
      "Create unique and engaging blog posts effortlessly with Auto Blogging AI, generating content tailored to your chosen topics.",
    fields: [
      {
        type: "textArea",
        label: "Blog Topic",
        field: "Blog Topic",
        placeholder: "The Future of Renewable Energy",
        required: true,
        optional: false,
        id: "blogtopic",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating blog content. You will be provided with a specific topic and you will draft a unique and engaging blog post based on that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { blogTopic } = fields;
      let prompt =
        "Draft a unique and engaging blog post based on the following topic:\n";
      prompt += "Blog Topic: " + blogTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "cover-letter-maker",
    name: "Cover Letter Maker",
    slug: "cover-letter-maker",
    shortDescription:
      "Create a professional cover letter tailored to your job description and personal details with our easy-to-use Cover Letter Maker.",
    fields: [
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "We are looking for a software engineer with...",
        required: true,
        optional: false,
        id: "jobdescription",
      },
      {
        type: "textArea",
        label: "Personal Information",
        field: "Personal Information",
        placeholder: "I am a software engineer with 5 years of experience...",
        required: false,
        optional: true,
        id: "personalinformation",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating cover letters. You will be provided with a job description and personal information, and you will generate a professional cover letter tailored to the job.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { jobDescription, personalInformation } = fields;
      let prompt = "Generate a cover letter based on the following:\n";
      prompt += "Job Description: " + jobDescription + "\n";
      if (personalInformation) {
        prompt += "Personal Information: " + personalInformation + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-original-content-generator",
    name: "AI Original Content Generator",
    slug: "ai-original-content-generator",
    shortDescription:
      "Generate unique content effortlessly with our AI Original Content Generator, tailored to your topics or prompts.",
    fields: [
      {
        type: "textArea",
        label: "Content Topic",
        field: "Content Topic",
        placeholder: "The Impact of Climate Change",
        required: true,
        optional: false,
        id: "contenttopic",
      },
      {
        type: "textArea",
        label: "Content Length or Description",
        field: "Content Length or Description",
        placeholder: "3 paragraphs",
        required: false,
        optional: true,
        id: "contentlengthordescription",
      },
    ],
    categories: ["Writing", "Marketing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating original content. You will be provided with a topic and additional details, and you will create engaging and informative content based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { contentTopic, contentLengthOrDescription } = fields;
      let prompt = "Generate original content based on the following:\n";
      prompt += "Content Topic: " + contentTopic + ".\n";
      if (contentLengthOrDescription) {
        prompt +=
          "Content Length or Description: " +
          contentLengthOrDescription +
          ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-email-reply-reviewer",
    name: "AI Email Reply Reviewer",
    slug: "ai-email-reply-reviewer",
    shortDescription:
      "Get instant feedback on your email drafts with our AI Email Reply Reviewer, ensuring contextually relevant and polished responses.",
    fields: [
      {
        type: "textArea",
        label: "Received Email or Thread",
        field: "Received Email or Thread",
        placeholder: "Email thread or the received email goes here...",
        required: true,
        optional: false,
        id: "receivedemailorthread",
      },
      {
        type: "textArea",
        label: "Draft Email Response",
        field: "Draft Email Response",
        placeholder: "Your draft response to the email...",
        required: false,
        optional: true,
        id: "draftemailresponse",
      },
    ],
    categories: ["Email", "Support", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in reviewing and providing feedback on email responses. You will be provided with a received email or thread and a draft response. Your task is to review the draft response and provide constructive feedback.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { receivedEmailOrThread, draftEmailResponse } = fields;
      let prompt = `Review the following draft email response based on the received email or thread:
  Received Email or Thread: ${receivedEmailOrThread}
  `;
      if (draftEmailResponse) {
        prompt += `Draft Email Response: ${draftEmailResponse}
  `;
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "catchy-tech-name-generator",
    name: "Catchy Tech Name Generator",
    slug: "catchy-tech-name-generator",
    shortDescription:
      "Generate catchy names for your tech startup or product with our unique Tech Name Generator. Make your brand unforgettable!",
    fields: [
      {
        type: "textArea",
        label: "Industry",
        field: "Industry",
        placeholder: "e.g., Artificial Intelligence, Fintech, SaaS",
        required: true,
        optional: false,
        id: "industry",
      },
      {
        type: "textArea",
        label: "Target Audience",
        field: "Target Audience",
        placeholder: "e.g., Businesses, Consumers, Developers",
        required: false,
        optional: true,
        id: "targetaudience",
      },
      {
        type: "textArea",
        label: "Keywords",
        field: "Keywords",
        placeholder: "e.g., Innovative, Secure, Efficient",
        required: false,
        optional: true,
        id: "keywords",
      },
    ],
    categories: ["Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating catchy and memorable names for tech startups and products. You will be provided with some base information, and you will generate a list of creative names based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Industry, TargetAudience, Keywords } = fields;
      let prompt = "Generate catchy tech names based on the following:\n";
      prompt += "Industry: " + Industry + ".\n";
      if (TargetAudience) {
        prompt += "Target audience: " + TargetAudience + ".\n";
      }
      if (Keywords) {
        prompt += "Keywords: " + Keywords + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "punctuation-checker",
    name: "Punctuation Checker",
    slug: "punctuation-checker",
    shortDescription:
      "Ensure your writing shines with our Punctuation Checker, correcting errors and enhancing clarity in your text effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Text to Check",
        field: "Text to Check",
        placeholder: "Enter text here...",
        required: true,
        optional: false,
        id: "texttocheck",
      },
    ],
    categories: ["Writing", "Education", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in punctuation checking and correction. You will be provided with a text, and your task is to identify and correct any punctuation errors present in the text.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textToCheck } = fields;
      let prompt =
        "Please check the following text for punctuation errors and provide corrections:\n";
      prompt += "Text: " + textToCheck + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "multi-question-answering-ai",
    name: "Multi-Question Answering AI",
    slug: "multi-question-answering-ai",
    shortDescription:
      "Get accurate and detailed answers to multiple questions at once with our Multi-Question Answering AI. Simplify your inquiries today!",
    fields: [
      {
        type: "textArea",
        label: "Questions",
        field: "Questions",
        placeholder:
          "What is the capital of France? Who was the first president of the United States?",
        required: true,
        optional: false,
        id: "questions",
      },
    ],
    categories: ["Support", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an advanced AI designed to provide accurate and detailed answers to multiple questions. You will receive a list of questions, and your task is to answer each one comprehensively.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Questions } = fields;
      let prompt =
        "Please provide detailed answers to the following questions:\n";
      prompt += Questions + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "thesis-statement-generator",
    name: "Thesis Statement Generator",
    slug: "thesis-statement-generator",
    shortDescription:
      "Create a strong thesis statement effortlessly with our generator, designed to craft concise statements based on your prompts.",
    fields: [
      {
        type: "textArea",
        label: "Prompt",
        field: "Prompt",
        placeholder: "Discuss the impact of social media on mental health.",
        required: true,
        optional: false,
        id: "prompt",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting thesis statements. You will be provided with a prompt, and your task is to generate a strong and concise thesis statement based on that prompt.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Prompt } = fields;
      let prompt =
        "Generate a thesis statement based on the following prompt:\n";
      prompt += "Prompt: " + Prompt + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-first-draft-generator",
    name: "AI First Draft Generator",
    slug: "ai-first-draft-generator",
    shortDescription:
      "Generate initial drafts of essays and articles effortlessly with our AI First Draft Generator. Perfect your writing from the start!",
    fields: [
      {
        type: "textArea",
        label: "Topic and Type",
        field: "Topic and Type",
        placeholder: "Essay on the effects of climate change",
        required: true,
        optional: false,
        id: "topicandtype",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "Rising global temperatures, Melting ice caps, Increased frequency of natural disasters",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating first drafts for essays, articles, or reports. You will be provided with a topic and key points, and you will create an initial draft based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { topicAndType, keyPoints } = fields;
      let prompt = "Generate a first draft based on the following:\n";
      prompt += "Topic and Type: " + topicAndType + ".\n";
      if (keyPoints) {
        prompt += "Key Points: " + keyPoints + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-recipe-developer",
    name: "AI Recipe Developer",
    slug: "ai-recipe-developer",
    shortDescription:
      "Create unique recipes with our AI Recipe Developer, tailored to your ingredients, dietary needs, and cuisine preferences!",
    fields: [
      {
        type: "textArea",
        label: "Ingredients",
        field: "Ingredients",
        placeholder: "Chicken, bell peppers, rice",
        required: true,
        optional: false,
        id: "ingredients",
      },
      {
        type: "textArea",
        label: "Dietary Restrictions",
        field: "Dietary Restrictions",
        placeholder: "Gluten-free",
        required: false,
        optional: true,
        id: "dietaryrestrictions",
      },
      {
        type: "textArea",
        label: "Cuisine Type",
        field: "Cuisine Type",
        placeholder: "Italian",
        required: false,
        optional: true,
        id: "cuisinetype",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating unique recipes. You will be provided with specific ingredients, dietary restrictions, and cuisine types to generate a recipe.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Ingredients, DietaryRestrictions, CuisineType } = fields;
      let prompt = "Generate a unique recipe based on the following:\n";
      prompt += "Ingredients: " + Ingredients + ".\n";
      if (DietaryRestrictions) {
        prompt += "Dietary restrictions: " + DietaryRestrictions + ".\n";
      }
      if (CuisineType) {
        prompt += "Cuisine type: " + CuisineType + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "blog-post-meta-description-generator",
    name: "Blog Post Meta Description Generator",
    slug: "blog-post-meta-description-generator",
    shortDescription:
      "Create catchy meta descriptions for your blog posts effortlessly with our generator—optimized for titles, keywords, and main ideas.",
    fields: [
      {
        type: "textArea",
        label: "Blog Post Title",
        field: "Blog Post Title",
        placeholder: "Learning to Code: A Complete Guide for Beginners",
        required: true,
        optional: false,
        id: "blogposttitle",
      },
      {
        type: "textArea",
        label: "Blog Post Keywords",
        field: "Blog Post Keywords",
        placeholder:
          "learn to code, coding for beginners, coding languages, coding resources, coding courses",
        required: false,
        optional: true,
        id: "blogpostkeywords",
      },
      {
        type: "textArea",
        label: "Blog Post Main Idea",
        field: "Blog Post Main Idea",
        placeholder:
          "Provide a comprehensive overview of the basics of coding, the benefits of learning to code, the best coding languages to start with, and the best resources and courses to learn coding.",
        required: false,
        optional: true,
        id: "blogpostmainidea",
      },
    ],
    categories: ["Website", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating meta descriptions for blog posts. You will be provided with the title, keywords, and main idea of a blog post, and you will create a short and catchy meta description based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { blogPostTitle, blogPostKeywords, blogPostMainIdea } = fields;
      let prompt = "Generate a meta description for the following blog post:\n";
      prompt += "Blog Post Title: " + blogPostTitle + "\n";
      if (blogPostKeywords) {
        prompt += "Keywords: " + blogPostKeywords + "\n";
      }
      if (blogPostMainIdea) {
        prompt += "Main Idea: " + blogPostMainIdea + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "history-ai",
    name: "History AI",
    slug: "history-ai",
    shortDescription:
      "Explore History AI for accurate insights on historical events, figures, and periods that enrich your understanding of the past.",
    fields: [
      {
        type: "textArea",
        label: "Historical Query",
        field: "Historical Query",
        placeholder: "Tell me about the French Revolution.",
        required: true,
        optional: false,
        id: "historicalquery",
      },
    ],
    categories: ["Education", "Writing", "History"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a knowledgeable assistant specializing in historical information. You will be provided with a historical query, and you will generate a comprehensive and accurate response based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { historicalQuery } = fields.historicalQuery;
      let prompt =
        "Provide detailed information based on the following query:\n";
      prompt += "Historical Query: " + historicalQuery + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "key-notes-identifier",
    name: "Key Notes Identifier",
    slug: "key-notes-identifier",
    shortDescription:
      "Analyze text effortlessly with Key Notes Identifier, pinpointing essential points for clarity and focus in your content.",
    fields: [
      {
        type: "textArea",
        label: "Text for Analysis",
        field: "Text for Analysis",
        placeholder: "Enter the text you want to analyze here.",
        required: true,
        optional: false,
        id: "textforanalysis",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing text and identifying key points. You will be provided with a block of text, and your task is to extract the most important information from it.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textForAnalysis } = fields;
      let prompt = "Analyze the following text and identify the key points:\n";
      prompt += textForAnalysis + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-podcast-script-outliner",
    name: "AI Podcast Script Outliner",
    slug: "ai-podcast-script-outliner",
    shortDescription:
      "Generate structured podcast outlines effortlessly with our AI tool, including key talking points and guest questions for engaging episodes.",
    fields: [
      {
        type: "textArea",
        label: "Podcast Theme",
        field: "Podcast Theme",
        placeholder: "Technology Trends",
        required: true,
        optional: false,
        id: "podcasttheme",
      },
      {
        type: "textArea",
        label: "Key Talking Points",
        field: "Key Talking Points",
        placeholder:
          "1. The rise of AI\n2. Impact on jobs\n3. Future predictions",
        required: false,
        optional: true,
        id: "keytalkingpoints",
      },
      {
        type: "textArea",
        label: "Guest Details",
        field: "Guest Details",
        placeholder: "John Doe, AI Expert",
        required: false,
        optional: true,
        id: "guestdetails",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating structured outlines for podcast episodes. You will be provided with details about the podcast theme, key talking points, and guest information to create a comprehensive outline.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { podcastTheme, keyTalkingPoints, guestDetails } = fields;
      let prompt =
        "Generate a structured outline for a podcast episode based on the following:\n";
      prompt += "Podcast Theme: " + podcastTheme + "\n";
      if (keyTalkingPoints) {
        prompt += "Key Talking Points: " + keyTalkingPoints + "\n";
      }
      if (guestDetails) {
        prompt += "Guest Details: " + guestDetails + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-writers-block-buster",
    name: "AI Writer's Block Buster",
    slug: "ai-writers-block-buster",
    shortDescription:
      "Overcome writer's block with AI Writer's Block Buster! Generate creative prompts and story starters for any writing project.",
    fields: [
      {
        type: "textArea",
        label: "Genre",
        field: "Genre",
        placeholder: "Fantasy, Mystery, Romance, etc.",
        required: true,
        optional: false,
        id: "genre",
      },
      {
        type: "textArea",
        label: "Writing Style",
        field: "Writing Style",
        placeholder: "Descriptive, Narrative, Expository, etc.",
        required: false,
        optional: true,
        id: "writingstyle",
      },
      {
        type: "textArea",
        label: "Themes",
        field: "Themes",
        placeholder: "Love, Adventure, Friendship, etc.",
        required: false,
        optional: true,
        id: "themes",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating creative writing prompts and story starters. You will be provided with some base information, and you will generate a unique prompt to help overcome writer's block.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Genre, WritingStyle, Themes } = fields;
      let prompt =
        "Generate a creative writing prompt based on the following:\n";
      prompt += "Genre: " + Genre + ".\n";
      if (WritingStyle) {
        prompt += "Writing Style: " + WritingStyle + ".\n";
      }
      if (Themes) {
        prompt += "Themes: " + Themes + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "science-question-answer-ai",
    name: "Science Question Answer AI",
    slug: "science-question-answer-ai",
    shortDescription:
      "Get accurate answers to your science questions with our AI-driven Science Question Answer tool. Explore factual information today!",
    fields: [
      {
        type: "textArea",
        label: "Science Question",
        field: "Science Question",
        placeholder: "What is the function of mitochondria in a cell?",
        required: true,
        optional: false,
        id: "sciencequestion",
      },
    ],
    categories: ["Education", "Science", "Support"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing accurate and factual answers to science questions. You will be given a specific science question, and your task is to provide a clear and concise answer based on established scientific knowledge.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { scienceQuestion } = fields;
      let prompt =
        "Provide a detailed answer to the following science question:\n";
      prompt += "Question: " + scienceQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-smart-notes-creator",
    name: "AI Smart Notes Creator",
    slug: "ai-smart-notes-creator",
    shortDescription:
      "Transform your text into concise, organized notes effortlessly with our AI Smart Notes Creator. Simplify your note-taking today!",
    fields: [
      {
        type: "textArea",
        label: "Text",
        field: "Text",
        placeholder: "Enter the text you want to create notes from here.",
        required: true,
        optional: false,
        id: "text",
      },
    ],
    categories: ["Writing", "Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing and organizing information. You will be provided with a block of text, and your task is to create concise and structured notes based on the content provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Text } = fields;
      let prompt =
        "Create concise and organized notes from the following text:\n";
      prompt += Text + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "similarity-search-tool",
    name: "Similarity Search Tool",
    slug: "similarity-search-tool",
    shortDescription:
      "Effortlessly find similar items with our Similarity Search Tool. Streamline your searches and enhance your results today!",
    fields: [
      {
        type: "textArea",
        label: "List",
        field: "List",
        placeholder: "Apple, Banana, Orange, Pear, Peach",
        required: true,
        optional: false,
        id: "list",
      },
      {
        type: "textArea",
        label: "Description",
        field: "Description",
        placeholder: "A sweet, round fruit",
        required: false,
        optional: true,
        id: "description",
      },
    ],
    categories: ["Business", "Support", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in similarity search. You will be provided with a list of items and a description, and you will identify items from the list that closely match the given description.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { List, Description } = fields;
      let prompt =
        "Search for items in the provided list that match the following description:\n";
      prompt += "List: " + List + "\n";
      if (Description) {
        prompt += "Description: " + Description + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "software-sales-partner-onboarding-assistant",
    name: "Software Sales Partner Onboarding Assistant",
    slug: "software-sales-partner-onboarding-assistant",
    shortDescription:
      "Streamline your software sales partner onboarding with our assistant, generating a comprehensive and effective onboarding plan.",
    fields: [
      {
        type: "textArea",
        label: "Software Product Details",
        field: "Software Product Details",
        placeholder: "Product name, features, benefits, target audience...",
        required: true,
        optional: false,
        id: "softwareproductdetails",
      },
      {
        type: "textArea",
        label: "Sales Partner Background",
        field: "Sales Partner Background",
        placeholder: "Experience, strengths, past sales records...",
        required: false,
        optional: true,
        id: "salespartnerbackground",
      },
    ],
    categories: ["Sales", "Training Material", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in onboarding software sales partners. You will be provided with details about the software product and the sales partner's background, and you will generate a comprehensive onboarding plan based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { softwareProductDetails, salesPartnerBackground } = fields;
      let prompt =
        "Generate a comprehensive onboarding plan based on the following:\n";
      prompt += "Software Product Details: " + softwareProductDetails + "\n";
      if (salesPartnerBackground) {
        prompt += "Sales Partner Background: " + salesPartnerBackground + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "groundhog-day-message-generator",
    name: "Groundhog Day Message Generator",
    slug: "groundhog-day-message-generator",
    shortDescription:
      "Create fun and engaging Groundhog Day messages effortlessly with our easy-to-use message generator. Celebrate in style!",
    fields: [
      {
        type: "textArea",
        label: "Year",
        field: "Year",
        placeholder: "2024",
        required: true,
        optional: false,
        id: "year",
      },
      {
        type: "textArea",
        label: "Shadow Seen?",
        field: "Shadow Seen?",
        placeholder: "Yes/No",
        required: false,
        optional: true,
        id: "shadowseen?",
      },
    ],
    categories: ["Social Media", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting engaging and fun messages for Groundhog Day. You will be provided with details about the year and whether the groundhog saw its shadow, and you will generate a creative Groundhog Day message based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Year, ShadowSeen } = fields;
      let prompt = "Generate a Groundhog Day message based on the following:\n";
      prompt += "Year: " + Year + ".\n";
      if (ShadowSeen) {
        prompt += "Shadow seen: " + ShadowSeen + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "essay-style-analyzer",
    name: "Essay Style Analyzer",
    slug: "essay-style-analyzer",
    shortDescription:
      "Analyze and replicate writing styles with our Essay Style Analyzer, perfect for mastering multiple essays effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Essay 1",
        field: "Essay 1",
        placeholder: "It was a bright cold day in April...",
        required: true,
        optional: false,
        id: "essay1",
      },
      {
        type: "textArea",
        label: "Essay 2",
        field: "Essay 2",
        placeholder: "In the town of West Egg, the self-proclaimed...",
        required: false,
        optional: true,
        id: "essay2",
      },
      {
        type: "textArea",
        label: "Generated Essay Instructions",
        field: "Generated Essay Instructions",
        placeholder: "Write a short piece in the same style here about...",
        required: false,
        optional: true,
        id: "generatedessayinstructions",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing writing styles. You will be provided with multiple essays, and your task is to learn and replicate the writing style demonstrated in those essays.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { essay1, essay2, generatedEssayInstructions } = fields;
      let prompt =
        "Analyze the following essays and generate a new piece based on the identified style:\n";
      prompt += "Essay 1: " + essay1 + "\n";
      if (essay2) {
        prompt += "Essay 2: " + essay2 + "\n";
      }
      if (generatedEssayInstructions) {
        prompt +=
          "Instructions for generated essay: " +
          generatedEssayInstructions +
          "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "grant-proposal-drafter",
    name: "Grant Proposal Drafter",
    slug: "grant-proposal-drafter",
    shortDescription:
      "Craft compelling grant proposals effortlessly! Outline key components and project descriptions with our expert grant proposal drafter.",
    fields: [
      {
        type: "textArea",
        label: "Project Description",
        field: "Project Description",
        placeholder: "Our project aims to...",
        required: true,
        optional: false,
        id: "projectdescription",
      },
      {
        type: "textArea",
        label: "Key Components",
        field: "Key Components",
        placeholder: "1. Innovative Approach 2. Community Impact...",
        required: false,
        optional: true,
        id: "keycomponents",
      },
    ],
    categories: ["Writing", "Business", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in drafting grant proposals. You will be provided with a project description and key components, and you will generate a compelling grant proposal based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { projectDescription, keyComponents } = fields;
      let prompt = "Draft a grant proposal based on the following:\n";
      prompt += "Project Description: " + projectDescription + "\n";
      if (keyComponents) {
        prompt += "Key Components: " + keyComponents + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-travel-itinerary-planner",
    name: "AI Travel Itinerary Planner",
    slug: "ai-travel-itinerary-planner",
    shortDescription:
      "Create personalized travel itineraries with our AI planner based on your destination, duration, interests, and budget.",
    fields: [
      {
        type: "textArea",
        label: "Destination",
        field: "Destination",
        placeholder: "Paris, France",
        required: true,
        optional: false,
        id: "destination",
      },
      {
        type: "textArea",
        label: "Duration",
        field: "Duration",
        placeholder: "7 days",
        required: false,
        optional: true,
        id: "duration",
      },
      {
        type: "textArea",
        label: "Interests",
        field: "Interests",
        placeholder: "Art, History, Food",
        required: false,
        optional: true,
        id: "interests",
      },
    ],
    categories: ["Travel", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in designing customized travel itineraries. You will be provided with details about the destination, duration, interests, and budget, and you will create a tailored travel itinerary based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Destination, Duration, Interests } = fields;
      let prompt =
        "Create a travel itinerary based on the following details:\n";
      prompt += "Destination: " + Destination + ".\n";
      if (Duration) {
        prompt += "Duration: " + Duration + ".\n";
      }
      if (Interests) {
        prompt += "Interests: " + Interests + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "shakespeare-to-succession-text-converter",
    name: "Shakespeare to Succession Text Converter",
    slug: "shakespeare-to-succession-text-converter",
    shortDescription:
      "Transform Shakespearean text into modern dialogue inspired by Succession. Experience classic literature in a contemporary style!",
    fields: [
      {
        type: "textArea",
        label: "Shakespearean Text",
        field: "Shakespearean Text",
        placeholder: "To be, or not to be, that is the question...",
        required: true,
        optional: false,
        id: "shakespeareantext",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in transforming Shakespearean text into modern dialogue that resembles the style used in the television show, Succession. You will be provided with a piece of Shakespearean text and you will convert it into contemporary language.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { shakespeareanText } = fields;
      let prompt = `Transform the following Shakespearean text into dialogue that resembles the contemporary, modern language used in the television show, Succession:
  Shakespearean Text: ${shakespeareanText}`;
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "history-fact-finder",
    name: "History Fact Finder",
    slug: "history-fact-finder",
    shortDescription:
      "Explore detailed and accurate insights on historical events, figures, and periods with History Fact Finder.",
    fields: [
      {
        type: "textArea",
        label: "Historical Topic",
        field: "Historical Topic",
        placeholder: "American Civil War",
        required: true,
        optional: false,
        id: "historicaltopic",
      },
    ],
    categories: ["Education", "Writing", "History"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in historical research and analysis. You will be provided with a specific historical topic, and your task is to provide detailed and accurate information about that topic.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { HistoricalTopic } = fields;
      let prompt =
        "Provide detailed information on the following historical topic:\n";
      prompt += "Historical Topic: " + HistoricalTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "restaurant-name-ideas-generator",
    name: "Restaurant Name Ideas Generator",
    slug: "restaurant-name-ideas-generator",
    shortDescription:
      "Generate unique restaurant name ideas based on cuisine, location, and atmosphere with our creative name generator tool.",
    fields: [
      {
        type: "textArea",
        label: "Cuisine",
        field: "Cuisine",
        placeholder: "e.g., Italian, Mexican, Fusion",
        required: true,
        optional: false,
        id: "cuisine",
      },
      {
        type: "textArea",
        label: "Location",
        field: "Location",
        placeholder: "e.g., City, Neighborhood, Street",
        required: false,
        optional: true,
        id: "location",
      },
      {
        type: "textArea",
        label: "Atmosphere",
        field: "Atmosphere",
        placeholder: "e.g., Casual, Fine dining, Family-friendly",
        required: false,
        optional: true,
        id: "atmosphere",
      },
    ],
    categories: ["Marketing", "Business", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating creative and unique restaurant name ideas. You will be provided with details about the cuisine, location, and atmosphere, and you will generate a list of suitable restaurant names based on that information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Cuisine, Location, Atmosphere } = fields;
      let prompt =
        "Generate a list of restaurant name ideas based on the following:\n";
      prompt += "Cuisine: " + Cuisine + ".\n";
      if (Location) {
        prompt += "Location: " + Location + ".\n";
      }
      if (Atmosphere) {
        prompt += "Atmosphere: " + Atmosphere + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "succinct-note-organizer",
    name: "Succinct Note Organizer",
    slug: "succinct-note-organizer",
    shortDescription:
      "Transform lengthy notes into clear, concise points with our Succinct Note Organizer for better understanding and productivity.",
    fields: [
      {
        type: "textArea",
        label: "Input Notes",
        field: "Input Notes",
        placeholder: "Enter your lengthy and complex notes here.",
        required: true,
        optional: false,
        id: "inputnotes",
      },
    ],
    categories: ["Education", "Writing", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in summarizing and organizing notes. You will be provided with lengthy and complex notes, and your task is to simplify and summarize them into concise points that are easy to understand.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { inputNotes } = fields;
      let prompt =
        "Please summarize the following notes into concise points:\n";
      prompt += "Input Notes: " + inputNotes + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "course-hero-tutor",
    name: "Course Hero Tutor",
    slug: "course-hero-tutor",
    shortDescription:
      "Get expert tutoring and educational assistance for various subjects with Course Hero Tutor. Enhance your learning today!",
    fields: [
      {
        type: "textArea",
        label: "Question or Topic",
        field: "Question or Topic",
        placeholder: "Explain Newton's Third Law",
        required: true,
        optional: false,
        id: "questionortopic",
      },
    ],
    categories: ["Education", "Training Material"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert tutor with extensive knowledge in various subjects. You will be provided with a question or topic, and you will generate a detailed explanation or answer based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { questionOrTopic } = fields;
      let prompt =
        "Provide a detailed explanation or answer for the following question or topic:\n";
      prompt += "Question/Topic: " + questionOrTopic + ".\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "speech-analyzer",
    name: "Speech Analyzer",
    slug: "speech-analyzer",
    shortDescription:
      "Analyze and summarize key themes in speeches with our Speech Analyzer tool for clearer insights and impactful communication.",
    fields: [
      {
        type: "textArea",
        label: "Speech Text",
        field: "Speech Text",
        placeholder: "Enter the text of the speech here",
        required: true,
        optional: false,
        id: "speechtext",
      },
    ],
    categories: ["Education", "Writing", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in analyzing and summarizing speeches. You will be provided with the text of a speech, and your task is to identify and summarize the key themes and messages conveyed in it.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { speechText } = fields;
      let prompt =
        "Analyze the following speech and summarize its key themes and messages:\n";
      prompt += "Speech Text: " + speechText + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "cover-letter-generator-from-job-description",
    name: "Cover Letter Generator from Job Description",
    slug: "cover-letter-generator-from-job-description",
    shortDescription:
      "Craft a standout cover letter effortlessly! Our generator tailors your letter to any job description for maximum impact.",
    fields: [
      {
        type: "textArea",
        label: "Job Description",
        field: "Job Description",
        placeholder: "We are looking for a software engineer with...",
        required: true,
        optional: false,
        id: "jobdescription",
      },
      {
        type: "textArea",
        label: "User Skills and Experiences",
        field: "User Skills and Experiences",
        placeholder: "I have 5 years of experience in software development...",
        required: false,
        optional: true,
        id: "userskillsandexperiences",
      },
    ],
    categories: ["Writing", "Business", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating cover letters. You will be provided with a job description and the user's skills and experiences, and you will create a tailored cover letter based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { jobDescription, userSkillsAndExperiences } = fields;
      let prompt = "Generate a cover letter based on the following:\n";
      prompt += "Job Description: " + jobDescription + "\n";
      if (userSkillsAndExperiences) {
        prompt +=
          "User Skills and Experiences: " + userSkillsAndExperiences + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-fitness-plan-creator",
    name: "AI Fitness Plan Creator",
    slug: "ai-fitness-plan-creator",
    shortDescription:
      "Create your personalized fitness journey with our AI Fitness Plan Creator, offering tailored workouts and meal plans for your goals.",
    fields: [
      {
        type: "textArea",
        label: "Fitness Goals",
        field: "Fitness Goals",
        placeholder: "Lose weight, gain muscle, increase stamina",
        required: true,
        optional: false,
        id: "fitnessgoals",
      },
      {
        type: "textArea",
        label: "Dietary Preferences",
        field: "Dietary Preferences",
        placeholder: "Vegan, gluten-free, low-carb",
        required: false,
        optional: true,
        id: "dietarypreferences",
      },
      {
        type: "textArea",
        label: "Exercise Preferences",
        field: "Exercise Preferences",
        placeholder: "Running, yoga, weightlifting",
        required: false,
        optional: true,
        id: "exercisepreferences",
      },
    ],
    categories: ["Business", "Training Material", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creating personalized fitness plans. You will be provided with individual fitness goals and preferences, and you will generate a tailored workout routine and meal plan based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { fitnessGoals, dietaryPreferences, exercisePreferences } = fields;
      let prompt =
        "Generate a personalized fitness plan based on the following:\n";
      prompt += "Fitness Goals: " + fitnessGoals + ".\n";
      if (dietaryPreferences) {
        prompt += "Dietary Preferences: " + dietaryPreferences + ".\n";
      }
      if (exercisePreferences) {
        prompt += "Exercise Preferences: " + exercisePreferences + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-speech-drafter",
    name: "AI Speech Drafter",
    slug: "ai-speech-drafter",
    shortDescription:
      "Generate impactful speech drafts effortlessly with AI Speech Drafter, featuring engaging openings, key points, and strong conclusions.",
    fields: [
      {
        type: "textArea",
        label: "Occasion",
        field: "Occasion",
        placeholder: "Graduation",
        required: true,
        optional: false,
        id: "occasion",
      },
      {
        type: "textArea",
        label: "Audience",
        field: "Audience",
        placeholder: "High school students",
        required: false,
        optional: true,
        id: "audience",
      },
      {
        type: "textArea",
        label: "Key Points",
        field: "Key Points",
        placeholder:
          "The importance of education, The power of perseverance, The value of friendship",
        required: false,
        optional: true,
        id: "keypoints",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in drafting speeches. You will be provided with details about the occasion, audience, and key points, and you will generate a well-structured speech draft based on this information.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Occasion, Audience, KeyPoints } = fields;
      let prompt = "Generate a speech draft based on the following:\n";
      prompt += "Occasion: " + Occasion + ".\n";
      if (Audience) {
        prompt += "Audience: " + Audience + ".\n";
      }
      if (KeyPoints) {
        prompt += "Key Points: " + KeyPoints + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-creative-writing-generator",
    name: "AI Creative Writing Generator",
    slug: "ai-creative-writing-generator",
    shortDescription:
      "Generate unique creative writing pieces effortlessly with our AI generator, tailored to your prompts and specifications.",
    fields: [
      {
        type: "textArea",
        label: "Writing Prompt",
        field: "Writing Prompt",
        placeholder: "Write a short story about a journey to Mars",
        required: true,
        optional: false,
        id: "writingprompt",
      },
      {
        type: "textArea",
        label: "Writing Style",
        field: "Writing Style",
        placeholder: "Adventurous, Informative, Humorous, etc.",
        required: false,
        optional: true,
        id: "writingstyle",
      },
    ],
    categories: ["Writing", "Education", "Marketing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in creative writing. You will be provided with a writing prompt and style, and you will generate a creative piece of writing based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { writingPrompt, writingStyle } = fields;
      let prompt =
        "Generate a creative piece of writing based on the following:\n";
      prompt += "Writing Prompt: " + writingPrompt + "\n";
      if (writingStyle) {
        prompt += "Writing Style: " + writingStyle + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "generate-hypothesis",
    name: "Generate Hypothesis",
    slug: "generate-hypothesis",
    shortDescription:
      "Easily generate a hypothesis based on your research question and background information for effective study planning.",
    fields: [
      {
        type: "textArea",
        label: "Research Question",
        field: "Research Question",
        placeholder: "What is the effect of light intensity on photosynthesis?",
        required: true,
        optional: false,
        id: "researchquestion",
      },
      {
        type: "textArea",
        label: "Background Information",
        field: "Background Information",
        placeholder:
          "Photosynthesis is a process used by plants and other organisms to convert light energy...",
        required: false,
        optional: true,
        id: "backgroundinformation",
      },
    ],
    categories: ["Education", "Writing", "Research"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating hypotheses. You will be provided with a research question and optional background information, and you will generate a relevant hypothesis based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { ResearchQuestion, BackgroundInformation } = fields;
      let prompt = "Generate a hypothesis based on the following:\n";
      prompt += "Research Question: " + ResearchQuestion + ".\n";
      if (BackgroundInformation) {
        prompt += "Background Information: " + BackgroundInformation + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "science-query-solver",
    name: "Science Query Solver",
    slug: "science-query-solver",
    shortDescription:
      "Get accurate answers to your science questions with Science Query Solver. Explore comprehensive solutions for all your scientific inquiries.",
    fields: [
      {
        type: "textArea",
        label: "Science Question",
        field: "Science Question",
        placeholder: "What is the theory of relativity?",
        required: true,
        optional: false,
        id: "sciencequestion",
      },
    ],
    categories: ["Education", "Support", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in providing accurate and comprehensive answers to science-related questions. You will be given a specific science question, and your task is to generate a detailed and informative response.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { scienceQuestion } = fields;
      let prompt =
        "Provide a detailed answer to the following science question:\n";
      prompt += "Science Question: " + scienceQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "economics-query-solver",
    name: "Economics Query Solver",
    slug: "economics-query-solver",
    shortDescription:
      "Get clear answers to your economics questions with our Economics Query Solver. Detailed explanations at your fingertips!",
    fields: [
      {
        type: "textArea",
        label: "Economics Question",
        field: "Economics Question",
        placeholder: "What is the impact of inflation on the economy?",
        required: true,
        optional: false,
        id: "economicsquestion",
      },
    ],
    categories: ["Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in economics. You will be provided with a specific economics-related question, and you will provide a detailed explanation and answer based on the information given.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { economicsQuestion } = fields;
      let prompt =
        "Provide a detailed explanation and answer to the following economics question:\n";
      prompt += "Economics Question: " + economicsQuestion + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "comma-checker",
    name: "Comma Checker",
    slug: "comma-checker",
    shortDescription:
      "Effortlessly improve your writing with our Comma Checker, which identifies and corrects missing or incorrect commas in your text.",
    fields: [
      {
        type: "textArea",
        label: "Text for Comma Check",
        field: "Text for Comma Check",
        placeholder: "Enter text here to check for comma usage.",
        required: true,
        optional: false,
        id: "textforcommacheck",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grammar and punctuation. Your task is to examine the provided text for comma usage and suggest corrections for any incorrect or missing commas.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { textForCommaCheck } = fields;
      let prompt =
        "Please check the following text for comma usage and suggest corrections:\n";
      prompt += textForCommaCheck + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "creepypasta-generator",
    name: "Creepypasta Generator",
    slug: "creepypasta-generator",
    shortDescription:
      "Generate chilling and original creepypasta stories with our AI-powered creepypasta generator. Dive into the eerie today!",
    fields: [
      {
        type: "textArea",
        label: "Story Prompt",
        field: "Story Prompt",
        placeholder: "Haunted house, Lost time, Mysterious figure",
        required: true,
        optional: false,
        id: "storyprompt",
      },
    ],
    categories: ["Writing", "Education"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in generating creepypasta stories. You will be provided with a story prompt and you will create an original, engaging, and eerie creepypasta story based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { storyPrompt } = fields;
      let prompt = "Generate a creepypasta story based on the following:\n";
      prompt += "Story Prompt: " + storyPrompt + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "ai-chemistry-research-assistant",
    name: "AI Chemistry Research Assistant",
    slug: "ai-chemistry-research-assistant",
    shortDescription:
      "Enhance your chemistry research with our AI Chemistry Research Assistant, streamlining academic studies and boosting productivity.",
    fields: [
      {
        type: "textArea",
        label: "Research Topic",
        field: "Research Topic",
        placeholder: "Impact of chemical X on the environment",
        required: true,
        optional: false,
        id: "researchtopic",
      },
      {
        type: "textArea",
        label: "Specific Questions",
        field: "Specific Questions",
        placeholder:
          "What are the properties of chemical X? How does it affect the environment?",
        required: false,
        optional: true,
        id: "specificquestions",
      },
    ],
    categories: ["Education", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in chemistry research. You will be provided with a research topic and specific questions, and you will assist in generating relevant information and insights based on the provided details.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { researchTopic, specificQuestions } = fields;
      let prompt = "Assist in conducting research based on the following:\n";
      prompt += "Research Topic: " + researchTopic + ".\n";
      if (specificQuestions) {
        prompt += "Specific Questions: " + specificQuestions + ".\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "creative-story-writer",
    name: "Creative Story Writer",
    slug: "creative-story-writer",
    shortDescription:
      "Generate unique story ideas, plots, characters, and themes effortlessly with our Creative Story Writer based on your input!",
    fields: [
      {
        type: "textArea",
        label: "Prompt",
        field: "Prompt",
        placeholder: "A mysterious artifact is found in a small town",
        required: true,
        optional: false,
        id: "prompt",
      },
      {
        type: "textArea",
        label: "Setting",
        field: "Setting",
        placeholder: "Small coastal town in the 1800s",
        required: false,
        optional: true,
        id: "setting",
      },
      {
        type: "textArea",
        label: "Characters",
        field: "Characters",
        placeholder:
          "John - local fisherman, Mary - town's historian, Mysterious stranger",
        required: false,
        optional: true,
        id: "characters",
      },
    ],
    categories: ["Writing", "Education", "Creative Story Writer"],
    buildSystemPrompt: () => {
      let prompt =
        "You are a creative writing assistant. Your task is to generate unique story ideas, plots, characters, settings, and themes based on the user's input.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { Prompt, Setting, Characters } = fields;
      let prompt = "Generate a creative story based on the following:\n";
      prompt += "Prompt: " + Prompt + "\n";
      if (Setting) {
        prompt += "Setting: " + Setting + "\n";
      }
      if (Characters) {
        prompt += "Characters: " + Characters + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "grant-writing-ai",
    name: "Grant Writing AI",
    slug: "grant-writing-ai",
    shortDescription:
      "Streamline your grant proposals with Grant Writing AI, offering guidance, structure, and language suggestions for success.",
    fields: [
      {
        type: "textArea",
        label: "Grant Details",
        field: "Grant Details",
        placeholder: "Grant name, organization, deadline, etc.",
        required: true,
        optional: false,
        id: "grantdetails",
      },
      {
        type: "textArea",
        label: "Project Description",
        field: "Project Description",
        placeholder: "Describe your project in detail.",
        required: false,
        optional: true,
        id: "projectdescription",
      },
      {
        type: "textArea",
        label: "Goals and Objectives",
        field: "Goals and Objectives",
        placeholder: "What are the goals and objectives of your project?",
        required: false,
        optional: true,
        id: "goalsandobjectives",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in grant writing. You will be provided with details about a grant proposal, and you will generate a structured and persuasive grant proposal based on the information provided.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { grantDetails, projectDescription, goalsAndObjectives } = fields;
      let prompt = "Generate a grant proposal based on the following:\n";
      prompt += "Grant Details: " + grantDetails + "\n";
      if (projectDescription) {
        prompt += "Project Description: " + projectDescription + "\n";
      }
      if (goalsAndObjectives) {
        prompt += "Goals and Objectives: " + goalsAndObjectives + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "corporate-storyteller",
    name: "Corporate Storyteller",
    slug: "corporate-storyteller",
    shortDescription:
      "Transform your company's history into a compelling narrative for milestone celebrations with a powerful CEO message.",
    fields: [
      {
        type: "textArea",
        label: "Company History",
        field: "Company History",
        placeholder: "Our company was founded in 1990...",
        required: true,
        optional: false,
        id: "companyhistory",
      },
      {
        type: "textArea",
        label: "Milestone",
        field: "Milestone",
        placeholder: "25th Anniversary",
        required: false,
        optional: true,
        id: "milestone",
      },
    ],
    categories: ["Business", "Marketing", "Writing"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in crafting corporate narratives. You will be provided with the company's history and a milestone, and you will create a compelling story suitable for a CEO's message.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      let prompt = "Create a corporate narrative based on the following:\n";
      prompt += "Company History: " + fields["Company History"] + "\n";
      if (fields["Milestone"]) {
        prompt += "Milestone: " + fields["Milestone"] + "\n";
      }
      return prompt;
    },
    freeLimit: 5,
  },
  {
    id: "sentence-rephraser-ai",
    name: "Sentence Rephraser AI",
    slug: "sentence-rephraser-ai",
    shortDescription:
      "Enhance your writing with Sentence Rephraser AI, improving clarity and style while preserving original meanings effortlessly.",
    fields: [
      {
        type: "textArea",
        label: "Original Sentence",
        field: "Original Sentence",
        placeholder: "The quick brown fox jumps over the lazy dog.",
        required: true,
        optional: false,
        id: "originalsentence",
      },
    ],
    categories: ["Writing", "Education", "Business"],
    buildSystemPrompt: () => {
      let prompt =
        "You are an expert in rephrasing sentences. You will be provided with a sentence, and your task is to improve its clarity, grammar, or style without changing its original meaning.";
      return prompt;
    },
    buildUserPrompt: (fields) => {
      const { originalSentence } = fields;
      let prompt =
        "Rephrase the following sentence for improved clarity and style:\n";
      prompt += "Original Sentence: " + originalSentence + "\n";
      return prompt;
    },
    freeLimit: 5,
  },
];
export const templatesById = keyBy(baseTemplates, "slug");
